import React, { useEffect, useState, useRef, createContext, useContext, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useSearchParams, Navigate, useParams } from 'react-router-dom';
import { Menu, X, Instagram, ArrowRight, Play, ChevronDown } from 'lucide-react';
import { SITE_NAME, NAVIGATION, TESTIMONIALS, UDDANNELSE_TESTIMONIALS, PODCASTS, BLOG_POSTS, BUZZSPROUT_EPISODES } from './data';
import { cn } from './lib/utils';
import { imageUrl, assetPath } from './lib/images';
import LogoPage from './pages/LogoPage';
import { LanguageProvider, useLanguage } from './i18n/LanguageContext';
import { CmsProvider, useCms, resolveBuzzsproutEpisode } from './lib/cms';
import { getNavLinks, getLocalizedPath, getLanguageFromPathname } from './i18n/routes';
import LanguageSwitcher from './components/LanguageSwitcher';
import SEO from './components/SEO';
import './App.css';

// --- Components ---

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      const scroll = () => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      };
      scroll();
      const timer = setTimeout(scroll, 120);
      return () => clearTimeout(timer);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [pathname, hash]);
  return null;
};

const PageTransition = ({ children }) => {
  const location = useLocation();
  return (
    <div key={location.pathname} className="animate-fade-in">
      {children}
    </div>
  );
};

// --- Testimonials System ---

const TestimonialContext = createContext(null);

export const useTestimonialModal = () => useContext(TestimonialContext);

export const TestimonialProvider = ({ children }) => {
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const { lang } = useLanguage();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setSelectedTestimonial(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <TestimonialContext.Provider value={{ selectedTestimonial, setSelectedTestimonial, openTestimonial: (t) => setSelectedTestimonial(t) }}>
      {children}
      {selectedTestimonial && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-stone-900/60 backdrop-blur-xs transition-opacity duration-300 animate-in fade-in"
          onClick={() => setSelectedTestimonial(null)}
        >
          <div 
            className="bg-white rounded-sm border border-stone-200 shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-6 md:p-8 border-b border-stone-100 flex items-start justify-between bg-stone-50/50">
              <div>
                <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#817262] block mb-1">
                  {selectedTestimonial.title || (lang === 'en' ? 'Testimonial' : 'Udtalelse')}
                </span>
                <h3 className="text-2xl font-serif text-stone-900 font-medium">{selectedTestimonial.name}</h3>
              </div>
              <button
                onClick={() => setSelectedTestimonial(null)}
                className="text-stone-400 hover:text-stone-800 p-2 rounded-full hover:bg-stone-200/50 transition-colors cursor-pointer"
                aria-label={lang === 'en' ? 'Close' : 'Luk'}
              >
                <i className="fa-light fa-xmark text-xl" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 md:p-8 overflow-y-auto space-y-6">
              {selectedTestimonial.highlight && (
                <div className="p-5 bg-[#f5efe9]/60 border-l-3 border-[#817262] rounded-r-sm">
                  <p className="font-serif italic text-base md:text-lg text-stone-800 leading-relaxed">
                    "{selectedTestimonial.highlight}"
                  </p>
                </div>
              )}
              
              <div className="space-y-4 text-stone-700 font-light text-base leading-relaxed whitespace-pre-line">
                "{selectedTestimonial.text}"
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 md:px-8 border-t border-stone-100 bg-stone-50/50 flex justify-end">
              <button
                onClick={() => setSelectedTestimonial(null)}
                className="px-6 py-2.5 bg-[#817262] text-white text-xs uppercase tracking-[0.2em] font-medium hover:bg-stone-800 transition-colors rounded-xs shadow-xs cursor-pointer"
              >
                {lang === 'en' ? 'Close' : 'Luk udtalelse'}
              </button>
            </div>
          </div>
        </div>
      )}
    </TestimonialContext.Provider>
  );
};

const ImageLightboxModal = ({ isOpen, onClose, images = [], currentIndex = 0, onNavigate }) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && onNavigate && currentIndex < images.length - 1) {
        onNavigate(currentIndex + 1);
      }
      if (e.key === 'ArrowLeft' && onNavigate && currentIndex > 0) {
        onNavigate(currentIndex - 1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, images.length, onClose, onNavigate]);

  if (!isOpen || !images[currentIndex]) return null;

  const current = images[currentIndex];
  const fullSrc = current.largeSrc || current.src?.replace('width=600', 'width=1600')?.replace('width=200', 'width=1600') || current.src;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 md:p-8 bg-stone-950/80 backdrop-blur-xs animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div 
        className="relative max-w-4xl max-h-[90vh] w-full flex flex-col items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute -top-12 right-0 md:top-3 md:right-3 z-20 w-10 h-10 rounded-full bg-stone-900/90 text-white hover:bg-stone-800 flex items-center justify-center transition-colors cursor-pointer border border-stone-700 shadow-xl"
          aria-label="Luk billede"
        >
          <i className="fa-solid fa-xmark text-lg" />
        </button>

        {/* Prev Button */}
        {images.length > 1 && currentIndex > 0 && (
          <button
            type="button"
            onClick={() => onNavigate(currentIndex - 1)}
            className="absolute left-2 md:-left-14 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-stone-900/90 text-white hover:bg-stone-800 flex items-center justify-center transition-colors cursor-pointer border border-stone-700 shadow-xl"
            aria-label="Forrige billede"
          >
            <i className="fa-solid fa-chevron-left text-sm" />
          </button>
        )}

        {/* Next Button */}
        {images.length > 1 && currentIndex < images.length - 1 && (
          <button
            type="button"
            onClick={() => onNavigate(currentIndex + 1)}
            className="absolute right-2 md:-right-14 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-stone-900/90 text-white hover:bg-stone-800 flex items-center justify-center transition-colors cursor-pointer border border-stone-700 shadow-xl"
            aria-label="Næste billede"
          >
            <i className="fa-solid fa-chevron-right text-sm" />
          </button>
        )}

        {/* Main Image */}
        <div className="overflow-hidden rounded-sm bg-stone-950 shadow-2xl border border-stone-800 max-h-[80vh] flex items-center justify-center">
          <img 
            src={fullSrc} 
            alt={current.alt || "Forstørret billede"} 
            className="max-h-[78vh] w-auto max-w-full object-contain rounded-xs select-none"
          />
        </div>

        {/* Caption / Pagination indicator */}
        {(current.alt || images.length > 1) && (
          <div className="mt-3 text-center text-stone-300 text-xs sm:text-sm font-light flex items-center justify-center gap-3">
            {current.alt && <span>{current.alt}</span>}
            {images.length > 1 && (
              <span className="text-stone-400 text-xs">
                ({currentIndex + 1} / {images.length})
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const renderQuoteText = (rawText) => {
  if (!rawText) return '';
  return rawText.split(/(<u>.*?<\/u>)/g).map((part, pIdx) => {
    if (part.startsWith('<u>') && part.endsWith('</u>')) {
      const inner = part.slice(3, -4);
      return <span key={pIdx} className="underline underline-offset-2 not-italic font-normal">{inner}</span>;
    }
    return part;
  });
};

const TestimonialQuoteCard = ({ testimonial, className = "" }) => {
  const { openTestimonial } = useTestimonialModal() || {};
  const { lang } = useLanguage();
  if (!testimonial) return null;

  return (
    <div className={cn("p-6 sm:p-8 border border-stone-200/90 shadow-xs sm:shadow-none bg-stone-50/50 flex flex-col justify-between hover:border-[#817262]/40 hover:shadow-md transition-all duration-300 rounded-sm relative group", className)}>
      <div className="space-y-4">
        <div className="text-[#817262]/30 text-3xl font-serif pointer-events-none select-none">”</div>
        <p className="text-stone-700 text-sm md:text-base font-serif italic leading-relaxed">
          "{renderQuoteText(testimonial.highlight || testimonial.text)}"
        </p>
      </div>
      
      <div className="mt-6 pt-6 border-t border-stone-200/60 flex flex-col items-start gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-stone-900">{testimonial.name}</p>
        </div>
        {openTestimonial && (
          <button
            onClick={() => openTestimonial(testimonial)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#004f3b] hover:text-emerald-800 transition-colors cursor-pointer group/btn"
          >
            <span>{lang === 'en' ? 'Read full testimonial' : 'Læs hele udtalelsen her'}</span>
            <i className="fa-light fa-arrow-right text-[10px] group-hover/btn:translate-x-1 transition-transform" />
          </button>
        )}
      </div>
    </div>
  );
};

const TestimonialCallout = ({ testimonialName, customTitle, className = "" }) => {
  const { openTestimonial } = useTestimonialModal() || {};
  const { lang } = useLanguage();
  const { testimonials } = useCms();
  const testimonial = (testimonials && testimonials.find(t => t.name === testimonialName)) || (TESTIMONIALS.find(t => t.name === testimonialName)) || TESTIMONIALS[0];

  if (!testimonial) return null;

  return (
    <div className={cn("my-10 bg-[#f5efe9]/70 border-l-4 border-[#817262] p-8 md:p-10 rounded-r-sm shadow-xs relative overflow-hidden", className)}>
      <div className="absolute top-2 right-6 text-[#817262]/15 text-7xl font-serif pointer-events-none select-none">”</div>
      <div className="relative z-10 space-y-4">
        {customTitle && (
          <span className="text-[11px] uppercase tracking-[0.3em] font-bold text-[#817262] block">
            {customTitle}
          </span>
        )}
        <blockquote className="text-stone-800 font-serif italic text-lg md:text-xl leading-relaxed">
          "{testimonial.highlight || testimonial.text}"
        </blockquote>
        <div className="pt-2 flex flex-wrap items-center justify-between gap-4 border-t border-[#817262]/15">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-stone-900 block">{testimonial.name}</span>
          </div>
          {openTestimonial && (
            <button
              onClick={() => openTestimonial(testimonial)}
              className="inline-flex items-center gap-2 text-xs font-semibold text-[#004f3b] hover:text-emerald-900 transition-colors cursor-pointer group/btn"
            >
              <span>{lang === 'en' ? 'Read full testimonial' : 'Læs hele udtalelsen her'}</span>
              <i className="fa-light fa-arrow-right text-[10px] group-hover/btn:translate-x-1 transition-transform" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const YouTubeEmbed = ({ url, title = "YouTube video", isShort = false, className = "" }) => {
  if (!url) return null;
  let videoId = "";
  if (url.includes('/shorts/')) {
    videoId = url.split('/shorts/')[1]?.split('?')[0];
  } else if (url.includes('youtu.be/')) {
    videoId = url.split('youtu.be/')[1]?.split('?')[0];
  } else if (url.includes('v=')) {
    videoId = url.split('v=')[1]?.split('&')[0];
  }

  if (!videoId) return null;

  const hasCustomMaxWidth = className.includes('max-w-');

  if (isShort || url.includes('/shorts/')) {
    return (
      <div className={cn("w-full mx-auto overflow-hidden rounded-xl shadow-lg border border-stone-200/60 bg-stone-900 aspect-[9/16]", !hasCustomMaxWidth && "max-w-[320px]", className)}>
        <iframe
          src={`https://www.youtube.com/embed/${videoId}`}
          title={title}
          loading="lazy"
          className="w-full h-full border-0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; webshare"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <div className={cn("w-full overflow-hidden rounded-xl shadow-lg border border-stone-200/60 bg-stone-900 aspect-video", className)}>
      <iframe
        src={`https://www.youtube.com/embed/${videoId}`}
        title={title}
        loading="lazy"
        className="w-full h-full border-0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; webshare"
        allowFullScreen
      />
    </div>
  );
};

const NewsletterPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('');
  const { lang, t } = useLanguage();
  const location = useLocation();

  // Deaktiver nyhedsbrevspopup på workshop-sider
  const isWorkshopPage = location.pathname.includes('/workshop') || 
    location.pathname.includes('/smagsproeve') ||
    location.pathname.includes('/kvinde-kend-din-power') ||
    location.pathname.includes('/woman-know-your-power');

  useEffect(() => {
    if (isWorkshopPage) {
      setIsOpen(false);
      return;
    }

    const dismissedTime = localStorage.getItem('newsletter_popup_dismissed_timestamp');
    const sessionClosed = sessionStorage.getItem('newsletter_popup_session_closed');
    const subscribed = localStorage.getItem('newsletter_popup_subscribed');

    if (subscribed || sessionClosed) return;

    if (dismissedTime) {
      const days = (Date.now() - parseInt(dismissedTime, 10)) / (1000 * 60 * 60 * 24);
      if (days < 7) return;
    }

    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 8000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    sessionStorage.setItem('newsletter_popup_session_closed', 'true');
    localStorage.setItem('newsletter_popup_dismissed_timestamp', Date.now().toString());
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setStatus('loading');

    const formData = new FormData(e.target);
    const honey = formData.get('website_url');
    if (honey) {
      setStatus('success');
      setEmail('');
      localStorage.setItem('newsletter_popup_subscribed', 'true');
      setTimeout(() => setIsOpen(false), 2000);
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: 'Tilmelding til nyhedsbrev',
          email: email,
          message: email,
          website_url: ''
        }),
      });
      if (response.ok) {
        setStatus('success');
        setEmail('');
        localStorage.setItem('newsletter_popup_subscribed', 'true');
        setTimeout(() => setIsOpen(false), 2500);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  if (!isOpen || isWorkshopPage) return null;

  const isEn = lang === 'en';

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-stone-900/60 backdrop-blur-xs animate-fade-in">
      <div 
        className="bg-stone-900 text-white rounded-sm border border-stone-800 p-6 md:p-8 max-w-lg w-full relative shadow-2xl space-y-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 text-stone-400 hover:text-white transition-colors p-2 rounded-full cursor-pointer"
          aria-label={isEn ? "Close popup" : "Luk popup"}
        >
          <i className="fa-solid fa-xmark text-lg" />
        </button>

        <div className="space-y-2 pr-6">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#817262] font-semibold block">
            {isEn ? "Inspiration & News" : "Inspiration & Nyheder"}
          </span>
          <h3 className="text-2xl font-serif text-white">{t('newsletter.popupTitle')}</h3>
          <p className="text-stone-300 text-xs md:text-sm font-light leading-relaxed">
            {t('newsletter.popupDesc')}
          </p>
        </div>

        <form 
          id="signup" 
          action="https://e.lumant.dk/api/v1/optin/list_6v-RTZGckC/subscribe" 
          method="post" 
          className="space-y-3"
        >
          <input type="hidden" name="ref" value="" />
          <div style={{ position: 'absolute', left: '-5000px' }} aria-hidden="true">
            <input type="text" name="hp_reference" tabIndex={-1} autoComplete="off" />
          </div>
          
          <div className="flex flex-col gap-2.5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input 
                type="text" 
                name="name" 
                placeholder={isEn ? "Your name" : "Dit navn"} 
                className="bg-stone-950 border border-stone-700 rounded-sm px-4 py-3 text-xs text-white placeholder-stone-500 focus:border-[#817262] outline-none transition-all w-full"
              />
              <input 
                type="email" 
                name="email" 
                placeholder={isEn ? "Your email" : "Din e-mail"} 
                pattern="[^@\s]+@[^@\s]+\.[^@\s]+" 
                required 
                className="bg-stone-950 border border-stone-700 rounded-sm px-4 py-3 text-xs text-white placeholder-stone-500 focus:border-[#817262] outline-none transition-all w-full"
              />
            </div>
            <input 
              type="submit" 
              name="submit" 
              value={isEn ? "SUBSCRIBE NOW" : "TILMELD MIG NU"} 
              className="w-full bg-[#817262] hover:bg-[#6e6153] text-white px-6 py-3 text-xs uppercase tracking-widest font-bold rounded-sm transition-all duration-300 cursor-pointer"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

const FadeIn = ({ children, delay = 0, className = "", direction = "up" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0, rootMargin: "0px 0px 50px 0px" });

    const currentRef = domRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, []);

  const getTransform = () => {
    if (isVisible) return "translate-y-0 translate-x-0";
    switch (direction) {
      case "up": return "translate-y-12";
      case "down": return "-translate-y-12";
      case "left": return "translate-x-12";
      case "right": return "-translate-x-12";
      default: return "translate-y-12";
    }
  };

  return (
    <div
      ref={domRef}
      className={cn(
        "transition-all duration-1000 ease-out",
        isVisible ? "opacity-100" : "opacity-0",
        getTransform(),
        className
      )}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const VideoPlayerPlaceholder = ({ title, duration = "2:30" }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const timerRef = useRef(null);

  // Parse duration string "MM:SS" to total seconds
  const [min, sec] = duration.split(':').map(Number);
  const totalSeconds = min * 60 + sec;

  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setCurrentTime((prev) => {
          if (prev >= totalSeconds) {
            setIsPlaying(false);
            setProgress(0);
            return 0;
          }
          const nextTime = prev + 1;
          setProgress((nextTime / totalSeconds) * 100);
          return nextTime;
        });
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPlaying, totalSeconds]);

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const handleToggle = () => {
    setIsPlaying(!isPlaying);
  };

  return (
    <div className="flex flex-col space-y-2 my-4">
      <div className="aspect-video w-full bg-stone-950 rounded-sm overflow-hidden relative group border border-stone-800/10 shadow-md flex items-center justify-center">
        {/* Video Background / Thumbnail */}
        <div className="absolute inset-0 bg-stone-900 flex flex-col justify-between p-4">
          {/* Top Bar */}
          <div className="z-10 flex justify-between items-start">
            <div className="bg-stone-950/60 backdrop-blur-sm px-3 py-1.5 rounded-sm">
              <p className="text-[10px] font-bold text-stone-300 uppercase tracking-widest">Video Introduktion</p>
              <h5 className="text-xs font-serif text-white mt-0.5">{title}</h5>
            </div>
            <span className="text-[10px] bg-stone-950/60 backdrop-blur-sm text-stone-300 px-2 py-1 rounded-sm font-mono">
              {duration}
            </span>
          </div>

          {/* Animated Ambient Background when playing */}
          {isPlaying ? (
            <div className="absolute inset-0 bg-gradient-to-tr from-stone-900 via-stone-800 to-stone-900 flex items-center justify-center overflow-hidden">
              <div className="absolute w-64 h-64 bg-stone-700/20 rounded-full blur-3xl animate-pulse" />
              <div className="absolute w-48 h-48 bg-stone-600/10 rounded-full blur-2xl animate-ping" style={{ animationDuration: '4s' }} />
              <div className="z-10 text-center space-y-2">
                <i className="fa-light fa-waveform-lines text-3xl text-stone-300 animate-pulse" />
                <p className="text-xs text-stone-400 font-light tracking-wider">Afspiller video-præsentation...</p>
              </div>
            </div>
          ) : (
            <div className="absolute inset-0 bg-stone-900/40 flex items-center justify-center">
              {/* Overlay pattern */}
              <div className="absolute inset-0 bg-[radial-gradient(#817262_1px,transparent_1px)] [background-size:16px_16px] opacity-10" />
            </div>
          )}

          {/* Play/Pause Center Button Overlay */}
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <button
              onClick={handleToggle}
              className={cn(
                "w-14 h-14 rounded-full flex items-center justify-center transition-all shadow-lg cursor-pointer",
                isPlaying
                  ? "bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm scale-90"
                  : "bg-white text-stone-900 hover:scale-110 hover:bg-stone-100"
              )}
              aria-label={isPlaying ? "Pause video" : "Afspil video"}
            >
              {isPlaying ? (
                <i className="fa-solid fa-pause text-lg" />
              ) : (
                <i className="fa-solid fa-play text-lg ml-1" />
              )}
            </button>
          </div>

          {/* Bottom Controls Bar */}
          <div className="z-10 w-full bg-gradient-to-t from-stone-950/80 to-transparent p-3 absolute bottom-0 left-0 right-0">
            {/* Progress Bar */}
            <div className="relative w-full h-1 bg-white/20 rounded-full overflow-hidden cursor-pointer mb-2">
              <div
                className="absolute top-0 left-0 h-full bg-stone-300 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-[11px] text-stone-300 font-mono">
              <div className="flex items-center space-x-3">
                <span>{formatTime(currentTime)} / {duration}</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="fa-light fa-volume-high" />
                <i className="fa-light fa-expand" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TestimonialsShowcase = ({ 
  testimonials = null, 
  category = null, 
  badge, 
  title, 
  subtitle, 
  className = "py-20 md:py-24 bg-[#fbf9f6] border-b border-stone-200/60 px-6",
  cardBg = "bg-white",
  showViewAll = true,
  customItems = null
}) => {
  const { lang } = useLanguage();
  const isEn = lang === 'en';
  const { testimonials: cmsTestimonials } = useCms();
  
  const allList = (cmsTestimonials && cmsTestimonials.length > 0) ? cmsTestimonials : TESTIMONIALS;
  
  let list = customItems || testimonials;
  if (!list) {
    if (category) {
      list = allList.filter(t => t.category === category || (t.categories && t.categories.includes(category)) || (t.title && t.title.toLowerCase().includes(category.toLowerCase())));
      if (list.length < 4) {
        const remaining = allList.filter(t => !list.includes(t));
        list = [...list, ...remaining];
      }
    } else {
      list = allList;
    }
  }
  
  // Use 4 items for clean grid
  const displayList = list.slice(0, 4);
  if (displayList.length === 0) return null;

  const defaultBadge = isEn ? "Participant Testimonials" : "Udtalelser fra deltagere og klienter";
  const defaultTitle = isEn ? "What clients and participants experience" : "Hvad andre oplever sammen med Mette";
  const viewAllPath = isEn ? "/en/about-mette/testimonials" : "/om-mette/udtalelser";

  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef(null);

  const scrollToIndex = (idx) => {
    const container = scrollRef.current;
    if (!container) return;
    const cards = container.querySelectorAll('[data-testimonial-slide]');
    if (cards[idx]) {
      cards[idx].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      setActiveIndex(idx);
    }
  };

  const handleScroll = () => {
    const container = scrollRef.current;
    if (!container) return;
    const scrollLeft = container.scrollLeft;
    const cards = container.querySelectorAll('[data-testimonial-slide]');
    if (cards.length === 0) return;
    const containerCenter = scrollLeft + container.offsetWidth / 2;
    let closestIndex = 0;
    let closestDistance = Infinity;
    cards.forEach((card, idx) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const distance = Math.abs(containerCenter - cardCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = idx;
      }
    });
    setActiveIndex(closestIndex);
  };

  return (
    <section className={className}>
      <div className="max-w-7xl mx-auto">
        <FadeIn>
          {/* Mobile Slider / Swiper (sm:hidden) */}
          <div className="sm:hidden relative">
            <div 
              ref={scrollRef}
              onScroll={handleScroll}
              className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-3 -mx-6 px-6 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] touch-pan-x"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {displayList.map((item, idx) => (
                <div 
                  key={idx} 
                  data-testimonial-slide 
                  className="w-[80vw] max-w-[310px] shrink-0 snap-center flex flex-col"
                >
                  <TestimonialQuoteCard testimonial={item} className={cn("h-full", cardBg)} />
                </div>
              ))}
            </div>

            {/* Slider Controls: Dots and Prev/Next */}
            {displayList.length > 1 && (
              <div className="flex items-center justify-between mt-5 px-1">
                <button
                  type="button"
                  onClick={() => scrollToIndex(Math.max(0, activeIndex - 1))}
                  disabled={activeIndex === 0}
                  aria-label={isEn ? "Previous testimonial" : "Forrige udtalelse"}
                  className={cn(
                    "w-9 h-9 rounded-full flex items-center justify-center border transition-all cursor-pointer",
                    activeIndex === 0 
                      ? "opacity-30 border-stone-200 text-stone-400 cursor-not-allowed" 
                      : "border-[#817262]/30 text-[#817262] bg-white shadow-xs hover:bg-stone-50 active:scale-95"
                  )}
                >
                  <i className="fa-solid fa-chevron-left text-xs" />
                </button>

                <div className="flex items-center gap-2">
                  {displayList.map((_, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => scrollToIndex(idx)}
                      aria-label={`${isEn ? "Go to testimonial" : "Gå til udtalelse"} ${idx + 1}`}
                      className={cn(
                        "transition-all duration-300 rounded-full cursor-pointer",
                        activeIndex === idx 
                          ? "w-6 h-2 bg-[#817262]" 
                          : "w-2 h-2 bg-stone-300 hover:bg-stone-400"
                      )}
                    />
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => scrollToIndex(Math.min(displayList.length - 1, activeIndex + 1))}
                  disabled={activeIndex === displayList.length - 1}
                  aria-label={isEn ? "Next testimonial" : "Næste udtalelse"}
                  className={cn(
                    "w-9 h-9 rounded-full flex items-center justify-center border transition-all cursor-pointer",
                    activeIndex === displayList.length - 1 
                      ? "opacity-30 border-stone-200 text-stone-400 cursor-not-allowed" 
                      : "border-[#817262]/30 text-[#817262] bg-white shadow-xs hover:bg-stone-50 active:scale-95"
                  )}
                >
                  <i className="fa-solid fa-chevron-right text-xs" />
                </button>
              </div>
            )}
          </div>

          {/* Desktop / Tablet Grid (hidden sm:grid) */}
          <div className="hidden sm:grid gap-6 items-stretch grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {displayList.map((item, idx) => (
              <FadeIn key={idx} delay={idx * 150} direction="up" className="h-full">
                <TestimonialQuoteCard testimonial={item} className={cn("h-full", cardBg)} />
              </FadeIn>
            ))}
          </div>

          {showViewAll && (
            <div className="text-center mt-10">
              <Link
                to={viewAllPath}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#817262] hover:text-stone-900 transition-colors underline underline-offset-4"
              >
                <span>{isEn ? "See all client testimonials →" : "Se alle klientudtalelser her →"}</span>
              </Link>
            </div>
          )}
        </FadeIn>
      </div>
    </section>
  );
};

// Home Testimonials Slider (Mobile swipe 1-by-1, Desktop 4-column grid)
const HomeTestimonialsSlider = ({ testimonials = [], isEn = false }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef(null);

  const scrollToIndex = (idx) => {
    const container = scrollRef.current;
    if (!container) return;
    const cards = container.querySelectorAll('[data-home-testimonial-slide]');
    if (cards[idx]) {
      cards[idx].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      setActiveIndex(idx);
    }
  };

  const handleScroll = () => {
    const container = scrollRef.current;
    if (!container) return;
    const scrollLeft = container.scrollLeft;
    const cards = container.querySelectorAll('[data-home-testimonial-slide]');
    if (cards.length === 0) return;
    const containerCenter = scrollLeft + container.offsetWidth / 2;
    let closestIndex = 0;
    let closestDistance = Infinity;
    cards.forEach((card, idx) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const distance = Math.abs(containerCenter - cardCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = idx;
      }
    });
    setActiveIndex(closestIndex);
  };

  return (
    <div>
      {/* Mobile Slider / Swiper (sm:hidden) */}
      <div className="sm:hidden relative">
        <div 
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-3 -mx-6 px-6 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] touch-pan-x"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {testimonials.map((item, idx) => (
            <div 
              key={idx} 
              data-home-testimonial-slide 
              className="w-[85vw] max-w-[340px] shrink-0 snap-center flex flex-col"
            >
              <TestimonialQuoteCard testimonial={item} className="h-full bg-stone-50/60" />
            </div>
          ))}
        </div>

        {/* Slider Controls: Dots and Prev/Next */}
        {testimonials.length > 1 && (
          <div className="flex items-center justify-between mt-5 px-1">
            <button
              type="button"
              onClick={() => scrollToIndex(Math.max(0, activeIndex - 1))}
              disabled={activeIndex === 0}
              aria-label={isEn ? "Previous testimonial" : "Forrige udtalelse"}
              className={cn(
                "w-9 h-9 rounded-full flex items-center justify-center border transition-all cursor-pointer",
                activeIndex === 0 
                  ? "opacity-30 border-stone-200 text-stone-400 cursor-not-allowed" 
                  : "border-[#817262]/30 text-[#817262] bg-white shadow-xs hover:bg-stone-50 active:scale-95"
              )}
            >
              <i className="fa-solid fa-chevron-left text-xs" />
            </button>

            <div className="flex items-center gap-2">
              {testimonials.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => scrollToIndex(idx)}
                  aria-label={`${isEn ? "Go to testimonial" : "Gå til udtalelse"} ${idx + 1}`}
                  className={cn(
                    "transition-all duration-300 rounded-full cursor-pointer",
                    activeIndex === idx 
                      ? "w-6 h-2 bg-[#817262]" 
                      : "w-2 h-2 bg-stone-300 hover:bg-stone-400"
                  )}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={() => scrollToIndex(Math.min(testimonials.length - 1, activeIndex + 1))}
              disabled={activeIndex === testimonials.length - 1}
              aria-label={isEn ? "Next testimonial" : "Næste udtalelse"}
              className={cn(
                "w-9 h-9 rounded-full flex items-center justify-center border transition-all cursor-pointer",
                activeIndex === testimonials.length - 1 
                  ? "opacity-30 border-stone-200 text-stone-400 cursor-not-allowed" 
                  : "border-[#817262]/30 text-[#817262] bg-white shadow-xs hover:bg-stone-50 active:scale-95"
              )}
            >
              <i className="fa-solid fa-chevron-right text-xs" />
            </button>
          </div>
        )}
      </div>

      {/* Desktop/Tablet Grid (hidden sm:grid) */}
      <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {testimonials.map((item, idx) => (
          <FadeIn key={idx} delay={idx * 100} className="h-full">
            <TestimonialQuoteCard testimonial={item} className="h-full" />
          </FadeIn>
        ))}
      </div>
    </div>
  );
};

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const pathname = location.pathname;
  const hash = location.hash;
  const { lang, t } = useLanguage();
  const { navigation } = useCms();
  const navItems = getNavLinks(lang, navigation);
  const kontaktText = navigation?.find(n => n.nokle === 'kontakt_knap')?.titel?.[lang] || t('nav.kontakt');

  const homePath = lang === 'en' ? '/en' : '/';
  const contactPath = lang === 'en' ? '/en/contact' : '/kontakt';

  return (
    <header className="fixed top-0 left-0 w-full z-50 bg-white border-b border-stone-100 shadow-xs">
      {/* Top Utility Bar for Language Switcher & Tagline */}
      <div className="bg-[#f5efe9] border-b border-[#817262]/10 py-1.5 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="w-full flex justify-between items-center text-xs">
          <div className="hidden sm:flex items-center space-x-3 text-[11px] uppercase tracking-[0.15em] text-[#817262] font-medium">
            <span>Mette Brudvig</span>
            <span className="text-[#817262]/40">•</span>
            <span className="text-stone-600 font-normal">
              {lang === 'en' ? "Empowerment & New Era Leadership" : "Empowerment & Ny Tids Lederskab"}
            </span>
          </div>
          <div className="flex items-center space-x-4 ml-auto">
            <a 
              href="tel:+4527507083" 
              className="flex items-center gap-1.5 text-stone-700 hover:text-stone-900 font-medium transition-colors text-xs"
              title={lang === 'en' ? "Call Mette Brudvig: +45 27 50 70 83" : "Ring til Mette Brudvig: 27 50 70 83"}
            >
              <i className="fa-solid fa-phone text-[11px] text-[#817262]" />
              <span>27 50 70 83</span>
            </a>
            <span className="text-stone-300">|</span>
            <LanguageSwitcher />
          </div>
        </div>
      </div>

      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12">
        <nav className="w-full py-2.5 flex justify-between items-center gap-4">
        {/* Logo helt ud til venstre */}
        <Link to={homePath} className="flex items-center space-x-3 group shrink-0">
          <div className="w-14 h-14 sm:w-16 sm:h-16 lg:w-16 lg:h-16 xl:w-20 xl:h-20 2xl:w-24 2xl:h-24 flex items-center justify-center overflow-hidden group-hover:scale-105 transition-transform duration-300">
            <img 
              src="https://assets.auxodev.dk/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/logo-transparent.png" 
              alt="Mette Brudvig Logo" 
              className="w-full h-full object-contain"
            />
          </div>
        </Link>

        {/* Desktop Nav Links - centreret mellem logo og knap */}
        <div className="hidden lg:flex items-center justify-center gap-1.5 xl:gap-3.5 2xl:gap-6 flex-1 px-2">
          {navItems.map((item) => {
            if (item.children) {
              const isChildActive = (item.href && pathname === item.href) || item.children.some(child => {
                const childPath = child.href.split('#')[0];
                if (childPath === '/' || childPath === '/en') return pathname === childPath;
                return pathname === childPath || pathname.startsWith(childPath);
              });
              return (
                <div key={item.name} className="relative group py-2">
                  {item.href ? (
                    <Link
                      to={item.href}
                      className={cn(
                        "flex items-center text-[11px] xl:text-xs 2xl:text-sm uppercase tracking-wider transition-colors select-none hover:text-[#004f3b] whitespace-nowrap",
                        isChildActive ? "text-stone-900 font-bold" : "text-stone-500 hover:text-stone-900"
                      )}
                    >
                      {item.name}
                      <ChevronDown size={12} className="ml-0.5 xl:ml-1 transition-transform duration-200 group-hover:rotate-180 shrink-0" />
                    </Link>
                  ) : (
                    <span
                      className={cn(
                        "flex items-center text-[11px] xl:text-xs 2xl:text-sm uppercase tracking-wider transition-colors cursor-pointer select-none whitespace-nowrap",
                        isChildActive ? "text-stone-900 font-bold" : "text-stone-500 hover:text-stone-900"
                      )}
                    >
                      {item.name}
                      <ChevronDown size={12} className="ml-0.5 xl:ml-1 transition-transform duration-200 group-hover:rotate-180 shrink-0" />
                    </span>
                  )}
                  <div className="absolute top-full left-0 mt-1 w-max min-w-[20rem] max-w-md bg-white border border-stone-100 shadow-xl rounded-sm py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    {item.children.map((child) => {
                      const isSelected = (pathname + hash) === child.href || (pathname === child.href && !hash);
                      return (
                        <div key={child.name} className="flex flex-col">
                          <Link
                            to={child.href}
                            onClick={() => {
                              if (child.href.includes('#')) {
                                const [, h] = child.href.split('#');
                                if (h) {
                                  setTimeout(() => {
                                    const el = document.getElementById(h);
                                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                                  }, 50);
                                }
                              }
                            }}
                            className={cn(
                              "block px-4 py-2 text-xs uppercase tracking-wider transition-colors whitespace-nowrap",
                              isSelected ? "text-[#004f3b] font-bold bg-stone-50" : "text-stone-700 hover:bg-stone-50 hover:text-stone-900"
                            )}
                          >
                            {child.name}
                          </Link>
                          {child.subChildren && (
                            <div className="pl-6 pr-3 py-1 space-y-1 bg-[#f5efe9]/50 border-l-2 border-[#817262] my-1 mx-3 rounded-xs">
                              {child.subChildren.map((sub) => {
                                const isSubSelected = (pathname + hash) === sub.href;
                                return (
                                  <Link
                                    key={sub.name}
                                    to={sub.href}
                                    className={cn(
                                      "block px-2 py-1.5 text-[11px] font-medium transition-colors rounded-xs whitespace-nowrap",
                                      isSubSelected
                                        ? "text-[#004f3b] font-bold bg-white shadow-2xs"
                                        : "text-stone-600 hover:text-[#817262] hover:bg-white/80"
                                    )}
                                  >
                                    {sub.name}
                                  </Link>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }

            return (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  "text-[11px] xl:text-xs 2xl:text-sm uppercase tracking-wider transition-colors whitespace-nowrap",
                  pathname === item.href ? "text-stone-900 font-bold" : "text-stone-500 hover:text-stone-900"
                )}
              >
                {item.name}
              </Link>
            );
          })}
        </div>

        {/* Kontakt-knap helt ud til højre */}
        <div className="hidden lg:flex items-center shrink-0">
          <Link
            to={contactPath}
            className="border border-stone-900 px-3 py-1.5 xl:px-4 xl:py-2 text-[11px] xl:text-xs uppercase tracking-wider hover:bg-stone-900 hover:text-white transition-all duration-300 font-medium whitespace-nowrap"
          >
            {kontaktText}
          </Link>
        </div>

        {/* Mobile / Tablet Toggle */}
        <div className="flex items-center gap-3 lg:hidden">
          <button className="p-2 text-stone-800" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle menu">
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="lg:hidden absolute top-full left-0 w-full bg-white border-b border-stone-100 py-8 px-6 flex flex-col space-y-6 shadow-xl max-h-[85vh] overflow-y-auto">
          {navItems.map((item) => {
            if (item.children) {
              const isChildActive = (item.href && pathname === item.href) || item.children.some(child => {
                const childPath = child.href.split('#')[0];
                if (childPath === '/' || childPath === '/en') return pathname === childPath;
                return pathname === childPath || pathname.startsWith(childPath);
              });
              return (
                <div key={item.name} className="flex flex-col space-y-3">
                  {item.href ? (
                    <Link
                      to={item.href}
                      onClick={() => setIsOpen(false)}
                      className={cn(
                        "text-lg font-serif text-stone-900 font-bold hover:text-[#004f3b] transition-colors flex items-center justify-between",
                        isChildActive && "text-[#004f3b]"
                      )}
                    >
                      <span>{item.name}</span>
                      <span className="text-xs uppercase tracking-widest font-sans font-normal text-stone-500 underline ml-2">({lang === 'en' ? 'Overview' : 'Oversigt'})</span>
                    </Link>
                  ) : (
                    <span
                      className={cn(
                        "text-lg font-serif text-stone-900 font-bold",
                        isChildActive && "text-[#004f3b]"
                      )}
                    >
                      {item.name}
                    </span>
                  )}
                  <div className="pl-4 flex flex-col space-y-3 border-l border-stone-100">
                    {item.children.map((child) => {
                      const isSelected = (pathname + hash) === child.href || (pathname === child.href && !hash);
                      return (
                        <div key={child.name} className="flex flex-col space-y-1.5">
                          <Link
                            to={child.href}
                            onClick={() => {
                              setIsOpen(false);
                              if (child.href.includes('#')) {
                                const [, h] = child.href.split('#');
                                if (h) {
                                  setTimeout(() => {
                                    const el = document.getElementById(h);
                                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                                  }, 150);
                                }
                              }
                            }}
                            className={cn(
                              "text-base font-serif transition-colors",
                              isSelected ? "text-[#004f3b] font-bold" : "text-stone-700 hover:text-stone-900 font-medium"
                            )}
                          >
                            {child.name}
                          </Link>
                          {child.subChildren && (
                            <div className="pl-3 flex flex-col space-y-2 border-l border-[#817262]/40 my-1 py-1">
                              {child.subChildren.map((sub) => {
                                const isSubSelected = (pathname + hash) === sub.href;
                                return (
                                  <Link
                                    key={sub.name}
                                    to={sub.href}
                                    onClick={() => setIsOpen(false)}
                                    className={cn(
                                      "text-sm font-sans transition-colors",
                                      isSubSelected ? "text-[#004f3b] font-bold" : "text-stone-600 hover:text-stone-900"
                                    )}
                                  >
                                    {sub.name}
                                  </Link>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }

            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setIsOpen(false)}
                className={cn(
                  "text-lg font-serif text-stone-900",
                  pathname === item.href && "text-[#004f3b] font-bold"
                )}
              >
                {item.name}
              </Link>
            );
          })}

          <div className="pt-4 border-t border-stone-100 flex flex-col gap-3">
            <a
              href="tel:+4527507083"
              className="w-full text-center bg-[#817262] text-white py-3 text-xs uppercase tracking-[0.2em] font-medium flex items-center justify-center gap-2 rounded-xs shadow-xs hover:bg-[#6c5e50] transition-colors"
            >
              <i className="fa-solid fa-phone text-xs" />
              <span>{lang === 'en' ? "Call: +45 27 50 70 83" : "Ring op: 27 50 70 83"}</span>
            </a>
            <Link
              to={contactPath}
              onClick={() => setIsOpen(false)}
              className="w-full text-center border border-stone-900 py-3 text-xs uppercase tracking-[0.2em] font-medium"
            >
              {kontaktText}
            </Link>
            <div className="flex justify-between items-center bg-stone-50 p-3 rounded-lg">
              <span className="text-xs font-medium uppercase tracking-wider text-stone-600">Sprog / Language</span>
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

const Footer = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('');
  const { lang, t } = useLanguage();
  const { navigation } = useCms();
  const navItems = getNavLinks(lang, navigation);

  const privacyPath = lang === 'en' ? '/en/privacy-policy' : '/privatlivspolitik';

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setStatus('loading');
    
    const formData = new FormData(e.target);
    const honey = formData.get('website_url');
    if (honey) {
      setStatus('success');
      setEmail('');
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: 'Tilmelding til nyhedsbrev',
          email: email,
          message: email,
          website_url: ''
        }),
      });
      if (response.ok) {
        setStatus('success');
        setEmail('');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <footer className="bg-stone-900 text-white pt-20 pb-12 px-6 border-t border-stone-800">
      <div className="max-w-7xl mx-auto space-y-16">
        {/* Top section: Brand, Columns, Newsletter */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8 items-start">
          
          {/* Brand & Intro (col 1-4) */}
          <div className="lg:col-span-4 space-y-6 text-center md:text-left">
            <div className="text-stone-400 text-xs sm:text-sm font-light leading-relaxed max-w-sm mx-auto md:mx-0 space-y-2">
              <p className="font-serif text-stone-200 text-sm sm:text-base font-normal">
                {lang === 'en' 
                  ? 'Are you ready for spiritual activation and deep personal growth?' 
                  : 'Er du klar til spirituel aktivering og dyb personlig udvikling?'}
              </p>
              <p>
                {lang === 'en'
                  ? 'I work with you who want to strengthen the connection to your soul, develop your intuition, heal what holds you back, and unfold more of the potential you are here with.'
                  : 'Jeg arbejder med dig, der vil styrke forbindelsen til din sjæl, udvikle din intuition, heale det, der holder dig tilbage, og udfolde mere af det potentiale, du er her med.'}
              </p>
            </div>

            {/* Direct Contact info */}
            <div className="pt-2 space-y-2.5 text-xs text-stone-300">
              <div>
                <a 
                  href="tel:+4527507083" 
                  className="inline-flex items-center gap-2.5 text-white hover:text-[#c7b9aa] transition-colors font-medium"
                >
                  <i className="fa-solid fa-phone text-[#817262] text-xs" />
                  <span>27 50 70 83</span>
                </a>
              </div>
              <div>
                <a 
                  href="mailto:mette@mettebrudvig.dk" 
                  className="inline-flex items-center gap-2.5 hover:text-white transition-colors"
                >
                  <i className="fa-solid fa-envelope text-[#817262] text-xs" />
                  <span>mette@mettebrudvig.dk</span>
                </a>
              </div>
            </div>

            {/* Social icons */}
            <div className="flex items-center justify-center md:justify-start gap-3 pt-2">
              <a 
                href="https://www.instagram.com/mettebrudvig/" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="w-8 h-8 rounded-full bg-stone-800 hover:bg-[#817262] text-stone-300 hover:text-white flex items-center justify-center text-xs transition-colors"
              >
                <i className="fa-brands fa-instagram" />
              </a>
              <a 
                href="https://www.facebook.com/mettebrudvighjertetstidsalder" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="w-8 h-8 rounded-full bg-stone-800 hover:bg-[#817262] text-stone-300 hover:text-white flex items-center justify-center text-xs transition-colors"
              >
                <i className="fa-brands fa-facebook" />
              </a>
              <a 
                href="https://www.linkedin.com/in/mette-brudvig-b6200a12/" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="w-8 h-8 rounded-full bg-stone-800 hover:bg-[#817262] text-stone-300 hover:text-white flex items-center justify-center text-xs transition-colors"
              >
                <i className="fa-brands fa-linkedin" />
              </a>
              <a 
                href="https://open.spotify.com/show/3w5L5y0dHLNa0FfIr6PVsL?si=e41e2a3acacf4535" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Spotify"
                className="w-8 h-8 rounded-full bg-stone-800 hover:bg-[#817262] text-stone-300 hover:text-white flex items-center justify-center text-xs transition-colors"
              >
                <i className="fa-brands fa-spotify" />
              </a>
              <a 
                href="https://www.buzzsprout.com/2530331/episodes" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Podcast"
                className="w-8 h-8 rounded-full bg-stone-800 hover:bg-[#817262] text-stone-300 hover:text-white flex items-center justify-center text-xs transition-colors"
              >
                <i className="fa-solid fa-podcast" />
              </a>
            </div>
          </div>

          {/* Navigation link columns (col 5-8) */}
          <div className="lg:col-span-4 grid grid-cols-2 gap-6 sm:gap-8 text-left">
            {/* Column 1: Forløb, Kurser & Retreats */}
            <div className="space-y-4">
              <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#c7b9aa] block">
                {lang === 'en' ? 'Programs & Retreats' : 'Uddannelse & Kurser'}
              </span>
              <ul className="space-y-2.5 text-xs text-stone-300">
                <li>
                  <Link to={lang === 'en' ? '/en/unleash-yourself' : '/frisaet-dig'} className="hover:text-white transition-colors">
                    {lang === 'en' ? 'Unleash Yourself' : 'Frisæt dig'}
                  </Link>
                </li>
                <li>
                  <Link to={lang === 'en' ? '/en/unleash-yourself/physical-program' : '/frisaet-dig/fysisk-forloeb'} className="hover:text-white transition-colors">
                    {lang === 'en' ? '1-Year Physical Program' : 'Fysisk årsuddannelse'}
                  </Link>
                </li>
                <li>
                  <Link to={lang === 'en' ? '/en/unleash-yourself/workshop' : '/frisaet-dig/workshop'} className="hover:text-white transition-colors">
                    {lang === 'en' ? '1-Day Workshop' : 'Endagsworkshop'}
                  </Link>
                </li>
                <li>
                  <Link to={lang === 'en' ? '/en/retreats' : '/retreats'} className="hover:text-white transition-colors">
                    Retreats
                  </Link>
                </li>
                <li>
                  <Link to={lang === 'en' ? '/en/sessions' : '/sessioner'} className="hover:text-white transition-colors">
                    {lang === 'en' ? 'Sessions & Mentoring' : 'Sessioner & Sparring'}
                  </Link>
                </li>
                <li>
                  <Link to={lang === 'en' ? '/en/free' : '/gratis'} className="hover:text-white transition-colors">
                    {lang === 'en' ? 'Free content & meditations' : 'Gratis meditationer'}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Column 2: Om, Inspiration & Indhold */}
            <div className="space-y-4">
              <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#c7b9aa] block">
                {lang === 'en' ? 'About & Media' : 'Om & Inspiration'}
              </span>
              <ul className="space-y-2.5 text-xs text-stone-300">
                <li>
                  <Link to={lang === 'en' ? '/en/about-mette' : '/om-mette'} className="hover:text-white transition-colors">
                    {lang === 'en' ? 'About Mette' : 'Om Mette'}
                  </Link>
                </li>
                <li>
                  <Link to={lang === 'en' ? '/en/about-mette/testimonials' : '/om-mette/udtalelser'} className="hover:text-white transition-colors">
                    {lang === 'en' ? 'Testimonials' : 'Udtalelser'}
                  </Link>
                </li>
                <li>
                  <Link to={lang === 'en' ? '/en/podcasts' : '/podcasts'} className="hover:text-white transition-colors">
                    Podcast
                  </Link>
                </li>
                <li>
                  <Link to={lang === 'en' ? '/en/blog' : '/blog'} className="hover:text-white transition-colors">
                    Blog
                  </Link>
                </li>
                <li>
                  <Link to={lang === 'en' ? '/en/foredrag' : '/foredrag'} className="hover:text-white transition-colors">
                    {lang === 'en' ? 'Keynotes & Talks' : 'Foredrag'}
                  </Link>
                </li>
                <li>
                  <Link to={lang === 'en' ? '/en/contact' : '/kontakt'} className="hover:text-white transition-colors">
                    {lang === 'en' ? 'Contact' : 'Kontakt'}
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Newsletter Signup (col 9-12) */}
          <div className="lg:col-span-4 bg-stone-800/50 p-6 sm:p-7 rounded-sm border border-stone-800 space-y-4">
            <div className="space-y-1.5">
              <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#c7b9aa] block">
                {lang === 'en' ? 'Newsletter' : 'Nyhedsbrev'}
              </span>
              <h3 className="text-base font-serif text-white">{t('newsletter.popupTitle')}</h3>
              <p className="text-stone-400 text-xs font-light leading-relaxed">
                {t('newsletter.popupDesc')}
              </p>
            </div>

            <form 
              id="signup" 
              action="https://e.lumant.dk/api/v1/optin/list_6v-RTZGckC/subscribe" 
              method="post" 
              className="space-y-2.5 pt-1"
            >
              <input type="hidden" name="ref" value="" />
              <div style={{ position: 'absolute', left: '-5000px' }} aria-hidden="true">
                <input type="text" name="hp_reference" tabIndex={-1} autoComplete="off" />
              </div>

              <div className="flex flex-col gap-2">
                <input 
                  type="text" 
                  name="name" 
                  placeholder={lang === 'en' ? "Your name" : "Dit navn"} 
                  className="bg-stone-900 border border-stone-700/80 rounded-sm px-3.5 py-2.5 text-xs text-white placeholder-stone-500 focus:border-[#817262] outline-none transition-all w-full"
                />
                <input 
                  type="email" 
                  name="email" 
                  placeholder={lang === 'en' ? "Your email" : "Din e-mail"} 
                  pattern="[^@\s]+@[^@\s]+\.[^@\s]+" 
                  required 
                  className="bg-stone-900 border border-stone-700/80 rounded-sm px-3.5 py-2.5 text-xs text-white placeholder-stone-500 focus:border-[#817262] outline-none transition-all w-full"
                />
                <input 
                  type="submit" 
                  name="submit" 
                  value={lang === 'en' ? "SUBSCRIBE NOW" : "TILMELD MIG NU"} 
                  className="w-full bg-[#817262] hover:bg-[#6e6153] text-white px-5 py-2.5 text-[11px] uppercase tracking-widest font-bold rounded-sm transition-all duration-300 cursor-pointer shadow-xs mt-0.5"
                />
              </div>
            </form>
          </div>

        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-stone-800/80 flex flex-col sm:flex-row justify-between items-center gap-4 text-stone-400 text-[11px] font-light">
          <div>
            © {new Date().getFullYear()} {SITE_NAME}. {lang === 'en' ? 'All rights reserved.' : 'Alle rettigheder forbeholdes.'}
          </div>
          <div className="flex items-center gap-6">
            <Link to={privacyPath} className="hover:text-stone-200 transition-colors">
              {t('nav.privacyPolicy')}
            </Link>
            <LanguageSwitcher variant="footer" />
          </div>
        </div>
      </div>
    </footer>
  );
};

// --- Pages ---

const HomePage = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isHjemmeside2Playing, setIsHjemmeside2Playing] = useState(false);
  const [isHjemmeside2LightboxOpen, setIsHjemmeside2LightboxOpen] = useState(false);
  const { lang, t } = useLanguage();
  const { testimonials, podcasts, forside } = useCms();

  const workshopsPath = lang === 'en' ? '/en/keynotes/private' : '/foredrag/privat';
  const contactPath = lang === 'en' ? '/en/contact' : '/kontakt';
  const aboutPath = lang === 'en' ? '/en/about-mette' : '/om-mette';
  const podcastsPath = lang === 'en' ? '/en/podcasts' : '/podcasts';
  const frisaetPath = lang === 'en' ? '/en/unleash-yourself' : '/frisaet-dig';

  const getVal = (field, fallback) => {
    if (!forside) return fallback;
    const val = forside[field];
    if (!val) return fallback;
    if (typeof val === 'object') {
      return val[lang] || val['da'] || fallback;
    }
    return val || fallback;
  };

  const getImg = (field, fallback) => {
    if (!forside) return fallback;
    const val = forside[field];
    if (Array.isArray(val) && val.length > 0) return val[0];
    if (typeof val === 'string' && val.trim()) return val;
    return fallback;
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsLightboxOpen(false);
        setIsHjemmeside2LightboxOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div>
      {/* Top Hero: Velkommen til Mette Brudvig / Velkommen til min hjemmeside */}
      <section className="relative min-h-[60vh] md:min-h-[70vh] pt-[185px] md:pt-[215px] lg:pt-[225px] pb-12 flex items-center bg-[#f5efe9] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center z-10 w-full">
          
          {/* Left Content Column */}
          <FadeIn direction="right">
            <h1 className="leading-[1.15] mb-6">
              <span className="text-sm md:text-base font-sans tracking-[0.25em] uppercase text-[#817262] font-medium block mb-2">
                {getVal('hero_badge', lang === 'en' ? 'Welcome to' : 'Velkommen til')}
              </span>
              <span className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-serif font-normal text-stone-900 block">
                {getVal('hero_titel', 'Mette Brudvig')}
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl font-serif text-stone-800 font-normal leading-snug mb-4 max-w-lg">
              {getVal('hero_tagline', lang === 'en'
                ? "Your path to personal liberation and inner leadership"
                : "Din vej til personlig frisættelse og indre lederskab")}
            </p>

            <p className="text-stone-600 font-normal text-base md:text-lg leading-relaxed max-w-lg mb-8">
              {getVal('hero_tekst', lang === 'en'
                ? "I help you return home to yourself, so you can fulfill your full potential and create the life you dream of."
                : "Jeg hjælper dig med at finde hjem til dig selv, så du kan udleve dit fulde potentiale og skabe det liv, du drømmer om.")}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to={frisaetPath} 
                className="inline-flex items-center justify-center space-x-3 bg-[#817262] text-white px-8 py-4 uppercase text-xs tracking-[0.2em] font-medium hover:bg-stone-800 transition-all shadow-md group rounded-xs"
              >
                <span>{t('home.ctaPrimary')}</span>
                <i className="fa-solid fa-arrow-right group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link 
                to={aboutPath} 
                className="inline-flex items-center justify-center border border-stone-800 px-8 py-4 uppercase text-xs tracking-[0.2em] text-stone-800 hover:bg-stone-900 hover:text-white transition-all font-medium rounded-xs"
              >
                {t('home.ctaSecondary')}
              </Link>
            </div>
          </FadeIn>

        </div>

        {/* Right Image Background Layer */}
        <div className="absolute top-0 right-0 w-full md:w-1/2 h-full opacity-20 md:opacity-100 pt-20 md:pt-24 lg:pt-28 pointer-events-none">
          <img 
            src={getImg('hero_billede', 'https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/mettebrudvigokt2021-111-1.jpg')} 
            alt="Mette Brudvig" 
            className="w-full h-full object-cover object-top pointer-events-none"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#f5efe9] via-transparent to-transparent hidden md:block pointer-events-none" />
        </div>
      </section>

      {/* Primary Offerings Showcase: Frisæt Dig, Retreats & 1:1 Sessioner */}
      <section className="py-20 md:py-28 px-6 bg-[#fbf9f6] overflow-hidden relative border-b border-stone-200/60">
        <div className="max-w-7xl mx-auto">
          {/* Grid: 4 kasser ved siden af videopræsentationen */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 items-stretch">
            {/* Venstre side: De 4 kasser i 2x2 grid */}
            <div className="lg:col-span-7 xl:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 items-stretch order-2 lg:order-1">
              {/* 1. FORLØB */}
              <FadeIn delay={100} className="bg-gradient-to-br from-[#fbf8f5] via-white to-[#f5efe9] p-5 sm:p-6 rounded-2xl border-2 border-[#817262] shadow-md shadow-[#817262]/10 flex flex-col justify-between h-full group hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-28 h-28 bg-[#817262]/10 -z-0 rounded-bl-full opacity-80 pointer-events-none" />
                <div className="space-y-3 relative z-10 flex-1 flex flex-col">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[11px] uppercase tracking-widest font-bold text-[#817262]">
                      {getVal('kasse_forloeb_badge', lang === 'en' ? 'Programs' : 'Forløb')}
                    </span>
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden border-2 border-white shadow-md relative z-20 shrink-0 group-hover:scale-105 transition-transform duration-300 bg-stone-100">
                      <img 
                        src={getImg('kasse_forloeb_billede', 'https://assets.auxodev.dk/cdn-cgi/image/width=200,height=200,fit=cover,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/image_14.png')}
                        alt={getVal('kasse_forloeb_titel', lang === 'en' ? 'Programs' : 'Frisæt dig forløb')}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl sm:text-2xl font-serif font-medium text-stone-800 leading-snug">
                      {getVal('kasse_forloeb_titel', lang === 'en' ? 'Unleash Yourself & Programs' : 'Frisæt dig forløb')}
                    </h3>
                    <p className="text-xs italic text-[#817262] font-serif mt-1 mb-2">
                      {getVal('kasse_forloeb_undertitel', lang === 'en' ? 'Physical 1-year program & Online course' : 'Fysisk årsforløb & Online forløb')}
                    </p>
                    <p className="text-stone-700 font-light text-xs sm:text-sm leading-relaxed">
                      {getVal('kasse_forloeb_tekst', lang === 'en'
                        ? 'A transformative 1-year program in Copenhagen, Aarhus, or Live Online. Designed for you who wish to live with courage, trust, and deep soul alignment.'
                        : 'Et dybt transformerende årsforløb i København, Århus eller Live Online. Til dig der vil leve med mod, tillid og dyb sjælsforbindelse.')}
                    </p>
                  </div>
                </div>
                <div className="mt-5 pt-3 relative z-10 w-full">
                  <Link 
                    to={frisaetPath}
                    className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#817262] text-white uppercase text-[11px] tracking-widest font-bold hover:bg-stone-900 transition-all rounded-xl shadow-md hover:shadow-lg w-full transform hover:scale-[1.02] text-center"
                  >
                    <span>{getVal('kasse_forloeb_knap', lang === 'en' ? 'Read full details' : 'Læs alt om forløbet')}</span>
                    <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </Link>
                </div>
              </FadeIn>

              {/* 2. KURSUS */}
              <FadeIn delay={200} className="bg-white p-5 sm:p-6 rounded-2xl border border-stone-200/90 shadow-xs flex flex-col justify-between h-full group hover:border-[#817262]/60 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 relative overflow-hidden">
                <div className="space-y-3 relative z-10 flex-1 flex flex-col">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[11px] uppercase tracking-widest font-bold text-stone-500">
                      {getVal('kasse_workshop_badge', lang === 'en' ? 'Courses' : 'Kursus')}
                    </span>
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden border-2 border-white shadow-md relative z-20 shrink-0 group-hover:scale-105 transition-transform duration-300 bg-stone-100">
                      <img 
                        src={getImg('kasse_workshop_billede', 'https://assets.auxodev.dk/cdn-cgi/image/width=200,height=200,fit=cover,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/mettebrudvigokt2021-124_1.jpg')}
                        alt={getVal('kasse_workshop_titel', lang === 'en' ? 'Courses' : 'Kurser')}
                        className="w-full h-full object-cover object-center"
                      />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl sm:text-2xl font-serif font-medium text-stone-800 leading-snug">
                      {getVal('kasse_workshop_titel', lang === 'en' ? 'Courses' : 'Kurser')}
                    </h3>
                    <p className="text-xs italic text-[#817262] font-serif mt-1 mb-2">
                      {getVal('kasse_workshop_undertitel', lang === 'en' ? 'Unleash Yourself 1-day courses & WOMAN – Know Your Power' : 'Frisæt dig endagskurser & Kvinde – kend din power')}
                    </p>
                    <p className="text-stone-600 font-light text-xs sm:text-sm leading-relaxed">
                      {getVal('kasse_workshop_tekst', lang === 'en'
                        ? 'Intensive courses with teaching, meditation, reflection, and exercises. Ground yourself in your core and reclaim your authentic inner strength.'
                        : 'Intensive kurser med undervisning, meditation, refleksion og øvelser. Mærk din kerne, genfind din styrke og skab dyb indre forandring.')}
                    </p>
                  </div>
                </div>
                <div className="mt-5 pt-3 relative z-10 w-full">
                  <Link 
                    to={lang === 'en' ? '/en/unleash-yourself/workshop' : '/frisaet-dig/workshop'}
                    className="inline-flex items-center justify-center gap-2 px-4 py-3 border border-[#817262] text-[#817262] uppercase text-[11px] tracking-widest font-bold hover:bg-[#817262] hover:text-white transition-colors rounded-xl shadow-xs w-full text-center"
                  >
                    <span>{getVal('kasse_workshop_knap', lang === 'en' ? 'Explore Courses' : 'Se Kurser')}</span>
                    <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </Link>
                </div>
              </FadeIn>

              {/* 3. SESSIONER */}
              <FadeIn delay={300} className="bg-white p-5 sm:p-6 rounded-2xl border border-stone-200/90 shadow-xs flex flex-col justify-between h-full group hover:border-[#817262]/60 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 relative overflow-hidden">
                <div className="space-y-3 relative z-10 flex-1 flex flex-col">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[11px] uppercase tracking-widest font-bold text-stone-500">
                      {getVal('kasse_sessioner_badge', lang === 'en' ? '1:1 Sessions' : 'Sessioner')}
                    </span>
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden border-2 border-white shadow-md relative z-20 shrink-0 group-hover:scale-105 transition-transform duration-300 bg-stone-100">
                      <img 
                        src={getImg('kasse_sessioner_billede', 'https://assets.auxodev.dk/cdn-cgi/image/width=200,height=200,fit=cover,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/billede-til-merete.jpg')}
                        alt={getVal('kasse_sessioner_titel', lang === 'en' ? '1:1 Sessions' : '1:1 Sessioner')}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl sm:text-2xl font-serif font-medium text-stone-800 leading-snug">
                      {getVal('kasse_sessioner_titel', lang === 'en' ? '1:1 Sessions' : '1:1 Sessioner')}
                    </h3>
                    <p className="text-xs italic text-[#817262] font-serif mt-1 mb-2">
                      {getVal('kasse_sessioner_undertitel', lang === 'en' ? 'Clairvoyance, Mentoring & Leadership' : 'Clairvoyant rådgivning & spirituel mentoring')}
                    </p>
                    <p className="text-stone-600 font-light text-xs sm:text-sm leading-relaxed">
                      {getVal('kasse_sessioner_tekst', lang === 'en'
                        ? 'Personal guidance and mentoring designed to clarify your direction, dissolve blockages, and feed directly into your growth journeys.'
                        : 'Fortrolige og nærværende individuelle sessioner, der skaber klarhed, retning og opløser begrænsninger – ideelt som trædesten til forløb og retreats.')}
                    </p>
                  </div>
                </div>
                <div className="mt-5 pt-3 relative z-10 w-full">
                  <Link 
                    to={lang === 'en' ? '/en/sessions' : '/sessioner'}
                    className="inline-flex items-center justify-center gap-2 px-4 py-3 border border-stone-800 text-stone-800 uppercase text-[11px] tracking-widest font-bold hover:bg-stone-900 hover:text-white transition-colors rounded-xl shadow-xs w-full text-center"
                  >
                    <span>{getVal('kasse_sessioner_knap', lang === 'en' ? 'View 1:1 Sessions' : 'Se 1:1 Sessioner')}</span>
                    <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </Link>
                </div>
              </FadeIn>

              {/* 4. RETREAT */}
              <FadeIn delay={400} className="bg-white p-5 sm:p-6 rounded-2xl border border-stone-200/90 shadow-xs flex flex-col justify-between h-full group hover:border-[#817262]/60 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 relative overflow-hidden">
                <div className="space-y-3 relative z-10 flex-1 flex flex-col">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[11px] uppercase tracking-widest font-bold text-stone-500">
                      {getVal('kasse_retreat_badge', lang === 'en' ? 'Retreats' : 'Retreat')}
                    </span>
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden border-2 border-white shadow-md relative z-20 shrink-0 group-hover:scale-105 transition-transform duration-300 bg-stone-100">
                      <img 
                        src={getImg('kasse_retreat_billede', 'https://assets.auxodev.dk/cdn-cgi/image/width=200,height=200,fit=cover,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/montserrat-retreat.jpg')}
                        alt={getVal('kasse_retreat_titel', lang === 'en' ? 'Retreats' : 'Retreats')}
                        className="w-full h-full object-cover object-center"
                      />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl sm:text-2xl font-serif font-medium text-stone-800 leading-snug">
                      {getVal('kasse_retreat_titel', lang === 'en' ? 'Retreats' : 'Retreats')}
                    </h3>
                    <p className="text-xs italic text-[#817262] font-serif mt-1 mb-2">
                      {getVal('kasse_retreat_undertitel', lang === 'en' ? 'Sacred Montserrat retreat, Costa del Sol & Thailand' : 'Helligt retreat til Montserrat, Costa del Sol & Thailand')}
                    </p>
                    <p className="text-stone-600 font-light text-xs sm:text-sm leading-relaxed">
                      {getVal('kasse_retreat_tekst', lang === 'en'
                        ? 'Exclusive and immersive retreats in magical, powerful landscapes. Step away from daily noise to recharge, find inner stillness, and deepen your presence.'
                        : 'Eksklusive og fordybende retreats i magiske, kraftfulde omgivelser. Træd væk fra hverdagens støj og genfind din indre ro, energi og sjælsforbindelse.')}
                    </p>
                  </div>
                </div>
                <div className="mt-5 pt-3 relative z-10 w-full">
                  <Link 
                    to={lang === 'en' ? '/en/retreats' : '/retreats'}
                    className="inline-flex items-center justify-center gap-2 px-4 py-3 border border-[#817262] text-[#817262] uppercase text-[11px] tracking-widest font-bold hover:bg-[#817262] hover:text-white transition-colors rounded-xl shadow-xs w-full text-center"
                  >
                    <span>{getVal('kasse_retreat_knap', lang === 'en' ? 'Explore Retreats' : 'Udforsk Retreats')}</span>
                    <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </Link>
                </div>
              </FadeIn>
            </div>

            {/* Højre side: Video præsentation */}
            <div className="lg:col-span-5 xl:col-span-4 flex order-1 lg:order-2">
              <FadeIn delay={200} className="w-full flex">
                <div className="bg-white p-6 sm:p-8 rounded-3xl border border-[#817262]/25 shadow-lg flex flex-col items-center justify-center text-center relative overflow-hidden w-full h-full">
                  <div className="absolute top-0 right-0 w-36 h-36 bg-[#f5efe9] -z-0 rounded-bl-full opacity-60 pointer-events-none" />
                  <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-[#817262] mb-4 relative z-10">
                    <i className="fa-solid fa-circle-play text-sm" aria-hidden="true" />
                    <span>{getVal('rejse_video_titel', lang === 'en' ? 'Watch Video Presentation' : 'Video Præsentation')}</span>
                  </div>
                  
                  <div className="w-full max-w-[260px] sm:max-w-[280px] mx-auto relative z-10">
                    <YouTubeEmbed 
                      url={forside?.rejse_video_url || "https://youtube.com/shorts/huZK8V_g4Kw?feature=share"} 
                      title={getVal('rejse_video_undertitel', lang === 'en' ? "Welcome to my website" : "Velkommen til min hjemmeside")} 
                      className="shadow-xl relative z-10 w-full rounded-2xl border border-[#817262]/30 max-w-full overflow-hidden"
                    />
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Preview */}
      <section className="py-32 px-6 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <FadeIn className="mb-20">
            <h2 className="text-4xl font-serif max-w-2xl mb-8 leading-tight">
              {getVal('udtalelser_overskrift', t('home.testimonialsHeading'))}
            </h2>
            <p className="text-stone-500 italic">
              {getVal('udtalelser_undertitel', t('home.testimonialsSub'))}
            </p>
          </FadeIn>
          
          <div className="mb-16">
            <HomeTestimonialsSlider testimonials={(testimonials || TESTIMONIALS).slice(0, 4)} isEn={lang === 'en'} />
          </div>
          <FadeIn className="text-center">
            <Link to={aboutPath} className="inline-flex items-center space-x-4 group text-[#004f3b] uppercase text-xs tracking-[0.2em] font-bold">
              <span>{t('home.testimonialsCta')}</span>
              <ArrowRight size={16} className="group-hover:translate-x-2 transition-transform" />
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* Hvem er jeg Section - New Editorial Layout */}
      <section className="py-24 md:py-32 bg-stone-50 px-6 overflow-hidden border-t border-b border-stone-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            {/* Image Column (Left) */}
            <div className="lg:col-span-5 relative">
              <FadeIn direction="right">
                <div className="relative aspect-[4/5] overflow-hidden shadow-2xl rounded-sm bg-stone-100 group">
                  <img 
                    src={getImg('hvem_billede', 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/image_3.png')} 
                    alt="Mette Brudvig Portræt" 
                    className="w-full h-full object-cover object-top transition-transform duration-1000 group-hover:scale-105"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#817262]/10 -z-10 rounded-full blur-2xl" />
              </FadeIn>
            </div>

            {/* Content Column (Right) */}
            <div className="lg:col-span-7">
              <FadeIn direction="left">
                <h2 className="text-4xl md:text-5xl font-serif text-stone-900 mb-8">
                  {getVal('hvem_overskrift', t('home.whoHeading'))}
                </h2>
                <div className="space-y-6 text-stone-600 font-light leading-relaxed text-lg">
                  {forside?.hvem_tekst ? (
                    <div 
                      className="cms-rich-text text-stone-600 font-light leading-relaxed text-lg space-y-6"
                      dangerouslySetInnerHTML={{ 
                        __html: typeof forside.hvem_tekst === 'object' 
                          ? (forside.hvem_tekst[lang] || forside.hvem_tekst.da || '') 
                          : forside.hvem_tekst 
                      }}
                    />
                  ) : (
                    <>
                      <p>{t('home.whoP1')}</p>
                      <p>{t('home.whoP2')}</p>
                      <p>{t('home.whoP3')}</p>
                      <p className="font-medium text-stone-800 italic">{t('home.whoP4')}</p>
                      <p>{t('home.whoP5')}</p>
                    </>
                  )}
                  <div className="pt-6 flex flex-wrap items-center gap-6">
                    <Link to={contactPath} className="inline-flex items-center space-x-2 bg-[#004f3b] text-white px-6 py-3 uppercase text-xs tracking-[0.2em] hover:bg-emerald-900 transition-all text-center font-medium shadow-xs rounded-sm">
                      <span>{t('home.whoCta')}</span>
                      <i className="fa-light fa-arrow-right" />
                    </Link>
                    <Link to={aboutPath} className="inline-flex items-center space-x-2 bg-[#817262] text-white px-6 py-3 uppercase text-xs tracking-[0.2em] hover:bg-stone-800 transition-all text-center font-medium shadow-xs">
                      <span>{t('nav.omMette')}</span>
                      <i className="fa-light fa-arrow-right" />
                    </Link>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Podcast CTA Section */}
      <section className="py-32 px-6 bg-stone-900 text-white">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <h2 className="text-4xl font-serif text-center mb-20 tracking-tight">
              {getVal('podcast_overskrift', t('home.podcastHeading'))}
            </h2>
          </FadeIn>
          <div className="grid md:grid-cols-2 gap-12 lg:gap-20">
            {(podcasts || PODCASTS).map((pod, idx) => {
              const isHighlighted = pod.title === "Forbundet i den nye tid";
              const title = lang === 'en' ? (idx === 0 ? "Connected in the New Era" : "With the Heart First") : pod.title;
              const description = lang === 'en' ? (idx === 0 ? "A spiritual podcast about spiritual awakening, the spiritual world, and quantum science backing it up." : "Deep conversations on spiritual awakening and using quantum physics for personal growth and living in trust.") : pod.description;
              return (
                <FadeIn key={idx} delay={idx * 200}>
                  <div 
                    className={`group block space-y-8 p-6 rounded-sm transition-all duration-500 ${
                      isHighlighted 
                        ? 'bg-stone-800/40 border border-[#817262]/35 shadow-lg relative' 
                        : ''
                    }`}
                  >
                    <a
                      href={pod.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative aspect-square overflow-hidden rounded-sm bg-stone-100 block"
                    >
                      {isHighlighted && (
                        <div className="absolute top-4 left-4 bg-[#817262] text-white text-[9px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 rounded-sm shadow-md z-10 flex items-center gap-1.5">
                          <i className="fa-solid fa-star text-[8px]" />
                          <span>{t('home.podcastBadge')}</span>
                        </div>
                      )}
                      <img 
                        src={pod.image} 
                        alt={title} 
                        className="w-full h-full object-cover opacity-100 group-hover:scale-105 transition-all duration-700" 
                      />
                      <div className="absolute inset-0 bg-stone-900/20 group-hover:bg-transparent transition-colors duration-500" />
                      <div className="absolute bottom-8 right-8 w-14 h-14 bg-white rounded-full flex items-center justify-center text-stone-900 shadow-xl translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                        <Play size={20} fill="currentColor" />
                      </div>
                    </a>
                    <div className="space-y-3">
                      <h3 className="text-2xl font-serif flex items-center gap-2.5">
                        <a href={pod.link} target="_blank" rel="noopener noreferrer" className="hover:text-[#817262] transition-colors">
                          {title}
                        </a>
                        {isHighlighted && (
                          <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full bg-[#817262]/20 text-[#817262] text-[9px] uppercase tracking-wider font-sans font-bold">
                            {t('home.podcastFeatured')}
                          </span>
                        )}
                      </h3>
                      <p className="text-stone-400 font-light text-sm leading-relaxed max-w-md">
                        {description}
                      </p>

                      {/* Fremhævet nyeste episode link */}
                      {isHighlighted && (
                        <div className="pt-2 pb-1">
                          <Link
                            to={lang === 'en' ? '/en/podcasts#lars-muhl' : '/podcasts#lars-muhl'}
                            className="inline-flex items-center gap-2.5 px-3.5 py-2 rounded-xs bg-[#817262]/15 hover:bg-[#817262]/25 border border-[#817262]/30 text-xs text-stone-200 hover:text-white transition-all duration-300 group/ep"
                          >
                            <span className="inline-block w-2 h-2 rounded-full bg-[#817262] animate-pulse" />
                            <span className="font-medium text-[#c2b19e] group-hover/ep:text-white">
                              {lang === 'en' ? 'Latest episode:' : 'Nyeste episode:'}
                            </span>
                            <span className="truncate max-w-[210px] sm:max-w-[260px] text-stone-100 font-serif italic">
                              Lars Muhl
                            </span>
                            <ArrowRight size={13} className="text-[#817262] group-hover/ep:translate-x-1 group-hover/ep:text-white transition-all shrink-0 ml-auto" />
                          </Link>
                        </div>
                      )}

                      <div className="pt-2 flex flex-wrap items-center gap-4 text-[10px] uppercase tracking-[0.2em] font-bold text-stone-400">
                        <a 
                          href={pod.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-white transition-colors flex items-center gap-1.5"
                        >
                          <i className="fa-brands fa-spotify text-sm text-[#1DB954]" />
                          <span>Spotify</span>
                        </a>
                        <span className="text-stone-600">•</span>
                        <a
                          href={pod.buzzsproutLink || "https://www.buzzsprout.com/2530331/episodes"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-white transition-colors flex items-center gap-1.5 text-stone-300 hover:underline"
                        >
                          <i className="fa-solid fa-podcast text-sm text-[#817262]" />
                          <span>Buzzsprout</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* Retreats Introduction Section on Homepage */}
      <section className="py-16 md:py-24 px-4 sm:px-6 bg-[#f5efe9] border-t border-[#817262]/15 overflow-hidden relative">
        {/* Background Image with soft opacity and warm gradient overlay */}
        <div className="absolute inset-0 pointer-events-none">
          <img
            src={getImg('retreats_baggrundsbillede', 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/sk-c3-a6rmbillede-2026-07-01-kl-22-00-02_2.png')}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#f5efe9]/80 via-[#f5efe9]/50 to-[#f5efe9]/90" />
        </div>

        <div className="max-w-6xl mx-auto space-y-10 relative z-10">
          <FadeIn className="space-y-3.5 max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-serif text-stone-900 leading-tight">
              {getVal('retreats_sektion_overskrift', t('nav.retreats'))}
            </h2>
            <div className="h-0.5 w-12 bg-[#817262] mx-auto rounded-full opacity-80" />
            <p className="text-stone-700 font-light text-base md:text-lg leading-relaxed pt-1">
              {getVal('retreats_sektion_beskrivelse', lang === 'en' 
                ? "Join a retreat and create space for reflection, stillness, and spiritual renewal in beautiful, serene surroundings."
                : "Tag med på retreat og skab rum til fordybelse, ro og sjælelig opvågning i smukke, nærværende omgivelser.")}
            </p>
          </FadeIn>

          {/* 1 outer box containing 3 Retreats on the Left, Video on the Right */}
          <FadeIn delay={100} className="max-w-5xl mx-auto">
            <div className="bg-white/95 backdrop-blur-xs rounded-none p-6 sm:p-8 md:p-10 border border-[#817262]/20 shadow-xs">
              <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
                {/* Left Column: 3 Retreat Cards in 1 Vertical Column without images */}
                <div className="lg:col-span-7 flex flex-col justify-center">
                  <div className="flex flex-col gap-4 text-left">
                    {/* Card 1: Montserrat */}
                    <Link
                      to={lang === 'en' ? "/en/montserrat" : "/montserrat"}
                      className="group bg-[#f5efe9]/60 hover:bg-[#f5efe9] border border-[#817262]/15 hover:border-[#817262]/40 p-5 transition-all duration-300 flex flex-col justify-between space-y-3 shadow-xs hover:shadow-sm"
                    >
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="bg-[#817262]/10 text-[#817262] text-[10px] uppercase font-bold tracking-widest px-2.5 py-1 rounded-full border border-[#817262]/20">
                            Retreat 1
                          </span>
                        </div>
                        <h3 className="text-xl font-serif text-stone-900 group-hover:text-[#817262] transition-colors">
                          Montserrat
                        </h3>
                        <p className="text-stone-600 font-light text-sm leading-relaxed">
                          {lang === 'en'
                            ? "6 days of immersion, grounding, and spiritual awakening in Catalonia's sacred mountains."
                            : "6 dage med fordybelse, forankring og sjælelig opvågning i Cataloniens hellige bjerge."}
                        </p>
                      </div>
                      <div className="pt-2.5 border-t border-[#817262]/15 flex items-center justify-between text-xs uppercase tracking-[0.2em] font-semibold text-[#817262]">
                        <span>{lang === 'en' ? "Read more" : "Læs mere"}</span>
                        <i className="fa-light fa-arrow-right group-hover:translate-x-1.5 transition-transform duration-300 text-[#817262]" />
                      </div>
                    </Link>

                    {/* Card 2: Costa del Sol */}
                    <Link
                      to={lang === 'en' ? "/en/costa-del-sol" : "/costa-del-sol"}
                      className="group bg-[#f5efe9]/60 hover:bg-[#f5efe9] border border-[#817262]/15 hover:border-[#817262]/40 p-5 transition-all duration-300 flex flex-col justify-between space-y-3 shadow-xs hover:shadow-sm"
                    >
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="bg-[#817262]/10 text-[#817262] text-[10px] uppercase font-bold tracking-widest px-2.5 py-1 rounded-full border border-[#817262]/20">
                            Retreat 2
                          </span>
                        </div>
                        <h3 className="text-xl font-serif text-stone-900 group-hover:text-[#817262] transition-colors">
                          {lang === 'en' ? "Spiritual Retreat on Costa del Sol" : "Retreat på Costa del Sol"}
                        </h3>
                        <p className="text-stone-600 font-light text-sm leading-relaxed">
                          {lang === 'en'
                            ? "5 days from October 6th–11th in a lush Andalusian paradise."
                            : "5 dage fra den 6.–11. oktober i et frodigt andalusisk paradis."}
                        </p>
                      </div>
                      <div className="pt-2.5 border-t border-[#817262]/15 flex items-center justify-between text-xs uppercase tracking-[0.2em] font-semibold text-[#817262]">
                        <span>{lang === 'en' ? "Read more" : "Læs mere"}</span>
                        <i className="fa-light fa-arrow-right group-hover:translate-x-1.5 transition-transform duration-300 text-[#817262]" />
                      </div>
                    </Link>

                    {/* Card 3: Spirituelt retreat i Thailand */}
                    <Link
                      to={lang === 'en' ? "/en/thailand-retreat" : "/thailand-retreat"}
                      className="group bg-[#f5efe9]/60 hover:bg-[#f5efe9] border border-[#817262]/15 hover:border-[#817262]/40 p-5 transition-all duration-300 flex flex-col justify-between space-y-3 shadow-xs hover:shadow-sm"
                    >
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="bg-[#817262]/10 text-[#817262] text-[10px] uppercase font-bold tracking-widest px-2.5 py-1 rounded-full border border-[#817262]/20">
                            Retreat 3
                          </span>
                        </div>
                        <h3 className="text-xl font-serif text-stone-900 group-hover:text-[#817262] transition-colors">
                          {lang === 'en' ? "Spiritual Retreat in Thailand" : "Spirituelt retreat i Thailand"}
                        </h3>
                        <p className="text-stone-600 font-light text-sm leading-relaxed">
                          {lang === 'en'
                            ? "10 days of immersion, gentle yoga, TRE, sound healing, and tropical nature."
                            : "10 dage med fordybelse, blid yoga, TRE, lydhealing og tropisk natur."}
                        </p>
                      </div>
                      <div className="pt-2.5 border-t border-[#817262]/15 flex items-center justify-between text-xs uppercase tracking-[0.2em] font-semibold text-[#817262]">
                        <span>{lang === 'en' ? "Read more" : "Læs mere"}</span>
                        <i className="fa-light fa-arrow-right group-hover:translate-x-1.5 transition-transform duration-300 text-[#817262]" />
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Right Column: Video Embed */}
                <div className="lg:col-span-5 flex items-center justify-center">
                  <div className="w-full max-w-[340px] sm:max-w-[360px] flex items-center justify-center">
                    <YouTubeEmbed 
                      url={forside?.retreats_video_url || "https://youtube.com/shorts/-GWn3JeJ8Q8?feature=share"} 
                      title={lang === 'en' ? "Intro to retreats" : "Intro til retreats"} 
                    />
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={190} className="pt-2 text-center">
            <Link 
              to={lang === 'en' ? "/en/retreats" : "/retreats"} 
              className="inline-flex items-center space-x-2.5 bg-[#817262] text-white px-8 py-3.5 uppercase text-xs tracking-[0.2em] font-medium hover:bg-stone-800 transition-all shadow-xs hover:shadow-md hover:-translate-y-0.5 duration-200 group"
            >
              <span>{getVal('retreats_knap_alle', lang === 'en' ? "See all retreats" : "Se alle retreats")}</span>
              <i className="fa-light fa-arrow-right group-hover:translate-x-1 transition-transform" />
            </Link>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

const SessionerPage = () => {
  const { lang, t } = useLanguage();
  const { sessions } = useCms();

  useEffect(() => {
    document.title = lang === 'en' ? "Mette Brudvig - 1:1 Sessions" : "Mette Brudvig - 1:1 Sessioner";
    window.scrollTo(0, 0);
  }, [lang]);

  const isEn = lang === 'en';

  const session1 = sessions?.find(s => (s.url_slug?.da || s.url_slug) === 'clairvoyant-raadgivning' || (s.url_slug?.en || s.url_slug) === 'clairvoyant-mentoring');
  const session2 = sessions?.find(s => (s.url_slug?.da || s.url_slug) === 'ledersparring' || (s.url_slug?.en || s.url_slug) === 'leadership-mentoring');
  const session3 = sessions?.find(s => (s.url_slug?.da || s.url_slug) === 'mentorforloeb' || (s.url_slug?.en || s.url_slug) === 'mentor-program');

  return (
    <div className="bg-[#faf9f6] min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] md:min-h-[70vh] pt-[185px] md:pt-[215px] lg:pt-[225px] pb-12 flex items-center bg-[#f5efe9] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center z-10 w-full">
          <FadeIn direction="right">
            <h1 className="text-4xl md:text-6xl font-serif text-stone-900 leading-[1.1] mb-6">
              {t('sessionerPage.title')}
            </h1>
            <p className="text-lg md:text-xl text-stone-700 font-light leading-relaxed max-w-lg">
              {t('sessionerPage.sub')}
            </p>
          </FadeIn>
        </div>
        <div className="absolute top-0 right-0 w-full md:w-1/2 h-full opacity-20 md:opacity-100 pt-20 md:pt-24 lg:pt-28 pointer-events-none">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/billede-til-merete.jpg" 
            alt="Mette Brudvig Sessioner" 
            className="w-full h-full object-cover object-top pointer-events-none"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#f5efe9] via-transparent to-transparent hidden md:block pointer-events-none" />
        </div>
      </section>

      {/* 3 Dedicated Sessions Overview Cards & Video Presentation */}
      <section className="py-20 md:py-28 bg-white px-6">
        <div className="max-w-7xl mx-auto space-y-16">
          <FadeIn className="text-center max-w-3xl mx-auto space-y-4">
            <h2 className="text-3xl md:text-5xl font-serif text-stone-900 leading-tight">
              {isEn ? "Choose the session that suits you" : "Vælg den session, der passer til dig"}
            </h2>
            <p className="text-stone-600 font-light text-base md:text-lg leading-relaxed">
              {isEn
                ? "Here you can explore my three 1:1 offerings. Click on each session to read the full description and booking details."
                : "Her kan du gå i dybden med mine tre individuelle sessionstyper. Klik ind på hver enkelt underside for at læse mere og booke din tid."}
            </p>
          </FadeIn>

          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Left side: The 3 Session Cards */}
            <div className="lg:col-span-7 space-y-6">
              {/* Card 1: Clairvoyant rådgivning */}
              <FadeIn delay={100} className="bg-[#faf9f6] p-7 md:p-8 rounded-sm border border-stone-200/80 shadow-xs flex flex-col justify-between hover:border-[#817262] hover:shadow-md transition-all duration-300 group">
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#817262]">
                      {session1?.kategori_badge?.[lang] || session1?.kategori_badge?.da || (isEn ? "Personal Mentoring" : "Personlig Sparring")}
                    </span>
                    <h3 className="text-2xl font-serif text-stone-900 group-hover:text-[#817262] transition-colors">
                      {session1?.titel?.[lang] || session1?.titel?.da || (isEn ? "Clairvoyant Advisory & Personal Mentoring" : "Clairvoyant rådgivning & personlig sparring")}
                    </h3>
                  </div>
                  <p className="text-stone-600 font-light text-sm leading-relaxed">
                    {session1?.kort_beskrivelse?.[lang] || session1?.kort_beskrivelse?.da || (isEn
                      ? "Do you need clarity and insight? Perhaps because you stand at a crossroads, find it difficult to feel yourself clearly, or need to understand what is calling you."
                      : "Har du brug for klarhed og indsigt? Måske fordi du står ved en skillevej, har svært ved at mærke dig selv og se klart eller har brug for at forstå, hvad der kalder på dig.")}
                  </p>
                  <div className="pt-3 border-t border-stone-200/60 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-stone-500 font-light">
                    <div className="flex items-center gap-2">
                      <span className="text-[#817262]">✧</span>
                      <span>{isEn ? "Duration: 30 or 60 minutes" : "Varighed: 30 eller 60 minutter"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[#817262]">✧</span>
                      <span className="font-semibold text-stone-800">
                        {session1?.varighed_og_pris?.[lang] || session1?.varighed_og_pris?.da || (isEn ? "Price: 550 / 950 DKK" : "Pris: 550 kr. / 950 kr.")}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="pt-5 mt-5 border-t border-stone-200/60">
                  <Link
                    to={isEn ? "/en/sessions/clairvoyant-mentoring" : "/sessioner/clairvoyant-raadgivning"}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#817262] text-white hover:bg-stone-800 transition-colors uppercase text-xs tracking-widest font-bold rounded-sm shadow-xs group-hover:shadow-sm"
                  >
                    <span>{isEn ? "Read more & book" : "Læs mere & book"}</span>
                    <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </FadeIn>

              {/* Card 2: Ledersparring */}
              <FadeIn delay={200} className="bg-[#faf9f6] p-7 md:p-8 rounded-sm border border-stone-200/80 shadow-xs flex flex-col justify-between hover:border-[#817262] hover:shadow-md transition-all duration-300 group">
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#817262]">
                      {session2?.kategori_badge?.[lang] || session2?.kategori_badge?.da || (isEn ? "Executive Leadership" : "Erhverv & Ledelse")}
                    </span>
                    <h3 className="text-2xl font-serif text-stone-900 group-hover:text-[#817262] transition-colors">
                      {session2?.titel?.[lang] || session2?.titel?.da || (isEn ? "Leadership Mentoring in New Era Leadership" : "Ledersparring i Ny Tids Lederskab")}
                    </h3>
                  </div>
                  <p className="text-stone-600 font-light text-sm leading-relaxed">
                    {session2?.kort_beskrivelse?.[lang] || session2?.kort_beskrivelse?.da || (isEn
                      ? "Strategic clarity, heightened intuition, and human insight to strengthen your leadership, well-being, and decision-making."
                      : "Strategisk klarhed, skærpet intuition og menneskelig indsigt til at styrke dit lederskab, din trivsel og dine beslutninger.")}
                  </p>
                  <div className="pt-3 border-t border-stone-200/60 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-stone-500 font-light">
                    <div className="flex items-center gap-2">
                      <span className="text-[#817262]">✧</span>
                      <span>{isEn ? "Single session (75 min) or program" : "Enkelt session (75 min) eller forløb"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[#817262]">✧</span>
                      <span className="font-semibold text-stone-800">
                        {session2?.varighed_og_pris?.[lang] || session2?.varighed_og_pris?.da || (isEn ? "1,600 DKK excl. VAT" : "1.600 kr. ekskl. moms")}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="pt-5 mt-5 border-t border-stone-200/60">
                  <Link
                    to={isEn ? "/en/sessions/leadership-mentoring" : "/sessioner/ledersparring"}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#817262] text-white hover:bg-stone-800 transition-colors uppercase text-xs tracking-widest font-bold rounded-sm shadow-xs group-hover:shadow-sm"
                  >
                    <span>{isEn ? "Read more & book" : "Læs mere & book"}</span>
                    <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </FadeIn>

              {/* Card 3: Mentorforløb 1:1 */}
              <FadeIn delay={300} className="bg-[#faf9f6] p-7 md:p-8 rounded-sm border border-stone-200/80 shadow-xs flex flex-col justify-between hover:border-[#817262] hover:shadow-md transition-all duration-300 group">
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#817262]">
                      {session3?.kategori_badge?.[lang] || session3?.kategori_badge?.da || (isEn ? "Deep Transformation" : "Dyb Transformation")}
                    </span>
                    <h3 className="text-2xl font-serif text-stone-900 group-hover:text-[#817262] transition-colors">
                      {session3?.titel?.[lang] || session3?.titel?.da || (isEn ? "1:1 Mentoring & Development Program" : "Mentor- og udviklingsforløb 1:1")}
                    </h3>
                  </div>
                  <p className="text-stone-600 font-light text-sm leading-relaxed">
                    {session3?.kort_beskrivelse?.[lang] || session3?.kort_beskrivelse?.da || (isEn
                      ? "Deep transformation & meditation for integrating heart intelligence into your daily life across multiple dedicated sessions."
                      : "Dyb transformation & meditation for dig, der vil integrere hjertets intelligens i din hverdag i et længere, dedikeret rum.")}
                  </p>
                  <div className="pt-3 border-t border-stone-200/60 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-stone-500 font-light">
                    <div className="flex items-center gap-2">
                      <span className="text-[#817262]">✧</span>
                      <span>{isEn ? "10 intensive sessions" : "10 intensive sessioner"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[#817262]">✧</span>
                      <span className="font-semibold text-stone-800">
                        {session3?.varighed_og_pris?.[lang] || session3?.varighed_og_pris?.da || (isEn ? "Price: 8,500 DKK" : "Pris: 8.500 kr.")}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="pt-5 mt-5 border-t border-stone-200/60">
                  <Link
                    to={isEn ? "/en/sessions/mentor-program" : "/sessioner/mentorforloeb"}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#817262] text-white hover:bg-stone-800 transition-colors uppercase text-xs tracking-widest font-bold rounded-sm shadow-xs group-hover:shadow-sm"
                  >
                    <span>{isEn ? "Read more & book" : "Læs mere & book"}</span>
                    <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </FadeIn>
            </div>

            {/* Right side: Video Presentation */}
            <div className="lg:col-span-5 lg:sticky lg:top-28">
              <FadeIn delay={200} className="bg-[#f5efe9] p-8 md:p-10 rounded-sm border border-stone-200/80 shadow-xs space-y-6">
                <div className="space-y-3">
                  <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#817262] block">
                    {isEn ? "Video Intro" : "Video Præsentation"}
                  </span>
                  <h2 className="text-2xl md:text-3xl font-serif text-stone-900 leading-snug">
                    {isEn ? "A warm intro to 1:1 sessions with Mette" : "En introduktion til 1:1 sessioner med Mette"}
                  </h2>
                  <p className="text-stone-600 font-light text-sm leading-relaxed">
                    {isEn
                      ? "Watch this short video where Mette introduces how she works with clairvoyance, intuition, and heart intelligence in personal sessions."
                      : "Se en kort video, hvor Mette fortæller om sin tilgang til clairvoyance, intuition og hjertets intelligens i individuelle sessioner."}
                  </p>
                </div>
                <div className="flex justify-center pt-2">
                  <YouTubeEmbed 
                    url="https://youtube.com/shorts/8g5-MMfl_do?feature=share" 
                    title={isEn ? "Intro to 1:1 sessions" : "Intro til 1:1 sessioner"} 
                    className="w-full max-w-sm shadow-md"
                  />
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials - flyttet op foran booking for at skabe social proof */}
      <TestimonialsShowcase
        category="sessioner"
        badge={isEn ? "Client Experiences" : "Klientudtalelser"}
        title={isEn ? "What clients say about 1:1 sessions" : "Hvad klienter siger om 1:1 sessioner"}
        className="py-20 md:py-24 bg-[#faf9f6] px-6 border-t border-stone-200/60"
        cardBg="bg-white"
      />
    </div>
  );
};

/* 1. Underside: Clairvoyant Rådgivning & Personlig Sparring */
const ClairvoyantRaadgivningPage = () => {
  const { lang } = useLanguage();
  const isEn = lang === 'en';
  const { sessions } = useCms();
  const session = sessions?.find(s => (s.url_slug?.da || s.url_slug) === 'clairvoyant-raadgivning' || (s.url_slug?.en || s.url_slug) === 'clairvoyant-mentoring');

  const title = session?.titel?.[lang] || session?.titel?.da || (isEn ? "Clairvoyant Advisory & Personal Mentoring" : "Clairvoyant Rådgivning og Personlig Sparring");
  const badge = session?.kategori_badge?.[lang] || session?.kategori_badge?.da || (isEn ? "Personal Mentoring" : "Personlig Sparring");
  const subNav = session?.undertitel?.[lang] || session?.undertitel?.da || (isEn ? "Clairvoyant Advisory" : "Clairvoyant Rådgivning");
  const heroImage = session?.hero_billede?.[0] || "https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/billede-til-merete.jpg";
  const introQuote = session?.intro_citat?.[lang] || session?.intro_citat?.da || (isEn 
    ? "Do you need clarity and insight? Perhaps because you stand at a crossroads, find it difficult to feel yourself clearly, or need to understand what is calling you."
    : "Har du brug for klarhed og indsigt? Måske fordi du står ved en skillevej, har svært ved at mærke dig selv og se klart eller har brug for at forstå, hvad der kalder på dig.");
  const introHtml = session?.intro_tekst?.[lang] || session?.intro_tekst?.da;
  const processTitle = session?.saadan_foregaar_det_titel?.[lang] || session?.saadan_foregaar_det_titel?.da || (isEn ? "How the session works" : "Sådan foregår sessionen");
  const processHtml = session?.saadan_foregaar_det_tekst?.[lang] || session?.saadan_foregaar_det_tekst?.da;
  const takeawayTitle = session?.udbytte_titel?.[lang] || session?.udbytte_titel?.da || (isEn ? "Your takeaways" : "Dit udbytte");
  const takeawayHtml = session?.udbytte_tekst?.[lang] || session?.udbytte_tekst?.da;
  const topicsTitle = session?.emner_titel?.[lang] || session?.emner_titel?.da || (isEn ? "A session can address" : "En session kan bl.a. handle om");
  const topicsHtml = session?.emner_punkter?.[lang] || session?.emner_punkter?.da;
  const videoUrl = session?.video_url || "https://youtube.com/shorts/8g5-MMfl_do?feature=share";
  const priceTitle = session?.pris_titel?.[lang] || session?.pris_titel?.da || (isEn ? "Price" : "Pris");
  const priceHtml = session?.pris_detaljer?.[lang] || session?.pris_detaljer?.da;
  const bookingText = session?.booking_tekst?.[lang] || session?.booking_tekst?.da || (isEn ? "Book your session by emailing me at " : "Du booker din tid ved at skrive til mig på ");
  const bookingEmail = session?.booking_email || "mette@mettebrudvig.dk";
  const bookingPhone = session?.booking_telefon || "27 50 70 83";
  const testimonialName = session?.udtalelse_navn || "Kirsten Pedersen";
  const testimonialTitle = session?.udtalelse_titel?.[lang] || session?.udtalelse_titel?.da || (isEn ? "Client Testimonial • Clairvoyance Session" : "Udtalelse fra klient • Clairvoyance-session");

  useEffect(() => {
    document.title = isEn 
      ? `Mette Brudvig - ${title}` 
      : `Mette Brudvig - ${title}`;
    window.scrollTo(0, 0);
  }, [isEn, title]);

  return (
    <div className="bg-[#faf9f6] min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] md:min-h-[70vh] pt-[185px] md:pt-[215px] lg:pt-[225px] pb-12 flex items-center bg-[#f5efe9] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center z-10 w-full">
          <FadeIn direction="right">
            <span className="text-xs uppercase tracking-[0.4em] font-bold text-[#817262] mb-4 block">
              {badge}
            </span>
            <h1 className="text-4xl md:text-6xl font-serif text-stone-900 leading-[1.1] mb-6">
              {title}
            </h1>
            <p className="text-lg md:text-xl text-stone-700 font-light leading-relaxed max-w-lg">
              {introQuote}
            </p>
          </FadeIn>
        </div>
        <div className="absolute top-0 right-0 w-full md:w-1/2 h-full opacity-20 md:opacity-100 pt-20 md:pt-24 lg:pt-28 pointer-events-none">
          <img 
            src={heroImage} 
            alt={`Mette Brudvig ${title}`} 
            className="w-full h-full object-cover object-top pointer-events-none"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#f5efe9] via-transparent to-transparent hidden md:block pointer-events-none" />
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-20 md:py-28 bg-white px-6">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
              {/* Left Column: Content */}
              <div className="lg:col-span-7 space-y-10">
                {introHtml ? (
                  <div 
                    className="space-y-6 text-stone-700 font-light text-lg leading-relaxed [&>p]:mb-4"
                    dangerouslySetInnerHTML={{ __html: introHtml }}
                  />
                ) : (
                  <div className="space-y-6 text-stone-700 font-light text-lg leading-relaxed">
                    <p>
                      {isEn
                        ? "When I tune into you, it feels like looking through a veil. I connect with a higher intelligence that wishes the best for you and your life unfolding. I see your potential and strengths, and gain insight into what limits you."
                        : "Når jeg tuner ind på dig, er det som at se gennem et slør. Jeg forbinder mig med en højere intelligens, som ønsker det bedste for dig og for din livsudfoldelse. Jeg ser dit potentiale og dine styrker, og jeg får indblik i, hvad der begrænser dig."}
                    </p>
                    <p>
                      {isEn
                        ? "In a session, I combine clairvoyant guidance with coaching, mentoring, and my extensive background in human dynamics, communication, and personal transformation."
                        : "I en session kombinerer jeg clairvoyant rådgivning med coaching, mentoring og min erfaring med mennesker, kommunikation og personlig udvikling."}
                    </p>
                    <p>
                      {isEn
                        ? "You receive clarity, fresh perspectives, and deep support to feel yourself, your truth, and your authentic power."
                        : "Du får klarhed, nye perspektiver og støtte til at mærke dig selv, din sandhed og din styrke."}
                    </p>
                  </div>
                )}

                <div className="bg-[#f5efe9]/40 p-8 md:p-10 rounded-sm border border-[#817262]/10 space-y-6">
                  <h3 className="text-xl font-serif text-stone-900">
                    {processTitle}
                  </h3>
                  {processHtml ? (
                    <div 
                      className="space-y-4 text-stone-600 font-light text-base leading-relaxed [&>p]:mb-3"
                      dangerouslySetInnerHTML={{ __html: processHtml }}
                    />
                  ) : (
                    <div className="space-y-4 text-stone-600 font-light text-base leading-relaxed">
                      <p>
                        {isEn ? "We begin with whatever is present for you right now." : "Vi tager udgangspunkt i det, der fylder hos dig lige nu."}
                      </p>
                      <p>
                        {isEn 
                          ? "You are welcome to bring specific questions, but no advance preparation is needed. I work intuitively and clairvoyantly, combining insights received with dialogue, reflection, and practical guidance."
                          : "Du må gerne komme med konkrete spørgsmål, men du behøver ikke have forberedt noget. Jeg arbejder intuitivt og clairvoyant og kombinerer det, jeg modtager, med samtale, refleksion og konkrete råd."}
                      </p>
                      <p>
                        {isEn 
                          ? "The session is a confidential space where human, practical, and spiritual dimensions coexist naturally."
                          : "Sessionen er et fortroligt rum, hvor der er plads til både det menneskelige, det praktiske og det spirituelle."}
                      </p>
                    </div>
                  )}
                </div>

                <div className="bg-[#817262]/5 p-8 md:p-10 rounded-sm border border-[#817262]/20 space-y-4">
                  <h3 className="text-xl font-serif text-stone-900">
                    {takeawayTitle}
                  </h3>
                  {takeawayHtml ? (
                    <div 
                      className="space-y-3 text-stone-600 font-light text-base leading-relaxed [&>p]:mb-2"
                      dangerouslySetInnerHTML={{ __html: takeawayHtml }}
                    />
                  ) : (
                    <div className="space-y-3 text-stone-600 font-light text-base leading-relaxed">
                      <p>
                        {isEn ? "You leave the session with greater clarity and a stronger connection to yourself." : "Du går fra sessionen med større klarhed og en tydeligere forbindelse til dig selv."}
                      </p>
                      <p>
                        {isEn 
                          ? "You receive new perspectives, practical advice, and a deeper understanding of what holds you back and what wants to emerge."
                          : "Du får nye perspektiver, konkrete råd og en bedre forståelse af, hvad der holder dig tilbage, og hvad der ønsker at vokse frem."}
                      </p>
                    </div>
                  )}
                </div>

                {/* Online Booking & Priser Card */}
                <div className="bg-[#f5efe9] p-7 md:p-9 rounded-2xl border border-[#817262]/20 shadow-sm space-y-6">
                  <div className="border-b border-[#817262]/15 pb-4">
                    <span className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[#817262] block mb-1">
                      {isEn ? "Online Booking & Prices" : "Online booking & priser"}
                    </span>
                    <h4 className="text-2xl font-serif text-stone-900 font-bold tracking-tight">{priceTitle}</h4>
                  </div>

                  {/* Direct Checkout Cards */}
                  <div className="space-y-4">
                    {/* Option 1: 30 min session */}
                    <div className="bg-white p-6 rounded-xl border border-[#817262]/15 shadow-2xs hover:border-[#817262]/40 hover:shadow-md transition-all duration-300 group">
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div className="space-y-1">
                          <h5 className="text-lg font-serif font-bold text-stone-900 group-hover:text-[#817262] transition-colors">
                            {isEn ? "30 min. 1:1 session" : "30 min. 1:1 session"}
                          </h5>
                          <p className="text-xs text-stone-600 font-light leading-relaxed">
                            {isEn 
                              ? "Clairvoyant insight into you, your potential, and your path" 
                              : "Clairvoyant indsigt i dig, dit potentiale og din vej"}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <span className="text-xl md:text-2xl font-serif font-bold text-stone-900 whitespace-nowrap block">
                            550 kr.
                          </span>
                          <span className="text-[10px] text-stone-500 font-light block uppercase tracking-wider">
                            {isEn ? "Direct booking" : "Direkte booking"}
                          </span>
                        </div>
                      </div>
                      <a
                        href="https://pay.lumant.ai/kurv/gRxb68lK-QJybAjoTwo3V"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 flex items-center justify-center gap-2.5 w-full bg-[#817262] hover:bg-stone-900 text-white py-3 px-4 rounded-lg uppercase text-xs tracking-wider font-semibold shadow-xs hover:shadow transition-all duration-200"
                      >
                        <span>{isEn ? "Book 30 min. session (550 DKK)" : "Book 30 min. session (550 kr.)"}</span>
                        <i className="fa-solid fa-arrow-up-right-from-square text-[11px] group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
                      </a>
                    </div>

                    {/* Option 2: 1 hour (60 min) session */}
                    <div className="bg-white p-6 rounded-xl border border-[#817262]/15 shadow-2xs hover:border-[#817262]/40 hover:shadow-md transition-all duration-300 group">
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div className="space-y-1">
                          <h5 className="text-lg font-serif font-bold text-stone-900 group-hover:text-[#817262] transition-colors">
                            {isEn ? "1 hour (60 min.) 1:1 session" : "1 time (60 min.) 1:1 session"}
                          </h5>
                          <p className="text-xs text-stone-600 font-light leading-relaxed">
                            {isEn 
                              ? "Clairvoyant insight into you, your potential, and your path" 
                              : "Clairvoyant indsigt i dig, dit potentiale og din vej"}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <span className="text-xl md:text-2xl font-serif font-bold text-stone-900 whitespace-nowrap block">
                            950 kr.
                          </span>
                          <span className="text-[10px] text-stone-500 font-light block uppercase tracking-wider">
                            {isEn ? "Direct booking" : "Direkte booking"}
                          </span>
                        </div>
                      </div>
                      <a
                        href="https://pay.lumant.ai/kurv/99UQ-11-session"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 flex items-center justify-center gap-2.5 w-full bg-[#817262] hover:bg-stone-900 text-white py-3 px-4 rounded-lg uppercase text-xs tracking-wider font-semibold shadow-xs hover:shadow transition-all duration-200"
                      >
                        <span>{isEn ? "Book 1 hour session (950 DKK)" : "Book 1 time session (950 kr.)"}</span>
                        <i className="fa-solid fa-arrow-up-right-from-square text-[11px] group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
                      </a>
                    </div>
                  </div>

                  {/* Direct Contact & Questions */}
                  <div className="pt-2">
                    <div className="bg-white/60 backdrop-blur-xs p-5 rounded-xl border border-[#817262]/15 space-y-2.5">
                      <div className="flex items-center gap-2 text-stone-900">
                        <i className="fa-regular fa-envelope text-[#817262] text-xs" aria-hidden="true" />
                        <h5 className="text-sm font-serif font-bold">{isEn ? "Questions or Direct Booking" : "Spørgsmål eller direkte booking"}</h5>
                      </div>
                      <p className="text-xs text-stone-600 font-light leading-relaxed">
                        {bookingText ? (bookingText.endsWith(" ") ? bookingText : `${bookingText} `) : (isEn ? "Book your session by emailing me at " : "Du booker din tid ved at skrive til mig på ")}
                        <a href={`mailto:${bookingEmail}`} className="underline font-medium text-[#817262] hover:text-stone-900 transition-colors">
                          {bookingEmail}
                        </a>
                      </p>
                      {bookingPhone && (
                        <div className="pt-1 border-t border-[#817262]/10">
                          <a 
                            href={`tel:+45${bookingPhone.replace(/\s+/g, '')}`} 
                            className="inline-flex items-center gap-2 text-xs font-semibold text-[#817262] hover:text-stone-900 transition-colors"
                          >
                            <i className="fa-solid fa-phone text-[11px]" aria-hidden="true" />
                            <span>{isEn ? `Call to hear more: +45 ${bookingPhone}` : `Ring, og hør mere: ${bookingPhone}`}</span>
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: Bullets & Testimonial */}
              <div className="lg:col-span-5 space-y-8 lg:sticky lg:top-40">
                <div className="bg-stone-50 p-8 md:p-10 border border-stone-100 rounded-sm space-y-6">
                  <h3 className="text-lg uppercase tracking-wider font-bold text-stone-800">
                    {topicsTitle}
                  </h3>
                  {topicsHtml ? (
                    <div 
                      className="[&_ul]:space-y-4 [&_li]:flex [&_li]:items-start [&_li]:text-stone-700 [&_li]:font-light [&_li]:text-base [&_li]:before:content-['✧'] [&_li]:before:text-[#817262] [&_li]:before:mr-3 [&_li]:before:font-serif [&_li]:before:text-lg [&_li]:before:leading-none"
                      dangerouslySetInnerHTML={{ __html: topicsHtml }}
                    />
                  ) : (
                    <ul className="space-y-4">
                      {(isEn ? [
                        "Career life, direction, and purpose",
                        "Relationships and recurring patterns",
                        "Major choices and life transitions",
                        "Your intuition and inner wisdom",
                        "Boundaries, self-worth, and authenticity",
                        "Restlessness, doubt, or feeling stuck",
                        "Your energy and what drains you",
                        "The next natural step in your life"
                      ] : [
                        "Arbejdsliv, retning og formål",
                        "Relationer og tilbagevendende mønstre",
                        "Store valg og livsforandringer",
                        "Din intuition og indre viden",
                        "Grænser, selvværd og autenticitet",
                        "Uro, tvivl eller følelsen af at være kørt fast",
                        "Din energi og det, der dræner dig",
                        "Det næste naturlige skridt i dit liv"
                      ]).map((item, idx) => (
                        <li key={idx} className="flex items-start text-stone-700 font-light text-base">
                          <span className="text-[#817262] mr-3 font-serif text-lg leading-none">✧</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {videoUrl && (
                    <div className="pt-2">
                      <YouTubeEmbed 
                        url={videoUrl} 
                        title={isEn ? `Intro to ${title}` : `Intro til ${title}`} 
                        className="w-full max-w-full"
                      />
                    </div>
                  )}
                </div>

                {/* Customer Quote Callout */}
                <TestimonialCallout 
                  testimonialName={testimonialName} 
                  customTitle={testimonialTitle} 
                  className="!my-0"
                />
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

/* 2. Underside: Ledersparring i Ny Tids Lederskab */
const LedersparringSessionPage = () => {
  const { lang } = useLanguage();
  const isEn = lang === 'en';
  const { sessions } = useCms();
  const session = sessions?.find(s => (s.url_slug?.da || s.url_slug) === 'ledersparring' || (s.url_slug?.en || s.url_slug) === 'leadership-mentoring');

  const title = session?.titel?.[lang] || session?.titel?.da || (isEn ? "Confidential Intuitive Executive Mentoring" : "Fortrolig intuitiv ledersparring");
  const badge = session?.kategori_badge?.[lang] || session?.kategori_badge?.da || (isEn ? "Executive Leadership" : "Erhverv & Ledelse");
  const subNav = session?.undertitel?.[lang] || session?.undertitel?.da || (isEn ? "Leadership Mentoring" : "Ledersparring");
  const heroImage = session?.hero_billede?.[0] || "https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/billede-til-merete.jpg";
  const introQuote = session?.intro_citat?.[lang] || session?.intro_citat?.da || (isEn
    ? "Strategic clarity, heightened intuition, and human insight to strengthen your leadership, well-being, and decision-making."
    : "Strategisk klarhed, skærpet intuition og menneskelig indsigt til at styrke dit lederskab, din trivsel og dine beslutninger.");
  const introHtml = session?.intro_tekst?.[lang] || session?.intro_tekst?.da;
  const processTitle = session?.saadan_foregaar_det_titel?.[lang] || session?.saadan_foregaar_det_titel?.da || (isEn ? "How leadership mentoring works" : "Sådan foregår ledersparringen");
  const processHtml = session?.saadan_foregaar_det_tekst?.[lang] || session?.saadan_foregaar_det_tekst?.da;
  const takeawayTitle = session?.udbytte_titel?.[lang] || session?.udbytte_titel?.da || (isEn ? "Your takeaways" : "Dit udbytte");
  const takeawayHtml = session?.udbytte_tekst?.[lang] || session?.udbytte_tekst?.da;
  const topicsTitle = session?.emner_titel?.[lang] || session?.emner_titel?.da || (isEn ? "Executive mentoring can address" : "Ledersparringen kan blandt andet bruges til");
  const topicsHtml = session?.emner_punkter?.[lang] || session?.emner_punkter?.da;
  const videoUrl = session?.video_url || "https://youtube.com/shorts/8g5-MMfl_do?feature=share";
  const priceTitle = session?.pris_titel?.[lang] || session?.pris_titel?.da || (isEn ? "Price & Program" : "Pris & Forløb");
  const priceHtml = session?.pris_detaljer?.[lang] || session?.pris_detaljer?.da;
  const bookingText = session?.booking_tekst?.[lang] || session?.booking_tekst?.da || (isEn ? "Book your session by emailing " : "Du booker din tid ved at skrive til mig på ");
  const bookingEmail = session?.booking_email || "mette@mettebrudvig.dk";
  const bookingPhone = session?.booking_telefon || "27 50 70 83";
  const testimonialName = session?.udtalelse_navn || "Kirsten Pedersen";
  const testimonialTitle = session?.udtalelse_titel?.[lang] || session?.udtalelse_titel?.da || (isEn ? "Client Testimonial • Leadership" : "Udtalelse fra klient • Ledelse & sparring");

  useEffect(() => {
    document.title = isEn 
      ? `Mette Brudvig - ${title}` 
      : `Mette Brudvig - ${title}`;
    window.scrollTo(0, 0);
  }, [isEn, title]);

  return (
    <div className="bg-[#faf9f6] min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] md:min-h-[70vh] pt-[185px] md:pt-[215px] lg:pt-[225px] pb-12 flex items-center bg-[#f5efe9] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center z-10 w-full">
          <FadeIn direction="right">
            <span className="text-xs uppercase tracking-[0.4em] font-bold text-[#817262] mb-4 block">
              {badge}
            </span>
            <h1 className="text-4xl md:text-6xl font-serif text-stone-900 leading-[1.1] mb-6">
              {title}
            </h1>
            <p className="text-lg md:text-xl text-stone-700 font-light leading-relaxed max-w-lg">
              {introQuote}
            </p>
          </FadeIn>
        </div>
        <div className="absolute top-0 right-0 w-full md:w-1/2 h-full opacity-20 md:opacity-100 pt-20 md:pt-24 lg:pt-28 pointer-events-none">
          <img 
            src={heroImage} 
            alt={`Mette Brudvig ${title}`} 
            className="w-full h-full object-cover object-top pointer-events-none"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#f5efe9] via-transparent to-transparent hidden md:block pointer-events-none" />
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-20 md:py-28 bg-white px-6">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
              {/* Left Column: Content */}
              <div className="lg:col-span-7 space-y-10">
                {introHtml ? (
                  <div 
                    className="space-y-6 text-stone-700 font-light text-lg leading-relaxed [&>p]:mb-4"
                    dangerouslySetInnerHTML={{ __html: introHtml }}
                  />
                ) : (
                  <div className="space-y-6 text-stone-700 font-light text-lg leading-relaxed">
                    <p>
                      {isEn 
                        ? "As a leader, you often stand alone with decisions impacting people, organizations, and the future."
                        : "Som leder står du ofte alene med beslutninger, der påvirker både mennesker, organisation og fremtid."}
                    </p>
                    <p>
                      {isEn 
                        ? "In my confidential executive mentoring, I combine strategic reflection, executive coaching, and clairvoyant guidance."
                        : "I min fortrolige ledersparring kombinerer jeg strategisk refleksion, coaching og clairvoyant rådgivning."}
                    </p>
                    <p>
                      {isEn
                        ? "I help you gain deeper insights into yourself, your situation, and the people involved, so you feel clearly what the right path is for you and your organization. I help strengthen your connection to intuition and heart intelligence so you can lead with genuine presence."
                        : "Jeg hjælper dig til en dybere indsigt i dig selv, situationen og de involverede, så du mærker tydeligere, hvad der er den rette vej for dig og organisationen. Jeg hjælper dig med at styrke forbindelsen til din intuition og hjertets intelligens, så du kan lære at stå endnu stærkere i dig selv og i din ledelse."}
                    </p>
                    <p>
                      {isEn
                        ? "This is a confidential space to think out loud, explore doubt, and align with your authentic truth rather than reactive triggers."
                        : "Det er et fortroligt refleksionsrum, hvor du kan tænke højt, undersøge din tvivl og øve dig på at mærke din intuition. Du kan stå ved dine beslutninger, fordi de ikke er truffet fra dine triggers, men fra din sandhed og ved brug af din fulde intelligens."}
                    </p>
                  </div>
                )}

                <div className="bg-[#817262]/5 p-8 md:p-10 rounded-sm border border-[#817262]/20 space-y-4">
                  <h3 className="text-xl font-serif text-stone-900">{takeawayTitle}</h3>
                  {takeawayHtml ? (
                    <div 
                      className="space-y-3 text-stone-600 font-light text-base leading-relaxed [&>p]:mb-2"
                      dangerouslySetInnerHTML={{ __html: takeawayHtml }}
                    />
                  ) : (
                    <div className="space-y-3 text-stone-600 font-light text-base leading-relaxed">
                      <p>
                        {isEn ? "You gain greater clarity, a broader perspective, and a confident foundation for decision-making." : "Du får større klarhed, et bredere perspektiv og et mere sikkert grundlag for dine beslutninger."}
                      </p>
                      <p>
                        {isEn 
                          ? "You walk away with calm, direction, and courage to act in alignment with your professional mastery, intuition, and values."
                          : "Målet er, at du går derfra med ro, retning og mod til at handle i overensstemmelse med både din faglighed, din intuition og dine værdier."}
                      </p>
                    </div>
                  )}
                </div>

                {/* Customer Quote Callout */}
                <TestimonialCallout 
                  testimonialName={testimonialName} 
                  customTitle={testimonialTitle} 
                />
              </div>

              {/* Right Column: Bullets & Pricing Card */}
              <div className="lg:col-span-5 space-y-8 lg:sticky lg:top-40">
                <div className="bg-stone-50 p-8 md:p-10 border border-stone-100 rounded-sm space-y-6">
                  <h3 className="text-lg uppercase tracking-wider font-bold text-stone-800">
                    {topicsTitle}
                  </h3>
                  {topicsHtml ? (
                    <div 
                      className="[&_ul]:space-y-4 [&_li]:flex [&_li]:items-start [&_li]:text-stone-700 [&_li]:font-light [&_li]:text-base [&_li]:before:content-['✧'] [&_li]:before:text-[#817262] [&_li]:before:mr-3 [&_li]:before:font-serif [&_li]:before:text-lg [&_li]:before:leading-none"
                      dangerouslySetInnerHTML={{ __html: topicsHtml }}
                    />
                  ) : (
                    <ul className="space-y-4">
                      {(isEn ? [
                        "Crucial decisions and strategic choices",
                        "Leadership dynamics, teamwork, and relationships",
                        "Organizational transformations and shifts",
                        "Navigating conflicts and difficult conversations",
                        "Team recruitment and key talent alignment",
                        "Your energy, motivation, and leadership sustainability",
                        "Standing grounded and authentic in your leadership role",
                        "Building trust and genuine followership",
                        "Navigating complexity with calm clarity"
                      ] : [
                        "Vigtige beslutninger og strategiske valg",
                        "Udfordringer i ledelse, samarbejde og relationer",
                        "Forandringer i organisationen",
                        "Konflikter og vanskelige samtaler",
                        "Rekruttering og sammensætning af teams",
                        "Din egen energi, motivation og bæredygtighed som leder",
                        "At stå tydeligt og autentisk i dit lederskab",
                        "At skabe ægte følgeskab gennem troværdighed og tillid",
                        "At finde klarhed i komplekse situationer"
                      ]).map((item, idx) => (
                        <li key={idx} className="flex items-start text-stone-700 font-light text-base">
                          <span className="text-[#817262] mr-3 font-serif text-lg leading-none">✧</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {videoUrl && (
                    <div className="pt-2">
                      <YouTubeEmbed 
                        url={videoUrl} 
                        title={isEn ? `Intro to ${title}` : `Intro til ${title}`} 
                        className="w-full max-w-full"
                      />
                    </div>
                  )}
                </div>

                <div className="bg-[#f5efe9] p-8 md:p-10 border border-[#817262]/20 rounded-sm space-y-6 shadow-sm">
                  <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-[#817262]">{priceTitle}</h4>
                  <div className="space-y-4">
                    {priceHtml ? (
                      <div 
                        className="text-stone-700 font-light space-y-2 text-sm leading-relaxed [&>p]:flex [&>p]:justify-between [&>p]:items-baseline [&_strong]:text-lg [&_strong]:font-bold [&_strong]:text-stone-900"
                        dangerouslySetInnerHTML={{ __html: priceHtml }}
                      />
                    ) : (
                      <>
                        <div className="flex justify-between items-baseline border-b border-[#817262]/10 pb-3">
                          <span className="text-stone-600 font-light">{isEn ? "75 minutes" : "75 minutter"}</span>
                          <span className="text-lg font-bold text-stone-900">1.600 kr. {isEn ? "excl. VAT" : "ekskl. moms"}</span>
                        </div>
                        <div className="flex justify-between items-baseline pb-3">
                          <span className="text-stone-600 font-light">{isEn ? "Mentoring Program (10 sessions)" : "Sparringsforløb (10 sessioner)"}</span>
                          <span className="text-lg font-bold text-stone-900">12.500 kr. {isEn ? "excl. VAT" : "ekskl. moms"}</span>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="pt-4 border-t border-[#817262]/10 space-y-2">
                    <h5 className="text-sm font-serif font-bold text-stone-900">{isEn ? "Contact & Booking" : "Kontakt & booking"}</h5>
                    <p className="text-xs text-stone-600 font-light">
                      {bookingText}
                      <a href={`mailto:${bookingEmail}`} className="underline font-medium text-[#817262] hover:text-stone-900">
                        {bookingEmail}
                      </a>
                    </p>
                    {bookingPhone && (
                      <div className="pt-2">
                        <a 
                          href={`tel:+45${bookingPhone.replace(/\s+/g, '')}`} 
                          className="inline-flex items-center gap-2 text-xs font-semibold text-[#817262] hover:text-stone-900 transition-colors"
                        >
                          <i className="fa-solid fa-phone text-xs" />
                          <span>{isEn ? `Call to hear more: +45 ${bookingPhone}` : `Ring, og hør mere: ${bookingPhone}`}</span>
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Navigation to other sessions */}
      <section className="py-16 bg-[#faf9f6] border-t border-stone-200/70 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <span className="text-xs uppercase tracking-widest text-[#817262] font-bold block mb-2">
              {isEn ? "Explore Other Sessions" : "Udforsk Øvrige Sessioner"}
            </span>
            <h3 className="text-2xl font-serif text-stone-900">
              {isEn ? "More 1:1 offerings" : "Flere individuelle muligheder"}
            </h3>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              to={isEn ? "/en/sessions/clairvoyant-mentoring" : "/sessioner/clairvoyant-raadgivning"}
              className="p-6 bg-white border border-stone-200/80 rounded-sm hover:border-[#817262] hover:shadow-sm transition-all group"
            >
              <span className="text-[11px] uppercase tracking-widest text-[#817262] font-bold block mb-1">
                {isEn ? "Personal Mentoring" : "Personlig Sparring"}
              </span>
              <h4 className="text-lg font-serif text-stone-900 group-hover:text-[#817262] transition-colors mb-2">
                {isEn ? "Clairvoyant Advisory & Personal Mentoring" : "Clairvoyant rådgivning & personlig sparring"}
              </h4>
              <p className="text-stone-600 text-xs font-light line-clamp-2">
                {isEn 
                  ? "Do you need clarity and insight? Gain clairvoyant guidance and mentoring."
                  : "Har du brug for klarhed og indsigt? Få clairvoyant rådgivning og personlig sparring."}
              </p>
            </Link>
            <Link
              to={isEn ? "/en/sessions/mentor-program" : "/sessioner/mentorforloeb"}
              className="p-6 bg-white border border-stone-200/80 rounded-sm hover:border-[#817262] hover:shadow-sm transition-all group"
            >
              <span className="text-[11px] uppercase tracking-widest text-[#817262] font-bold block mb-1">
                {isEn ? "Transformation" : "Dyb Transformation"}
              </span>
              <h4 className="text-lg font-serif text-stone-900 group-hover:text-[#817262] transition-colors mb-2">
                {isEn ? "1:1 Mentoring & Development Program" : "Mentor- og udviklingsforløb 1:1"}
              </h4>
              <p className="text-stone-600 text-xs font-light line-clamp-2">
                {isEn 
                  ? "Deep transformation & meditation for integrating heart intelligence into your daily life."
                  : "Dyb transformation & meditation for dig, der vil integrere hjertets intelligens i din hverdag."}
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* Kontakt & Booking CTA */}
      <section className="py-24 bg-white px-6 border-t border-stone-100">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <FadeIn>
            <h2 className="text-3xl md:text-4xl font-serif text-stone-900">{isEn ? "Contact & Booking" : "Kontakt & booking"}</h2>
            <p className="text-stone-600 font-light text-lg max-w-2xl mx-auto leading-relaxed">
              {bookingText}
              <a 
                href={`mailto:${bookingEmail}`} 
                className="text-lg font-medium text-stone-900 underline decoration-[#817262] hover:decoration-stone-900 transition-all"
              >
                {bookingEmail}
              </a>
            </p>
            {bookingPhone && (
              <div className="pt-4">
                <a 
                  href={`tel:+45${bookingPhone.replace(/\s+/g, '')}`} 
                  className="inline-flex items-center gap-2.5 px-6 py-3.5 bg-[#817262] text-white hover:bg-stone-800 transition-colors uppercase text-xs tracking-widest font-bold rounded-sm shadow-sm"
                >
                  <i className="fa-solid fa-phone text-xs" />
                  <span>{isEn ? `Call to hear more: +45 ${bookingPhone}` : `Ring, og hør mere: ${bookingPhone}`}</span>
                </a>
              </div>
            )}
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

/* 3. Underside: Spirituelt Mentor- og Udviklingsforløb 1:1 */
const MentorforloebSessionPage = () => {
  const { lang } = useLanguage();
  const isEn = lang === 'en';
  const { sessions } = useCms();
  const session = sessions?.find(s => (s.url_slug?.da || s.url_slug) === 'mentorforloeb' || (s.url_slug?.en || s.url_slug) === 'mentor-program');

  const title = session?.titel?.[lang] || session?.titel?.da || (isEn ? "Spiritual Mentoring Program" : "Spirituelt Mentorforløb");
  const badge = session?.kategori_badge?.[lang] || session?.kategori_badge?.da || (isEn ? "Deep Transformation" : "Dyb Transformation");
  const subNav = session?.undertitel?.[lang] || session?.undertitel?.da || (isEn ? "1:1 Mentoring Program" : "Mentorforløb 1:1");
  const heroImage = session?.hero_billede?.[0] || "https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/billede-til-merete.jpg";
  const introQuote = session?.intro_citat?.[lang] || session?.intro_citat?.da || (isEn 
    ? "Deep transformation & meditation for integrating heart intelligence into your daily life."
    : "Dyb transformation & meditation for dig, der vil integrere hjertets intelligens i din hverdag.");
  const introHtml = session?.intro_tekst?.[lang] || session?.intro_tekst?.da;
  const processTitle = session?.saadan_foregaar_det_titel?.[lang] || session?.saadan_foregaar_det_titel?.da || (isEn ? "How the mentoring journey works" : "Sådan foregår forløbet");
  const processHtml = session?.saadan_foregaar_det_tekst?.[lang] || session?.saadan_foregaar_det_tekst?.da;
  const takeawayTitle = session?.udbytte_titel?.[lang] || session?.udbytte_titel?.da || (isEn ? "Your takeaways" : "Dit udbytte");
  const takeawayHtml = session?.udbytte_tekst?.[lang] || session?.udbytte_tekst?.da;
  const topicsTitle = session?.emner_titel?.[lang] || session?.emner_titel?.da || (isEn ? "The program includes" : "Forløbet indeholder blandt andet");
  const topicsHtml = session?.emner_punkter?.[lang] || session?.emner_punkter?.da;
  const videoUrl = session?.video_url || "https://youtube.com/shorts/8g5-MMfl_do?feature=share";
  const priceTitle = session?.pris_titel?.[lang] || session?.pris_titel?.da || (isEn ? "Price" : "Pris");
  const priceHtml = session?.pris_detaljer?.[lang] || session?.pris_detaljer?.da;
  const bookingText = session?.booking_tekst?.[lang] || session?.booking_tekst?.da || (isEn ? "Book your session by emailing " : "Du booker din tid ved at skrive til mig på ");
  const bookingEmail = session?.booking_email || "mette@mettebrudvig.dk";
  const bookingPhone = session?.booking_telefon || "27 50 70 83";
  const testimonialName = session?.udtalelse_navn || "Else Støchkel";
  const testimonialTitle = session?.udtalelse_titel?.[lang] || session?.udtalelse_titel?.da || (isEn ? "Client Testimonial • Mentoring Program" : "Udtalelse fra klient • Mentorforløb");

  useEffect(() => {
    document.title = isEn 
      ? `Mette Brudvig - ${title}` 
      : `Mette Brudvig - ${title}`;
    window.scrollTo(0, 0);
  }, [isEn, title]);

  return (
    <div className="bg-[#faf9f6] min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] md:min-h-[70vh] pt-[185px] md:pt-[215px] lg:pt-[225px] pb-12 flex items-center bg-[#f5efe9] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center z-10 w-full">
          <FadeIn direction="right">
            <span className="text-xs uppercase tracking-[0.4em] font-bold text-[#817262] mb-4 block">
              {badge}
            </span>
            <h1 className="text-4xl md:text-6xl font-serif text-stone-900 leading-[1.1] mb-6">
              {title}
            </h1>
            <p className="text-lg md:text-xl text-stone-700 font-light leading-relaxed max-w-lg">
              {introQuote}
            </p>
          </FadeIn>
        </div>
        <div className="absolute top-0 right-0 w-full md:w-1/2 h-full opacity-20 md:opacity-100 pt-20 md:pt-24 lg:pt-28 pointer-events-none">
          <img 
            src={heroImage} 
            alt={`Mette Brudvig ${title}`} 
            className="w-full h-full object-cover object-top pointer-events-none"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#f5efe9] via-transparent to-transparent hidden md:block pointer-events-none" />
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-20 md:py-28 bg-white px-6">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
              {/* Left Column: Content */}
              <div className="lg:col-span-7 space-y-10">
                {introHtml ? (
                  <div 
                    className="space-y-6 text-stone-700 font-light text-lg leading-relaxed [&>p]:mb-4"
                    dangerouslySetInnerHTML={{ __html: introHtml }}
                  />
                ) : (
                  <div className="space-y-6 text-stone-700 font-light text-lg leading-relaxed">
                    <p>
                      {isEn
                        ? "A tailored, 1:1 journey for individuals seeking a dedicated container to anchor heart intelligence in daily life. We work intensively with meditation, energy shifts, and releasing old boundaries."
                        : "Et skræddersyet, individuelt forløb for dig, der ønsker et længere, dedikeret rum til at integrere hjertets intelligens i din hverdag. Vi arbejder intensivt med meditation, energi-skift og at slippe de gamle rammer."}
                    </p>
                  </div>
                )}

                <div className="bg-[#817262]/5 p-8 md:p-10 rounded-sm border border-[#817262]/20 space-y-4">
                  <h3 className="text-xl font-serif text-stone-900">{takeawayTitle}</h3>
                  {takeawayHtml ? (
                    <div 
                      className="space-y-3 text-stone-600 font-light text-base leading-relaxed [&>p]:mb-2"
                      dangerouslySetInnerHTML={{ __html: takeawayHtml }}
                    />
                  ) : (
                    <div className="space-y-3 text-stone-600 font-light text-base leading-relaxed">
                      <p>
                        {isEn 
                          ? "A deeply transformative journey helping you integrate intuition and heart intelligence into daily life so you can stand grounded and act with conscious alignment."
                          : "Du får et dybt transformerende forløb, der hjælper dig med at integrere din intuition og hjertets intelligens i din hverdag, så du kan stå stærkt i dig selv og handle med bevidsthed."}
                      </p>
                    </div>
                  )}
                </div>

                {/* Customer Quote Callout */}
                <TestimonialCallout 
                  testimonialName={testimonialName} 
                  customTitle={testimonialTitle} 
                />
              </div>

              {/* Right Column: Bullets & Pricing Card */}
              <div className="lg:col-span-5 space-y-8 lg:sticky lg:top-40">
                <div className="bg-stone-50 p-8 md:p-10 border border-stone-100 rounded-sm space-y-6">
                  <h3 className="text-lg uppercase tracking-wider font-bold text-stone-800">
                    {topicsTitle}
                  </h3>
                  {topicsHtml ? (
                    <div 
                      className="[&_ul]:space-y-4 [&_li]:flex [&_li]:items-start [&_li]:text-stone-700 [&_li]:font-light [&_li]:text-base [&_li]:before:content-['✧'] [&_li]:before:text-[#817262] [&_li]:before:mr-3 [&_li]:before:font-serif [&_li]:before:text-lg [&_li]:before:leading-none"
                      dangerouslySetInnerHTML={{ __html: topicsHtml }}
                    />
                  ) : (
                    <ul className="space-y-4">
                      {(isEn ? [
                        "Tailored multi-session journey",
                        "Intensive practice in meditation and intuition",
                        "Ongoing mentoring and energy work between sessions",
                        "Guided meditations tailored to your personal path"
                      ] : [
                        "Skræddersyet forløb over flere sessioner",
                        "Intensiv træning i meditation og intuition",
                        "Løbende sparring og energiarbejde mellem sessioner",
                        "Guidede meditationer tilpasset dine specifikke behov"
                      ]).map((item, idx) => (
                        <li key={idx} className="flex items-start text-stone-700 font-light text-base">
                          <span className="text-[#817262] mr-3 font-serif text-lg leading-none">✧</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {videoUrl && (
                    <div className="pt-2">
                      <YouTubeEmbed 
                        url={videoUrl} 
                        title={isEn ? `Intro to ${title}` : `Intro til ${title}`} 
                        className="w-full max-w-full"
                      />
                    </div>
                  )}
                </div>

                <div className="bg-[#f5efe9] p-8 md:p-10 border border-[#817262]/20 rounded-sm space-y-6 shadow-sm">
                  <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-[#817262]">{priceTitle}</h4>
                  <div className="space-y-4">
                    {priceHtml ? (
                      <div 
                        className="text-stone-700 font-light space-y-2 text-sm leading-relaxed [&>p]:flex [&>p]:justify-between [&>p]:items-baseline [&_strong]:text-lg [&_strong]:font-bold [&_strong]:text-stone-900"
                        dangerouslySetInnerHTML={{ __html: priceHtml }}
                      />
                    ) : (
                      <div className="flex justify-between items-baseline">
                        <span className="text-stone-600 font-light">{isEn ? "10 sessions" : "10 sessioner"}</span>
                        <span className="text-lg font-bold text-stone-900">8.500 kr.</span>
                      </div>
                    )}
                  </div>
                  <div className="pt-4 border-t border-[#817262]/10 space-y-2">
                    <h5 className="text-sm font-serif font-bold text-stone-900">{isEn ? "Contact & Booking" : "Kontakt & booking"}</h5>
                    <p className="text-xs text-stone-600 font-light">
                      {bookingText}
                      <a href={`mailto:${bookingEmail}`} className="underline font-medium text-[#817262] hover:text-stone-900">
                        {bookingEmail}
                      </a>
                    </p>
                    {bookingPhone && (
                      <div className="pt-2">
                        <a 
                          href={`tel:+45${bookingPhone.replace(/\s+/g, '')}`} 
                          className="inline-flex items-center gap-2 text-xs font-semibold text-[#817262] hover:text-stone-900 transition-colors"
                        >
                          <i className="fa-solid fa-phone text-xs" />
                          <span>{isEn ? `Call to hear more: +45 ${bookingPhone}` : `Ring, og hør mere: ${bookingPhone}`}</span>
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Navigation to other sessions */}
      <section className="py-16 bg-[#faf9f6] border-t border-stone-200/70 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <span className="text-xs uppercase tracking-widest text-[#817262] font-bold block mb-2">
              {isEn ? "Explore Other Sessions" : "Udforsk Øvrige Sessioner"}
            </span>
            <h3 className="text-2xl font-serif text-stone-900">
              {isEn ? "More 1:1 offerings" : "Flere individuelle muligheder"}
            </h3>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              to={isEn ? "/en/sessions/clairvoyant-mentoring" : "/sessioner/clairvoyant-raadgivning"}
              className="p-6 bg-white border border-stone-200/80 rounded-sm hover:border-[#817262] hover:shadow-sm transition-all group"
            >
              <span className="text-[11px] uppercase tracking-widest text-[#817262] font-bold block mb-1">
                {isEn ? "Personal Mentoring" : "Personlig Sparring"}
              </span>
              <h4 className="text-lg font-serif text-stone-900 group-hover:text-[#817262] transition-colors mb-2">
                {isEn ? "Clairvoyant Advisory & Personal Mentoring" : "Clairvoyant rådgivning & personlig sparring"}
              </h4>
              <p className="text-stone-600 text-xs font-light line-clamp-2">
                {isEn 
                  ? "Do you need clarity and insight? Gain clairvoyant guidance and mentoring."
                  : "Har du brug for klarhed og indsigt? Få clairvoyant rådgivning og personlig sparring."}
              </p>
            </Link>
            <Link
              to={isEn ? "/en/sessions/leadership-mentoring" : "/sessioner/ledersparring"}
              className="p-6 bg-white border border-stone-200/80 rounded-sm hover:border-[#817262] hover:shadow-sm transition-all group"
            >
              <span className="text-[11px] uppercase tracking-widest text-[#817262] font-bold block mb-1">
                {isEn ? "Executive Mentoring" : "Erhverv & Ledelse"}
              </span>
              <h4 className="text-lg font-serif text-stone-900 group-hover:text-[#817262] transition-colors mb-2">
                {isEn ? "Leadership Mentoring in New Era Leadership" : "Ledersparring i Ny Tids Lederskab"}
              </h4>
              <p className="text-stone-600 text-xs font-light line-clamp-2">
                {isEn 
                  ? "Strategic clarity, heightened intuition, and human insight to strengthen your leadership."
                  : "Strategisk klarhed, skærpet intuition og menneskelig indsigt til at styrke dit lederskab."}
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* Kontakt & Booking CTA */}
      <section className="py-24 bg-white px-6 border-t border-stone-100">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <FadeIn>
            <h2 className="text-3xl md:text-4xl font-serif text-stone-900">{isEn ? "Contact & Booking" : "Kontakt & booking"}</h2>
            <p className="text-stone-600 font-light text-lg max-w-2xl mx-auto leading-relaxed">
              {bookingText}
              <a 
                href={`mailto:${bookingEmail}`} 
                className="text-lg font-medium text-stone-900 underline decoration-[#817262] hover:decoration-stone-900 transition-all"
              >
                {bookingEmail}
              </a>
            </p>
            {bookingPhone && (
              <div className="pt-4">
                <a 
                  href={`tel:+45${bookingPhone.replace(/\s+/g, '')}`} 
                  className="inline-flex items-center gap-2.5 px-6 py-3.5 bg-[#817262] text-white hover:bg-stone-800 transition-colors uppercase text-xs tracking-widest font-bold rounded-sm shadow-sm"
                >
                  <i className="fa-solid fa-phone text-xs" />
                  <span>{isEn ? `Call to hear more: +45 ${bookingPhone}` : `Ring, og hør mere: ${bookingPhone}`}</span>
                </a>
              </div>
            )}
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

const parseModuleItems = (htmlOrItems) => {
  if (Array.isArray(htmlOrItems)) return htmlOrItems;
  if (!htmlOrItems || typeof htmlOrItems !== 'string') return [];
  const liMatches = [...htmlOrItems.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)];
  if (liMatches.length > 0) {
    return liMatches.map(m => m[1].replace(/<[^>]+>/g, '').trim()).filter(Boolean);
  }
  const pMatches = [...htmlOrItems.matchAll(/<p[^>]*>([\s\S]*?)<\/p>/gi)];
  if (pMatches.length > 0) {
    return pMatches.map(m => m[1].replace(/<[^>]+>/g, '').trim()).filter(Boolean);
  }
  return htmlOrItems.split('\n').map(s => s.trim()).filter(Boolean);
};

const FrisaetDigPage = () => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [showAllModules, setShowAllModules] = useState(true);
  const [showMoreProducts, setShowMoreProducts] = useState(false);
  const { lang, t } = useLanguage();
  const location = useLocation();
  const { frisaetDig, frisaetDigModuler } = useCms();

  const heroBadge = frisaetDig?.hero_badge?.[lang] || frisaetDig?.hero_badge?.da || t('frisaetDigPage.badge');
  const heroTitle = frisaetDig?.hero_titel?.[lang] || frisaetDig?.hero_titel?.da || (lang === 'en' ? "Unleash Yourself" : "Frisæt dig");
  const heroTagline = frisaetDig?.hero_tagline?.[lang] || frisaetDig?.hero_tagline?.da || (lang === 'en' 
    ? "– Development program in personal freedom and inner leadership, so you can discover your full potential and create the life you dream of." 
    : "– Spirituelt udviklingsforløb i personlig frisættelse og indre lederskab, så du kan opdage dit fulde potentiale og skabe det liv, du drømmer om.");
  const heroImage = frisaetDig?.hero_billede?.[0] || "https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/image_14.png";
  const videoUrl = frisaetDig?.video_url || "https://youtu.be/tgftDIXSBkw?si=JyV7xRh2Xh67gOns";

  const introBadge = frisaetDig?.intro_badge?.[lang] || frisaetDig?.intro_badge?.da || (lang === 'en' ? "Unleash Yourself Program" : "Frisæt dig forløb");
  const introHtml = frisaetDig?.intro_tekst?.[lang] || frisaetDig?.intro_tekst?.da;
  const introFremhaevet = frisaetDig?.intro_fremhaevet?.[lang] || frisaetDig?.intro_fremhaevet?.da || (lang === 'en'
    ? "You work with your personal and spiritual freedom and your personal leadership, so you become better at feeling yourself, trusting your intuition, staying true to yourself, and acting authentically."
    : "Du arbejder med din personlige og spirituelle frisættelse og dit personlige lederskab, så du bliver bedre til at mærke dig selv, stole på din intuition, være tro med dig selv og handle autentisk");
  const introBillede = frisaetDig?.intro_billede?.[0] || "/kundebilleder/Mette+som+underviser.jpg";
  const introCitat = frisaetDig?.intro_citat?.[lang] || frisaetDig?.intro_citat?.da || (lang === 'en'
    ? "“A carefully structured journey home to yourself and your soul essence, so you can discover and unfold your full potential and create the life you dream of.”"
    : "“En nøje tilrettelagt rejse hjem til dig selv og din sjælsessens, så du kan opdage og udfolde dit fulde potentiale og skabe det liv, du drømmer om.”");

  const formaalBadge = frisaetDig?.formaal_badge?.[lang] || frisaetDig?.formaal_badge?.da || (lang === 'en' ? "The Work" : "Formål & Retning");
  const formaalTitle = frisaetDig?.formaal_titel?.[lang] || frisaetDig?.formaal_titel?.da || (lang === 'en' ? "What you work towards" : "Det arbejder du hen imod");
  const formaalHtml = frisaetDig?.formaal_tekst?.[lang] || frisaetDig?.formaal_tekst?.da;
  const formaalFremhaevet = frisaetDig?.formaal_fremhaevet?.[lang] || frisaetDig?.formaal_fremhaevet?.da || (lang === 'en'
    ? "In short, the Unleash Yourself program is about setting yourself free to be who you are and create the life you dream of."
    : "Kort fortalt handler Frisæt dig forløbet om at sætte dig selv fri til at være den, du er, og skabe det liv, du drømmer om.");
  const formaalBillede = frisaetDig?.formaal_billede?.[0] || "/kundebilleder/MetteBrudvigOkt2021-125.jpg";

  const modulerBadge = frisaetDig?.moduler_badge?.[lang] || frisaetDig?.moduler_badge?.da || (lang === 'en' ? "Curriculum" : "Moduloversigt");
  const modulerTitle = frisaetDig?.moduler_titel?.[lang] || frisaetDig?.moduler_titel?.da || (lang === 'en' ? "Modules" : "Moduler");
  const modulerAfsluttendeHtml = frisaetDig?.moduler_afsluttende_tekst?.[lang] || frisaetDig?.moduler_afsluttende_tekst?.da;

  const fallbackModules = [
    {
      title: lang === 'en' ? "Module 1: Your Transformation" : "Modul 1: Din transformation",
      intro: lang === 'en' ? "You sense where you stand now and what kind of life you wish to move towards." : "Du mærker, hvor du står nu, og hvilket liv du ønsker at bevæge dig hen imod.",
      items: lang === 'en' ? [
        "Meditation and introduction to the program",
        "When life leads the way – my own story",
        "Where are you already courageous, proactive, and trusting in life?",
        "Where are you holding yourself back?",
        "What do you wish to unleash yourself from?",
        "How do you wish to feel when the program is complete?",
        "You set a clear intention for your transformation"
      ] : [
        "Meditation og introduktion til forløbet",
        "Når livet fører an – min egen historie",
        "Hvor er du allerede modig, handlekraftig og i tillid til livet?",
        "Hvor holder du dig selv tilbage?",
        "Hvad ønsker du at frisætte dig fra?",
        "Hvordan ønsker du at have det, når forløbet er slut?",
        "Du sætter en tydelig intention for din transformation"
      ]
    },
    {
      title: lang === 'en' ? "Module 2: Understand Yourself as Energy" : "Modul 2: Forstå dig selv som energi",
      intro: lang === 'en' ? "You learn to understand how your energy creates your life and how you can transform it." : "Du lærer at forstå, hvordan din energi skaber dit liv, og hvordan du kan ændre den.",
      items: lang === 'en' ? [
        "What is energy and frequency?",
        "Where is your energy right now?",
        "When do you vibrate with a clear signal, and when is there 'noise' on the line?",
        "Authenticity, resonance, and body signals",
        "Past and future exist only in your thoughts and patterns – learn to live in the present",
        "Learn to access the space of stillness where you can create anew",
        "Neuroplasticity of the brain and creating new programming",
        "How to shift your energy and create new outcomes",
        "Meditation to feel your core energy and learn how to return to it"
      ] : [
        "Hvad er energi og frekvens?",
        "Hvor er din energi lige nu?",
        "Hvornår vibrerer du med et klart signal, og hvornår er der \u201dstøj\u201d på linjen?",
        "Autenticitet, resonans og kroppens signaler",
        "Fortid og fremtid findes kun i dine tanker og mønstre – lær at leve i nuet",
        "Lær at tilgå tomrummet, hvor du kan skabe nyt",
        "Hjernens foranderlighed og at skabe nye programmer",
        "Sådan ændrer du din energi og skaber nye resultater",
        "Meditation, hvor du mærker din egen grundenergi og lærer at finde tilbage i den"
      ]
    },
    {
      title: lang === 'en' ? "Module 3: Put the Soul in the Driver's Seat" : "Modul 3: Sjælen i førersædet",
      intro: lang === 'en' ? "You learn to distinguish between the soul's guidance and the ego's fear, control, triggers, and old narratives." : "Du lærer at skelne mellem sjælens vejledning og egoets frygt, kontrol, triggers og gamle fortællinger.",
      items: lang === 'en' ? [
        "Understanding soul and ego",
        "The logical brain and heart intelligence",
        "How does soul guidance feel?",
        "How to release fear, control, and resistance",
        "Who has been in the driver's seat of your life?",
        "How to put your soul in the driver's seat",
        "How to strengthen your heart intelligence",
        "Meditation to connect deeply with your soul"
      ] : [
        "Forståelse af sjæl og ego",
        "Den logiske hjerne og hjertets intelligens",
        "Hvordan føles sjælens vejledning?",
        "Hvordan du slipper frygt, kontrol og modstand",
        "Hvem har siddet i førersædet i dit liv?",
        "Sådan får du sjælen i førersædet",
        "Sådan styrker du hjertets intelligens",
        "Meditation, hvor du forbinder dig med din sjæl"
      ]
    },
    {
      title: lang === 'en' ? "Module 4: Become Conscious" : "Modul 4: Bliv bevidst",
      intro: lang === 'en' ? "You cannot change what you are not conscious of. Therefore, you learn to observe yourself without judgment while taking authentic command over your words and behavior." : "Du kan ikke ændre det, du ikke er bevidst om. Derfor skal du lære at betragte dig selv uden fordømmelse, mens du tager autentisk styring over dine ord og din adfærd.",
      items: lang === 'en' ? [
        "Observe 'your human self' in everyday situations",
        "What triggers you?",
        "Which automatic reactions do you repeat?",
        "Which roles do you step into?",
        "What do you say when you feel insecure?",
        "What do you do when you feel rejected, pressured, or overlooked?",
        "Hidden self-sabotage",
        "How to create a gap between impulse and action",
        "Become more free and more genuinely yourself"
      ] : [
        "Betragt \u201ddit menneske\u201d i hverdagens situationer",
        "Hvad aktiverer dig?",
        "Hvilke automatiske reaktioner gentager du?",
        "Hvilke roller træder du ind i?",
        "Hvad siger du, når du bliver utryg?",
        "Hvad gør du, når du føler dig afvist, presset eller overset?",
        "Skjult selvsabotage",
        "Sådan skaber du et mellemrum mellem impuls og handling",
        "Bliv mere fri og mere dig"
      ]
    },
    {
      title: lang === 'en' ? "Module 5: Shed Your Deadweight" : "Modul 5: Slip din dødvægt",
      intro: lang === 'en' ? "Now you will identify what should not come along with you further, and begin releasing it." : "Nu skal du identificere det, der ikke skal med dig videre, og du skal begynde at give slip.",
      items: lang === 'en' ? [
        "What are you carrying that no longer serves you?",
        "Which relationships, roles, and obligations drain you?",
        "Which perceptions of yourself are outdated?",
        "Cast off your deadweight",
        "What doesn't belong in the new, free version of you?",
        "The fear of letting go – how your brain resists change",
        "How it feels when energy begins to release",
        "The labor of birthing the new – discomfort when releasing the old (growth phases)",
        "Guided meditation for letting go"
      ] : [
        "Hvad har du med, som ikke længere tjener dig?",
        "Hvilke relationer, roller og forpligtelser dræner dig?",
        "Hvilke forestillinger om dig selv er forældede?",
        "Smid din dødvægt",
        "Hvad hører ikke til i den nye frie version af dig?",
        "Frygten for at give slip – din hjerne modarbejder dig",
        "Sådan føles det, når energi begynder at slippe",
        "Fødselsarbejdet – ubehaget ved at slippe det gamle (vækstfaser)",
        "Guidet meditation, hvor du giver slip"
      ]
    },
    {
      title: lang === 'en' ? "Module 6: Healing and Self-Love" : "Modul 6: Healing og selvkærlighed",
      intro: lang === 'en' ? "You go deeper into the experiences, patterns, and energies that have kept you stuck. You work with self-love, gentleness, and effectiveness." : "Du går dybere ind i de erfaringer, mønstre og energier, der har holdt dig fast. Du arbejder selvkærligt, nænsom og effektivt.",
      items: lang === 'en' ? [
        "Releasing stagnant energy from traumas and emotional footprints",
        "Outdated beliefs vs. feeling and reacting from your core",
        "Recurring patterns in your life",
        "Inner child healing",
        "The chakras and their significance",
        "Chakra healing",
        "Past lives – karmic healing",
        "Forgiveness and freeing your story",
        "Releasing pain and carrying the learnings forward"
      ] : [
        "At slippe stagneret energi fra traumer og følelsesmæssige aftryk",
        "Forældede overbevisninger frem for at føle og reagere fra din kerne",
        "Mønstre, der går igen i dit liv",
        "Indre barn healing",
        "Chakraerne og deres betydning",
        "Chakrahealing",
        "Tidligere liv – karmisk healing",
        "Tilgivelse og frisættelse af din historie",
        "At slippe smerten og tage læringen med videre"
      ]
    },
    {
      title: lang === 'en' ? "Module 7: Discover Your Inherent Gifts and Purpose" : "Modul 7: Find dine iboende gaver og dit formål",
      intro: lang === 'en' ? "You connect with your inherent gifts, passion, and purpose, becoming conscious of how to bring them to life." : "Du finder ind til dine iboende gaver, din passion og dit formål og bliver bevidst om, hvordan du kan omsætte dem i dit liv.",
      items: lang === 'en' ? [
        "Identifying the gifts and abilities that lie naturally within you",
        "Gain insight into your soul's blueprint, direction, and purpose (automatic writing)",
        "What brings you authentic joy, energy, and freedom",
        "Understanding your passion as guidance from your soul",
        "How to give your gifts and passion more space in your life"
      ] : [
        "Du identificerer de gaver og evner, der ligger naturligt i dig",
        "Du får indblik i din sjæls blueprint, retning og dit formål (automatskrivning)",
        "Hvad der giver dig ægte glæde, energi og frihed",
        "Forstå din passion som vejledning fra din sjæl",
        "Sådan giver du dine gaver og din passion mere plads i dit liv"
      ]
    },
    {
      title: lang === 'en' ? "Module 8: Create Connection to Your Dream Life" : "Modul 8: Skab forbindelsen til dit drømmeliv",
      intro: lang === 'en' ? "You discover the version of you and the life you dream of, building a strong inner and energetic connection to it." : "Du finder frem til den version af dig og det liv, du drømmer om, og du begynder at skabe en stærk indre og energetisk forbindelse til det.",
      items: lang === 'en' ? [
        "Who are you in the life you dream of?",
        "How does the new version of you think, feel, and act?",
        "Guidance to build concrete scenes from your desired life to use in meditation",
        "Guided training to feel and embody already being this new version of you",
        "Energetic practices to attract and manifest the new in your life"
      ] : [
        "Hvem er du i det liv, du drømmer om?",
        "Hvordan tænker, føler og handler den nye version af dig?",
        "Du får hjælp til at skabe konkrete scener fra dit ønskeliv, som du skal bruge i meditation",
        "Jeg guider dig til at mærke og træne følelsen af allerede at være den nye version af dig",
        "Energetiske øvelser, der hjælper dig med at tiltrække og skabe det nye i dit liv"
      ]
    },
    {
      title: lang === 'en' ? "Module 9: Step Into the New Version of You" : "Modul 9: Træd ind i den nye version af dig",
      intro: lang === 'en' ? "You begin living as the new version of yourself in daily life, practicing new choices, actions, and responses in concrete situations." : "Du begynder at leve som den nye version af dig i din hverdag og træner nye valg, handlinger og reaktioner i konkrete situationer.",
      items: lang === 'en' ? [
        "How to embody the new version of you in daily life",
        "New choices, actions, and habits",
        "Standing firm as yourself in relationships and at work",
        "Recognizing when old patterns take over",
        "Mentoring and training to return to the new version of you"
      ] : [
        "Sådan bliver du til den nye version af dig i din hverdag",
        "Nye valg, handlinger og vaner",
        "Sådan står du ved dig selv i relationer og på arbejde",
        "Hvordan du opdager, når gamle mønstre tager over",
        "Sparring og træning i at finde tilbage til den nye version af dig"
      ]
    },
    {
      title: lang === 'en' ? "Module 10: In Flow With Life" : "Modul 10: I flow med livet",
      intro: lang === 'en' ? "You learn to read signs, synchronicities, and repetitions as a language, becoming better at co-creating with what life presents." : "Du lærer at læse tegn, synkroniciteter og gentagelser som et sprog og bliver bedre til at samarbejde med det, livet viser dig.",
      items: lang === 'en' ? [
        "How to recognize signs and synchronicities in your life",
        "What repetitions and meaningful events reveal to you",
        "The difference between forcing vs. following what opens up (an art of balance!)",
        "When to act and when to wait?",
        "How to consciously and trustingly co-create with life, letting life lead"
      ] : [
        "Sådan genkender du tegn og synkroniciteter i dit liv",
        "Hvad gentagelser og meningsfulde begivenheder fortæller dig",
        "Forskellen på at presse og på at følge det, der åbner sig (en balancekunst!)",
        "Hvornår du skal handle, og hvornår du skal vente?",
        "Sådan samarbejder du mere bevidst og tillidsfuldt med livet og lader livet fører an"
      ]
    },
    {
      title: lang === 'en' ? "Module 11: Stand Firm in the New Version of You" : "Modul 11: Stå fast i den nye version af dig",
      intro: lang === 'en' ? "You evaluate where you stand now, what you have transformed, and how to strengthen the new version of you so you hold firm even when life presents challenges." : "Du ser på, hvor du står nu, hvad du har forandret, og hvordan du styrker den nye version af dig, så du kan holde fast. Også når livet udfordrer dig.",
      items: lang === 'en' ? [
        "Where do you stand now compared to your original goal?",
        "What have you released and transformed?",
        "What will you continue working with?",
        "How to reinforce the new version of you",
        "Your path forward after the program"
      ] : [
        "Hvor står du nu i forhold til dit oprindelige mål?",
        "Hvad har du sluppet og forandret?",
        "Hvad skal du fortsat arbejde med?",
        "Sådan styrker du den nye version af dig",
        "Din vej videre efter forløbet"
      ]
    }
  ];

  const displayedModules = useMemo(() => {
    if (frisaetDigModuler && Array.isArray(frisaetDigModuler) && frisaetDigModuler.length > 0) {
      return frisaetDigModuler.map((mod, idx) => {
        const title = mod.titel?.[lang] || mod.titel?.da || (typeof mod.titel === 'string' ? mod.titel : '') || fallbackModules[idx]?.title || '';
        const intro = mod.undertitel?.[lang] || mod.undertitel?.da || (typeof mod.undertitel === 'string' ? mod.undertitel : '') || fallbackModules[idx]?.intro || '';
        const rawContent = mod.indhold?.[lang] || mod.indhold?.da || (typeof mod.indhold === 'string' ? mod.indhold : '');
        
        let items = parseModuleItems(rawContent);
        if (!items || items.length === 0) {
          items = fallbackModules[idx]?.items || [];
        }

        return {
          id: mod.id || idx,
          title,
          intro,
          items
        };
      });
    }
    return fallbackModules.map((m, idx) => ({ id: idx, ...m }));
  }, [frisaetDigModuler, lang]);

  useEffect(() => {
    document.title = lang === 'en' ? "Mette Brudvig - Unleash Yourself" : "Mette Brudvig - Frisæt dig";
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 150);
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [lang, location.hash]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsLightboxOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="bg-[#faf9f6] min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] pt-[185px] md:pt-[215px] lg:pt-[225px] pb-12 flex items-center bg-[#f5efe9] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-12 items-center z-10 w-full">
          <div className="md:col-span-7 space-y-6">
            <FadeIn direction="right">
              <span className="text-xs uppercase tracking-[0.4em] font-bold text-[#817262] mb-4 block">
                {heroBadge}
              </span>
              <h1 className="text-4xl md:text-6xl font-serif text-stone-900 leading-[1.1] mb-6">
                {heroTitle}
              </h1>
              <p className="text-xl md:text-2xl font-serif italic text-stone-800 leading-relaxed max-w-2xl mb-6">
                {heroTagline}
              </p>

              <div className="flex flex-wrap items-center gap-4 pt-2">
                <Link
                  to={lang === 'en' ? "/en/unleash-yourself/physical-program#holdstart-og-datoer" : "/frisaet-dig/fysisk-forloeb#holdstart-og-datoer"}
                  className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#817262] text-white hover:bg-stone-800 rounded-sm text-xs uppercase tracking-widest font-bold transition-all shadow-sm"
                >
                  <span>{lang === 'en' ? "Buy your spot in the education" : "Køb din plads på uddannelsen"}</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
                <Link
                  to={lang === 'en' ? "/en/contact?subject=Free 20-min conversation: Unleash Yourself" : "/kontakt?subject=Gratis%2020%20minutters%20samtale%3A%20Fris%C3%A6t%20dig"}
                  className="inline-flex items-center gap-2 px-6 py-3.5 bg-white text-stone-800 border border-[#817262]/30 hover:bg-[#f5efe9] rounded-sm text-xs uppercase tracking-widest font-bold transition-all shadow-2xs"
                >
                  <i className="fa-regular fa-calendar-check text-xs text-[#817262]" aria-hidden="true" />
                  <span>{lang === 'en' ? "Book 20-min conversation on Unleash Yourself" : "Book 20 min. samtale om frisæt dig"}</span>
                </Link>
              </div>
            </FadeIn>
          </div>
        </div>
        <div className="absolute top-0 right-0 w-full md:w-1/2 h-full opacity-15 md:opacity-100 pt-20 md:pt-24 lg:pt-28 pointer-events-none">
          <img 
            src={heroImage} 
            alt="Mette Brudvig - Frisæt dig" 
            className="w-full h-full object-cover object-top pointer-events-none"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#f5efe9] via-transparent to-transparent hidden md:block pointer-events-none" />
        </div>
      </section>

      {/* Sektion: Udtalelser fra deltagere (4 stk) */}
      <TestimonialsShowcase
        customItems={UDDANNELSE_TESTIMONIALS}
        badge={lang === 'en' ? "Testimonials About Mette as Teacher" : "Udtalelser om mig som underviser – til uddannelsen"}
        title={lang === 'en' ? "What participants experience with Mette as teacher" : "Udtalelser om Mette som underviser"}
        subtitle={lang === 'en' ? "Experiences from participants in the education." : "Hvad kursister og deltagere oplever sammen med Mette på uddannelsen."}
        className="py-20 md:py-24 bg-white px-6 border-b border-stone-200/60"
        cardBg="bg-white"
      />

      {/* ===== Frisæt dig forløb: Introduktion ===== */}
      <section className="pt-20 md:pt-24 pb-10 md:pb-12 bg-[#f3efe9] px-6 border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start mb-6">
              <div className="lg:col-span-7 space-y-6 order-2 lg:order-1">
                <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#817262] block">
                  {introBadge}
                </span>

                {introHtml ? (
                  <div 
                    className="text-stone-700 font-light text-base md:text-lg leading-relaxed space-y-4 [&>p]:mb-4"
                    dangerouslySetInnerHTML={{ __html: introHtml }}
                  />
                ) : (
                  <div className="text-stone-700 font-light text-base md:text-lg leading-relaxed space-y-5">
                    <p>
                      {lang === 'en'
                        ? "This is a transformative program for you who want to stand stronger in yourself, let go of what holds you back, and create a life you love."
                        : "Dette er et transformerende forløb til dig, der vil stå stærkere i dig selv, slippe det, der holder dig tilbage, og skabe et liv, du elsker."}
                    </p>
                    <p>
                      {lang === 'en'
                        ? "It is about finding your way to the core of who you are as a human being and soul, and finding the courage to be yourself. About finding peace in who you are, letting go of the need to adapt to others, and daring to walk the path you feel is right for you during the course."
                        : "Det handler om at finde ind til kernen af, hvem du er som menneske og sjæl og finde modet til at være dig selv. Om at finde ro i den, du er, slippe behovet for at tilpasse dig andre og turde gå den vej, du under forløbet mærker er den rigtige for dig."}
                    </p>
                  </div>
                )}

                <div className="bg-white p-6 sm:p-7 border-l-4 border-[#817262] rounded-r-sm shadow-2xs space-y-3">
                  <p className="text-stone-900 font-medium text-base sm:text-lg leading-relaxed">
                    {introFremhaevet}
                  </p>
                </div>

                {!introHtml && (
                  <p className="text-stone-700 font-light text-base md:text-lg leading-relaxed pt-1">
                    {lang === 'en'
                      ? "You increase your awareness and become more conscious of your patterns, your energy, your choices, and your direction, so you can act more in alignment with the life you wish to create."
                      : "Du øger din bevidsthed og bliver mere bevidst om dine mønstre, din energi, dine valg og din retning, så du kan handle mere i overensstemmelse med det liv, du ønsker at skabe."}
                  </p>
                )}

                <div className="pt-3 flex flex-row flex-wrap sm:flex-nowrap items-center gap-3 sm:gap-3.5">
                  <Link 
                    to={lang === 'en' ? "/en/unleash-yourself/physical-program#holdstart-og-datoer" : "/frisaet-dig/fysisk-forloeb#holdstart-og-datoer"}
                    className="inline-flex items-center justify-center gap-2 sm:gap-2.5 px-4 sm:px-6 py-3 sm:py-3.5 bg-[#817262] text-white hover:bg-stone-800 rounded-sm text-[11px] sm:text-xs uppercase tracking-wider sm:tracking-widest font-bold transition-all shadow-sm w-full sm:w-auto text-center"
                  >
                    <span>{lang === 'en' ? "Buy your spot in the education" : "Køb din plads på uddannelsen"}</span>
                    <i className="fa-solid fa-arrow-right text-xs shrink-0" aria-hidden="true" />
                  </Link>
                  <Link 
                    to={lang === 'en' ? "/en/contact?subject=Free 20-min conversation: Unleash Yourself" : "/kontakt?subject=Gratis%2020%20minutters%20samtale%3A%20Fris%C3%A6t%20dig"}
                    className="inline-flex items-center justify-center gap-2 sm:gap-2.5 px-4 sm:px-6 py-3 sm:py-3.5 bg-white text-stone-800 border border-stone-300 hover:bg-stone-50 rounded-sm text-[11px] sm:text-xs uppercase tracking-wider sm:tracking-widest font-bold transition-all shadow-2xs w-full sm:w-auto text-center"
                  >
                    <i className="fa-regular fa-calendar-check text-xs text-[#817262] shrink-0" aria-hidden="true" />
                    <span>{lang === 'en' ? "Book a conversation" : "Bestil en samtale"}</span>
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-5 space-y-6 order-1 lg:order-2">
                {/* Intro Video */}
                {videoUrl && (
                  <div id="video-intro" className="overflow-hidden rounded-sm">
                    <YouTubeEmbed 
                      url={videoUrl} 
                      title={lang === 'en' ? "Intro to Unleash Yourself Education" : "Intro til Frisæt dig uddannelsen"} 
                    />
                  </div>
                )}

                <div className="bg-[#817262] text-white p-6 rounded-sm space-y-3 shadow-xs">
                  <p className="font-serif text-base sm:text-lg leading-relaxed italic">
                    {introCitat}
                  </p>
                  <p className="text-xs uppercase tracking-widest font-bold text-white/90 pt-1">
                    — Mette Brudvig
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ===== Sektion: Moduler ===== */}
      <section id="moduler" className="pt-12 md:pt-14 pb-24 bg-white px-6 border-b border-stone-100 scroll-mt-24">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="mb-8 space-y-4">
              <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
                {modulerTitle}
              </h2>
            </div>

            {/* Modultekst */}
            <div className="space-y-4 mb-10 max-w-4xl">
              {modulerAfsluttendeHtml ? (
                <div 
                  className="text-stone-800 font-light text-base md:text-lg leading-relaxed space-y-4 [&>p]:mb-4"
                  dangerouslySetInnerHTML={{ __html: modulerAfsluttendeHtml }}
                />
              ) : (
                <div className="text-stone-800 font-light text-base md:text-lg leading-relaxed space-y-4">
                  <p>
                    {lang === 'en'
                      ? "The program is 360-degree personal development focusing on personal freedom and inner leadership. Thus, it is not only through my teaching and energy in the sessions that you develop. It also activates something in you on a soul level that works effectively between modules, creating true transformation and connection between body, mind, and soul."
                      : "Forløbet er 360 graders personlig udvikling med fokus på personlig frisættelse og indre lederskab. Dermed er det ikke kun gennem min læring og energi i undervisningen, du udvikler dig. Det aktiverer også noget i dig på sjælsplan, som arbejder effektivt videre mellem modulerne og skaber ægte transformation og forbindelse mellem krop, sind og sjæl."}
                  </p>
                  <p>
                    {lang === 'en'
                      ? "I have structured the content and modules to create safety, empowerment, and effective development. In short: a carefully structured journey home to yourself and your soul essence, so you can discover and unfold your full potential and create the life you dream of."
                      : "Jeg har tilrettelagt indhold og moduler, så det skaber tryghed, empowerment og effektiv udvikling. Kort sagt: en nøje tilrettelagt rejse hjem til dig selv og din sjælsessens, så du kan opdage og udfolde dit fulde potentiale og skabe det liv, du drømmer om."}
                  </p>
                </div>
              )}
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {displayedModules.slice(0, showAllModules ? undefined : 3).map((mod, idx) => (
              <FadeIn key={mod.id || idx} delay={idx * 30} className="bg-stone-50 p-6 border border-stone-200/60 rounded-sm shadow-sm flex flex-col space-y-4">
                <h4 className="font-serif text-lg text-stone-900">{mod.title}</h4>
                <p className="text-stone-600 font-light text-sm leading-relaxed">{mod.intro}</p>
                {mod.items && mod.items.length > 0 && (
                  <ul className="space-y-1.5 pt-2 border-t border-stone-200">
                    {mod.items.map((it, iIdx) => (
                      <li key={iIdx} className="flex items-start text-xs text-stone-600 font-light">
                        <i className="fa-light fa-sparkles text-[#817262] mt-0.5 mr-2 flex-shrink-0" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </FadeIn>
            ))}
          </div>

          <div className="text-center mt-10">
            <button
              onClick={() => setShowAllModules(!showAllModules)}
              className="inline-flex items-center gap-2 bg-[#817262] text-white px-8 py-3.5 uppercase text-[10px] tracking-widest font-bold hover:bg-stone-800 transition-colors rounded-sm shadow-sm cursor-pointer"
            >
              <span>
                {showAllModules 
                  ? (lang === 'en' ? "Show fewer modules" : "Vis færre moduler")
                  : (lang === 'en' ? "See all modules" : "Se flere moduler")
                }
              </span>
              <i className={`fa-light ${showAllModules ? 'fa-chevron-up' : 'fa-chevron-down'} text-xs`} />
            </button>
          </div>
        </div>
      </section>

      {/* ===== Sektion: Det arbejder du hen imod ===== */}
      <section className="py-20 md:py-24 bg-[#f5efe9]/30 px-6 border-b border-stone-200/60">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-5 order-2 lg:order-1">
                <div className="relative h-[340px] sm:h-[400px] rounded-sm overflow-hidden shadow-sm border border-stone-200/60">
                  <img 
                    src={formaalBillede} 
                    alt="Mette Brudvig" 
                    className="w-full h-full object-cover" 
                  />
                </div>
              </div>

              <div className="lg:col-span-7 order-1 lg:order-2 space-y-6">
                <h2 className="text-2xl md:text-4xl font-serif text-stone-900">
                  {formaalTitle}
                </h2>

                {formaalHtml ? (
                  <div 
                    className="text-stone-700 font-light text-base md:text-lg leading-relaxed space-y-4 [&>p]:mb-4"
                    dangerouslySetInnerHTML={{ __html: formaalHtml }}
                  />
                ) : (
                  <div className="text-stone-700 font-light text-base md:text-lg leading-relaxed space-y-5">
                    <p>
                      {lang === 'en'
                        ? <>The program combines teaching, <strong>meditation, reflection, exercises, and concrete work with your own life.</strong></>
                        : <>Forløbet kombinerer undervisning, <strong>meditation, refleksion, øvelser og konkret arbejde med dit eget liv.</strong></>}
                    </p>
                    <p>
                      {lang === 'en'
                        ? "I use my own experiences along the way, so you can understand the steps, recognize them in your own life, and use them in your own development."
                        : "Jeg bruger mine egne erfaringer undervejs, så du kan forstå trinene, genkende dem i dit eget liv og bruge dem i din egen udvikling."}
                    </p>
                  </div>
                )}

                <div className="p-6 bg-[#f5efe9] border border-[#817262]/20 border-l-4 border-l-[#817262] rounded-r-sm space-y-2 shadow-2xs">
                  <p className="font-serif italic text-stone-900 text-lg sm:text-xl leading-relaxed">
                    {formaalFremhaevet}
                  </p>
                </div>

                <div className="pt-2 flex flex-wrap gap-4 items-center">
                  <Link
                    to={lang === 'en' ? "/en/contact?subject=Free 20-min conversation: Unleash Yourself" : "/kontakt?subject=Gratis%2020%20minutters%20samtale%3A%20Fris%C3%A6t%20dig"}
                    className="inline-flex items-center gap-2 px-6 py-3.5 bg-white text-stone-800 border border-[#817262]/30 hover:bg-[#f5efe9] rounded-sm text-xs uppercase tracking-widest font-bold transition-all shadow-2xs"
                  >
                    <span>{lang === 'en' ? "Book 20-min conversation on Unleash Yourself" : "Book 20 min. samtale om frisæt dig"}</span>
                    <i className="fa-solid fa-arrow-right text-xs text-[#817262]" aria-hidden="true" />
                  </Link>
                  <a
                    href="#moduler"
                    className="inline-flex items-center gap-2 px-6 py-3.5 bg-white text-stone-800 border border-stone-300 hover:bg-[#f5efe9] rounded-sm text-xs uppercase tracking-widest font-bold transition-all"
                  >
                    <span>{lang === 'en' ? "See modules" : "Se moduler"}</span>
                    <i className="fa-solid fa-arrow-up text-xs text-[#817262]" aria-hidden="true" />
                  </a>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Embedded Produktoversigt */}
      <section id="vaelg-doer" className="py-24 bg-[#faf7f2] px-6 border-b border-stone-200/80 scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-serif text-stone-900 mb-4">
                {lang === 'en' ? "Choose your doorway to transformation" : "Vælg din dør ind til transformationen"}
              </h3>
              <p className="text-sm md:text-base text-stone-600 font-light max-w-2xl mx-auto">
                {lang === 'en' 
                  ? "Explore all the ways you can work with Unleash Yourself – from the transformative 1-year in-person education and intensive workshops to online mini-courses, individual mentoring, and keynotes."
                  : "Udforsk alle måder, du kan arbejde med Frisæt dig på – fra den transformerende 1-årige fysiske uddannelse og intensive workshops til online minikurser, individuelle forløb og foredrag."}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
              {/* Product 1: Fysisk årsforløb - Primary / Featured */}
              <div className="bg-white p-7 sm:p-8 border-2 border-[#817262] rounded-sm flex flex-col justify-between space-y-6 shadow-sm hover:shadow-md transition-all relative group">
                <div className="absolute -top-3.5 left-6 bg-[#817262] text-white text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1 rounded-xs shadow-2xs">
                  {lang === 'en' ? "1-Year In-Person" : "Fysisk Årsuddannelse"}
                </div>

                <div className="space-y-5 pt-2">
                  <div>
                    <h4 className="text-2xl font-serif text-stone-900">
                      {lang === 'en' ? "Physical 1-Year Program" : "Fysisk årsforløb"}
                    </h4>
                    <p className="text-xs text-stone-600 font-light mt-1.5 leading-relaxed">
                      {lang === 'en' 
                        ? "10 in-person module days over an entire year with deep teaching, healing, mentoring, and 1:1 session."
                        : "10 fysiske moduldage (kl. 13–18) over et helt år med dybdegående undervisning, energiarbejde og personlig sparring."}
                    </p>
                  </div>

                  {/* Price Section */}
                  <div className="pt-4 border-t border-stone-200/80 space-y-2.5">
                    <div>
                      <span className="text-[11px] uppercase tracking-widest text-stone-500 font-medium block">
                        {lang === 'en' ? "Total Price" : "Samlet pris"}
                      </span>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-serif font-bold text-stone-900">
                          {lang === 'en' ? "26,500 DKK" : "26.500 kr."}
                        </span>
                      </div>
                    </div>

                    {/* Ratebetaling Highlight */}
                    <div className="inline-flex items-center gap-2 bg-[#f5efe9] border border-[#817262]/30 text-[#817262] px-3 py-2 rounded-sm text-xs font-semibold w-full">
                      <i className="fa-regular fa-credit-card text-xs shrink-0" aria-hidden="true" />
                      <span>
                        {lang === 'en' 
                          ? "Can be paid in 3 installments (3 x 8,833 DKK)" 
                          : "Kan betales i 3 rater (3 x 8.833 kr.)"}
                      </span>
                    </div>

                    <div className="text-xs text-stone-700 font-medium space-y-1.5 pt-2 border-t border-stone-100">
                      <p className="text-[10px] uppercase tracking-wider font-bold text-[#817262]">
                        {lang === 'en' ? "Next holdstart 2027:" : "Dato for næste holdstart:"}
                      </p>
                      <p className="flex items-center gap-1.5 text-xs text-stone-800">
                        <i className="fa-regular fa-calendar text-[#817262] text-xs shrink-0" aria-hidden="true" />
                        <span><strong>Kbh:</strong> Onsdag d. 24. feb. 2027</span>
                      </p>
                      <p className="flex items-center gap-1.5 text-xs text-stone-800">
                        <i className="fa-regular fa-calendar text-[#817262] text-xs shrink-0" aria-hidden="true" />
                        <span><strong>Århus:</strong> Tirsdag d. 2. mar. 2027</span>
                      </p>
                      <Link 
                        to={lang === 'en' ? "/en/unleash-yourself/physical-program" : "/frisaet-dig/fysisk-forloeb"} 
                        className="inline-flex items-center gap-1 text-[11px] text-[#817262] font-bold hover:underline pt-0.5"
                      >
                        <span>{lang === 'en' ? "See all 10 module dates →" : "Se alle 10 moduldatoer her →"}</span>
                      </Link>
                    </div>
                  </div>

                  {/* Highlights */}
                  <ul className="space-y-2 text-xs text-stone-700 font-light pt-3 border-t border-stone-100">
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#817262] text-xs mt-0.5" aria-hidden="true" />
                      <span>10 hele moduldage (kl. 13.00–18.00) fordelt over et år</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#817262] text-xs mt-0.5" aria-hidden="true" />
                      <span>Inkl. 1:1 clairvoyant rådgivning med Mette</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#817262] text-xs mt-0.5" aria-hidden="true" />
                      <span>Små, trygge hold med fortroligt fællesskab</span>
                    </li>
                  </ul>
                </div>

                <Link 
                  to={lang === 'en' ? "/en/unleash-yourself/physical-program" : "/frisaet-dig/fysisk-forloeb"}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#817262] text-white py-3 px-4 uppercase text-[11px] tracking-widest font-bold hover:bg-stone-800 transition-colors rounded-sm shadow-xs"
                >
                  <span>{lang === 'en' ? "View physical program" : "Se det fysiske forløb"}</span>
                  <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </Link>
              </div>

              {/* Product 2: Frisæt dig endagsworkshop */}
              <div className="bg-white p-7 sm:p-8 border border-stone-200/90 rounded-sm flex flex-col justify-between space-y-6 shadow-xs hover:shadow-md transition-all group">
                <div className="space-y-5">
                  <div>
                    <h4 className="text-2xl font-serif text-stone-900">
                      {lang === 'en' ? "1-Day Workshop" : "Frisæt dig endagsworkshop"}
                    </h4>
                    <p className="text-xs text-stone-600 font-light mt-1.5 leading-relaxed">
                      {lang === 'en' 
                        ? "Intensive 1-day kickstart. Learn the 7 steps to freedom, heart leadership, and energy practices."
                        : "Intensiv 1-dags kickstart (kl. 10.00–16.00). Lær de 7 trin til frisættelse og få konkrete redskaber til hjertets lederskab."}
                    </p>
                  </div>

                  {/* Price Section */}
                  <div className="pt-4 border-t border-stone-200/80 space-y-2.5">
                    <div>
                      <span className="text-[11px] uppercase tracking-widest text-stone-500 font-medium block">
                        {lang === 'en' ? "Workshop Price" : "Pris pr. workshop"}
                      </span>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-serif font-bold text-stone-900">
                          {lang === 'en' ? "850 DKK" : "850 kr."}
                        </span>
                      </div>
                      <p className="text-[11px] text-stone-500 font-light mt-0.5">
                        {lang === 'en' ? "Intro price (normal 1,250 DKK)" : "Intropris 850 kr. (normalpris 1.250 kr.)"}
                      </p>
                    </div>

                    <div className="text-xs text-stone-500 font-light space-y-0.5 pt-1">
                      <p className="flex items-center gap-1.5">
                        <i className="fa-solid fa-location-dot text-[#817262] text-[10px]" aria-hidden="true" />
                        <span><strong>København</strong> (Halo House): 31/10 el. 1/11 2026</span>
                      </p>
                      <p className="flex items-center gap-1.5">
                        <i className="fa-solid fa-location-dot text-[#817262] text-[10px]" aria-hidden="true" />
                        <span><strong>Århus</strong> (Business United): 9/1 el. 10/1 2027</span>
                      </p>
                    </div>
                  </div>

                  {/* Highlights */}
                  <ul className="space-y-2 text-xs text-stone-700 font-light pt-3 border-t border-stone-100">
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#817262] text-xs mt-0.5" aria-hidden="true" />
                      <span>Gennemgang af de 7 trin til personlig frisættelse</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#817262] text-xs mt-0.5" aria-hidden="true" />
                      <span>Meditation, refleksionsøvelser og energiarbejde</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#817262] text-xs mt-0.5" aria-hidden="true" />
                      <span>De 850 kr. fratrækkes ved tilmelding til årsforløbet</span>
                    </li>
                  </ul>
                </div>

                <Link 
                  to={lang === 'en' ? "/en/unleash-yourself/workshop" : "/frisaet-dig/workshop"}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#817262] text-white py-3 px-4 uppercase text-[11px] tracking-widest font-bold hover:bg-stone-800 transition-colors rounded-sm shadow-xs"
                >
                  <span>{lang === 'en' ? "View 1-day workshop" : "Se endagsworkshop"}</span>
                  <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </Link>
              </div>

              {/* Product 3: Online minikursus */}
              <div className="bg-white p-7 sm:p-8 border border-stone-200/90 rounded-sm flex flex-col justify-between space-y-6 shadow-xs hover:shadow-md transition-all group">
                <div className="space-y-5">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-[10px] uppercase tracking-widest font-bold text-[#817262] block">
                        {lang === 'en' ? "Online Product" : "Online produkt"}
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#817262]/10 text-[#817262] text-[10px] uppercase tracking-wider font-bold">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#817262] animate-pulse" />
                        {lang === 'en' ? "Coming soon" : "Kommer snart"}
                      </span>
                    </div>
                    <h4 className="text-2xl font-serif text-stone-900">
                      {lang === 'en' ? "Mini-Course – Recording" : "Minikursus – optagelse"}
                    </h4>
                    <p className="text-xs text-stone-600 font-light mt-1.5 leading-relaxed">
                      {lang === 'en' 
                        ? "Shed your dead weight and create the life you dream of. Full recording of the 2-hour online course, guided meditation, and manifestation tools."
                        : "Smid din dødvægt og skab det liv, du drømmer om. Fuld adgang til optagelsen af det 2-timers onlinekursus, guidede meditationer og manifestationsøvelser."}
                    </p>
                  </div>

                  {/* Status & Format Section */}
                  <div className="pt-4 border-t border-stone-200/80 space-y-2.5">
                    <div>
                      <span className="text-[11px] uppercase tracking-widest text-stone-500 font-medium block">
                        {lang === 'en' ? "Product Status" : "Produktstatus"}
                      </span>
                      <div className="flex items-baseline gap-2 mt-0.5">
                        <span className="text-2xl font-serif font-bold text-stone-900">
                          {lang === 'en' ? "Coming Soon" : "Kommer snart"}
                        </span>
                      </div>
                      <p className="text-[11px] text-stone-500 font-light mt-0.5">
                        {lang === 'en' 
                          ? "Access to the recording is being prepared as a new product" 
                          : "Adgang til optagelsen klargøres netop nu som nyt produkt"}
                      </p>
                    </div>

                    <div className="text-xs text-stone-500 font-light space-y-0.5 pt-1">
                      <p className="flex items-center gap-1.5">
                        <i className="fa-solid fa-circle-play text-[#817262] text-[11px]" aria-hidden="true" />
                        <span><strong>{lang === 'en' ? "Online recording" : "Online optagelse"}</strong> · {lang === 'en' ? "Watch at your own pace" : "Se i dit eget tempo"}</span>
                      </p>
                    </div>
                  </div>

                  {/* Highlights */}
                  <ul className="space-y-2 text-xs text-stone-700 font-light pt-3 border-t border-stone-100">
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#817262] text-xs mt-0.5" aria-hidden="true" />
                      <span>{lang === 'en' ? "Full access to the 2-hour online course recording" : "Fuld adgang til optagelsen af det 2-timers onlinekursus"}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#817262] text-xs mt-0.5" aria-hidden="true" />
                      <span>{lang === 'en' ? "Watch and rewatch at your own pace" : "Se og gense i dit eget tempo, når det passer dig"}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#817262] text-xs mt-0.5" aria-hidden="true" />
                      <span>{lang === 'en' ? "Guided meditation & energetic manifestation tools" : "Guidede meditationer & energetiske manifestationsværktøjer"}</span>
                    </li>
                  </ul>
                </div>

                <Link 
                  to={lang === 'en' ? "/en/free/unleash-yourself-miniworkshop" : "/gratis/frisaet-dig-miniworkshop"}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#817262] text-white py-3 px-4 uppercase text-[11px] tracking-widest font-bold hover:bg-stone-800 transition-colors rounded-sm shadow-xs"
                >
                  <span>{lang === 'en' ? "Read more about recording" : "Læs mere om optagelsen"}</span>
                  <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </Link>
              </div>

              {/* Online Program Card (Gemt for nu, bevaret i koden) */}
              {false && (
                <div className="bg-white p-8 md:p-9 border border-stone-200/90 rounded-sm flex flex-col justify-between space-y-6 shadow-xs hover:shadow-md transition-all group">
                  <div className="space-y-5">
                    <div>
                      <span className="text-[10px] uppercase tracking-widest font-bold text-[#817262] block mb-1">
                        {lang === 'en' ? "Live Online" : "Online Årsuddannelse"}
                      </span>
                      <h4 className="text-2xl font-serif text-stone-900">
                        {lang === 'en' ? "1-Year Online Course" : "Online årsforløb"}
                      </h4>
                      <p className="text-xs text-stone-600 font-light mt-1.5 leading-relaxed">
                        {lang === 'en' 
                          ? "1-Year Online Program in English • 16:00–19:30 CET with 10 live modules and recordings."
                          : "Online forløb på engelsk • Kl. 16.00–19.30 med 10 live moduler, guidede meditationer og optagelser."}
                      </p>
                    </div>

                    {/* Price Section */}
                    <div className="pt-4 border-t border-stone-200/80 space-y-2.5">
                      <div>
                        <span className="text-[11px] uppercase tracking-widest text-stone-500 font-medium block">
                          {lang === 'en' ? "Total Price" : "Samlet pris"}
                        </span>
                        <div className="flex items-baseline gap-2">
                          <span className="text-3xl sm:text-4xl font-serif font-bold text-stone-900">
                            {lang === 'en' ? "14,500 DKK" : "14.500 kr."}
                          </span>
                        </div>
                        <p className="text-[11px] text-stone-500 font-light mt-0.5">
                          €1,950 EUR · $2,250 USD · £1,650 GBP
                        </p>
                      </div>

                      {/* Ratebetaling Highlight */}
                      <div className="inline-flex items-center gap-2 bg-[#f5efe9] border border-[#817262]/30 text-[#817262] px-3 py-2 rounded-sm text-xs font-semibold w-full">
                        <i className="fa-regular fa-credit-card text-xs shrink-0" aria-hidden="true" />
                        <span>
                          {lang === 'en' 
                            ? "Can be paid in 3 installments (approx. 3 x 4,833 DKK)" 
                            : "Kan betales i 3 rater (ca. 3 x 4.833 kr.)"}
                        </span>
                      </div>

                      <div className="text-xs text-stone-500 font-light space-y-0.5 pt-1">
                        <p className="flex items-center gap-1.5">
                          <i className="fa-solid fa-video text-[#817262] text-[10px]" aria-hidden="true" />
                          <span>Live Online via Zoom (undervisningssprog engelsk)</span>
                        </p>
                      </div>
                    </div>

                    {/* Highlights */}
                    <ul className="space-y-2 text-xs text-stone-700 font-light pt-3 border-t border-stone-100">
                      <li className="flex items-start gap-2">
                        <i className="fa-solid fa-check text-[#817262] text-xs mt-0.5" aria-hidden="true" />
                        <span>10 live moduler (16.00–19.30 CET)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="fa-solid fa-check text-[#817262] text-xs mt-0.5" aria-hidden="true" />
                        <span>Inkl. 1:1 online mentor-session med Mette</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="fa-solid fa-check text-[#817262] text-xs mt-0.5" aria-hidden="true" />
                        <span>Ubegrænset adgang til moduloptagelser</span>
                      </li>
                    </ul>
                  </div>

                  <Link 
                    to={lang === 'en' ? "/en/unleash-yourself/online-program" : "/frisaet-dig/online-forloeb"}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#817262] text-white py-3 px-4 uppercase text-[11px] tracking-widest font-bold hover:bg-stone-800 transition-colors rounded-sm shadow-xs"
                  >
                    <span>{lang === 'en' ? "View online program" : "Se online forløb"}</span>
                    <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                  </Link>
                </div>
              )}
            </div>

            {/* Fold ud / fold ind knap for flere muligheder fjernet da alle 4 står direkte fremme */}

            {/* Direct call & clarification callout */}
            <div className="mt-12 bg-white border border-[#817262]/20 rounded-sm p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-2xs">
              <div className="text-center md:text-left space-y-1">
                <h5 className="font-serif text-lg text-stone-900 font-medium">
                  {lang === 'en' 
                    ? "Questions about the education or installment payments?" 
                    : "Har du spørgsmål til uddannelsen eller betaling i rater?"}
                </h5>
                <p className="text-xs sm:text-sm text-stone-600 font-light">
                  {lang === 'en'
                    ? "Book an informal 20-minute clarification call with Mette or call directly."
                    : "Book en uforpligtende 20 minutters afklarende samtale med Mette eller ring direkte."}
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
                <Link
                  to={lang === 'en' ? "/en/contact?subject=20-minute clarification: Unleash Yourself" : "/kontakt?subject=Afklarende samtale om Frisæt dig uddannelse"}
                  className="inline-flex items-center gap-2 px-5 py-3 bg-[#817262] hover:bg-stone-800 text-white rounded-sm text-xs uppercase tracking-wider font-bold transition-colors shadow-2xs"
                >
                  <i className="fa-regular fa-calendar-check text-xs" aria-hidden="true" />
                  <span>{lang === 'en' ? "Book 20 min conversation" : "Book 20 min samtale"}</span>
                </Link>
                <a 
                  href="tel:+4527507083"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-[#f5efe9] hover:bg-[#ebdcd0] text-stone-900 border border-[#817262]/30 rounded-sm text-xs uppercase tracking-wider font-bold transition-colors"
                >
                  <i className="fa-solid fa-phone text-[#817262] text-xs" aria-hidden="true" />
                  <span>Ring: 27 50 70 83</span>
                </a>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>




    </div>
  );
};

const FrisaetDigFysiskPage = () => {
  const { lang } = useLanguage();
  const isEn = lang === 'en';
  const frisaetPath = isEn ? '/en/unleash-yourself' : '/frisaet-dig';
  const { testimonials: cmsTestimonials } = useCms();

  const uddannelseTestimonials = UDDANNELSE_TESTIMONIALS;

  useEffect(() => {
    document.title = isEn 
      ? "Mette Brudvig - 1-Year Physical Education" 
      : "Mette Brudvig - Fysisk årsuddannelse";
    window.scrollTo(0, 0);
  }, [isEn]);

  return (
    <div className="bg-[#faf9f6] min-h-screen">
      {/* Subpage Header / Breadcrumb Hero */}
      <section className="relative pt-[185px] md:pt-[215px] lg:pt-[225px] pb-16 bg-[#f5efe9] border-b border-[#817262]/20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 z-10 relative">
          <FadeIn direction="up">
            <div className="mb-6">
              <Link 
                to={frisaetPath}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#817262] hover:text-stone-900 transition-colors"
              >
                <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" />
                <span>{isEn ? "Back to Unleash Yourself" : "Tilbage til Frisæt dig"}</span>
              </Link>
            </div>
            
            <h1 className="text-3xl md:text-5xl font-serif text-stone-900 mb-4">
              {isEn ? "1-Year Physical Education" : "Fysisk årsuddannelse"}
            </h1>
            <p className="text-stone-700 font-light text-base md:text-lg max-w-2xl leading-relaxed mb-6">
              {isEn 
                ? "We meet once a month in Copenhagen or Aarhus. Here you get teaching, joint healing, exercises and sparring in a safe and supportive community."
                : "Vi mødes én gang om måneden i København eller Århus. Her får du undervisning, fælles healing, øvelser og sparring i et trygt og nærværende fællesskab."}
            </p>

            <div className="pt-2">
              <Link
                to={isEn ? "/en/contact?subject=Free 20-min conversation: Physical education" : "/kontakt?subject=Gratis%2020%20minutters%20samtale%3A%20Fysisk%20uddannelse"}
                className="inline-flex items-center gap-3 px-6 py-3.5 bg-[#817262] text-white hover:bg-stone-800 transition-all duration-300 text-xs uppercase tracking-widest font-bold rounded-sm shadow-sm"
              >
                <span>
                  {isEn 
                    ? "Book free 20-minute consultation" 
                    : "Bestil gratis 20 minutters samtale"}
                </span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Main Content Section */}
      <section id="fysisk-forloeb" className="py-20 bg-white border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-6">
          <FadeIn>
            {/* Location Cards Side-by-Side */}
            <div id="holdstart-og-datoer" className="grid md:grid-cols-2 gap-8 mb-10 scroll-mt-24">
              {/* Kasse 1: København */}
              <div className="bg-[#f5efe9] p-6 sm:p-8 border border-[#817262]/20 rounded-sm flex flex-col shadow-sm hover:shadow-md transition-shadow">
                <div className="flex flex-col">
                  <div className="flex justify-between items-baseline border-b border-stone-300/60 pb-4 mb-5">
                    <div>
                      <span className="text-[10px] uppercase tracking-widest font-bold text-[#817262] block">
                        {isEn ? "Location" : "Lokation"}
                      </span>
                      <h4 className="text-2xl font-serif text-stone-900 font-medium">
                        {isEn ? "Copenhagen" : "København"}
                      </h4>
                      <p className="text-xs text-stone-700 mt-1 font-medium flex items-center gap-1.5">
                        <i className="fa-solid fa-location-dot text-[#817262]" aria-hidden="true" />
                        <span>Sofias Hus · Godthåbsvej 6A · 2000 Frederiksberg</span>
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] uppercase tracking-widest font-bold text-stone-500 block">
                        {isEn ? "Price" : "Pris"}
                      </span>
                      <span className="font-serif text-2xl font-bold text-stone-900 block">
                        {isEn ? "26,500 DKK" : "26.500 kr."}
                      </span>
                      <span className="text-[11px] text-[#817262] font-semibold block mt-0.5">
                        {isEn ? "Can be paid in 3 installments" : "Kan betales i 3 rater"}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between min-h-[26px]">
                      <h5 className="text-[11px] uppercase tracking-widest font-bold text-[#817262]">
                        {isEn ? "Dates in Copenhagen (2027)" : "Datoer i København (2027)"}
                      </h5>
                      <span className="px-2 py-0.5 text-[10px] uppercase font-bold tracking-wider bg-[#817262]/10 text-[#817262] rounded-xs border border-[#817262]/20">
                        2027
                      </span>
                    </div>
                      <ul className="text-xs sm:text-sm text-stone-900 font-medium space-y-1 divide-y divide-stone-200/80 bg-white/70 rounded-sm px-3.5 py-1 border border-stone-200/60">
                      {(isEn ? [
                        { mod: "Module 1", date: "Wednesday, February 24, 2027 from 13:00 to 18:00" },
                        { mod: "Module 2", date: "Wednesday, March 10, 2027 from 13:00 to 18:00" },
                        { mod: "Module 3", date: "Wednesday, April 7, 2027 from 13:00 to 18:00" },
                        { mod: "Module 4", date: "Wednesday, May 12, 2027 from 13:00 to 18:00" },
                        { mod: "Module 5", date: "Wednesday, June 9, 2027 from 13:00 to 18:00" },
                        { mod: "Module 6", date: "Wednesday, August 18, 2027 from 13:00 to 18:00" },
                        { mod: "Module 7", date: "Wednesday, September 8, 2027 from 13:00 to 18:00" },
                        { mod: "Module 8", date: "Wednesday, October 6, 2027 from 13:00 to 18:00" },
                        { mod: "Module 9", date: "Wednesday, November 3, 2027 from 13:00 to 18:00" },
                        { mod: "Module 10", date: "Wednesday, December 1, 2027 from 13:00 to 18:00" }
                      ] : [
                        { mod: "Modul 1", date: "Onsdag den 24. februar 2027 kl. 13.00–18.00" },
                        { mod: "Modul 2", date: "Onsdag den 10. marts 2027 kl. 13.00–18.00" },
                        { mod: "Modul 3", date: "Onsdag den 7. april 2027 kl. 13.00–18.00" },
                        { mod: "Modul 4", date: "Onsdag den 12. maj 2027 kl. 13.00–18.00" },
                        { mod: "Modul 5", date: "Onsdag den 9. juni 2027 kl. 13.00–18.00" },
                        { mod: "Modul 6", date: "Onsdag den 18. august 2027 kl. 13.00–18.00" },
                        { mod: "Modul 7", date: "Onsdag den 8. september 2027 kl. 13.00–18.00" },
                        { mod: "Modul 8", date: "Onsdag den 6. oktober 2027 kl. 13.00–18.00" },
                        { mod: "Modul 9", date: "Onsdag den 3. november 2027 kl. 13.00–18.00" },
                        { mod: "Modul 10", date: "Onsdag den 1. december 2027 kl. 13.00–18.00" }
                      ]).map((item, dIdx) => (
                        <li key={dIdx} className="py-2 flex items-center justify-between gap-3 text-xs sm:text-[13px]">
                          <span className="inline-flex items-center gap-1.5 font-bold text-[#817262] uppercase tracking-wider text-[11px] shrink-0">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#817262]"></span>
                            {item.mod}
                          </span>
                          <span className="font-medium text-stone-800 text-right">{item.date}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Lille note om inkluderet 1:1 session */}
                    <div className="mt-2.5 p-2.5 bg-white/80 border border-[#817262]/25 rounded-sm flex items-start sm:items-center gap-2.5 text-xs text-stone-700">
                      <div className="w-5 h-5 rounded-full bg-[#817262]/10 flex items-center justify-center shrink-0 mt-0.5 sm:mt-0 text-[#817262]">
                        <i className="fa-solid fa-sparkles text-[11px]" aria-hidden="true" />
                      </div>
                      <div className="leading-snug">
                        <span className="font-bold text-[#817262] mr-1.5">
                          {isEn ? "1:1 Clairvoyant Session:" : "1:1 Clairvoyant Session:"}
                        </span>
                        <span className="text-stone-700">
                          {isEn ? "En individuel 1:1 clairvoyant session med Mette er inkluderet i uddannelsen." : "En individuel 1:1 clairvoyant session med mig er inkluderet i uddannelsen."}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-stone-300/60 mt-6 flex-1 flex flex-col justify-between gap-4">
                  {/* Primær tilmelding */}
                  <a 
                    href="https://pay.lumant.ai/kurv/vQwN-forlob-kobenhavn" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full text-center bg-[#817262] text-white py-3.5 uppercase text-xs tracking-widest font-bold hover:bg-stone-800 transition-colors rounded-sm shadow-xs"
                  >
                    <span>{isEn ? "Book & Pay Online (Copenhagen)" : "Tilmeld & Betal online (København)"}</span>
                    <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true" />
                  </a>

                  {/* Lokationskort */}
                  <div className="p-4 bg-white border border-[#817262]/20 rounded-sm text-xs text-stone-700 shadow-2xs flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between font-serif font-bold text-stone-900 border-b border-stone-200/80 pb-1.5">
                        <span className="flex items-center gap-1.5 text-stone-900">
                          <i className="fa-solid fa-location-dot text-[#817262] text-[11px]" aria-hidden="true" />
                          Sofias Hus · Frederiksberg
                        </span>
                        <a 
                          href="https://www.sofiashus.dk/" 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-[11px] font-sans font-bold text-[#817262] hover:underline inline-flex items-center gap-1"
                        >
                          <span>sofiashus.dk</span>
                          <i className="fa-solid fa-arrow-up-right-from-square text-[9px]" aria-hidden="true" />
                        </a>
                      </div>
                      <p className="font-light leading-relaxed mt-2 text-stone-600">
                        Godthåbsvej 6A, 2000 Frederiksberg. Bus 2A lige til døren, Metro M3 ved Aksel Møllers Have (3 min.) og Metro M1/M2 på Frederiksberg (5 min.). Cykelparkering i gården.
                      </p>
                    </div>
                    <div className="pt-2.5 mt-2.5 border-t border-stone-100 flex items-center justify-between">
                      <a 
                        href="https://maps.google.com/?q=Sofias+Hus+Godth%C3%A5bsvej+6A+2000+Frederiksberg"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#817262] hover:underline"
                      >
                        <i className="fa-solid fa-diamond-turn-right text-[10px]" aria-hidden="true" />
                        <span>{isEn ? "View on Google Maps" : "Find vej på Google Maps"}</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Kasse 2: Århus */}
              <div className="bg-[#f5efe9] p-6 sm:p-8 border border-[#817262]/20 rounded-sm flex flex-col shadow-sm hover:shadow-md transition-shadow">
                <div className="flex flex-col">
                  <div className="flex justify-between items-baseline border-b border-stone-300/60 pb-4 mb-5">
                    <div>
                      <span className="text-[10px] uppercase tracking-widest font-bold text-[#817262] block">
                        {isEn ? "Location" : "Lokation"}
                      </span>
                      <h4 className="text-2xl font-serif text-stone-900 font-medium">
                        {isEn ? "Aarhus" : "Århus"}
                      </h4>
                      <p className="text-xs text-stone-700 mt-1 font-medium flex items-center gap-1.5">
                        <i className="fa-solid fa-location-dot text-[#817262]" aria-hidden="true" />
                        <span>Business United · Mosevej 20 B · 8240 Risskov</span>
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] uppercase tracking-widest font-bold text-stone-500 block">
                        {isEn ? "Price" : "Pris"}
                      </span>
                      <span className="font-serif text-2xl font-bold text-stone-900 block">
                        {isEn ? "26,500 DKK" : "26.500 kr."}
                      </span>
                      <span className="text-[11px] text-[#817262] font-semibold block mt-0.5">
                        {isEn ? "Can be paid in 3 installments" : "Kan betales i 3 rater"}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between min-h-[26px]">
                      <h5 className="text-[11px] uppercase tracking-widest font-bold text-[#817262]">
                        {isEn ? "Dates in Aarhus (2027)" : "Datoer i Århus (2027)"}
                      </h5>
                      <span className="px-2 py-0.5 text-[10px] uppercase font-bold tracking-wider bg-[#817262]/10 text-[#817262] rounded-xs border border-[#817262]/20">
                        2027
                      </span>
                    </div>
                      <ul className="text-xs sm:text-sm text-stone-900 font-medium space-y-1 divide-y divide-stone-200/80 bg-white/70 rounded-sm px-3.5 py-1 border border-stone-200/60">
                      {(isEn ? [
                        { mod: "Module 1", date: "Tuesday, March 2, 2027 from 13:00 to 18:00" },
                        { mod: "Module 2", date: "Tuesday, March 16, 2027 from 13:00 to 18:00" },
                        { mod: "Module 3", date: "Tuesday, April 13, 2027 from 13:00 to 18:00" },
                        { mod: "Module 4", date: "Tuesday, May 18, 2027 from 13:00 to 18:00" },
                        { mod: "Module 5", date: "Tuesday, June 15, 2027 from 13:00 to 18:00" },
                        { mod: "Module 6", date: "Tuesday, August 10, 2027 from 13:00 to 18:00" },
                        { mod: "Module 7", date: "Tuesday, September 14, 2027 from 13:00 to 18:00" },
                        { mod: "Module 8", date: "Tuesday, October 12, 2027 from 13:00 to 18:00" },
                        { mod: "Module 9", date: "Tuesday, November 9, 2027 from 13:00 to 18:00" },
                        { mod: "Module 10", date: "Tuesday, December 7, 2027 from 13:00 to 18:00" }
                      ] : [
                        { mod: "Modul 1", date: "Tirsdag den 2. marts 2027 kl. 13.00–18.00" },
                        { mod: "Modul 2", date: "Tirsdag den 16. marts 2027 kl. 13.00–18.00" },
                        { mod: "Modul 3", date: "Tirsdag den 13. april 2027 kl. 13.00–18.00" },
                        { mod: "Modul 4", date: "Tirsdag den 18. maj 2027 kl. 13.00–18.00" },
                        { mod: "Modul 5", date: "Tirsdag den 15. juni 2027 kl. 13.00–18.00" },
                        { mod: "Modul 6", date: "Tirsdag den 10. august 2027 kl. 13.00–18.00" },
                        { mod: "Modul 7", date: "Tirsdag den 14. september 2027 kl. 13.00–18.00" },
                        { mod: "Modul 8", date: "Tirsdag den 12. oktober 2027 kl. 13.00–18.00" },
                        { mod: "Modul 9", date: "Tirsdag den 9. november 2027 kl. 13.00–18.00" },
                        { mod: "Modul 10", date: "Tirsdag den 7. december 2027 kl. 13.00–18.00" }
                      ]).map((item, dIdx) => (
                        <li key={dIdx} className="py-2 flex items-center justify-between gap-3 text-xs sm:text-[13px]">
                          <span className="inline-flex items-center gap-1.5 font-bold text-[#817262] uppercase tracking-wider text-[11px] shrink-0">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#817262]"></span>
                            {item.mod}
                          </span>
                          <span className="font-medium text-stone-800 text-right">{item.date}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Lille note om inkluderet 1:1 session */}
                    <div className="mt-2.5 p-2.5 bg-white/80 border border-[#817262]/25 rounded-sm flex items-start sm:items-center gap-2.5 text-xs text-stone-700">
                      <div className="w-5 h-5 rounded-full bg-[#817262]/10 flex items-center justify-center shrink-0 mt-0.5 sm:mt-0 text-[#817262]">
                        <i className="fa-solid fa-sparkles text-[11px]" aria-hidden="true" />
                      </div>
                      <div className="leading-snug">
                        <span className="font-bold text-[#817262] mr-1.5">
                          {isEn ? "1:1 Clairvoyant Session:" : "1:1 Clairvoyant Session:"}
                        </span>
                        <span className="text-stone-700">
                          {isEn ? "En individuel 1:1 clairvoyant session med Mette er inkluderet i uddannelsen." : "En individuel 1:1 clairvoyant session med mig er inkluderet i uddannelsen."}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-stone-300/60 mt-6 flex-1 flex flex-col justify-between gap-4">
                  {/* Primær tilmelding */}
                  <a 
                    href="https://pay.lumant.ai/kurv/rt6e-forlob-arhus" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full text-center bg-[#817262] text-white py-3.5 uppercase text-xs tracking-widest font-bold hover:bg-stone-800 transition-colors rounded-sm shadow-xs"
                  >
                    <span>{isEn ? "Book & Pay Online (Aarhus)" : "Tilmeld & Betal online (Århus)"}</span>
                    <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true" />
                  </a>

                  {/* Lokationskort */}
                  <div className="p-4 bg-white border border-[#817262]/20 rounded-sm text-xs text-stone-700 shadow-2xs flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between font-serif font-bold text-stone-900 border-b border-stone-200/80 pb-1.5">
                        <span className="flex items-center gap-1.5 text-stone-900">
                          <i className="fa-solid fa-location-dot text-[#817262] text-[11px]" aria-hidden="true" />
                          Business United · Risskov
                        </span>
                      </div>
                      <p className="font-light leading-relaxed mt-2 text-stone-600">
                        Mosevej 20 B, 8240 Risskov
                      </p>
                    </div>
                    <div className="pt-2.5 mt-2.5 border-t border-stone-100 flex items-center justify-between">
                      <a 
                        href="https://maps.google.com/?q=Business+United+Mosevej+20+B+8240+Risskov"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#817262] hover:underline"
                      >
                        <i className="fa-solid fa-diamond-turn-right text-[10px]" aria-hidden="true" />
                        <span>{isEn ? "View on Google Maps" : "Find vej på Google Maps"}</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

const FrisaetDigOnlinePage = () => {
  const { lang } = useLanguage();
  const isEn = lang === 'en';
  const frisaetPath = isEn ? '/en/unleash-yourself' : '/frisaet-dig';
  const [openModule, setOpenModule] = useState(null);

  useEffect(() => {
    document.title = "Online Personal Freedom & Inner Leadership Course | Mette Brudvig";
    window.scrollTo(0, 0);
  }, []);

  const onlineDates = [
    { da: "Torsdag den 23. februar kl. 16.00–19.30", en: "Thursday, February 23 from 16:00 to 19:30 CET" },
    { da: "Torsdag den 9. marts kl. 16.00–19.30", en: "Thursday, March 9 from 16:00 to 19:30 CET" },
    { da: "Torsdag den 20. april kl. 16.00–19.30", en: "Thursday, April 20 from 16:00 to 19:30 CET" },
    { da: "Torsdag den 25. maj kl. 16.00–19.30", en: "Thursday, May 25 from 16:00 to 19:30 CET" },
    { da: "Torsdag den 22. juni kl. 16.00–19.30", en: "Thursday, June 22 from 16:00 to 19:30 CET" },
    { da: "Torsdag den 24. august kl. 16.00–19.30", en: "Thursday, August 24 from 16:00 to 19:30 CET" },
    { da: "Tirsdag den 7. september kl. 16.00–19.30", en: "Tuesday, September 7 from 16:00 to 19:30 CET" },
    { da: "Tirsdag den 5. oktober kl. 16.00–19.30", en: "Tuesday, October 5 from 16:00 to 19:30 CET" },
    { da: "Tirsdag den 16. november kl. 16.00–19.30", en: "Tuesday, November 16 from 16:00 to 19:30 CET" },
    { da: "Tirsdag den 15. december kl. 16.00–19.30", en: "Tuesday, December 15 from 16:00 to 19:30 CET" }
  ];

  const modules = [
    {
      num: "01",
      title: "Your Transformation",
      intro: "You sense where you stand now and what kind of life you wish to move towards.",
      items: [
        "Meditation and introduction to the program",
        "When life leads the way – my own story",
        "Where are you already courageous, proactive, and trusting in life?",
        "Where are you holding yourself back?",
        "What do you wish to unleash yourself from?",
        "How do you wish to feel when the program is complete?",
        "You set a clear intention for your transformation"
      ]
    },
    {
      num: "02",
      title: "Understand Yourself as Energy",
      intro: "You learn to understand how your energy creates your life and how you can transform it.",
      items: [
        "What is energy and frequency?",
        "Where is your energy right now?",
        "When do you vibrate with a clear signal, and when is there 'noise' on the line?",
        "Authenticity, resonance, and body signals",
        "Past and future exist only in your thoughts and patterns – learn to live in the present",
        "Learn to access the space of stillness where you can create anew",
        "Neuroplasticity of the brain and creating new programming",
        "How to shift your energy and create new outcomes",
        "Meditation to feel your core energy and learn how to return to it"
      ]
    },
    {
      num: "03",
      title: "Put the Soul in the Driver's Seat",
      intro: "You learn to distinguish between the soul's guidance and the ego's fear, control, triggers, and old narratives.",
      items: [
        "Understanding soul and ego",
        "The logical brain and heart intelligence",
        "How the soul and the ego express themselves in your life",
        "Trauma, triggers, patterns, and wounds",
        "Learn to recognize triggers and stay connected with yourself instead of reacting blindly",
        "The body's memory and unburdening emotional residue",
        "Meeting your fear with presence, compassion, and courage"
      ]
    },
    {
      num: "04",
      title: "The Soul's Superpower: Acceptance, Compassion & Boundaries",
      intro: "You practice stepping back, observing, and holding loving boundaries.",
      items: [
        "Acceptance as the foundation for inner transformation",
        "Compassion for yourself and others",
        "Setting boundaries with calmness, dignity, and without guilt",
        "Clearing energetic attachments and expectations",
        "Forgiveness and releasing energetic bonds to the past"
      ]
    },
    {
      num: "05",
      title: "Unleash Your Inherent Power & Authentic Presence",
      intro: "You step into your true power and release the fear of being seen as you are.",
      items: [
        "Standing firmly in your core without needing external validation",
        "Transforming fear of rejection into authentic presence",
        "Unfolding your unique gifts and natural authority",
        "Living in alignment with your soul's deepest truth"
      ]
    },
    {
      num: "06",
      title: "Strengthen Your Intuition & Soul Intelligence",
      intro: "You build deep trust in your inner compass and learn to receive spiritual guidance.",
      items: [
        "Opening up to intuition and subtle soul signals",
        "Distinguishing between wishful thinking, anxiety, and clear intuitive knowing",
        "Practical exercises to strengthen your clairvoyant and intuitive capacity",
        "Reading life's synchronicities and guidance in daily life"
      ]
    },
    {
      num: "07",
      title: "Living in True Partnership with Your Body",
      intro: "You heal the connection to your somatic vessel and cultivate loving bodily dialogue.",
      items: [
        "The body as a sacred messenger and intelligence system",
        "Releasing stress, tightness, and trapped emotions from the nervous system",
        "Cultivating daily somatic presence and deep relaxation",
        "Honoring your physical boundaries, rest, and vitality"
      ]
    },
    {
      num: "08",
      title: "Flow, Co-Creation & Manifestation",
      intro: "You learn how to co-create with life effortlessly from a state of trust.",
      items: [
        "The laws of attraction, alignment, and vibration in practice",
        "Shifting from forced willpower to magnetic flow",
        "Surrendering to divine timing and life's natural intelligence",
        "Manifesting your vision from the heart rather than ego desire"
      ]
    },
    {
      num: "09",
      title: "Soulful Relationships & Mature Feminine/Masculine Energy",
      intro: "You bring transformation into your connections with family, partner, and work.",
      items: [
        "Navigating relationship dynamics with love and healthy boundaries",
        "Harmonizing the mature feminine and masculine energies within",
        "Ending people-pleasing and co-dependent relationship patterns",
        "Inviting deep, authentic intimacy and soul connections into your life"
      ]
    },
    {
      num: "10",
      title: "Living Freely in Your Full Potential",
      intro: "Integration and anchoring your new, sovereign way of being in the world.",
      items: [
        "Anchoring your soul leadership and lifelong daily practice",
        "Celebrating your transformation and newfound freedom",
        "Stepping forward courageously into the next chapter of your life",
        "Closing ceremony and ongoing support network"
      ]
    }
  ];

  return (
    <div className="bg-[#faf9f6] min-h-screen">
      {/* Subpage Header / Breadcrumb Hero */}
      <section className="relative pt-[185px] md:pt-[215px] lg:pt-[225px] pb-16 bg-[#f5efe9] border-b border-[#817262]/20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 z-10 relative">
          <FadeIn direction="up">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
              <div className="lg:col-span-7">
                <div className="mb-6">
                  <Link 
                    to={frisaetPath}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#817262] hover:text-stone-900 transition-colors"
                  >
                    <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" />
                    <span>Back to Unleash Yourself</span>
                  </Link>
                </div>
                
                <div className="flex flex-wrap items-center gap-2.5 mb-3">
                  <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#817262]">
                    1-Year Online Program
                  </span>
                </div>

                <h1 className="text-3xl md:text-5xl lg:text-6xl font-serif text-stone-900 mb-4 leading-[1.15]">
                  Online Personal Freedom &amp; Inner Leadership Course
                </h1>
                
                <p className="text-stone-700 font-light text-base md:text-lg leading-relaxed">
                  A transformative 1-year live online journey connecting you with your soul, heart intelligence, and true inner leadership — with live teaching, group sparring, exercises, recordings, and an international community.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Main Content Section */}
      <section id="online-forloeb" className="py-20 bg-white border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-6">
          <FadeIn>
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
              
              {/* Left Column: Full English Course Description & Curriculum */}
              <div className="lg:col-span-7 space-y-10">
                
                {/* Introduction in English */}
                <div className="space-y-5 text-stone-700 font-light text-base md:text-lg leading-relaxed">
                  <h2 className="text-2xl md:text-3xl font-serif text-stone-900 font-normal">
                    Personal Freedom &amp; Inner Leadership
                  </h2>
                  <p className="font-serif italic text-lg text-stone-800">
                    – A life of courage and trust, where you connect with your soul and step into your full potential.
                  </p>
                  <p>
                    This course is for you who dream of a life where you feel whole, safe, and free to unfold with all the unique gifts you carry.
                  </p>
                  <p>
                    A life where you lovingly and with dignity always have your own back and manage to quietly set boundaries without regret. Where you connect with your intuition and heart intelligence, feel your passion, flow with life, and unfold your potential from a state of inner peace, self-love, and courage.
                  </p>
                </div>

                {/* Personal Story Callout from Mette */}
                <div className="bg-[#f5efe9]/50 p-6 sm:p-8 border-l-4 border-[#817262] rounded-r-sm space-y-3.5 shadow-2xs">
                  <p className="font-serif italic text-stone-900 font-medium text-base sm:text-lg">
                    Perhaps you are where I was before I took this exact journey back home to myself:
                  </p>
                  <p className="text-stone-700 font-light text-sm sm:text-base leading-relaxed">
                    &ldquo;I felt there wasn&apos;t enough joy, presence, time, inner peace, and security. I felt as if my soul was almost gnawing inside me for space, and my inherent gifts felt like a distant mirage. Like walking through a foggy labyrinth.
                  </p>
                  <p className="text-stone-700 font-light text-sm sm:text-base leading-relaxed">
                    I tried running faster and slower, changing my body and patterns through willpower, hoping the future would bring more stable joy of life and connection with myself. None of it had a lasting effect, and today I understand why.&rdquo;
                  </p>
                  <footer className="pt-2 text-xs uppercase tracking-widest font-bold text-[#817262]">
                    — Mette Brudvig
                  </footer>
                </div>

                {/* 1:1 Session add-on callout */}
                <div className="bg-[#f5efe9] p-5 border border-[#817262]/30 rounded-sm flex items-start gap-4 shadow-2xs">
                  <i className="fa-solid fa-sparkles text-[#817262] text-xl shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <h4 className="text-stone-900 font-serif font-medium text-base">
                      1:1 Clairvoyant Session Add-on
                    </h4>
                    <p className="text-stone-700 font-light text-sm sm:text-base mt-1">
                      A 1:1 clairvoyant mentoring session with Mette can be added at a special participant price.
                    </p>
                  </div>
                </div>

                {/* What You Receive */}
                <div className="space-y-4 pt-2">
                  <h3 className="text-xl md:text-2xl font-serif text-stone-900">
                    What You Receive
                  </h3>
                  <ul className="grid sm:grid-cols-2 gap-3.5 text-stone-800 font-light text-sm sm:text-base">
                    {[
                      "10 live online teaching modules (16:00–19:30 CET)",
                      "Live group sparring, exercises & coaching with Mette",
                      "Access to all module video & audio recordings",
                      "Guided audio meditations & somatic integration practices",
                      "Dedicated online community & support between modules",
                      "Deep connection with your intuition & soul frequency"
                    ].map((feat, idx) => (
                      <li key={idx} className="flex items-start gap-3 bg-[#faf9f6] p-3.5 border border-stone-200/70 rounded-sm">
                        <i className="fa-solid fa-check text-[#817262] text-xs mt-1 shrink-0" aria-hidden="true" />
                        <span className="leading-snug">{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* 4 Participant Testimonials - placed high to inspire continued reading */}
                <div className="pt-8 pb-4 border-t border-stone-200">
                  <div className="mb-6 space-y-1">
                    <span className="text-[11px] uppercase tracking-widest font-bold text-[#817262] block">
                      {isEn ? "Participant Testimonials" : "Udtalelser fra deltagere"}
                    </span>
                    <h3 className="text-2xl font-serif text-stone-900">
                      {isEn ? "What participants experience in the program" : "Hvad kursister oplever i forløbet"}
                    </h3>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {TESTIMONIALS.filter(t => t.category === "forloeb").slice(0, 4).map((t, idx) => (
                      <TestimonialQuoteCard key={idx} testimonial={t} className="bg-[#f5efe9]/50" />
                    ))}
                  </div>
                </div>

                {/* 10 Modules Breakdown in English */}
                <div className="space-y-4 pt-4 border-t border-stone-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-[11px] uppercase tracking-widest font-bold text-[#817262] block mb-1">Curriculum</span>
                      <h3 className="text-2xl font-serif text-stone-900">10 Modules (360° Transformation)</h3>
                    </div>
                  </div>
                  <div className="space-y-3 text-stone-600 font-light text-sm sm:text-base leading-relaxed">
                    <p>
                      The program is 360-degree personal development. This means that you do not only evolve through my teaching and energy during sessions, but it activates something in you on a soul level that works effectively within you between modules, creating true transformation and connection between body, mind, and soul.
                    </p>
                    <p>
                      I have structured the content and modules to provide security, empowerment, and effective growth. In short: a carefully designed journey back home to your soul essence, so you can live, thrive, and unfold freely as your whole self.
                    </p>
                  </div>

                  <div className="space-y-3 pt-2">
                    {modules.map((m, idx) => {
                      const isOpen = openModule === idx;
                      return (
                        <div 
                          key={idx} 
                          className="border border-stone-200 rounded-sm overflow-hidden bg-white transition-all shadow-2xs"
                        >
                          <button
                            type="button"
                            onClick={() => setOpenModule(isOpen ? null : idx)}
                            className="w-full p-4 sm:p-5 text-left flex items-center justify-between gap-4 hover:bg-[#f5efe9]/30 transition-colors cursor-pointer"
                          >
                            <div className="flex items-center gap-3.5">
                              <span className="font-serif text-[#817262] font-bold text-base md:text-lg">
                                {m.num}
                              </span>
                              <h4 className="font-serif text-stone-900 font-medium text-base md:text-lg">
                                Module {idx + 1}: {m.title}
                              </h4>
                            </div>
                            <i className={`fa-solid fa-chevron-down text-xs text-[#817262] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                          </button>
                          
                          {isOpen && (
                            <div className="px-5 pb-5 pt-1 text-sm text-stone-700 font-light space-y-3 border-t border-stone-100 bg-[#faf9f6]">
                              <p className="font-serif italic text-stone-800 text-sm sm:text-base">
                                {m.intro}
                              </p>
                              <ul className="space-y-2 pt-1">
                                {m.items.map((item, itemIdx) => (
                                  <li key={itemIdx} className="flex items-start gap-2.5">
                                    <i className="fa-solid fa-sparkle text-[#817262] text-[10px] mt-1 shrink-0" aria-hidden="true" />
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

              </div>

              {/* Right Column: Pricing, Currency Options & Schedule */}
              <div className="lg:col-span-5 sticky top-28">
                <div className="bg-[#f5efe9] p-6 sm:p-8 border border-[#817262]/30 rounded-sm space-y-6 shadow-md">
                  
                  {/* Format & Title */}
                  <div className="border-b border-stone-300/70 pb-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-widest font-bold text-[#817262]">Format</span>
                      <span className="inline-flex items-center gap-1.5 bg-[#817262] text-white text-[9px] uppercase tracking-widest font-bold px-2.5 py-0.5 rounded-full">
                        <i className="fa-solid fa-video text-[10px]" aria-hidden="true" />
                        Live Online in English
                      </span>
                    </div>
                    <h4 className="text-xl font-serif text-stone-900 font-medium leading-snug">
                      Online Personal Freedom &amp;<br />Inner Leadership Course
                    </h4>
                    <p className="text-xs text-stone-600 font-light mt-1">
                      10 Live Online Sessions • 16:00–19:30 CET
                    </p>
                  </div>

                  {/* Multi-Currency Pricing */}
                  <div className="bg-white/80 p-5 rounded-sm border border-[#817262]/20 space-y-3 shadow-2xs">
                    <div className="flex justify-between items-baseline">
                      <span className="text-[11px] uppercase tracking-widest font-bold text-stone-500">
                        Course Price
                      </span>
                      <span className="text-[11px] uppercase tracking-wider font-semibold text-[#817262]">
                        1-Year Program
                      </span>
                    </div>
                    
                    <div className="space-y-1.5 pt-1">
                      <div className="flex items-baseline justify-between">
                        <span className="font-serif text-2xl md:text-3xl font-bold text-stone-900">
                          14,500 DKK
                        </span>
                        <span className="text-xs text-stone-500 font-light">(14.500 kr.)</span>
                      </div>
                      
                      <div className="inline-flex items-center gap-2 bg-[#f5efe9] border border-[#817262]/30 text-[#817262] px-3 py-1.5 rounded-sm text-xs font-semibold w-full">
                        <i className="fa-regular fa-credit-card text-xs shrink-0" aria-hidden="true" />
                        <span>Can be paid in 3 installments (3 x 4,833 DKK)</span>
                      </div>
                      
                      <div className="pt-2 border-t border-stone-200/80 grid grid-cols-3 gap-2 text-center text-xs text-stone-700 font-medium">
                        <div className="bg-[#f5efe9] py-1.5 px-2 rounded-xs border border-stone-200/60">
                          €1,950 EUR
                        </div>
                        <div className="bg-[#f5efe9] py-1.5 px-2 rounded-xs border border-stone-200/60">
                          $2,250 USD
                        </div>
                        <div className="bg-[#f5efe9] py-1.5 px-2 rounded-xs border border-stone-200/60">
                          £1,650 GBP
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Dates List */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-baseline">
                      <span className="text-[10px] uppercase tracking-widest font-bold text-[#817262]">
                        Course Dates (16:00–19:30 CET)
                      </span>
                      <span className="text-[10px] text-stone-500 font-medium">10 Modules</span>
                    </div>

                    <ul className="text-xs sm:text-sm text-stone-900 font-medium space-y-1 divide-y divide-stone-300/50 bg-white/60 p-3 rounded-sm border border-stone-200/50">
                      {onlineDates.map((item, dIdx) => (
                        <li key={dIdx} className={`${dIdx > 0 ? 'pt-2' : 'pt-1'} pb-1 flex items-center justify-between gap-2`}>
                          <div className="flex items-center gap-2">
                            <i className="fa-regular fa-calendar-days text-[#817262] text-xs shrink-0" aria-hidden="true" />
                            <span>{item.en}</span>
                          </div>
                          <span className="text-[10px] text-stone-400 font-mono">#{dIdx + 1}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Actions */}
                  <div className="pt-2 space-y-3">
                    <a 
                      href="https://pay.lumant.ai/kurv/5PNa-transformational-online-course" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full text-center bg-[#817262] text-white py-3.5 uppercase text-xs tracking-widest font-bold hover:bg-stone-800 transition-colors rounded-sm shadow-xs cursor-pointer"
                    >
                      <span>{isEn ? "Book & Pay Online Now" : "Tilmeld & Betal online nu"}</span>
                      <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true" />
                    </a>

                    <Link 
                      to={isEn 
                        ? "/en/contact?subject=Enrollment: Online Personal Freedom Course" 
                        : "/kontakt?subject=Tilmelding%3A%20Online%20Personal%20Freedom%20Course"} 
                      className="block w-full text-center bg-white border border-[#817262]/40 text-stone-800 py-2.5 uppercase text-xs tracking-wider font-semibold hover:border-[#817262] transition-colors rounded-sm shadow-2xs cursor-pointer"
                    >
                      {isEn ? "Send inquiry / questions" : "Send forespørgsel / spørgsmål"}
                    </Link>
                    
                    <a 
                      href="tel:+4527507083" 
                      className="flex items-center justify-center gap-2 w-full border border-[#817262] bg-white text-[#817262] py-2.5 uppercase text-xs tracking-wider font-bold hover:bg-[#817262] hover:text-white transition-colors rounded-sm shadow-xs cursor-pointer"
                    >
                      <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
                      <span>{isEn ? "Call to hear more: 27 50 70 83" : "Ring, og hør mere: 27 50 70 83"}</span>
                    </a>

                    <Link 
                      to={isEn 
                        ? "/en/contact?subject=Free 20-min consultation: Online Personal Freedom Course" 
                        : "/kontakt?subject=Free%2020-min%20consultation%3A%20Online%20Personal%20Freedom%20Course"} 
                      className="block w-full text-center border border-stone-300 bg-stone-50/50 text-stone-700 py-3 uppercase text-[11px] tracking-wider font-semibold hover:bg-stone-200/60 transition-colors rounded-sm shadow-xs leading-snug px-3 cursor-pointer"
                    >
                      Book free 20-minute consultation
                    </Link>
                  </div>

                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

const FrisaetDigVideoPage = () => {
  const { lang } = useLanguage();
  const isEn = lang === 'en';
  const frisaetPath = isEn ? '/en/unleash-yourself' : '/frisaet-dig';

  useEffect(() => {
    document.title = isEn 
      ? "Mette Brudvig - 12-week video program" 
      : "Mette Brudvig - 12-ugers videoforløb";
    window.scrollTo(0, 0);
  }, [isEn]);

  return (
    <div className="bg-[#faf9f6] min-h-screen">
      {/* Subpage Header / Breadcrumb Hero */}
      <section className="relative pt-[185px] md:pt-[215px] lg:pt-[225px] pb-16 bg-[#f5efe9] border-b border-[#817262]/20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 z-10 relative">
          <FadeIn direction="up">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-12">
              <div className="lg:col-span-7">
                <div className="mb-6 flex flex-wrap items-center gap-3">
                  <Link 
                    to={frisaetPath}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#817262] hover:text-stone-900 transition-colors"
                  >
                    <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" />
                    <span>{isEn ? "Back to Unleash Yourself" : "Tilbage til Frisæt dig"}</span>
                  </Link>
                  <span className="text-stone-300">•</span>
                  <span className="inline-flex items-center gap-1.5 bg-[#817262] text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full shadow-xs">
                    <i className="fa-regular fa-clock text-xs" aria-hidden="true" />
                    {isEn ? "Coming soon" : "Kommer snart"}
                  </span>
                </div>
                
                <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#817262] mb-3 block">
                  {isEn ? "Self-study Video Program" : "Selvstudie Videoforløb"}
                </span>
                <h1 className="text-3xl md:text-5xl font-serif text-stone-900 mb-4 leading-tight">
                  {isEn ? "12-week video program" : "12-ugers videoforløb"}
                </h1>
                <p className="text-stone-700 font-light text-base md:text-lg leading-relaxed">
                  {isEn 
                    ? "A compact and effective kickstart to strengthen your heart intelligence and start your personal transformation."
                    : "En kompakt og effektiv kickstart til at styrke hjertets intelligens og sætte din personlige transformation i gang."}
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Main Content Section */}
      <section id="videoforloeb" className="py-20 bg-white border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-6">
          <FadeIn>
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
              {/* Left Column: Description & Highlights */}
              <div className="lg:col-span-7 space-y-8">
                <div className="text-stone-700 font-light text-base md:text-lg leading-relaxed space-y-4">
                  <p>
                    {isEn
                      ? "Through teaching, meditations, and practical exercises, you work to create more calm, direction, and contact with yourself. You follow the program from home and work at your own pace."
                      : "Gennem undervisning, meditationer og konkrete øvelser arbejder du med at skabe mere ro, retning og kontakt med dig selv. Du følger forløbet hjemmefra og arbejder i dit eget tempo."}
                  </p>
                </div>

                <div className="pt-6 border-t border-stone-200/80">
                  <h2 className="text-xs uppercase tracking-widest font-bold text-[#817262] mb-6">
                    {isEn ? "In this program you get:" : "I forløbet får du:"}
                  </h2>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3.5 text-stone-700 font-light text-base md:text-lg">
                      <span className="text-[#817262] font-serif text-xl leading-none">✧</span>
                      <span>{isEn ? "Teaching, meditations, and practical exercises" : "Undervisning, meditationer og konkrete øvelser"}</span>
                    </li>
                    <li className="flex items-start gap-3.5 text-stone-700 font-light text-base md:text-lg">
                      <span className="text-[#817262] font-serif text-xl leading-none">✧</span>
                      <span>{isEn ? "More calm, direction, and connection with yourself" : "Mere ro, retning og kontakt med dig selv"}</span>
                    </li>
                    <li className="flex items-start gap-3.5 text-stone-700 font-light text-base md:text-lg">
                      <span className="text-[#817262] font-serif text-xl leading-none">✧</span>
                      <span>{isEn ? "Full flexibility from home in your own pace" : "Fuld fleksibilitet hjemmefra i dit eget tempo"}</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Right Column: Pricing & Newsletter Form Box */}
              <div className="lg:col-span-5">
                <div className="bg-[#f5efe9]/30 p-8 md:p-10 border border-[#817262]/30 rounded-sm space-y-6 shadow-sm relative overflow-hidden">
                  <div className="flex items-center justify-between border-b border-[#817262]/20 pb-4">
                    <span className="text-xs uppercase tracking-widest font-bold text-[#817262]">
                      {isEn ? "Self-study video program" : "Selvstudie videoforløb"}
                    </span>
                    <span className="bg-white/80 border border-[#817262]/30 text-[#817262] text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-sm">
                      {isEn ? "12 weeks" : "12 uger"}
                    </span>
                  </div>

                  <div>
                    <span className="text-xs text-stone-500 uppercase tracking-widest font-bold block mb-1">
                      {isEn ? "Price" : "Pris"}
                    </span>
                    <p className="text-3xl font-serif text-stone-900">1.250 kr.</p>
                  </div>

                  <div className="bg-white p-6 rounded-sm border border-[#817262]/20 space-y-4 shadow-2xs">
                    <div className="flex items-start gap-3 text-stone-800">
                      <i className="fa-solid fa-ticket text-[#817262] mt-1 text-base shrink-0" aria-hidden="true" />
                      <p className="text-sm leading-relaxed font-medium">
                        {isEn
                          ? "Sign up for the newsletter and get 10% discount on the program when it launches."
                          : "Tilmeld dig nyhedsbrevet, og få 10 % rabat på forløbet, når det udkommer."}
                      </p>
                    </div>

                    <form 
                      action="https://e.lumant.dk/api/v1/optin/list_6v-RTZGckC/subscribe" 
                      method="post"
                      className="space-y-3 pt-2"
                    >
                      <input type="hidden" name="ref" value="" />
                      <div className="sr-only" aria-hidden="true">
                        <input type="text" name="hp_reference" tabIndex={-1} autoComplete="off" />
                      </div>
                      <input
                        type="text"
                        name="name"
                        placeholder={isEn ? "Your name" : "Dit navn"}
                        className="w-full h-11 px-3.5 bg-white border border-stone-300 rounded-sm text-stone-900 text-sm focus:outline-none focus:border-[#817262]"
                      />
                      <input
                        type="email"
                        name="email"
                        placeholder={isEn ? "Your email" : "Din e-mail"}
                        pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                        required
                        className="w-full h-11 px-3.5 bg-white border border-stone-300 rounded-sm text-stone-900 text-sm focus:outline-none focus:border-[#817262]"
                      />
                      <button
                        type="submit"
                        name="submit"
                        className="w-full h-11 bg-[#817262] hover:bg-[#6c5e50] text-white text-xs font-bold uppercase tracking-widest rounded-sm transition-colors cursor-pointer"
                      >
                        {isEn ? "Get 10% discount on release" : "Få 10 % rabat ved udgivelse"}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Kundeudtalelse */}
      {(() => {
        const frisaetTestimonial = TESTIMONIALS.find(tm => tm.name === "Else Støchkel") || TESTIMONIALS[0];
        if (!frisaetTestimonial) return null;
        const testimonialText = isEn
          ? `Dear Mette!
Meeting you is like a gift from heaven.
I am deeply grateful that you will help me continue on my spiritual journey.
You are simply such a wonderful human being - you meet people where they are, and bring out the best with light and great love.
Thank you for your wisdom and your open heart.`
          : frisaetTestimonial.text;

        return (
          <section className="py-24 bg-[#f5efe9]/30 px-6 border-b border-stone-100">
            <div className="max-w-5xl mx-auto">
              <FadeIn>
                <div className="text-center mb-10">
                  <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#817262] mb-3 block">
                    {isEn ? "Testimonial" : "Kundeudtalelse"}
                  </span>
                  <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
                    {isEn ? "What clients say" : "Hvad klienter siger"}
                  </h2>
                </div>
                <div className="bg-white p-8 md:p-12 border border-stone-200/60 rounded-sm shadow-sm">
                  <p className="text-stone-700 font-light leading-relaxed whitespace-pre-line italic">
                    "{testimonialText}"
                  </p>
                  <p className="font-bold text-stone-900 uppercase tracking-widest text-[10px] mt-6">{frisaetTestimonial.name}</p>
                </div>
              </FadeIn>
            </div>
          </section>
        );
      })()}

      {/* Closing CTA */}
      <section className="grid grid-cols-1 md:grid-cols-2 bg-stone-900 text-stone-100">
        <div className="relative h-[50vh] md:h-auto min-h-[400px]">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/image_12.png" 
            alt="Frisæt dig" 
            className="absolute inset-0 w-full h-full object-cover opacity-80"
          />
          <div className="absolute bottom-8 left-8 z-10">
            <Link to={frisaetPath} className="text-sm uppercase tracking-widest font-bold underline underline-offset-4 hover:text-stone-300 transition-colors">
              {isEn ? "Back to Unleash Yourself →" : "Tilbage til Frisæt dig →"}
            </Link>
          </div>
        </div>
        <div className="flex flex-col justify-center px-8 md:px-16 lg:px-24 py-24">
          <FadeIn>
            <h3 className="text-3xl md:text-4xl font-serif leading-tight mb-8">
              {isEn ? "Are you ready to take the next step?" : "Er du klar til at tage det næste skridt?"}
            </h3>
            <h3 className="text-3xl md:text-4xl font-serif leading-tight mb-12 text-stone-400">
              {isEn
                ? "Whether you desire an inspiring keynote, an intensive workshop, or a deep mentoring journey, you are always welcome to reach out. Let us have an informal conversation about what fits you best."
                : "Uanset om du ønsker et inspirerende foredrag til dit netværk, en intensiv workshop eller et dybtgående forløb, er du altid velkommen til at række ud. Lad os tage en uforpligtende snak om, hvad der passer bedst til dig."}
            </h3>
            <div className="flex flex-wrap gap-4">
              <Link 
                to={isEn ? "/en/contact?subject=Unleash Yourself Video Program" : "/kontakt?subject=Frisæt dig videoforløb"} 
                className="inline-block bg-white text-stone-900 px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-stone-100 transition-all"
              >
                {isEn ? "Contact me now" : "Kontakt mig nu"}
              </Link>
              <a 
                href="tel:+4527507083" 
                className="inline-flex items-center gap-2 border border-white/80 text-white px-7 py-4 text-xs uppercase tracking-[0.15em] font-bold hover:bg-white hover:text-stone-900 transition-all duration-300"
              >
                <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
                <span>{isEn ? "Call to hear more: 27 50 70 83" : "Ring, og hør mere: 27 50 70 83"}</span>
              </a>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

const FrisaetDigWorkshopPage = () => {
  const { lang } = useLanguage();
  const isEn = lang === 'en';
  const frisaetPath = isEn ? '/en/unleash-yourself' : '/frisaet-dig';
  const practicalPath = isEn ? '/en/unleash-yourself/workshop/practical-information' : '/frisaet-dig/workshop/praktisk-information';
  const { workshopFrisaetDig, testimonials } = useCms();
  const { openTestimonial } = useTestimonialModal() || {};

  const activeTestimonials = (testimonials && testimonials.length > 0) ? testimonials : TESTIMONIALS;
  const mariaTestimonial = activeTestimonials.find(t => t.name === "Maria Frandsen" && (t.title?.toLowerCase().includes("kursus") || t.text?.toLowerCase().includes("lærerig dag"))) || TESTIMONIALS.find(t => t.name === "Maria Frandsen" && t.title === "1-dagskursus");
  const janeTestimonial = activeTestimonials.find(t => t.name === "Jane Cedersted" && (t.title?.toLowerCase().includes("kursus") || t.text?.toLowerCase().includes("fint rum"))) || TESTIMONIALS.find(t => t.name === "Jane Cedersted" && t.title === "1-dagskursus");
  const workshopTestimonials = [mariaTestimonial, janeTestimonial].filter(Boolean);

  const badge = workshopFrisaetDig?.hero_badge?.[lang] || workshopFrisaetDig?.hero_badge?.da || (isEn ? "1-Day Workshop" : "Endagsworkshop");
  const title = workshopFrisaetDig?.hero_titel?.[lang] || workshopFrisaetDig?.hero_titel?.da || (isEn 
    ? "7 steps to create your dream life and learn to live from your heart" 
    : "Er du klar til at mærke og udfolde dine gaver og dit potentiale?");
  const subtitle = workshopFrisaetDig?.hero_undertitel?.[lang] || workshopFrisaetDig?.hero_undertitel?.da || (isEn 
    ? "Two 1-day workshops in Copenhagen: Saturday, October 31 and Sunday, November 1. Development in personal freedom and inner leadership." 
    : "To endagsworkshops i København: Lørdag den 31. oktober og søndag den 1. november. Et kursus til dig, der ønsker at komme hjem til dig selv og leve dit liv med mere mod, frihed og handlekraft.");
  const heroImage = (workshopFrisaetDig?.hero_billede?.[0] && !workshopFrisaetDig.hero_billede[0].includes('image_7')) 
    ? workshopFrisaetDig.hero_billede[0] 
    : "/kundebilleder/Mette+som+underviser.jpg";

  const introHtml = workshopFrisaetDig?.intro_tekst?.[lang] || workshopFrisaetDig?.intro_tekst?.da;
  const weWorkWithTitle = workshopFrisaetDig?.vi_arbejder_med_titel?.[lang] || workshopFrisaetDig?.vi_arbejder_med_titel?.da || (isEn ? "We work with:" : "Vi arbejder med:");
  const weWorkWithHtml = workshopFrisaetDig?.vi_arbejder_med_punkter?.[lang] || workshopFrisaetDig?.vi_arbejder_med_punkter?.da;
  const extraHtml = workshopFrisaetDig?.ekstra_tekst?.[lang] || workshopFrisaetDig?.ekstra_tekst?.da;
  const whatYouGetTitle = workshopFrisaetDig?.udbytte_titel?.[lang] || workshopFrisaetDig?.udbytte_titel?.da || (isEn ? "What you receive:" : "Det får du");
  const whatYouGetHtml = workshopFrisaetDig?.udbytte_punkter?.[lang] || workshopFrisaetDig?.udbytte_punkter?.da;

  const practicalTitle = workshopFrisaetDig?.praktisk_titel?.[lang] || workshopFrisaetDig?.praktisk_titel?.da || (isEn ? "Workshop Details" : "Praktisk information");
  const datesHtml = workshopFrisaetDig?.datoer_og_tid?.[lang] || workshopFrisaetDig?.datoer_og_tid?.da;
  const locationHtml = workshopFrisaetDig?.sted_detaljer?.[lang] || workshopFrisaetDig?.sted_detaljer?.da;
  const price = workshopFrisaetDig?.pris?.[lang] || workshopFrisaetDig?.pris?.da || (isEn ? "Intro price 850 DKK (normal 1,250 DKK)" : "Intropris 850 kr. (normalpris 1.250 kr.) pr. workshop");
  const cateringDiscountHtml = workshopFrisaetDig?.forplejning_og_rabat?.[lang] || workshopFrisaetDig?.forplejning_og_rabat?.da;
  const videoUrl = workshopFrisaetDig?.video_url || "https://youtu.be/qaWsd-IrvmY?si=StXHKmYsMyJN2ese";

  const ctaTitle = workshopFrisaetDig?.cta_titel?.[lang] || workshopFrisaetDig?.cta_titel?.da || (isEn ? "Are you ready for spiritual activation and deep personal development?" : "Er du klar til spirituel aktivering og dyb personlig udvikling?");
  const ctaText = workshopFrisaetDig?.cta_tekst?.[lang] || workshopFrisaetDig?.cta_tekst?.da || (isEn
    ? "I work with you who want to strengthen the connection to your soul, develop your intuition, heal what holds you back, and unfold more of the potential you are here with. Through courses, retreats, sessions, and teaching, I help you create real transformation in your life."
    : "Jeg arbejder med dig, der vil styrke forbindelsen til din sjæl, udvikle din intuition, heale det, der holder dig tilbage, og udfolde mere af det potentiale, du er her med. Gennem forløb, retreats, sessioner og undervisning hjælper jeg dig med at skabe reel transformation i dit liv.");

  useEffect(() => {
    document.title = isEn 
      ? `Mette Brudvig - ${title}` 
      : `Mette Brudvig - ${title}`;
    window.scrollTo(0, 0);
  }, [isEn, title]);

  const fallbackWeWorkWith = isEn ? [
    "Personal dismantling",
    "Consciousness",
    "You as energy",
    "Shifting your operating system",
    "Entering flow with life and strengthening your intuition",
    "Creating your dream life",
    "Your place in the world"
  ] : [
    "Personlig afvikling",
    "Bevidsthed",
    "Dig som energi",
    "At skifte styresystem",
    "At komme i flow med livet og styrke din intuition",
    "At skabe dit drømmeliv",
    "Din plads i verden"
  ];

  const fallbackWhatYouGet = isEn ? [
    "Intensive 1-day workshop with teaching, meditation, reflection and exercises",
    "The 7 steps to unleash yourself and create the life you desire",
    "Concrete tools to manage your patterns, your energy and what holds you back",
    "Time and peace to feel what you wish to release – and what you wish to create",
    "Work with heart, intuition, trust and flow",
    "A stronger connection to your soul and heart",
    "A shift in your energy that kickstarts your transformation",
    "Insight into what a dream life is for you, and trust that life helps you create it",
    "Inspiration to take concrete steps toward your dream life"
  ] : [
    "Intensiv endagsworkshop med undervisning, meditation, refleksion og øvelser",
    "De 7 trin til at frisætte dig og skabe det liv, du ønsker",
    "Konkrete værktøjer til at håndtere dine mønstre, din energi og det, der holder dig tilbage",
    "Tid og ro til at mærke, hvad du ønsker at slippe – og hvad du ønsker at skabe",
    "Arbejde med hjerte, intuition, tillid og flow",
    "En stærkere forbindelse til din sjæl og dit hjerte",
    "En forandring i din energi, der kickstarter din transformation",
    "Indsigt i, hvad et drømmeliv er for dig, og tryghed i, at livet hjælper dig med at skabe det",
    "Inspiration til at tage konkrete skridt mod dit drømmeliv"
  ];

  return (
    <div className="bg-[#faf9f6] min-h-screen">
      {/* Subpage Header / Breadcrumb Hero */}
      <section className="relative min-h-[65vh] md:min-h-[75vh] pt-[185px] md:pt-[215px] lg:pt-[225px] pb-16 bg-[#f5efe9] border-b border-stone-200/60 overflow-hidden flex items-center">
        <div className="max-w-7xl mx-auto px-6 z-10 relative w-full">
          <div className="max-w-2xl">
            <FadeIn>
              <h1 className="text-3xl md:text-5xl font-serif text-stone-900 mb-3">
                {title}
              </h1>
              <p className="text-[#817262] font-medium text-lg md:text-xl italic max-w-2xl leading-relaxed mb-6">
                {subtitle}
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <a 
                  href="#datoer-og-tilmelding" 
                  className="bg-[#004f3b] text-white px-7 py-3.5 text-xs uppercase tracking-[0.2em] font-bold hover:bg-emerald-900 transition-all rounded-sm shadow-sm"
                >
                  {isEn ? "See Dates & Book" : "Se datoer & Tilmelding"}
                </a>
              </div>
            </FadeIn>
          </div>
        </div>
        <div className="absolute top-0 right-0 w-full md:w-1/2 h-full opacity-15 md:opacity-100 pt-20 md:pt-24 lg:pt-28 pointer-events-none">
          <img 
            src={heroImage} 
            alt={isEn ? `Mette Brudvig - ${title}` : `Mette Brudvig underviser - ${title}`}
            className="w-full h-full object-cover object-center pointer-events-none"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#f5efe9] via-transparent to-transparent hidden md:block pointer-events-none" />
        </div>
      </section>

      {/* Main Content Section */}
      <section id="workshop" className="py-20 bg-white px-6 border-b border-stone-100">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="grid lg:grid-cols-12 gap-12 items-start">
              {/* Left Column: Description & Content */}
              <div className="lg:col-span-7 space-y-10">
                {/* Intro Paragraphs */}
                {introHtml ? (
                  <div 
                    className="text-stone-700 font-light text-base md:text-lg leading-relaxed space-y-5"
                    dangerouslySetInnerHTML={{ __html: introHtml }}
                  />
                ) : (
                  <div className="text-stone-700 font-light text-base md:text-lg leading-relaxed space-y-5">
                    <p>
                      {isEn
                        ? "Do you want to kickstart your transformation towards a life with more freedom, courage, trust and heart? To set yourself free from what is holding you back and begin creating a life you love? Then this weekend is for you."
                        : "Har du lyst til at kickstarte din transformation mod et liv med mere frihed, mod, tillid og hjerte? At sætte dig selv fri fra det, der holder dig tilbage og begynde at skabe et liv, du elsker? Så er denne weekend til dig."}
                    </p>
                    <p>
                      {isEn
                        ? "I take you through my 7 steps to liberation, created on the foundation of my own transformational journey, experience, professional knowledge and channeled insights."
                        : "Jeg tager dig gennem mine 7 trin til frisættelse, skabt på baggrund af min egen transformationsrejse, erfaring, faglige viden og kanaliseret indsigt."}
                    </p>
                    <p>
                      {isEn
                        ? "I have walked the path myself and use my own experiences along the way, so you can understand the steps, recognize them in your own life and apply them concretely in your own development. We work with your energy, your thought patterns and your intuition."
                        : "Jeg har selv gået vejen og bruger mine egne erfaringer undervejs, så du kan forstå trinene, genkende dem i dit eget liv og bruge dem konkret i din egen udvikling. Vi arbejder både med din energi, dine tankemønstre og din intuition."}
                    </p>
                  </div>
                )}

                {/* Vi arbejder med */}
                <div className="bg-[#f5efe9]/50 p-8 rounded-sm border border-[#817262]/20 space-y-5">
                  <h2 className="text-xs uppercase tracking-widest font-bold text-[#817262]">
                    {weWorkWithTitle}
                  </h2>
                  {weWorkWithHtml ? (
                    <div 
                      className="space-y-3 [&_ul]:space-y-3 [&_ol]:space-y-3 [&_ul]:list-none [&_ol]:list-none [&_ul]:p-0 [&_ol]:p-0 [&_ul]:m-0 [&_ol]:m-0 [&_li]:list-none [&_li]:flex [&_li]:items-start [&_li]:gap-3.5 [&_li]:text-stone-800 [&_li]:font-light [&_li]:text-base [&_li]:before:content-['✧'] [&_li]:before:text-[#817262] [&_li]:before:font-serif [&_li]:before:text-lg [&_li]:before:leading-none [&_li]:before:mt-0.5 [&_li]:before:shrink-0 [&_li_p]:inline [&_li_p]:m-0"
                      dangerouslySetInnerHTML={{ __html: weWorkWithHtml }}
                    />
                  ) : (
                    <ul className="space-y-3 list-none p-0 m-0">
                      {fallbackWeWorkWith.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3.5 text-stone-800 font-light text-base">
                          <span className="text-[#817262] font-serif text-lg leading-none mt-0.5 shrink-0">✧</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Additional Text */}
                {extraHtml ? (
                  <div 
                    className="text-stone-700 font-light text-base md:text-lg leading-relaxed space-y-5"
                    dangerouslySetInnerHTML={{ __html: extraHtml }}
                  />
                ) : (
                  <div className="text-stone-700 font-light text-base md:text-lg leading-relaxed space-y-5">
                    <p>
                      {isEn
                        ? "The weekend is a blend of teaching, meditation, reflection and exercises where you work directly with your own life."
                        : "Weekenden er en blanding af undervisning, meditation, refleksion og øvelser, hvor du arbejder direkte med dit eget liv."}
                    </p>
                    <p>
                      {isEn
                        ? "You leave with a stronger connection to yourself, greater faith that you can create the life you desire, and concrete tools on how to do so."
                        : "Du tager derfra med en stærkere forbindelse til dig selv, større tro på, at du kan skabe det liv, du ønsker, og konkrete redskaber til hvordan."}
                    </p>
                    <p className="font-medium text-stone-900">
                      {isEn
                        ? "You will receive a guided meditation to take home, so you can continue the work."
                        : "Du får en guidet meditation med, så du kan fortsætte arbejdet derhjemme."}
                    </p>
                  </div>
                )}

                {/* Det får du */}
                <div className="pt-8 border-t border-stone-200/80">
                  <h2 className="text-xs uppercase tracking-widest font-bold text-[#817262] mb-6">
                    {whatYouGetTitle}
                  </h2>
                  {whatYouGetHtml ? (
                    <div 
                      className="space-y-3.5 [&_ul]:space-y-3.5 [&_ol]:space-y-3.5 [&_ul]:list-none [&_ol]:list-none [&_ul]:p-0 [&_ol]:p-0 [&_ul]:m-0 [&_ol]:m-0 [&_li]:list-none [&_li]:flex [&_li]:items-start [&_li]:gap-3.5 [&_li]:text-stone-700 [&_li]:font-light [&_li]:text-base md:[&_li]:text-lg [&_li]:before:content-['✧'] [&_li]:before:text-[#817262] [&_li]:before:font-serif [&_li]:before:text-xl [&_li]:before:leading-none [&_li]:before:shrink-0 [&_li_p]:inline [&_li_p]:m-0"
                      dangerouslySetInnerHTML={{ __html: whatYouGetHtml }}
                    />
                  ) : (
                    <ul className="space-y-3.5 list-none p-0 m-0">
                      {fallbackWhatYouGet.map((bullet, idx) => (
                        <li key={idx} className="flex items-start gap-3.5 text-stone-700 font-light text-base md:text-lg">
                          <span className="text-[#817262] font-serif text-xl leading-none shrink-0">✧</span>
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Right Column: Video & Calm Summary */}
              <div className="lg:col-span-5 space-y-8">
                {/* Intro Video */}
                <YouTubeEmbed 
                  url={videoUrl} 
                  title={isEn ? "Intro to Unleash Yourself 1-day workshop" : "Intro til Frisæt dig endagsworkshop"} 
                />

                {/* Compact Dates & Booking Overview */}
                <div id="datoer-og-tilmelding" className="bg-[#f5efe9] p-3.5 sm:p-6 md:p-8 border border-[#817262]/30 rounded-sm space-y-3.5 sm:space-y-4 shadow-sm">
                  {/* Header */}
                  <div className="flex items-center justify-between pb-1 px-1 border-b border-[#817262]/20">
                    <span className="text-[11px] uppercase tracking-widest font-bold text-[#817262]">
                      {isEn ? "Dates & Registration" : "Datoer & Tilmelding"}
                    </span>
                    <span className="text-[11px] font-bold text-stone-800 font-serif">
                      {isEn ? "Intro price 850 DKK" : "Intropris 850 kr."}
                    </span>
                  </div>

                  {/* Workshop 1: København 31. okt */}
                  <div className="bg-white/95 p-3.5 sm:p-4.5 rounded-sm border border-stone-200/90 shadow-2xs space-y-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] font-semibold text-[#817262] uppercase tracking-wider">
                        {isEn ? "Copenhagen · Halo House" : "København · Halo House"}
                      </span>
                    </div>

                    <h4 className="text-sm sm:text-base font-serif font-bold text-stone-900 leading-snug">
                      {isEn ? "Saturday, October 31, 2026" : "Lørdag den 31. oktober 2026"}
                    </h4>

                    <div className="text-[11px] sm:text-xs text-stone-600 font-light space-y-1">
                      <p className="flex items-center gap-1.5">
                        <i className="fa-regular fa-clock text-[10px] text-[#817262] shrink-0" aria-hidden="true" />
                        <span>{isEn ? "10:00–16:00" : "Kl. 10.00–16.00"}</span>
                      </p>
                      <p className="flex items-start gap-1.5">
                        <i className="fa-regular fa-location-dot text-[10px] text-[#817262] mt-0.5 shrink-0" aria-hidden="true" />
                        <span className="leading-tight">{isEn ? "Store Regnegade 12, 4. floor, 1110 Cph K" : "Store Regnegade 12, 4. sal, 1110 Kbh K"}</span>
                      </p>
                    </div>

                    <a
                      href="https://pay.lumant.ai/kurv/kPQW-kursus-31-okt"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full bg-[#817262] text-white py-2.5 px-3 rounded-xs uppercase text-xs tracking-wider font-bold hover:bg-stone-900 transition-colors shadow-xs"
                    >
                      <span>{isEn ? "Book ticket" : "Køb billet"}</span>
                      <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
                    </a>
                  </div>

                  {/* Workshop 2: Sønderborg 14. nov */}
                  <div className="bg-white/95 p-3.5 sm:p-4.5 rounded-sm border border-stone-200/90 shadow-2xs space-y-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] font-semibold text-[#817262] uppercase tracking-wider">
                        {isEn ? "Sønderborg" : "Sønderborg"}
                      </span>
                    </div>

                    <h4 className="text-sm sm:text-base font-serif font-bold text-stone-900 leading-snug">
                      {isEn ? "Saturday, November 14, 2026" : "Lørdag den 14. november 2026"}
                    </h4>

                    <div className="text-[11px] sm:text-xs text-stone-600 font-light space-y-1">
                      <p className="flex items-center gap-1.5">
                        <i className="fa-regular fa-clock text-[10px] text-[#817262] shrink-0" aria-hidden="true" />
                        <span>{isEn ? "10:00–15:00" : "Kl. 10.00–15.00"}</span>
                      </p>
                      <p className="flex items-start gap-1.5">
                        <i className="fa-regular fa-location-dot text-[10px] text-[#817262] mt-0.5 shrink-0" aria-hidden="true" />
                        <span className="leading-tight">{isEn ? "Sønderborg" : "Sønderborg"}</span>
                      </p>
                    </div>

                    <a
                      href="https://pay.lumant.ai/kurv/uKr02SgdevQ9v8UqliEpG"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full bg-[#817262] text-white py-2.5 px-3 rounded-xs uppercase text-xs tracking-wider font-bold hover:bg-stone-900 transition-colors shadow-xs"
                    >
                      <span>{isEn ? "Book ticket" : "Køb billet"}</span>
                      <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
                    </a>
                  </div>

                  {/* Workshop 2: Århus 9. jan */}
                  <div className="bg-white/95 p-3.5 sm:p-4.5 rounded-sm border border-stone-200/90 shadow-2xs space-y-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] font-semibold text-[#817262] uppercase tracking-wider">
                        {isEn ? "Aarhus · Business United" : "Århus · Business United"}
                      </span>
                    </div>

                    <h4 className="text-sm sm:text-base font-serif font-bold text-stone-900 leading-snug">
                      {isEn ? "Saturday, January 9, 2027" : "Lørdag den 9. januar 2027"}
                    </h4>

                    <div className="text-[11px] sm:text-xs text-stone-600 font-light space-y-1">
                      <p className="flex items-center gap-1.5">
                        <i className="fa-regular fa-clock text-[10px] text-[#817262] shrink-0" aria-hidden="true" />
                        <span>{isEn ? "10:00–16:00" : "Kl. 10.00–16.00"}</span>
                      </p>
                      <p className="flex items-start gap-1.5">
                        <i className="fa-regular fa-location-dot text-[10px] text-[#817262] mt-0.5 shrink-0" aria-hidden="true" />
                        <span className="leading-tight">{isEn ? "Mosevej 20 B, 8240 Risskov" : "Mosevej 20 B, 8240 Risskov"}</span>
                      </p>
                    </div>

                    <a
                      href="https://pay.lumant.ai/kurv/GEs95vOg-YKHE-EmcgN_a"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full bg-[#817262] text-white py-2.5 px-3 rounded-xs uppercase text-xs tracking-wider font-bold hover:bg-stone-900 transition-colors shadow-xs"
                    >
                      <span>{isEn ? "Book ticket" : "Køb billet"}</span>
                      <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
                    </a>
                  </div>

                  {/* Workshop 3: Århus 10. jan */}
                  <div className="bg-white/95 p-3.5 sm:p-4.5 rounded-sm border border-stone-200/90 shadow-2xs space-y-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] font-semibold text-[#817262] uppercase tracking-wider">
                        {isEn ? "Aarhus · Business United" : "Århus · Business United"}
                      </span>
                    </div>

                    <h4 className="text-sm sm:text-base font-serif font-bold text-stone-900 leading-snug">
                      {isEn ? "Sunday, January 10, 2027" : "Søndag den 10. januar 2027"}
                    </h4>

                    <div className="text-[11px] sm:text-xs text-stone-600 font-light space-y-1">
                      <p className="flex items-center gap-1.5">
                        <i className="fa-regular fa-clock text-[10px] text-[#817262] shrink-0" aria-hidden="true" />
                        <span>{isEn ? "10:00–16:00" : "Kl. 10.00–16.00"}</span>
                      </p>
                      <p className="flex items-start gap-1.5">
                        <i className="fa-regular fa-location-dot text-[10px] text-[#817262] mt-0.5 shrink-0" aria-hidden="true" />
                        <span className="leading-tight">{isEn ? "Mosevej 20 B, 8240 Risskov" : "Mosevej 20 B, 8240 Risskov"}</span>
                      </p>
                    </div>

                    <a
                      href="https://pay.lumant.ai/kurv/BaDSMFZ3ZQO_zCC-x01YN"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full bg-[#817262] text-white py-2.5 px-3 rounded-xs uppercase text-xs tracking-wider font-bold hover:bg-stone-900 transition-colors shadow-xs"
                    >
                      <span>{isEn ? "Book ticket" : "Køb billet"}</span>
                      <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
                    </a>
                  </div>

                  <div className="pt-1.5 text-center">
                    <Link
                      to={isEn ? "/en/unleash-yourself/workshop/practical-information" : "/frisaet-dig/workshop/praktisk-information"}
                      className="inline-flex items-center gap-1.5 text-xs text-[#817262] hover:text-stone-900 font-semibold underline underline-offset-4 transition-colors py-1"
                    >
                      <span>{isEn ? "See full practical information & locations →" : "Se fuld praktisk information & lokationer →"}</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>


    </div>
  );
};

const FrisaetDigWorkshopPracticalPage = () => {
  const { lang } = useLanguage();
  const isEn = lang === 'en';
  const workshopPath = isEn ? '/en/unleash-yourself/workshop' : '/frisaet-dig/workshop';
  const frisaetPath = isEn ? '/en/unleash-yourself' : '/frisaet-dig';
  const [status, setStatus] = useState('');
  const [selectedWorkshop, setSelectedWorkshop] = useState('kbh-31-okt');
  const { workshopFrisaetDig, testimonials } = useCms();
  const { openTestimonial } = useTestimonialModal() || {};

  const workshopList = [
    {
      id: 'kbh-31-okt',
      city: isEn ? "Copenhagen · Halo House" : "København (Halo House)",
      address: "Store Regnegade 12, 4. sal, 1110 København K",
      date: isEn ? "Saturday, Oct 31, 2026" : "Lørdag den 31. oktober 2026",
      time: "kl. 10.00–16.00",
      season: isEn ? "Autumn 2026" : "Efterår 2026",
      url: "https://pay.lumant.ai/kurv/kPQW-kursus-31-okt",
      formValue: "København - Lørdag 31. oktober 2026"
    },
    {
      id: 'soenderborg-14-nov',
      city: isEn ? "Sønderborg" : "Sønderborg",
      address: "Sønderborg",
      date: isEn ? "Saturday, Nov 14, 2026" : "Lørdag den 14. november 2026",
      time: "kl. 10.00–15.00",
      season: isEn ? "Autumn 2026" : "Efterår 2026",
      url: "https://pay.lumant.ai/kurv/uKr02SgdevQ9v8UqliEpG",
      formValue: "Sønderborg - Lørdag 14. november 2026"
    },
    {
      id: 'aarhus-9-jan',
      city: isEn ? "Aarhus · Business United" : "Århus (Business United)",
      address: "Mosevej 20 B, 8240 Risskov",
      date: isEn ? "Saturday, Jan 9, 2027" : "Lørdag den 9. januar 2027",
      time: "kl. 10.00–16.00",
      season: isEn ? "Winter 2027" : "Vinter 2027",
      url: "https://pay.lumant.ai/kurv/GEs95vOg-YKHE-EmcgN_a",
      formValue: "Århus - Lørdag 9. januar 2027"
    },
    {
      id: 'aarhus-10-jan',
      city: isEn ? "Aarhus · Business United" : "Århus (Business United)",
      address: "Mosevej 20 B, 8240 Risskov",
      date: isEn ? "Sunday, Jan 10, 2027" : "Søndag den 10. januar 2027",
      time: "kl. 10.00–16.00",
      season: isEn ? "Winter 2027" : "Vinter 2027",
      url: "https://pay.lumant.ai/kurv/BaDSMFZ3ZQO_zCC-x01YN",
      formValue: "Århus - Søndag 10. januar 2027"
    }
  ];

  const activeWorkshop = workshopList.find(w => w.id === selectedWorkshop) || workshopList[0];

  const activeTestimonials = (testimonials && testimonials.length > 0) ? testimonials : TESTIMONIALS;
  const gitteTestimonial = activeTestimonials.find(t => t.name === "Gitte" && (t.title?.toLowerCase().includes("kursus") || t.title?.toLowerCase().includes("workshop"))) || TESTIMONIALS.find(t => t.name === "Gitte" && t.title === "En dags kursus");
  const sonjaTestimonial = activeTestimonials.find(t => t.name === "Sonja Dubgaard") || TESTIMONIALS.find(t => t.name === "Sonja Dubgaard");

  const heroBadge = workshopFrisaetDig?.praktisk_hero_badge?.[lang] || workshopFrisaetDig?.praktisk_hero_badge?.da || (isEn ? "Practical Information" : "Praktisk information");
  const heroTitle = workshopFrisaetDig?.praktisk_hero_titel?.[lang] || workshopFrisaetDig?.praktisk_hero_titel?.da || (isEn 
    ? "Practical Information – 1-Day Workshops" 
    : "Praktisk information – Endagsworkshops");
  const heroSubtitle = workshopFrisaetDig?.praktisk_hero_undertitel?.[lang] || workshopFrisaetDig?.praktisk_hero_undertitel?.da || (isEn 
    ? "Frisæt dig – Two separate 1-day workshops in Copenhagen (Saturday Oct 31 or Sunday Nov 1)." 
    : "Frisæt dig – To selvstændige endagsworkshops i København (lørdag 31. okt. eller søndag 1. nov.).");
  const heroImage = (workshopFrisaetDig?.praktisk_hero_billede?.[0] && !workshopFrisaetDig.praktisk_hero_billede[0].includes('image_7')) 
    ? workshopFrisaetDig.praktisk_hero_billede[0] 
    : (workshopFrisaetDig?.hero_billede?.[0] && !workshopFrisaetDig.hero_billede[0].includes('image_7'))
      ? workshopFrisaetDig.hero_billede[0]
      : "/kundebilleder/Mette+som+underviser.jpg";

  const overviewBadge = workshopFrisaetDig?.praktisk_sektion_badge?.[lang] || workshopFrisaetDig?.praktisk_sektion_badge?.da || (isEn ? "Overview" : "Oversigt");
  const overviewTitle = workshopFrisaetDig?.praktisk_sektion_titel?.[lang] || workshopFrisaetDig?.praktisk_sektion_titel?.da || (isEn ? "Practical Information" : "Praktisk information");
  const datesHtml = workshopFrisaetDig?.praktisk_datoer?.[lang] || workshopFrisaetDig?.praktisk_datoer?.da || workshopFrisaetDig?.datoer_og_tid?.[lang] || workshopFrisaetDig?.datoer_og_tid?.da;
  const locationHtml = workshopFrisaetDig?.praktisk_lokation?.[lang] || workshopFrisaetDig?.praktisk_lokation?.da || workshopFrisaetDig?.sted_detaljer?.[lang] || workshopFrisaetDig?.sted_detaljer?.da;
  const price = workshopFrisaetDig?.praktisk_pris?.[lang] || workshopFrisaetDig?.praktisk_pris?.da || workshopFrisaetDig?.pris?.[lang] || workshopFrisaetDig?.pris?.da || (isEn ? "Intro price 850 DKK (normal 1,250 DKK) per workshop" : "Intropris 850 kr. (normalpris 1.250 kr.) pr. workshop");
  const cateringHtml = workshopFrisaetDig?.praktisk_forplejning?.[lang] || workshopFrisaetDig?.praktisk_forplejning?.da;
  const discountHtml = workshopFrisaetDig?.praktisk_rabat_note?.[lang] || workshopFrisaetDig?.praktisk_rabat_note?.da;

  const formBadge = workshopFrisaetDig?.praktisk_form_badge?.[lang] || workshopFrisaetDig?.praktisk_form_badge?.da || (isEn ? "Registration & Questions" : "Tilmelding & Kontakt");
  const formTitle = workshopFrisaetDig?.praktisk_form_titel?.[lang] || workshopFrisaetDig?.praktisk_form_titel?.da || (isEn ? "Sign up for a workshop" : "Tilmeld dig en workshop");
  const formText = workshopFrisaetDig?.praktisk_form_tekst?.[lang] || workshopFrisaetDig?.praktisk_form_tekst?.da || (isEn 
    ? "Fill in the form to register for one of the workshops or if you have any questions." 
    : "Udfyld formularen for at tilmelde dig en af endagsworkshopperne i København eller weekendworkshoppen i Århus, eller hvis du har spørgsmål.");
  const phoneRaw = workshopFrisaetDig?.praktisk_telefon || "27507083";
  const phoneDisplay = phoneRaw.length === 8 ? `${phoneRaw.slice(0, 2)} ${phoneRaw.slice(2, 4)} ${phoneRaw.slice(4, 6)} ${phoneRaw.slice(6, 8)}` : phoneRaw;

  useEffect(() => {
    document.title = isEn 
      ? `Practical Information | Unleash Yourself 1-Day Workshops | Mette Brudvig` 
      : `Praktisk Information | Frisæt dig endagsworkshops | Mette Brudvig`;
    window.scrollTo(0, 0);
  }, [isEn]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    const formData = new FormData(e.target);
    const honey = formData.get('website_url');
    if (honey) {
      setStatus('success');
      e.target.reset();
      return;
    }
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="bg-[#faf9f6] min-h-screen">
      {/* Subpage Header / Breadcrumb Hero with Image Overlay */}
      <section className="relative min-h-[60vh] md:min-h-[70vh] pt-[185px] md:pt-[215px] lg:pt-[225px] pb-16 bg-[#f5efe9] border-b border-stone-200/60 overflow-hidden flex items-center">
        <div className="max-w-7xl mx-auto px-6 z-10 relative w-full">
          <div className="max-w-2xl">
            <FadeIn>
              <h1 className="text-3xl md:text-5xl font-serif text-stone-900 mb-3">
                {heroTitle}
              </h1>
              <p className="text-[#817262] font-medium text-lg md:text-xl italic max-w-2xl leading-relaxed mb-6">
                {heroSubtitle}
              </p>
            </FadeIn>
          </div>
        </div>
        <div className="absolute top-0 right-0 w-full md:w-1/2 h-full opacity-15 md:opacity-100 pt-20 md:pt-24 lg:pt-28 pointer-events-none">
          <img 
            src={heroImage} 
            alt={isEn ? "Mette Brudvig teaching practical weekend workshop" : "Mette Brudvig underviser på Frisæt dig weekendworkshop"}
            className="w-full h-full object-cover object-center pointer-events-none"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#f5efe9] via-transparent to-transparent hidden md:block pointer-events-none" />
        </div>
      </section>

      {/* Practical Details & Contact Form Content */}
      <section id="praktisk" className="py-16 md:py-24 bg-[#faf7f2] text-stone-800 px-6 border-b border-stone-200/80">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 lg:gap-16 items-start">
              
              {/* Kolonne 1: Priser */}
              <div className="space-y-6">
                <h2 className="font-serif text-2xl md:text-3xl text-stone-900 font-normal">
                  {isEn ? "Pricing" : "Priser"}
                </h2>

                <div className="space-y-4 text-xs leading-relaxed text-stone-700">
                  <p className="font-bold text-stone-900 text-sm">
                    {price}
                  </p>
                  
                  <div className="space-y-2">
                    <p className="text-xs text-stone-600 leading-relaxed">
                      {isEn 
                        ? "Special intro price per workshop. Secure your spot directly via ticket booking."
                        : "Intropris pr. selvstændigt 1-dags kursus (normalpris 1.250 kr.)."}
                    </p>
                  </div>

                  {discountHtml ? (
                    <div 
                      className="pt-2 text-xs text-stone-900 font-semibold leading-relaxed [&>p]:leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: discountHtml }}
                    />
                  ) : (
                    <div className="pt-2 text-xs text-stone-900 font-semibold leading-relaxed">
                      {isEn
                        ? "NOTE: If you subsequently choose to enroll in the Unleash Yourself 1-year program, the 850 DKK will be deducted from the price of the program."
                        : "OBS !! Vælger du efterfølgende at tilmelde dig Frisæt dig-årsforløbet, fratrækkes de 850 kr. fra prisen på forløbet."}
                    </div>
                  )}

                  <div className="pt-3 border-t border-stone-200/80">
                    <Link 
                      to={workshopPath}
                      className="inline-flex items-center gap-2 text-[#817262] hover:text-stone-900 text-[11px] uppercase tracking-widest font-bold transition-colors"
                    >
                      <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" />
                      <span>{isEn ? "Read about the workshop content" : "Læs om kursets indhold"}</span>
                    </Link>
                  </div>
                </div>
              </div>

              {/* Kolonne 2: Datoer & Book din plads */}
              <div className="space-y-6">
                <div>
                  <h2 className="font-serif text-2xl md:text-3xl text-stone-900 font-normal">
                    {isEn ? "Dates & Booking" : "Datoer & Tilmelding"}
                  </h2>
                  <p className="text-xs text-stone-500 mt-1">
                    {isEn ? "Click on a date to book your spot:" : "Klik på en dato for at booke din plads:"}
                  </p>
                </div>

                <div className="space-y-2.5">
                  {workshopList.map((ws) => (
                    <a
                      key={ws.id}
                      href={ws.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group w-full p-3.5 sm:p-4 rounded-sm border border-stone-200/90 bg-white hover:border-[#817262] hover:bg-[#faf7f2] shadow-2xs hover:shadow-xs transition-all duration-200 flex items-center justify-between gap-3 text-left"
                    >
                      <div className="min-w-0 pr-2">
                        <p className="text-xs sm:text-sm font-semibold text-stone-900 group-hover:text-[#817262] transition-colors leading-snug">
                          {ws.date}
                        </p>
                        <p className="text-[11px] text-stone-500 font-normal mt-0.5">
                          {ws.city} · {ws.time}
                        </p>
                      </div>
                      <div className="shrink-0 flex items-center gap-2.5">
                        <span className="text-xs font-semibold text-stone-800 whitespace-nowrap">
                          850 kr.
                        </span>
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#817262] group-hover:bg-stone-800 text-white text-[10px] sm:text-[11px] uppercase tracking-wider font-bold rounded-sm transition-colors shadow-2xs">
                          <span>{isEn ? "Book" : "Book"}</span>
                          <i className="fa-solid fa-arrow-up-right-from-square text-[9px]" aria-hidden="true" />
                        </span>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              {/* Kolonne 3: Praktisk */}
              <div className="space-y-6">
                <h2 className="font-serif text-2xl md:text-3xl text-stone-900 font-normal">
                  {isEn ? "Practical" : "Praktisk"}
                </h2>

                <div className="space-y-5 text-xs leading-relaxed text-stone-700">
                  {/* Lokationer / Sted */}
                  <div className="space-y-1">
                    <h3 className="font-bold text-stone-900 uppercase text-xs tracking-wider">
                      {isEn ? "Venues:" : "Sted:"}
                    </h3>
                    {locationHtml ? (
                      <div 
                        className="text-xs space-y-1 [&>p]:leading-relaxed [&>p>strong]:font-semibold [&>p>strong]:text-stone-900"
                        dangerouslySetInnerHTML={{ __html: locationHtml }}
                      />
                    ) : (
                      <div className="space-y-1.5 text-xs">
                        <p>
                          <span className="font-semibold text-stone-900">Halo House:</span> St. Regnegade 12, 4. sal, 1110 København K
                        </p>
                        <p>
                          <span className="font-semibold text-stone-900">{isEn ? "Sønderborg:" : "Sønderborg:"}</span>{" "}
                          Sønderborg Hus, Martin Nyrops Plads 2, 6400 Sønderborg
                        </p>
                        <p>
                          <span className="font-semibold text-stone-900">Århus (Business United):</span> Mosevej 20 B, 8240 Risskov
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Forplejning */}
                  <div className="space-y-1 pt-1">
                    <h3 className="font-bold text-stone-900 uppercase text-xs tracking-wider">
                      {isEn ? "Catering:" : "Forplejning:"}
                    </h3>
                    {cateringHtml ? (
                      <div 
                        className="text-xs space-y-1 [&>p]:leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: cateringHtml }}
                      />
                    ) : (
                      <div className="space-y-1 text-xs text-stone-600 leading-relaxed">
                        <p>
                          {isEn
                            ? "Coffee, tea, water, fruit, nuts, and sweet snacks are served throughout the day."
                            : "Der serveres kaffe, te, vand, frugt, nødder og lidt sødt i løbet af dagen."}
                        </p>
                        <p>
                          {isEn
                            ? "You can bring a packed lunch or buy lunch at one of the many restaurants and eateries in the area."
                            : "Du kan tage madpakke med eller købe frokost på en af de mange restauranter og spisesteder i området."}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Kontakt direkte note */}
                  <div className="pt-2 border-t border-stone-200/80">
                    <p className="text-xs text-stone-600">
                      <span className="font-bold text-stone-900">{isEn ? "Questions?" : "Spørgsmål?"}</span>{" "}
                      {isEn ? (
                        <>
                          Feel free to{" "}
                          <Link
                            to="/en/contact"
                            className="font-bold text-stone-900 underline hover:text-[#817262] transition-colors"
                          >
                            contact Mette here
                          </Link>
                          .
                        </>
                      ) : (
                        <>
                          Du er altid velkommen til at{" "}
                          <Link
                            to="/kontakt"
                            className="font-bold text-stone-900 underline hover:text-[#817262] transition-colors"
                          >
                            kontakte Mette her
                          </Link>
                          .
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

const FrisaetDigForedragPage = () => {
  const { lang } = useLanguage();
  const isEn = lang === 'en';
  const frisaetPath = isEn ? '/en/unleash-yourself' : '/frisaet-dig';

  useEffect(() => {
    document.title = isEn 
      ? "Mette Brudvig - Keynote | Unleash Yourself" 
      : "Mette Brudvig - Foredrag | Frisæt dig";
    window.scrollTo(0, 0);
  }, [isEn]);

  return (
    <div className="bg-[#faf9f6] min-h-screen">
      {/* Subpage Header / Breadcrumb Hero */}
      <section className="relative pt-[185px] md:pt-[215px] lg:pt-[225px] pb-16 bg-[#f5efe9] border-b border-[#817262]/20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 z-10 relative">
          <FadeIn direction="up">
            <div className="mb-6 flex flex-wrap items-center gap-3">
              <Link 
                to={frisaetPath}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#817262] hover:text-stone-900 transition-colors"
              >
                <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" />
                <span>{isEn ? "Back to Unleash Yourself" : "Tilbage til Frisæt dig"}</span>
              </Link>
              <span className="text-stone-300">•</span>
              <span className="inline-flex items-center gap-1.5 bg-[#817262] text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full shadow-xs">
                <i className="fa-light fa-microphone text-xs" aria-hidden="true" />
                {isEn ? "Keynote" : "Foredrag"}
              </span>
            </div>
            
            <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#817262] mb-3 block">
              {isEn ? "Keynote" : "Foredrag"}
            </span>
            <h1 className="text-3xl md:text-5xl font-serif text-stone-900 mb-2">
              {isEn ? "Keynote: Unleash Yourself" : "Foredrag: Frisæt dig"}
            </h1>
            <p className="text-[#817262] font-medium text-lg md:text-xl italic max-w-3xl leading-relaxed mb-4">
              {isEn ? "– Do you carry your soul? The power born from living your true essence." : "– Har du sjælen med? Om den styrke, der opstår, når du lever fra din sande essens."}
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Main Content Section */}
      <section id="foredrag" className="py-20 bg-white px-6 border-b border-stone-100">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="grid lg:grid-cols-12 gap-12 items-start">
              {/* Left Column: Description & Highlights */}
              <div className="lg:col-span-7 space-y-8">
                <div className="text-stone-700 font-light text-base md:text-lg leading-relaxed space-y-4">
                  <p>
                    {isEn
                      ? "Activating heart intelligence is the missing link in personal development. You strengthen your intuition, decision quality, and unshakeable inner presence."
                      : "Aktivering af hjertets intelligens er the missing link, når du arbejder med dig selv for at opnå større klarhed, trivsel og succes. Du styrker din intuition, kvaliteten af dine beslutninger og står stærkt i dig selv."}
                  </p>
                </div>

                <div className="pt-6 border-t border-stone-200/80">
                  <h2 className="text-xs uppercase tracking-widest font-bold text-[#817262] mb-6">
                    {isEn ? "You receive:" : "Det får du:"}
                  </h2>
                  <ul className="space-y-4">
                    {(isEn ? [
                      "Combination of science and spirituality",
                      "Practical tools to sharpen your intuition",
                      "Insights into the new paradigm and age of the heart"
                    ] : [
                      "Kombination af videnskab og spiritualitet",
                      "Redskaber til at skærpe din intuition",
                      "Indsigt i det nye paradigme og hjertets tidsalder"
                    ]).map((bullet, idx) => (
                      <li key={idx} className="flex items-start gap-3.5 text-stone-700 font-light text-base md:text-lg">
                        <span className="text-[#817262] font-serif text-xl leading-none">✧</span>
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Inspirational Quote / Philosophy */}
                <div className="bg-[#f5efe9]/40 p-8 rounded-sm border-l-4 border-[#817262] space-y-3">
                  <p className="font-serif italic text-stone-800 text-lg leading-relaxed">
                    {isEn
                      ? "“When you activate your heart intelligence, you step out of old frameworks and into a deep peace where your decisions arise from truth and integrity.”"
                      : "“Når du aktiverer hjertets intelligens, træder du ud af de gamle rammer og ind i en dyb ro, hvor dine beslutninger udspringer af sandhed og integritet.”"}
                  </p>
                  <span className="text-xs uppercase tracking-widest font-bold text-[#817262] block">
                    — Mette Brudvig
                  </span>
                </div>
              </div>

              {/* Right Column: Photo & Booking */}
              <div className="lg:col-span-5 space-y-6">
                {/* Photo */}
                <div className="bg-[#f5efe9]/30 p-6 rounded-sm border border-[#817262]/30 shadow-xs">
                  <div className="w-full max-w-[320px] mx-auto overflow-hidden rounded-xl shadow-lg border border-stone-200/60 bg-stone-900 aspect-[9/16]">
                    <img 
                      src="/kundebilleder/Mette+som+underviser.jpg" 
                      alt={isEn ? "Mette Brudvig holding a keynote" : "Mette Brudvig holder foredrag"} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Booking & Contact Box */}
                <div className="bg-[#f5efe9]/30 p-8 md:p-10 border border-[#817262]/30 rounded-sm space-y-6 shadow-sm">
                  <div className="border-b border-[#817262]/20 pb-4">
                    <span className="text-xs uppercase tracking-widest font-bold text-[#817262] block mb-1">
                      {isEn ? "Booking & Inquiries" : "Booking & Forespørgsel"}
                    </span>
                    <h3 className="text-2xl font-serif text-stone-900">
                      {isEn ? "Keynote: Unleash Yourself" : "Foredrag: Frisæt dig"}
                    </h3>
                  </div>

                  <p className="text-sm text-stone-600 font-light leading-relaxed">
                    {isEn
                      ? "Would you like to book this keynote for your company, organization, or group, or wish to inquire about a tailored presentation?"
                      : "Ønsker du at booke dette foredrag til din virksomhed, organisation eller gruppe, eller vil du høre mere om muligheden for et skræddersyet oplæg?"}
                  </p>

                  <Link 
                    to={isEn ? "/en/contact?subject=Keynote: Unleash Yourself" : "/kontakt?subject=Foredrag: Frisæt dig - Har du sjælen med?"} 
                    className="block w-full text-center bg-[#817262] text-white py-4 uppercase text-xs tracking-widest font-bold hover:bg-stone-800 transition-colors rounded-sm shadow-sm"
                  >
                    {isEn ? "Contact me about the keynote" : "Kontakt mig om foredraget"}
                  </Link>

                  <div className="pt-2 text-center">
                    <Link 
                      to={isEn ? "/en/contact?subject=20-min-samtale" : "/kontakt?subject=20-min-samtale"} 
                      className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-[#817262] hover:underline"
                    >
                      <i className="fa-regular fa-comments text-xs" aria-hidden="true" />
                      <span>{isEn ? "Book free 20-min clarity call" : "Book en gratis 20-minutters samtale"}</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="grid grid-cols-1 md:grid-cols-2 bg-stone-900 text-stone-100">
        <div className="relative h-[50vh] md:h-auto min-h-[400px]">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/image_12.png" 
            alt="Frisæt dig" 
            className="absolute inset-0 w-full h-full object-cover opacity-80"
          />
          <div className="absolute bottom-8 left-8 z-10">
            <Link to={frisaetPath} className="text-sm uppercase tracking-widest font-bold underline underline-offset-4 hover:text-stone-300 transition-colors">
              {isEn ? "Back to Unleash Yourself →" : "Tilbage til Frisæt dig →"}
            </Link>
          </div>
        </div>
        <div className="flex flex-col justify-center px-8 md:px-16 lg:px-24 py-24">
          <FadeIn>
            <h3 className="text-3xl md:text-4xl font-serif leading-tight mb-8">
              {isEn ? "Are you ready to take the next step?" : "Er du klar til at tage det næste skridt?"}
            </h3>
            <h3 className="text-3xl md:text-4xl font-serif leading-tight mb-12 text-stone-400">
              {isEn
                ? "Whether you desire an inspiring keynote, an intensive workshop, or a deep mentoring journey, you are always welcome to reach out. Let us have an informal conversation about what fits you best."
                : "Uanset om du ønsker et inspirerende foredrag til dit netværk, en intensiv workshop eller et dybtgående forløb, er du altid velkommen til at række ud. Lad os tage en uforpligtende snak om, hvad der passer bedst til dig."}
            </h3>
            <div className="flex flex-wrap gap-4">
              <Link 
                to={isEn ? "/en/contact?subject=Keynote: Unleash Yourself" : "/kontakt?subject=Foredrag: Frisæt dig"} 
                className="inline-block bg-white text-stone-900 px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-stone-100 transition-all"
              >
                {isEn ? "Contact me now" : "Kontakt mig nu"}
              </Link>
              <Link 
                to={frisaetPath} 
                className="inline-block border border-stone-100 px-8 py-4 text-xs uppercase tracking-[0.2em] hover:bg-stone-100 hover:text-stone-900 transition-all duration-300"
              >
                {isEn ? "See Frisæt dig overview" : "Se Frisæt dig oversigten"}
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

const KvindeKendDinPowerPage = () => {
  const { lang, t } = useLanguage();
  const { kvindeKendDinPower } = useCms();
  const isEn = lang === 'en';

  const heroBadge = kvindeKendDinPower?.hero_badge?.[lang] || kvindeKendDinPower?.hero_badge?.da || (isEn ? "Program & Workshop" : "Forløb & Workshop");
  const heroTitle = kvindeKendDinPower?.hero_titel?.[lang] || kvindeKendDinPower?.hero_titel?.da || t('kvindePowerPage.title');
  const heroSubtitleKursiv = isEn ? "– Awaken your intuition, inner strength, and action power" : "– Væk din intuition, din indre styrke og din handlekraft";
  const heroSubtitle = kvindeKendDinPower?.hero_undertitel?.[lang] || kvindeKendDinPower?.hero_undertitel?.da || t('kvindePowerPage.sub');
  const heroImage = kvindeKendDinPower?.hero_billede?.[0] || "https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/image_8.png";

  const filosofiHtml = kvindeKendDinPower?.filosofi_tekst?.[lang] || kvindeKendDinPower?.filosofi_tekst?.da;
  const filosofiVideo = kvindeKendDinPower?.filosofi_video_url || "https://youtube.com/shorts/doG3oiw1VsM?feature=share";
  const citatTekst = kvindeKendDinPower?.citat_tekst?.[lang] || kvindeKendDinPower?.citat_tekst?.da || (isEn
    ? '"When women step into their true power, they do not only lift themselves – they help shape a new culture where strength and care go hand in hand. That is the kind of leadership our children need in their lives."'
    : '"Når kvinder træder ind i deres sande power, løfter de ikke kun sig selv – de er med til at skabe en ny kultur, hvor styrke og omsorg går hånd i hånd. Det er den form for lederskab, vores børn har brug for i deres liv."');
  const mellemBillede = kvindeKendDinPower?.mellem_billede?.[0] || "https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/mettebrudvigokt2021-136.jpg";
  const essensHtml = kvindeKendDinPower?.essens_tekst?.[lang] || kvindeKendDinPower?.essens_tekst?.da;
  const forloeb6mdrDatoerHtml = kvindeKendDinPower?.forloeb_6mdr_datoer?.[lang] || kvindeKendDinPower?.forloeb_6mdr_datoer?.da;
  const foredragPris = kvindeKendDinPower?.foredrag_pris?.[lang] || kvindeKendDinPower?.foredrag_pris?.da || (isEn ? "Associations: 7,500 DKK / Corporate: 9,500 DKK" : "Foreninger: 7.500 kr. / Virksomhed: 9.500 kr.");

  const ctaTitle = kvindeKendDinPower?.cta_titel?.[lang] || kvindeKendDinPower?.cta_titel?.da || (isEn ? "Ready to awaken your true power?" : "Er du klar til spirituel aktivering og dyb personlig udvikling?");
  const ctaText = kvindeKendDinPower?.cta_tekst?.[lang] || kvindeKendDinPower?.cta_tekst?.da || (isEn
    ? "Whether you seek an inspiring keynote for your organization, an intensive workshop, or a dedicated mentoring program, you are always welcome to reach out for an informal dialogue."
    : "Jeg arbejder med dig, der vil styrke forbindelsen til din sjæl, udvikle din intuition, heale det, der holder dig tilbage, og udfolde mere af det potentiale, du er her med. Gennem forløb, retreats, sessioner og undervisning hjælper jeg dig med at skabe reel transformation i dit liv.");

  useEffect(() => {
    document.title = isEn ? "Mette Brudvig - Woman - Know Your Power!" : "Mette Brudvig - Kvinde - kend din power";
    window.scrollTo(0, 0);
  }, [isEn]);

  return (
    <div className="bg-[#faf9f6] min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] pt-[185px] md:pt-[215px] lg:pt-[225px] pb-12 flex items-center bg-[#f5efe9] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-12 items-center z-10 w-full">
          <div className="md:col-span-7 space-y-6">
            <FadeIn direction="right">
              <h1 className="text-4xl md:text-6xl font-serif text-stone-900 leading-[1.1] mb-6">
                {heroTitle}
              </h1>
              <p className="text-xl md:text-2xl font-serif italic text-stone-800 leading-relaxed mb-6">
                {heroSubtitleKursiv}
              </p>
              <p className="text-base md:text-lg text-stone-700 font-light leading-relaxed max-w-xl">
                {heroSubtitle}
              </p>
            </FadeIn>
          </div>
        </div>
        <div className="absolute top-0 right-0 w-full md:w-1/2 h-full opacity-15 md:opacity-100 pt-20 md:pt-24 lg:pt-28 pointer-events-none">
          <img 
            src={heroImage} 
            alt="Mette Brudvig - Kvinde – kend din power!" 
            className="w-full h-full object-cover object-top pointer-events-none"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#f5efe9] via-transparent to-transparent hidden md:block pointer-events-none" />
        </div>
      </section>

      {/* Deep Dive Philosophy */}
      <section className="py-24 bg-white px-6 border-b border-stone-100">
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              <div className="lg:col-span-7 text-stone-700 font-light text-base md:text-lg leading-relaxed space-y-6">
                <div>
                  <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#817262] mb-3 block">
                    {isEn ? "The Philosophy" : "Filosofien"}
                  </span>
                  <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-6">
                    {isEn ? "New Era Power Woman" : "Ny tids powerkvinde"}
                  </h2>
                </div>
                {filosofiHtml ? (
                  <div 
                    className="prose prose-stone max-w-none font-light text-base md:text-lg leading-relaxed space-y-4 text-stone-700 [&_p]:mb-4"
                    dangerouslySetInnerHTML={{ __html: filosofiHtml }}
                  />
                ) : (
                  <>
                    <p>
                      {isEn
                        ? "'Woman – Know Your Power' is not an opposition to men, but an honoring that in the new era – the age of the heart – mature feminine energies are desperately needed in our world. And these are qualities men can and should mature within themselves as well, so together we can foster a safe, sustainable, and empathetic world."
                        : "Kvinde – kend din power er ikke et fjendskab mod mænd, men en anerkendelse af, at i den nye æra – hjertets tidsalder – er det de modne feminine energier, verden har brug for. Og dem kan og bør mænd også modne i sig selv, så vi sammen kan skabe en tryg, bæredygtig og empatisk verden."}
                    </p>
                    <p>
                      {isEn
                        ? "Mature feminine energy is not weak. On the contrary! It carries a natural overview, creates safety, unites people, and makes wise decisions with both heart and decisive power. These are precisely the qualities called for in modern leadership, career, family life, and society."
                        : "Den modne feminine energi er ikke svag. Tværtimod! Den rummer et naturligt overblik, skaber tryghed, samler mennesker og træffer kloge beslutninger med både hjerte og handlekraft. Og det er lige netop de kvaliteter, den nye tid kalder på i både ledelse, arbejdsliv, familieliv og samfund."}
                    </p>
                    <p>
                      {isEn
                        ? "In these workshops and mentoring programs, you learn to stand grounded in yourself, balance your feminine and masculine energies, and lead yourself and others with authenticity and ease rather than exhaustion."
                        : "I disse workshops og forløb lærer du at stå stærkt i dig selv, finde balancen mellem din feminine og maskuline energi og modne dem, så du kan leve og lede dig selv og andre med autenticitet og overskud frem for anstrengelse. Du styrker din intuition, din sjælsforbindelse og dit personlige lederskab."}
                    </p>
                  </>
                )}
              </div>

              <div className="lg:col-span-5 flex justify-center w-full">
                <YouTubeEmbed 
                  url={filosofiVideo} 
                  title={isEn ? "Intro to Woman - Know Your Power" : "Intro til Kvinde - kend din power"} 
                />
              </div>
            </div>

            {/* Quote */}
            <div className="my-16 p-8 md:p-12 bg-[#f5efe9]/40 border-l-4 border-[#817262] rounded-r-sm">
              <p className="text-xl md:text-2xl font-serif italic text-stone-800 leading-relaxed">
                {citatTekst}
              </p>
              <p className="text-xs uppercase tracking-widest font-bold text-stone-500 mt-4">— Mette Brudvig</p>
            </div>

            {/* Image under quote */}
            <div className="my-16 overflow-hidden rounded-sm border border-stone-100 shadow-md">
              <img 
                src={mellemBillede} 
                alt="Mette Brudvig - Kvinde, kend din power" 
                className="w-full h-auto object-cover"
              />
            </div>

            {/* Deep Dive Personal Story */}
            <div className="text-stone-700 font-light text-base md:text-lg leading-relaxed space-y-6 mt-12">
              <h3 className="text-2xl font-serif text-stone-900 mb-4">
                {isEn ? "Knowing Your True Essence" : "At kende din sande essens"}
              </h3>
              {essensHtml ? (
                <div 
                  className="prose prose-stone max-w-none font-light text-base md:text-lg leading-relaxed space-y-4 text-stone-700 [&_p]:mb-4 [&_em]:font-serif [&_em]:italic [&_em]:text-stone-900 [&_em]:text-xl [&_em]:block [&_em]:pt-4 [&_em]:border-t [&_em]:border-stone-100"
                  dangerouslySetInnerHTML={{ __html: essensHtml }}
                />
              ) : (
                <>
                  <p>
                    {isEn
                      ? "Knowing your true essence and power is your path to joy, courage, self-trust, and inner peace. Your genuine power consists of grounded, heart-centered calm, decisiveness, and heart intelligence."
                      : "At kende din sande essens og power er din vej til livsglæde, mod, tillid til dig selv og livet og din indre fred. Din ægte power består af din bomstærke, jordbundne og hjerteforbundede ro, handlekraft og hjertets intelligens. Og det er den, vi arbejder med her."}
                  </p>
                  <p>
                    {isEn
                      ? "It is about standing strong in yourself, sensing what feels right, saying yes when it is a yes, and no when it is a no. Acting when something calls you makes you not only a better leader or partner, but a deeply fulfilled human being."
                      : "Den handler om din evne til at stå stærkt i dig selv og kende din sande essens og leve fra den. At mærke, hvad der er rigtigt. At sige ja, når det er et ja, og nej, når det er et nej. At handle, når noget kalder på dig. Det gør dig ikke bare til en bedre mor, en bedre medarbejder, leder, partner og veninde. Det gør dig til et lykkeligere og mere selvkærligt menneske, der mærker sig selv, sit formål og sit potentiale."}
                  </p>
                  <p>
                    {isEn
                      ? "I was once labeled a 'power woman' when juggling executive corporate roles, family life with three young children, and political engagement. But I learned that doing more was not the expression of my true power."
                      : "Jeg er selv blevet kaldt powerkvinde, når jeg har været udholdende og ambitiøs og har jongleret høje stillinger med familielivet med tre små børn, et politisk engagement og et aktivt socialliv. Men jeg har lært, at det ikke er de ting, jeg gør, der gør mig til en ægte powerkvinde."}
                  </p>
                  <p>
                    {isEn
                      ? "True strength is activated when we feel ourselves and remain anchored in our soul essence. Here we know our authentic worth, not tied to performance, but to standing unwavering in courage, love, and peace."
                      : "At mine resultater, mit tempo og min evne til at gå den ekstra mil for at please andre ikke var udtryk for min sande styrke. Tværtimod. Det var et udtryk for, at jeg ikke var forbundet med den. For den sande styrke aktiverer vi, når vi kan mærke os selv og er forankret i vores sjælsessens. Her kender vi vores sande værdi, og den er ikke bundet op på vores performance og udholdenhed, men på vores evne til at stå urokkeligt i os selv og i mod og tillid og skabe fred i og omkring os fra et kærligt og værdigt sted."}
                  </p>
                  <p className="font-serif italic text-stone-900 text-xl pt-4 border-t border-stone-100">
                    {isEn
                      ? '"When you stand strong in your mature feminine energy, you become impossible to overlook – and impossible to sway. Your true power is intuition, courage, and creation in its purest form."'
                      : '"Når du står stærkt i din modne feminine kraft, bliver du umulig at overse – og umulig at flytte væk fra dig selv. Din sande power er intuition, mod og skaberkraft i sin reneste form. Du skal ikke være mindre eller hårdere. Du skal være hel."'}
                  </p>
                </>
              )}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Signs you are ready */}
      <section className="py-24 bg-[#f5efe9]/20 px-6">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <div className="text-center mb-16">
              <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#817262] mb-3 block">
                {lang === 'en' ? "Self-Reflection" : "Selvrefleksion"}
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
                {lang === 'en' ? "What Does It Mean to Know Your True Power?" : "Hvad vil det sige at kende sin sande power?"}
              </h2>
              <p className="text-stone-600 font-light text-sm md:text-base max-w-xl mx-auto mt-4">
                {lang === 'en'
                  ? "Knowing your true essence and power is your path to joy, courage, self-trust, and inner peace:"
                  : "At kende din sande essens og power er din vej til livsglæde, mod, tillid til dig selv og livet og din indre fred:"}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-8 border border-stone-100 rounded-sm shadow-sm flex gap-4">
                <div className="w-10 h-10 rounded-full bg-[#f5efe9] flex items-center justify-center text-[#817262] flex-shrink-0">
                  <i className="fa-light fa-heart" />
                </div>
                <div>
                  <h4 className="font-serif text-lg text-stone-900 mb-2">
                    {lang === 'en' ? "Heart Intelligence" : "Hjertets intelligens"}
                  </h4>
                  <p className="text-stone-600 font-light text-sm leading-relaxed">
                    {lang === 'en'
                      ? "Your genuine power consists of grounded, heart-centered calm, decisiveness, and heart intelligence. It is standing strong in yourself and knowing your true essence."
                      : "Din ægte power består af din bomstærke, jordbundne og hjerteforbundede ro, handlekraft og hjertets intelligens. Det handler om evnen til at stå stærkt i dig selv og kende din sande essens."}
                  </p>
                </div>
              </div>

              <div className="bg-white p-8 border border-stone-100 rounded-sm shadow-sm flex gap-4">
                <div className="w-10 h-10 rounded-full bg-[#f5efe9] flex items-center justify-center text-[#817262] flex-shrink-0">
                  <i className="fa-light fa-shield-heart" />
                </div>
                <div>
                  <h4 className="font-serif text-lg text-stone-900 mb-2">
                    {lang === 'en' ? "Clear Boundaries" : "Tydelige grænser"}
                  </h4>
                  <p className="text-stone-600 font-light text-sm leading-relaxed">
                    {lang === 'en'
                      ? "Sensing what feels right. Saying yes when it is a yes, and no when it is a no. Taking action when something calls on you."
                      : "At mærke, hvad der er rigtigt. At sige ja, når det er et ja, og nej, når det er et nej. At handle, når noget kalder på dig."}
                  </p>
                </div>
              </div>

              <div className="bg-white p-8 border border-stone-100 rounded-sm shadow-sm flex gap-4">
                <div className="w-10 h-10 rounded-full bg-[#f5efe9] flex items-center justify-center text-[#817262] flex-shrink-0">
                  <i className="fa-light fa-feather" />
                </div>
                <div>
                  <h4 className="font-serif text-lg text-stone-900 mb-2">
                    {lang === 'en' ? "Releasing Performance" : "Slippe performance"}
                  </h4>
                  <p className="text-stone-600 font-light text-sm leading-relaxed">
                    {lang === 'en'
                      ? "True strength is activated when we feel ourselves and remain anchored in our soul essence. Your value is not tied to performance, pace, or endurance."
                      : "Sand styrke aktiveres, når vi kan mærke os selv og er forankret i vores sjælsessens. Din værdi er ikke bundet op på din performance, tempo eller udholdenhed."}
                  </p>
                </div>
              </div>

              <div className="bg-white p-8 border border-stone-100 rounded-sm shadow-sm flex gap-4">
                <div className="w-10 h-10 rounded-full bg-[#f5efe9] flex items-center justify-center text-[#817262] flex-shrink-0">
                  <i className="fa-light fa-sparkles" />
                </div>
                <div>
                  <h4 className="font-serif text-lg text-stone-900 mb-2">
                    {lang === 'en' ? "Inspiration for Others" : "Inspiration for andre"}
                  </h4>
                  <p className="text-stone-600 font-light text-sm leading-relaxed">
                    {lang === 'en'
                      ? "When you stand strong in your mature feminine energy, you become impossible to overlook – and impossible to sway. You become the woman, leader, and inspiration the world needs."
                      : "Når du står stærkt i din modne feminine kraft, bliver du umulig at overse – og umulig at flytte væk fra dig selv. Du bliver den kvinde, leder og inspiration, verden har brug for."}
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* De Fire Døre Ind */}
      <section className="py-24 bg-white px-6">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center max-w-3xl mx-auto mb-20">
              <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
                {lang === 'en' ? "Choose Your Door to Transformation" : "Vælg din dør ind til transformationen"}
              </h2>
              <p className="text-stone-600 font-light text-sm md:text-base mt-4">
                {lang === 'en'
                  ? "Wherever you are on your journey, I offer four different ways to work with the theme 'Woman - Know Your Power':"
                  : "Uanset hvor du er på din rejse, tilbyder jeg fire forskellige måder at arbejde med temaet \"Kvinde - kend din power\" på:"}
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Pathway 1: 6 Måneders Udviklingsforløb */}
              <div className="bg-stone-50 p-8 border border-stone-200/60 rounded-sm flex flex-col justify-between shadow-sm hover:shadow-md transition-all">
                <div className="space-y-6">
                  <div className="flex justify-end items-start">
                    <i className="fa-light fa-gem text-[#817262] text-xl" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start mb-2">
                    <div className="col-span-12 md:col-span-6">
                      <h3 className="text-2xl font-serif text-stone-900 mb-1">
                        {lang === 'en' ? "6-Month Journey" : "6-måneders forløb"}
                      </h3>
                      <p className="text-xs italic text-stone-500 mb-4">
                        {lang === 'en'
                          ? "For you who wish to activate your true essence and align with your authentic strength."
                          : "For dig, der vil aktivere den sande essens i dig og forbinde dig med din ægte styrke."}
                      </p>
                      <p className="text-stone-600 font-light text-sm leading-relaxed">
                        {lang === 'en'
                          ? "This development journey is for you who are ready to find home to yourself and activate your passion and true power. You connect with yourself to create a life filled with courage, trust, and decisiveness, guided by a clear inner compass."
                          : "Dette udviklingsforløb er for dig, der er klar til at finde hjem til dig selv og aktivere din passion og din sande power. Du forbinder dig med dig selv, så du skaber et liv med mod, tillid og handlekraft, fordi dit indre kompas er tydeligt, og du mærker dig selv, og hvad det er, du ønsker."}
                      </p>
                    </div>
                    <div className="col-span-12 md:col-span-6 flex justify-center md:justify-end">
                      <YouTubeEmbed 
                        url="https://youtube.com/shorts/zPKIvjYkClY?feature=share" 
                        title={lang === 'en' ? "6-month journey" : "6-måneders forløb"} 
                        className="w-full max-w-[220px]"
                      />
                    </div>
                  </div>
                  <div className="space-y-3 pt-2">
                    <h5 className="text-xs uppercase tracking-wider font-bold text-stone-900">
                      {lang === 'en' ? "What you get:" : "Det får du:"}
                    </h5>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                      {(lang === 'en' ? [
                        "Understanding of mature feminine power",
                        "Stronger connection to your intuition",
                        "Maturation of your feminine strength",
                        "Greater access to softness & presence",
                        "More courage, strength, and action",
                        "Working through limiting patterns",
                        "Setting clear, drama-free boundaries",
                        "Standing authentically in relationships",
                        "Meditations, exercises, and tools",
                        "A strong community of women"
                      ] : [
                        "Forståelse for den modne feminine kraft",
                        "En stærkere kontakt til din intuition",
                        "Modning af din feminine kraft",
                        "Større adgang til din blødhed & nærvær",
                        "Mere mod, styrke og handlekraft",
                        "Arbejdet med dine begrænsende mønstre",
                        "Evnen til at sætte grænser udramatisk",
                        "Træning i at stå tydeligt i relationer",
                        "Meditationer, øvelser og redskaber",
                        "Et stærkt fællesskab med kvinder"
                      ]).map((bullet, bIdx) => (
                        <li key={bIdx} className="flex items-start text-xs text-stone-600 font-light">
                          <i className="fa-light fa-sparkles text-[#817262] mt-0.5 mr-2 flex-shrink-0" />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-white p-4 border border-stone-100 rounded-sm space-y-3">
                    <p className="text-xs text-stone-700 font-light leading-relaxed">
                      <strong className="font-serif italic text-stone-900 block mb-1">
                        {lang === 'en' ? "Your benefits:" : "Dit udbytte:"}
                      </strong>
                      {lang === 'en'
                        ? "You become less dependent on others' judgments and more secure in your own choices. You feel faster what is right, what drains you, and what needs space. You stand stronger in your relationships, career, and personal leadership."
                        : "Du bliver mindre afhængig af andres vurderinger og mere sikker i dine egne valg. Du kan mærke hurtigere, hvad der er rigtigt, hvad der dræner dig, og hvad der skal have mere plads. Du står stærkere i dine relationer, i dit arbejdsliv og i dit personlige lederskab."}
                    </p>
                    <div className="pt-3 border-t border-stone-100">
                      <span className="text-[10px] uppercase tracking-widest font-bold text-[#817262] block mb-2">
                        {isEn ? "Dates in Copenhagen 2027:" : "Datoer i København 2027:"}
                      </span>
                      {forloeb6mdrDatoerHtml ? (
                        <div 
                          className="text-xs text-stone-800 font-medium space-y-1.5 [&_ul]:space-y-1.5 [&_li]:flex [&_li]:items-center [&_li]:gap-2"
                          dangerouslySetInnerHTML={{ __html: forloeb6mdrDatoerHtml }}
                        />
                      ) : (
                        <ul className="text-xs text-stone-800 font-medium space-y-1.5 divide-y divide-stone-100">
                          {(isEn ? [
                            "Wednesday, March 10 from 13:00 to 18:00 - In-person teaching",
                            "Wednesday, April 14 from 13:00 to 18:00 - In-person teaching",
                            "Wednesday, May 19 from 13:00 to 18:00 - In-person teaching",
                            "Wednesday, June 16 from 13:00 to 18:00 - In-person teaching",
                            "Wednesday, August 25 from 13:00 to 18:00 - In-person teaching",
                            "Wednesday, September 22 from 13:00 to 18:00 - In-person teaching"
                          ] : [
                            "Onsdag den 10. marts kl. 13.00–18.00",
                            "Onsdag den 14. april kl. 13.00–18.00",
                            "Onsdag den 19. maj kl. 13.00–18.00",
                            "Onsdag den 16. juni kl. 13.00–18.00",
                            "Onsdag den 25. august kl. 13.00–18.00",
                            "Onsdag den 22. september kl. 13.00–18.00"
                          ]).map((dateStr, dIdx) => (
                            <li key={dIdx} className={`${dIdx > 0 ? 'pt-1.5' : 'pt-1'} flex items-center gap-2`}>
                              <i className="fa-regular fa-calendar-days text-[#817262] text-xs shrink-0" aria-hidden="true" />
                              <span>{dateStr}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
                <div className="pt-8 mt-8 border-t border-stone-200/60 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <span className="text-[9px] uppercase tracking-widest text-stone-400 block font-bold">
                      {lang === 'en' ? "Registration & Dates" : "Tilmeld & Datoer"}
                    </span>
                    <span className="text-xs font-serif font-medium text-stone-700">
                      {lang === 'en' ? "Contact Mette for upcoming dates and registration" : "Kontakt Mette for kommende datoer og tilmelding"}
                    </span>
                  </div>
                  <a 
                    href="mailto:mette@mettebrudvig.dk?subject=Tilmelding%20og%20datoer:%206-m%C3%A5neders%20forl%C3%B8b" 
                    className="block w-full sm:w-auto text-center bg-[#817262] text-white px-6 py-3.5 uppercase text-[10px] tracking-widest font-bold hover:bg-stone-800 transition-colors rounded-sm shrink-0"
                  >
                    {lang === 'en' ? "Write to Mette" : "Skriv til Mette"}
                  </a>
                </div>
              </div>

              {/* Pathway 2: Weekendworkshop */}
              <div className="bg-stone-50 p-8 border border-stone-200/60 rounded-sm flex flex-col justify-between shadow-sm hover:shadow-md transition-all">
                <div className="space-y-6">
                  <div className="flex justify-end items-start">
                    <i className="fa-light fa-calendar-star text-[#817262] text-xl" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start mb-2">
                    <div className="col-span-12 md:col-span-6">
                      <h3 className="text-2xl font-serif text-stone-900 mb-1">
                        {lang === 'en' ? "Weekend Workshop" : "Weekendworkshop"}
                      </h3>
                      <p className="text-xs italic text-stone-500 mb-4">
                        {lang === 'en'
                          ? "For you who wish to connect with your true essence and align with authentic strength."
                          : "For dig, der vil i kontakt med den sande essens i dig og forbinde dig med din ægte styrke."}
                      </p>
                      <p className="text-stone-600 font-light text-sm leading-relaxed">
                        {lang === 'en'
                          ? "Many women play small because they are disconnected from their authentic strength. In this workshop, you go deep to your core, feeling your passion, presence, and true power."
                          : "Mange kvinder gør sig selv mindre, end de er, fordi de ikke er forbundet med deres sande styrke. På denne workshop arbejder du dig ind til din kerne, hvor du mærker dig selv, din passion og din sande styrke."}
                      </p>
                    </div>
                    <div className="col-span-12 md:col-span-6 flex justify-center md:justify-end">
                      <YouTubeEmbed 
                        url="https://youtube.com/shorts/qY9py6vzS2w?feature=share" 
                        title={lang === 'en' ? "Weekend workshop" : "Weekendworkshop"} 
                        className="w-full max-w-[220px]"
                      />
                    </div>
                  </div>
                  <div className="space-y-3 pt-2">
                    <h5 className="text-xs uppercase tracking-wider font-bold text-stone-900">
                      {lang === 'en' ? "What you get:" : "Det får du:"}
                    </h5>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                      {(lang === 'en' ? [
                        "Understanding of mature feminine power",
                        "Stronger contact with your intuition",
                        "Maturation of your feminine energy",
                        "Greater access to softness & presence",
                        "Tools to reclaim your energy",
                        "Meditations, exercises, and practices"
                      ] : [
                        "Forståelse for den modne feminine kraft",
                        "En stærkere kontakt til din intuition",
                        "Modning af din feminine kraft",
                        "Større adgang til din blødhed & nærvær",
                        "Redskaber til at tage din energi hjem",
                        "Meditationer, øvelser og redskaber"
                      ]).map((bullet, bIdx) => (
                        <li key={bIdx} className="flex items-start text-xs text-stone-600 font-light">
                          <i className="fa-light fa-sparkles text-[#817262] mt-0.5 mr-2 flex-shrink-0" />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-white p-4 border border-stone-100 rounded-sm">
                    <p className="text-xs text-stone-700 font-light leading-relaxed">
                      <strong className="font-serif italic text-stone-900 block mb-1">
                        {lang === 'en' ? "Your benefits:" : "Dit udbytte:"}
                      </strong>
                      {lang === 'en'
                        ? "You return home with a deeper connection to yourself and a clearer picture of your strength and how you wish to embody it."
                        : "Du går hjem med en stærkere forbindelse til dig selv og et mere præcist billede af, hvad din styrke er, og hvordan du ønsker at bruge den."}
                    </p>
                  </div>
                </div>
                <div className="pt-8 mt-8 border-t border-stone-200/60 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <span className="text-[9px] uppercase tracking-widest text-stone-400 block font-bold">
                      {lang === 'en' ? "Registration & Dates" : "Tilmeld & Datoer"}
                    </span>
                    <span className="text-xs font-serif font-medium text-stone-700">
                      {lang === 'en' ? "Contact Mette for dates and registration" : "Kontakt Mette for datoer og tilmelding"}
                    </span>
                  </div>
                  <a 
                    href="mailto:mette@mettebrudvig.dk?subject=Tilmelding%20og%20datoer:%20Weekendworkshop" 
                    className="block w-full sm:w-auto text-center bg-[#817262] text-white px-6 py-3.5 uppercase text-[10px] tracking-widest font-bold hover:bg-stone-800 transition-colors rounded-sm shrink-0"
                  >
                    {lang === 'en' ? "Write to Mette" : "Skriv til Mette"}
                  </a>
                </div>
              </div>

              {/* Pathway 3: 1-Dagsworkshop */}
              <div className="bg-stone-50 p-8 border border-stone-200/60 rounded-sm flex flex-col justify-between shadow-sm hover:shadow-md transition-all">
                <div className="space-y-6">
                  <div className="flex justify-end items-start">
                    <i className="fa-light fa-[#817262] fa-users text-xl" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-serif text-stone-900 mb-1">
                      {lang === 'en' ? "1-Day Workshop" : "1-dagsworkshop"}
                    </h3>
                    <p className="text-xs italic text-stone-500 mb-4">
                      {lang === 'en'
                        ? "An intensive day reconnecting you with your intuition, inner strength, and direction."
                        : "En intensiv dag, hvor du genfinder forbindelsen til din intuition, din styrke og din retning."}
                    </p>
                    <p className="text-stone-600 font-light text-sm leading-relaxed">
                      {lang === 'en'
                        ? "This workshop is for you who are tired of doubting what you already sense. In one day, gain clarity on where you lose your power and how to reclaim it."
                        : "Denne workshop er for dig, der er træt af at tvivle på det, du allerede mærker. På én dag får du et klart billede af, hvor du mister din kraft, og hvad der skal til for at tage den hjem igen."}
                    </p>
                  </div>
                  <div className="space-y-3 pt-2">
                    <h5 className="text-xs uppercase tracking-wider font-bold text-stone-900">
                      {lang === 'en' ? "What you get:" : "Det får du:"}
                    </h5>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                      {(lang === 'en' ? [
                        "Understanding of mature feminine power",
                        "Stronger contact with your intuition",
                        "Stronger connection to your authentic power & passion",
                        "Meditations and exercises to ground yourself"
                      ] : [
                        "Forståelse for den modne feminine kraft",
                        "En stærkere kontakt til din intuition",
                        "En stærkere kontakt til din sande styrke & passion",
                        "Meditation og øvelser, der hjælper dig med at mærke dig selv"
                      ]).map((bullet, bIdx) => (
                        <li key={bIdx} className="flex items-start text-xs text-stone-600 font-light">
                          <i className="fa-light fa-sparkles text-[#817262] mt-0.5 mr-2 flex-shrink-0" />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-white p-4 border border-stone-100 rounded-sm">
                    <p className="text-xs text-stone-700 font-light leading-relaxed">
                      <strong className="font-serif italic text-stone-900 block mb-1">
                        {lang === 'en' ? "Your benefits:" : "Dit udbytte:"}
                      </strong>
                      {lang === 'en'
                        ? "You leave the workshop with greater clarity, courage, and a strong connection to what you already know deep inside."
                        : "Du går fra workshoppen med større klarhed, mere mod og en stærkere forbindelse til det, du allerede ved."}
                    </p>
                  </div>
                </div>
                <div className="pt-8 mt-8 border-t border-stone-200/60 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <span className="text-[9px] uppercase tracking-widest text-stone-400 block font-bold">
                      {lang === 'en' ? "Registration & Dates" : "Tilmeld & Datoer"}
                    </span>
                    <span className="text-xs font-serif font-medium text-stone-700">
                      {lang === 'en' ? "Contact Mette for dates and registration" : "Kontakt Mette for datoer og tilmelding"}
                    </span>
                  </div>
                  <a 
                    href="mailto:mette@mettebrudvig.dk?subject=Tilmelding%20og%20datoer:%201-dagsworkshop" 
                    className="block w-full sm:w-auto text-center bg-[#817262] text-white px-6 py-3.5 uppercase text-[10px] tracking-widest font-bold hover:bg-stone-800 transition-colors rounded-sm shrink-0"
                  >
                    {lang === 'en' ? "Write to Mette" : "Skriv til Mette"}
                  </a>
                </div>
              </div>

              {/* Pathway 4: Foredrag */}
              <div className="bg-stone-50 p-8 border border-stone-200/60 rounded-sm flex flex-col justify-between shadow-sm hover:shadow-md transition-all">
                <div className="space-y-6">
                  <div className="flex justify-end items-start">
                    <i className="fa-light fa-microphone text-[#817262] text-xl" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-serif text-stone-900 mb-1">
                      {lang === 'en' ? "Keynote" : "Foredrag"}
                    </h3>
                    <p className="text-xs italic text-stone-500 mb-4">
                      {lang === 'en'
                        ? "On the strength that emerges when a woman feels herself and acts upon it."
                        : "Om den styrke, der opstår, når en kvinde kan mærke selv og handle på det."}
                    </p>
                    <p className="text-stone-600 font-light text-sm leading-relaxed">
                      {lang === 'en'
                        ? "What happens when a woman stops measuring her worth solely by performance? When she leads with intuition, emotional intelligence, and genuine strength? This talk inspires a new perspective on female empowerment and leadership."
                        : "Hvad sker der, når en kvinde holder op med at måle sin værdi på sine resultater? Når hun ikke længere forsøger at være stærk ved at bære mere, holde ud længere eller gøre sig mindre besværlig? Når hun i stedet bruger sin intuition, sin følelsesmæssige intelligens og sin handlekraft som reelle kompetencer?"}
                    </p>
                  </div>
                  <div className="space-y-3 pt-2">
                    <h5 className="text-xs uppercase tracking-wider font-bold text-stone-900">
                      {lang === 'en' ? "Takeaways:" : "Publikum får:"}
                    </h5>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                      {(lang === 'en' ? [
                        "Masculine vs. feminine strength dynamics",
                        "Deep understanding of mature feminine power",
                        "Restoring connection to intuition",
                        "Perspectives on soft strength and courage",
                        "Practical reflections for professional and personal life"
                      ] : [
                        "Forståelse for maskulin vs. feminin styrke",
                        "Forståelse for den modne feminine kraft",
                        "Indsigt i tabet af intuition",
                        "Perspektiver på blødhed og mod",
                        "Konkrete refleksioner til arbejdsliv & privatliv"
                      ]).map((bullet, bIdx) => (
                        <li key={bIdx} className="flex items-start text-xs text-stone-600 font-light">
                          <i className="fa-light fa-sparkles text-[#817262] mt-0.5 mr-2 flex-shrink-0" />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-white p-4 border border-stone-100 rounded-sm">
                    <p className="text-xs text-stone-700 font-light leading-relaxed">
                      <strong className="font-serif italic text-stone-900 block mb-1">
                        {lang === 'en' ? "Your benefits:" : "Dit udbytte:"}
                      </strong>
                      {lang === 'en'
                        ? "A profound understanding of your inherent power and insight into the shift our era calls for."
                        : "En stærkere forståelse af din egen kraft og et billede af, hvad det er for en tid, vi står over for."}
                    </p>
                  </div>
                </div>
                <div className="pt-8 mt-8 border-t border-stone-200/60 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <span className="text-[9px] uppercase tracking-widest text-stone-400 block font-bold">
                      {isEn ? "Price" : "Pris"}
                    </span>
                    <span className="text-sm font-serif font-semibold text-stone-900">
                      {foredragPris}
                    </span>
                  </div>
                  <a 
                    href="mailto:mette@mettebrudvig.dk?subject=Booking:%20Keynote%20Woman%20know%20your%20power" 
                    className="block w-full sm:w-auto text-center bg-[#817262] text-white px-6 py-3.5 uppercase text-[10px] tracking-widest font-bold hover:bg-stone-800 transition-colors rounded-sm shrink-0"
                  >
                    {isEn ? "Inquire about keynote" : "Kontakt om foredrag"}
                  </a>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

const ForedragPage = () => {
  const { lang } = useLanguage();
  const isEn = lang === 'en';
  const { foredrag } = useCms();
  const getCmsText = (field, fallback) => {
    if (!field) return fallback;
    if (typeof field === 'object') {
      return (isEn ? field.en : field.da) || field.da || field.en || fallback;
    }
    return field || fallback;
  };

  const titel = getCmsText(foredrag?.titel, isEn ? "My spiritual journey – the road home to myself" : "Min spirituelle rejse – vejen hjem til mig selv");
  const undertitel = getCmsText(foredrag?.undertitel, isEn 
    ? "Intimate keynote with only 25 seats · A personal and moving talk about waking up as an exhausted mother and career woman, meeting guides and miracles, and finding home in true essence."
    : "Intimt foredrag med kun 25 pladser · Et personligt og bevægende foredrag om at vågne op, finde hjem til sig selv og forstå livets mirakler og formål.");

  const dato = getCmsText(foredrag?.dato, isEn ? "November 1st" : "1. november");
  const stedNavn = getCmsText(foredrag?.sted_navn, isEn ? "Halo House, Central Copenhagen" : "Halo House, Indre København");
  const stedAdresse = getCmsText(foredrag?.sted_adresse, isEn ? "Store Regnegade 12, 4. sal, 1110 København K" : "Store Regnegade 12, 4. sal, 1110 København K");
  const pris = getCmsText(foredrag?.pris, isEn ? "160 kr." : "160 kr.");
  const prisNote = getCmsText(foredrag?.pris_note, isEn ? "per ticket (incl. VAT)" : "pr. billet (inkl. moms)");

  const slot1Titel = getCmsText(foredrag?.slot_1_titel, isEn ? "10:00 – 12:00" : "Kl. 10.00 – 12.00");
  const slot1Badge = getCmsText(foredrag?.slot_1_badge, isEn ? "Morning" : "Formiddag");
  const slot1Link = foredrag?.slot_1_link || "https://pay.lumant.ai/kurv/vbOi-foredrag";

  const slot2Titel = getCmsText(foredrag?.slot_2_titel, isEn ? "14:00 – 16:00" : "Kl. 14.00 – 16.00");
  const slot2Badge = getCmsText(foredrag?.slot_2_badge, isEn ? "Afternoon" : "Eftermiddag");
  const slot2Link = foredrag?.slot_2_link || "https://pay.lumant.ai/kurv/8102xrDhIfoG70oCYBvZS";

  const fremhaevetCitat = getCmsText(foredrag?.fremhaevet_citat, isEn
    ? "It is the story of losing oneself and finding home again – of life’s unexpected turns, the experiences that transform us, and how even the most challenging moments can mark the beginning of a whole new direction in life."
    : "Det er fortællingen om at miste sig selv og finde hjem igen – om livets uventede drejninger, de oplevelser, der forandrer os, og om, hvordan selv de mest udfordrende øjeblikke kan blive begyndelsen på en helt ny retning i livet.");

  const heroBilledeUrl = foredrag?.hero_billede?.[0] || foredrag?.billede?.[0] || "https://assets.auxodev.dk/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/billede-til-merete.jpg";
  const billedeUrl = foredrag?.billede?.[0] || "https://assets.auxodev.dk/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/billede-til-merete.jpg";
  const billedeTekst = getCmsText(foredrag?.billede_tekst, isEn ? "Author, Clairvoyant & Speaker" : "Forfatter, clairvoyant rådgiver, podcaster & foredragsholder");

  const telefon = foredrag?.spoergsmaal_telefon || "27 50 70 83";
  const foredragstekstHtml = getCmsText(foredrag?.foredragstekst, '');
  const videoUrl = foredrag?.video_url || "https://youtu.be/O-pq2XvMB94?si=CWeIb-ewREvYPyDw";

  const slots = {
    morning: {
      id: 'morning',
      timeLabel: slot1Titel,
      badge: dato,
      url: slot1Link,
      date: dato,
      shortTime: slot1Titel
    },
    afternoon: {
      id: 'afternoon',
      timeLabel: slot2Titel,
      badge: dato,
      url: slot2Link,
      date: dato,
      shortTime: slot2Titel
    }
  };

  useEffect(() => {
    document.title = isEn 
      ? `Mette Brudvig - ${titel} | Keynote` 
      : `Mette Brudvig - ${titel} | Foredrag`;
    window.scrollTo(0, 0);
  }, [isEn, titel]);

  return (
    <div className="bg-[#faf9f6] min-h-screen">
      {/* Subpage Header / Hero */}
      <section className="relative min-h-[65vh] md:min-h-[75vh] pt-[185px] md:pt-[215px] lg:pt-[225px] pb-16 bg-[#f5efe9] border-b border-stone-200/60 overflow-hidden flex items-center">
        <div className="max-w-7xl mx-auto px-6 z-10 relative w-full">
          <div className="max-w-2xl">
            <FadeIn>
              <h1 className="text-3xl md:text-5xl font-serif text-stone-900 mb-3">
                {titel}
              </h1>
              <p className="text-[#817262] font-medium text-lg md:text-xl italic max-w-2xl leading-relaxed mb-6">
                {undertitel}
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <a 
                  href="#booking" 
                  className="bg-[#004f3b] text-white px-7 py-3.5 text-xs uppercase tracking-[0.2em] font-bold hover:bg-emerald-900 transition-all rounded-sm shadow-sm"
                >
                  {isEn ? "See Times & Book" : "Se tidspunkter og tilmelding"}
                </a>
                <Link 
                  to={isEn ? "/en/about-mette" : "/om-mette"} 
                  className="inline-flex items-center gap-2 bg-white border border-[#817262] text-[#817262] hover:bg-[#817262] hover:text-white px-7 py-3.5 text-xs uppercase tracking-[0.2em] font-bold transition-all rounded-sm shadow-sm"
                >
                  <span>{isEn ? "About Me" : "Om mig"}</span>
                  <i className="fa-solid fa-arrow-right text-xs" />
                </Link>
              </div>
            </FadeIn>
          </div>
        </div>
        <div className="absolute top-0 right-0 w-full md:w-1/2 h-full opacity-15 md:opacity-100 pt-20 md:pt-24 lg:pt-28 pointer-events-none">
          <img 
            src={imageUrl(heroBilledeUrl, 'width=1200,quality=85,format=auto')} 
            alt={isEn ? `Mette Brudvig - ${titel}` : `Mette Brudvig - ${titel}`}
            className="w-full h-full object-cover object-center pointer-events-none"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#f5efe9] via-transparent to-transparent hidden md:block pointer-events-none" />
        </div>
      </section>

      {/* Main Content & Booking Section */}
      <section id="booking" className="py-16 md:py-24 px-6 bg-white border-b border-stone-100">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {/* Left Column: Authentic Foredrag Text */}
          <div className="lg:col-span-7 space-y-8">
            <FadeIn>
              <div className="space-y-3">
                <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#817262] block">
                  {isEn ? "About the Keynote" : "Om foredraget"}
                </span>
                <div className="h-0.5 w-16 bg-[#817262]/60 mt-3 mb-6" />
              </div>

              {/* Exact Text from Mette Brudvig / CMS */}
              {foredragstekstHtml ? (
                <div 
                  className="prose prose-stone max-w-none text-stone-700 font-light text-base md:text-lg leading-relaxed space-y-5 [&>p]:mb-5"
                  dangerouslySetInnerHTML={{ __html: foredragstekstHtml }}
                />
              ) : (
                <div className="prose prose-stone max-w-none text-stone-700 font-light text-base md:text-lg leading-relaxed space-y-5">
                  <p>
                    {isEn 
                      ? "In this intimate keynote with only 25 seats, I share my spiritual journey and how, as an exhausted mother and career woman, I awakened to find myself."
                      : "I dette intime foredrag med kun 25 pladser fortæller jeg om min spirituelle rejse, og hvordan jeg som udbrændt familiemor og karrierekvinde vågnede op til at finde mig selv."}
                  </p>
                  <p>
                    {isEn
                      ? "It became a journey filled with experiences that were difficult, painful, beautiful, and miraculous. From encounters with angels and spiritual guides to studying clairvoyance and developing a growing sense of my purpose and the role I am here to play in the world. Among other things, I was guided to the mountain of Montserrat, where I experienced divine miracles with Mother Mary."
                      : "Det blev en rejse fyldt med oplevelser, der både var svære, pinefulde, smukke og mirakuløse. Fra møder med engle og guider til en clairvoyanceuddannelse og en voksende følelse af at forstå mit formål og den rolle, jeg skal spille i verden. Jeg blev blandt andet ført til bjerget Montserrat, hvor jeg oplevede guddommelige mirakler med Jomfru Maria."}
                  </p>
                  <p>
                    {isEn
                      ? "Almost magically, my family and I were brought to Spain, where we lived for half a year. A loss occurred there that would prove crucial for my path forward and for how I view the world, life, and collective evolution."
                      : "Nærmest på magisk vis blev min familie og jeg ført til Spanien, hvor vi levede i et halvt år. Et dødsfald, der skulle få afgørende betydning for min videre vej og for mit syn på verden, livet og verdens udvikling."}
                  </p>
                  <p>
                    {isEn
                      ? "Today, I live a life passionately dedicated to contributing to creating peace within people and in the world through my podcast, 1:1 sessions, educations, courses, keynotes, books, and public speaking."
                      : "I dag lever jeg et liv, hvor jeg passioneret er drevet af at bidrage til at skabe fred i mennesker og i verden gennem min podcast, sessioner, uddannelser, kurser, foredrag, bøger og formidling."}
                  </p>
                </div>
              )}

              {/* Fremhævet citat */}
              {fremhaevetCitat && (
                <div className="bg-[#f5efe9]/70 border-l-4 border-[#817262] p-6 sm:p-7 rounded-r-sm my-6">
                  <p className="font-serif italic text-stone-900 text-lg md:text-xl leading-relaxed m-0">
                    {fremhaevetCitat}
                  </p>
                </div>
              )}

              {/* Photo of Mette */}
              <div className="pt-4">
                <div className="relative rounded-sm overflow-hidden shadow-md border border-stone-200/80 bg-stone-100">
                  <img 
                    src={imageUrl(billedeUrl, 'width=1000,quality=85,format=auto')} 
                    alt={`Mette Brudvig - ${titel}`} 
                    className="w-full h-80 sm:h-96 object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-950/70 via-transparent to-transparent flex items-end p-6">
                    <div className="text-white">
                      <p className="font-serif text-xl sm:text-2xl font-medium">Mette Brudvig</p>
                      <p className="text-xs uppercase tracking-widest text-[#f5efe9]/90 font-light">
                        {billedeTekst}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>

          {/* Right Column: Time Slot Selection & Booking Box */}
          <div className="lg:col-span-5 lg:sticky lg:top-28 space-y-6">
            <FadeIn delay={150} className="space-y-6">
              {/* Intro Video */}
              <YouTubeEmbed 
                url={videoUrl} 
                title={isEn ? `Intro to ${titel}` : `Intro til ${titel}`} 
              />

              <div id="booking" className="bg-[#f5efe9] border border-[#817262]/30 rounded-sm p-6 sm:p-8 shadow-md space-y-6">
                
                {/* Header */}
                <div>
                  <h3 className="text-2xl font-serif text-stone-900">
                    {isEn ? "Book ticket" : "Book billet"}
                  </h3>
                </div>

                {/* Booking Product */}
                <div className="space-y-4">
                  {/* Eftermiddag */}
                  <div className="bg-white p-5 rounded-sm border border-stone-200/90 shadow-2xs space-y-3.5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h4 className="text-lg font-serif font-bold text-stone-900">
                          {slot2Titel}
                        </h4>
                        <p className="text-xs text-stone-500 mt-0.5">
                          {dato} · {stedNavn}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <span className="text-base font-serif font-bold text-stone-900 block">{pris}</span>
                        <span className="text-[10px] text-stone-500">{prisNote}</span>
                      </div>
                    </div>

                    <a
                      href={slots.afternoon.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center justify-center gap-2.5 bg-[#817262] hover:bg-[#6e6153] text-white px-5 py-3.5 uppercase text-xs tracking-[0.2em] font-bold rounded-sm shadow-xs hover:shadow-md transition-all text-center group cursor-pointer"
                    >
                      <span>{isEn ? `Book ticket · ${slot2Titel}` : `Køb billet · ${slot2Titel}`}</span>
                      <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform" />
                    </a>
                  </div>
                </div>

                {/* Footer notes */}
                <div className="space-y-3 pt-1">
                  <p className="text-[11px] text-stone-500 text-center font-light leading-relaxed">
                    <i className="fa-solid fa-lock text-[10px] mr-1 text-[#817262]" />
                    {isEn 
                      ? "Secure payment powered by Stripe. You will receive an immediate order confirmation."
                      : "Sikker betaling via Stripe. Du modtager din bekræftelse og kvittering med det samme."}
                  </p>

                  {/* Contact Questions Callout */}
                  <div className="pt-3 border-t border-[#817262]/15 text-xs text-stone-600 flex items-center justify-between">
                    <span>{isEn ? "Questions?" : "Spørgsmål til foredraget?"}</span>
                    <Link 
                      to={isEn ? "/en/contact?subject=Keynote" : "/kontakt?subject=Foredrag"} 
                      className="font-medium text-[#817262] hover:underline flex items-center gap-1.5"
                    >
                      <i className="fa-regular fa-envelope text-xs" />
                      <span>{isEn ? "Contact Mette" : "Kontakt Mette"}</span>
                    </Link>
                  </div>
                </div>

              </div>
            </FadeIn>
          </div>

        </div>
      </section>
    </div>
  );
};

const ForedragPrivatPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { lang, t } = useLanguage();
  const isEn = lang === 'en';
  const tabParam = searchParams.get('tab');
  const themeParam = searchParams.get('theme');
  const activeTab = tabParam === 'corporate' ? 'corporate' : 'private';

  useEffect(() => {
    document.title = isEn ? "Mette Brudvig - Keynotes & Workshops for Individuals" : "Mette Brudvig - Foredrag & workshops til private";
  }, [isEn]);

  useEffect(() => {
    if (themeParam) {
      const element = document.getElementById(themeParam);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
      }
    }
  }, [themeParam]);

  const setActiveTab = (tab) => {
    setSearchParams({ tab });
  };

  const privateWorkshops = isEn ? [
    {
      title: "Past Lives and Karmic Healing",
      subtitle: "Explore a past life, and understand and dissolve unwanted patterns",
      videoUrl: "https://youtube.com/shorts/PdXUfuEnj20?feature=share",
      description: "Some patterns rightly feel as if they do not originate solely from this life. We may react, fear, yearn, or hold ourselves back without fully understanding why.\n\nIn this workshop we work with past lives and karmic healing. You will be guided into a past life where you get the opportunity to see, feel, and understand some of the deeper layers that may affect your life today.\n\nIt is not about entertainment or fascination – even though it is deeply exciting! It is about healing, understanding, and liberation.\n\nWhen we gain insight into old experiences residing in our energy system, we can begin to release them. We can understand why certain themes keep returning, and we can meet ourselves with greater gentleness, clarity, and freedom.",
      bullets: [
        "Knowledge of past lives and karmic patterns with examples from my own journeys into them",
        "A guided journey into a past life",
        "Time to write, feel, and understand what you experience",
        "Tools to release old attachments and patterns",
        "A deeper understanding of what your soul needs to heal"
      ],
      outcome: "You leave with greater insight into your patterns and an opportunity to let go of something limiting you. You also get access to the regression meditation so you can continue the work at home."
    },
    {
      title: "Strengthen Your Heart Intelligence",
      subtitle: "Spot your dead weight, make space for the soul, and kickstart your transformation toward a happier, freer, and more peaceful version of you.",
      imageUrl: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/mettebrudvigokt2021-136.jpg",
      description: "Many of us carry far too much that is no longer ours and that prevents us from achieving genuine joy and soul connection. This can be old expectations, others' opinions, roles that hold you back, and limiting stories about yourself.\n\nIn this workshop we work on separating the old so you can more clearly feel yourself, your innate spark, and the intelligence in your heart.\n\nWe work with reflection, energy exercises, and meditation so you can begin to feel what you are finished with, what needs more room, and which direction feels true to you.\n\nIn short, we work to remove the layers that prevent you from feeling your heart's intelligence and living more in alignment with it.",
      bullets: [
        "Clarity on what you wish to release",
        "Exercises to let go of old roles, expectations, and energies",
        "Exercises helping you sense your true soul essence",
        "A guided meditation where you let go of the old and shift your energy",
        "A meditation to take home that strengthens your heart connection",
        "Access to greater calm, direction, and inner strength"
      ]
    },
    {
      title: "Appear Authentic & Credible",
      subtitle: "Learn to communicate cleanly so people not only support you but naturally follow you",
      imageUrl: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/mettebrudvigokt2021-84-1.jpg",
      description: "You and your fellow human beings are electromagnetic beings, and to appear credible there must be synergy between what you say and the energy you vibrate with. Your listener's intuition senses it if there is a mismatch.\n\nTheir system feels from your energy whether there is honesty and authenticity, or whether you vibrate with signal errors from insecurity, pressure, defense, or hidden agendas.\n\nIn this workshop you learn to understand your own energy and how it affects others. You learn to sense when you broadcast cleanly and when there is noise on the line between what you say and the energy you actually radiate.\n\nAuthenticity is the key.\n\nWhen your words, values, and energy resonate, you appear credible and evoke genuine trust. Not because you try to convince anyone, but because people feel the alignment between what you say and who you are.\n\nThe workshop is particularly suitable for leaders, politicians, educators, advisors, and anyone who needs to communicate clearly, build trust, and bring people along.",
      bullets: [
        "Concrete understanding of how your energy impacts your credibility",
        "Insight into when you broadcast cleanly and when signal noise occurs",
        "Exercises to feel your own energy more clearly",
        "Tools to create greater calm, authenticity, and impact",
        "A guided meditation that raises your frequency (yours to keep)",
        "Understanding why people respond to more than your words (quantum physics)",
        "Training in creating resonance between your words and emitted energy"
      ],
      outcome: "You leave with a stronger sense of your own energy and concrete tools to appear more credible, trustworthy, and authentic."
    }
  ] : [
    {
      title: "Tidligere liv og karmisk healing",
      subtitle: "Gå på opdagelse i et tidligere liv, og forstå og opløs uønskede mønstre",
      videoUrl: "https://youtube.com/shorts/PdXUfuEnj20?feature=share",
      description: "Nogle mønstre føles med rette, som om de ikke kun kommer fra dette liv. Vi kan reagere, frygte, længes eller holde os tilbage uden helt at forstå hvorfor.\n\nPå denne workshop arbejder vi med tidligere liv og karmisk healing. Du bliver guidet ind i et tidligere liv, hvor du får mulighed for at se, mærke og forstå nogle af de dybere lag, der kan påvirke dit liv i dag.\n\nDet handler ikke om underholdning eller fascination – selvom det er vildt spændende! Det handler om healing, forståelse og frisættelse.\n\nNår vi får indblik i de gamle erfaringer, der sidder i vores energisystem, kan vi begynde at slippe dem. Vi kan forstå, hvorfor bestemte temaer bliver ved med at vende tilbage, og vi kan møde os selv med mere mildhed, klarhed og frihed.",
      bullets: [
        "Viden om tidligere liv og karmiske mønstre med eksempler fra mine egne rejser ind i dem",
        "En guidet rejse ind i et tidligere liv",
        "Tid til at skrive, mærke og forstå det, du oplever",
        "Redskaber til at slippe gamle bindinger og mønstre",
        "En dybere forståelse af, hvad din sjæl har brug for at hele"
      ],
      outcome: "Du går derfra med større indsigt i dine mønstre og en mulighed for at give slip på noget, der begrænser dig. Du får også adgang til regressionsmeditationen, så du kan fortsætte arbejdet derhjemme."
    },
    {
      title: "Styrk hjertets intelligens",
      subtitle: "Få øje på din dødvægt, skab plads til sjælen og kickstart din transformation mod en gladere, friere og mere fredfyldt version af dig.",
      imageUrl: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/mettebrudvigokt2021-136.jpg",
      description: "Mange af os bærer rundt på alt for meget, der ikke længere er vores, og som forhindrer os i at opnå ægte livsglæde og sjælsforbindelse. Det kan være gamle forventninger, andres meninger, roller, der holder dig tilbage, og begrænsende fortællinger om dig selv.\n\nPå denne workshop arbejder vi med at begynde at skille det gamle fra, så du tydeligere kan mærke dig selv, din iboende gnist og den intelligens, der findes i dit hjerte.\n\nVi arbejder med refleksion, energiøvelser og meditation, så du kan begynde at mærke, hvad du er færdig med, hvad der skal have mere plads, og hvilken retning der føles sand for dig.\n\nKort sagt arbejder vi med at fjerne de lag, der forhindrer dig i at mærke hjertets intelligens og leve mere i overensstemmelse med den.",
      bullets: [
        "Klarhed over, hvad du ønsker at slippe",
        "Øvelser til at give slip på gamle roller, forventninger og energier",
        "Øvelser, der hjælper dig med at mærke din sande sjælsessens",
        "En guidet meditation, hvor du giver slip på det gamle og ændrer din energi",
        "En meditation med hjem, som styrker din hjerteforbindelse",
        "Kontakt til mere ro, retning og indre styrke"
      ]
    },
    {
      title: "Fremstå troværdig",
      subtitle: "Lær at kommunikere rent, så mennesker ikke bare bakker op, men naturligt følger dig",
      imageUrl: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/mettebrudvigokt2021-84-1.jpg",
      description: "Du og dine medmennesker er elektromagnetiske væsener, og for at fremstå troværdig skal der være synergi mellem det, du siger, og den energi, du vibrerer med. Din modtagers intuition mærker det, hvis der ikke er.\n\nDeres system mærker på din energi, om der er ærlighed og autenticitet, eller om du vibrerer med signalfejl fra usikkerhed, pres, forsvar eller skjulte dagsordener.\n\nPå denne workshop lærer du at forstå din egen energi, og hvordan den påvirker andre. Du lærer at mærke, hvornår du sender rent, og hvornår der er støj på linjen mellem det, du siger, og den energi, du faktisk vibrerer med.\n\nAutenticitet er nøglen.\n\nNår dine ord, dine værdier og din energi er i resonans, fremstår du troværdig og vækker ægte tillid. Ikke fordi du forsøger at overbevise nogen, men fordi mennesker kan mærke, at der er sammenhæng mellem det, du siger, og den du er.\n\nWorkshoppen er særligt egnet til ledere, politikere, undervisere, rådgivere og andre, der skal kommunikere klart, skabe tillid og få mennesker med sig.",
      bullets: [
        "En konkret forståelse af, hvordan din energi påvirker din troværdighed",
        "Indblik i, hvornår du sender rent, og hvornår der opstår signalfejl",
        "Øvelser, der hjælper dig med at mærke din egen energi tydeligere",
        "Redskaber til at skabe større ro, autenticitet og gennemslagskraft",
        "En guidet meditation, der hæver din frekvens (den får du også med hjem)",
        "Forståelse for, hvorfor mennesker responderer på mere end dine ord (kvantefysik)",
        "Træning i at skabe resonans mellem det, du siger, og den energi, du udsender"
      ],
      outcome: "Du går derfra med en stærkere fornemmelse af din egen energi og konkrete redskaber til at fremstå mere troværdig, tillidsvækkende og autentisk."
    }
  ];

  const corporateWorkshops = [
    {
      title: "Fremtiden: spring ind i den",
      subtitle: "Bliv klar til fremtiden og høst alle de gunstige fordele – for både individet og organisationen.",
      videoUrl: "https://youtube.com/shorts/gF-xTMMBE2E?feature=share",
      description: "Vi står midt i et evolutionært skifte. De gamle måder at tænke, arbejde, leve og lede på passer ikke til dem, der udgør de næste generationer af kunder, medarbejdere, elever og medlemmer.\n\nHer bliver du klogere på bevægelsen fra det tribale til det planetariske: fra “os og dem” til en større bevidsthed om, at vi er forbundet med hinanden, naturen og den verden, vi er en del af.\n\nDu får viden, refleksioner og konkrete øvelser, der hjælper dig med at forstå, hvad tiden kalder på — og hvordan du selv bliver mere klar, bevidst og handlekraftig i den nye virkelighed.\n\nWorkshoppen er for jer, der mærker, at fremtiden ikke kun handler om teknologi, økonomi og systemer, men også om bevidsthed, ansvar, menneskelighed og mod.",
      bullets: [
        "Indblik i det store bevidsthedsskifte, vi står i",
        "Forståelse for, hvad det betyder for dig, dine kunder, dine relationer og samfundet",
        "Øvelser, der hjælper dig med at se fremtidsversionen af dig",
        "Inspiration til at leve og lede mere bevidst",
        "Konkrete refleksioner over, hvad du skal slippe, og hvad du skal træde ind i – for vi skal ikke have mere, men bedre."
      ],
      outcome: "Du går derfra med en større forståelse af fremtiden og en klarere fornemmelse af, hvordan du selv er en del af den og kan få et forspring."
    },
    {
      title: "Fremstå troværdig",
      subtitle: "Lær at kommunikere rent, så mennesker ikke bare bakker op, men naturligt følger dig",
      videoUrl: "https://youtube.com/shorts/DO0nH6ZNGvo?feature=share",
      description: "Du og dine medmennesker er elektromagnetiske væsener, og for at fremstå troværdig skal der være synergi mellem det, du siger, og den energi, du vibrerer med. Deres intuition mærker det, hvis der ikke er.\n\nMærker deres system ærlighed og autenticitet, eller opfatter det signalfejl fra usikkerhed, pres, forsvar eller skjulte dagsordener?\n\nPå denne workshop lærer du at forstå din egen energi, og hvordan den påvirker andre. Du lærer at mærke, hvornår du sender rent, og hvornår der er støj på linjen mellem det, du siger, og den energi, du faktisk vibrerer med.\n\nAutenticitet er nøglen.\n\nNår dine ord, dine værdier og din energi er i resonans, fremstår du troværdig og vækker ægte tillid. Ikke fordi du forsøger at overbevise nogen, men fordi mennesker kan mærke, at der er sammenhæng mellem det, du siger, og den du er.\n\nWorkshoppen er særligt egnet til ledere, politikere, undervisere, rådgivere og andre, der skal kommunikere klart, skabe tillid og få mennesker med sig.",
      bullets: [
        "En konkret forståelse af, hvordan din energi påvirker din troværdighed",
        "Indblik i, hvornår du sender rent, og hvornår der opstår signalfejl",
        "Øvelser, der hjælper dig med at mærke din egen energi tydeligere",
        "Redskaber til at skabe større ro, autenticitet og gennemslagskraft",
        "En guidet meditation, der hæver din frekvens (den får du også med hjem)",
        "Forståelse for, hvorfor mennesker responderer på mere end dine ord (kvantefysik)",
        "Træning i at skabe resonans mellem det, du siger, og den energi, du udsender"
      ],
      outcome: "Du går derfra med en stærkere fornemmelse af din egen energi og konkrete redskaber til at fremstå mere troværdig, tillidsvækkende og autentisk."
    }
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] md:min-h-[80vh] pt-[185px] md:pt-[215px] lg:pt-[225px] pb-16 flex items-center bg-[#f5efe9] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center z-10 w-full">
          <FadeIn direction="right">
            <span className="text-xs uppercase tracking-[0.4em] font-bold text-[#817262] mb-4 block">
              {t('foredragPage.badge')}
            </span>
            <h1 className="text-4xl md:text-6xl font-serif text-stone-900 leading-[1.1] mb-8">
              {lang === 'en' ? "For Individuals" : "Til Private"}
            </h1>
            <p className="text-xl text-stone-600 font-light leading-relaxed max-w-lg">
              {lang === 'en' 
                ? "Create a life filled with courage, trust, and deep soul connection through 360-degree personal development, energy work, and meditation."
                : "Skab et liv med mod, tillid og dyb sjælsforbindelse. Gennem 360-graders personlig udvikling med energiarbejde, meditation og konkret handling går du vejen hjem til din sjælskerne."}
            </p>
          </FadeIn>
        </div>
        <div className="absolute top-0 right-0 w-full md:w-1/2 h-full opacity-20 md:opacity-100 pt-20 md:pt-24 lg:pt-28 pointer-events-none">
          <img 
            src="/kundebilleder/image1.jpg" 
            alt="Mette Brudvig Foredrag til Private" 
            className="w-full h-full object-cover object-top pointer-events-none"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#f5efe9] via-transparent to-transparent hidden md:block pointer-events-none" />
        </div>
      </section>

      {/* Intro Statement */}
      <section className="py-16 bg-white px-6">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <FadeIn>
            <p className="text-2xl md:text-3xl font-serif text-stone-800 leading-relaxed italic">
              {lang === 'en'
                ? '"We stand on the threshold of a new era, and activating heart intelligence is personal development for a new era."'
                : '"Vi står på tærsklen af en ny tid, og aktivering af hjertets intelligens er ny tids personlig udvikling."'}
            </p>
          </FadeIn>
          <FadeIn delay={150} className="max-w-2xl mx-auto pt-4">
            <div className="overflow-hidden rounded-md shadow-md aspect-video border border-stone-200">
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/mettebrudvigokt2021-136.jpg" 
                alt="Mette Brudvig" 
                className="w-full h-full object-cover" 
              />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 4 Udtalelser fra deltagere og kursister - flyttet op for at inspirere til videre læsning */}
      <TestimonialsShowcase
        badge={lang === 'en' ? "Participant Experiences" : "Udtalelser fra deltagere"}
        title={lang === 'en' ? "What others experience with Mette" : "Hvad andre oplever sammen med Mette"}
        subtitle={lang === 'en' ? "Authentic experiences from participants in courses, programs, and keynotes." : "Autentiske oplevelser fra deltagere i kurser, forløb og foredrag."}
        className="py-20 md:py-24 bg-[#faf9f6] px-6 border-t border-b border-stone-200/60"
        cardBg="bg-white"
      />

      {/* SIGNATURTEMER SECTION */}
      <section id="planlagte" className="py-24 bg-stone-50 px-6 border-t border-b border-stone-100 scroll-mt-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs uppercase tracking-[0.4em] font-bold text-[#817262] mb-4 block">
              {lang === 'en' ? "Themes" : "Temaer"}
            </span>
            <h2 className="text-4xl md:text-5xl font-serif text-stone-900 mb-6">
              {lang === 'en' ? "My Two Main Themes" : "Mine To Hovedtemaer"}
            </h2>
            <p className="text-stone-600 font-light text-lg leading-relaxed">
              {lang === 'en'
                ? "I have gathered my services under two strong signature themes. Each theme offers different doorways into the same deep transformation – whether you seek an inspiring keynote, an intensive workshop, or a comprehensive program."
                : "Jeg har samlet mine ydelser under to stærke signaturtemaer. Hvert tema tilbyder forskellige døre ind til den samme dybe transformation – uanset om du ønsker et inspirerende foredrag, en intensiv workshop eller et dybdegående forløb."}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* SIGNATURTEMA 1: FRISÆT DIG */}
            <div id="frisaet-dig" className="bg-white border border-stone-200/60 rounded-sm p-8 md:p-12 shadow-sm flex flex-col justify-between hover:shadow-md transition-all scroll-mt-24">
              <div className="space-y-6">
                <div className="relative aspect-[16/10] overflow-hidden rounded-sm border border-stone-100 bg-stone-50">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/image_12.png" 
                    alt="Mette Brudvig - Frisæt dig" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="space-y-3">
                  <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#817262] bg-[#f5efe9] px-3 py-1 rounded-full inline-block">
                    {isEn ? "Unleash Yourself" : "Frisæt dig"}
                  </span>
                  <h3 className="text-3xl font-serif text-stone-900">
                    {isEn ? "Unleash Yourself" : "Frisæt dig"}
                  </h3>
                  <p className="text-base font-serif italic text-stone-800 leading-relaxed">
                    {isEn ? "– live with courage and trust as your whole self" : "– lev med mod og tillid som hele dig"}
                  </p>
                  <p className="text-stone-600 font-light text-sm leading-relaxed">
                    {isEn
                      ? "Do you wish to feel who you deep down are, and what a dream life is for you? To release yourself from old patterns and beliefs so you lovingly and worthily always have your own back and can calmly say no without regret."
                      : "Ønsker du at mærke, hvem du inderst inde er, og hvad et drømmeliv er for dig? At sætte dig selv fri af gamle mønstre og overbevisninger, så du kærligt og værdigt altid har din egen ryg og kan sige roligt fra uden fortrydelse."}
                  </p>
                </div>
              </div>
              <div className="pt-8 border-t border-stone-100 mt-8">
                <Link 
                  to={isEn ? "/en/unleash-yourself" : "/frisaet-dig"} 
                  className="inline-flex w-full justify-center items-center gap-2 bg-[#817262] text-white py-3 px-6 uppercase text-xs tracking-widest font-bold hover:bg-stone-800 transition-colors rounded-sm"
                >
                  <span>{isEn ? "Read more about Unleash Yourself" : "Læs mere om Frisæt dig"}</span>
                  <i className="fa-light fa-arrow-right" />
                </Link>
              </div>
            </div>

            {/* SIGNATURTEMA 2: KVINDE - KEND DIN POWER! */}
            <div id="kvinde-power" className="bg-white border border-stone-200/60 rounded-sm p-8 md:p-12 shadow-sm flex flex-col justify-between hover:shadow-md transition-all scroll-mt-24">
              <div className="space-y-6">
                <div className="relative aspect-[16/10] overflow-hidden rounded-sm border border-stone-100 bg-stone-50">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/image_7.png" 
                    alt="KVINDE - Kend din power!" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="space-y-3">
                  <span className="text-xs uppercase tracking-[0.3em] font-bold text-white bg-[#817262] px-3 py-1 rounded-full inline-block">
                    {isEn ? "Woman – Know Your Power!" : "Kvinde – kend din power!"}
                  </span>
                  <h3 className="text-3xl font-serif text-stone-900">
                    {isEn ? "Woman – Know Your Power!" : "Kvinde – kend din power!"}
                  </h3>
                  <p className="text-base font-serif italic text-stone-800 leading-relaxed">
                    {isEn ? "Awaken your intuition, inner strength, and action power." : "Væk din intuition, din indre styrke og din handlekraft."}
                  </p>
                  <p className="text-stone-600 font-light text-sm leading-relaxed">
                    {isEn
                      ? "The world does not need women acting like men. It needs women who know their own power and dare to stand in it – with sensitivity, intuition, heart, and courage."
                      : "Verden har ikke brug for kvinder, der opfører sig som mænd. Den har brug for kvinder, der kender deres egen kraft og tør stå i den – med intuition, nærvær, hjerte og mod."}
                  </p>
                </div>
              </div>
              <div className="pt-8 border-t border-stone-100 mt-8">
                <Link 
                  to={isEn ? "/en/woman-know-your-power" : "/kvinde-kend-din-power"} 
                  className="inline-flex w-full justify-center items-center gap-2 bg-[#817262] text-white py-3 px-6 uppercase text-xs tracking-widest font-bold hover:bg-stone-800 transition-colors rounded-sm"
                >
                  <span>{isEn ? "Read more about Woman – Know Your Power" : "Læs mere om Kvinde – kend din power"}</span>
                  <i className="fa-light fa-arrow-right" />
                </Link>
              </div>
            </div>
          </div>

          {/* REMOVED OLD BLOCKS */}
          <div className="hidden">
            <div id="old-frisaet-dig-placeholder">
          <div id="frisaet-dig" className="bg-white border border-stone-200/60 rounded-sm p-8 md:p-16 shadow-sm mb-24 scroll-mt-24">
            <div className="grid lg:grid-cols-12 gap-12 items-center mb-16">
              <div className="lg:col-span-7 space-y-6">
                <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#817262] bg-[#f5efe9] px-3 py-1 rounded-full">Frisæt dig</span>
                <h3 className="text-4xl md:text-5xl font-serif text-stone-900 leading-tight">Frisæt dig</h3>
                <p className="text-xl font-serif italic text-stone-800 leading-relaxed">– lev med mod og tillid som hele dig</p>
                <div className="h-px w-16 bg-stone-200" />
                <div className="text-stone-600 font-light text-base leading-relaxed space-y-4">
                  <p>
                    Ønsker du at mærke, hvem du inderst inde er, og hvad et drømmeliv er for dig? At sætte dig selv fri af gamle mønstre og overbevisninger, så du kærligt og værdigt altid har din egen ryg og kan sige roligt fra uden fortrydelse.
                  </p>
                  <p>
                    Når du ønsker at skabe ægte frihed i dit indre og ydre liv, så er viljestyrke og beslutsomhed ikke nok. Du skal også ændre din energi, så den passer til den nye frie version af dig. Her arbejder vi med at fjerne de lag, der forhindrer dig i at mærke og leve fra din sjælskerne.
                  </p>
                </div>
                <div className="pt-4">
                  <Link 
                    to="/frisaet-dig" 
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-[#817262] hover:text-stone-900 transition-colors group"
                  >
                    <span>Læs mere om dette signaturtema</span>
                    <i className="fa-light fa-arrow-right transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="relative p-4 border border-stone-200 bg-stone-50">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/mettebrudvigokt2021-124_1.jpg" 
                    alt="Mette Brudvig - Frisæt dig" 
                    className="w-full aspect-[4/5] object-cover"
                  />
                </div>
              </div>
            </div>

            <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-stone-900 border-b border-stone-100 pb-4 mb-8">
              Vælg din dør ind til temaet:
            </h4>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Card 1: Foredrag */}
              <div className="bg-stone-50 p-8 border border-stone-100 rounded-sm flex flex-col justify-between">
                <div className="space-y-4">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-stone-400 block">Foredrag</span>
                  <h5 className="text-xl font-serif text-stone-900">Har du sjælen med?</h5>
                  <p className="text-xs italic text-stone-500">Om den styrke, der opstår, når du lever fra din sande essens.</p>
                  <p className="text-sm text-stone-600 font-light leading-relaxed">
                    Aktivering af hjertets intelligens er the missing link, når du arbejder med dig selv for at opnå større klarhed, trivsel og succes. Du styrker din intuition, kvaliteten af dine beslutninger og står stærkt i dig selv.
                  </p>
                  <ul className="space-y-2 pt-2">
                    <li className="flex items-start text-xs text-stone-600 font-light">
                      <i className="fa-light fa-sparkles text-[#817262] mt-0.5 mr-2 flex-shrink-0" />
                      <span>Kombination af videnskab og spiritualitet</span>
                    </li>
                    <li className="flex items-start text-xs text-stone-600 font-light">
                      <i className="fa-light fa-sparkles text-[#817262] mt-0.5 mr-2 flex-shrink-0" />
                      <span>Redskaber til at skærpe din intuition</span>
                    </li>
                  </ul>
                </div>
                <div className="pt-6 mt-6 border-t border-stone-200/60">
                  <Link 
                    to="/kontakt?subject=Foredrag: Har du sjælen med?" 
                    className="block w-full text-center bg-[#817262] text-white py-3 uppercase text-[10px] tracking-widest font-bold hover:bg-stone-800 transition-colors rounded-sm"
                  >
                    Kontakt mig om foredraget
                  </Link>
                </div>
              </div>

              {/* Card 2: Workshop */}
              <div className="bg-stone-50 p-8 border border-stone-100 rounded-sm flex flex-col justify-between">
                <div className="space-y-4">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-stone-400 block">Weekendworkshop</span>
                  <h5 className="text-xl font-serif text-stone-900">Frisæt dig weekendworkshop</h5>
                  <p className="text-xs italic text-stone-500">7 trin til at skabe dit drømmeliv og lære at leve fra dit hjerte</p>
                  <p className="text-sm text-stone-600 font-light leading-relaxed">
                    Har du lyst til at kickstarte din transformation mod et liv med mere frihed, mod, tillid og hjerte? Jeg tager dig gennem mine 7 trin til frisættelse, skabt på baggrund af min egen transformationsrejse, erfaring, faglige viden og kanaliseret indsigt.
                  </p>
                  <div className="my-4">
                    <YouTubeEmbed url="https://youtube.com/shorts/1Ik5dwg5rc4?feature=share" title="Intro til Frisæt dig weekendworkshop" />
                  </div>
                  <ul className="space-y-2 pt-2">
                    <li className="flex items-start text-xs text-stone-600 font-light">
                      <i className="fa-light fa-sparkles text-[#817262] mt-0.5 mr-2 flex-shrink-0" />
                      <span>To dages workshop med undervisning, meditation, refleksion og øvelser</span>
                    </li>
                    <li className="flex items-start text-xs text-stone-600 font-light">
                      <i className="fa-light fa-sparkles text-[#817262] mt-0.5 mr-2 flex-shrink-0" />
                      <span>De 7 trin til at frisætte dig og skabe det liv, du ønsker</span>
                    </li>
                  </ul>
                </div>
                <div className="pt-6 mt-6 border-t border-stone-200/60">
                  <Link 
                    to="/frisaet-dig/workshop" 
                    className="block w-full text-center bg-[#817262] text-white py-3 uppercase text-[10px] tracking-widest font-bold hover:bg-stone-800 transition-colors rounded-sm"
                  >
                    Læs mere om workshoppen
                  </Link>
                </div>
              </div>

              {/* Card 3: Forløb */}
              <div className="bg-stone-50 p-8 border border-stone-100 rounded-sm flex flex-col justify-between">
                <div className="space-y-4">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-stone-400 block">Forløb</span>
                  <h5 className="text-xl font-serif text-stone-900">Spirituelt Mentorforløb</h5>
                  <p className="text-xs italic text-stone-500">Et skræddersyet, individuelt forløb for dig.</p>
                  <p className="text-sm text-stone-600 font-light leading-relaxed">
                    Et dybt transformerende forløb, der hjælper dig med at integrere din intuition og hjertets intelligens i din hverdag. Vi arbejder intensivt med meditation, energi-skift og at slippe de gamle rammer.
                  </p>
                  <div className="my-4">
                    <YouTubeEmbed url="https://youtube.com/shorts/sc-IH6dddRI?feature=share" title="Intro til Frisæt dig forløb" />
                  </div>
                  <ul className="space-y-2 pt-2">
                    <li className="flex items-start text-xs text-stone-600 font-light">
                      <i className="fa-light fa-sparkles text-[#817262] mt-0.5 mr-2 flex-shrink-0" />
                      <span>Individuelle, fortrolige sessioner med Mette</span>
                    </li>
                    <li className="flex items-start text-xs text-stone-600 font-light">
                      <i className="fa-light fa-sparkles text-[#817262] mt-0.5 mr-2 flex-shrink-0" />
                      <span>Skræddersyet energiarbejde og clairvoyance</span>
                    </li>
                  </ul>
                </div>
                <div className="pt-6 mt-6 border-t border-stone-200/60">
                  <Link 
                    to="/kontakt?subject=Forløb: Spirituelt Mentorforløb" 
                    className="block w-full text-center bg-[#817262] text-white py-3 uppercase text-[10px] tracking-widest font-bold hover:bg-stone-800 transition-colors rounded-sm"
                  >
                    Kontakt mig om forløbet
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* SIGNATURTEMA 2: KVINDE - KEND DIN POWER! */}
          <div id="kvinde-power" className="bg-[#f5efe9]/40 border border-[#817262]/20 rounded-sm p-8 md:p-16 shadow-sm scroll-mt-24">
            <div className="grid lg:grid-cols-12 gap-12 items-center mb-16">
              <div className="lg:col-span-7 space-y-6">
                <span className="text-xs uppercase tracking-[0.3em] font-bold text-white bg-[#817262] px-3 py-1 rounded-full">Kvinde – kend din power!</span>
                <h3 className="text-4xl md:text-5xl font-serif text-stone-900 leading-tight">KVINDE - Kend din power!</h3>
                <p className="text-xl font-serif italic text-stone-800 leading-relaxed">Væk din intuition, din indre styrke og din handlekraft.</p>
                <div className="h-px w-16 bg-[#817262]/30" />
                <div className="text-stone-700 font-light text-base leading-relaxed space-y-4">
                  <p>
                    Verden har ikke brug for kvinder, der opfører sig som mænd. Den har brug for kvinder, der kender deres egen kraft og tør stå i den – med intuition, nærvær, hjerte og mod.
                  </p>
                  <p>
                    Kvinde – kend din power er ikke et fjendskab mod mænd, men en anerkendelse af, at i den nye æra – hjertets tidsalder – er det de modne feminine energier, verden har brug for. Og dem kan og bør mænd også modne i sig selv, så vi sammen kan skabe en tryg, bæredygtig og empatisk verden.
                  </p>
                  <p>
                    Den modne feminine energi er ikke svag. Tværtimod! Den rummer et naturligt overblik, skaber tryghed, samler mennesker og træffer kloge beslutninger med både hjerte og handlekraft. Og det er lige netop de kvaliteter, den nye tid kalder på i både ledelse, arbejdsliv, familieliv og samfund.
                  </p>
                  <p>
                    I disse workshops og forløb lærer du at stå stærkt i dig selv, finde balancen mellem din feminine og maskuline energi og modne dem, så du kan leve og lede dig selv og andre med autenticitet og overskud frem for anstrengelse. Du styrker din intuition, din sjælsforbindelse og dit personlige lederskab.
                  </p>
                </div>
                <div className="pt-4">
                  <Link 
                    to="/kvinde-kend-din-power" 
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-[#817262] hover:text-stone-900 transition-colors group"
                  >
                    <span>Læs mere om dette signaturtema</span>
                    <i className="fa-light fa-arrow-right transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
              <div className="lg:col-span-5 space-y-6">
                <div className="relative p-4 border border-[#817262]/20 bg-white">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/image_7.png" 
                    alt="KVINDE - Kend din power!" 
                    className="w-full aspect-[4/5] object-cover"
                  />
                </div>
                <div className="my-4">
                  <YouTubeEmbed 
                    url="https://youtube.com/shorts/doG3oiw1VsM?feature=share" 
                    title={isEn ? "Intro to Woman - Know Your Power" : "Intro til Kvinde - kend din power"} 
                  />
                </div>
                <div className="bg-white/80 p-6 border-l-2 border-[#817262] rounded-r-sm shadow-sm">
                  <p className="text-stone-800 font-light text-sm italic leading-relaxed">
                    {isEn
                      ? '"When women step into their true power, they elevate not only themselves – they help create a new culture where strength and care go hand in hand. That is the kind of leadership our children need in their lives."'
                      : '"Når kvinder træder ind i deres sande power, løfter de ikke kun sig selv – de er med til at skabe a ny kultur, hvor styrke og omsorg går hånd i hånd. Det er den form for lederskab, vores børn har brug for i deres liv."'}
                  </p>
                </div>
              </div>
            </div>

            {/* Ekstra dybdegående tekst fra Mette */}
            <div className="bg-white/60 p-8 md:p-12 rounded-sm border border-stone-200/40 mb-16 space-y-6 text-stone-700 font-light text-base leading-relaxed">
              <p>
                {isEn
                  ? "Knowing your true essence and power is your path to joy, courage, self-trust, and inner peace. Your genuine power consists of grounded, heart-centered calm, decisiveness, and heart intelligence."
                  : "At kende din sande essens og power er din vej til livsglæde, mod, tillid til dig selv og livet og din indre fred. Din ægte power består af din bomstærke, jordbundne og hjerteforbundede ro, handlekraft og hjertets intelligens. Og det er den, vi arbejder med her."}
              </p>
              <p>
                {isEn
                  ? "It is about standing strong in yourself, sensing what feels right, saying yes when it is a yes, and no when it is a no. Acting when something calls you makes you not only a better leader or partner, but a deeply fulfilled human being."
                  : "Den handler om din evne til at stå stærkt i dig selv og kende din sande essens og leve fra den. At mærke, hvad der er rigtigt. At sige ja, når det er et ja, og nej, når det er et nej. At handle, når noget kalder på dig. Det gør dig ikke bare til en bedre mor, en bedre medarbejder, leder, partner og veninde. Det gør dig til et lykkeligere og mere selvkærligt menneske, der mærker sig selv, sit formål og sit potentiale."}
              </p>
              <p>
                {isEn
                  ? "I was once labeled a 'power woman' when juggling executive corporate roles, family life with three young children, and political engagement. But I learned that doing more was not the expression of my true power."
                  : "Jeg er selv blevet kaldt powerkvinde, når jeg har været udholdende og ambitiøs og har jongleret høje stillinger med familielivet med tre små børn, et politisk engagement og et aktivt socialliv. Men jeg har lært, at det ikke er de ting, jeg gør, der gør mig til en ægte powerkvinde."}
              </p>
              <p>
                {isEn
                  ? "True strength is activated when we feel ourselves and remain anchored in our soul essence. Here we know our authentic worth, not tied to performance, but to standing unwavering in courage, love, and peace."
                  : "At mine resultater, mit tempo og min evne til at gå den ekstra mil for at please andre ikke var udtryk for min sande styrke. Tværtimod. Det var et udtryk for, at jeg ikke var forbundet med den. For den sande styrke aktiverer vi, når vi kan mærke os selv og er forankret i vores sjælsessens. Her kender vi vores sande værdi, og den er ikke bundet op på vores performance og udholdenhed, men på vores evne til at stå urokkeligt i os selv og i mod og tillid og skabe fred i og omkring os fra et kærligt og værdigt sted."}
              </p>
              <p className="font-serif italic text-stone-900 text-lg">
                {isEn
                  ? '"When you stand strong in your mature feminine energy, you become impossible to overlook – and impossible to sway. Your true power is intuition, courage, and creation in its purest form."'
                  : '"Når du står stærkt i din modne feminine kraft, bliver du umulig at overse - og umulig at flytte væk fra dig selv. Din sande power er intuition, mod og skaberkraft i sin reneste form. Du skal ikke være mindre eller hårdere. Du skal være hel."'}
              </p>
            </div>

            <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-stone-900 border-b border-stone-200/60 pb-4 mb-8">
              {isEn ? "Choose your door into this theme:" : "Vælg din dør ind til temaet:"}
            </h4>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Card 1: 6 Måneders Forløb */}
              <div className="bg-white p-8 border border-stone-200/60 rounded-sm flex flex-col justify-between shadow-sm hover:shadow-md transition-all">
                <div className="space-y-6">
                  <div className="flex justify-between items-start">
                    <span className="text-[10px] uppercase tracking-widest font-bold text-stone-400 bg-stone-100 px-2.5 py-1 rounded-sm">
                      {isEn ? "6-Month Development Journey" : "6 Måneders Udviklingsforløb"}
                    </span>
                    <span className="text-xs font-serif text-[#817262] font-semibold">
                      {isEn ? "Journey" : "Forløb"}
                    </span>
                  </div>
                  <h5 className="text-2xl font-serif text-stone-900">
                    {isEn ? "Woman - Know Your Power" : "Kvinde - kend din power"}
                  </h5>
                  <p className="text-sm italic text-stone-500 font-serif">
                    {isEn
                      ? "For you who wish to activate your true essence and align with your authentic strength."
                      : "For dig, der vil aktivere den sande essens i dig og forbinde dig med din ægte styrke."}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
                    <div className="col-span-12 md:col-span-6 space-y-3">
                      <p className="text-sm text-stone-600 font-light leading-relaxed">
                        {isEn
                          ? "This development journey is for you who are ready to find home to yourself and activate your passion and true power. You connect with yourself to create a life filled with courage, trust, and decisiveness, guided by a clear inner compass."
                          : "Dette udviklingsforløb er for dig, der er klar til at finde hjem til dig selv og aktivere din passion og din sande power. Du forbinder dig med dig selv, så du skaber et liv med mod, tillid og handlekraft, fordi dit indre kompas er tydeligt, og du mærker dig selv, og hvad det er, du ønsker."}
                      </p>
                      <p className="text-sm text-stone-600 font-light leading-relaxed">
                        {isEn
                          ? "Through six months, you work deep into your core, training your ability to sense what is true for you. Learn to see through patterns that cause doubt, pleasing, or overperforming. 360° mind, body, and soul transformation."
                          : "Gennem seks måneder arbejder du dig ind til din kerne, og du træner din evne til at mærke, hvad der er sandt for dig. Du lærer at gennemskue de mønstre, der får dig til at tvivle, tilpasse dig eller overpræstere. Vi arbejder 360 grader (mind, body, soul) med at ændre din energi."}
                      </p>
                    </div>
                    <div className="col-span-12 md:col-span-6 flex justify-center md:justify-end">
                      <YouTubeEmbed 
                        url="https://youtube.com/shorts/zPKIvjYkClY?feature=share" 
                        title={isEn ? "Woman - Know Your Power program" : "Kvinde - kend din power forløb"} 
                        className="w-full max-w-[220px]"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <h6 className="text-[10px] uppercase tracking-widest font-bold text-stone-900">
                      {isEn ? "What you get:" : "Det får du:"}
                    </h6>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {(isEn ? [
                        "Understanding of mature feminine energy",
                        "Stronger connection to your intuition",
                        "Maturation of your feminine power",
                        "Greater access to soft presence & warmth",
                        "More courage, strength, and decisiveness",
                        "Work on your limiting patterns",
                        "Ability to set boundaries calmly",
                        "Training in standing clear in relationships",
                        "Meditations, exercises, and tools",
                        "Strong community with women who want more from life"
                      ] : [
                        "Forståelse for den modne feminine kraft",
                        "En stærkere kontakt til din intuition",
                        "Modning af din feminine kraft",
                        "Større adgang til din blødhed & nærvær",
                        "Mere mod, styrke og handlekraft",
                        "Arbejdet med dine begrænsende mønstre",
                        "Evnen til at sætte grænser udramatisk",
                        "Træning i at stå tydeligt i relationer",
                        "Meditationer, øvelser og redskaber",
                        "Et stærkt fællesskab med kvinder, der også vil mere med deres liv"
                      ]).map((bullet, bIdx) => (
                        <li key={bIdx} className="flex items-start text-xs text-stone-600 font-light">
                          <i className="fa-light fa-sparkles text-[#817262] mt-0.5 mr-2 flex-shrink-0" />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-stone-50 p-4 border border-stone-100 rounded-sm space-y-2">
                    <p className="text-xs text-stone-700 font-light leading-relaxed">
                      <strong className="font-serif italic text-stone-900 block">
                        {isEn ? "Your outcome:" : "Dit udbytte:"}
                      </strong>
                      {isEn 
                        ? "You become less dependent on others' judgments and more confident in your own choices. You feel faster what is right, what drains you, and what needs more space. You stand stronger in your relationships, career, and personal leadership."
                        : "Du bliver mindre afhængig af andres vurderinger og mere sikker i dine egne valg. Du kan mærke hurtigere, hvad der er rigtigt, hvad der dræner dig, og hvad der skal have mere plads. Du står stærkere i dine relationer, i dit arbejdsliv og i dit personlige lederskab."}
                    </p>
                  </div>
                </div>
                
                <div className="pt-6 mt-8 border-t border-stone-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="text-stone-900">
                    <span className="text-[9px] uppercase tracking-widest text-stone-400 block font-bold">
                      {isEn ? "Price" : "Pris"}
                    </span>
                    <span className="text-sm font-serif font-semibold">
                      {isEn ? "In-person: 12,500 DKK / Online: 8,500 DKK" : "Fysisk: 12.500 kr. / Online: 8.500 kr."}
                    </span>
                  </div>
                  <Link 
                    to={isEn ? "/en/contact?subject=Program: Woman - know your power" : "/kontakt?subject=Forløb: Kvinde - kend din power"} 
                    className="bg-[#817262] text-white px-6 py-3 uppercase text-[10px] tracking-widest font-bold hover:bg-stone-800 transition-colors rounded-sm text-center"
                  >
                    {isEn ? "Contact me about the program" : "Kontakt mig om forløbet"}
                  </Link>
                </div>
              </div>

              {/* Card 2: Weekendworkshop */}
              <div className="bg-white p-8 border border-stone-200/60 rounded-sm flex flex-col justify-between shadow-sm hover:shadow-md transition-all">
                <div className="space-y-6">
                  <div className="flex justify-between items-start">
                    <span className="text-[10px] uppercase tracking-widest font-bold text-stone-400 bg-stone-100 px-2.5 py-1 rounded-sm">
                      {isEn ? "Weekend Workshop" : "Weekendworkshop"}
                    </span>
                    <span className="text-xs font-serif text-[#817262] font-semibold">
                      {isEn ? "Workshop" : "Workshop"}
                    </span>
                  </div>
                  <h5 className="text-2xl font-serif text-stone-900">
                    {isEn ? "Woman - Know Your Power" : "Kvinde - kend din power"}
                  </h5>
                  <p className="text-sm italic text-stone-500 font-serif">
                    {isEn ? "For you who want to connect with your true essence and align with your authentic strength." : "For dig, der vil i kontakt med den sande essens i dig og forbinde dig med din ægte styrke."}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
                    <div className="col-span-12 md:col-span-6 space-y-3">
                      <p className="text-sm text-stone-600 font-light leading-relaxed">
                        {isEn ? "Many women make themselves smaller than they are because they are not connected to their true strength. In this workshop, you work your way to your core, where you feel yourself, your passion, and your true strength." : "Mange kvinder gør sig selv mindre, end de er, fordi de ikke er forbundet med deres sande styrke. På denne workshop arbejder du dig ind til din kerne, hvor du mærker dig selv, din passion og din sande styrke."}
                      </p>
                      <p className="text-sm text-stone-600 font-light leading-relaxed">
                        {isEn ? "You connect with your feminine power and the softness and intuition that is one of your greatest strengths and intelligence. You work on sensing what life and what version of you you desire. Who are you when you are whole, joyful, and free?" : "Du forbinder dig med din feminine kraft og den blødhed og intuition, der er en af dine allerstørste styrker og intelligens. Du arbejder med at mærke, hvilket liv og hvilken version af dig, du ønsker. Hvem er du, når du er hel, livsglad og fri?"}
                      </p>
                    </div>
                    <div className="col-span-12 md:col-span-6 flex justify-center md:justify-end">
                      <YouTubeEmbed 
                        url="https://youtube.com/shorts/qY9py6vzS2w?feature=share" 
                        title={isEn ? "Woman - Know Your Power workshop" : "Kvinde - kend din power workshop"} 
                        className="w-full max-w-[220px]"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <h6 className="text-[10px] uppercase tracking-widest font-bold text-stone-900">
                      {isEn ? "What you get:" : "Det får du:"}
                    </h6>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {(isEn ? [
                        "Understanding of mature feminine power",
                        "Stronger connection to your intuition",
                        "Maturation of your feminine power",
                        "Greater access to softness & presence",
                        "Tools to bring your energy home",
                        "Meditations, exercises, and tools"
                      ] : [
                        "Forståelse for den modne feminine kraft",
                        "En stærkere kontakt til din intuition",
                        "Modning af din feminine kraft",
                        "Større adgang til blødhed & nærvær",
                        "Redskaber til at tage din energi hjem",
                        "Meditationer, øvelser og redskaber"
                      ]).map((bullet, bIdx) => (
                        <li key={bIdx} className="flex items-start text-xs text-stone-600 font-light">
                          <i className="fa-light fa-sparkles text-[#817262] mt-0.5 mr-2 flex-shrink-0" />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-stone-50 p-4 border border-stone-100 rounded-sm space-y-2">
                    <p className="text-xs text-stone-700 font-light leading-relaxed">
                      <strong className="font-serif italic text-stone-900 block">
                        {isEn ? "Your outcome:" : "Dit udbytte:"}
                      </strong>
                      {isEn ? "You go home with a stronger connection to yourself and a clearer picture of what your strength is and how you want to use it." : "Du går hjem med en stærkere forbindelse til dig selv og et mere præcist billede af, hvad din styrke er, og hvordan du ønsker at bruge den."}
                    </p>
                  </div>
                </div>
                
                <div className="pt-6 mt-8 border-t border-stone-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="text-stone-900">
                    <span className="text-[9px] uppercase tracking-widest text-stone-400 block font-bold">
                      {isEn ? "Time & Price" : "Tid & Pris"}
                    </span>
                    <span className="text-sm font-serif font-semibold">
                      {isEn ? "Sat 10–16 & Sun 10–15 | 2,800 DKK" : "Lør 10-16 & Søn 10-15 | 2.800 kr."}
                    </span>
                  </div>
                  <Link 
                    to={isEn ? "/en/contact?subject=Weekend workshop: Woman - know your power" : "/kontakt?subject=Weekendworkshop: Kvinde - kend din power"} 
                    className="bg-[#817262] text-white px-6 py-3 uppercase text-[10px] tracking-widest font-bold hover:bg-stone-800 transition-colors rounded-sm text-center"
                  >
                    {isEn ? "Contact me about the workshop" : "Kontakt mig om workshoppen"}
                  </Link>
                </div>
              </div>

              {/* Card 3: 1-Dagsworkshop */}
              <div className="bg-white p-8 border border-stone-200/60 rounded-sm flex flex-col justify-between shadow-sm hover:shadow-md transition-all">
                <div className="space-y-6">
                  <div className="flex justify-between items-start">
                    <span className="text-[10px] uppercase tracking-widest font-bold text-stone-400 bg-stone-100 px-2.5 py-1 rounded-sm">
                      {isEn ? "1-Day Workshop" : "1-Dagsworkshop"}
                    </span>
                    <span className="text-xs font-serif text-[#817262] font-semibold">
                      {isEn ? "Workshop" : "Workshop"}
                    </span>
                  </div>
                  <h5 className="text-2xl font-serif text-stone-900">
                    {isEn ? "Woman - Know Your Power" : "Kvinde - kend din power"}
                  </h5>
                  <p className="text-sm italic text-stone-500 font-serif">
                    {isEn ? "An intensive day reconnecting with your intuition, strength, and direction." : "En intensiv dag, hvor du genfinder forbindelsen til din intuition, din styrke og din retning."}
                  </p>
                  <p className="text-sm text-stone-600 font-light leading-relaxed">
                    {isEn ? "This workshop is for you who are tired of doubting what you already sense inside. In a single day you get a clear picture of where you lose your power and what it takes to reclaim it." : "Denne workshop er for dig, der er træt af at tvivle på det, du allerede mærker. På én dag får du et klart billede af, hvor du mister din kraft, og hvad der skal til for at tage den hjem igen."}
                  </p>
                  <p className="text-sm text-stone-600 font-light leading-relaxed">
                    {isEn ? "We work with your intuition, energy, boundaries, and ability to act on what you know. Feminine power is intuition, courage, and creative force in its purest form. It is your superpower!" : "Vi arbejder med din intuition, din energi, dine grænser og din evne til at handle på det, du ved. Den feminine kraft er intuition, mod og skaberkraft i sin reneste form. Den er din superpower!"}
                  </p>
                  
                  <div className="space-y-3">
                    <h6 className="text-[10px] uppercase tracking-widest font-bold text-stone-900">
                      {isEn ? "What you get:" : "Det får du:"}
                    </h6>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {(isEn ? [
                        "Understanding of mature feminine power",
                        "Stronger connection to your intuition",
                        "Stronger connection to your authentic strength",
                        "Meditation and exercises to feel yourself"
                      ] : [
                        "Forståelse for den modne feminine kraft",
                        "En stærkere kontakt til din intuition",
                        "Stærkere kontakt til din sande styrke",
                        "Meditation og øvelser til at mærke dig selv"
                      ]).map((bullet, bIdx) => (
                        <li key={bIdx} className="flex items-start text-xs text-stone-600 font-light">
                          <i className="fa-light fa-sparkles text-[#817262] mt-0.5 mr-2 flex-shrink-0" />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-stone-50 p-4 border border-stone-100 rounded-sm space-y-2">
                    <p className="text-xs text-stone-700 font-light leading-relaxed">
                      <strong className="font-serif italic text-stone-900 block">
                        {isEn ? "Your outcome:" : "Dit udbytte:"}
                      </strong>
                      {isEn ? "You leave the workshop with greater clarity, more courage, and a stronger connection to what you already know." : "Du går fra workshoppen med større klarhed, mere mod og en stærkere forbindelse til det, du allerede ved."}
                    </p>
                  </div>
                </div>
                
                <div className="pt-6 mt-8 border-t border-stone-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="text-stone-900">
                    <span className="text-[9px] uppercase tracking-widest text-stone-400 block font-bold">
                      {isEn ? "Time & Price" : "Tid & Pris"}
                    </span>
                    <span className="text-sm font-serif font-semibold">
                      {isEn ? "10:00–16:00 | 1,250 DKK" : "Kl. 10.00-16.00 | 1.250 kr."}
                    </span>
                  </div>
                  <Link 
                    to={isEn ? "/en/contact?subject=1-day workshop: Woman - know your power" : "/kontakt?subject=1-dagsworkshop: Kvinde - kend din power"} 
                    className="bg-[#817262] text-white px-6 py-3 uppercase text-[10px] tracking-widest font-bold hover:bg-stone-800 transition-colors rounded-sm text-center"
                  >
                    {isEn ? "Contact me about the workshop" : "Kontakt mig om workshoppen"}
                  </Link>
                </div>
              </div>

              {/* Card 4: Foredrag */}
              <div className="bg-white p-8 border border-stone-200/60 rounded-sm flex flex-col justify-between shadow-sm hover:shadow-md transition-all">
                <div className="space-y-6">
                  <div className="flex justify-between items-start">
                    <span className="text-[10px] uppercase tracking-widest font-bold text-stone-400 bg-stone-100 px-2.5 py-1 rounded-sm">
                      {isEn ? "Keynote" : "Foredrag"}
                    </span>
                    <span className="text-xs font-serif text-[#817262] font-semibold">
                      {isEn ? "Keynote" : "Foredrag"}
                    </span>
                  </div>
                  <h5 className="text-2xl font-serif text-stone-900">
                    {isEn ? "Power Woman - Know Your Power" : "Powerkvinde - kend din power"}
                  </h5>
                  <p className="text-sm italic text-stone-500 font-serif">
                    {isEn ? "On the strength that arises when a woman senses herself and acts on it." : "Om den styrke, der opstår, når en kvinde kan mærke sig selv og handle på det."}
                  </p>
                  <p className="text-sm text-stone-600 font-light leading-relaxed">
                    {isEn ? "What happens when a woman stops measuring her worth by her results? When she no longer tries to be strong by carrying more, enduring longer, or making herself less burdensome? When she instead uses her intuition, emotional intelligence, and action power as real core competencies?" : "Hvad sker der, når en kvinde holder op med at måle sin værdi på sine resultater? Når hun ikke længere forsøger at være stærk ved at bære mere, holde ud længere eller gøre sig mindre besværlig? Når hun i stedet bruger sin intuition, sin følelsesmæssige intelligens og sin handlekraft som reelle kompetencer?"}
                  </p>
                  <p className="text-sm text-stone-600 font-light leading-relaxed">
                    {isEn ? "In this keynote, I show why feminine power is not the opposite of strength. It is a different form of strength, and precisely the one our world needs right now. When you stand in your feminine power, you become impossible to overlook – and impossible to sway." : "I foredraget viser jeg, hvorfor feminin kraft ikke er det modsatte af styrke. Den er en anden form for styrke og lige præcis den, som verden har brug for lige nu. Når du står i din feminine kraft, bliver du umulig at overse - og umulig at flytte væk fra dig selv."}
                  </p>
                  
                  <div className="space-y-3">
                    <h6 className="text-[10px] uppercase tracking-widest font-bold text-stone-900">
                      {isEn ? "The audience gets:" : "Publikum får:"}
                    </h6>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {(isEn ? [
                        "Understanding of masculine vs feminine strength",
                        "Understanding of mature feminine power",
                        "Insight into loss of intuition",
                        "Perspectives on softness and courage",
                        "Concrete reflections for career & personal life"
                      ] : [
                        "Forståelse for maskulin vs. feminin styrke",
                        "Forståelse for den modne feminine kraft",
                        "Indsigt i tabet af intuition",
                        "Perspektiver på blødhed og mod",
                        "Konkrete refleksioner til arbejdsliv & privatliv"
                      ]).map((bullet, bIdx) => (
                        <li key={bIdx} className="flex items-start text-xs text-stone-600 font-light">
                          <i className="fa-light fa-sparkles text-[#817262] mt-0.5 mr-2 flex-shrink-0" />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-stone-50 p-4 border border-stone-100 rounded-sm space-y-2">
                    <p className="text-xs text-stone-700 font-light leading-relaxed">
                      <strong className="font-serif italic text-stone-900 block">
                        {isEn ? "Your outcome:" : "Dit udbytte:"}
                      </strong>
                      {isEn ? "A stronger understanding of your own power and a vision of the era ahead, and why mature feminine energy must be activated now. In both women and men." : "En stærkere forståelse af din egen kraft og et billede af, hvad det er for en tid, vi står over for, og hvorfor den modne feminine kraft skal aktiveres nu. Både i kvinder og mænd."}
                    </p>
                  </div>
                </div>
                
                <div className="pt-6 mt-8 border-t border-stone-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="text-stone-900">
                    <span className="text-[9px] uppercase tracking-widest text-stone-400 block font-bold">
                      {isEn ? "Price" : "Pris"}
                    </span>
                    <span className="text-sm font-serif font-semibold">
                      {isEn ? "Network: 7,500 DKK / Business: 9,500 DKK" : "Netværk: 7.500 kr. / Virksomhed: 9.500 kr."}
                    </span>
                  </div>
                  <Link 
                    to={isEn ? "/en/contact?subject=Keynote: Power Woman - know your power" : "/kontakt?subject=Foredrag: Powerkvinde - kend din power"} 
                    className="bg-[#817262] text-white px-6 py-3 uppercase text-[10px] tracking-widest font-bold hover:bg-stone-800 transition-colors rounded-sm text-center"
                  >
                    {isEn ? "Contact me about the keynote" : "Kontakt mig om foredraget"}
                  </Link>
                </div>
              </div>
            </div>
          </div>
          </div>
          </div>
        </div>
      </section>

      {/* ØVRIGE WORKSHOPS SECTION */}
      <section className="py-32 bg-white px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-20">
            <span className="text-xs uppercase tracking-[0.4em] font-bold text-stone-400 mb-4 block">
              {isEn ? "Immersion & Transformation" : "Fordybelse & Transformation"}
            </span>
            <h2 className="text-4xl md:text-5xl font-serif text-stone-900 mb-6">
              {isEn ? "Other Workshops for Individuals" : "Øvrige Workshops for Private"}
            </h2>
            <p className="text-stone-500 font-light text-lg leading-relaxed">
              {isEn ? "My other workshops are designed to create deep, tangible change in body, mind, and energy within specific themes." : "Mine øvrige workshops er designet til at skabe dyb, mærkbar forandring i både krop, sind og energi inden for specifikke temaer."}
            </p>
          </div>

          {/* Workshops List */}
          <div className="space-y-24">
            {privateWorkshops.map((w, idx) => (
              <FadeIn key={idx} delay={idx * 100} className="bg-stone-50/50 border border-stone-100 p-8 md:p-16 rounded-sm shadow-sm hover:shadow-md transition-all">
                <div className="grid lg:grid-cols-12 gap-12 items-start">
                  <div className="lg:col-span-7 space-y-6">
                    <span className="text-xs uppercase tracking-[0.3em] font-bold text-stone-400">
                      {isEn ? `Workshop ${idx + 1}` : `Workshop ${idx + 1}`}
                    </span>
                    <h3 className="text-3xl md:text-4xl font-serif text-stone-900 leading-tight">{w.title}</h3>
                    <p className="text-lg font-serif italic text-stone-800 leading-relaxed">{w.subtitle}</p>
                    <div className="h-px w-16 bg-stone-200" />
                    <div className="text-stone-600 font-light text-base leading-relaxed whitespace-pre-wrap space-y-4">
                      {w.description.split('\n\n').map((para, pIdx) => (
                        <p key={pIdx}>{para}</p>
                      ))}
                    </div>
                    
                    {w.outcome && (
                      <div className="bg-emerald-950/5 p-6 border-l-2 border-[#817262] rounded-r-sm mt-8">
                        <p className="text-stone-800 font-light text-base leading-relaxed">
                          <strong className="font-serif italic text-stone-900 block mb-1">
                            {isEn ? "Your outcome:" : "Dit udbytte:"}
                          </strong>
                          {w.outcome}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className="lg:col-span-5 bg-white p-8 border border-stone-100 shadow-sm rounded-sm space-y-6">
                    {w.imageUrl && !w.videoUrl && (
                      <div className="overflow-hidden rounded-md shadow-sm aspect-video">
                        <img src={w.imageUrl} alt={w.title} className="w-full h-full object-cover" />
                      </div>
                    )}
                    {w.videoUrl && (
                      <YouTubeEmbed url={w.videoUrl} title={w.title} className="w-full max-w-full" />
                    )}
                    <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-stone-900 border-b border-stone-100 pb-4">
                      {isEn ? "You get among other things:" : "Du får blandt andet:"}
                    </h4>
                    <ul className="space-y-4">
                      {w.bullets.map((bullet, bIdx) => (
                        <li key={bIdx} className="flex items-start text-stone-600 font-light text-sm leading-relaxed">
                          <i className="fa-light fa-sparkles text-[#817262] mt-1 mr-3 flex-shrink-0" />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="pt-6 border-t border-stone-100">
                      <Link 
                        to={isEn ? `/en/contact?subject=Workshop: ${w.title}` : `/kontakt?subject=Workshop: ${w.title}`} 
                        className="block w-full text-center bg-[#817262] text-white py-3.5 uppercase text-xs tracking-[0.2em] font-bold hover:bg-stone-800 transition-colors rounded-sm"
                      >
                        {isEn ? "Contact me or learn more" : "Kontakt mig eller hør mere"}
                      </Link>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Dark Split-Feature Block */}
      <section className="grid grid-cols-1 md:grid-cols-2 bg-stone-900 text-stone-100">
        <div className="relative h-[50vh] md:h-auto min-h-[400px]">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/mettebrudvigokt2021-52.jpg" 
            alt="Kontakt Mette - Virksomhed, forening eller organisation" 
            className="absolute inset-0 w-full h-full object-cover opacity-80"
          />
          <div className="absolute bottom-8 left-8 z-10">
            <Link to={lang === 'en' ? "/en/keynotes/businesses" : "/foredrag/virksomheder"} className="text-sm uppercase tracking-widest font-bold underline underline-offset-4 hover:text-stone-300 transition-colors">
              {lang === 'en' ? "See offerings for businesses →" : "Se tilbud til virksomheder →"}
            </Link>
          </div>
        </div>
        <div className="flex flex-col justify-center px-8 md:px-16 lg:px-24 py-24">
          <FadeIn>
            <h3 className="text-3xl md:text-4xl font-serif leading-tight mb-8">
              {lang === 'en'
                ? "Looking for keynotes or workshops for your organization?"
                : "Søger du noget til din virksomhed, forening eller organisation?"}
            </h3>
            <h3 className="text-3xl md:text-4xl font-serif leading-tight mb-12 text-stone-400">
              {lang === 'en'
                ? "I offer tailored keynotes and workshops on future consciousness, authentic communication, and conscious leadership."
                : "Jeg tilbyder skræddersyede forløb, foredrag og workshops om fremtidsforståelse, troværdighed og bevidst ledelse."}
            </h3>
            <div className="flex flex-wrap gap-4">
              <Link to={lang === 'en' ? "/en/keynotes/businesses" : "/foredrag/virksomheder"} className="inline-block bg-white text-stone-900 px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-stone-100 transition-all">
                {lang === 'en' ? "See business page" : "Se virksomhedssiden"}
              </Link>
              <Link to={lang === 'en' ? "/en/contact" : "/kontakt"} className="inline-block border border-stone-100 px-8 py-4 text-xs uppercase tracking-[0.2em] hover:bg-stone-100 hover:text-stone-900 transition-all duration-300">
                {lang === 'en' ? "Contact Mette" : "Kontakt Mette"}
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

const getCorporateWorkshops = (isEn, cmsVirksomheder = []) => {
  const defaultList = isEn ? [
    {
      id: "spring-ind-i-fremtiden",
      num: 1,
      slug: "/en/keynotes/businesses/leap-into-the-future",
      slugDa: "/virksomheder/spring-ind-i-fremtiden",
      slugEn: "/en/keynotes/businesses/leap-into-the-future",
      title: "Leap Into the Future",
      subtitle: "Prepare for the future and harvest benefits for both the individual and the organization.",
      videoUrl: "https://youtube.com/shorts/gF-xTMMBE2E?feature=share",
      description: "We are in the midst of an evolutionary shift. Old ways of thinking, working, living, and leading no longer fit the generations forming our future customers, employees, and community members.\n\nHere you gain deep insights into the movement from tribal to planetary consciousness: moving beyond 'us vs them' toward an interconnected awareness of nature, humanity, and our shared world.\n\nYou receive reflections, practices, and tools to understand what the times call for — and how to step forward with clarity, consciousness, and courage in this new era.\n\nThis workshop is for teams that feel the future is not merely about technology and finances, but about awareness, responsibility, humanity, and courage.",
      bullets: [
        "Insights into the shift in consciousness we are experiencing",
        "Understanding what this means for you, your clients, and society",
        "Practices helping you envision your future leadership version",
        "Inspiration to live and lead with conscious presence",
        "Reflections on what to release and what to step into"
      ],
      outcome: "You leave with a broader perspective on the future and clear insights on how to gain an authentic head start."
    },
    {
      id: "ny-energi-i-teamet",
      num: 2,
      slug: "/en/keynotes/businesses/new-energy-in-the-team",
      slugDa: "/virksomheder/ny-energi-i-teamet",
      slugEn: "/en/keynotes/businesses/new-energy-in-the-team",
      title: "New Energy in the Team",
      subtitle: "Foster well-being and elevated performance by cultivating higher collective energy.",
      imageUrl: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/mettebrudvigokt2021-52.jpg",
      description: "A team is not made of roles, tasks, meetings, and KPIs alone. Every team member also carries energy that directly shapes the overall atmosphere and collective well-being at work.\n\nEveryone knows the feeling of stepping into a room and instantly sensing lightness, resistance, trust, or tension. It directly impacts collaboration, creativity, and decisions.\n\nThis workshop is for teams seeking greater well-being, clarity, and shared direction. We work with the energy between people and how to lift it in a practical, grounded way.\n\nIt is not about forced positivity; it is about building a conscious culture where people feel safe enough to contribute, take responsibility, and bring their true strengths.",
      bullets: [
        "Understanding energy dynamics, relationships, and collective frequency in teams",
        "Strategies to take ownership of personal energy under pressure",
        "Insights into what drains a team vs what elevates it",
        "Practical tools for clearer collaboration and communication",
        "A shared experience inspiring renewed momentum and focus"
      ],
      outcome: "You leave with elevated awareness of your shared atmosphere and actionable tools for daily well-being and performance."
    },
    {
      id: "fremstaa-trovaerdig",
      num: 3,
      slug: "/en/keynotes/businesses/communicate-with-authenticity",
      slugDa: "/virksomheder/fremstaa-trovaerdig",
      slugEn: "/en/keynotes/businesses/communicate-with-authenticity",
      title: "Communicate with Authenticity & Trust",
      subtitle: "Learn clear, aligned communication so people naturally follow your lead.",
      videoUrl: "https://youtube.com/shorts/DO0nH6ZNGvo?feature=share",
      description: "Human beings respond to both words and subtle energy signals. To communicate with true credibility, your spoken message must align with your energetic resonance. Intuition picks up discrepancies immediately.\n\nWhen there are mixed signals from stress, defense, or hidden agendas, trust weakens. When your words, values, and presence align, you project effortless authenticity and trust.\n\nIn this workshop, you learn to understand your own presence and its impact. You learn to recognize when you transmit with clarity and when noise distorts your message.\n\nIdeal for leaders, executives, educators, advisors, and anyone communicating key decisions and building trust.",
      bullets: [
        "Clear understanding of how presence impacts credibility",
        "Recognizing signals of clarity vs noise in communication",
        "Practices to feel and regulate your internal energy state",
        "Tools to enhance calm, authenticity, and leadership impact",
        "A guided meditation raising frequency (which you take home)",
        "Understanding quantum physics principles behind interpersonal resonance"
      ],
      outcome: "You leave with a stronger connection to your inner presence and practical tools to communicate with credibility and authenticity."
    },
    {
      id: "aktiver-hjertets-intelligens",
      num: 4,
      slug: "/en/keynotes/businesses/activate-heart-intelligence",
      slugDa: "/virksomheder/aktiver-hjertets-intelligens",
      slugEn: "/en/keynotes/businesses/activate-heart-intelligence",
      title: "Activate Heart Intelligence in the Organization",
      subtitle: "Strengthen employees' ability to connect with their intuition, make clear decisions, and contribute with presence.",
      imageUrl: "/kundebilleder/Mette+som+underviser.jpg",
      description: "Many individuals navigate primarily through pressure, roles, speed, and outer expectations. Over time, this creates distance from personal values, intuition, and healthy boundaries.\n\nHeart intelligence is the deep inner wisdom that helps us know who we are, what we stand for, and how to act with integrity. When organizations activate this resource, both individual well-being and organizational resilience grow.\n\nIn this workshop, we bridge thoughts, emotions, values, energy, and action. Participants gain awareness of what shapes their decision-making and collaboration, enabling them to stand grounded in their role.\n\nThrough reflection, energetic exercises, and meditation, participants learn to quiet outer noise and access authentic clarity.",
      bullets: [
        "Understanding heart intelligence and its professional applications",
        "Elevated awareness of core values, boundaries, and internal signals",
        "Practices strengthening presence, intuition, and mental clarity",
        "Tools for authentic, responsible decision-making",
        "Insights into how internal state impacts teamwork and leadership",
        "A shared language for authenticity, human responsibility, and conscious leadership"
      ],
      outcome: "Your team leaves with deep awareness of how heart intelligence strengthens individual well-being, teamwork, and organizational culture."
    }
  ] : [
    {
      id: "spring-ind-i-fremtiden",
      num: 1,
      slug: "/virksomheder/spring-ind-i-fremtiden",
      slugDa: "/virksomheder/spring-ind-i-fremtiden",
      slugEn: "/en/keynotes/businesses/leap-into-the-future",
      title: "Spring ind i fremtiden",
      subtitle: "Bliv klar til fremtiden og høst alle de gunstige fordele – for både individet og organisationen.",
      videoUrl: "https://youtube.com/shorts/gF-xTMMBE2E?feature=share",
      description: "Vi står midt i et evolutionært skifte. De gamle måder at tænke, arbejde, leve og lede på passer ikke til dem, der udgør de næste generationer af kunder, medarbejdere, elever og medlemmer.\n\nHer bliver du klogere på bevægelsen fra det tribale til det planetariske: fra “os og dem” til en større bevidsthed om, at vi er forbundet med hinanden, naturen og den verden, vi er en del af.\n\nDu får viden, refleksioner og konkrete øvelser, der hjælper dig med at forstå, hvad tiden kalder på — og hvordan du selv bliver mere klar, bevidst og handlekraftig i den nye virkelighed.\n\nWorkshoppen er for jer, der mærker, at fremtiden ikke kun handler om teknologi, økonomi og systemer, men også om bevidsthed, ansvar, menneskelighed og mod.",
      bullets: [
        "Indblik i det store bevidsthedsskifte, vi står i",
        "Forståelse for, hvad det betyder for dig, dine kunder, dine relationer og samfundet",
        "Øvelser, der hjælper dig med at se fremtidsversionen af dig",
        "Inspiration til leve og lede mere bevidst",
        "Konkrete refleksioner over, hvad du skal slippe, og hvad du skal træde ind i – for vi skal ikke have mere, men bedre."
      ],
      outcome: "Du går derfra med en større forståelse af fremtiden og en klarere fornemmelse af, hvordan du selv er en del af den og kan få et forspring."
    },
    {
      id: "ny-energi-i-teamet",
      num: 2,
      slug: "/virksomheder/ny-energi-i-teamet",
      slugDa: "/virksomheder/ny-energi-i-teamet",
      slugEn: "/en/keynotes/businesses/new-energy-in-the-team",
      title: "Ny energi i teamet",
      subtitle: "Skab trivsel og performance ved at skabe en højere energi.",
      imageUrl: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/mettebrudvigokt2021-52.jpg",
      description: "Et team er ikke kun roller, opgaver, møder og mål. Hvert medlem af teamet er også energi, og den energi har stor betydning for den samlede stemning og kollektive trivsel på arbejdspladsen.\n\nAlle kender følelsen af at træde ind i et rum og mærke, om der er lethed, modstand, tillid eller uro. Det påvirker samarbejdet, kreativiteten, beslutningerne og den måde, vi er sammen på.\n\nDenne workshop er for teams, der ønsker mere trivsel, klarhed og fælles retning. Vi arbejder med den energi, der ligger mellem mennesker, og med hvordan I kan løfte den på en konkret og praktisk måde.\n\nDet handler ikke om at være enige om alt eller gå rundt og være positive hele tiden. Det handler om at skabe et mere bevidst fællesskab, hvor mennesker føler sig trygge nok til at bidrage, tage ansvar og bruge deres styrker.",
      bullets: [
        "Viden om energi, relationer og fælles frekvens i et team",
        "Øvelser, så I skaber strategi for at tage ansvar for jeres egen energi – også når det spidser til",
        "Indblik i, hvad der dræner teamet, og hvad der løfter det",
        "Konkrete redskaber til bedre samarbejde og kommunikation",
        "En fælles oplevelse, der kan skabe ny energi og retning"
      ],
      outcome: "I går derfra med større bevidsthed om jeres fælles energi og konkrete greb til at skabe mere trivsel, klarhed og performance i hverdagen."
    },
    {
      id: "fremstaa-trovaerdig",
      num: 3,
      slug: "/virksomheder/fremstaa-trovaerdig",
      slugDa: "/virksomheder/fremstaa-trovaerdig",
      slugEn: "/en/keynotes/businesses/communicate-with-authenticity",
      title: "Fremstå troværdig",
      subtitle: "Lær at kommunikere rent, så mennesker ikke bare bakker op, men naturally følger dig",
      videoUrl: "https://youtube.com/shorts/DO0nH6ZNGvo?feature=share",
      description: "Du og dine medmennesker er elektromagnetiske væsener, og for at fremstå troværdig skal der være synergi mellem det, du siger, og den energi, du vibrerer med. Din modtagers intuition mærker det, hvis der ikke er.\n\nDeres system mærker på din energi, om der er ærlighed og autenticitet, eller om du vibrerer med signalfejl fra usikkerhed, pres, forsvar eller skjulte dagsordener.\n\nPå denne workshop lærer du at forstå din egen energi, og hvordan den påvirker andre. Du lærer at mærke, hvornår du sender rent, og hvornår der er støj på linjen mellem det, du siger, og den energi, du faktisk vibrerer med.\n\nAutenticitet er nøglen.\n\nNår dine ord, dine værdier og din energi er i resonans, fremstår du troværdig og vækker ægte tillid. Ikke fordi du forsøger at overbevise nogen, men fordi mennesker kan mærke, at der er sammenhæng mellem det, du siger, og den du er.\n\nWorkshoppen er særligt egnet til ledere, politikere, undervisere, rådgivere og andre, der skal kommunikere klart, skabe tillid og få mennesker med sig.",
      bullets: [
        "En konkret forståelse af, hvordan din energi påvirker din troværdighed",
        "Indblik i, hvornår du sender rent, og hvornår der opstår signalfejl",
        "Øvelser, der hjælper dig med at mærke din egen energi tydeligere",
        "Redskaber til at skabe større ro, autenticitet og gennemslagskraft",
        "En guidet meditation, der hæver din frekvens (den får du også med hjem)",
        "Forståelse for, hvorfor mennesker responderer på mere end dine ord (kvantefysik)",
        "Træning i at skabe resonans mellem det, du siger, og den energi, du udsender"
      ],
      outcome: "Du går derfra med en stærkere fornemmelse af din egen energi og konkrete redskaber til at fremstå mere troværdig, tillidsvækkende og autentisk."
    },
    {
      id: "aktiver-hjertets-intelligens",
      num: 4,
      slug: "/virksomheder/aktiver-hjertets-intelligens",
      slugDa: "/virksomheder/aktiver-hjertets-intelligens",
      slugEn: "/en/keynotes/businesses/activate-heart-intelligence",
      title: "Aktivér hjertets intelligens i organisationen",
      subtitle: "Styrk medarbejdernes evne til at mærke sig selv, træffe klarere beslutninger og bidrage med større autenticitet, ansvar og menneskeligt nærvær.",
      imageUrl: "/kundebilleder/Mette+som+underviser.jpg",
      description: "Mange mennesker har lært at navigere ud fra krav, roller, tempo og forventninger. Det kan skabe afstand til egne værdier, intuition og grænser og føre til usikkerhed, overbelastning og beslutninger, der ikke opleves som rigtige.\n\nHjertets intelligens er den dybere viden i os, som hjælper os med at mærke, hvem vi er, hvad vi står for, og hvordan vi kan handle med integritet. Når mennesker får bedre adgang til den, styrkes både deres personlige fundament og deres evne til at indgå konstruktivt i organisationen.\n\nPå denne workshop arbejder vi med at skabe større forbindelse mellem tanker, følelser, værdier, energi og handlinger. Deltagerne bliver mere bevidste om, hvad der påvirker deres beslutninger, kommunikation og samarbejde, og hvordan de kan stå tydeligere og mere autentisk i deres rolle.\n\nVi arbejder med refleksion, energiøvelser og meditation, så deltagerne kan mærke deres egne signaler, skabe mere indre ro og få adgang til den klarhed, der opstår, når de ikke kun handler ud fra pres, vaner og ydre forventninger.\n\nKort sagt arbejder vi med at styrke den indre forbindelse, som gør mennesker bedre i stand til at lede sig selv, samarbejde med andre og træffe ansvarlige beslutninger.",
      bullets: [
        "En forståelse af, hvad hjertets intelligens er, og hvordan den kan anvendes professionelt",
        "Større bevidsthed om egne værdier, grænser og indre signaler",
        "Øvelser, der styrker nærvær, intuition og indre klarhed",
        "Redskaber til at træffe mere autentiske og ansvarlige beslutninger",
        "Indsigt i, hvordan energi og indre tilstand påvirker kommunikation og samarbejde",
        "En guidet meditation, der skaber ro og styrker forbindelsen til den indre retning",
        "Et fælles sprog for autenticitet, menneskeligt ansvar og bevidst ledelse",
        "Inspiration til at skabe en organisation med mere hjerte, sjæl og bæredygtighed"
      ],
      outcome: "I går derfra med større bevidsthed om, hvordan hjertets intelligens kan styrke den enkelte medarbejder, samarbejdet og organisationens menneskelige fundament."
    }
  ];

  if (!cmsVirksomheder || cmsVirksomheder.length === 0) {
    return defaultList;
  }

  return defaultList.map(item => {
    const cmsItem = cmsVirksomheder.find(cv => {
      const cvSlug = cv.url_slug?.da || cv.url_slug?.en || cv.url_slug;
      return cvSlug === item.id || cv.id === item.id;
    });

    if (!cmsItem) return item;

    const title = isEn 
      ? (cmsItem.titel?.en || cmsItem.titel?.da || item.title)
      : (cmsItem.titel?.da || cmsItem.titel?.en || item.title);

    const subtitle = isEn
      ? (cmsItem.undertitel?.en || cmsItem.undertitel?.da || item.subtitle)
      : (cmsItem.undertitel?.da || cmsItem.undertitel?.en || item.subtitle);

    const desc = isEn
      ? (cmsItem.indhold?.en ? cmsItem.indhold.en.replace(/<[^>]+>/g, '\n\n').trim() : (cmsItem.kort_beskrivelse?.en || item.description))
      : (cmsItem.indhold?.da ? cmsItem.indhold.da.replace(/<[^>]+>/g, '\n\n').trim() : (cmsItem.kort_beskrivelse?.da || item.description));

    const image = (cmsItem.billede && cmsItem.billede[0]) || item.imageUrl;

    return {
      ...item,
      title: title || item.title,
      subtitle: subtitle || item.subtitle,
      description: desc || item.description,
      imageUrl: image || item.imageUrl
    };
  });
};

const ForedragVirksomhederPage = () => {
  const { lang, t } = useLanguage();
  const { virksomheder } = useCms();

  useEffect(() => {
    document.title = lang === 'en' ? "Mette Brudvig - Keynotes & Workshops for Businesses" : "Mette Brudvig - Foredrag & Workshops til Virksomheder";
    window.scrollTo(0, 0);
  }, [lang]);

  const isEn = lang === 'en';
  const corporateWorkshops = getCorporateWorkshops(isEn, virksomheder);

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] md:min-h-[80vh] pt-[185px] md:pt-[215px] lg:pt-[225px] pb-16 flex items-center bg-[#f5efe9] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center z-10 w-full">
          <FadeIn direction="right">
            <span className="text-xs uppercase tracking-[0.4em] font-bold text-[#817262] mb-4 block">
              {t('foredragPage.badge')}
            </span>
            <h1 className="text-4xl md:text-6xl font-serif text-stone-900 leading-[1.1] mb-8">
              {lang === 'en' ? "For Businesses" : "Til Virksomheder"}
            </h1>
            <p className="text-xl text-stone-600 font-light leading-relaxed max-w-lg mb-8">
              {lang === 'en'
                ? "Bring heart intelligence, future consciousness, and authentic communication into corporate leadership, workplace culture, and organizational development."
                : "Bring hjertets intelligens, fremtidsforståelse og autentisk kommunikation ind i virksomhedsledelse, arbejdskultur og organisationsudvikling."}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <a 
                href="#workshops" 
                className="bg-[#004f3b] text-white px-7 py-3.5 text-xs uppercase tracking-[0.2em] font-bold hover:bg-emerald-900 transition-all rounded-sm shadow-sm"
              >
                {lang === 'en' ? "See Workshops" : "Se Workshops"}
              </a>
              <a 
                href="tel:+4527507083" 
                className="inline-flex items-center gap-2.5 bg-white border border-[#817262] text-[#817262] hover:bg-[#817262] hover:text-white px-7 py-3.5 text-xs uppercase tracking-[0.2em] font-bold transition-all rounded-sm shadow-sm"
              >
                <i className="fa-solid fa-phone text-xs" />
                <span>{lang === 'en' ? "Call me: 27 50 70 83" : "Ring til mig: 27 50 70 83"}</span>
              </a>
            </div>
          </FadeIn>
        </div>
        <div className="absolute top-0 right-0 w-full md:w-1/2 h-full opacity-20 md:opacity-100 pt-20 md:pt-24 lg:pt-28 pointer-events-none">
          <img 
            src="/kundebilleder/Mette+som+underviser.jpg" 
            alt="Mette Brudvig Foredrag til Virksomheder" 
            className="w-full h-full object-cover object-top pointer-events-none"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#f5efe9] via-transparent to-transparent hidden md:block pointer-events-none" />
        </div>
      </section>

      {/* Intro Statement */}
      <section className="py-24 bg-white px-6">
        <div className="max-w-4xl mx-auto text-center">
          <FadeIn>
            <p className="text-2xl md:text-3xl font-serif text-stone-800 leading-relaxed italic">
              {lang === 'en'
                ? '"We need to bring heart intelligence and future consciousness into business leadership."'
                : '"Vi skal have hjertets intelligens og fremtidsforståelse med ind i virksomhedsledelse."'}
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Detailed Section */}
      <section className="py-32 bg-stone-50 px-6 border-t border-b border-stone-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <FadeIn direction="right" className="order-2 lg:order-1">
              <span className="text-xs uppercase tracking-[0.4em] font-bold text-stone-400 mb-6 block">
                {lang === 'en' ? "New Era Leadership" : "Ny tids lederskab"}
              </span>
              <h2 className="text-4xl md:text-5xl font-serif text-stone-900 mb-8">
                {lang === 'en' ? "Future Consciousness & Elevation" : "Fremtidsforståelse & Bevidsthed"}
              </h2>
              <div className="space-y-8 text-stone-600 font-light text-lg leading-relaxed">
                <p className="text-[#004f3b] font-serif italic text-xl md:text-2xl leading-relaxed">
                  {isEn
                    ? "Traditional leadership models no longer fulfill new generations."
                    : "De gamle måder at lede på passer ikke til de nye generationer."}
                </p>
                <p>
                  {isEn
                    ? "We stand in the middle of an evolutionary paradigm shift. Upcoming generations of clients, leaders, and employees seek consciousness, deep meaning, and radical authenticity."
                    : "Vi står midt i et evolutionært skifte. De næste generationer af kunder, medarbejdere og medlemmer søger bevidsthed, mening og autenticitet."}
                </p>
                <p>
                  {isEn
                    ? "When we integrate heart intelligence and future consciousness into corporate leadership and work culture, we cultivate organizations that not only perform, but thrive, innovate, and honor holistic responsibility. It is a shift from 'us vs them' to interconnected leadership."
                    : "Når vi bringer hjertets intelligens og fremtidsforståelse ind i virksomhedsledelse og arbejdskultur, skaber vi organisationer, der ikke bare præsterer, men som trives, innoverer og tager ansvar for helheden. Det handler om at gå fra ‘os og dem’ til en dybere forbundethed og fornemmelse af ansvar."}
                </p>
              </div>

              <TestimonialCallout 
                testimonialName="Marianne Helleskov" 
                customTitle={isEn ? "Executive & Corporate Testimonial" : "Udtalelse om Erhvervsforløb & Intuition"} 
                className="mt-8 mb-0"
              />
            </FadeIn>
            <FadeIn direction="left" className="order-1 lg:order-2">
              <div className="relative p-4 border border-stone-200 bg-white">
                <img 
                  src="/kundebilleder/MetteBrudvigOkt2021-125.jpg" 
                  alt="Mette Brudvig Undervisning" 
                  className="w-full aspect-[4/5] object-cover"
                />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Workshops Section */}
      <section id="workshops" className="py-32 bg-white px-6 scroll-mt-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-20">
            <span className="text-xs uppercase tracking-[0.4em] font-bold text-stone-400 mb-4 block">
              {isEn ? "Consciousness & Transformation" : "Bevidsthed & Transformation"}
            </span>
            <h2 className="text-4xl md:text-5xl font-serif text-stone-900 mb-6">
              {isEn ? "Workshops for Businesses" : "Workshops for Virksomheder"}
            </h2>
            <p className="text-stone-500 font-light text-lg leading-relaxed">
              {isEn
                ? "Tailored keynotes and workshops equipping your organization for tomorrow's shift in consciousness."
                : "Skræddersyede forløb og workshops, der ruster jeres organisation til fremtidens bevidsthedsskifte."}
            </p>
          </div>

          {/* Workshops List */}
          <div className="space-y-24">
            {corporateWorkshops.map((w, idx) => (
              <FadeIn key={w.id} delay={idx * 100} className="bg-stone-50/50 border border-stone-100 p-8 md:p-16 rounded-sm shadow-sm hover:shadow-md transition-all scroll-mt-28" id={w.id}>
                <div className="grid lg:grid-cols-12 gap-12 items-start">
                  <div className="lg:col-span-7 space-y-6">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#817262]">Workshop {idx + 1}</span>
                      <Link 
                        to={w.slug} 
                        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold text-[#004f3b] hover:text-emerald-950 underline underline-offset-4 transition-colors"
                      >
                        <span>{isEn ? "View dedicated page" : "Gå til dedikeret underside"}</span>
                        <i className="fa-solid fa-arrow-right text-[10px]" />
                      </Link>
                    </div>
                    <h3 className="text-3xl md:text-4xl font-serif text-stone-900 leading-tight">
                      <Link to={w.slug} className="hover:text-[#004f3b] transition-colors">
                        {w.title}
                      </Link>
                    </h3>
                    <p className="text-lg font-serif italic text-stone-800 leading-relaxed">{w.subtitle}</p>
                    <div className="h-px w-16 bg-stone-200" />
                    <div className="text-stone-600 font-light text-base leading-relaxed whitespace-pre-wrap space-y-4">
                      {w.description.split('\n\n').map((para, pIdx) => (
                        <p key={pIdx}>{para}</p>
                      ))}
                    </div>
                    
                    {w.outcome && (
                      <div className="bg-emerald-950/5 p-6 border-l-2 border-[#004f3b] rounded-r-sm mt-8">
                        <p className="text-stone-800 font-light text-base leading-relaxed">
                          <strong className="font-serif italic text-stone-900 block mb-1">
                            {isEn ? "Your Outcome:" : "Dit udbytte:"}
                          </strong>
                          {w.outcome}
                        </p>
                      </div>
                    )}

                    <div className="pt-4 flex flex-wrap items-center gap-4">
                      <Link 
                        to={w.slug} 
                        className="inline-flex items-center gap-2 bg-[#004f3b] text-white px-6 py-3.5 text-xs uppercase tracking-[0.2em] font-bold hover:bg-emerald-900 transition-all rounded-sm shadow-xs"
                      >
                        <span>{isEn ? "Read more about the workshop" : "Læs mere om workshoppen"}</span>
                        <i className="fa-solid fa-arrow-right text-xs" />
                      </Link>
                      <a 
                        href="tel:+4527507083" 
                        className="inline-flex items-center gap-2 bg-white border border-[#817262] text-[#817262] hover:bg-[#817262] hover:text-white px-6 py-3.5 text-xs uppercase tracking-[0.2em] font-bold transition-all rounded-sm shadow-xs"
                      >
                        <i className="fa-solid fa-phone text-xs" />
                        <span>{isEn ? "Call me: 27 50 70 83" : "Ring: 27 50 70 83"}</span>
                      </a>
                    </div>
                  </div>
                  
                  <div className="lg:col-span-5 bg-white p-8 border border-stone-100 shadow-sm rounded-sm space-y-6">
                    {w.imageUrl && !w.videoUrl && (
                      <div className="overflow-hidden rounded-md shadow-sm aspect-video">
                        <img src={w.imageUrl} alt={w.title} className="w-full h-full object-cover" />
                      </div>
                    )}
                    {w.videoUrl && (
                      <YouTubeEmbed url={w.videoUrl} title={w.title} className="w-full max-w-full" />
                    )}
                    <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-stone-900 border-b border-stone-100 pb-4">
                      {isEn ? "What you receive:" : "I får blandt andet:"}
                    </h4>
                    <ul className="space-y-4">
                      {w.bullets.map((bullet, bIdx) => (
                        <li key={bIdx} className="flex items-start text-stone-600 font-light text-sm leading-relaxed">
                          <i className="fa-light fa-sparkles text-emerald-700 mt-1 mr-3 flex-shrink-0" />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="pt-6 border-t border-stone-100 space-y-3">
                      <Link 
                        to={w.slug} 
                        className="block w-full text-center bg-[#004f3b] text-[#faf9f6] py-3.5 uppercase text-xs tracking-[0.2em] font-bold hover:bg-emerald-900 transition-colors rounded-sm shadow-xs"
                      >
                        {isEn ? "Read full workshop details" : "Læs alle detaljer om workshoppen"}
                      </Link>
                      <Link 
                        to={isEn ? "/en/contact" : "/kontakt"} 
                        className="block w-full text-center bg-stone-100 text-stone-800 py-3 uppercase text-xs tracking-[0.2em] font-bold hover:bg-stone-200 transition-colors rounded-sm"
                      >
                        {isEn ? "Contact me for booking" : "Kontakt mig for booking"}
                      </Link>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Dark Split-Feature Block */}
      <section className="grid grid-cols-1 md:grid-cols-2 bg-stone-900 text-stone-100">
        <div className="relative h-[50vh] md:h-auto min-h-[400px]">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/mettebrudvigokt2021-52.jpg" 
            alt="Kontakt Mette Brudvig" 
            className="absolute inset-0 w-full h-full object-cover object-top opacity-80"
          />
          <div className="absolute bottom-8 left-8 z-10">
            <Link to={isEn ? "/en/keynotes/private" : "/foredrag/privat"} className="text-sm uppercase tracking-widest font-bold underline underline-offset-4 hover:text-stone-300 transition-colors">
              {isEn ? "See offerings for individuals →" : "Se tilbud til private →"}
            </Link>
          </div>
        </div>
        <div className="flex flex-col justify-center px-8 md:px-16 lg:px-24 py-24">
          <FadeIn>
            <h3 className="text-3xl md:text-4xl font-serif leading-tight mb-8">
              {isEn 
                ? "Want to learn more about how we can tailor a program for your organization?"
                : "Vil du høre mere om, hvordan vi kan skræddersy et forløb til din virksomhed?"}
            </h3>
            <h3 className="text-3xl md:text-4xl font-serif leading-tight mb-12 text-stone-400">
              {isEn
                ? "Contact me for an informal conversation about your goals and how we best bring consciousness and future insight to life for your team."
                : "Kontakt mig for en uforpligtende samtale om jeres behov, og hvordan vi bedst bringer bevidsthed og fremtidsforståelse i spil hos jer."}
            </h3>
            <div className="flex flex-wrap gap-4">
              <Link to={isEn ? "/en/contact" : "/kontakt"} className="inline-block bg-white text-stone-900 px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-stone-100 transition-all">
                {isEn ? "Contact Mette" : "Kontakt Mette"}
              </Link>
              <a 
                href="tel:+4527507083" 
                className="inline-flex items-center gap-2 bg-[#817262] text-white px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#6e6153] transition-all"
              >
                <i className="fa-solid fa-phone text-xs" />
                <span>{isEn ? "Call me: 27 50 70 83" : "Ring til mig: 27 50 70 83"}</span>
              </a>
              <Link to={isEn ? "/en/keynotes/private" : "/foredrag/privat"} className="inline-block border border-stone-100 px-8 py-4 text-xs uppercase tracking-[0.2em] hover:bg-stone-100 hover:text-stone-900 transition-all duration-300">
                {isEn ? "See private workshops" : "Se private workshops"}
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

// Dedicated Workshop Page Component for Business Workshops
const VirksomhedWorkshopDetailPage = ({ workshopId }) => {
  const { lang, t } = useLanguage();
  const { virksomheder } = useCms();
  const isEn = lang === 'en';
  const allWorkshops = getCorporateWorkshops(isEn, virksomheder);
  const currentWorkshop = allWorkshops.find(w => w.id === workshopId) || allWorkshops[0];
  const otherWorkshops = allWorkshops.filter(w => w.id !== currentWorkshop.id);

  useEffect(() => {
    document.title = `${currentWorkshop.title} | ${isEn ? "Business Workshop | Mette Brudvig" : "Virksomhedsworkshop | Mette Brudvig"}`;
    window.scrollTo(0, 0);
  }, [currentWorkshop.title, isEn]);

  const overviewPath = isEn ? "/en/keynotes/businesses" : "/foredrag/virksomheder";
  const contactPath = isEn ? "/en/contact" : "/kontakt";

  return (
    <div className="bg-[#faf9f6] min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] md:min-h-[70vh] pt-[185px] md:pt-[215px] lg:pt-[225px] pb-14 flex items-center bg-[#f5efe9] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center z-10 w-full">
          <FadeIn direction="right" className="lg:col-span-7">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#817262] mb-4 flex-wrap">
              <Link to={overviewPath} className="hover:underline font-bold">
                {isEn ? "Businesses" : "Virksomheder"}
              </Link>
              <span>/</span>
              <span className="text-stone-500 font-medium">
                {isEn ? `Workshop ${currentWorkshop.num}` : `Workshop ${currentWorkshop.num}`}
              </span>
            </div>

            <span className="text-xs uppercase tracking-[0.4em] font-bold text-[#817262] mb-4 block">
              {isEn ? `Workshop ${currentWorkshop.num} for Businesses` : `Workshop ${currentWorkshop.num} for Virksomheder`}
            </span>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-stone-900 leading-[1.1] mb-6">
              {currentWorkshop.title}
            </h1>

            <p className="text-lg md:text-xl text-stone-700 font-serif italic leading-relaxed max-w-2xl mb-8">
              {currentWorkshop.subtitle}
            </p>

            <div className="flex flex-wrap items-center gap-4">
              <Link 
                to={contactPath} 
                className="bg-[#004f3b] text-white px-7 py-3.5 text-xs uppercase tracking-[0.2em] font-bold hover:bg-emerald-900 transition-all rounded-sm shadow-sm"
              >
                {isEn ? "Book or inquire" : "Book eller hør mere"}
              </Link>
              <a 
                href="tel:+4527507083" 
                className="inline-flex items-center gap-2.5 bg-white border border-[#817262] text-[#817262] hover:bg-[#817262] hover:text-white px-7 py-3.5 text-xs uppercase tracking-[0.2em] font-bold transition-all rounded-sm shadow-sm"
              >
                <i className="fa-solid fa-phone text-xs" />
                <span>{isEn ? "Call me: 27 50 70 83" : "Ring til mig: 27 50 70 83"}</span>
              </a>
              <Link 
                to={overviewPath} 
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-stone-600 hover:text-stone-900 font-bold underline underline-offset-4 transition-colors py-3"
              >
                <span>{isEn ? "← All business workshops" : "← Alle virksomhedsworkshops"}</span>
              </Link>
            </div>
          </FadeIn>

          <FadeIn direction="left" className="lg:col-span-5">
            <div className="relative rounded-sm overflow-hidden border border-stone-200 bg-white shadow-md p-3">
              {currentWorkshop.videoUrl ? (
                <YouTubeEmbed url={currentWorkshop.videoUrl} title={currentWorkshop.title} className="w-full" />
              ) : currentWorkshop.imageUrl ? (
                <img 
                  src={currentWorkshop.imageUrl} 
                  alt={currentWorkshop.title} 
                  className="w-full aspect-[4/3] object-cover rounded-xs"
                />
              ) : (
                <img 
                  src="/kundebilleder/Mette+som+underviser.jpg" 
                  alt={currentWorkshop.title} 
                  className="w-full aspect-[4/3] object-cover rounded-xs"
                />
              )}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Main Workshop Content Section */}
      <section className="py-20 md:py-28 bg-white px-6 border-b border-stone-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Left Column: Detailed Description */}
            <div className="lg:col-span-7 space-y-10">
              <FadeIn>
                <div className="space-y-4">
                  <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#817262] block">
                    {isEn ? "About the Workshop" : "Om Workshoppen"}
                  </span>
                  <h2 className="text-3xl md:text-4xl font-serif text-stone-900 leading-tight">
                    {currentWorkshop.title}
                  </h2>
                  <p className="text-lg md:text-xl font-serif italic text-stone-800 leading-relaxed border-l-2 border-[#817262] pl-6 py-1">
                    {currentWorkshop.subtitle}
                  </p>
                </div>

                <div className="h-px w-20 bg-stone-200 my-8" />

                <div className="space-y-6 text-stone-700 font-light text-lg leading-relaxed whitespace-pre-wrap">
                  {currentWorkshop.description.split('\n\n').map((para, pIdx) => (
                    <p key={pIdx}>{para}</p>
                  ))}
                </div>

                {currentWorkshop.outcome && (
                  <div className="bg-emerald-950/5 p-8 border-l-4 border-[#004f3b] rounded-r-sm mt-10 shadow-2xs">
                    <p className="text-stone-800 font-light text-lg leading-relaxed">
                      <strong className="font-serif italic text-xl text-stone-900 block mb-2">
                        {isEn ? "Your Outcome:" : "Dit udbytte:"}
                      </strong>
                      {currentWorkshop.outcome}
                    </p>
                  </div>
                )}

                {/* Testimonial Callout */}
                <div className="mt-12 pt-8 border-t border-stone-100">
                  <TestimonialCallout 
                    testimonialName="Marianne Helleskov" 
                    customTitle={isEn ? "Executive Testimonial on Corporate Development & Intuition" : "Udtalelse om Erhvervsforløb & Intuition"} 
                    className="mb-0"
                  />
                </div>
              </FadeIn>
            </div>

            {/* Right Column: Key Takeaways & Booking Card */}
            <div className="lg:col-span-5">
              <FadeIn delay={150}>
                <div className="sticky top-28 bg-stone-50/70 border border-stone-200 p-8 md:p-10 rounded-sm shadow-sm space-y-8">
                  <div>
                    <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#817262] block mb-2">
                      {isEn ? "Key Takeaways" : "Det får I"}
                    </span>
                    <h3 className="text-xl md:text-2xl font-serif text-stone-900 border-b border-stone-200 pb-4">
                      {isEn ? "What you receive:" : "I får blandt andet:"}
                    </h3>
                  </div>

                  <ul className="space-y-4">
                    {currentWorkshop.bullets.map((bullet, bIdx) => (
                      <li key={bIdx} className="flex items-start text-stone-700 font-light text-base leading-relaxed">
                        <i className="fa-light fa-sparkles text-emerald-700 mt-1 mr-3 flex-shrink-0 text-sm" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="p-5 bg-white border border-stone-200/80 rounded-sm space-y-2">
                    <p className="text-xs uppercase tracking-widest text-[#817262] font-bold">
                      {isEn ? "Format & Customization" : "Format & Tilpasning"}
                    </p>
                    <p className="text-stone-600 font-light text-sm leading-relaxed">
                      {isEn 
                        ? "The workshop is tailored to your team's specific context, group size, and goals. Can be conducted on-site or off-site."
                        : "Workshoppen skræddersys til jeres teams konkrete behov, deltagertal og rammer. Kan afholdes hos jer eller eksternt."}
                    </p>
                  </div>

                  <div className="pt-2 space-y-3">
                    <Link 
                      to={contactPath} 
                      className="block w-full text-center bg-[#004f3b] text-white py-4 uppercase text-xs tracking-[0.2em] font-bold hover:bg-emerald-900 transition-colors rounded-sm shadow-sm"
                    >
                      {isEn ? "Inquire about this workshop" : "Hør om denne workshop"}
                    </Link>
                    <a 
                      href="tel:+4527507083" 
                      className="flex items-center justify-center gap-2 w-full bg-white border border-[#817262] text-[#817262] hover:bg-[#817262] hover:text-white py-3.5 uppercase text-xs tracking-[0.2em] font-bold transition-all rounded-sm shadow-xs"
                    >
                      <i className="fa-solid fa-phone text-xs" />
                      <span>{isEn ? "Call me: 27 50 70 83" : "Ring til mig: 27 50 70 83"}</span>
                    </a>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Other Business Workshops Section */}
      <section className="py-24 bg-[#f5efe9] px-6">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
              <div>
                <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#817262] mb-3 block">
                  {isEn ? "Explore More" : "Udforsk Flere"}
                </span>
                <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
                  {isEn ? "Other Workshops for Businesses" : "Andre Workshops for Virksomheder"}
                </h2>
              </div>
              <Link 
                to={overviewPath} 
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#004f3b] hover:text-emerald-950 underline underline-offset-4 transition-colors"
              >
                <span>{isEn ? "See full business overview" : "Se hele virksomhedsoversigten"}</span>
                <i className="fa-solid fa-arrow-right text-xs" />
              </Link>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {otherWorkshops.map((w) => (
                <div key={w.id} className="bg-white border border-stone-200/80 p-8 rounded-sm shadow-sm hover:shadow-md transition-all flex flex-col justify-between">
                  <div className="space-y-4">
                    <span className="text-[11px] uppercase tracking-[0.3em] font-bold text-[#817262] block">
                      Workshop {w.num}
                    </span>
                    <h3 className="text-2xl font-serif text-stone-900 leading-snug">
                      <Link to={w.slug} className="hover:text-[#004f3b] transition-colors">
                        {w.title}
                      </Link>
                    </h3>
                    <p className="text-sm font-serif italic text-stone-700 leading-relaxed">
                      {w.subtitle}
                    </p>
                  </div>

                  <div className="pt-6 mt-6 border-t border-stone-100 flex items-center justify-between">
                    <Link 
                      to={w.slug} 
                      className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold text-[#004f3b] hover:underline"
                    >
                      <span>{isEn ? "Learn more" : "Læs mere"}</span>
                      <i className="fa-solid fa-arrow-right text-[10px]" />
                    </Link>
                    <a 
                      href="tel:+4527507083" 
                      className="text-stone-500 hover:text-stone-900 text-xs transition-colors"
                      title="Ring 27 50 70 83"
                    >
                      <i className="fa-solid fa-phone" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Dark Split Contact CTA Section */}
      <section className="grid grid-cols-1 md:grid-cols-2 bg-stone-900 text-stone-100">
        <div className="relative h-[50vh] md:h-auto min-h-[400px]">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/mettebrudvigokt2021-52.jpg" 
            alt="Kontakt Mette Brudvig" 
            className="absolute inset-0 w-full h-full object-cover object-top opacity-80"
          />
          <div className="absolute bottom-8 left-8 z-10">
            <Link to={overviewPath} className="text-sm uppercase tracking-widest font-bold underline underline-offset-4 hover:text-stone-300 transition-colors">
              {isEn ? "← Back to business overview" : "← Tilbage til virksomhedsoversigten"}
            </Link>
          </div>
        </div>
        <div className="flex flex-col justify-center px-8 md:px-16 lg:px-24 py-24">
          <FadeIn>
            <h3 className="text-3xl md:text-4xl font-serif leading-tight mb-8">
              {isEn 
                ? "Want to learn more about how we can tailor this workshop for your team?"
                : "Vil du høre mere om, hvordan vi kan skræddersy denne workshop til jeres team?"}
            </h3>
            <h3 className="text-3xl md:text-4xl font-serif leading-tight mb-12 text-stone-400">
              {isEn
                ? "Contact me for an informal conversation about your goals and how we best bring consciousness and future insight to life for your organization."
                : "Kontakt mig for en uforpligtende samtale om jeres behov, og hvordan vi bedst bringer bevidsthed og fremtidsforståelse i spil hos jer."}
            </h3>
            <div className="flex flex-wrap gap-4">
              <Link to={contactPath} className="inline-block bg-white text-stone-900 px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-stone-100 transition-all">
                {isEn ? "Contact Mette" : "Kontakt Mette"}
              </Link>
              <a 
                href="tel:+4527507083" 
                className="inline-flex items-center gap-2 bg-[#817262] text-white px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#6e6153] transition-all"
              >
                <i className="fa-solid fa-phone text-xs" />
                <span>{isEn ? "Call me: 27 50 70 83" : "Ring til mig: 27 50 70 83"}</span>
              </a>
              <Link to={overviewPath} className="inline-block border border-stone-100 px-8 py-4 text-xs uppercase tracking-[0.2em] hover:bg-stone-100 hover:text-stone-900 transition-all duration-300">
                {isEn ? "See all workshops" : "Se alle workshops"}
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

// 4 Specific Page Wrappers for Direct Route Matching
const VirksomhedSpringIndIFremtidenPage = () => <VirksomhedWorkshopDetailPage workshopId="spring-ind-i-fremtiden" />;
const VirksomhedNyEnergiITeametPage = () => <VirksomhedWorkshopDetailPage workshopId="ny-energi-i-teamet" />;
const VirksomhedFremstaaTrovaerdigPage = () => <VirksomhedWorkshopDetailPage workshopId="fremstaa-trovaerdig" />;
const VirksomhedAktiverHjertetsIntelligensPage = () => <VirksomhedWorkshopDetailPage workshopId="aktiver-hjertets-intelligens" />;

const OmMettePage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [visibleCount, setVisibleCount] = useState(6);
  const [expandedTestimonials, setExpandedTestimonials] = useState({});
  const [showAllTestimonials, setShowAllTestimonials] = useState(false);
  const { lang, t } = useLanguage();
  const { testimonials, podcasts, omMette } = useCms();
  const isEn = lang === 'en';

  const activeTestimonials = (testimonials && testimonials.length > 0) ? testimonials : TESTIMONIALS;
  const activePodcasts = (podcasts && podcasts.length > 0) ? podcasts : PODCASTS;

  const getCmsText = (field, fallbackDa, fallbackEn) => {
    if (!omMette || !omMette[field]) return isEn ? fallbackEn : fallbackDa;
    const val = omMette[field];
    if (typeof val === 'string') return val;
    if (typeof val === 'object') {
      return (isEn ? (val.en || val.da) : (val.da || val.en)) || (isEn ? fallbackEn : fallbackDa);
    }
    return isEn ? fallbackEn : fallbackDa;
  };

  const getCmsHtml = (field, fallbackDaHtml, fallbackEnHtml) => {
    if (!omMette || !omMette[field]) return isEn ? fallbackEnHtml : fallbackDaHtml;
    const val = omMette[field];
    if (typeof val === 'string') return val;
    if (typeof val === 'object') {
      return (isEn ? (val.en || val.da) : (val.da || val.en)) || (isEn ? fallbackEnHtml : fallbackDaHtml);
    }
    return isEn ? fallbackEnHtml : fallbackDaHtml;
  };

  const getCmsImage = (field, fallbackUrl) => {
    if (omMette?.[field] && Array.isArray(omMette[field]) && omMette[field].length > 0 && omMette[field][0]) {
      return omMette[field][0];
    }
    return fallbackUrl;
  };

  const heroTitle = getCmsText('titel', t('omMettePage.title'), t('omMettePage.title'));
  const heroSub = getCmsText('undertitel', t('omMettePage.sub'), t('omMettePage.sub'));
  const heroIntro = getCmsText('intro_tekst', t('omMettePage.intro'), t('omMettePage.intro'));
  const heroImage = getCmsImage('profil_billede', "https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/image_3.png");
  const secImage = getCmsImage('sekundaert_billede', "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/mettebrudvigokt2021-52.jpg");
  const thirdImage = getCmsImage('tredje_billede', "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/mettebrudvigokt2021-121.jpg");
  const videoUrl = omMette?.video_url || "https://youtu.be/pp2tYmTrfKg";

  useEffect(() => {
    document.title = lang === 'en' ? "Mette Brudvig - About Mette" : "Mette Brudvig - Om Mette";
  }, [lang]);

  useEffect(() => {
    setVisibleCount(6);
  }, [searchTerm, activeFilter]);

  const toggleExpand = (idx) => {
    setExpandedTestimonials(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  const getFilterCount = (filterType) => {
    return activeTestimonials.filter(t => {
      const textLower = (t.text || '').toLowerCase();
      if (filterType === 'all') return true;
      if (filterType === 'clairvoyance') {
        return textLower.includes('clairvoyance') || textLower.includes('reading') || textLower.includes('seance') || textLower.includes('kanal') || textLower.includes('guides') || textLower.includes('ånde');
      }
      if (filterType === 'erhverv') {
        return textLower.includes('erhverv') || textLower.includes('leder') || textLower.includes('arbejde') || textLower.includes('præstation') || textLower.includes('balance') || textLower.includes('stress');
      }
      if (filterType === 'personlig') {
        return textLower.includes('ro') || textLower.includes('retning') || textLower.includes('vejledning') || textLower.includes('livsvej') || textLower.includes('tryg') || textLower.includes('kærlig') || textLower.includes('omsorg') || textLower.includes('nærvær');
      }
      return true;
    }).length;
  };

  const filteredTestimonials = activeTestimonials.filter(t => {
    const textLower = (t.text || '').toLowerCase();
    const nameLower = (t.name || '').toLowerCase();
    const matchesSearch = textLower.includes(searchTerm.toLowerCase()) || nameLower.includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    if (activeFilter === 'all') return true;
    if (activeFilter === 'clairvoyance') {
      return textLower.includes('clairvoyance') || textLower.includes('reading') || textLower.includes('seance') || textLower.includes('kanal') || textLower.includes('guides') || textLower.includes('ånde');
    }
    if (activeFilter === 'erhverv') {
      return textLower.includes('erhverv') || textLower.includes('leder') || textLower.includes('arbejde') || textLower.includes('præstation') || textLower.includes('balance') || textLower.includes('stress');
    }
    if (activeFilter === 'personlig') {
      return textLower.includes('ro') || textLower.includes('retning') || textLower.includes('vejledning') || textLower.includes('livsvej') || textLower.includes('tryg') || textLower.includes('kærlig') || textLower.includes('omsorg') || textLower.includes('nærvær');
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-[#faf9f6]">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] md:min-h-[70vh] pt-[185px] md:pt-[215px] lg:pt-[225px] pb-12 flex items-center bg-[#f5efe9] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center z-10 w-full">
          <FadeIn direction="right">
            <h1 className="text-4xl md:text-6xl font-serif text-stone-900 leading-[1.1] mb-6">
              {heroTitle}
            </h1>
            <p className="text-lg md:text-xl font-serif text-stone-800 leading-relaxed italic mb-4 max-w-lg">
              {heroSub}
            </p>
            <p className="text-stone-700 font-light text-base md:text-lg leading-relaxed max-w-lg">
              {heroIntro}
            </p>
          </FadeIn>
        </div>
        <div className="absolute top-0 right-0 w-full md:w-1/2 h-full opacity-20 md:opacity-100 pt-20 md:pt-24 lg:pt-28 pointer-events-none">
          <img 
            src={heroImage} 
            alt="Mette Brudvig" 
            className="w-full h-full object-cover object-center pointer-events-none"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#f5efe9] via-transparent to-transparent hidden md:block pointer-events-none" />
        </div>
      </section>

      {/* Sektion 1: Familie & Rejsen til Sydspanien */}
      <section className="py-24 bg-white px-6 border-b border-stone-100">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7 space-y-6 text-stone-700 font-light text-base md:text-lg leading-relaxed">
                <p className="font-serif font-normal text-xl text-stone-900 leading-relaxed">
                  {getCmsText('familie_overskrift', 'Jeg er 42 år, mor til tre dejlige børn og gift med min sjæleven, Mathies.', 'I am 42 years old, mother of three wonderful children, and married to my soulmate, Mathies.')}
                </p>
                <div 
                  className="space-y-4"
                  dangerouslySetInnerHTML={{ 
                    __html: getCmsHtml('familie_tekst', 
                      '<p>Vores familieliv begyndte i København, hvorefter vi flyttede til dejlige Høruphav uden for Sønderborg i 2017. Vi har forsøgt at trives i hamsterhjulet i rammer, der var for stramme og i et tempo, der gav os åndenød. I 2025 slap vi tøjlerne, hoppede ud af den kendte hverdag og pakkede elbilen med alt for meget bagage, vores tre børn og to hunde og kørte 3000 kilometer til Sydspanien. Her fandt vi roen, perspektivet og hinanden. Nu bor vi igen i vores dejlige villa – genforelsket i både vores liv herhjemme og i Danmark. Og i en sammenrystet og nærværende udgave af familien, hvor et tempo, hvor alle kan have det godt, er blevet højeste prioritet.</p>',
                      '<p>Our family life began in Copenhagen before moving to Southern Denmark in 2017. Like many, we experienced the strain of the modern hamster wheel. In 2025, we took a leap of faith, packed our electric car with our three children and two dogs, and drove 3,000 kilometers to Southern Spain. There we discovered stillness, perspective, and reconnection. Now back in Denmark, we live with a newfound appreciation for presence, where a sustainable pace for the whole family is our highest priority.</p>'
                    )
                  }}
                />
              </div>

              <div className="lg:col-span-5 flex flex-col items-center justify-center">
                <div className="w-full max-w-[340px]">
                  <YouTubeEmbed 
                    url={videoUrl} 
                    title={lang === 'en' ? "Mette Brudvig tells her own story" : "Mette Brudvig fortæller sin historie"} 
                    isShort={true}
                  />
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Sektion 2: Uddannelse & Karrierevejen */}
      <section className="py-24 bg-[#f5efe9]/30 px-6 border-b border-stone-100">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-5 order-2 lg:order-1 flex">
                <div className="rounded-sm overflow-hidden border border-stone-200 bg-stone-100 shadow-lg relative group w-full aspect-[4/5]">
                  <img 
                    src={secImage} 
                    alt="Mette Brudvig - Cand.comm & Journalistik" 
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-900/20 via-transparent to-transparent"></div>
                </div>
              </div>

              <div className="lg:col-span-7 order-1 lg:order-2 space-y-6 text-stone-700 font-light text-base md:text-lg leading-relaxed">
                <div 
                  className="space-y-4"
                  dangerouslySetInnerHTML={{ 
                    __html: getCmsHtml('karriere_tekst',
                      '<p>Jeg er uddannet cand.comm. i journalistik samt Kultur- og Sprogmødestudier fra Roskilde Universitet og blev formet som journalist på Politiken. Gennem mit arbejdsliv har jeg bevæget mig mellem mennesker, kommunikation, udvikling og ledelse.</p><p>Jeg har arbejdet for bedre vilkår for socialt udsatte som kommunikationsansvarlig i Frelsens Hær, været helt tæt på unge mennesker som efterskolelærer på Nordborg Slot og arbejdet med grøn omstilling, forretningsudvikling og PR. Senest har jeg været kommerciel direktør i en fond.</p><p>Udefra kunne det ligne en karriere med mange forskellige retninger. For mig giver det hele mening i dag.</p>',
                      '<p>I hold a Master\'s degree in Journalism and Intercultural Studies from Roskilde University and trained as a journalist at Politiken. Throughout my professional career, I have worked across human development, strategic communication, and executive leadership.</p><p>I have advocated for socially vulnerable groups at the Salvation Army, taught young minds, worked with sustainable business innovation, and most recently served as Commercial Director in a foundation.</p><p>From the outside, it might look like a career of diverse directions. To me, it all makes profound sense today.</p>'
                    )
                  }}
                />

                <div className="p-6 bg-white border-l-4 border-[#817262] rounded-r-sm shadow-xs mt-6">
                  <p className="font-serif font-normal text-stone-900 text-lg md:text-xl">
                    {getCmsText('karriere_citat', 'For min egen vej har handlet om at finde hjem og få de kompetencer og egenskaber, jeg skal bruge for at følge min livsbane og udleve mit formål.', 'My path was about acquiring the tools, skills, and perspectives needed to fulfill my true life purpose.')}
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Sektion 3: Vendepunktet & Sjælens Kald */}
      <section className="py-24 bg-white px-6 border-b border-stone-100">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <div className="space-y-8 text-stone-700 font-light text-base md:text-lg leading-relaxed max-w-4xl mx-auto">
              <div 
                className="space-y-4"
                dangerouslySetInnerHTML={{ 
                  __html: getCmsHtml('vendepunkt_tekst_1',
                    '<p>Gennem udbrændthed blev jeg tvunget til at stoppe op, give slip på det, der ikke længere var mig, og begynde at forstå mig selv, verden og livet fra et nyt perspektiv. Både åndeligt og videnskabeligt.</p><p>Jeg gik hele vejen ind til kernen af mig selv. Til spørgsmålet om, hvem jeg egentlig er, hvad jeg er her for, og hvornår jeg føler mig hel, fredfyldt og levende.</p>',
                    '<p>Through severe burnout, I was forced to stop, let go of external conditioning, and understand myself, life, and the world from a fresh perspective – both spiritually and scientifically.</p><p>I went deep into my core: Who am I truly? What am I here to create? When do I feel genuinely whole, peaceful, and alive?</p>'
                  )
                }}
              />

              {/* Quote box in Frisæt dig style */}
              <div className="p-8 md:p-10 bg-[#f5efe9]/50 border-l-4 border-[#817262] rounded-r-sm shadow-xs space-y-4 my-10">
                <p className="font-serif italic text-lg md:text-xl text-stone-800">
                  {getCmsText('fremhaevet_citat_overskrift', 'Svaret fandt jeg i det arbejde, som hele denne hjemmeside handler om:', 'The answer laid in the work this entire platform is built upon:')}
                </p>
                <p className="font-serif font-normal text-stone-900 text-xl md:text-2xl leading-relaxed">
                  "{getCmsText('fremhaevet_citat', 'At hjælpe mennesker med at løfte og frisætte sig ved at skabe plads til sjælen og aktivere den intelligens, klarhed og dybe forståelse, der allerede findes i dem.', 'Helping individuals unleash themselves by creating space for the soul and activating the innate clarity, wisdom, and intelligence within.')}"
                </p>
              </div>

              <div 
                className="space-y-4"
                dangerouslySetInnerHTML={{ 
                  __html: getCmsHtml('vendepunkt_tekst_2',
                    '<p>For mig blev rejsen til en egentlig spirituel opvågning. En rejse, jeg både har været udfordret af og dybt taknemmelig for, og som markant har åbnet og udvidet mit syn på mennesker, verden og livet.</p><p>I dag kan jeg læse energier og hjælpe mennesker med at forstå deres sjælelige egenskaber, deres nuværende situation og deres potentiale gennem clairvoyance. Men jeg arbejder også gennem videnskab, fremtidsforskning, coaching, rådgivning og undervisning for at vi sammen kan skabe en bedre verden.</p>',
                    '<p>For me, the journey became a genuine spiritual awakening that expanded my understanding of human consciousness and life.</p><p>Today, I combine clairvoyant energy readings with quantum physics, futurology, executive coaching, and spiritual mentorship to help usher in a more conscious, peaceful world.</p>'
                  )
                }}
              />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Sektion 4: Det Nye Paradigme & Helheden */}
      <section className="py-24 bg-[#f5efe9]/20 px-6 border-b border-stone-100">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7 space-y-6 text-stone-700 font-light text-base md:text-lg leading-relaxed">
                <p className="font-serif font-normal text-xl text-stone-900">
                  {getCmsText('paradigme_overskrift', 'For mig hænger det hele sammen.', 'To me, everything is interconnected.')}
                </p>
                <div 
                  className="space-y-4"
                  dangerouslySetInnerHTML={{ 
                    __html: getCmsHtml('paradigme_tekst',
                      '<p>Vi står som mennesker midt i et skifte. Et nyt paradigme er ved at tage form, hvor vi ikke længere kun skal tænke os til svarene, men også lære at lytte indad, mærke klarere og aktivere hjertets intelligens til gavn for os selv, vores relationer, vores arbejdspladser og vores klode.</p><p>Jeg er desuden uddannet coach fra Grow2Learn, har taget Masterclass i fremtidsforskning hos Universal Futurist, en bestyrelsesuddannelse på CBS og en uddannelse som ny tids clairvoyant hos Halo House.</p><p>For mig hører hjertets intelligens og fremtidsforståelse ikke kun hjemme i det personlige liv. Det skal også med ind i ledelse, organisationer, bestyrelseslokaler og de beslutninger, der former vores fælles fremtid.</p><p>Jeg er desuden medstifter og underviser i NELA, hvor vi uddanner ny tids ledere, og hvor vi faciliterer netværk og tænketank.</p>',
                      '<p>Humanity is experiencing a paradigm shift where we must move beyond pure intellect to activate heart intelligence for our well-being, organizations, and planet.</p><p>I am certified as an executive coach through Grow2Learn, hold a Masterclass in Futurology from Universal Futurist, completed board training at CBS, and trained as a modern clairvoyant at Halo House.</p><p>To me, heart intelligence and future consciousness belong not only in private life, but also in executive leadership, organizational strategy, and boardrooms where tomorrow\'s decisions are shaped.</p><p>I am also co-founder and educator at NELA, where we train new era leaders and facilitate networks and think tank initiatives.</p>'
                    )
                  }}
                />

                <div className="p-6 bg-white border border-stone-200/80 rounded-sm shadow-xs mt-6 space-y-2">
                  <p className="font-serif font-normal text-stone-900 text-lg">
                    {lang === 'en' ? "And yes, I also host podcasts." : "Ja, og så har jeg også podcasts."}
                  </p>
                  <p className="text-stone-600 font-light text-sm md:text-base">
                    {getCmsText('podcast_boks_tekst', 'Dem finder du nederst på siden. Her undersøger jeg netop den nye tid gennem samtaler om videnskab, spiritualitet, energi, sjælen og min egen rejse.', 'You can explore them further down the page. Here I explore the new era through dialogues on science, spirituality, energy, the soul, and my personal journey.')}
                  </p>
                </div>
              </div>

              <div className="lg:col-span-5 flex">
                <div className="rounded-sm overflow-hidden border border-stone-200 bg-stone-100 shadow-lg relative group w-full aspect-[4/5]">
                  <img 
                    src={thirdImage} 
                    alt="Mette Brudvig - Fremtid & Hjertets Intelligens" 
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-900/20 via-transparent to-transparent"></div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Spirituel Opvågning & Sjælens Intelligens */}
      <section className="py-24 px-6 bg-[#817262] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-white blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-white blur-3xl"></div>
        </div>
        <div className="max-w-4xl mx-auto text-center space-y-8 relative z-10">
          <FadeIn>
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-8 text-2xl text-amber-200">
              <i className="fa-light fa-sparkles" />
            </div>
            <h2 className="text-3xl md:text-4xl font-serif mb-8">
              {getCmsText('sjael_overskrift', 'Vejen hjem til sjælen', 'The Path Home to the Soul')}
            </h2>
          </FadeIn>
          <div className="space-y-6 text-lg md:text-xl font-light leading-relaxed text-stone-100 text-left md:text-center">
            <FadeIn delay={100}>
              <div 
                className="space-y-6"
                dangerouslySetInnerHTML={{ 
                  __html: getCmsHtml('sjael_tekst',
                    '<p>Min egen vej hjem til sjælen gik gennem udbrændthed. Jeg måtte give slip på det, der ikke længere tjente mig, og begynde at forstå mig selv, verden og universet fra både et åndeligt, futuristisk og et videnskabeligt perspektiv.</p><p>Langsomt nåede jeg ind til kernen af mig selv. Til forståelsen af, hvem jeg er, hvad mit formål er, og hvornår jeg føler mig hel, fredfyldt og livsglad.</p><p>Det gør jeg, når jeg arbejder med det, hele denne hjemmeside handler om: at hjælpe mennesker med at løfte og frisætte sig ved at skabe plads til sjælen og aktivere den enorme intelligens, klarhed og forståelse, den rummer. Og for derigennem at bidrage til at skabe en bedre verden, flere kan trives i.</p><p>For mig udviklede rejsen sig til en regulær spirituel opvågning, og jeg har elsket at gå hele vejen. Den har gjort mig i stand til at læse energier og hjælpe andre med at forstå deres sjælelige egenskaber, deres aktuelle situation og deres potentiale gennem clairvoyance.</p><p>Men mit arbejde hviler ikke kun på det spirituelle. Jeg trækker også på videnskab, fremtidsforskning, coaching, rådgivning, samfundsviden og undervisning.</p><p>Det hele hænger sammen i en større forståelse af den tid, vi som art bevæger os ind i: et nyt paradigme, hvor vi aktiverer hjertets intelligens til gavn for os selv, vores omgivelser og kloden.</p>',
                    '<p>My personal path home to my soul went through severe burnout. I had to let go of what no longer served me and begin to understand myself, humanity, and the universe through spiritual, futuristic, and scientific perspectives.</p><p>Slowly I reached my core: understanding who I am, what my purpose is, and when I feel genuinely whole, peaceful, and joyful.</p><p>I feel whole when I create what this entire platform is built around: helping people unleash themselves by making room for the soul and activating its deep clarity, wisdom, and understanding — contributing to a better world.</p><p>For me, the journey unfolded into a full spiritual awakening. It enabled me to read subtle energy and guide others through clairvoyance to understand their soul gifts, present situation, and potential.</p><p>Yet my work rests not only on spirituality. I continuously integrate science, futurology, executive coaching, and leadership experience.</p><p>It all connects into a broader understanding of the era we are entering: a new paradigm where we activate heart intelligence for our well-being, organizations, and the planet.</p>'
                  )
                }}
              />
            </FadeIn>
            <FadeIn delay={400}>
              <p className="font-serif italic text-amber-300 text-2xl pt-4">
                {getCmsText('sjael_fremhaevet_tekst', 'Jeg kalder det ‘Hjertets tidsalder’.', 'I call it ‘The Age of the Heart’.')}
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* 4 Udtalelser - flyttet op for at skabe troværdighed og social proof tidligt */}
      <TestimonialsShowcase
        badge={isEn ? "Client & Participant Experiences" : "Klient- og kursistoplevelser"}
        title={isEn ? "What others experience with Mette" : "Hvad andre oplever sammen med Mette"}
        subtitle={isEn ? "Authentic reflections from clients in sessions, courses, and retreats." : "Autentiske tilbagemeldinger fra klienter i sessioner, kurser og retreats."}
        className="py-20 md:py-24 bg-[#faf9f6] border-b border-stone-200/60 px-6"
        cardBg="bg-white"
      />

      {/* Uddannelser & Erhverv */}
      <section className="py-24 px-6 bg-stone-50 border-b border-stone-100">
        <div className="max-w-5xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <FadeIn>
              <span className="text-xs uppercase tracking-[0.4em] font-bold text-stone-400 block">
                {getCmsText('erhverv_badge', 'Uddannelse & Ledelse', 'Education & Leadership')}
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mt-2">
                {getCmsText('erhverv_overskrift', 'Hjertets intelligens i virksomhedsledelse', 'Heart Intelligence in Corporate Leadership')}
              </h2>
              <div className="h-1 w-12 bg-[#004f3b] mx-auto my-6"></div>
              <p className="text-stone-600 font-light text-lg leading-relaxed">
                {getCmsText('erhverv_tekst', 'Nå ja, jeg er også uddannet coach fra Grow2Learn, har taget Masterclass i fremtidsforskning hos Universal Futurist og har taget bestyrelsesuddannelse på CBS. For vi skal have hjertets intelligens og fremtidsforståelse med ind i virksomhedsledelse.', 'Oh, and I am also a certified coach from Grow2Learn, hold a Masterclass in Futurology from Universal Futurist, and completed board training at CBS. Because we need to bring heart intelligence and future consciousness into corporate leadership.')}
              </p>
            </FadeIn>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <FadeIn delay={100} className="bg-white p-8 rounded-sm shadow-sm border border-stone-100 flex flex-col items-center text-center">
              <div className="w-14 h-14 bg-emerald-50 text-[#004f3b] rounded-full flex items-center justify-center mb-6 text-2xl">
                <i className="fa-light fa-users-gear" />
              </div>
              <h3 className="text-lg font-serif text-stone-900 mb-2">Grow2Learn</h3>
              <p className="text-stone-500 font-light text-sm">{isEn ? "Certified Coach" : "Uddannet Coach"}</p>
            </FadeIn>

            <FadeIn delay={200} className="bg-white p-8 rounded-sm shadow-sm border border-stone-100 flex flex-col items-center text-center">
              <div className="w-14 h-14 bg-emerald-50 text-[#004f3b] rounded-full flex items-center justify-center mb-6 text-2xl">
                <i className="fa-light fa-compass" />
              </div>
              <h3 className="text-lg font-serif text-stone-900 mb-2">Universal Futurist</h3>
              <p className="text-stone-500 font-light text-sm">{isEn ? "Masterclass in Futurology" : "Masterclass i fremtidsforskning"}</p>
            </FadeIn>

            <FadeIn delay={300} className="bg-white p-8 rounded-sm shadow-sm border border-stone-100 flex flex-col items-center text-center">
              <div className="w-14 h-14 bg-emerald-50 text-[#004f3b] rounded-full flex items-center justify-center mb-6 text-2xl">
                <i className="fa-light fa-graduation-cap" />
              </div>
              <h3 className="text-lg font-serif text-stone-900 mb-2">CBS</h3>
              <p className="text-stone-500 font-light text-sm">{isEn ? "Board Training" : "Bestyrelsesuddannelse"}</p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Podcasts Section */}
      <section className="py-24 px-6 bg-stone-50 border-t border-b border-stone-100">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 items-center mb-16">
            <div className="lg:col-span-5 space-y-4">
              <FadeIn>
                <span className="text-xs uppercase tracking-[0.4em] font-bold text-stone-400 block">
                  {isEn ? "Listen in" : "Lyt med"}
                </span>
                <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mt-2">Podcasts</h2>
                <div className="h-1 w-12 bg-[#817262] my-6"></div>
                <p className="text-stone-600 font-light text-lg leading-relaxed">
                  {isEn 
                    ? "And... I also host podcasts. You can find them at the bottom of the page, focusing on this new era—science, spirituality, energy, the soul, and my own journey."
                    : "Og…. så har jeg også nogle podcasts. Dem kan du finde i bunden af siden, og de handler netop om denne nye tid. Både videnskaben, det spirituelle, energi, sjælen og min egen rejse."}
                </p>
              </FadeIn>
            </div>
            <div className="lg:col-span-7">
              <div className="grid sm:grid-cols-2 gap-8">
                {activePodcasts.map((pod, idx) => {
                  const isHighlighted = pod.title === "Forbundet i den nye tid";
                  const podTitle = isEn && pod.titleEn ? pod.titleEn : pod.title;
                  const podDesc = isEn && pod.descriptionEn ? pod.descriptionEn : pod.description;
                  return (
                    <FadeIn 
                      key={idx} 
                      delay={idx * 150} 
                      className={`bg-white rounded-sm overflow-hidden flex flex-col h-full transition-all duration-300 ${
                        isHighlighted 
                          ? "shadow-md border-2 border-[#817262]/40 ring-1 ring-[#817262]/10" 
                          : "shadow-sm border border-stone-100"
                      }`}
                    >
                      <div className="relative aspect-square overflow-hidden bg-stone-100">
                        {isHighlighted && (
                          <div className="absolute top-4 left-4 bg-[#817262] text-white text-[9px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 rounded-sm shadow-md z-10 flex items-center gap-1">
                            <i className="fa-solid fa-star text-[8px]" />
                            <span>{isEn ? "Listen in" : "Lyt med"}</span>
                          </div>
                        )}
                        <img 
                          src={pod.image} 
                          alt={podTitle} 
                          className="w-full h-full object-cover" 
                        />
                      </div>
                      <div className="p-6 flex flex-col flex-grow justify-between space-y-4">
                        <div>
                          <h3 className="text-xl font-serif text-stone-900 mb-2 flex items-center gap-2">
                            <span>{podTitle}</span>
                            {isHighlighted && (
                              <span className="inline-block w-2 h-2 rounded-full bg-[#817262]" />
                            )}
                          </h3>
                          <p className="text-stone-500 font-light text-sm leading-relaxed line-clamp-3">{podDesc}</p>
                        </div>
                        <div className="pt-2 flex flex-col sm:flex-row gap-2">
                          <a 
                            href={pod.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`inline-flex items-center justify-center space-x-1.5 text-white px-3 py-2.5 uppercase text-[10px] tracking-[0.15em] font-bold transition-all text-center flex-1 ${
                              isHighlighted 
                                ? "bg-[#817262] hover:bg-[#6e6153]" 
                                : "bg-stone-700 hover:bg-stone-800"
                            }`}
                          >
                            <i className="fa-brands fa-spotify text-xs" />
                            <span>Spotify</span>
                          </a>
                          <a 
                            href={pod.buzzsproutLink || "https://www.buzzsprout.com/2530331/episodes"} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center space-x-1.5 text-stone-800 bg-stone-100 hover:bg-stone-200 border border-stone-300 px-3 py-2.5 uppercase text-[10px] tracking-[0.15em] font-bold transition-all text-center flex-1"
                          >
                            <i className="fa-solid fa-podcast text-xs text-[#817262]" />
                            <span>Buzzsprout</span>
                          </a>
                        </div>
                      </div>
                    </FadeIn>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-white text-center">
        <div className="max-w-3xl mx-auto space-y-8">
          <FadeIn>
            <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
              {getCmsText('cta_overskrift', 'Jeg håber, vi mødes!', 'I hope we connect!')}
            </h2>
            <p className="text-stone-600 font-light text-lg max-w-xl mx-auto">
              {getCmsText('cta_tekst', 'Er du klar til at åbne for sjælens kraft og intelligens i dig?', 'Are you ready to unleash the power and intelligence of your soul?')}
            </p>
            <div className="pt-8 flex flex-col sm:flex-row justify-center gap-4">
              <Link to={isEn ? "/en/sessions" : "/sessioner"} className="bg-[#004f3b] text-white px-8 py-4 uppercase text-xs tracking-[0.2em] font-bold hover:bg-emerald-900 transition-all">
                {isEn ? "See Sessions" : "Se sessioner"}
              </Link>
              <Link to={isEn ? "/en/contact" : "/kontakt"} className="border border-stone-900 text-stone-900 px-8 py-4 uppercase text-xs tracking-[0.2em] font-bold hover:bg-stone-900 hover:text-white transition-all">
                {isEn ? "Contact Me" : "Kontakt mig"}
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

const UdtalelserPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [expandedTestimonials, setExpandedTestimonials] = useState({});
  const { lang, t } = useLanguage();
  const { testimonials } = useCms();
  const isEn = lang === 'en';

  useEffect(() => {
    document.title = isEn ? "Mette Brudvig - Testimonials" : "Mette Brudvig - Udtalelser";
    window.scrollTo(0, 0);
  }, [isEn]);

  const toggleExpand = (idx) => {
    setExpandedTestimonials(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  const homePath = isEn ? '/en' : '/';
  const aboutPath = isEn ? '/en/about-mette' : '/om-mette';

  const filterCategories = [
    { id: 'all', label: isEn ? 'All' : 'Alle' },
    { id: 'forloeb', label: isEn ? 'Programs & Mentoring' : 'Forløb' },
    { id: 'kurser', label: isEn ? 'Education & Courses' : 'Uddannelse og kurser' },
    { id: 'sessioner', label: isEn ? 'Clairvoyance & Sessions' : 'Clairvoyance & Sessioner' },
    { id: 'foredrag', label: isEn ? 'Business & Leadership' : 'Erhverv & Lederskab' }
  ];

  const sourceList = (testimonials && testimonials.length > 0) ? testimonials : TESTIMONIALS;

  const filteredList = sourceList.filter(item => {
    const textMatch = (item.text || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
                      (item.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                      (item.title && item.title.toLowerCase().includes(searchTerm.toLowerCase()));
    if (!textMatch) return false;

    if (activeFilter === 'all') return true;
    if (activeFilter === 'forloeb') {
      const cats = item.categories || [item.category];
      const titleLower = (item.title || '').toLowerCase();
      const textLower = (item.text || '').toLowerCase();
      return item.name === 'Else Støchkel' ||
             cats.includes('forloeb') ||
             titleLower.includes('forløb') ||
             titleLower.includes('mentor') ||
             titleLower.includes('årsforløb') ||
             textLower.includes('forløb') ||
             textLower.includes('spirituelle rejse');
    }
    if (activeFilter === 'kurser') {
      const cats = item.categories || [item.category];
      const titleLower = (item.title || '').toLowerCase();
      const textLower = (item.text || '').toLowerCase();
      return cats.includes('workshop') ||
             titleLower.includes('kursus') || 
             titleLower.includes('workshop') ||
             titleLower.includes('uddannelse') ||
             textLower.includes('kursus') || 
             textLower.includes('workshop') ||
             textLower.includes('uddannelse');
    }
    if (activeFilter === 'sessioner') {
      const cats = item.categories || [item.category];
      const titleLower = (item.title || '').toLowerCase();
      const textLower = (item.text || '').toLowerCase();
      return cats.includes('sessioner') || 
             titleLower.includes('clairvoyance') || 
             titleLower.includes('session') || 
             textLower.includes('clairvoyance') || 
             textLower.includes('session') || 
             textLower.includes('reading');
    }
    if (activeFilter === 'foredrag') {
      const cats = item.categories || [item.category];
      const titleLower = (item.title || '').toLowerCase();
      const textLower = (item.text || '').toLowerCase();
      return cats.includes('foredrag') || 
             titleLower.includes('erhverv') || 
             titleLower.includes('foredrag') || 
             textLower.includes('erhverv') || 
             textLower.includes('leder');
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-[#faf9f6]">
      <SEO 
        title={isEn ? "Testimonials | Client experiences with Mette Brudvig" : "Udtalelser | Klienters oplevelser med Mette Brudvig"}
        description={isEn ? "Here you can see a selection of testimonials from clients who have been in sessions or programs with me." : "Her kan du se et udpluk af udtalelser fra klienter, der har været i sessioner eller er i forløb med mig."}
      />

      {/* Hero Section */}
      <section className="relative pt-[185px] md:pt-[215px] lg:pt-[225px] pb-16 md:pb-20 bg-[#f5efe9] border-b border-stone-200/60 overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <FadeIn>
            <h1 className="text-4xl md:text-6xl font-serif text-stone-900 mb-6 leading-tight">
              {isEn ? "Testimonials" : "Udtalelser"}
            </h1>
            <div className="h-1 w-12 bg-[#817262] mb-6"></div>
            <p className="text-lg md:text-xl font-serif text-stone-800 leading-relaxed max-w-3xl">
              {isEn 
                ? "Here you can see a selection of testimonials from clients who have been in sessions or programs with me."
                : "Her kan du se et udpluk af udtalelser fra klienter, der har været i sessioner eller er i forløb med mig."}
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Filter and Search Bar */}
      <section className="py-8 bg-white border-b border-stone-200/80 px-6 sticky top-[68px] sm:top-[74px] lg:top-[82px] z-30 shadow-2xs backdrop-blur-md bg-white/95">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Search Field */}
          <div className="relative w-full md:w-80">
            <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-stone-400">
              <i className="fa-light fa-magnifying-glass text-sm" />
            </span>
            <input
              type="text"
              placeholder={isEn ? "Search by name or keyword..." : "Søg på navn eller ord..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-8 py-2.5 bg-stone-50 border border-stone-200 rounded-sm focus:outline-none focus:border-[#817262] focus:bg-white text-stone-800 placeholder-stone-400 text-xs transition-all"
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-stone-400 hover:text-stone-600"
              >
                <i className="fa-light fa-xmark text-xs" />
              </button>
            )}
          </div>

          {/* Category Filter Pills */}
          <div className="flex flex-wrap gap-2 w-full md:w-auto items-center justify-start md:justify-end">
            {filterCategories.map((filter) => {
              const isActive = activeFilter === filter.id;
              return (
                <button
                  key={filter.id}
                  onClick={() => setActiveFilter(filter.id)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer ${
                    isActive 
                      ? 'bg-[#817262] text-white shadow-xs' 
                      : 'bg-stone-50 hover:bg-stone-100 text-stone-600 border border-stone-200'
                  }`}
                >
                  {filter.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-16 md:py-24 px-6">
        <div className="max-w-6xl mx-auto">
          {filteredList.length === 0 ? (
            <div className="text-center py-16 bg-white border border-stone-200 rounded-sm p-8">
              <p className="text-stone-500 font-light text-base mb-4">
                {isEn ? "No testimonials found matching your search." : "Ingen udtalelser matcher din søgning."}
              </p>
              <button
                onClick={() => { setSearchTerm(''); setActiveFilter('all'); }}
                className="text-xs uppercase tracking-widest font-bold text-[#817262] underline"
              >
                {isEn ? "Reset filters" : "Nulstil filtre"}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10 items-stretch">
              {filteredList.map((item, idx) => {
                const isLong = item.text.length > 340;
                const isExpanded = expandedTestimonials[idx];
                const displayText = isLong && !isExpanded ? `${item.text.substring(0, 320)}...` : item.text;

                return (
                  <FadeIn 
                    key={idx}
                    delay={(idx % 2) * 80}
                    className="bg-white p-8 md:p-10 border border-stone-200/80 rounded-sm shadow-xs hover:shadow-md transition-all duration-300 flex flex-col justify-between relative group"
                  >
                    <div>
                      {/* Quote mark decoration */}
                      <div className="text-stone-200 text-5xl font-serif leading-none mb-3 select-none pointer-events-none">”</div>
                      
                      {/* Testimonial body text */}
                      <p className="text-stone-700 font-light leading-relaxed text-base whitespace-pre-line italic">
                        "{displayText.split(/(<u>.*?<\/u>)/g).map((part, pIdx) => {
                          if (part.startsWith('<u>') && part.endsWith('</u>')) {
                            const inner = part.slice(3, -4);
                            return <span key={pIdx} className="underline underline-offset-2 not-italic font-normal">{inner}</span>;
                          }
                          return part;
                        })}"
                      </p>

                      {isLong && (
                        <button
                          onClick={() => toggleExpand(idx)}
                          className="text-xs font-semibold text-[#817262] hover:text-[#6e6153] transition-colors flex items-center space-x-1.5 mt-4 focus:outline-none cursor-pointer"
                        >
                          <span>{isExpanded ? (isEn ? 'Vis mindre' : 'Vis mindre') : (isEn ? 'Læs hele udtalelsen' : 'Læs hele udtalelsen')}</span>
                          <i className={`fa-light ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'} text-[10px]`} />
                        </button>
                      )}
                    </div>

                    {/* Author & Context Footer */}
                    <div className="mt-8 pt-6 border-t border-stone-100 flex items-center justify-between">
                      <div>
                        <h3 className="font-serif text-stone-900 text-lg font-medium">
                          {item.name}
                        </h3>
                        <span className="text-[11px] uppercase tracking-wider text-[#817262] font-medium block mt-0.5">
                          {item.title || (isEn ? "Client" : "Klient")}
                        </span>
                      </div>
                    </div>
                  </FadeIn>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Bottom CTA Section */}
      <section className="py-24 px-6 bg-white border-t border-stone-200/60 text-center">
        <div className="max-w-3xl mx-auto space-y-8">
          <FadeIn>
            <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
              {isEn ? "Are you ready to step inward?" : "Klar til at tage skridtet indad?"}
            </h2>
            <p className="text-stone-600 font-light text-lg max-w-xl mx-auto">
              {isEn 
                ? "Book a clairvoyant session or join one of my transformational programs." 
                : "Book en personlig clairvoyance-session eller deltag i et transformerende forløb."}
            </p>
            <div className="pt-8 flex flex-col sm:flex-row justify-center gap-4">
              <Link to={isEn ? "/en/sessions" : "/sessioner"} className="bg-[#004f3b] text-white px-8 py-4 uppercase text-xs tracking-[0.2em] font-bold hover:bg-emerald-900 transition-all rounded-sm shadow-xs">
                {isEn ? "See Sessions" : "Se sessioner"}
              </Link>
              <Link to={isEn ? "/en/contact" : "/kontakt"} className="border border-stone-900 text-stone-900 px-8 py-4 uppercase text-xs tracking-[0.2em] font-bold hover:bg-stone-900 hover:text-white transition-all rounded-sm">
                {isEn ? "Contact Me" : "Kontakt mig"}
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

const VirksomhedspraktikPage = () => {
  const { lang, t } = useLanguage();
  const { virksomhedspraktik } = useCms();
  const isEn = lang === 'en';
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    document.title = isEn 
      ? "Mette Brudvig - Internship | SoMe Content Creator & Brand Builder" 
      : "Mette Brudvig - Virksomhedspraktik | SoMe Content Creator & Brand Builder";
    window.scrollTo(0, 0);
  }, [isEn]);

  const getCmsText = (field, fallbackDa, fallbackEn) => {
    if (!virksomhedspraktik || !virksomhedspraktik[field]) return isEn ? fallbackEn : fallbackDa;
    const val = virksomhedspraktik[field];
    if (typeof val === 'string') return val;
    if (typeof val === 'object') {
      return (isEn ? (val.en || val.da) : (val.da || val.en)) || (isEn ? fallbackEn : fallbackDa);
    }
    return isEn ? fallbackEn : fallbackDa;
  };

  const getCmsHtml = (field, fallbackDaHtml, fallbackEnHtml) => {
    if (!virksomhedspraktik || !virksomhedspraktik[field]) return isEn ? fallbackEnHtml : fallbackDaHtml;
    const val = virksomhedspraktik[field];
    if (typeof val === 'string') return val;
    if (typeof val === 'object') {
      return (isEn ? (val.en || val.da) : (val.da || val.en)) || (isEn ? fallbackEnHtml : fallbackDaHtml);
    }
    return isEn ? fallbackEnHtml : fallbackDaHtml;
  };

  const getCmsImage = (field, fallbackUrl) => {
    if (virksomhedspraktik?.[field] && Array.isArray(virksomhedspraktik[field]) && virksomhedspraktik[field].length > 0 && virksomhedspraktik[field][0]) {
      return virksomhedspraktik[field][0];
    }
    return fallbackUrl;
  };

  const heroBadge = getCmsText('hero_badge', 'Virksomhedspraktik', 'Internship');
  const heroTitel = getCmsText('hero_titel', 'SoMe Content Creator & Brand Builder søges til virksomhedspraktik', 'SoMe Content Creator & Brand Builder for Internship');
  const heroUndertitel = getCmsText('hero_undertitel', 'Vær med på mit næste skridt i at skabe et stærkt brand', 'Join me on the next step of building a strong, authentic brand');
  const heroImage = getCmsImage('hero_billede', 'https://assets.auxodev.dk/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/mettebrudvigokt2021-111-1.jpg');
  
  const introHtml = getCmsHtml(
    'intro_tekst',
    `<p>Jeg søger en kreativ og dygtig virksomhedspraktikant, der har lyst til at være med helt tæt på, mens jeg bygger mit brand og tager det til næste niveau.</p><p>Mit arbejde handler om personlig frisættelse, indre lederskab, spiritualitet og om at bringe mere autenticitet og hjerte til mennesker og verden.</p><p>Nu leder jeg efter en, der kan hjælpe mig med at få budskaberne endnu stærkere ud.</p>`,
    `<p>I am looking for a creative and skilled intern who wants to be close to the journey as I build my brand and take it to the next level.</p><p>My work centers on personal freedom, inner leadership, spirituality, and bringing more authenticity and heart into the world.</p><p>Now I am seeking someone to help share these messages with greater impact.</p>`
  );

  const hvemErDuTitel = getCmsText('hvem_er_du_titel', 'Hvem er du?', 'Who are you?');
  const hvemErDuHtml = getCmsHtml(
    'hvem_er_du_tekst',
    `<p>Du skal elske at lave video og lækkert content.</p><p>Du skal kunne tage lyd fra min podcast, video og andet eksisterende materiale og omsætte det til kreative reels, videoer og opslag, der fanger, fungerer på sociale medier og får mennesker til at stoppe op.</p><p>Du skal have blik for, hvad der virker visuelt, kunne klippe lækkert og have lyst til at tænke kreativt sammen med mig.</p><p>Jeg søger ikke nødvendigvis en med at langt CV, bare du har talent, idéer, hjerte, energi og lyst til at skabe.</p>`,
    `<p>You love creating video and engaging content.</p><p>You can turn podcast audio, video, and materials into creative reels and social media posts that captivate and resonate.</p><p>You have an eye for visual aesthetics, crisp editing, and enjoy creative brainstorming.</p><p>I am looking for talent, passion, energy, and creativity rather than a long CV.</p>`
  );

  const udbytteTitel = getCmsText('udbytte_titel', 'Det får du ud af praktikken', 'What you get out of the internship');
  const udbyttePunkterHtml = getCmsHtml(
    'udbytte_punkter',
    `<ul><li>Du bliver en del af arbejdet med at bygge et brand fra tæt hold og får mulighed for selv at sætte et kreativt aftryk på udviklingen.</li><li>Du får erfaring med video, content, SoMe, storytelling, personligt brand og thought leadership.</li><li>Du får mulighed for at skabe materiale, der rent faktisk bliver brugt, og som du kan tage med videre i din portfolio.</li><li>Du får lov til at prøve dine idéer af og får løbende feedback på dit arbejde og er med i sparring omkring strategien.</li><li>Du får også mulighed for at samarbejde med mit bureau og få indblik i, hvordan vi arbejder professionelt med brand, kommunikation og markedsføring.</li><li>Og så bliver du en del af et brand, der er i udvikling, og som har en ambition om at nå langt flere mennesker - også internationalt</li></ul>`,
    `<ul><li>You become part of building a brand up close with the chance to leave your creative mark.</li><li>You gain hands-on experience with video, content, SoMe, storytelling, personal branding, and thought leadership.</li><li>You create real material used across channels that you can add to your portfolio.</li><li>You get to test your ideas with ongoing feedback and strategic sparring.</li><li>You will also collaborate with my agency and gain insight into professional branding, communication, and marketing.</li><li>You join a brand in growth with international ambitions.</li></ul>`
  );

  const praktikkenTitel = getCmsText('praktikken_titel', 'Praktikken', 'The Internship');
  const praktikkenHtml = getCmsHtml(
    'praktikken_tekst',
    `<p>Praktikken er for dig, der er ledig og har mulighed for fire ugers virksomhedspraktik gennem dit jobcenter.</p>`,
    `<p>The internship is for individuals eligible for a 4-week company internship through the job center.</p>`
  );

  const ansoegningTitel = getCmsText('ansoegning_titel', 'Sådan søger du', 'How to apply');
  const ansoegningHtml = getCmsHtml(
    'ansoegning_tekst',
    `<p>Du ansøger ved at sende mig en kort video af dig og dine tanker om, hvilken type content du har lyst til at arbejde med.</p><p>Videoen må meget gerne være lækkert klippet og vise din personlighed, kreativitet og dit blik for content.</p><p>De kandidater, der går videre, bliver inviteret til en samtale.</p><p>Herefter vælger jeg den kandidat, der bliver tilbudt praktikpladsen.</p><p>Har du lyst til at være med til at bygge et stærkt brand og skabe content, der når mennesker?</p><p>Så glæder jeg mig til at se din video, som sendes til mette@mettebrudvig.dk</p>`,
    `<p>You apply by sending me a short video of yourself and your thoughts on the type of content you want to create.</p><p>The video should showcase your personality, creativity, and eye for visual content and editing.</p><p>Selected candidates will be invited for an interview.</p><p>Would you like to be part of building an authentic brand and creating impactful content?</p><p>Send your video to mette@mettebrudvig.dk</p>`
  );

  const kontaktEmail = virksomhedspraktik?.kontakt_email || 'mette@mettebrudvig.dk';
  const podcastLink = virksomhedspraktik?.podcast_link || 'https://open.spotify.com/show/3w5L5y0dHLNa0FfIr6PVsL?si=7e87ec12043147c3';
  const hjemmesideLink = virksomhedspraktik?.hjemmeside_link || 'https://www.mettebrudvig.dk/';
  const hilsenTekst = getCmsText('hilsen_tekst', 'De bedste hilsner\nMette Brudvig', 'Warm regards\nMette Brudvig');

  const homePath = isEn ? '/en' : '/';
  const aboutPath = isEn ? '/en/about-mette' : '/om-mette';

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(kontaktEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="bg-[#faf9f6] min-h-screen">
      {/* Hero / Header Section */}
      <section className="relative min-h-[60vh] md:min-h-[70vh] pt-[185px] md:pt-[215px] lg:pt-[225px] pb-16 bg-[#f5efe9] border-b border-stone-200/60 overflow-hidden flex items-center">
        <div className="absolute inset-0 opacity-15 pointer-events-none bg-[radial-gradient(#817262_1px,transparent_1px)] [background-size:24px_24px]" />
        
        <div className="max-w-6xl mx-auto px-6 relative z-10 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7 space-y-6">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-serif text-stone-900 leading-[1.15] tracking-tight">
                {heroTitel}
              </h1>
              
              <p className="text-lg md:text-xl font-serif italic text-[#817262] leading-relaxed">
                {heroUndertitel}
              </p>

              <div 
                className="prose prose-stone text-stone-600 font-light text-base sm:text-lg leading-relaxed space-y-4 pt-2"
                dangerouslySetInnerHTML={{ __html: introHtml }}
              />

              <div className="pt-4 flex flex-wrap gap-4">
                <a
                  href={`mailto:${kontaktEmail}?subject=${encodeURIComponent(isEn ? 'Internship - SoMe Content Creator & Brand Builder' : 'Virksomhedspraktik - SoMe Content Creator & Brand Builder')}`}
                  className="inline-flex items-center space-x-3 bg-[#817262] text-white px-7 py-4 uppercase text-xs tracking-[0.2em] font-bold hover:bg-[#6e6052] transition-all rounded-sm shadow-sm cursor-pointer"
                >
                  <i className="fa-solid fa-video text-sm" />
                  <span>{isEn ? "Send video application" : "Send videoansøgning"}</span>
                </a>
                <button
                  type="button"
                  onClick={handleCopyEmail}
                  className="inline-flex items-center space-x-2 border border-[#817262]/40 text-stone-800 bg-white/80 hover:bg-white px-5 py-4 uppercase text-xs tracking-[0.2em] font-medium transition-all rounded-sm cursor-pointer"
                >
                  <i className={`fa-solid ${copied ? 'fa-check text-emerald-600' : 'fa-copy'}`} />
                  <span>{copied ? (isEn ? "Email copied!" : "Email kopieret!") : kontaktEmail}</span>
                </button>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative mx-auto max-w-md lg:max-w-none">
                <div className="relative rounded-sm overflow-hidden shadow-2xl border-4 border-white bg-white">
                  <img
                    src={heroImage.includes('assets.auxodev.dk') && !heroImage.includes('/cdn-cgi/image/') ? heroImage.replace('https://assets.auxodev.dk/', 'https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/') : heroImage}
                    alt="Mette Brudvig"
                    className="w-full h-[400px] sm:h-[480px] object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-900/40 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-xs p-4 rounded-xs border border-stone-200/80 text-center">
                    <p className="text-xs uppercase tracking-[0.25em] text-[#817262] font-semibold">
                      Mette Brudvig
                    </p>
                    <p className="text-[11px] text-stone-500 font-light mt-0.5">
                      {isEn ? "Spiritual Mentor & Thought Leader" : "Spirituel rådgiver, forfatter & facilitator"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Body */}
      <div className="max-w-6xl mx-auto px-6 py-20 space-y-20">
        
        {/* Section: Hvem du er */}
        <section className="bg-white rounded-sm p-8 md:p-12 border border-stone-200/80 shadow-xs space-y-8">
          <div className="flex items-center space-x-3 text-[#817262]">
            <span className="text-xl">✧</span>
            <h2 className="text-2xl md:text-3xl font-serif text-stone-900">
              {hvemErDuTitel}
            </h2>
          </div>
          
          <div 
            className="prose prose-stone max-w-none text-stone-600 font-light text-base sm:text-lg leading-relaxed space-y-5"
            dangerouslySetInnerHTML={{ __html: hvemErDuHtml }}
          />

          <div className="grid sm:grid-cols-2 gap-4 pt-4">
            <div className="p-5 rounded-sm bg-[#f5efe9]/50 border border-[#817262]/20 space-y-2">
              <div className="flex items-center space-x-2 text-[#817262] font-medium text-sm">
                <i className="fa-solid fa-clapperboard" />
                <span>{isEn ? "Video & Visuals" : "Video & Visuelt"}</span>
              </div>
              <p className="text-xs sm:text-sm text-stone-600 font-light leading-relaxed">
                {isEn ? "Reels, video clips, and creative formatting that catches attention on social media." : "Reels, videoklip og fængende formater til Instagram, Facebook og LinkedIn."}
              </p>
            </div>
            <div className="p-5 rounded-sm bg-[#f5efe9]/50 border border-[#817262]/20 space-y-2">
              <div className="flex items-center space-x-2 text-[#817262] font-medium text-sm">
                <i className="fa-solid fa-podcast" />
                <span>{isEn ? "Podcast & Storytelling" : "Podcast & Lydklip"}</span>
              </div>
              <p className="text-xs sm:text-sm text-stone-600 font-light leading-relaxed">
                {isEn ? "Turning audio and podcast episodes into engaging short-form stories." : "Omsætte lyd fra podcast og samtaler til inspirerende uddrag og opslag."}
              </p>
            </div>
          </div>
        </section>

        {/* Section: Det får du ud af praktikken */}
        <section className="space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-3">
            <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#817262]">
              {isEn ? "Benefits & Experience" : "Udbytte & Erfaring"}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
              {udbytteTitel}
            </h2>
          </div>

          <div className="bg-white rounded-sm p-8 md:p-12 border border-stone-200/80 shadow-xs">
            <div 
              className="prose prose-stone max-w-none text-stone-700 font-light text-base sm:text-lg leading-relaxed [&>ul]:space-y-4 [&>ul]:list-none [&>ul]:pl-0 [&>ul>li]:relative [&>ul>li]:pl-8 [&>ul>li]:before:content-['✧'] [&>ul>li]:before:absolute [&>ul>li]:before:left-0 [&>ul>li]:before:text-[#817262] [&>ul>li]:before:font-bold [&>ul>li]:before:text-lg"
              dangerouslySetInnerHTML={{ __html: udbyttePunkterHtml }}
            />
          </div>
        </section>

        {/* Section: Praktikken Framework */}
        <section className="bg-[#817262] text-white rounded-sm p-8 md:p-10 shadow-md">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <span className="inline-block uppercase tracking-[0.3em] text-[10px] font-bold bg-white/15 px-3 py-1 rounded-full text-white">
              {praktikkenTitel}
            </span>
            <div 
              className="text-lg md:text-xl font-light leading-relaxed [&>p]:leading-relaxed"
              dangerouslySetInnerHTML={{ __html: praktikkenHtml }}
            />
          </div>
        </section>

        {/* Section: Sådan søger du */}
        <section className="bg-[#f5efe9] border border-[#817262]/30 rounded-sm p-8 md:p-12 shadow-xs space-y-8">
          <div className="space-y-3">
            <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#817262]">
              {isEn ? "Application Process" : "Ansøgningsproces"}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
              {ansoegningTitel}
            </h2>
          </div>

          <div 
            className="prose prose-stone max-w-none text-stone-700 font-light text-base sm:text-lg leading-relaxed space-y-4"
            dangerouslySetInnerHTML={{ __html: ansoegningHtml }}
          />

          {/* Action Box */}
          <div className="bg-white p-6 sm:p-8 rounded-sm border border-[#817262]/25 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#817262]">
                  {isEn ? "Direct Email for Video Application" : "Direkte email til din videoansøgning"}
                </p>
                <p className="text-lg sm:text-xl font-serif text-stone-900 font-medium mt-1">
                  {kontaktEmail}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <a
                  href={`mailto:${kontaktEmail}?subject=${encodeURIComponent(isEn ? 'Internship - SoMe Content Creator & Brand Builder' : 'Virksomhedspraktik - SoMe Content Creator & Brand Builder')}`}
                  className="inline-flex items-center space-x-2 bg-[#817262] text-white px-6 py-3.5 uppercase text-xs tracking-[0.2em] font-bold hover:bg-[#6e6052] transition-all rounded-sm shadow-xs cursor-pointer"
                >
                  <i className="fa-solid fa-paper-plane text-xs" />
                  <span>{isEn ? "Open Email" : "Send mail"}</span>
                </a>
                <button
                  type="button"
                  onClick={handleCopyEmail}
                  className="inline-flex items-center space-x-2 border border-stone-300 text-stone-700 hover:bg-stone-50 px-4 py-3.5 uppercase text-xs tracking-[0.2em] font-medium transition-all rounded-sm cursor-pointer"
                >
                  <i className={`fa-solid ${copied ? 'fa-check text-emerald-600' : 'fa-copy'}`} />
                  <span>{copied ? (isEn ? "Copied" : "Kopieret") : (isEn ? "Copy" : "Kopiér")}</span>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Section: Du kan se mit arbejde her */}
        <section className="space-y-6">
          <h3 className="text-xs uppercase tracking-[0.3em] font-bold text-[#817262] text-center">
            {isEn ? "Explore My Work" : "Du kan se mit arbejde her"}
          </h3>
          
          <div className="grid sm:grid-cols-2 gap-6">
            <a
              href={podcastLink}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white p-6 sm:p-8 rounded-sm border border-stone-200 hover:border-[#817262] transition-all shadow-xs hover:shadow-md flex items-center space-x-5"
            >
              <div className="w-14 h-14 rounded-full bg-[#1DB954]/10 text-[#1DB954] flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                <i className="fa-brands fa-spotify text-2xl" />
              </div>
              <div className="space-y-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#817262] font-bold">
                  Podcast
                </p>
                <p className="text-base font-serif text-stone-900 group-hover:text-[#817262] transition-colors truncate">
                  {isEn ? "Listen on Spotify" : "Lyt med på Spotify"}
                </p>
                <p className="text-xs text-stone-500 font-light truncate">
                  {podcastLink}
                </p>
              </div>
            </a>

            <a
              href={hjemmesideLink}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white p-6 sm:p-8 rounded-sm border border-stone-200 hover:border-[#817262] transition-all shadow-xs hover:shadow-md flex items-center space-x-5"
            >
              <div className="w-14 h-14 rounded-full bg-[#817262]/10 text-[#817262] flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                <i className="fa-solid fa-globe text-2xl" />
              </div>
              <div className="space-y-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#817262] font-bold">
                  {isEn ? "Website" : "Hjemmeside"}
                </p>
                <p className="text-base font-serif text-stone-900 group-hover:text-[#817262] transition-colors truncate">
                  mettebrudvig.dk
                </p>
                <p className="text-xs text-stone-500 font-light truncate">
                  {hjemmesideLink}
                </p>
              </div>
            </a>
          </div>
        </section>

        {/* Sign-off */}
        <div className="pt-8 border-t border-stone-200/80 text-center space-y-2">
          <div className="font-serif italic text-stone-700 text-lg space-y-1">
            {hilsenTekst.split('\n').map((line, idx) => (
              <span key={idx} className="block">
                {line}
              </span>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

const BlogPage = () => {
  const { lang, t } = useLanguage();
  const { blogPosts } = useCms();
  const isEn = lang === 'en';

  useEffect(() => {
    document.title = isEn ? "Mette Brudvig - Blog | Articles & Insights" : "Mette Brudvig - Blog | Artikler & Indsigter";
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [isEn]);

  const activeBlogPosts = (blogPosts && blogPosts.length > 0) ? blogPosts : BLOG_POSTS;
  const featuredPost = activeBlogPosts[0] || BLOG_POSTS[0];
  const otherPosts = activeBlogPosts.slice(1);
  const podcastsPath = isEn ? '/en/podcasts' : '/podcasts';

  return (
    <div className="min-h-screen bg-[#faf9f6]">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] md:min-h-[70vh] pt-[185px] md:pt-[215px] lg:pt-[225px] pb-12 flex items-center bg-[#f5efe9] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center z-10 w-full">
          <FadeIn direction="right">
            <h1 className="text-4xl md:text-6xl font-serif text-stone-900 leading-[1.1] mb-6">
              {t('blogPage.title')}
            </h1>
            <p className="text-lg md:text-xl font-serif text-stone-800 leading-relaxed italic mb-4 max-w-lg">
              {isEn 
                ? "Inspiration, deep reflections, and personal writing on spiritual awakening, heart intelligence, and personal leadership." 
                : "Inspiration, dybe indsigter og personlige artikler om den nye tid, hjertets intelligens og den spirituelle transformation."}
            </p>
            <p className="text-stone-700 font-light text-base md:text-lg leading-relaxed max-w-lg">
              {t('blogPage.sub')}
            </p>
          </FadeIn>
        </div>
        <div className="absolute top-0 right-0 w-full md:w-1/2 h-full opacity-20 md:opacity-100 pt-20 md:pt-24 lg:pt-28 pointer-events-none">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/image_5.png" 
            alt="Mette Brudvig Blog" 
            className="w-full h-full object-cover object-top pointer-events-none"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#f5efe9] via-transparent to-transparent hidden md:block pointer-events-none" />
        </div>
      </section>

      {/* Main Blog Content */}
      <div className="py-20 md:py-28 px-6">
        <div className="max-w-6xl mx-auto space-y-16">
          {/* Featured Post */}
        {featuredPost && (
          <FadeIn delay={100}>
            <div className="bg-white border border-stone-200/70 shadow-sm rounded-sm overflow-hidden group">
              <div className="grid lg:grid-cols-12 items-stretch">
                <div className="lg:col-span-7 relative h-72 sm:h-96 lg:h-full min-h-[320px] overflow-hidden bg-stone-100">
                  <img 
                    src={imageUrl(featuredPost.image, 'width=1600,quality=95,format=auto')} 
                    alt={isEn && featuredPost.titleEn ? featuredPost.titleEn : featuredPost.title} 
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
                  />
                  <div className="absolute top-4 left-4 bg-[#004f3b] text-white text-[9px] uppercase tracking-widest font-bold px-3 py-1 rounded-sm shadow-sm">
                    {isEn && featuredPost.categoryEn ? featuredPost.categoryEn : featuredPost.category}
                  </div>
                </div>
                <div className="lg:col-span-5 p-8 sm:p-10 lg:p-12 flex flex-col justify-between space-y-6">
                  <div className="space-y-4">
                    <div className="text-stone-400 text-xs font-light">
                      {(isEn && featuredPost.dateEn ? featuredPost.dateEn : featuredPost.date)} • {isEn ? "By Mette Brudvig" : "Af Mette Brudvig"}
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-serif text-stone-900 group-hover:text-[#004f3b] transition-colors leading-snug">
                      {isEn && featuredPost.titleEn ? featuredPost.titleEn : featuredPost.title}
                    </h2>
                    <p className="text-stone-600 font-light text-sm sm:text-base leading-relaxed line-clamp-4">
                      {isEn && featuredPost.excerptEn ? featuredPost.excerptEn : featuredPost.excerpt}
                    </p>
                  </div>
                  <div className="pt-2">
                    <Link 
                      to={isEn ? `/en/blog/${featuredPost.id}` : `/blog/${featuredPost.id}`}
                      className="inline-flex items-center space-x-2 text-xs uppercase tracking-[0.2em] font-bold text-[#004f3b] hover:text-emerald-900 transition-colors"
                    >
                      <span>{t('blogPage.readFull')}</span>
                      <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        )}

        {/* Other Blog Posts Grid */}
        {otherPosts.length > 0 && (
          <FadeIn delay={150}>
            <div className="space-y-8">
              <div className="flex items-center justify-between border-b border-stone-200 pb-4">
                <h2 className="text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold">
                  {t('blogPage.moreArticles')}
                </h2>
                <span className="text-xs text-stone-400">
                  {otherPosts.length} {isEn ? (otherPosts.length === 1 ? "article" : "articles") : (otherPosts.length === 1 ? "artikel" : "artikler")}
                </span>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {otherPosts.map((post) => {
                  const postTitle = isEn && post.titleEn ? post.titleEn : post.title;
                  const postCategory = isEn && post.categoryEn ? post.categoryEn : post.category;
                  const postDate = isEn && post.dateEn ? post.dateEn : post.date;
                  const postExcerpt = isEn && post.excerptEn ? post.excerptEn : post.excerpt;

                  return (
                    <div key={post.id} className="bg-white border border-stone-100 shadow-sm rounded-sm overflow-hidden group flex flex-col justify-between hover:shadow-md transition-shadow">
                      <div>
                        <div className="relative h-52 overflow-hidden bg-stone-100">
                          <img 
                            src={imageUrl(post.image, 'width=800,quality=95,format=auto')} 
                            alt={postTitle} 
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
                          />
                          <div className="absolute top-3 left-3 bg-[#004f3b] text-white text-[8px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-sm">
                            {postCategory}
                          </div>
                        </div>
                        <div className="p-6 space-y-3">
                          <div className="text-stone-400 text-[11px] font-light">{postDate}</div>
                          <h3 className="text-xl font-serif text-stone-900 group-hover:text-[#004f3b] transition-colors line-clamp-2">
                            {postTitle}
                          </h3>
                          <p className="text-stone-600 font-light text-sm leading-relaxed line-clamp-3">
                            {postExcerpt}
                          </p>
                        </div>
                      </div>
                      <div className="p-6 pt-0">
                        <Link 
                          to={isEn ? `/en/blog/${post.id}` : `/blog/${post.id}`}
                          className="inline-flex items-center space-x-2 text-xs uppercase tracking-[0.15em] font-bold text-[#004f3b] hover:text-emerald-900 transition-colors"
                        >
                          <span>{t('blogPage.readMore')}</span>
                          <ArrowRight size={12} />
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </FadeIn>
        )}

        {/* Cross-Link Banner to Podcast */}
        <FadeIn delay={200}>
          <div className="bg-[#f5efe9] border border-[#817262]/30 rounded-sm p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="space-y-3 text-center md:text-left max-w-xl">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#817262]">
                <i className="fa-solid fa-microphone text-xs" />
                <span>{isEn ? "Prefer to listen?" : "Vil du også lytte med?"}</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-serif text-stone-900">
                {isEn ? "Listen to Mette's Podcasts" : "Lyt til Mettes Podcasts"}
              </h3>
              <p className="text-stone-600 font-light text-sm sm:text-base leading-relaxed">
                {isEn 
                  ? "Explore deep conversations on spiritual awakening, quantum physics, heart intelligence, and personal leadership on Spotify and Buzzsprout." 
                  : "Udforsk dybe samtaler om spirituel opvågning, kvantefysik, hjertets intelligens og personligt lederskab på Spotify og Buzzsprout."}
              </p>
            </div>
            <Link 
              to={podcastsPath}
              className="inline-flex items-center gap-3 bg-[#817262] text-white px-8 py-4 uppercase text-xs tracking-widest font-bold hover:bg-[#6e6153] transition-colors rounded-sm shadow-sm whitespace-nowrap shrink-0"
            >
              <span>{isEn ? "Go to Podcasts" : "Gå til Podcast"}</span>
              <ArrowRight size={14} />
            </Link>
          </div>
        </FadeIn>
      </div>
    </div>
  </div>
  );
};

const PodcastsPage = () => {
  const { lang, t } = useLanguage();
  const { podcasts, podcastEpisoder, fyraftenVideos } = useCms();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSeason, setSelectedSeason] = useState('all');
  const [activeEpisode, setActiveEpisode] = useState(null);
  const [playingVideoId, setPlayingVideoId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlayerMinimized, setIsPlayerMinimized] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [useIframeFallback, setUseIframeFallback] = useState(false);
  const [featuredVideoActive, setFeaturedVideoActive] = useState(false);
  const [synopsisExpanded, setSynopsisExpanded] = useState(false);
  const audioRef = useRef(null);
  const episodesSectionRef = useRef(null);
  const isEn = lang === 'en';

  useEffect(() => {
    document.title = isEn ? "Mette Brudvig - Podcast | Conversations on New Era Leadership" : "Mette Brudvig - Podcast | Samtaler om Ny Tids Lederskab";
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [isEn]);

  // Audio helper - guarantees the exact episode URL is loaded every single time
  const getEpisodeAudioUrl = (ep) => {
    if (!ep) return '';
    const resolved = resolveBuzzsproutEpisode(ep.title || ep.titleEn, ep.link, ep.episodeId);
    if (resolved?.audioUrl) return resolved.audioUrl;
    if (ep.audioUrl) return ep.audioUrl;
    if (ep.episodeId) return `https://www.buzzsprout.com/2530331/episodes/${ep.episodeId}.mp3`;
    if (ep.link && ep.link.includes('/episodes/') && !ep.link.endsWith('/episodes')) {
      const slug = ep.link.split('/episodes/')[1];
      return `https://www.buzzsprout.com/2530331/episodes/${slug}.mp3`;
    }
    return '';
  };

  const getEpisodeEmbedUrl = (ep) => {
    if (!ep) return '';
    const resolved = resolveBuzzsproutEpisode(ep.title || ep.titleEn, ep.link, ep.episodeId);
    if (resolved?.episodeId) {
      return `https://www.buzzsprout.com/2530331/${resolved.episodeId}?client_source=small_player&iframe=true`;
    }
    if (ep.episodeId) {
      return `https://www.buzzsprout.com/2530331/${ep.episodeId}?client_source=small_player&iframe=true`;
    }
    if (ep.link && ep.link.includes('/episodes/') && !ep.link.endsWith('/episodes')) {
      const slug = ep.link.split('/episodes/')[1];
      const idMatch = slug.match(/^(\d+)/);
      const epId = idMatch ? idMatch[1] : slug;
      return `https://www.buzzsprout.com/2530331/${epId}?client_source=small_player&iframe=true`;
    }
    return '';
  };

  const handlePlayEpisode = (ep) => {
    const resolved = resolveBuzzsproutEpisode(ep.title || ep.titleEn, ep.link, ep.episodeId);
    const enriched = {
      ...ep,
      episodeId: resolved?.episodeId || ep.episodeId || '',
      link: resolved?.link || ep.link,
      audioUrl: resolved?.audioUrl || ep.audioUrl
    };
    setActiveEpisode(enriched);
    setIsPlaying(true);
    setIsPlayerMinimized(false);
  };

  const handleClosePlayer = () => {
    setActiveEpisode(null);
    setIsPlaying(false);
  };

  const formatSeconds = (secs) => {
    if (!secs || isNaN(secs)) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const activePodcasts = (podcasts && podcasts.length > 0) ? podcasts : PODCASTS;
  const rawEpisodes = (podcastEpisoder && podcastEpisoder.length > 0) ? podcastEpisoder : BUZZSPROUT_EPISODES;

  const larsMuhlEpisode = rawEpisodes.find(ep => {
    const t = ((ep.title || ep.titleEn || '') + ' ' + (ep.id || '') + ' ' + (ep.episodeId || '')).toLowerCase();
    return t.includes('lars muhl') || t.includes('19908368');
  }) || rawEpisodes[0];

  const filteredEpisodes = rawEpisodes.filter(ep => {
    const epTitle = (isEn && ep.titleEn ? ep.titleEn : ep.title) || '';
    const epDesc = (isEn && ep.descriptionEn ? ep.descriptionEn : ep.description) || '';
    const epSeason = ep.season || '';

    const matchesSearch = searchTerm.trim() === '' || 
      epTitle.toLowerCase().includes(searchTerm.toLowerCase()) || 
      epDesc.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesSeason = selectedSeason === 'all' || 
      (selectedSeason === 's2' && (epSeason.toLowerCase().includes('sæson 2') || epSeason.toLowerCase().includes('season 2'))) ||
      (selectedSeason === 's1' && (epSeason.toLowerCase().includes('sæson 1') || epSeason.toLowerCase().includes('season 1')));

    return matchesSearch && matchesSeason;
  });

  const scrollToEpisodes = () => {
    if (episodesSectionRef.current) {
      episodesSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const blogPath = isEn ? '/en/blog' : '/blog';

  return (
    <div className="min-h-screen bg-[#faf9f6]">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] md:min-h-[70vh] pt-[185px] md:pt-[215px] lg:pt-[225px] pb-12 flex items-center bg-[#f5efe9] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center z-10 w-full">
          <FadeIn direction="right">
            <span className="text-xs uppercase tracking-[0.4em] font-bold text-[#817262] mb-4 block">
              {t('podcastsPage.badge')}
            </span>
            <h1 className="text-4xl md:text-6xl font-serif text-stone-900 leading-[1.1] mb-6">
              {t('podcastsPage.title')}
            </h1>
            <p className="text-lg md:text-xl text-stone-700 font-light leading-relaxed max-w-lg">
              {t('podcastsPage.sub')}
            </p>
          </FadeIn>
        </div>
        <div className="absolute top-0 right-0 w-full md:w-1/2 h-full opacity-20 md:opacity-100 pt-20 md:pt-24 lg:pt-28 pointer-events-none">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/billede-til-merete.jpg" 
            alt="Mette Brudvig Podcast" 
            className="w-full h-full object-cover object-top pointer-events-none"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#f5efe9] via-transparent to-transparent hidden md:block pointer-events-none" />
        </div>
      </section>

      {/* Featured Episode: Lars Muhl Showcase - Full Width */}
      <section id="lars-muhl" className="w-full bg-gradient-to-br from-[#2a2420] via-[#1f1b18] to-[#151210] border-y border-[#817262]/40 text-white relative overflow-hidden py-14 sm:py-16 md:py-20 px-6 sm:px-8 lg:px-12 scroll-mt-28 shadow-2xl">
        {/* Subtle background ambient blur */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#817262]/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#004f3b]/10 rounded-full blur-3xl pointer-events-none" />

        <FadeIn>
          <div className="relative z-10 flex flex-col items-center space-y-8 max-w-6xl mx-auto">
                {/* 1. OVER VIDEO: Meta badges, Title & Guest/Host */}
                <div className="w-full text-center max-w-3xl mx-auto space-y-3">
                  <div className="flex flex-wrap items-center justify-center gap-2.5">
                    <span className="inline-flex items-center gap-1.5 bg-[#817262]/30 text-[#e4ded8] text-xs font-medium px-3.5 py-1 rounded-full border border-[#817262]/40 backdrop-blur-xs">
                      <i className="fa-solid fa-microphone text-[10px] text-[#c2b19e]" />
                      <span>{isEn ? "Season 2 · Oct 4, 2026" : "Sæson 2 · 4. oktober 2026"}</span>
                    </span>
                    <span className="inline-flex items-center gap-1.5 bg-white/10 text-stone-300 text-xs font-mono px-3.5 py-1 rounded-full border border-white/10">
                      <i className="fa-regular fa-clock text-[10px] text-[#c2b19e]" />
                      <span>1:11:00</span>
                    </span>
                  </div>

                  <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-[2.6rem] font-serif text-white leading-[1.2] tracking-tight">
                    {isEn && larsMuhlEpisode?.titleEn ? (
                      larsMuhlEpisode.titleEn
                    ) : (
                      larsMuhlEpisode?.title === "Lars Muhl om mennesket, livet og det guddommelige" || !larsMuhlEpisode?.title ? (
                        <>Lars Muhl om mennesket, livet <span className="whitespace-nowrap">og det guddommelige</span></>
                      ) : (
                        larsMuhlEpisode.title.replace("og det guddommelige", "\u00A0og\u00A0det\u00A0guddommelige")
                      )
                    )}
                  </h2>
                  <p className="text-base sm:text-lg text-[#c2b19e] font-serif italic">
                    {isEn ? "With guest Lars Muhl · Hosted by Mette Brudvig" : "Gæst: Lars Muhl · Vært: Mette Brudvig"}
                  </p>
                </div>

                {/* 2. THE VIDEO: Stor og centreret */}
                <div className="w-full flex flex-col items-center">
                  <div className="w-full aspect-video rounded-xl overflow-hidden border border-white/20 shadow-2xl relative bg-stone-900 ring-1 ring-white/10">
                    <iframe
                      src="https://www.youtube-nocookie.com/embed/oQs6F6Q45To?rel=0&playsinline=1"
                      title="Lars Muhl om mennesket, livet og det guddommelige"
                      className="w-full h-full border-0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  </div>

                  {/* Elegant integreret action bar lige under videoen */}
                  <div className="w-full mt-4 p-3 sm:p-4 rounded-xl bg-black/40 border border-white/10 backdrop-blur-md flex flex-col sm:flex-row items-center justify-between gap-3 shadow-lg">
                    <div className="flex items-center justify-center sm:justify-start gap-2 text-xs text-[#c2b19e] text-center w-full sm:w-auto">
                      <span className="inline-block w-2 h-2 rounded-full bg-[#1DB954] animate-pulse shrink-0" />
                      <span className="font-medium">{isEn ? "Listen or watch on your preferred platform:" : "Lyt eller se på din foretrukne platform:"}</span>
                    </div>

                    <div className="flex flex-wrap items-center justify-center sm:justify-end gap-2 sm:gap-2.5 w-full sm:w-auto">
                      <button
                        type="button"
                        onClick={() => handlePlayEpisode(larsMuhlEpisode)}
                        className="inline-flex items-center gap-2 bg-[#817262] hover:bg-[#968574] text-white px-3.5 py-2 rounded-md text-xs font-bold tracking-wide transition-all shadow-sm hover:shadow hover:-translate-y-0.5 cursor-pointer whitespace-nowrap"
                      >
                        <i className="fa-solid fa-play text-[11px]" />
                        <span>{isEn ? "Play episode" : "Afspil afsnit"}</span>
                      </button>

                      <a
                        href="https://open.spotify.com/episode/3gpA8tDKGEjRLyQDCzZzWA?si=659be4df2af74954"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-[#1DB954] hover:bg-[#1aa34a] text-white px-3.5 py-2 rounded-md text-xs font-bold tracking-wide transition-all shadow-sm hover:shadow hover:-translate-y-0.5 whitespace-nowrap"
                      >
                        <i className="fa-brands fa-spotify text-sm" />
                        <span>Spotify</span>
                        <i className="fa-solid fa-arrow-up-right-from-square text-[9px] opacity-80" />
                      </a>

                      <a
                        href="https://youtu.be/oQs6F6Q45To?si=aPlPcf4uf6p22r-V"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-[#FF0000] hover:bg-[#cc0000] text-white px-3.5 py-2 rounded-md text-xs font-bold tracking-wide transition-all shadow-sm hover:shadow hover:-translate-y-0.5 whitespace-nowrap"
                      >
                        <i className="fa-brands fa-youtube text-sm" />
                        <span>YouTube</span>
                        <i className="fa-solid fa-arrow-up-right-from-square text-[9px] opacity-80" />
                      </a>

                      <a
                        href="https://www.buzzsprout.com/2530331/episodes/19908368"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-stone-200 px-3 py-2 rounded-md text-xs font-medium tracking-wide transition-all border border-white/15 hover:-translate-y-0.5 whitespace-nowrap"
                      >
                        <i className="fa-solid fa-podcast text-[#c2b19e] text-xs" />
                        <span>Buzzsprout</span>
                        <i className="fa-solid fa-arrow-up-right-from-square text-[9px] opacity-70" />
                      </a>
                    </div>
                  </div>
                </div>

                {/* 3. UNDER VIDEO: Teksten & Handlingsknapper */}
                <div className="w-full space-y-6 text-left">
                  {/* Synopsis box with collapsible content */}
                  <div className="rounded-xl bg-black/30 border border-white/10 p-6 sm:p-8 space-y-4 text-stone-200/90 font-light text-sm sm:text-base leading-relaxed shadow-inner">
                    <p className="font-medium text-white text-base sm:text-lg">
                      {isEn 
                        ? "Lars Muhl speaks about Jesus, Mary Magdalene, God, consciousness, life, and a near-death experience."
                        : "Lars Muhl fortæller om Jesus, Maria Magdalena, Gud, bevidsthed, livet og en nærdødsoplevelse."}
                    </p>

                    {synopsisExpanded && (
                      <div className="space-y-4 pt-1 transition-all duration-300">
                        <p className="text-stone-300 text-sm sm:text-[15px] leading-relaxed">
                          {isEn
                            ? "Lars tells of Jesus and Mary Magdalene as a couple and as two teachers who together represented the masculine and the feminine. On why both forces are necessary, and how central aspects of the original understanding were lost through translations, interpretations, and the way Christianity became organized religion."
                            : "Lars fortæller om Jesus og Maria Magdalena som et par og som to lærere, der sammen repræsenterede det maskuline og det feminine. Om hvorfor begge kræfter er nødvendige, og om hvordan centrale dele af den oprindelige forståelse er gået tabt gennem oversættelser, fortolkninger og den måde, vi har gjort kristendommen til religion."}
                        </p>
                        <p className="text-stone-300 text-sm sm:text-[15px] leading-relaxed">
                          {isEn
                            ? "He also shares a near-death experience that transformed his understanding of life, and encounters with the Seer of Andalusia. And the conversation ventures into the ultimate questions: What is a human being? What is consciousness? What is God?"
                            : "Han fortæller også om en nærdødsoplevelse, der forandrede hans forståelse af livet, og om oplevelser med seeren fra Andalusien. Og så bevæger samtalen sig ind i de helt store spørgsmål: Hvad er et menneske? Hvad er bevidsthed? Hvad er Gud?"}
                        </p>
                        <p className="font-serif italic text-[#e4ded8] text-sm sm:text-base pt-2 border-t border-white/10">
                          {isEn
                            ? "An open and consciousness-expanding conversation with Lars Muhl about the greatest questions in life."
                            : "En åben og bevidsthedsudvidende samtale med Lars Muhl om nogle af de største spørgsmål i livet."}
                        </p>
                      </div>
                    )}

                    <div className="pt-2">
                      <button
                        type="button"
                        onClick={() => setSynopsisExpanded(!synopsisExpanded)}
                        className="inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-[#c2b19e] hover:text-white transition-colors cursor-pointer group"
                      >
                        <span>
                          {synopsisExpanded 
                            ? (isEn ? "Fold sammen" : "Fold sammen")
                            : (isEn ? "Læs mere om afsnittet" : "Læs mere om afsnittet")}
                        </span>
                        <i className={`fa-solid fa-chevron-${synopsisExpanded ? 'up' : 'down'} text-[11px] transition-transform duration-200 group-hover:translate-y-${synopsisExpanded ? '-0.5' : '0.5'}`} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </FadeIn>
          </section>

      {/* Main Podcast Content */}
      <div className="py-20 md:py-28 px-6">
        <div className="max-w-6xl mx-auto space-y-16">

        {/* About the Podcast Series */}
        <div id="forbundet-i-den-nye-tid" className="scroll-mt-32">
          <FadeIn>
            <div className="bg-white border border-[#817262]/25 rounded-sm p-8 sm:p-10 md:p-12 shadow-sm w-full">
            <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
              <div className="md:col-span-4 flex justify-center">
                <div className="relative w-48 h-48 sm:w-56 sm:h-56 rounded-sm overflow-hidden border border-stone-200 shadow-md">
                  <img 
                    src="/kundebilleder/podcast1.png" 
                    alt="Forbundet i den nye tid cover" 
                    className="w-full h-full object-cover" 
                  />
                  <div className="absolute top-3 left-3 bg-stone-900/80 backdrop-blur-xs text-white text-[10px] uppercase font-bold tracking-wider px-2.5 py-1 rounded-xs flex items-center gap-1.5">
                    <i className="fa-solid fa-podcast text-[#c2b19e]" />
                    <span>Podcast</span>
                  </div>
                </div>
              </div>

              <div className="md:col-span-8 space-y-5 text-center md:text-left">
                <div>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-stone-900 leading-snug">
                    {isEn ? "Connected in the New Era" : "Forbundet i den nye tid"}
                  </h2>
                  <p className="text-xs text-stone-500 font-light mt-1">
                    {isEn ? "Host: Mette Brudvig" : "Vært: Mette Brudvig"}
                  </p>
                </div>

                <div className="space-y-3.5 text-stone-600 font-sans font-light leading-relaxed text-sm sm:text-base">
                  <p>
                    {isEn 
                      ? "We dive into spirituality, astrology, science, personal development, and society. Mette Brudvig is your host, searching for knowledge, insight, and solutions for the future."
                      : "Vi dykker ned i spiritualitet, astrologi, videnskab, personlig udvikling og samfund. Mette Brudvig er vært og på jagt efter viden, indsigt og fremtidens løsninger."}
                  </p>
                  <p>
                    {isEn
                      ? "She interviews some of the most inspiring people in the field and openly shares her own experiences and spiritual insights."
                      : "Hun interviewer nogle af de mest inspirerende mennesker på området og deler selv ærligt ud af sine egne erfaringer og spirituelle oplevelser."}
                  </p>
                  <p>
                    {isEn
                      ? "She is a mother, journalist, and clairvoyant, with a background as a boarding school teacher, communications manager, and commercial director."
                      : "Hun er familiemor, journalist og clairvoyant og har tidligere været blandt andet efterskolelærer, kommunikationsansvarlig og kommerciel direktør."}
                  </p>
                  <p className="text-stone-800 font-normal">
                    {isEn
                      ? "The podcast is for anyone curious about how, through the spiritual path, we can create greater well-being, sustainability, and peace within ourselves and in the world."
                      : "Podcasten er til dig, der er nysgerrig på, hvordan vi ad den spirituelle vej kan skabe mere trivsel, bæredygtighed og fred i os selv og i verden."}
                  </p>
                </div>

                {/* Direct Action Links */}
                <div className="pt-2 flex flex-wrap items-center justify-center md:justify-start gap-3.5">
                  <a
                    href="https://open.spotify.com/show/3w5L5y0dHLNa0FfIr6PVsL?si=fa3eaecb8d5b4a9b"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2.5 bg-[#1DB954] hover:bg-[#1aa34a] text-white px-5 py-3 rounded-xs text-xs font-semibold uppercase tracking-wider transition-all shadow-2xs hover:shadow-sm"
                  >
                    <i className="fa-brands fa-spotify text-base" />
                    <span>{isEn ? "Listen on Spotify" : "Lyt på Spotify"}</span>
                    <i className="fa-solid fa-arrow-up-right-from-square text-[10px] ml-0.5 opacity-80" />
                  </a>

                  <a
                    href="https://www.buzzsprout.com/2530331/episodes"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2.5 bg-[#f5efe9] hover:bg-[#ede3d8] text-stone-800 px-5 py-3 rounded-xs text-xs font-semibold uppercase tracking-wider transition-all border border-[#817262]/30 shadow-2xs hover:shadow-sm"
                  >
                    <i className="fa-solid fa-podcast text-[#817262] text-sm" />
                    <span>{isEn ? "Buzzsprout Archive" : "Buzzsprout Arkiv"}</span>
                    <i className="fa-solid fa-arrow-up-right-from-square text-[10px] ml-0.5 opacity-80" />
                  </a>

                  <a
                    href="https://www.youtube.com/@MetteBrudvig"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2.5 bg-[#FF0000] hover:bg-[#cc0000] text-white px-5 py-3 rounded-xs text-xs font-semibold uppercase tracking-wider transition-all shadow-2xs hover:shadow-sm"
                  >
                    <i className="fa-brands fa-youtube text-base" />
                    <span>{isEn ? "YouTube Channel" : "YouTube kanal"}</span>
                    <i className="fa-solid fa-arrow-up-right-from-square text-[10px] ml-0.5 opacity-80" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
      </div>

        {/* Buzzsprout Episodes Section */}
        <section ref={episodesSectionRef} className="space-y-8 pt-4 scroll-mt-28">
          <FadeIn>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-4 border-b border-stone-200">
              <div className="space-y-2">
                <h2 className="text-3xl sm:text-4xl font-serif text-stone-900">
                  {t('podcastsPage.episodesTitle')}
                </h2>
                <p className="text-stone-500 font-light text-sm sm:text-base max-w-2xl">
                  {t('podcastsPage.episodesSub')}
                </p>
              </div>

              {/* Filters & Search */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                {/* Search Input */}
                <div className="relative min-w-[240px]">
                  <i className="fa-solid fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400 text-xs" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('podcastsPage.searchPlaceholder')}
                    className="w-full pl-9 pr-8 py-2.5 bg-white border border-stone-200 rounded-sm text-xs text-stone-800 placeholder-stone-400 focus:outline-none focus:border-[#817262]"
                  />
                  {searchTerm && (
                    <button 
                      type="button" 
                      onClick={() => setSearchTerm('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 text-xs"
                    >
                      <i className="fa-solid fa-xmark" />
                    </button>
                  )}
                </div>

                {/* Season Filter Tabs */}
                <div className="inline-flex bg-white border border-stone-200 rounded-sm p-0.5 self-start sm:self-auto">
                  <button
                    type="button"
                    onClick={() => setSelectedSeason('all')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-xs transition-colors cursor-pointer ${
                      selectedSeason === 'all' 
                        ? 'bg-[#817262] text-white shadow-2xs' 
                        : 'text-stone-600 hover:text-stone-900'
                    }`}
                  >
                    {isEn ? "All" : "Alle"} ({rawEpisodes.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedSeason('s2')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-xs transition-colors cursor-pointer ${
                      selectedSeason === 's2' 
                        ? 'bg-[#817262] text-white shadow-2xs' 
                        : 'text-stone-600 hover:text-stone-900'
                    }`}
                  >
                    {isEn ? "Season 2" : "Sæson 2"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedSeason('s1')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-xs transition-colors cursor-pointer ${
                      selectedSeason === 's1' 
                        ? 'bg-[#817262] text-white shadow-2xs' 
                        : 'text-stone-600 hover:text-stone-900'
                    }`}
                  >
                    {isEn ? "Season 1" : "Sæson 1"}
                  </button>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* Episode Cards Grid */}
          {filteredEpisodes.length === 0 ? (
            <div className="bg-white border border-stone-200 rounded-sm p-12 text-center space-y-3">
              <i className="fa-solid fa-magnifying-glass text-stone-300 text-3xl mb-2" />
              <p className="text-stone-600 font-serif text-lg">{t('podcastsPage.noEpisodesFound')}</p>
              <button
                type="button"
                onClick={() => { setSearchTerm(''); setSelectedSeason('all'); }}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#817262] hover:text-stone-900"
              >
                <span>{isEn ? "Reset filters" : "Nulstil filtre"}</span>
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {filteredEpisodes.map((ep, idx) => {
                const resolved = resolveBuzzsproutEpisode(ep.title || ep.titleEn, ep.link, ep.episodeId);
                const epTitle = (isEn && ep.titleEn ? ep.titleEn : ep.title) || '';
                const epDesc = (isEn && ep.descriptionEn ? ep.descriptionEn : ep.description) || '';
                const epSeason = isEn && ep.seasonEn ? ep.seasonEn : ep.season;
                const epDate = isEn && ep.dateEn ? ep.dateEn : ep.date;
                const epImage = ep.image || (activePodcasts[0] && activePodcasts[0].image) || '/kundebilleder/podcast1.png';
                const epLink = resolved?.link || ep.link || 'https://www.buzzsprout.com/2530331/episodes';
                const epId = resolved?.episodeId || ep.episodeId;
                const isCurrentEpisode = Boolean(activeEpisode && (activeEpisode.id === ep.id || (activeEpisode.episodeId && epId && activeEpisode.episodeId === epId)));

                return (
                  <FadeIn key={ep.id || idx} delay={(idx % 6) * 60} className="flex flex-col h-full">
                    <div className={`bg-white border rounded-sm p-6 flex flex-col justify-between h-full transition-all duration-300 shadow-2xs group ${
                      isCurrentEpisode 
                        ? 'border-[#817262] ring-2 ring-[#817262]/30 shadow-md' 
                        : 'border-stone-200/90 hover:border-[#817262]/40 hover:shadow-md'
                    }`}>
                      <div className="space-y-4">
                        {/* Header: Thumbnail + Meta info */}
                        <div className="flex items-start gap-4">
                          <button
                            type="button"
                            onClick={() => handlePlayEpisode(ep)}
                            className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-sm overflow-hidden bg-stone-100 shrink-0 border border-stone-100 shadow-2xs cursor-pointer group/thumb text-left focus:outline-none focus:ring-2 focus:ring-[#817262]"
                            title={isEn ? "Play episode" : "Afspil episode"}
                          >
                            <img
                              src={epImage}
                              alt={epTitle}
                              className="w-full h-full object-cover group-hover/thumb:scale-105 transition-transform duration-300"
                            />
                            <div className={`absolute inset-0 flex items-center justify-center transition-all ${
                              isCurrentEpisode
                                ? 'bg-black/40 opacity-100'
                                : 'bg-black/25 opacity-0 group-hover/thumb:opacity-100'
                            }`}>
                              <span className="w-10 h-10 rounded-full bg-[#817262] text-white flex items-center justify-center shadow-md transform transition-transform group-hover/thumb:scale-110">
                                {isCurrentEpisode ? (
                                  <i className="fa-solid fa-headphones text-xs animate-bounce" />
                                ) : (
                                  <i className="fa-solid fa-play text-xs ml-0.5" />
                                )}
                              </span>
                            </div>
                          </button>

                          <div className="flex-1 min-w-0 space-y-1.5">
                            <div className="flex flex-wrap items-center gap-2">
                              {epSeason && (
                                <span className="inline-block bg-[#f5efe9] text-[#817262] text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-xs">
                                  {epSeason}
                                </span>
                              )}
                              {ep.duration && (
                                <span className="inline-flex items-center gap-1 text-stone-500 text-[11px] font-mono">
                                  <i className="fa-regular fa-clock text-[10px]" />
                                  <span>{ep.duration}</span>
                                </span>
                              )}
                              {isCurrentEpisode && (
                                <span className="inline-flex items-center gap-1.5 text-white text-[10px] font-bold tracking-wider uppercase px-2.5 py-0.5 rounded-xs shadow-2xs bg-[#817262] animate-pulse">
                                  <i className="fa-solid fa-headphones text-[9px]" />
                                  <span>{isEn ? "In player" : "I afspilleren"}</span>
                                </span>
                              )}
                            </div>

                            {epDate && (
                              <p className="text-[11px] text-stone-400 flex items-center gap-1.5">
                                <i className="fa-regular fa-calendar-days text-[10px]" />
                                <span>{epDate}</span>
                              </p>
                            )}

                            <h3 
                              onClick={() => handlePlayEpisode(ep)}
                              className="text-base sm:text-lg font-serif text-stone-900 group-hover:text-[#817262] hover:text-[#817262] transition-colors leading-snug line-clamp-2 cursor-pointer"
                              title={isEn ? "Play episode" : "Afspil episode"}
                            >
                              {epTitle}
                            </h3>
                          </div>
                        </div>

                        {/* Description */}
                        <p className="text-stone-600 font-light text-xs sm:text-sm leading-relaxed line-clamp-3">
                          {epDesc}
                        </p>
                      </div>

                      {/* Action Buttons */}
                      <div className="pt-4 mt-4 border-t border-stone-100 flex flex-wrap items-center justify-between gap-2.5">
                        <button
                          type="button"
                          onClick={() => handlePlayEpisode(ep)}
                          className={`inline-flex items-center gap-2 text-xs font-semibold tracking-wider uppercase transition-colors px-4 py-2.5 rounded-xs cursor-pointer shadow-2xs ${
                            isCurrentEpisode
                              ? 'bg-stone-900 text-white ring-2 ring-[#817262]'
                              : 'bg-[#817262] hover:bg-[#6e6153] text-white hover:shadow-xs'
                          }`}
                        >
                          <i className={`fa-solid ${isCurrentEpisode ? 'fa-headphones' : 'fa-play'} text-xs`} />
                          <span>
                            {isCurrentEpisode 
                              ? (isEn ? "Playing in corner 🎧" : "Spiller i hjørnet 🎧") 
                              : (isEn ? "Play episode" : "Afspil afsnit")}
                          </span>
                        </button>

                        <div className="flex items-center gap-2 flex-wrap">
                          {ep.spotifyLink && (
                            <a
                              href={ep.spotifyLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 bg-[#1DB954]/10 hover:bg-[#1DB954]/20 text-[#15803d] text-[11px] uppercase tracking-wider font-semibold px-3 py-1.5 rounded-sm transition-colors border border-[#1DB954]/25 shadow-2xs"
                              title={isEn ? "Listen on Spotify" : "Lyt på Spotify"}
                            >
                              <i className="fa-brands fa-spotify text-[#1DB954] text-xs" />
                              <span>Spotify</span>
                              <i className="fa-solid fa-arrow-up-right-from-square text-[9px]" />
                            </a>
                          )}

                          {ep.youtubeLink && (
                            <a
                              href={ep.youtubeLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 bg-[#FF0000]/10 hover:bg-[#FF0000]/15 text-[#cc0000] text-[11px] uppercase tracking-wider font-semibold px-3 py-1.5 rounded-sm transition-colors border border-[#FF0000]/25 shadow-2xs"
                              title={isEn ? "Watch episode on YouTube" : "Se afsnittet på YouTube"}
                            >
                              <i className="fa-brands fa-youtube text-[#FF0000] text-xs" />
                              <span>YouTube</span>
                              <i className="fa-solid fa-arrow-up-right-from-square text-[9px]" />
                            </a>
                          )}

                          <a
                            href={epLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 bg-[#f5efe9] hover:bg-[#ede3d8] text-stone-800 text-[11px] uppercase tracking-wider font-semibold px-3 py-1.5 rounded-sm transition-colors border border-[#817262]/20 shadow-2xs"
                          >
                            <span>{t('podcastsPage.listenOnBuzzsprout')}</span>
                            <i className="fa-solid fa-arrow-up-right-from-square text-[9px]" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </FadeIn>
                );
              })}
            </div>
          )}
        </section>

        {/* “Fyraften og sjælefred med Mette” Section */}
        <section id="fyraften-og-sjaelefred" className="space-y-8 pt-8 border-t border-stone-200/80 scroll-mt-32">
          <FadeIn>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-4 border-b border-stone-200">
              <div className="space-y-2">
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-bold text-[#cc0000]">
                  <i className="fa-brands fa-youtube text-base text-[#FF0000]" />
                  <span>{isEn ? "YouTube Series · Live Meditations" : "YouTube Serie · Live Meditationer"}</span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-serif text-stone-900">
                  {isEn ? "After-work & Soul Peace with Mette" : "Fyraften og sjælefred med Mette"}
                </h2>
                <p className="text-stone-600 font-light text-sm sm:text-base max-w-2xl leading-relaxed">
                  {isEn
                    ? "Here you can land in your body, find your peace, and gather your energy. Watch and listen to Mette's guided meditations and insights on consciousness, energy, and the new era."
                    : "Her kan du lande i din krop, finde din ro og samle din energi. Se og lyt til Mettes guidede meditationer og budskaber om bevidsthed, energi og den nye tidsalder."}
                </p>
              </div>

              <a
                href="https://www.youtube.com/@MetteBrudvig"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 bg-[#FF0000] hover:bg-[#cc0000] text-white px-5 py-3 rounded-xs text-xs font-semibold uppercase tracking-wider transition-all shadow-xs hover:shadow-sm shrink-0 self-start md:self-auto"
              >
                <i className="fa-brands fa-youtube text-base" />
                <span>{isEn ? "Visit YouTube Channel" : "Besøg kanalen på YouTube"}</span>
                <i className="fa-solid fa-arrow-up-right-from-square text-[10px] opacity-80" />
              </a>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(fyraftenVideos || []).map((video, vIdx) => {
              const isVideoActive = playingVideoId === video.videoId;
              const videoTitle = isEn && video.titleEn ? video.titleEn : video.title;
              const videoDesc = isEn && video.descriptionEn ? video.descriptionEn : video.description;
              const videoDate = isEn && video.dateEn ? video.dateEn : video.date;

              return (
                <FadeIn key={video.id || vIdx} delay={(vIdx % 3) * 80} className="flex flex-col h-full">
                  <div className="bg-white border border-stone-200/90 hover:border-[#817262]/40 rounded-sm overflow-hidden flex flex-col justify-between h-full transition-all duration-300 shadow-2xs hover:shadow-md group">
                    {/* Media container: Direct YouTube iframe (no thumbnail) */}
                    <div>
                      <div className="relative aspect-video bg-stone-900 overflow-hidden">
                        <iframe
                          src={`https://www.youtube-nocookie.com/embed/${video.videoId}?rel=0&playsinline=1`}
                          title={videoTitle}
                          className="w-full h-full border-0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          loading="lazy"
                        />
                      </div>

                      {/* Content */}
                      <div className="p-5 space-y-2.5">
                        <div className="flex items-center gap-2 text-[11px] text-stone-500 font-light">
                          <span>{videoDate}</span>
                          <span>•</span>
                          <span>Mette Brudvig</span>
                        </div>

                        <h3 className="font-serif text-base sm:text-lg text-stone-900 group-hover:text-[#817262] transition-colors leading-snug">
                          {videoTitle}
                        </h3>

                        <p className="text-stone-600 font-light text-xs leading-relaxed line-clamp-3">
                          {videoDesc}
                        </p>
                      </div>
                    </div>

                    {/* Bottom Action */}
                    <div className="p-5 pt-0 mt-3 border-t border-stone-100 flex items-center justify-end gap-3">
                      <a
                        href={video.youtubeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-[#cc0000] hover:text-[#990000] text-xs font-semibold uppercase tracking-wider transition-colors py-1"
                      >
                        <i className="fa-brands fa-youtube text-sm text-[#FF0000]" />
                        <span>{isEn ? "Open in YouTube" : "Åbn på YouTube"}</span>
                        <i className="fa-solid fa-arrow-up-right-from-square text-[9px]" />
                      </a>
                    </div>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </section>

        {/* Cross-Link Banner to Blog */}
        <FadeIn delay={200}>
          <div className="bg-[#f5efe9] border border-[#817262]/30 rounded-sm p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="space-y-3 text-center md:text-left max-w-xl">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#817262]">
                <i className="fa-solid fa-newspaper text-xs" />
                <span>{isEn ? "Prefer to read?" : "Vil du også læse med?"}</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-serif text-stone-900">
                {isEn ? "Explore Mette's Blog & Articles" : "Udforsk Mettes Blog & Artikler"}
              </h3>
              <p className="text-stone-600 font-light text-sm sm:text-base leading-relaxed">
                {isEn 
                  ? "Read personal insights, articles, and perspectives on the new era, heart intelligence, and personal transformation." 
                  : "Læs personlige indsigter, artikler og perspektiver på den nye tid, hjertets intelligens og personlig transformation."}
              </p>
            </div>
            <Link 
              to={blogPath}
              className="inline-flex items-center gap-3 bg-[#817262] text-white px-8 py-4 uppercase text-xs tracking-widest font-bold hover:bg-[#6e6153] transition-colors rounded-sm shadow-sm whitespace-nowrap shrink-0"
            >
              <span>{isEn ? "Go to Blog" : "Gå til Blog"}</span>
              <ArrowRight size={14} />
            </Link>
          </div>
        </FadeIn>
      </div>
    </div>

    {/* Floating Corner Audio Player (Nede i hjørnet) */}
    {activeEpisode && (
      <div 
        className={`fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 transition-all duration-300 ${
          isPlayerMinimized 
            ? 'w-auto' 
            : 'w-[calc(100vw-2rem)] sm:w-[420px] max-w-md'
        }`}
      >
        {isPlayerMinimized ? (
          /* Minimized Floating Pill */
          <div className="bg-[#2d2926] text-white rounded-full shadow-2xl p-2 pr-4 flex items-center gap-3 border border-[#817262]/40 backdrop-blur-md">
            <div className="relative w-10 h-10 rounded-full overflow-hidden shrink-0 border border-[#817262]">
              <img 
                src={activeEpisode.image || '/kundebilleder/podcast1.png'} 
                alt={activeEpisode.title} 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="min-w-0 max-w-[150px] sm:max-w-[200px]">
              <p className="text-xs font-serif font-medium text-white truncate">
                {isEn && activeEpisode.titleEn ? activeEpisode.titleEn : activeEpisode.title}
              </p>
              <p className="text-[10px] text-[#c2b19e] font-sans">
                {activeEpisode.duration || (isEn ? "Podcast Episode" : "Podcast Afsnit")}
              </p>
            </div>
            <div className="flex items-center gap-1.5 ml-auto">
              <button
                type="button"
                onClick={() => setIsPlayerMinimized(false)}
                className="w-8 h-8 rounded-full bg-[#817262] hover:bg-[#6e6153] text-white flex items-center justify-center transition-transform hover:scale-105 cursor-pointer shadow-xs"
                title={isEn ? "Open player" : "Åbn afspiller"}
              >
                <i className="fa-solid fa-up-right-and-down-left-from-center text-xs" />
              </button>
              <button
                type="button"
                onClick={handleClosePlayer}
                className="w-7 h-7 text-stone-400 hover:text-white flex items-center justify-center cursor-pointer transition-colors"
                title={isEn ? "Close" : "Luk"}
              >
                <i className="fa-solid fa-xmark text-xs" />
              </button>
            </div>
          </div>
        ) : (
          /* Full Corner Player Card with Buzzsprout Small Player */
          <div className="bg-[#2d2926] text-white rounded-xl shadow-2xl border border-[#817262]/40 overflow-hidden backdrop-blur-md p-4 sm:p-5 space-y-3">
            {/* Top row: Episode Info, Minimize & Close */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="relative w-12 h-12 rounded-sm overflow-hidden shrink-0 border border-[#817262]/50 shadow-xs">
                  <img 
                    src={activeEpisode.image || '/kundebilleder/podcast1.png'} 
                    alt={activeEpisode.title} 
                    className="w-full h-full object-cover" 
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[10px] uppercase tracking-wider text-[#c2b19e] font-bold">
                      {activeEpisode.season || "Forbundet i den nye tid"}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[9px] bg-[#817262] text-white px-1.5 py-0.2 rounded-xs uppercase font-semibold">
                      {isEn ? "Player" : "Afspiller"}
                    </span>
                  </div>
                  <h4 className="text-sm font-serif font-medium text-stone-100 truncate leading-snug">
                    {isEn && activeEpisode.titleEn ? activeEpisode.titleEn : activeEpisode.title}
                  </h4>
                  <p className="text-[11px] text-stone-400 font-light truncate">
                    {activeEpisode.duration ? `${activeEpisode.duration} • ` : ""}{activeEpisode.date || "Mette Brudvig"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1 shrink-0 -mt-1 -mr-1">
                <button
                  type="button"
                  onClick={() => setIsPlayerMinimized(true)}
                  className="w-7 h-7 text-stone-400 hover:text-white rounded-xs hover:bg-white/10 flex items-center justify-center transition-colors cursor-pointer"
                  title={isEn ? "Minimize" : "Minimer"}
                >
                  <i className="fa-solid fa-window-minimize text-[10px] -translate-y-1" />
                </button>
                <button
                  type="button"
                  onClick={handleClosePlayer}
                  className="w-7 h-7 text-stone-400 hover:text-white rounded-xs hover:bg-white/10 flex items-center justify-center transition-colors cursor-pointer"
                  title={isEn ? "Close" : "Luk"}
                >
                  <i className="fa-solid fa-xmark text-sm" />
                </button>
              </div>
            </div>

            {/* Embedded Buzzsprout Small Player */}
            <div className="rounded-sm overflow-hidden bg-white shadow-inner border border-stone-200">
              <iframe
                key={activeEpisode.episodeId || activeEpisode.id}
                src={`${getEpisodeEmbedUrl(activeEpisode)}&autoplay=true`}
                width="100%"
                height="190"
                frameBorder="0"
                scrolling="no"
                allow="autoplay; encrypted-media; fullscreen"
                title={isEn && activeEpisode.titleEn ? activeEpisode.titleEn : activeEpisode.title}
                className="w-full block"
              />
            </div>

            {/* Bottom Quick Links */}
            <div className="pt-2 border-t border-white/10 flex items-center justify-between text-xs text-stone-300">
              <a
                href="https://open.spotify.com/show/3w5L5y0dHLNa0FfIr6PVsL?si=fa3eaecb8d5b4a9b"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#1DB954] transition-colors inline-flex items-center gap-1.5"
              >
                <i className="fa-brands fa-spotify text-sm text-[#1DB954]" />
                <span>Spotify</span>
              </a>
              <a
                href={activeEpisode.link || "https://www.buzzsprout.com/2530331/episodes"}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors inline-flex items-center gap-1.5 text-stone-400"
              >
                <span>{isEn ? "Open on Buzzsprout" : "Åbn på Buzzsprout"}</span>
                <i className="fa-solid fa-arrow-up-right-from-square text-[9px]" />
              </a>
            </div>
          </div>
        )}
      </div>
    )}
  </div>
  );
};

// --- Dedicated Blog Post Page Component ---
const BlogPostPage = () => {
  const { id } = useParams();
  const { lang, t } = useLanguage();
  const { blogPosts } = useCms();

  const allPosts = (blogPosts && blogPosts.length > 0) ? blogPosts : BLOG_POSTS;
  const post = allPosts.find((p) => p.id === id || p.slugDa === id || p.slugEn === id) || BLOG_POSTS.find((p) => p.id === id);
  const isEn = lang === 'en';
  const blogPath = isEn ? '/en/blog' : '/blog';

  const postTitle = post ? (isEn && post.titleEn ? post.titleEn : post.title) : '';
  const postCategory = post ? (isEn && post.categoryEn ? post.categoryEn : post.category) : '';
  const postDate = post ? (isEn && post.dateEn ? post.dateEn : post.date) : '';
  const postContent = post ? (isEn && post.contentEn ? post.contentEn : post.content) : null;
  const postHtml = post ? (isEn && post.htmlContentEn ? post.htmlContentEn : (post.htmlContentDa || post.html)) : null;

  useEffect(() => {
    if (post) {
      document.title = `Mette Brudvig - ${postTitle}`;
    } else {
      document.title = isEn ? "Mette Brudvig - Blog" : "Mette Brudvig - Blog";
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [post, postTitle, isEn]);

  if (!post) {
    return (
      <div className="pt-[185px] md:pt-[215px] lg:pt-[225px] pb-48 px-6 text-center min-h-screen bg-[#faf9f6] flex flex-col items-center justify-center">
        <h1 className="text-3xl font-serif text-stone-900 mb-4">
          {isEn ? "Blog post not found" : "Blogindlæg ikke fundet"}
        </h1>
        <p className="text-stone-500 font-light mb-8">
          {isEn ? "The blog post you are looking for does not exist or has been moved." : "Det blogindlæg, du leder efter, findes desværre ikke eller er blevet flyttet."}
        </p>
        <Link to={blogPath} className="bg-[#004f3b] text-white px-8 py-3 uppercase text-xs tracking-[0.2em] font-bold hover:bg-emerald-900 transition-all">
          {t('common.backToBlog')}
        </Link>
      </div>
    );
  }

  // Find other posts for the "Andre læser også" section
  const otherPosts = allPosts.filter((p) => p.id !== id && p.slugDa !== id && p.slugEn !== id).slice(0, 2);

  return (
    <div className="pt-[185px] md:pt-[215px] lg:pt-[225px] pb-24 bg-[#faf9f6] min-h-screen px-6">
      <div className="max-w-3xl mx-auto">
        {/* Back Link */}
        <FadeIn>
          <Link 
            to={blogPath} 
            className="inline-flex items-center space-x-2 text-xs uppercase tracking-[0.2em] font-bold text-stone-500 hover:text-stone-900 transition-colors mb-12"
          >
            <i className="fa-solid fa-arrow-left text-xs" />
            <span>{t('common.backToBlog')}</span>
          </Link>
        </FadeIn>

        {/* Article Header */}
        <article className="space-y-10">
          <FadeIn delay={50} className="space-y-6">
            <span className="inline-block bg-[#004f3b] text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-sm">
              {postCategory}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-stone-900 leading-tight">
              {postTitle}
            </h1>
            <div className="flex items-center space-x-4 text-stone-500 text-sm font-light border-b border-stone-200 pb-6">
              <span>{isEn ? "By Mette Brudvig" : "Af Mette Brudvig"}</span>
              <span>•</span>
              <span>{postDate}</span>
            </div>
          </FadeIn>

          {/* Featured Image */}
          <FadeIn delay={100}>
            <div className="aspect-[16/10] md:aspect-[16/9] w-full overflow-hidden rounded-sm shadow-md bg-stone-100">
              <img 
                src={imageUrl(post.image, 'width=1600,quality=95,format=auto')} 
                alt={postTitle} 
                className="w-full h-full object-cover"
              />
            </div>
          </FadeIn>

          {/* Article Body */}
          <div className="text-stone-700 font-light text-lg md:text-xl leading-relaxed space-y-8 pt-6">
            {postContent && Array.isArray(postContent) && postContent.length > 0 ? (
              postContent.map((item, idx) => {
                if (item.type === 'p') {
                  // Apply drop cap to the very first paragraph
                  if (idx === 0) {
                    return (
                      <p key={idx} className="first-letter:text-5xl first-letter:font-serif first-letter:font-bold first-letter:float-left first-letter:mr-3 first-letter:text-[#004f3b] first-letter:leading-none">
                        {item.text}
                      </p>
                    );
                  }
                  return <p key={idx}>{item.text}</p>;
                }
                if (item.type === 'h3') {
                  return <h3 key={idx} className="text-2xl md:text-3xl font-serif text-stone-900 mt-12 mb-4 pt-4">{item.text}</h3>;
                }
                if (item.type === 'quote') {
                  return (
                    <blockquote key={idx} className="border-l-4 border-[#817262] pl-6 my-10 italic text-stone-900 text-xl md:text-2xl font-serif font-light bg-stone-50/50 py-4 pr-4 rounded-r-sm">
                      "{item.text}"
                    </blockquote>
                  );
                }
                if (item.type === 'audio') {
                  const audioTitle = isEn && item.titleEn ? item.titleEn : item.title;
                  return (
                    <div key={idx} className="my-10 p-6 bg-white border border-stone-200/60 rounded-sm shadow-sm space-y-4">
                      <p className="text-stone-800 font-medium text-base flex items-center gap-2">
                        <i className="fa-solid fa-headphones text-[#817262]" />
                        <span>{isEn ? "Listen to this insight and experience here:" : "Den indsigt og oplevelse kan du høre om her:"}</span>
                      </p>
                      <MeditationAudioPlayer 
                        title={audioTitle} 
                        duration={item.duration} 
                        durationSeconds={item.durationSeconds} 
                      />
                    </div>
                  );
                }
                return null;
              })
            ) : postHtml ? (
              <div 
                className="space-y-6 [&>p]:leading-relaxed [&>p:first-of-type]:first-letter:text-5xl [&>p:first-of-type]:first-letter:font-serif [&>p:first-of-type]:first-letter:font-bold [&>p:first-of-type]:first-letter:float-left [&>p:first-of-type]:first-letter:mr-3 [&>p:first-of-type]:first-letter:text-[#004f3b] [&>p:first-of-type]:first-letter:leading-none [&>h3]:text-2xl [&>h3]:md:text-3xl [&>h3]:font-serif [&>h3]:text-stone-900 [&>h3]:mt-12 [&>h3]:mb-4 [&>h3]:pt-4 [&>blockquote]:border-l-4 [&>blockquote]:border-[#817262] [&>blockquote]:pl-6 [&>blockquote]:my-10 [&>blockquote]:italic [&>blockquote]:text-stone-900 [&>blockquote]:text-xl [&>blockquote]:md:text-2xl [&>blockquote]:font-serif [&>blockquote]:font-light [&>blockquote]:bg-stone-50/50 [&>blockquote]:py-4 [&>blockquote]:pr-4 [&>blockquote]:rounded-r-sm"
                dangerouslySetInnerHTML={{ __html: postHtml }} 
              />
            ) : null}
          </div>
        </article>

        {/* Share & Newsletter Divider */}
        <div className="border-t border-stone-200 mt-20 pt-12">
          <div className="bg-white border border-stone-200/60 rounded-sm p-8 text-center space-y-6 shadow-sm">
            <h3 className="text-2xl font-serif text-stone-900">{t('newsletter.popupTitle')}</h3>
            <p className="text-stone-500 font-light text-sm max-w-md mx-auto">
              {t('newsletter.popupDesc')}
            </p>
            <form 
              id="signup" 
              action="https://e.lumant.dk/api/v1/optin/list_6v-RTZGckC/subscribe" 
              method="post" 
              className="flex flex-col sm:flex-row gap-2 max-w-lg mx-auto"
            >
              <input type="hidden" name="ref" value="" />
              <div style={{ position: 'absolute', left: '-5000px' }} aria-hidden="true">
                <input type="text" name="hp_reference" tabIndex={-1} autoComplete="off" />
              </div>

              <input 
                type="text" 
                name="name" 
                placeholder="Dit navn" 
                className="flex-1 px-4 py-3 border border-stone-200 rounded-sm text-sm focus:outline-none focus:border-[#817262] bg-stone-50 text-stone-900"
              />
              <input 
                type="email" 
                name="email" 
                placeholder="Din e-mail" 
                pattern="[^@\s]+@[^@\s]+\.[^@\s]+" 
                required 
                className="flex-1 px-4 py-3 border border-stone-200 rounded-sm text-sm focus:outline-none focus:border-[#817262] bg-stone-50 text-stone-900"
              />
              <input 
                type="submit" 
                name="submit" 
                value="TILMELD MIG NU" 
                className="bg-[#817262] hover:bg-[#6e6153] text-white px-6 py-3 uppercase text-xs tracking-widest font-bold transition-all rounded-sm whitespace-nowrap cursor-pointer shrink-0"
              />
            </form>
          </div>
        </div>

        {/* "Andre læser også" Section */}
        {otherPosts.length > 0 && (
          <div className="mt-24 space-y-8">
            <h3 className="text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold border-b border-stone-200 pb-3">
              {isEn ? "Others also read" : "Andre læser også"}
            </h3>
            <div className="grid md:grid-cols-2 gap-8">
              {otherPosts.map((otherPost) => {
                const oTitle = isEn && otherPost.titleEn ? otherPost.titleEn : otherPost.title;
                const oCat = isEn && otherPost.categoryEn ? otherPost.categoryEn : otherPost.category;
                const oDate = isEn && otherPost.dateEn ? otherPost.dateEn : otherPost.date;
                const oExcerpt = isEn && otherPost.excerptEn ? otherPost.excerptEn : otherPost.excerpt;

                return (
                  <Link 
                    key={otherPost.id} 
                    to={isEn ? `/en/blog/${otherPost.id}` : `/blog/${otherPost.id}`} 
                    className="group block bg-white border border-stone-100 shadow-sm rounded-sm overflow-hidden flex flex-col justify-between hover:shadow-md transition-shadow"
                  >
                    <div>
                      <div className="relative h-48 overflow-hidden">
                        <img 
                          src={otherPost.image} 
                          alt={oTitle} 
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
                        />
                        <div className="absolute top-3 left-3 bg-[#004f3b] text-white text-[8px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm">
                          {oCat}
                        </div>
                      </div>
                      <div className="p-6 space-y-2">
                        <div className="text-stone-400 text-[11px] font-light">{oDate}</div>
                        <h4 className="text-lg font-serif text-stone-900 group-hover:text-[#004f3b] transition-colors line-clamp-2">
                          {oTitle}
                        </h4>
                        <p className="text-stone-600 font-light text-xs leading-relaxed line-clamp-2">
                          {oExcerpt}
                        </p>
                      </div>
                    </div>
                    <div className="p-6 pt-0">
                      <span className="inline-flex items-center space-x-2 text-xs uppercase tracking-[0.15em] font-bold text-[#004f3b] group-hover:text-emerald-900 transition-colors">
                        <span>{t('common.readMore')}</span>
                        <i className="fa-solid fa-arrow-right text-[10px]" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// --- Meditation Audio Player Component ---
const MeditationAudioPlayer = ({ title, duration, durationSeconds }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setCurrentTime((prev) => {
          if (prev >= durationSeconds) {
            setIsPlaying(false);
            return 0;
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, durationSeconds]);

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleScrub = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const percentage = clickX / width;
    setCurrentTime(Math.floor(percentage * durationSeconds));
  };

  return (
    <div className="bg-stone-50 border border-stone-200/60 rounded-sm p-4 max-w-md my-4 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-widest text-stone-400 font-medium">Meditation & Lyd</span>
        <span className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Montserrat Frekvens</span>
      </div>
      <div className="flex items-center space-x-4">
        <button 
          onClick={handlePlayPause}
          type="button"
          className="w-10 h-10 rounded-full bg-[#004f3b] text-white flex items-center justify-center hover:bg-emerald-900 transition-colors shadow-sm cursor-pointer"
        >
          {isPlaying ? (
            <i className="fa-solid fa-pause text-xs" />
          ) : (
            <i className="fa-solid fa-play text-xs ml-0.5" />
          )}
        </button>
        <div className="flex-grow">
          <div className="text-xs font-medium text-stone-800 mb-1 line-clamp-1">{title}</div>
          <div 
            onClick={handleScrub}
            className="h-1 bg-stone-200 rounded-full overflow-hidden cursor-pointer relative"
          >
            <div 
              className="h-full bg-[#004f3b] transition-all duration-300"
              style={{ width: `${(currentTime / durationSeconds) * 100}%` }}
            />
          </div>
          <div className="flex justify-between text-[9px] text-stone-400 mt-1 font-mono">
            <span>{formatTime(currentTime)}</span>
            <span>{duration}</span>
          </div>
        </div>
        <button 
          onClick={() => setIsMuted(!isMuted)}
          type="button"
          className="text-stone-400 hover:text-stone-600 transition-colors cursor-pointer"
        >
          {isMuted ? (
            <i className="fa-solid fa-volume-xmark text-sm" />
          ) : (
            <i className="fa-solid fa-volume-high text-sm" />
          )}
        </button>
      </div>
    </div>
  );
};

// --- Retreats Page Component ---
const RetreatsPage = () => {
  const [activeTab, setActiveTab] = useState('all');
  const { lang, t } = useLanguage();
  const location = useLocation();
  const isEn = lang === 'en';

  useEffect(() => {
    document.title = isEn 
      ? "Mette Brudvig - Retreats in Denmark & Spain" 
      : "Mette Brudvig - Retreats i Danmark & Spanien";

    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [isEn, location.hash]);

  const montserratLink = isEn ? "/en/montserrat" : "/montserrat";
  const costaDelSolLink = isEn ? "/en/costa-del-sol" : "/costa-del-sol";
  const thailandLink = isEn ? "/en/thailand-retreat" : "/thailand-retreat";
  const contactLink = isEn ? "/en/contact" : "/kontakt";

  return (
    <div className="min-h-screen bg-[#faf9f6] text-stone-800">
      {/* Light & Airy Hero Section */}
      <section className="relative pt-[185px] md:pt-[215px] lg:pt-[225px] pb-20 md:pb-28 px-6 bg-[#f5efe9] overflow-hidden border-b border-stone-200">
        <div className="absolute inset-0 z-0 opacity-40">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/malaga-palm-trees-retreat.jpg" 
            alt="Retreats af Mette Brudvig" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#f5efe9]/90 via-[#f5efe9]/80 to-[#faf9f6]"></div>
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-8 md:gap-12 items-center">
            {/* Left Column: Text & Navigation Buttons */}
            <div className="lg:col-span-7 space-y-6 text-left">
              <FadeIn>
                <span className="text-xs uppercase tracking-[0.4em] font-bold text-[#817262] mb-3 block">
                  {isEn ? "Immersion, Stillness & Spiritual Awakening" : "Fordybelse, Ro & Sjælelig Opvågning"}
                </span>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-stone-900 leading-tight">
                  {isEn ? "Retreats in Denmark and Spain" : "Retreats i Danmark og Spanien"}
                </h1>
                <div className="h-0.5 w-20 bg-[#817262] my-6"></div>
              </FadeIn>
              <FadeIn delay={150}>
                <p className="text-lg md:text-xl font-light text-stone-700 leading-relaxed">
                  {isEn 
                    ? "Join one of my three unique retreats designed to help you ground in your body, quiet your mind, and create a deeper connection to your soul."
                    : "Tag med på et af mine tre unikke retreats skabt til at hjælpe dig med at lande i kroppen, finde ro i sindet og skabe en dybere forbindelse til din sjæl."}
                </p>
              </FadeIn>
              
              {/* Quick Navigation Cards / Filter Bar */}
              <FadeIn delay={250} className="pt-4">
                <p className="text-xs uppercase tracking-[0.3em] text-stone-500 font-medium mb-3">
                  {isEn ? "Explore the three retreats" : "Udforsk de tre retreats"}
                </p>
                <div className="flex flex-wrap gap-2.5">
                  <Link 
                    to={montserratLink} 
                    className="bg-white border border-stone-300 hover:border-[#817262] px-5 py-2.5 rounded-full text-xs font-medium uppercase tracking-wider text-stone-800 hover:text-[#817262] transition-all shadow-sm"
                  >
                    1. Montserrat
                  </Link>
                  <Link 
                    to={costaDelSolLink} 
                    className="bg-white border border-stone-300 hover:border-[#817262] px-5 py-2.5 rounded-full text-xs font-medium uppercase tracking-wider text-stone-800 hover:text-[#817262] transition-all shadow-sm"
                  >
                    2. Costa del Sol
                  </Link>
                  <Link 
                    to={thailandLink} 
                    className="bg-white border border-stone-300 hover:border-[#817262] px-5 py-2.5 rounded-full text-xs font-medium uppercase tracking-wider text-stone-800 hover:text-[#817262] transition-all shadow-sm"
                  >
                    3. Thailand
                  </Link>
                </div>
              </FadeIn>
            </div>

            {/* Right Column: General Retreat Video */}
            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <FadeIn delay={200} className="w-full max-w-[340px] sm:max-w-[360px]">
                <YouTubeEmbed 
                  url="https://youtube.com/shorts/-GWn3JeJ8Q8?feature=share" 
                  title={isEn ? "Intro to retreats" : "Intro til retreats"} 
                />
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* THREE DISTINCT RETREATS SECTION */}
      <div className="space-y-24 py-20 px-6 max-w-6xl mx-auto">

        {/* 1. MONTSERRAT RETREAT */}
        <section id="montserrat" className="scroll-mt-40 md:scroll-mt-48 bg-white border border-stone-200/80 rounded-sm p-8 md:p-14 shadow-sm hover:shadow-md transition-shadow">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-6">
              <h2 className="text-3xl md:text-5xl font-serif text-stone-900 leading-tight">
                Montserrat
              </h2>
              <p className="text-xl font-serif italic text-[#004f3b]">
                {isEn ? "6 days of immersion, grounding, and spiritual awakening" : "6 dage med fordybelse, forankring og sjælelig opvågning"}
              </p>
              <div className="h-0.5 w-16 bg-[#004f3b]"></div>
              <div className="text-stone-600 font-light text-base md:text-lg leading-relaxed space-y-4">
                <p>
                  {isEn 
                    ? "Join me in Northern Spain in Catalonia's sacred mountains and divine cathedral, where monk and boy choirs sing your soul awake. Here, sacred feminine energy is profound and veil between worlds is thin."
                    : "Tag med til Nordspanien i Cataloniens hellige bjerge og guddommelige katedral, hvor munke- og drengekor synger din sjæl vågen. Her er den hellige feminine energi stærk, og slørene til din bevidsthed er tynde."}
                </p>
                <p>
                  {isEn 
                    ? "On this pilgrimage you gain clarity on your true authentic divine self. Through guided meditations and sacred energy teachings, you return home to your soul core."
                    : "På denne rejse får du forståelse for den sande, autentiske guddommelige version af dig. Gennem meditationer og undervisning i de hellige energier rejser du hjem til din sjælskerne."}
                </p>
              </div>
              <div className="pt-4 flex flex-wrap gap-4 items-center">
                <Link 
                  to={montserratLink} 
                  className="inline-flex items-center space-x-3 bg-[#004f3b] text-white px-8 py-3.5 uppercase text-xs tracking-[0.2em] font-bold hover:bg-emerald-950 transition-all rounded-sm shadow-sm"
                >
                  <span>{isEn ? "Read more about Montserrat retreat" : "Læs mere om Montserrat retreat"}</span>
                  <i className="fa-light fa-arrow-right" />
                </Link>
                <a 
                  href="tel:+4527507083" 
                  className="inline-flex items-center space-x-2 text-stone-700 hover:text-[#004f3b] uppercase text-xs tracking-[0.15em] font-bold px-4 py-3.5 transition-colors border border-stone-200 hover:border-[#004f3b] rounded-sm bg-white"
                >
                  <i className="fa-solid fa-phone text-xs text-[#004f3b]" />
                  <span>{isEn ? "Call to hear more: 27 50 70 83" : "Ring, og hør mere: 27 50 70 83"}</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative aspect-[4/3] md:aspect-[3/4] overflow-hidden rounded-sm border border-stone-200 shadow-sm">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/montserrat-retreat.jpg" 
                  alt="Montserrat bjerge og kloster" 
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </div>
        </section>

        {/* 2. COSTA DEL SOL RETREAT */}
        <section id="costa-del-sol" className="scroll-mt-40 md:scroll-mt-48 bg-white border border-stone-200/80 rounded-sm p-8 md:p-14 shadow-sm hover:shadow-md transition-shadow">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-6">
              <h2 className="text-3xl md:text-5xl font-serif text-stone-900 leading-tight">
                {isEn ? "Hjem til krop og sjæl – Spiritual Retreat on Costa del Sol" : "Hjem til krop og sjæl – spirituelt retreat på Costa del Sol"}
              </h2>
              <p className="text-xl font-serif italic text-[#004f3b] font-medium">
                {isEn 
                  ? "5 days from October 6th–11th"
                  : "5 dage fra den 6.–11. oktober"}
              </p>
              <div className="h-0.5 w-16 bg-[#004f3b]"></div>
              <div className="text-stone-600 font-light text-base md:text-lg leading-relaxed space-y-4">
                <p>
                  {isEn 
                    ? "Step out of everyday life into a lush Andalusian paradise in the late South Spanish summer on beautiful La Fuente."
                    : "Her træder du ud af hverdagen og ind i et frodigt andalusisk paradis i den sydspanske sensommer på smukke La Fuente."}
                </p>
                <p>
                  {isEn 
                    ? "Through gentle yoga exercises, breathwork, healing meditations, sound healing, and cacao ceremony, you strengthen the connection with your soul."
                    : "Gennem blide yogaøvelser, åndedræt, healende meditationer, lydhealing og cacaoceremoni styrker du forbindelsen med din sjæl og mærker dig selv."}
                </p>
              </div>
              <div className="pt-4 flex flex-wrap gap-4 items-center">
                <Link 
                  to={costaDelSolLink} 
                  className="inline-flex items-center space-x-3 bg-[#004f3b] text-white px-8 py-3.5 uppercase text-xs tracking-[0.2em] font-bold hover:bg-emerald-950 transition-all rounded-sm shadow-sm"
                >
                  <span>{isEn ? "Read more about Costa del Sol retreat" : "Læs mere om Costa del Sol retreat"}</span>
                  <i className="fa-light fa-arrow-right" />
                </Link>
                <a 
                  href="tel:+4527507083" 
                  className="inline-flex items-center space-x-2 text-stone-700 hover:text-[#004f3b] uppercase text-xs tracking-[0.15em] font-bold px-4 py-3.5 transition-colors border border-stone-200 hover:border-[#004f3b] rounded-sm bg-white"
                >
                  <i className="fa-solid fa-phone text-xs text-[#004f3b]" />
                  <span>{isEn ? "Call to hear more: 27 50 70 83" : "Ring, og hør mere: 27 50 70 83"}</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative aspect-[4/3] md:aspect-[3/4] overflow-hidden rounded-sm border border-stone-200 shadow-sm">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/4b44ef21-4f88-42cb-bc9a-b504fb86f0b6_361c7dfa.jpg" 
                  alt="Hjem til krop og sjæl - Costa del Sol retreat" 
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </div>
        </section>

        {/* 3. THAILAND RETREAT */}
        <section id="thailand-retreat" className="scroll-mt-40 md:scroll-mt-48 bg-white border border-stone-200/80 rounded-sm p-8 md:p-14 shadow-sm hover:shadow-md transition-shadow">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-6">
              <h2 className="text-3xl md:text-5xl font-serif text-stone-900 leading-tight">
                {isEn ? "Home to Body & Soul – Spiritual Retreat in Thailand" : "Hjem til krop og sjæl – spirituelt retreat i Thailand"}
              </h2>
              <p className="text-xl font-serif italic text-[#004f3b]">
                {isEn 
                  ? "10 days in late January / early February – exact dates coming soon"
                  : "10 dage i slutningen af januar/starten af februar – præcise datoer kommer snart"}
              </p>
              <div className="h-0.5 w-16 bg-[#004f3b]"></div>
              <div className="text-stone-600 font-light text-base md:text-lg leading-relaxed space-y-4">
                <p>
                  {isEn 
                    ? "Step out of daily life into Thailand's warm, lush and sensual world. The retreat takes place in a scenic location surrounded by tropical nature, where there is room for peace, immersion, and warm sunny days."
                    : "Træd ud af hverdagen og ind i Thailands varme, frodige og sanselige verden. Retreatet kommer til at foregå et naturskønt sted omgivet af tropisk natur, hvor der er plads til både ro, fordybelse og lange dage i varmen."}
                </p>
                <p>
                  {isEn 
                    ? "Together with Mette and Louise, experience gentle yoga, TRE, sound healing, cacao ceremony, guided spiritual meditations, and a included day trip in Thailand's magnificent nature."
                    : "Sammen med Mette og Louise oplever du blid yoga, TRE, lydhealing, cacaoceremoni, guidede spirituelle meditationer og en inkluderet heldagsudflugt i den storslåede thailandske natur."}
                </p>
              </div>
              <div className="pt-4 flex flex-wrap gap-4 items-center">
                <Link 
                  to={thailandLink} 
                  className="inline-flex items-center space-x-3 bg-[#004f3b] text-white px-8 py-3.5 uppercase text-xs tracking-[0.2em] font-bold hover:bg-emerald-950 transition-all rounded-sm shadow-sm"
                >
                  <span>{isEn ? "Read more about Thailand retreat" : "Læs mere om Thailand retreat"}</span>
                  <i className="fa-light fa-arrow-right" />
                </Link>
                <a 
                  href="tel:+4527507083" 
                  className="inline-flex items-center space-x-2 text-stone-700 hover:text-[#004f3b] uppercase text-xs tracking-[0.15em] font-bold px-4 py-3.5 transition-colors border border-stone-200 hover:border-[#004f3b] rounded-sm bg-white"
                >
                  <i className="fa-solid fa-phone text-xs text-[#004f3b]" />
                  <span>{isEn ? "Call to hear more: 27 50 70 83" : "Ring, og hør mere: 27 50 70 83"}</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative aspect-[4/3] md:aspect-[3/4] overflow-hidden rounded-sm border border-stone-200 shadow-sm">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/thailand-retreat-hero.jpg" 
                  alt="Spirituelt retreat i Thailand" 
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};












const getYouTubeId = (url) => {
  if (!url) return "";
  if (url.includes('/shorts/')) {
    return url.split('/shorts/')[1]?.split('?')[0];
  } else if (url.includes('youtu.be/')) {
    return url.split('youtu.be/')[1]?.split('?')[0];
  } else if (url.includes('v=')) {
    return url.split('v=')[1]?.split('&')[0];
  }
  return "";
};

const MontserratVideoGallery = ({ isEn }) => {
  const [activeIdx, setActiveIdx] = useState(0);

  const videos = [
    {
      url: "https://youtube.com/shorts/tFAeT0h1cqY",
      titleDa: "Montserrat - Video 1",
      titleEn: "Montserrat - Video 1",
      isShort: true,
    },
    {
      url: "https://youtu.be/gZKsXRM6qCY",
      titleDa: "Montserrat - Video 2",
      titleEn: "Montserrat - Video 2",
      isShort: false,
    },
    {
      url: "https://youtu.be/uDuSRT8rFBo",
      titleDa: "Montserrat - Video 3",
      titleEn: "Montserrat - Video 3",
      isShort: false,
    },
    {
      url: "https://youtu.be/7S1v55rWf0g",
      titleDa: "Montserrat - Video 4",
      titleEn: "Montserrat - Video 4",
      isShort: false,
    },
    {
      url: "https://youtu.be/oBxYFNJGD54",
      titleDa: "Montserrat - Video 5",
      titleEn: "Montserrat - Video 5",
      isShort: false,
    },
    {
      url: "https://youtu.be/gFpelDyCC3g",
      titleDa: "Montserrat - Video 6",
      titleEn: "Montserrat - Video 6",
      isShort: false,
    },
    {
      url: "https://youtube.com/shorts/btb452NsgAo?si=jgy2yIeuHPFDBLeK",
      titleDa: "Montserrat - Video 7",
      titleEn: "Montserrat - Video 7",
      isShort: true,
    },
  ];

  const currentVideo = videos[activeIdx] || videos[0];

  return (
    <div className="w-full max-w-4xl mx-auto bg-stone-50/80 border border-stone-200/90 rounded-2xl p-4 sm:p-6 md:p-8 shadow-xs">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
        {/* Main Video Display Player */}
        <div className="lg:col-span-8 flex flex-col items-center justify-center w-full min-h-[320px] sm:min-h-[420px]">
          <div className="w-full flex flex-col items-center justify-center">
            <YouTubeEmbed 
              key={currentVideo.url} 
              url={currentVideo.url} 
              title={isEn ? currentVideo.titleEn : currentVideo.titleDa} 
              className={currentVideo.isShort ? "max-w-[290px] sm:max-w-[320px] max-h-[500px] w-full" : "w-full max-w-4xl"}
            />
          </div>
        </div>

        {/* Scrollable Playlist Menu */}
        <div className="lg:col-span-4 flex flex-col h-full">
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-stone-200">
            <h3 className="font-serif text-lg text-stone-900 font-semibold flex items-center gap-2">
              <i className="fa-solid fa-play-circle text-[#004f3b]" aria-hidden="true" />
              {isEn ? "Video Playlist" : "Video menu"}
            </h3>
            <span className="text-xs text-stone-500 font-medium bg-stone-100 px-2 py-0.5 rounded-full border border-stone-200/60">
              {videos.length} {isEn ? "videos" : "videoer"}
            </span>
          </div>

          <div className="overflow-y-auto max-h-[480px] sm:max-h-[520px] pr-1.5 space-y-2.5 scrollbar-thin">
            {videos.map((vid, idx) => {
              const videoId = getYouTubeId(vid.url);
              const isSelected = idx === activeIdx;
              const thumbUrl = videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : '';

              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setActiveIdx(idx)}
                  className={cn(
                    "w-full flex items-center gap-3 p-2.5 rounded-xl transition-all text-left border cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#004f3b]/50",
                    isSelected
                      ? "bg-white border-[#004f3b] shadow-md ring-2 ring-[#004f3b]/20"
                      : "bg-white/70 hover:bg-white border-stone-200/80 hover:border-stone-300 shadow-xs"
                  )}
                >
                  {/* Thumbnail container */}
                  <div className="relative w-24 sm:w-28 shrink-0 aspect-video rounded-lg overflow-hidden bg-stone-900 border border-stone-200/60">
                    {thumbUrl ? (
                      <img
                        src={thumbUrl}
                        alt={isEn ? vid.titleEn : vid.titleDa}
                        loading="lazy"
                        decoding="async"
                        width="112"
                        height="63"
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-stone-800 flex items-center justify-center text-white/50 text-xs">
                        Video
                      </div>
                    )}
                    
                    {/* Overlay */}
                    <div className={cn(
                      "absolute inset-0 flex items-center justify-center transition-colors",
                      isSelected ? "bg-[#004f3b]/20" : "bg-black/25 hover:bg-black/10"
                    )}>
                      <div className={cn(
                        "w-7 h-7 rounded-full flex items-center justify-center text-white text-xs shadow-sm transition-transform",
                        isSelected ? "bg-[#004f3b] scale-110" : "bg-black/60"
                      )}>
                        <i className="fa-solid fa-play ml-0.5" aria-hidden="true" />
                      </div>
                    </div>

                    {vid.isShort && (
                      <span className="absolute bottom-1 right-1 bg-red-600/90 text-white text-[9px] font-bold px-1.5 py-0.5 rounded shadow-xs">
                        SHORT
                      </span>
                    )}
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className={cn(
                        "text-[10px] font-medium px-2 py-0.5 rounded-full inline-block",
                        isSelected ? "bg-[#004f3b] text-white" : "bg-stone-100 text-stone-600"
                      )}>
                        #{idx + 1}
                      </span>
                      {isSelected && (
                        <span className="text-[11px] font-medium text-[#004f3b]">
                          {isEn ? "• Playing" : "• Vist nu"}
                        </span>
                      )}
                    </div>
                    <h4 className={cn(
                      "text-sm leading-snug truncate",
                      isSelected ? "text-[#004f3b] font-semibold" : "text-stone-800 font-medium"
                    )}>
                      {isEn ? vid.titleEn : vid.titleDa}
                    </h4>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const MontserratPage = () => {
  const [status, setStatus] = useState('');
  const [showStory, setShowStory] = useState(false);
  const { lang, t } = useLanguage();
  const isEn = lang === 'en';
  const { retreatMontserrat } = useCms();

  const heroBadge = retreatMontserrat?.hero_badge?.[lang] || retreatMontserrat?.hero_badge?.da || (isEn ? "22-27 November 2026" : "22-27 november 2026");
  const heroTitle = retreatMontserrat?.hero_titel?.[lang] || retreatMontserrat?.hero_titel?.da || (isEn ? "Sacred Spiritual Retreat to Montserrat" : "Helligt retreat til Montserrat");
  const heroSubtitle = retreatMontserrat?.hero_undertitel?.[lang] || retreatMontserrat?.hero_undertitel?.da || (isEn 
    ? "Join us in Northern Spain in Catalonia's sacred mountains and divine cathedral, where monk and boy choirs sing your soul awake." 
    : "Tag med til Nordspanien i Cataloniens hellige bjerge og guddommelige katedral, hvor munke- og drengekor synger din sjæl vågen.");
  const heroExtraText = retreatMontserrat?.hero_ekstra_tekst?.[lang] || retreatMontserrat?.hero_ekstra_tekst?.da || (isEn
    ? "Here, the sacred feminine energy is strong, and the veils to your consciousness are thin."
    : "Her er den hellige feminine energi stærk, og slørene til din bevidsthed er tynde.");
  const heroImage = retreatMontserrat?.hero_billede?.[0] || "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/sk-c3-a6rmbillede-2026-07-01-kl-22-00-02_2.png";

  const fordybelseTitle = retreatMontserrat?.fordybelse_titel?.[lang] || retreatMontserrat?.fordybelse_titel?.da || (isEn ? "5 days in Montserrat – immersion, grounding and spiritual awakening" : "5 dage i Montserrat – fordybelse, forankring og sjælelig opvågning");
  const fordybelseHtml = retreatMontserrat?.fordybelse_tekst?.[lang] || retreatMontserrat?.fordybelse_tekst?.da;

  const detFaarDuMedHtml = retreatMontserrat?.det_faar_du_med?.[lang] || retreatMontserrat?.det_faar_du_med?.da;
  const programOverskrifterHtml = retreatMontserrat?.program_overskrifter?.[lang] || retreatMontserrat?.program_overskrifter?.da;
  const prisOgDatoHtml = retreatMontserrat?.pris_og_dato?.[lang] || retreatMontserrat?.pris_og_dato?.da;

  const sjaelHtml = retreatMontserrat?.sjael_sektion?.[lang] || retreatMontserrat?.sjael_sektion?.da;
  const sjaelBillede1 = retreatMontserrat?.sjael_billede_1?.[0] || "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/basilika_2.webp";
  const sjaelBillede2 = retreatMontserrat?.sjael_billede_2?.[0] || "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/sk-c3-a6rmbillede-2026-07-04-kl-17-54-55_2.png";

  const jomfruMariaHtml = retreatMontserrat?.jomfru_maria_tekst?.[lang] || retreatMontserrat?.jomfru_maria_tekst?.da;
  const musikHtml = retreatMontserrat?.musik_tekst?.[lang] || retreatMontserrat?.musik_tekst?.da;
  const musikBillede1 = retreatMontserrat?.musik_billede_1?.[0] || "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/sk-c3-a6rmbillede-2026-07-04-kl-17-53-57_2.png";
  const musikBillede2 = retreatMontserrat?.musik_billede_2?.[0] || "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/sk-c3-a6rmbillede-2026-07-04-kl-18-29-12_2.png";

  const praktiskInfoHtml = retreatMontserrat?.praktisk_info?.[lang] || retreatMontserrat?.praktisk_info?.da;
  const flyBillede = retreatMontserrat?.praktisk_fly_billede?.[0] || "https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/flyafgange.png";

  useEffect(() => {
    document.title = isEn 
      ? "Mette Brudvig - Sacred Spiritual Retreat in Montserrat" 
      : "Mette Brudvig - Spirituelt retreat i Montserrat";
    window.scrollTo(0, 0);
  }, [isEn]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#faf9f6] text-stone-800">
      {/* Light & Airy Hero Section */}
      <section className="pt-[185px] md:pt-[215px] lg:pt-[225px] pb-10 md:pb-12 px-6 bg-[#f5efe9]">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <FadeIn>
            <span className="text-xs uppercase tracking-[0.4em] font-bold text-[#004f3b] mb-4 block">
              {heroBadge}
            </span>
            <h1 className="text-4xl md:text-6xl font-serif text-stone-900 leading-tight">
              {heroTitle}
            </h1>
            <p className="text-lg md:text-xl font-serif italic text-[#004f3b] max-w-2xl mx-auto mt-4 leading-relaxed">
              {heroSubtitle}
            </p>
            {heroExtraText && (
              <p className="text-sm md:text-base font-light text-stone-700 max-w-xl mx-auto mt-2">
                {heroExtraText}
              </p>
            )}
            <div className="h-0.5 w-16 bg-[#004f3b] mx-auto my-6"></div>
          </FadeIn>

          {/* Hero Image shown in its full height and aspect ratio */}
          <FadeIn>
            <div className="mt-8 rounded-2xl overflow-hidden shadow-xl border border-stone-200/60 bg-white">
              <img 
                src={heroImage} 
                alt={heroTitle} 
                className="w-full h-auto object-contain block"
              />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Main Content */}
      <section className="pt-6 md:pt-10 pb-20 px-6 bg-[#faf9f6]">
        <div className="max-w-4xl mx-auto space-y-20">
          
          {/* Section: 5 dage i Montserrat */}
          <div className="space-y-8">
            <FadeIn>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
                {fordybelseTitle}
              </h2>
              <div className="h-0.5 w-12 bg-[#004f3b] mt-4 mb-6"></div>
            </FadeIn>

            <div className="space-y-4 text-stone-700 font-light text-base md:text-lg leading-relaxed">
              <FadeIn>
                {fordybelseHtml ? (
                  <div 
                    className="prose prose-stone max-w-none text-stone-700 font-light text-base md:text-lg leading-relaxed space-y-4 [&>p]:leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: fordybelseHtml }} 
                  />
                ) : (
                  <>
                    <p>
                      {isEn
                        ? "Montserrat is the place where I myself have taken the largest spiritual quantum leaps. Here I have received my deepest spiritual insights and upgrades, and I have experienced sacred miracles."
                        : "Montserrat er det sted, hvor jeg selv har taget de største spirrituelle kvantespring. Her har jeg fået mine dybeste spirituelle indsigter og opgraderinger, og jeg har oplevet hellige mirakler."}
                    </p>
                    <p className="mt-4">
                      {isEn
                        ? "This retreat is for you who feel a calling toward greater inner peace, clarity and connection with your soul and the divine."
                        : "Dette retreat er for dig, der mærker et kald mod større indre ro, klarhed og forbindelse med din sjæl og det guddommelige."}
                    </p>
                    <p className="mt-4">
                      {isEn
                        ? "For you who wish for spiritual growth and need a safe space where what you experience can land and be integrated."
                        : "For dig, der ønsker spirituel vækst og har brug for et trygt rum, hvor det, du oplever, kan få lov at lande og integreres."}
                    </p>
                  </>
                )}
              </FadeIn>
            </div>
          </div>

          {/* Praktisk overblik tidligt på siden: Det får du med & Program i overskrifter */}
          <div className="grid md:grid-cols-2 gap-12 border-t border-stone-200 pt-12">
            <FadeIn className="bg-white p-8 border border-stone-200/80 rounded-sm shadow-sm space-y-6">
              {detFaarDuMedHtml ? (
                <div 
                  className="prose prose-stone max-w-none text-stone-700 font-light text-base space-y-4 [&>h3]:text-2xl [&>h3]:font-serif [&>h3]:text-stone-900 [&>h3]:mb-4 [&>ul]:space-y-3 [&>ul]:list-disc [&>ul]:pl-5 [&>ul>li]:leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: detFaarDuMedHtml }} 
                />
              ) : (
                <>
                  <h3 className="text-2xl font-serif text-stone-900">
                    {isEn ? "What's Included" : "Det får du med"}
                  </h3>
                  <ul className="space-y-3 text-stone-700 font-light text-base">
                    {(isEn ? [
                      "private apartment in the mountain village near the monastery",
                      "guided meditations that strengthen the sacred connection",
                      "teaching, immersion, and reflection in a safe space",
                      "hiking trips in Montserrat's sacred mountains",
                      "time for silence, immersion, and personal process",
                      "a small and safe community with like-minded people",
                      "One-hour 1:1 clairvoyant consultation with me prior to the journey"
                    ] : [
                      "egen lejlighed i bjergbyen ved klosteret",
                      "guidede meditationer, der styrker den hellige forbindelse",
                      "undervisning, fordybelse og refleksion i et trygt rum",
                      "vandreture i Montserrats hellige bjerge",
                      "tid til stilhed, fordybelse og egen proces",
                      "et lille og trygt fællesskab med ligesindede",
                      "En times 1:1 clairvoyant rådgivning med mig forud for rejsen"
                    ]).map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="text-[#004f3b] mr-3 font-serif">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </FadeIn>

            <FadeIn delay={100} className="bg-white p-8 border border-stone-200/80 rounded-sm shadow-sm space-y-6 flex flex-col justify-between">
              {programOverskrifterHtml ? (
                <div 
                  className="prose prose-stone max-w-none text-stone-700 font-light text-base space-y-4 [&>h3]:text-2xl [&>h3]:font-serif [&>h3]:text-stone-900 [&>h3]:mb-4 [&>ul]:space-y-3 [&>ul]:list-disc [&>ul]:pl-5 [&>ul>li]:leading-relaxed [&>p]:text-stone-500 [&>p]:italic [&>p]:text-sm [&>p]:pt-4 [&>p]:border-t [&>p]:border-stone-100"
                  dangerouslySetInnerHTML={{ __html: programOverskrifterHtml }} 
                />
              ) : (
                <>
                  <div className="space-y-6">
                    <h3 className="text-2xl font-serif text-stone-900">
                      {isEn ? "Program Highlights" : "Program i overskrifter"}
                    </h3>
                    <ul className="space-y-3 text-stone-700 font-light text-base">
                      {(isEn ? [
                        "morning prayer with Monk singing in the cathedral and visit to the Black Madonna",
                        "breakfast in your private apartment",
                        "shared immersion, energy integration, and meditation",
                        "lunch on your own or at a café on the mountain",
                        "afternoon with teaching, silence, or hiking",
                        "evening prayer with both the monk choir and the boys' choir",
                        "integration and rest"
                      ] : [
                        "morgenbøn med Munkesang i katedralen og besøg ved den Sorte Madonna",
                        "morgenmad i egen lejlighed",
                        "fælles fordybelse, integration af energien og meditation",
                        "frokost på egen hånd eller en cafe på bjerget",
                        "eftermiddag med undervisning, stilhed eller vandring",
                        "aftenbøn med både munkekoret og drengekoret",
                        "integration og hvile"
                      ]).map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-[#004f3b] mr-3 font-serif">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="text-stone-500 italic text-sm pt-4 border-t border-stone-100">
                    {isEn
                      ? "Generous space for pauses, stillness, and individual needs."
                      : "Der er plads til pauser, stilhed og individuelle behov."}
                  </p>
                </>
              )}
            </FadeIn>
          </div>

          {/* Dato og Pris i selvstændig boks */}
          <FadeIn className="bg-[#ffffff] border border-stone-200 p-8 md:p-10 rounded-sm shadow-sm space-y-6">
            {prisOgDatoHtml ? (
              <div 
                className="prose prose-stone max-w-none text-stone-800 font-light text-base md:text-lg leading-relaxed [&>h3]:text-2xl [&>h3]:font-serif [&>h3]:text-stone-900 [&>h3]:mb-4 [&>ul]:space-y-1.5 [&>ul]:list-disc [&>ul]:pl-5"
                dangerouslySetInnerHTML={{ __html: prisOgDatoHtml }} 
              />
            ) : (
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="space-y-2">
                  <h3 className="text-2xl font-serif text-stone-900">
                    {isEn ? "Dates & Pricing" : "Dato og Pris"}
                  </h3>
                  <p className="font-medium text-stone-900 text-lg">
                    {isEn ? "Next journey is November 22-27, 2026." : "Næste rejse er den 22-27 november 2026."}
                  </p>
                </div>
                <div className="space-y-2 min-w-[280px] md:min-w-[340px]">
                  <p className="font-semibold text-stone-900">{isEn ? "Price:" : "Pris:"}</p>
                  <ul className="space-y-1 text-sm md:text-base text-stone-800 font-light">
                    <li>• {isEn ? "Early bird until October 18: 8,500 DKK" : "Early bird indtil den 18. oktober: 8.500 kroner"}</li>
                    <li>• {isEn ? "Regular price: 10,500 DKK" : "Almindelig pris: 10.500 kroner"}</li>
                    <li className="text-[#004f3b] font-medium pt-1">• {isEn ? "Installment option available" : "Mulighed for ratebetaling."}</li>
                  </ul>
                </div>
              </div>
            )}

            <div className="pt-4 border-t border-stone-100 flex flex-wrap items-center gap-4">
              <a 
                href="https://pay.lumant.ai/kurv/Me4P-montserrat-retreat-november-2026" 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#004f3b] text-white px-7 py-3.5 uppercase text-xs tracking-[0.2em] font-bold hover:bg-emerald-950 transition-colors rounded-sm shadow-md"
              >
                <span>{isEn ? "Book & Pay Online (Montserrat)" : "Tilmeld & Betal online (Montserrat)"}</span>
                <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true" />
              </a>
              <a 
                href="#tilmelding-montserrat" 
                className="inline-flex items-center justify-center gap-2 bg-white text-stone-800 border border-stone-300 hover:border-[#004f3b] px-6 py-3.5 uppercase text-xs tracking-[0.15em] font-medium transition-colors rounded-sm shadow-2xs"
              >
                <span>{isEn ? "Send inquiry" : "Send forespørgsel"}</span>
                <i className="fa-solid fa-arrow-down text-xs" aria-hidden="true" />
              </a>
              <a 
                href="tel:+4527507083" 
                className="inline-flex items-center gap-2 bg-stone-50 hover:bg-stone-100 text-stone-800 border border-stone-300 px-6 py-3.5 rounded-sm font-medium text-xs uppercase tracking-[0.15em] transition-colors text-center"
              >
                <i className="fa-solid fa-phone text-[#004f3b] text-xs" />
                <span>{isEn ? "Call: 27 50 70 83" : "Ring: 27 50 70 83"}</span>
              </a>
            </div>
          </FadeIn>

          {/* Section: Udtalelser fra deltagere og klienter (flyttet op for at inspirere til videre læsning) */}
          <div className="border-t border-stone-200/80 pt-16 pb-4">
            <FadeIn>
              <div className="text-center max-w-3xl mx-auto mb-10 space-y-2">
                <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#004f3b] block">
                  {isEn ? "Participant Testimonials" : "Udtalelser fra deltagere"}
                </span>
                <h2 className="text-3xl font-serif text-stone-900">
                  {isEn ? "What clients and participants say" : "Det siger tidligere deltagere"}
                </h2>
                <div className="h-0.5 w-12 bg-[#004f3b] mt-3 mx-auto"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  TESTIMONIALS.find(t => t.name === "Nina Bonde"),
                  TESTIMONIALS.find(t => t.name === "Signe Aarestrup"),
                  TESTIMONIALS.find(t => t.name === "Marianne Helleskov"),
                  TESTIMONIALS.find(t => t.name === "Sara Delpin")
                ].filter(Boolean).map((t, idx) => (
                  <FadeIn key={idx} delay={idx * 80} className="h-full">
                    <TestimonialQuoteCard testimonial={t} className="h-full bg-stone-50/70" />
                  </FadeIn>
                ))}
              </div>
            </FadeIn>
          </div>

          {/* Fordybelse og indhold derefter: Section: Forbind dig med din sjæl */}
          <div className="border-t border-stone-200 pt-16 space-y-10">
            {sjaelHtml ? (
              <div 
                className="prose prose-stone max-w-none text-stone-700 font-light text-base md:text-lg leading-relaxed space-y-6 [&>h3]:text-3xl [&>h3]:md:text-4xl [&>h3]:font-serif [&>h3]:text-stone-900 [&>h3]:mb-4 [&>p]:leading-relaxed"
                dangerouslySetInnerHTML={{ __html: sjaelHtml }} 
              />
            ) : (
              <>
                <FadeIn>
                  <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
                    {isEn ? "Connect with your soul" : "Forbind dig med din sjæl"}
                  </h2>
                  <div className="h-0.5 w-12 bg-[#004f3b] mt-4 mb-6"></div>
                </FadeIn>

                <div className="grid md:grid-cols-12 gap-8 items-center">
                  <div className="md:col-span-7 space-y-4 text-stone-700 font-light text-base md:text-lg leading-relaxed">
                    <FadeIn>
                      <p>
                        {isEn
                          ? "On this journey you gain an understanding of the true, authentic divine version of you."
                          : "På denne rejse får du forståelse for den sande, autentiske guddommelige version af dig."}
                      </p>
                      <p className="mt-4">
                        {isEn
                          ? "Through meditations and teaching in the sacred energies, you journey home to your soul core."
                          : "Gennem meditationer og undervisning i de hellige energier, rejser du hjem til din sjælskerne."}
                      </p>
                    </FadeIn>
                  </div>
                  <div className="md:col-span-5">
                    <FadeIn delay={100}>
                      <img 
                        src={sjaelBillede1} 
                        alt="Basilika" 
                        className="w-full h-auto rounded-sm shadow-md border border-stone-200 object-cover"
                      />
                    </FadeIn>
                  </div>
                </div>

                <div className="grid md:grid-cols-12 gap-8 items-center pt-4">
                  <div className="md:col-span-5 order-2 md:order-1">
                    <FadeIn delay={100}>
                      <img 
                        src={sjaelBillede2} 
                        alt="Montserrat bjerge" 
                        className="w-full h-auto rounded-sm shadow-md border border-stone-200 object-cover"
                      />
                    </FadeIn>
                  </div>
                  <div className="md:col-span-7 order-1 md:order-2 space-y-4 text-stone-700 font-light text-base md:text-lg leading-relaxed">
                    <FadeIn>
                      <p>
                        {isEn
                          ? "Your true self is a version with dignity, joy of living, inner peace and love for life."
                          : "Dit sande jeg er en version med værdighed, livsglæde, indre fred og kærlighed til livet."}
                      </p>
                      <p className="mt-4">
                        {isEn
                          ? "And whatever else you discover about yourself is up to the sacred energies and willingness to open yourself to them."
                          : "Og hvad du ellers opdager om dig selv, er op til de hellige energier og villighed til at åbne dig for dem."}
                      </p>
                      <div className="mt-6 p-4 bg-[#f5efe9] border-l-4 border-[#004f3b] text-stone-800 font-serif italic text-base">
                        {isEn
                          ? "The package includes a one-hour clairvoyance session with me prior to the journey (online or physical)."
                          : "Pakken inkluderer en en times clairvoyance ved mig forud for rejsen (online eller fysisk)."}
                      </div>
                    </FadeIn>
                  </div>
                </div>
              </>
            )}

            {sjaelHtml && (
              <div className="grid md:grid-cols-2 gap-8 pt-4">
                <FadeIn delay={100}>
                  <img 
                    src={sjaelBillede1} 
                    alt="Basilika" 
                    className="w-full h-64 md:h-80 object-cover rounded-sm shadow-md border border-stone-200"
                  />
                </FadeIn>
                <FadeIn delay={150}>
                  <img 
                    src={sjaelBillede2} 
                    alt="Montserrat bjerge" 
                    className="w-full h-64 md:h-80 object-cover rounded-sm shadow-md border border-stone-200"
                  />
                </FadeIn>
              </div>
            )}
          </div>

          {/* Section: Mærk Jomfru Marias nærvær' */}
          <div className="border-t border-stone-200 pt-16 space-y-6">
            {jomfruMariaHtml ? (
              <div 
                className="prose prose-stone max-w-none text-stone-700 font-light text-base md:text-lg leading-relaxed space-y-4 [&>h3]:text-3xl [&>h3]:md:text-4xl [&>h3]:font-serif [&>h3]:text-stone-900 [&>h3]:mb-4 [&>p]:leading-relaxed"
                dangerouslySetInnerHTML={{ __html: jomfruMariaHtml }} 
              />
            ) : (
              <>
                <FadeIn>
                  <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
                    {isEn ? "Feel Virgin Mary's presence" : "Mærk Jomfru Marias nærvær'"}
                  </h2>
                  <div className="h-0.5 w-12 bg-[#004f3b] mt-4 mb-6"></div>
                </FadeIn>

                <FadeIn className="space-y-4 text-stone-700 font-light text-base md:text-lg leading-relaxed">
                  <p>
                    {isEn
                      ? "For me personally, Montserrat is the place where I have received some of my deepest spiritual insights and upgrades. I truly feel Mother Mary's presence, and she even became alive to me with a message in the cathedral. It has been life-changing for me, which is why I want to hold space for others right here."
                      : "For mig personligt er Montserrat det sted, hvor jeg har fået nogle af mine dybeste spirituelle indsigter og opgraderinger. Jeg mærker virkelig Moder Marias nærvær, og hun er sågar blevet levende for mig med et budsskab i katedralen. Det har været livsforvandlende for mig, og derfor ønsker jeg at holde run for andre netop her."}
                  </p>
                  <p>
                    {isEn
                      ? "It was here in Montserrat that the Black Madonna was found in a cave, and around her that the cathedral and Benedictine monastery are built."
                      : "Det var her i Montserrat, at den Sorte Madonna blev fundet i en grotte, og det omkring hende, at katedralen og munkeklosteret er bygget."}
                  </p>
                  <p>
                    {isEn
                      ? "Many experience that the connection to intuition, soul, and inner wisdom is strengthened here. The mountains hold caves connected with both Virgin Mary and Mary Magdalene energies, which we visit on hiking trips. If you cannot hike in the mountains, there is plenty of this lovely energy on the monastery mountain itself, where we stay."
                      : "Mange oplever, at forbindelsen til intuition, sjæl og indre visdom styrkes her. Bjergene rummer grotter forbundet med både Jomfru Maria og Maria Magdalena energier, som vi besøger på vandreture. Kan du ikke vandre i bjergene, er rigeligt af denne dejlige energi på selve klosterbjerget, hvor vi bor."}
                  </p>
                </FadeIn>
              </>
            )}
          </div>

          {/* Section: Magisk musik */}
          <div className="border-t border-stone-200 pt-16 space-y-8">
            {musikHtml ? (
              <div 
                className="prose prose-stone max-w-none text-stone-700 font-light text-base md:text-lg leading-relaxed space-y-4 [&>h3]:text-3xl [&>h3]:md:text-4xl [&>h3]:font-serif [&>h3]:text-stone-900 [&>h3]:mb-4 [&>p]:leading-relaxed [&>p>em]:text-xl [&>p>em]:md:text-2xl [&>p>em]:font-serif [&>p>em]:italic [&>p>em]:text-stone-900 [&>p>em]:block [&>p>em]:text-center [&>p>em]:pt-6"
                dangerouslySetInnerHTML={{ __html: musikHtml }} 
              />
            ) : (
              <>
                <FadeIn>
                  <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
                    {isEn ? "Magical music" : "Magisk musik"}
                  </h2>
                  <div className="h-0.5 w-12 bg-[#004f3b] mt-4 mb-6"></div>
                </FadeIn>

                <FadeIn className="space-y-4 text-stone-700 font-light text-base md:text-lg leading-relaxed">
                  <p>
                    {isEn
                      ? "We attend morning and evening prayer, where the monks from the Benedictine Order sing with voices and frequencies so beautiful and divine that it resonates in the soul and awakens it. It sounds poetic, I know. But that is how I can best describe it."
                      : "Vi går til morgen- og aftenbøn, hvor munkene fra Benedictordnen synger med stemmer og frekvenser så smukke og guddommelige, at det giver genklang i sjælen og vækker den. Det lyder poetisk, det ved jeg Men det er sådan, jeg bedst kan beskrive det."}
                  </p>
                  <p>
                    {isEn
                      ? "The monastery's world-famous boys' choir sings along at the evening mass, and it is a great experience, and the energy in the cathedral is very special."
                      : "Klosterets verdensberømte drengekor synger med ved aftensmessen, og det er en stor oplevelse, og energien i katedralen er helt særlig."}
                  </p>
                  <p>
                    {isEn
                      ? "It is as if the veils to our soul insights are thinner here."
                      : "Det er, som om slørene til vores sjælelige indsigter er tyndere her."}
                  </p>
                </FadeIn>
              </>
            )}

            <div className="grid sm:grid-cols-2 gap-6 pt-4">
              <FadeIn delay={100}>
                <img 
                  src={musikBillede1} 
                  alt="Katedral og natur" 
                  className="w-full h-64 object-cover rounded-sm border border-stone-200 shadow-md"
                />
              </FadeIn>
              <FadeIn delay={150}>
                <img 
                  src={musikBillede2} 
                  alt="Udsigt over bjerget" 
                  className="w-full h-64 object-cover rounded-sm border border-stone-200 shadow-md"
                />
              </FadeIn>
            </div>

            {!musikHtml && (
              <FadeIn className="pt-6 text-center">
                <p className="text-xl md:text-2xl font-serif italic text-stone-900">
                  {isEn
                    ? "Are you ready to open for the soul's power and intelligence in you?"
                    : "Er du klar til at åbne for sjælens kraft og intelligens i dig?"}
                </p>
              </FadeIn>
            )}
          </div>

          {/* Section: Videoer fra Montserrat */}
          <div className="border-t border-stone-200 pt-16 space-y-10">
            <FadeIn className="text-center max-w-2xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
                {isEn ? "Videos from Montserrat" : "Videoer fra Montserrat"}
              </h2>
              <div className="h-0.5 w-12 bg-[#004f3b] mt-4 mb-6 mx-auto"></div>
            </FadeIn>

            <FadeIn>
              <MontserratVideoGallery isEn={isEn} />
            </FadeIn>
          </div>

          {/* Praktisk information & Anbefalet flyrejse */}
          <div className="border-t border-stone-200 pt-12 space-y-8 bg-[#f5efe9]/50 p-8 md:p-12 rounded-sm border">
            {praktiskInfoHtml ? (
              <div 
                className="prose prose-stone max-w-none text-stone-700 font-light text-base leading-relaxed space-y-4 [&>h3]:text-xl [&>h3]:font-serif [&>h3]:text-stone-900 [&>h3]:mt-6 [&>h3:first-child]:mt-0 [&>p]:leading-relaxed [&>p>a]:underline [&>p>a]:font-medium [&>p>a]:text-stone-900 hover:[&>p>a]:text-[#004f3b]"
                dangerouslySetInnerHTML={{ __html: praktiskInfoHtml }} 
              />
            ) : (
              <>
                <FadeIn className="space-y-4">
                  <h3 className="text-xl font-serif text-stone-900">
                    {isEn ? "Meals & Transport" : "Forplejning og transport"}
                  </h3>
                  <div className="text-stone-700 font-light text-base leading-relaxed space-y-3">
                    <p>
                      {isEn
                        ? "Option to buy partial or full board at the restaurant next door. There is also a good café where you can buy your morning coffee and breakfast if you do not wish to make it yourself in the apartment."
                        : "Mulighed for at købe delvis eller fuld forplejning på restauranten ved siden af. Der er også en god cafe, hvor du kan købe din morgenkaffe og morgenmad, hvis du ikke ønsker at lave det selv i lejligheden."}
                    </p>
                    <p>
                      {isEn
                        ? "Price Excl. Flight ticket to Barcelona and meals."
                        : "Pris Ekskl. Flybillet til Barcelona og forplejning."}
                    </p>
                    <p>
                      {isEn
                        ? "Incl. transport to and from the airport. We stop at a supermarket on the way up so you can buy groceries."
                        : "Inkl. transport til og fra lufthaven. Vi standser ved et supermarked på vej derop, så du kan handle madvarer ind."}
                    </p>
                  </div>
                </FadeIn>

                <FadeIn className="space-y-4 border-t border-stone-200/80 pt-6">
                  <h3 className="text-xl font-serif text-stone-900">
                    {isEn ? "Recommended Flight" : "Anbefalet flyrejse"}
                  </h3>
                  <p className="text-stone-700 font-light text-base leading-relaxed">
                    {isEn
                      ? "This is the flight departure I personally take. If you fly from another airport, that is completely fine as long as arrival and departure times align for shared airport transfers."
                      : "Dette er den afgang, jeg selv rejser med. Flyver du fra et andet sted, er det helt fint, så længe ankomst- og afgangstidspunkterne passer nogenlunde af hensyn til transporten mellem lufthavnen og bjerget."}
                  </p>
                </FadeIn>

                <FadeIn className="space-y-4 border-t border-stone-200/80 pt-6">
                  <h3 className="text-xl font-serif text-stone-900">
                    {isEn ? "Registration" : "Tilmelding"}
                  </h3>
                  <div className="text-stone-700 font-light text-base leading-relaxed space-y-3">
                    <p>
                      {isEn
                        ? "Register by writing to me at "
                        : "Du tilmelder dig ved at skrive til mig på "}
                      <a href="mailto:mette@mettebrudvig.dk" className="underline font-medium text-stone-900 hover:text-[#004f3b]">mette@mettebrudvig.dk</a>
                      {isEn ? " or by calling " : " eller ringe direkte på "}
                      <a href="tel:+4527507083" className="underline font-medium text-stone-900 hover:text-[#004f3b]">27 50 70 83</a>.
                      {isEn
                        ? " You are also welcome to request a phone call to see if travelling with me feels aligned."
                        : " Her kan du også bede om at blive ringet op, så du kan mærke, om en tur med mig føles rigtig, og få svar på dine spørgsmål."}
                    </p>
                    <p>
                      {isEn
                        ? "I host two Montserrat retreats per year. Dates are arranged once a group of 4 people wish to travel together. Write to "
                        : "Jeg holder to retreats i Montserrat om året. Turene bliver planlagt, når I er fire, der gerne vil med. Vi finder en dato, der passer jer, og så slår jeg det op. Skriv til mig på "}
                      <a href="mailto:mette@mettebrudvig.dk" className="underline font-medium text-stone-900 hover:text-[#004f3b]">mette@mettebrudvig.dk</a>.
                    </p>
                  </div>
                </FadeIn>
              </>
            )}

            {flyBillede && (
              <div className="my-6 max-w-2xl mx-auto overflow-hidden rounded-sm border border-stone-300 p-2 bg-white shadow-sm">
                <img 
                  src={flyBillede} 
                  alt="Anbefalet flyrejse screenshot med Norwegian CPH til BCN afgang" 
                  className="w-full h-auto rounded-sm"
                />
              </div>
            )}
          </div>

          {/* Contact Form for Montserrat */}
          <div id="tilmelding-montserrat" className="border-t border-stone-200 pt-16">
            <FadeIn className="bg-white p-8 md:p-12 rounded-sm border border-stone-200 shadow-md max-w-3xl mx-auto space-y-6">
              <div className="text-center space-y-2">
                <h3 className="text-2xl font-serif text-stone-900">
                  {isEn ? "Registration & Questions" : "Tilmelding & Spørgsmål"}
                </h3>
                <p className="text-stone-600 font-light text-sm">
                  {isEn
                    ? "Write to Mette to learn more or reserve your spot."
                    : "Skriv til Mette for at høre mere eller booke din plads."}
                </p>
                <div className="pt-2">
                  <a 
                    href="https://pay.lumant.ai/kurv/Me4P-montserrat-retreat-november-2026"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between gap-3 bg-[#004f3b] text-white p-4 rounded-sm hover:bg-emerald-950 transition-colors shadow-sm"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center text-white shrink-0">
                        <i className="fa-solid fa-credit-card text-xs" aria-hidden="true" />
                      </div>
                      <div className="text-left">
                        <span className="text-[10px] uppercase tracking-widest text-emerald-200 font-bold block">
                          {isEn ? "Direct Registration" : "Direkte tilmelding"}
                        </span>
                        <span className="font-bold text-xs sm:text-sm">
                          {isEn ? "Book your spot here" : "Book din plads her"}
                        </span>
                      </div>
                    </div>
                    <i className="fa-solid fa-arrow-up-right-from-square text-xs text-emerald-200" aria-hidden="true" />
                  </a>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6 pt-4">
                <input type="text" name="website_url" className="sr-only absolute" tabIndex="-1" autoComplete="off" />
                <input type="hidden" name="subject" value={isEn ? "Inquiry: Sacred spiritual retreat in Montserrat" : "Forespørgsel: Spirituelt retreat i Montserrat"} />

                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase tracking-widest text-stone-500 font-medium">
                      {isEn ? "Name" : "Navn"}
                    </label>
                    <input 
                      required 
                      type="text" 
                      name="name"
                      placeholder={isEn ? "Your full name" : "Dit fulde navn"} 
                      className="w-full bg-stone-50 border border-stone-200 rounded-sm px-4 py-3 text-sm focus:border-[#004f3b] outline-none"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase tracking-widest text-stone-500 font-medium">
                      {isEn ? "Email" : "E-mail"}
                    </label>
                    <input 
                      required 
                      type="email" 
                      name="email"
                      placeholder={isEn ? "Your email address" : "Din e-mailadresse"} 
                      className="w-full bg-stone-50 border border-stone-200 rounded-sm px-4 py-3 text-sm focus:border-[#004f3b] outline-none"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase tracking-widest text-stone-500 font-medium">
                    {isEn ? "Message" : "Besked"}
                  </label>
                  <textarea 
                    required 
                    name="message"
                    rows={4} 
                    placeholder={isEn ? "Write your questions or request for a non-binding conversation..." : "Skriv dine spørgsmål eller ønske om uforpligtende samtale..."} 
                    className="w-full bg-stone-50 border border-stone-200 rounded-sm px-4 py-3 text-sm focus:border-[#004f3b] outline-none"
                  />
                </div>

                <button 
                  type="submit" 
                  className="w-full bg-[#004f3b] text-white py-4 uppercase text-xs tracking-[0.3em] font-bold hover:bg-emerald-900 transition-colors rounded-sm shadow-md cursor-pointer"
                >
                  {isEn ? "Send inquiry" : "Send henvendelse"}
                </button>

                {status === 'success' && (
                  <p className="text-emerald-700 text-center font-serif italic pt-2 text-sm">
                    {isEn 
                      ? "Thank you! Your inquiry has been sent. Mette will get back to you as soon as possible."
                      : "Tak! Din henvendelse er sendt. Mette vender tilbage hurtigst muligt."}
                  </p>
                )}
                {status === 'error' && (
                  <p className="text-red-700 text-center font-serif italic pt-2 text-sm">
                    {isEn ? "An error occurred. Please try again." : "Der skete en fejl. Prøv venligst igen."}
                  </p>
                )}
              </form>
            </FadeIn>
          </div>

          {/* Expandable personal story */}
          <div className="mt-16 text-center">
            <FadeIn>
              <button
                onClick={() => setShowStory(!showStory)}
                className="inline-flex items-center space-x-3 text-[#004f3b] hover:text-emerald-950 transition-colors font-serif italic text-lg cursor-pointer underline underline-offset-4 decoration-[#004f3b]/30"
              >
                <i className="fa-solid fa-book-open-reader" />
                <span>
                  {isEn 
                    ? (showStory ? "Hide my personal experiences" : "Read about my own spiritual experiences in Montserrat")
                    : (showStory ? "Skjul mine personlige oplevelser" : "Læs om mine egne spirituelle oplevelser i Montserrat")}
                </span>
              </button>
            </FadeIn>

            {showStory && (
              <FadeIn className="mt-8 max-w-3xl mx-auto text-left bg-white p-8 border border-stone-200/80 rounded-sm shadow-sm">
                <p className="text-stone-700 font-light leading-relaxed text-sm italic">
                  {isEn
                    ? '"For me personally, Montserrat is the place where I have received some of my deepest spiritual insights and upgrades. I truly feel Mother Mary\'s presence, and she even became alive to me with a message in the cathedral. It has been life-changing for me, which is why I want to hold space for others right here. It was here in Montserrat that the Black Madonna was found in a cave, and it is around her that the cathedral and monastery were built. Many experience that the connection to intuition, soul, and inner wisdom is strengthened here. The mountains hold caves connected with both Virgin Mary and Mary Magdalene energies, which we visit on walks."'
                    : '"For mig personligt er Montserrat det sted, hvor jeg har fået nogle af mine dybeste spirituelle indsigter og opgraderinger. Jeg mærker virkelig Moder Marias nærvær, og hun er sågar blevet levende for mig med et budskab i katedralen. Det har været livsforvandlende for mig, og derfor ønsker jeg at holde rum for andre netop her. Det var her i Montserrat, at den Sorte Madonna blev fundet i en grotte, og det er omkring hende, at katedralen og munkeklosteret er bygget. Mange oplever, at forbindelsen til intuition, sjæl og indre visdom styrkes her. Bjergene rummer grotter forbundet med både Jomfru Maria og Maria Magdalena energier, som vi besøger på vandreture."'}
                </p>
              </FadeIn>
            )}
          </div>

          {/* Customized Retreat Option */}
          <div className="mt-20 border-t border-stone-200/80 pt-16 text-center max-w-3xl mx-auto space-y-6">
            <FadeIn>
              <h4 className="text-xl font-serif text-stone-900">
                {isEn ? "Customized Retreats" : "Skræddersyede retreats"}
              </h4>
              <p className="text-stone-600 font-light leading-relaxed text-sm md:text-base">
                {isEn
                  ? "I also create customized retreats if you are a group of at least 4 people who wish to travel together. Here you can choose yoga, hiking, spiritual growth, and more, which I would love to tell you about."
                  : "Jeg skræddersyer også retreat, hvis I er mindst 4 personer, der gerne vil afsted sammen. Her kan I vælge yoga, vandring, spirituel vækst og andet, jeg gerne fortæller mere om."}
              </p>
              <p className="text-sm md:text-base text-[#004f3b] font-medium mt-4">
                {isEn ? "Write to me at " : "Skriv til mig på "}
                <a href="mailto:mette@mettebrudvig.dk" className="underline font-bold hover:text-emerald-950">mette@mettebrudvig.dk</a>
                {isEn ? " or call " : " eller ring på "}
                <a href="tel:+4527507083" className="underline font-bold hover:text-emerald-950">27 50 70 83</a>
                {isEn ? ", and I will call you for a chat about your wishes, budget, etc." : ", så tager vi en snak om dine ønsker, budget mm."}
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Montserrat Interest Form (styled light, airy, professional) */}
      <section className="py-20 px-6 bg-[#f5efe9] border-t border-stone-200">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-5 space-y-6">
              <FadeIn direction="right">
                <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#004f3b]">
                  {isEn ? "Montserrat Retreat" : "Montserrat retreat"}
                </span>
                <h2 className="text-3xl font-serif text-stone-900 mt-2">
                  {isEn ? "Questions about the Montserrat retreat?" : "Spørgsmål til Montserrat retreatet?"}
                </h2>
                <div className="h-0.5 w-12 bg-[#004f3b] my-4"></div>
                <p className="text-stone-600 font-light text-sm leading-relaxed">
                  {isEn
                    ? "Write or call if you have questions about the sacred retreat to Montserrat, the journey, or wish to have a non-binding chat."
                    : "Skriv eller ring, hvis du vil høre mere om det hellige retreat til Montserrat, rejsen eller have en uforpligtende samtale."}
                </p>
                <div className="pt-2">
                  <a 
                    href="tel:+4527507083" 
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-[#004f3b] hover:text-emerald-950 transition-colors"
                  >
                    <i className="fa-solid fa-phone text-xs" />
                    <span>{isEn ? "Call to hear more: +45 27 50 70 83" : "Ring, og hør mere: 27 50 70 83"}</span>
                  </a>
                </div>
              </FadeIn>
            </div>

            <div className="md:col-span-7">
              <FadeIn direction="left" className="bg-white p-8 md:p-10 rounded-sm shadow-sm border border-stone-200/80">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <input type="text" name="website_url" className="sr-only absolute" tabIndex="-1" autoComplete="off" />
                  
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                      <label className="text-[10px] uppercase tracking-widest text-stone-500 font-medium">
                        {isEn ? "Name" : "Navn"}
                      </label>
                      <input 
                        required 
                        type="text" 
                        name="name" 
                        placeholder={isEn ? "Your full name" : "Dit fulde navn"}
                        className="w-full bg-stone-50 border border-stone-200 rounded-sm px-4 py-3 text-sm focus:border-[#004f3b] focus:bg-white outline-none transition-all text-stone-800 font-light placeholder-stone-400"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] uppercase tracking-widest text-stone-500 font-medium">
                        {isEn ? "Email" : "Email"}
                      </label>
                      <input 
                        required 
                        type="email" 
                        name="email" 
                        placeholder={isEn ? "your@email.com" : "din@email.dk"}
                        className="w-full bg-stone-50 border border-stone-200 rounded-sm px-4 py-3 text-sm focus:border-[#004f3b] focus:bg-white outline-none transition-all text-stone-800 font-light placeholder-stone-400"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                      <label className="text-[10px] uppercase tracking-widest text-stone-500 font-medium">
                        {isEn ? "Phone (optional)" : "Telefon (valgfri)"}
                      </label>
                      <input 
                        type="tel" 
                        name="phone" 
                        placeholder={isEn ? "Your phone number" : "Dit telefonnummer"}
                        className="w-full bg-stone-50 border border-stone-200 rounded-sm px-4 py-3 text-sm focus:border-[#004f3b] focus:bg-white outline-none transition-all text-stone-800 font-light placeholder-stone-400"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] uppercase tracking-widest text-stone-500 font-medium">
                        {isEn ? "Select Retreat Preference" : "Vælg Retreat"}
                      </label>
                      <select 
                        required
                        name="retreat_interest" 
                        className="w-full bg-stone-50 border border-stone-200 rounded-sm px-4 py-3 text-sm focus:border-[#004f3b] focus:bg-white outline-none transition-all text-stone-800 font-light cursor-pointer"
                      >
                        <option value="Helligt retreat til Montserrat">
                          {isEn ? "Sacred Retreat to Montserrat" : "Helligt retreat til Montserrat"}
                        </option>
                        <option value="Skræddersyet retreat (min. 4 personer)">
                          {isEn ? "Customized Retreat (min. 4 people)" : "Skræddersyet retreat (min. 4 personer)"}
                        </option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase tracking-widest text-stone-500 font-medium">
                      {isEn ? "Message or requests (optional)" : "Besked eller ønsker (valgfri)"}
                    </label>
                    <textarea 
                      name="message" 
                      rows="3" 
                      placeholder={isEn ? "Feel free to write a bit about your expectations or requests..." : "Skriv gerne lidt om dine forventninger eller ønsker..."}
                      className="w-full bg-stone-50 border border-stone-200 rounded-sm px-4 py-3 text-sm focus:border-[#004f3b] focus:bg-white outline-none transition-all text-stone-800 font-light resize-none placeholder-stone-400"
                    ></textarea>
                  </div>

                  <button 
                    type="submit" 
                    className="w-full bg-[#004f3b] text-white py-4 uppercase text-xs tracking-[0.3em] font-bold hover:bg-emerald-950 transition-colors rounded-sm shadow-md cursor-pointer"
                  >
                    {isEn ? "Send inquiry" : "Send forespørgsel"}
                  </button>

                  {status === 'success' && (
                    <p className="text-[#004f3b] text-center font-serif italic pt-2 text-sm font-medium">
                      {isEn
                        ? "Thank you! Your inquiry has been sent. Mette will get back to you as soon as possible."
                        : "Tak! Din forespørgsel er sendt. Mette vender tilbage hurtigst muligt."}
                    </p>
                  )}
                  {status === 'error' && (
                    <p className="text-red-700 text-center font-serif italic pt-2 text-sm">
                      {isEn ? "An error occurred. Please try again." : "Der skete en fejl. Prøv venligst igen."}
                    </p>
                  )}
                </form>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const CostaDelSolPage = () => {
  const [status, setStatus] = useState('');
  const [showLouiseBio, setShowLouiseBio] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(null);
  const { lang } = useLanguage();
  const isEn = lang === 'en';
  const { retreatCostaDelSol } = useCms();

  const heroBadge = retreatCostaDelSol?.hero_badge?.[lang] || retreatCostaDelSol?.hero_badge?.da || (isEn ? "Retreat 2 • Costa del Sol" : "Retreat 2 • Costa del Sol");
  const heroTitle = retreatCostaDelSol?.hero_titel?.[lang] || retreatCostaDelSol?.hero_titel?.da || (isEn ? "Home to Body & Soul – Spiritual Retreat on Costa del Sol" : "Hjem til krop og sjæl – spirituelt retreat på Costa del Sol");
  const heroSubtitle = retreatCostaDelSol?.hero_undertitel?.[lang] || retreatCostaDelSol?.hero_undertitel?.da || (isEn ? "5 days from October 6th–11th" : "5 dage fra den 6.–11. oktober");
  const heroImage = retreatCostaDelSol?.hero_billede?.[0] || "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/4b44ef21-4f88-42cb-bc9a-b504fb86f0b6_361c7dfa.jpg";
  const omOverskrift = retreatCostaDelSol?.om_overskrift?.[lang] || retreatCostaDelSol?.om_overskrift?.da || (isEn ? "Lush Andalusian Paradise" : "Frodigt andalusisk paradis");
  const omTekstHtml = retreatCostaDelSol?.om_tekst?.[lang] || retreatCostaDelSol?.om_tekst?.da;
  const detFaarDuTitel = retreatCostaDelSol?.det_faar_du_titel?.[lang] || retreatCostaDelSol?.det_faar_du_titel?.da || (isEn ? "Highlights" : "Det får du på retreatet");
  const detFaarDuPunkterHtml = retreatCostaDelSol?.det_faar_du_punkter?.[lang] || retreatCostaDelSol?.det_faar_du_punkter?.da;
  const detFaarDuBillede1 = retreatCostaDelSol?.det_faar_du_billede_1?.[0] || "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/3acbd5f0-7406-4e97-b302-6d77084f3b77_31c05988.jpg";
  const detFaarDuBillede2 = retreatCostaDelSol?.det_faar_du_billede_2?.[0] || "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/7ca0b125-8cf5-4581-9c1e-63198954fcb4_d597a93b.jpg";
  const prisHtml = retreatCostaDelSol?.pris_sektion?.[lang] || retreatCostaDelSol?.pris_sektion?.da;
  const trygtRumTitel = retreatCostaDelSol?.trygt_rum_titel?.[lang] || retreatCostaDelSol?.trygt_rum_titel?.da || (isEn ? "A Safe Space for Immersion and Peace" : "Et trygt rum til fordybelse og fred");
  const trygtRumTekstHtml = retreatCostaDelSol?.trygt_rum_tekst?.[lang] || retreatCostaDelSol?.trygt_rum_tekst?.da;
  const louiseTekstHtml = retreatCostaDelSol?.louise_tekst?.[lang] || retreatCostaDelSol?.louise_tekst?.da;
  const louiseFuldBioHtml = retreatCostaDelSol?.louise_fuld_bio?.[lang] || retreatCostaDelSol?.louise_fuld_bio?.da;
  const metteTekstHtml = retreatCostaDelSol?.mette_tekst?.[lang] || retreatCostaDelSol?.mette_tekst?.da;
  const programTekstHtml = retreatCostaDelSol?.program_tekst?.[lang] || retreatCostaDelSol?.program_tekst?.da;
  const formTitel = retreatCostaDelSol?.form_titel?.[lang] || retreatCostaDelSol?.form_titel?.da || (isEn ? "Inquiry & Booking" : "Tilmelding & Spørgsmål");
  const formTekst = retreatCostaDelSol?.form_tekst?.[lang] || retreatCostaDelSol?.form_tekst?.da || (isEn ? "Fill out the form below if you wish to learn more, join the interest list, or reserve your spot on the Costa del Sol retreat." : "Udfyld formularen her, hvis du ønsker at høre mere, tilmelde dig interesse-listen eller reservere din plads på retreatet på Costa del Sol.");

  const louiseImages = [
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/d18b2160-2e0b-4e51-a0a0-8a7025fb6c97.jpg",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/d18b2160-2e0b-4e51-a0a0-8a7025fb6c97.jpg",
      alt: isEn ? "Louise - Yoga & TRE" : "Louise - Yoga og TRE"
    },
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/1fd99919-d159-4125-a1a7-06d67e871e2c.jpg",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/1fd99919-d159-4125-a1a7-06d67e871e2c.jpg",
      alt: isEn ? "Louise - Sound Healing & Teacher" : "Louise - Lydhealing og underviser"
    }
  ];

  const metteImages = [
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/ska-rmbillede-2026-08-26-kl-08-46-09.png",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/ska-rmbillede-2026-08-26-kl-08-46-09.png",
      alt: isEn ? "Mette Brudvig - Spiritual Teacher & Clairvoyant" : "Mette Brudvig - Spirituel Lærer & Clairvoyant"
    }
  ];

  const costaDelSolGallery = [
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/3acbd5f0-7406-4e97-b302-6d77084f3b77_31c05988.jpg",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1800,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/3acbd5f0-7406-4e97-b302-6d77084f3b77_31c05988.jpg",
      alt: isEn ? "Costa del Sol Retreat - La Fuente" : "Costa del Sol Retreat - La Fuente"
    },
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/4b44ef21-4f88-42cb-bc9a-b504fb86f0b6_361c7dfa.jpg",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1800,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/4b44ef21-4f88-42cb-bc9a-b504fb86f0b6_361c7dfa.jpg",
      alt: isEn ? "Costa del Sol Retreat - La Fuente" : "Costa del Sol Retreat - La Fuente"
    },
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/7ca0b125-8cf5-4581-9c1e-63198954fcb4_d597a93b.jpg",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1800,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/7ca0b125-8cf5-4581-9c1e-63198954fcb4_d597a93b.jpg",
      alt: isEn ? "Costa del Sol Retreat - La Fuente" : "Costa del Sol Retreat - La Fuente"
    },
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/7fb045ae-6278-4bf5-afcb-e4dcb90f72e8_48896c63.jpg",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1800,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/7fb045ae-6278-4bf5-afcb-e4dcb90f72e8_48896c63.jpg",
      alt: isEn ? "Costa del Sol Retreat - La Fuente" : "Costa del Sol Retreat - La Fuente"
    },
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/7ff28f5a-bd95-4599-8216-5b2d5351ac52_8505b31a.jpg",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1800,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/7ff28f5a-bd95-4599-8216-5b2d5351ac52_8505b31a.jpg",
      alt: isEn ? "Costa del Sol Retreat - La Fuente" : "Costa del Sol Retreat - La Fuente"
    },
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/9cda0ed8-5060-489c-be46-aff61bfe117b_aaf42b0b.jpg",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1800,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/9cda0ed8-5060-489c-be46-aff61bfe117b_aaf42b0b.jpg",
      alt: isEn ? "Costa del Sol Retreat - La Fuente" : "Costa del Sol Retreat - La Fuente"
    },
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/341f989e-9a3c-4219-b1ab-d134b69463ab_3c76d8b2.jpg",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1800,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/341f989e-9a3c-4219-b1ab-d134b69463ab_3c76d8b2.jpg",
      alt: isEn ? "Costa del Sol Retreat - La Fuente" : "Costa del Sol Retreat - La Fuente"
    },
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/4396d262-7d87-406f-a8de-feddb25eb3e5_438c16ea.png",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1800,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/4396d262-7d87-406f-a8de-feddb25eb3e5_438c16ea.png",
      alt: isEn ? "Costa del Sol Retreat - La Fuente" : "Costa del Sol Retreat - La Fuente"
    },
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/4471a29b-486c-46b1-85a7-3312f98d263b_37bb399e.jpg",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1800,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/4471a29b-486c-46b1-85a7-3312f98d263b_37bb399e.jpg",
      alt: isEn ? "Costa del Sol Retreat - La Fuente" : "Costa del Sol Retreat - La Fuente"
    },
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/5573a39c-23fe-4e88-b89a-3782556c0ee4_d5fb5c8b.jpg",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1800,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/5573a39c-23fe-4e88-b89a-3782556c0ee4_d5fb5c8b.jpg",
      alt: isEn ? "Costa del Sol Retreat - La Fuente" : "Costa del Sol Retreat - La Fuente"
    },
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/846976da-832d-47aa-9f3e-b0ebbffa0c42_b2461db3.jpg",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1800,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/846976da-832d-47aa-9f3e-b0ebbffa0c42_b2461db3.jpg",
      alt: isEn ? "Costa del Sol Retreat - La Fuente" : "Costa del Sol Retreat - La Fuente"
    },
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/a84c3cca-71b3-4706-8cfe-74279dd8858c_71916304.jpg",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1800,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/a84c3cca-71b3-4706-8cfe-74279dd8858c_71916304.jpg",
      alt: isEn ? "Costa del Sol Retreat - La Fuente" : "Costa del Sol Retreat - La Fuente"
    },
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/bbcf678c-5059-4865-8ce7-6d9438374333_bbe94470.jpg",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1800,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/bbcf678c-5059-4865-8ce7-6d9438374333_bbe94470.jpg",
      alt: isEn ? "Costa del Sol Retreat - La Fuente" : "Costa del Sol Retreat - La Fuente"
    },
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/c6f8deb1-d247-4ed2-9753-44afe51b3b7a_df8ecf67.jpg",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1800,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/c6f8deb1-d247-4ed2-9753-44afe51b3b7a_df8ecf67.jpg",
      alt: isEn ? "Costa del Sol Retreat - La Fuente" : "Costa del Sol Retreat - La Fuente"
    },
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/cd46ff7f-04ea-484c-b7ce-747d332f9aea_d02dcc12.jpg",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1800,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/cd46ff7f-04ea-484c-b7ce-747d332f9aea_d02dcc12.jpg",
      alt: isEn ? "Costa del Sol Retreat - La Fuente" : "Costa del Sol Retreat - La Fuente"
    },
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/d52579fb-b4e0-4caf-bb28-acb244b8d610_30e249f8.png",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1800,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/d52579fb-b4e0-4caf-bb28-acb244b8d610_30e249f8.png",
      alt: isEn ? "Costa del Sol Retreat - La Fuente" : "Costa del Sol Retreat - La Fuente"
    },
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/f3468705-e64b-4880-8679-6566b580a995_20595c4b.jpg",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1800,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/f3468705-e64b-4880-8679-6566b580a995_20595c4b.jpg",
      alt: isEn ? "Costa del Sol Retreat - La Fuente" : "Costa del Sol Retreat - La Fuente"
    }
  ];

  const [lightboxImages, setLightboxImages] = useState(louiseImages);

  useEffect(() => {
    document.title = isEn 
      ? "Mette Brudvig - Spiritual Retreat on Costa del Sol" 
      : "Mette Brudvig - Spirituelt retreat på Costa del Sol";
    window.scrollTo(0, 0);
  }, [isEn]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#faf9f6] text-stone-800">
      {/* Light & Airy Hero Section */}
      <section className="pt-[185px] md:pt-[215px] lg:pt-[225px] pb-16 md:pb-24 px-6 bg-[#f5efe9]">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <FadeIn>
            <span className="text-xs uppercase tracking-[0.4em] font-bold text-[#004f3b] mb-4 block">
              {heroBadge}
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-serif text-stone-900 leading-tight">
              {heroTitle}
            </h1>
            <p className="text-lg md:text-2xl font-serif italic text-[#004f3b] max-w-2xl mx-auto mt-4 leading-relaxed font-medium">
              {heroSubtitle}
            </p>
            <div className="h-0.5 w-16 bg-[#004f3b] mx-auto my-6"></div>
          </FadeIn>

          {/* Hero Image */}
          <FadeIn>
            <div className="mt-8 rounded-2xl overflow-hidden shadow-xl border border-stone-200/60 bg-white">
              <img 
                src={heroImage} 
                alt="La Fuente Costa del Sol retreat" 
                className="w-full h-auto object-cover max-h-[520px] block"
              />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 px-6 bg-[#faf9f6]">
        <div className="max-w-4xl mx-auto space-y-20">
          
          {/* Section: Om retreatet / La Fuente */}
          <div className="space-y-8">
            <FadeIn>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
                {omOverskrift}
              </h2>
              <div className="h-0.5 w-12 bg-[#004f3b] mt-4 mb-6"></div>
            </FadeIn>

            <div className="space-y-5 text-stone-700 font-light text-base md:text-lg leading-relaxed">
              <FadeIn className="space-y-5">
                {omTekstHtml ? (
                  <div 
                    className="space-y-5"
                    dangerouslySetInnerHTML={{ __html: omTekstHtml }}
                  />
                ) : (
                  <>
                    <p>
                      {isEn 
                        ? "Step out of everyday life into a lush Andalusian paradise in the late South Spanish summer."
                        : "Her træder du ud af hverdagen og ind i et frodigt andalusisk paradis i den sydspanske sensommer."}
                    </p>
                    <p>
                      {isEn 
                        ? "Luxury and authenticity meet at beautiful La Fuente, where rooms and living spaces are distributed across two lovely houses with plenty of wonderful outdoor space. The villas are located on a large estate surrounded by gardens, fruit trees, mountains, and nature as far as the eye can see. There is natural spring water in the pool, and the property is primarily solar-powered."
                        : "Luksus og autenticitet mødes her på smukke La Fuente, hvor værelser og opholdsrum er fordelt på to skønne huse med masser af lækker udendørsplads. Villaerne ligger på en stor landejendom omgivet af haver, frugttræer, bjerge og natur så langt øjet rækker. Her er naturally kildevand i poolen, og stedet drives primært af solenergi."}
                    </p>
                    <p>
                      {isEn 
                        ? "Here you can wake up to birdsong, enjoy that it is still summer and sunny in Southern Spain, and watch the sun set over the Andalusian landscape."
                        : "Her kan du vågne til fuglesang, nyde at det stadig er sommer og sol her i Sydspanien og se solen gå ned over det andalusiske landskab."}
                    </p>
                  </>
                )}
              </FadeIn>
            </div>
          </div>

          {/* Section: Det får du på retreatet */}
          <div className="border-t border-stone-200 pt-16">
            <div className="grid md:grid-cols-12 gap-10 items-stretch">
              <div className="md:col-span-7 bg-white p-8 border border-stone-200/80 rounded-sm shadow-sm space-y-6 flex flex-col justify-between">
                <div>
                  <h2 className="text-2xl md:text-3xl font-serif text-stone-900">
                    {detFaarDuTitel}
                  </h2>
                  <div className="h-0.5 w-12 bg-[#004f3b] mt-3 mb-6"></div>
                </div>
                {detFaarDuPunkterHtml ? (
                  <div 
                    className="space-y-4 text-stone-700 font-light text-base [&>ul]:space-y-4 [&>ul>li]:flex [&>ul>li]:items-start [&>ul>li]:before:content-['•'] [&>ul>li]:before:text-[#004f3b] [&>ul>li]:before:mr-3 [&>ul>li]:before:font-serif [&>ul>li]:before:font-bold"
                    dangerouslySetInnerHTML={{ __html: detFaarDuPunkterHtml }}
                  />
                ) : (
                  <ul className="space-y-4 text-stone-700 font-light text-base">
                    {(isEn ? [
                      "Gentle yoga focused on peace, presence, and grounding",
                      "Guided meditations and inner reflection",
                      "Gentle TRE focused on calming the nervous system",
                      "Greater awareness of bodily signals and your own needs",
                      "Sound healing and cacao ceremony",
                      "Healthy, nourishing, and delicious food",
                      "Time for silence, nature, and yourself",
                      "Practical tools and a meditation to take home with you"
                    ] : [
                      "Blid yoga med fokus på ro, nærvær og jordforbindelse",
                      "Guidede meditationer og indre fordybelse",
                      "Nænsom TRE med fokus på at berolige nervesystemet",
                      "Større bevidsthed om kroppens signaler og egne behov",
                      "Lydhealing og cacaoceremoni",
                      "Sund, nærende og lækker mad",
                      "Tid til stilhed, natur og dig selv",
                      "Konkrete værktøjer og en meditation, du kan tage med hjem"
                    ]).map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="text-[#004f3b] mr-3 font-serif">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="md:col-span-5 flex flex-col gap-4">
                <FadeIn delay={100} className="flex-1 h-full">
                  <img 
                    src={detFaarDuBillede1} 
                    alt="Finca La Fuente pool og udendørsarealer" 
                    className="w-full h-full min-h-[160px] object-cover rounded-sm border border-stone-200 shadow-sm"
                  />
                </FadeIn>
                <FadeIn delay={150} className="flex-1 h-full">
                  <img 
                    src={detFaarDuBillede2} 
                    alt="La Fuente haver og bjerge" 
                    className="w-full h-full min-h-[160px] object-cover rounded-sm border border-stone-200 shadow-sm"
                  />
                </FadeIn>
              </div>
            </div>
          </div>

          {/* Section: Pris */}
          <div className="border-t border-stone-200 pt-16">
            <FadeIn className="bg-white border border-stone-200 p-8 md:p-12 rounded-sm shadow-sm space-y-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-serif text-stone-900">
                  {isEn ? "Price & Terms" : "Pris"}
                </h2>
                <div className="h-0.5 w-12 bg-[#004f3b] mt-3 mb-6"></div>
              </div>
              {prisHtml ? (
                <div 
                  className="space-y-4 text-stone-700 font-light text-base md:text-lg leading-relaxed [&>p>strong]:text-[#004f3b] [&>p>strong]:font-semibold"
                  dangerouslySetInnerHTML={{ __html: prisHtml }}
                />
              ) : (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <div className="flex flex-wrap items-baseline gap-3.5">
                      <span className="text-3xl md:text-5xl font-serif font-bold text-[#004f3b]">
                        {isEn ? "7,500 DKK" : "7.500 kr."}
                      </span>
                      <span className="text-emerald-800 bg-emerald-50 px-3.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider border border-emerald-200">
                        {isEn ? "Early bird until Sept. 20 (40% discount)" : "Early bird indtil 20. september (40% rabat)"}
                      </span>
                    </div>
                    <div className="text-stone-500 text-sm md:text-base font-light flex items-center gap-2 pt-0.5">
                      <span>{isEn ? "Original price:" : "Oprindelig pris:"}</span>
                      <span className="line-through font-medium text-stone-600">{isEn ? "12,500 DKK" : "12.500 kr."}</span>
                    </div>
                  </div>
                  <p className="text-stone-700 font-light text-base md:text-lg pt-1">
                    {isEn ? (
                      <>
                        Early bird before September 20th: DKK 7,500 per person in shared double room
                        <br />
                        (40% discount, original price DKK 12,500).
                      </>
                    ) : (
                      <>
                        Early bird inden 20. september: 7.500 kr. pr. person i delt dobbeltværelse
                        <br />
                        (40% rabat, oprindelig pris 12.500 kr.).
                      </>
                    )}
                  </p>
                  <p className="text-stone-600 font-light text-sm">
                    {isEn 
                      ? "If two of you travel together, you can naturally share a room."
                      : "Rejser I to sammen, kan I naturligt få værelse sammen."}
                  </p>
                  <p className="text-stone-800 font-medium text-base pt-2">
                    {isEn ? "Single room supplement: DKK 4,000" : "Tillæg for eneværelse: 4.000 kr."}
                  </p>
                </div>
              )}

              <div className="pt-6 border-t border-stone-200 space-y-3 text-stone-600 font-light text-sm leading-relaxed">
                <p className="flex items-start gap-2">
                  <i className="fa-solid fa-circle-check text-[#004f3b] mt-1 text-xs" />
                  <span>
                    {isEn 
                      ? "The price includes food, accommodation, and transport to and from Málaga Airport, which is approximately an hour's drive from the villa."
                      : "Prisen inkluderer kost, logi samt transport til og fra Málaga Lufthavn, som ligger cirka en times kørsel fra villaen."}
                  </span>
                </p>
                <p className="flex items-start gap-2">
                  <i className="fa-solid fa-circle-xmark text-stone-400 mt-1 text-xs" />
                  <span>
                    {isEn ? "The price excludes flight and insurance." : "Prisen er eksklusiv fly og forsikring."}
                  </span>
                </p>
              </div>

              <div className="pt-4 flex flex-col sm:flex-row sm:items-center gap-3.5">
                <a 
                  href="https://pay.lumant.ai/kurv/Ia5X-retreat-costa-del-sol-okt-2027" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#004f3b] text-white px-8 py-4 rounded-sm font-bold text-xs uppercase tracking-[0.2em] hover:bg-emerald-950 transition-colors shadow-md text-center"
                >
                  <span>{isEn ? "Book & Pay Online (Costa del Sol)" : "Tilmeld & Betal online (Costa del Sol)"}</span>
                  <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true" />
                </a>
                <a 
                  href="tel:+4527507083"
                  className="inline-flex items-center justify-center gap-2 bg-stone-50 hover:bg-stone-100 text-stone-800 border border-stone-300 px-6 py-4 rounded-sm font-medium text-xs uppercase tracking-[0.15em] transition-colors text-center"
                >
                  <i className="fa-solid fa-phone text-[#004f3b] text-xs" aria-hidden="true" />
                  <span>{isEn ? "Call: 27 50 70 83" : "Ring: 27 50 70 83"}</span>
                </a>
              </div>
            </FadeIn>
          </div>

          {/* Section: Trygt rum & Vores baggrund */}
          <div className="border-t border-stone-200 pt-16 space-y-8">
            <FadeIn>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
                {trygtRumTitel}
              </h2>
              <div className="h-0.5 w-12 bg-[#004f3b] mt-4 mb-6"></div>
            </FadeIn>

            <div className="space-y-5 text-stone-700 font-light text-base md:text-lg leading-relaxed">
              <FadeIn className="space-y-5">
                {trygtRumTekstHtml ? (
                  <div 
                    className="space-y-5 [&>p>strong]:font-medium [&>p>strong]:text-stone-900"
                    dangerouslySetInnerHTML={{ __html: trygtRumTekstHtml }}
                  />
                ) : (
                  <>
                    <p>
                      {isEn 
                        ? "The retreat is hosted by me, Mette, and my wonderful skilled friend Louise participates and contributes with lovely gentle yoga, sound healing, and her deep knowledge of the body. It is not a classic yoga retreat focused on advanced flows or how flexible or experienced you are."
                        : "Retreatet holdes af  mig, Mette, og min dejlige dygtige veninde Louise deltager og bidrager med skøn blid yoga, lydhealing og sin store viden om kroppen. Det er ikke et klassisk yoga retreat med fokus på avancerede flows eller på, hvor smidig eller øvet du er."}
                    </p>
                    <p>
                      {isEn 
                        ? "The retreat is created from our own experiences with life's challenges. Louise has been through a cancer journey and struggled with anxiety, and Mette has been affected by stress (more than once), and we both had to create a healthier and more peaceful life to thrive and feel whole. We have both educated ourselves and undergone major personal transformation so we can also help others find their peace and joy in life."
                        : "Retreatet er skabt ud fra vores egne erfaringer med livets udfordringer. Louise har været igennem et kræftforløb og kæmpet med angst, og Mette har været stressramt (mere end en gang), og vi har begge været nødt til at skabe et sundere og mere fredfyldt liv for at trives og føle os hele. Vi har begge uddannet os og gennemgået stor personlig transformation, så vi også kan hjælpe andre mennesker med at finde deres fred og livsglæde."}
                    </p>
                    <p>
                      {isEn 
                        ? "We have both felt firsthand what it means to need to stop, slow down, and find our way back to ourselves. Therefore, we wish to use what we have learned and experienced to create a safe space for reflection and peace, where there is room for you exactly as you are."
                        : "Vi har begge mærket på egen krop, hvad det vil sige at have brug for at stoppe op, komme ned i gear og finde tilbage til sig selv. Derfor ønsker vi at bruge det, vi selv har lært og erfaret, til at skabe et trygt rum til fordybelse og fred, hvor der er plads til dig, præcis som du er."}
                    </p>
                    <p>
                      {isEn 
                        ? "We have designed the days so your body truly gets to settle down and you come closer to yourself. Through gentle yoga exercises, breathwork, healing meditations, and time to simply be you, you strengthen the connection with your soul and feel yourself. New insights, emotions, and a deeper understanding of yourself and the life you wish to live can emerge when you return home. You will gain tools to regulate your nervous system and continue to strengthen your connection to yourself and your soul growth in daily life."
                        : "Vi har designet dagene, så din krop virkelig får lov til at falde til ro, og du kommer tættere på dig selv. Gennem blide yogaøvelser, åndedræt, healende meditationer og tid til at bare at være dig, styrker du forbindelsen med din sjæl og mærker dig selv. Her kan der opstå nye indsigter, følelser og en dybere forståelse af dig selv og det liv, du ønsker at leve, når du kommer hjem. Du får værktøjer med hjem til at kunne regulere dit nervesystem og fortsat styrke din forbindelse til dig selv og din sjælelige vækst i din hverdag."}
                    </p>
                    <p>
                      {isEn 
                        ? "We also invite you to a beautiful cacao ceremony, sound healing, and guided spiritual meditations to meet your guides, and you get the opportunity to travel back to a past life."
                        : "Vi inviterer også til en smuk cacaoceremoni, lydhealing og guidede spirituelle meditationer til møder med dine guider, og du får mulighed for at rejse tilbage til et tidligere liv."}
                    </p>
                    <p>
                      {isEn 
                        ? "The pace is slow, and you get plenty of time to simply be. You can take a daily dip in the spring water pool, sunbathe by the pool while reading a book, or take a nap in the shade of one of the garden's beautiful fruit trees. If you feel like exploring, you can take an excursion to the beach at Casares Costa or to the cozy, white mountain village of Casares Pueblo."
                        : "Tempoet er lavt, og du får også god tid til bare at være. Du kan tage en daglig dukkert i poolen med kildevand, slikke sol ved poolen, mens du læser en dog eller tage lur i skyggen af et af havens smukke frugttræer. Er energien til mere, kan du også tage til udflugt til stranden ved Casares Costa eller til den hyggelige, hvide bjergby Casares Pueblo."}
                    </p>
                    <p className="font-medium text-stone-900 bg-[#f5efe9] p-5 rounded-sm border-l-4 border-[#004f3b]">
                      {isEn 
                        ? "The dates are arranged so you can stay and enjoy the autumn holiday on lovely Costa Del Sol, where the Mediterranean, Malaga, Granada, and magnificent mountain landscapes offer a wealth of experiences."
                        : "Datoerne er tilrettelagt, så du kan blive og nyde efterårsferien på dejlige Costa Del Sol, hvor både Middelhavet, Malaga, Granada og det storslåede bjerglandskaber byder på et hav af oplevelser."}
                    </p>
                  </>
                )}
              </FadeIn>
            </div>
          </div>

          {/* Section: Dine værter og undervisere */}
          <div className="border-t border-stone-200 pt-16 space-y-10">
            <FadeIn>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
                {isEn ? "Your Hosts & Teachers" : "Dine værter og undervisere"}
              </h2>
              <div className="h-0.5 w-12 bg-[#004f3b] mt-4 mb-6"></div>
            </FadeIn>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Host 1: Louise */}
              <FadeIn className="bg-white p-8 border border-stone-200/80 rounded-sm shadow-sm space-y-5 flex flex-col justify-between">
                <div className="space-y-4">
                  <span className="text-xs uppercase tracking-widest text-[#004f3b] font-bold block">
                    {isEn ? "Yoga, TRE & Sound Healing" : "Yoga, TRE & Lydhealing"}
                  </span>
                  <h3 className="text-2xl font-serif text-stone-900">Louise</h3>

                  {/* 2 Billeder af Louise */}
                  <div className="grid grid-cols-2 gap-2.5 my-3 rounded-xs overflow-hidden">
                    {louiseImages.map((img, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          setLightboxImages(louiseImages);
                          setActiveImageIndex(idx);
                        }}
                        className="relative group overflow-hidden rounded-xs cursor-pointer block text-left focus:outline-none focus:ring-2 focus:ring-[#004f3b] aspect-[3/4] sm:aspect-auto sm:h-44 md:h-52 bg-stone-100"
                        title={isEn ? "Click to enlarge" : "Klik for at forstørre"}
                        aria-label={img.alt}
                      >
                        <img 
                          src={img.src} 
                          alt={img.alt} 
                          className="w-full h-full object-cover rounded-xs transition-transform duration-300 group-hover:scale-105" 
                        />
                        <div className="absolute inset-0 bg-stone-900/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                          <span className="w-8 h-8 rounded-full bg-white/95 text-[#004f3b] flex items-center justify-center text-xs shadow-md transform scale-90 group-hover:scale-100 transition-transform">
                            <i className="fa-solid fa-magnifying-glass-plus" />
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>

                  {louiseTekstHtml ? (
                    <div 
                      className="text-stone-700 font-light text-base leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: louiseTekstHtml }}
                    />
                  ) : (
                    <p className="text-stone-700 font-light text-base leading-relaxed">
                      {isEn 
                        ? "Louise is a trained educator. She was diagnosed with chronic cancer and dived deep into nutrition, body, and energy to heal herself. She has completed multiple yoga teacher trainings, including in the East, and is certified in TRE (nervous system regulation) and sound healing. Louise has been cancer-free for 16 years."
                        : "Louise er uddannet pædagog. Hun blev diagnosticeret med kronisk kræft og er dykket dybt ned i kost, krop og energi for at helbrede sig selv. Hun har taget flere yogauddannelser, bla. i Østen, og hun er uddannet i TRE (regulering af nervesystemet) og lydhealing. Louise har været kræftfri i 16 år."}
                    </p>
                  )}

                  {/* Expandable Bio */}
                  {showLouiseBio && (
                    <div className="pt-4 border-t border-stone-100 space-y-4 text-stone-600 font-light text-sm leading-relaxed text-left">
                      {louiseFuldBioHtml ? (
                        <div 
                          className="space-y-4"
                          dangerouslySetInnerHTML={{ __html: louiseFuldBioHtml }}
                        />
                      ) : (
                        <>
                          <p>
                            Louises rejse ind i arbejdet med krop og bevidsthed begyndte for 18 år siden, da hun fik konstateret kræft flere steder i kroppen. Denne periode blev et vendepunkt, som åbnede for en dybere forståelse af kroppens egen intelligens og evne til heling. Hun har været igennem lange sygdomsforløb præget af dødsangst, paranoia, dysregulering samt både fysiske og psykiske udfordringer. Disse erfaringer udgør idag et vigtigt fundament for hendes arbejde. Det har givet hende en dyb forståelse for vores sårbarhed og nervesystemets kompleksitet og samtidig tydeliggjort vigtigheden af at skabe trygge rammer, hvor vi kan møde os selv nænsomt og kærligt. Særligt i de øjeblikke, hvor frygt og indre hårdhed tager over.
                          </p>
                          <p>
                            Louise har en bachelor i pædagogik og har gennemført flere 200-timers yogalæreruddannelser bla i Indonesien, Portugal og USA. Hun bruger somatic bevægelse i tilgangen til kroppen, derudover er hun uddannet TRE space holder og faciliterer klangbad, Gennem årene har hun rejst verden rundt, lært tai chi i bjergene i kina, dyrket muay thai i thailand og undervist på retreats og workshops både i Danmark og i udlandet.
                          </p>
                          <p>
                            Hendes faglige baggrund og internationale uddannelser danner et solidt fundament for hendes arbejde, hvor hun forener viden, erfaring og nærvær i mødet med det enkelte menneske. Hendes tilgang er både jordnær, sjov og nænsom. Hun arbejder med kroppen som indgang til forandring og møder hver proces med nærvær, respekt og blidhed. Hun inddrager energiarbejde og lydfrekvenser fra krystalsangskåle og andre instrumenter, som støtter nervesystemet og inviterer til dyb afspænding og indre ro.
                          </p>
                          <p>
                            Det Louise er god til er at skabe trygge rammer, hvor der er plads til at lande i kroppen, mærke sig selv og give slip i eget tempo. For hende handler heling ikke om at presse eller fikse, men om at lytte og møde det, der er, med venlighed og nænsomhed. Vi behøver ikke være bange for vores skyggesider. Det, vi kalder mørke, er ofte blot det, vi endnu ikke har set eller forstået. Når vi bringer opmærksomhed og bevidsthed til det, kan det blive en kilde til indsigt, forandring og større selvforståelse.
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </div>

                <button 
                  onClick={() => setShowLouiseBio(!showLouiseBio)}
                  className="inline-flex items-center gap-2 text-[#004f3b] text-sm font-medium hover:underline pt-2 cursor-pointer"
                >
                  <span>{showLouiseBio ? (isEn ? "Show less" : "Vis mindre") : (isEn ? "Read more about Louise here" : "Læs mere om Louise her")}</span>
                  <i className={`fa-light ${showLouiseBio ? 'fa-chevron-up' : 'fa-chevron-down'} text-xs`} />
                </button>
              </FadeIn>

              {/* Host 2: Mette */}
              <FadeIn delay={100} className="bg-white p-8 border border-stone-200/80 rounded-sm shadow-sm space-y-5 flex flex-col justify-between">
                <div className="space-y-4">
                  <span className="text-xs uppercase tracking-widest text-[#004f3b] font-bold block">
                    {isEn ? "Spiritual Teacher & Clairvoyant" : "Spirituel Lærer & Clairvoyant"}
                  </span>
                  <h3 className="text-2xl font-serif text-stone-900">Mette</h3>

                  {/* Billede af Mette */}
                  <div className="my-3 rounded-xs overflow-hidden">
                    <button
                      type="button"
                      onClick={() => {
                        setLightboxImages(metteImages);
                        setActiveImageIndex(0);
                      }}
                      className="relative group overflow-hidden rounded-xs cursor-pointer block text-left focus:outline-none focus:ring-2 focus:ring-[#004f3b] w-full aspect-[4/3] sm:aspect-auto sm:h-64 md:h-72 bg-stone-100"
                      title={isEn ? "Click to enlarge" : "Klik for at forstørre"}
                      aria-label={isEn ? "Mette Brudvig" : "Mette Brudvig"}
                    >
                      <img 
                        src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/ska-rmbillede-2026-08-26-kl-08-46-09.png" 
                        alt="Mette Brudvig" 
                        className="w-full h-full object-cover object-[center_20%] rounded-xs transition-transform duration-300 group-hover:scale-105" 
                      />
                      <div className="absolute inset-0 bg-stone-900/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                        <span className="w-8 h-8 rounded-full bg-white/95 text-[#004f3b] flex items-center justify-center text-xs shadow-md transform scale-90 group-hover:scale-100 transition-transform">
                          <i className="fa-solid fa-magnifying-glass-plus" />
                        </span>
                      </div>
                    </button>
                  </div>

                  {metteTekstHtml ? (
                    <div 
                      className="text-stone-700 font-light text-base leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: metteTekstHtml }}
                    />
                  ) : (
                    <p className="text-stone-700 font-light text-base leading-relaxed">
                      {isEn 
                        ? "Mette is a spiritual teacher, coach, and clairvoyant. She is a trained journalist and former boarding school teacher, emphasizing explaining both spiritual and scientific aspects in understandable, practical ways. She facilitates cacao ceremonies and deep meditations for both healing and soul journeys."
                        : "Mette er spirituel lærer, coach og clairvoyant. Hun er uddannet journalist og har været efterskolelærer og gør meget ud af at forklare både det åndelige og videnskabelige, så det er forståeligt og brugbart. Hun laver cacoa ceremoni og dybe meditationer indenfor både healing og sjælerejser."}
                    </p>
                  )}
                </div>

                <Link 
                  to={isEn ? "/en/about-mette" : "/om-mette"}
                  className="inline-flex items-center gap-2 text-[#004f3b] text-sm font-medium hover:underline pt-2"
                >
                  <span>{isEn ? "Read more about Mette here" : "Læs mere om Mette her"}</span>
                  <i className="fa-light fa-arrow-right text-xs" />
                </Link>
              </FadeIn>
            </div>
          </div>

          {/* Section: Program */}
          <div className="border-t border-stone-200 pt-16 space-y-8">
            <FadeIn>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
                {isEn ? "Program" : "Program"}
              </h2>
              <div className="h-0.5 w-12 bg-[#004f3b] mt-4 mb-6"></div>
              {programTekstHtml ? (
                <div 
                  className="bg-white p-8 md:p-10 border border-stone-200/80 rounded-sm shadow-sm space-y-4 text-stone-700 font-light text-base [&>ul]:grid [&>ul]:sm:grid-cols-2 [&>ul]:gap-4 [&>ul>li]:flex [&>ul>li]:items-start [&>ul>li]:space-x-3 [&>ul>li]:bg-[#f5efe9]/50 [&>ul>li]:p-3.5 [&>ul>li]:rounded-sm [&>ul>li]:border [&>ul>li]:border-stone-200/50 [&>ul>li]:before:content-['✦'] [&>ul>li]:before:text-[#004f3b] [&>ul>li]:before:text-xs [&>ul>li]:before:mt-1 [&>p]:italic [&>p]:text-stone-600"
                  dangerouslySetInnerHTML={{ __html: programTekstHtml }}
                />
              ) : (
                <>
                  <p className="text-stone-600 font-light text-base md:text-lg leading-relaxed italic">
                    {isEn 
                      ? "We follow the energy and you as a group, and we can receive channeled messages about what you and the group need. Therefore, here is the program in headlines rather than exact details:"
                      : "Vi følger energien og jer som gruppe, og vi kan få kanaliserede beskeder om, hvad du og I har brug for. Derfor får du her programmet her får du i overskrifter og ikke detaljer:"}
                  </p>
                  <div className="bg-white p-8 md:p-10 border border-stone-200/80 rounded-sm shadow-sm space-y-4 mt-6">
                    <ul className="grid sm:grid-cols-2 gap-4 text-stone-700 font-light text-base">
                      {(isEn ? [
                        "Morning yoga",
                        "Morning dip in the spring water pool for those who wish",
                        "Healthy, delicious breakfast",
                        "Shared reflection and meditation",
                        "Peace and quiet time by the pool, in the garden, or wherever you wish",
                        "Healthy, light, and delicious lunch",
                        "Mountain hike – connection to yourself and nature (optional)",
                        "Peace and quiet time by the pool, in the garden, or wherever you wish",
                        "Spiritual meditation traveling inward for answers and insights (optional)",
                        "Healthy, delicious dinner",
                        "Cozy conversations or personal time",
                        "Sound healing"
                      ] : [
                        "Morgenyoga",
                        "Morgendukkert i poolen med kildevand for dem, der har lyst",
                        "Sund velsmagende morgenmad",
                        "Fælles fordybelse og meditation",
                        "Ro og egen tid ved poolen, i haven eller hvor, du ønsker",
                        "Sund, let og velsmagende frokost",
                        "Vandretur i bjergene – forbindelse til dig selv og til naturen (frivilligt)",
                        "Ro og egen tid ved poolen, i haven eller hvor, du ønsker",
                        "Spirituel meditation, hvor du rejser indad efter svar og indsigter (frivilligt)",
                        "Sund velsmagende aftensmad",
                        "Hygge og samtaler eller egentid",
                        "Lydhealing"
                      ]).map((item, idx) => (
                        <li key={idx} className="flex items-start space-x-3 bg-[#f5efe9]/50 p-3.5 rounded-sm border border-stone-200/50">
                          <i className="fa-solid fa-sparkles text-[#004f3b] text-xs mt-1" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="pt-4 text-stone-600 font-light text-sm italic border-t border-stone-100">
                      {isEn 
                        ? "Our teaching and the knowledge that can inspire you come naturally into everything we do."
                        : "Vores undervisning og den viden, der kan inspirerer dig, kommer naturally ind i alt, hvad vi laver."}
                    </p>
                    <p className="text-stone-500 font-light text-xs pt-1 flex items-center gap-1.5">
                      <i className="fa-solid fa-phone text-[#004f3b] text-[11px]" aria-hidden="true" />
                      <span>
                        {isEn ? "Questions before signing up? Call Mette directly on " : "Har du spørgsmål inden tilmelding? Ring direkte til Mette på "}
                        <a href="tel:+4527507083" className="underline font-medium text-stone-800 hover:text-[#004f3b]">27 50 70 83</a>
                      </span>
                    </p>
                  </div>
                </>
              )}
            </FadeIn>
          </div>

          {/* Section: Galleri / Stemningsbilleder fra Costa del Sol & La Fuente */}
          <div className="border-t border-stone-200 pt-16 space-y-8">
            <FadeIn>
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                  <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#004f3b] block mb-2">
                    {isEn ? "Gallery & Atmosphere" : "Galleri & Stemning"}
                  </span>
                  <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
                    {isEn ? "Atmosphere & Surroundings at La Fuente" : "Stemningsbilleder fra Costa del Sol & La Fuente"}
                  </h2>
                  <div className="h-0.5 w-12 bg-[#004f3b] mt-4"></div>
                </div>
                <p className="text-xs uppercase tracking-widest text-stone-500 font-medium">
                  {isEn ? "Click any image to enlarge" : "Klik på et billede for at forstørre"}
                </p>
              </div>
            </FadeIn>

            <FadeIn>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                {costaDelSolGallery.map((img, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      setLightboxImages(costaDelSolGallery);
                      setActiveImageIndex(idx);
                    }}
                    className={`relative group overflow-hidden rounded-sm cursor-pointer block text-left focus:outline-none focus:ring-2 focus:ring-[#004f3b] bg-stone-100 border border-stone-200/80 shadow-xs hover:shadow-md transition-all ${
                      idx === 0 || idx === 7 ? 'col-span-2 row-span-2 aspect-[4/3] sm:aspect-auto' : 'aspect-square'
                    }`}
                    title={isEn ? "Click to enlarge" : "Klik for at forstørre"}
                    aria-label={img.alt}
                  >
                    <img 
                      src={img.src} 
                      alt={img.alt} 
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" 
                    />
                    <div className="absolute inset-0 bg-stone-900/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <span className="w-10 h-10 rounded-full bg-white/95 text-[#004f3b] flex items-center justify-center text-sm shadow-md transform scale-90 group-hover:scale-100 transition-transform">
                        <i className="fa-solid fa-magnifying-glass-plus" />
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </FadeIn>
          </div>

          {/* 4 Udtalelser fra deltagere og klienter - flyttet op inden tilmeldingsskema */}
          <div className="border-t border-stone-200/80 pt-16 pb-6">
            <FadeIn>
              <div className="text-center max-w-3xl mx-auto mb-10 space-y-2">
                <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#004f3b] block">
                  {isEn ? "Participant Testimonials" : "Udtalelser fra deltagere"}
                </span>
                <h3 className="text-2xl sm:text-3xl font-serif text-stone-900">
                  {isEn ? "What clients and participants say about Mette" : "Det siger tidligere deltagere om Mette"}
                </h3>
                <div className="h-0.5 w-12 bg-[#004f3b] mt-3 mx-auto"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  TESTIMONIALS.find(t => t.name === "Nina Bonde"),
                  TESTIMONIALS.find(t => t.name === "Signe Aarestrup"),
                  TESTIMONIALS.find(t => t.name === "Marianne Helleskov"),
                  TESTIMONIALS.find(t => t.name === "Sara Delpin")
                ].filter(Boolean).map((t, idx) => (
                  <FadeIn key={idx} delay={idx * 80} className="h-full">
                    <TestimonialQuoteCard testimonial={t} className="h-full bg-stone-50/70" />
                  </FadeIn>
                ))}
              </div>
            </FadeIn>
          </div>

          {/* Contact Form */}
          <div id="tilmelding-skema" className="border-t border-stone-200 pt-16">
            <FadeIn className="bg-white p-8 md:p-12 rounded-sm border border-stone-200 shadow-md max-w-3xl mx-auto space-y-6">
              <div className="text-center space-y-2">
                <h3 className="text-2xl font-serif text-stone-900">
                  {formTitel}
                </h3>
                <p className="text-stone-600 font-light text-sm">
                  {formTekst}
                </p>
                <div className="pt-2">
                  <a 
                    href="https://pay.lumant.ai/kurv/Ia5X-retreat-costa-del-sol-okt-2027"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between gap-3 bg-[#004f3b] text-white p-4 rounded-sm hover:bg-emerald-950 transition-colors shadow-sm"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center text-white shrink-0">
                        <i className="fa-solid fa-credit-card text-xs" aria-hidden="true" />
                      </div>
                      <div className="text-left">
                        <span className="font-bold text-sm sm:text-base">
                          {isEn ? "Book your spot here" : "Book din plads her"}
                        </span>
                      </div>
                    </div>
                    <i className="fa-solid fa-arrow-up-right-from-square text-xs text-emerald-200" aria-hidden="true" />
                  </a>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6 pt-4">
                <input type="text" name="website_url" className="sr-only absolute" tabIndex="-1" autoComplete="off" />
                <input type="hidden" name="subject" value={isEn ? "Inquiry: Spiritual retreat on Costa del Sol" : "Forespørgsel: Spirituelt retreat på Costa del Sol"} />

                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase tracking-widest text-stone-500 font-medium">
                      {isEn ? "Name" : "Navn"}
                    </label>
                    <input 
                      required 
                      type="text" 
                      name="name"
                      placeholder={isEn ? "Your full name" : "Dit fulde navn"} 
                      className="w-full bg-stone-50 border border-stone-200 rounded-sm px-4 py-3 text-sm focus:border-[#004f3b] outline-none"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase tracking-widest text-stone-500 font-medium">
                      {isEn ? "Email" : "E-mail"}
                    </label>
                    <input 
                      required 
                      type="email" 
                      name="email"
                      placeholder={isEn ? "Your email address" : "Din e-mailadresse"} 
                      className="w-full bg-stone-50 border border-stone-200 rounded-sm px-4 py-3 text-sm focus:border-[#004f3b] outline-none"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase tracking-widest text-stone-500 font-medium">
                    {isEn ? "Message" : "Besked"}
                  </label>
                  <textarea 
                    required 
                    name="message"
                    rows={4} 
                    placeholder={isEn ? "Write your questions or wishes regarding room type..." : "Skriv dine spørgsmål eller ønsker vedr. værelsestype..."} 
                    className="w-full bg-stone-50 border border-stone-200 rounded-sm px-4 py-3 text-sm focus:border-[#004f3b] outline-none"
                  />
                </div>

                <button 
                  type="submit" 
                  className="w-full bg-[#004f3b] text-white py-4 uppercase text-xs tracking-[0.3em] font-bold hover:bg-emerald-900 transition-colors rounded-sm shadow-md cursor-pointer"
                >
                  {isEn ? "Send inquiry" : "Send henvendelse"}
                </button>

                {status === 'success' && (
                  <p className="text-emerald-700 text-center font-serif italic pt-2 text-sm">
                    {isEn 
                      ? "Thank you! Your inquiry has been sent. Mette will get back to you as soon as possible."
                      : "Tak! Din henvendelse er sendt. Mette vender tilbage hurtigst muligt."}
                  </p>
                )}
                {status === 'error' && (
                  <p className="text-red-700 text-center font-serif italic pt-2 text-sm">
                    {isEn ? "An error occurred. Please try again." : "Der skete en fejl. Prøv venligst igen."}
                  </p>
                )}
              </form>
            </FadeIn>
          </div>

        </div>
      </section>

      {/* Lightbox Modal */}
      <ImageLightboxModal 
        isOpen={activeImageIndex !== null}
        onClose={() => setActiveImageIndex(null)}
        images={lightboxImages || louiseImages}
        currentIndex={activeImageIndex ?? 0}
        onNavigate={setActiveImageIndex}
      />
    </div>
  );
};

const CostaDelSolSmagsproevePage = () => {
  const [showLouiseBio, setShowLouiseBio] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(null);
  const [lightboxImages, setLightboxImages] = useState(null);
  const { lang } = useLanguage();
  const isEn = lang === 'en';

  const retreatPath = isEn ? '/en/costa-del-sol' : '/costa-del-sol';

  useEffect(() => {
    document.title = isEn 
      ? "Mette Brudvig - 3 Hours with Sound Healing, Gentle Yoga & Meditation in Copenhagen" 
      : "Mette Brudvig - 3 timer med lydhealing, blid yoga og meditation den 2. september i København";
    window.scrollTo(0, 0);
  }, [isEn]);

  const louiseImages = [
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/d18b2160-2e0b-4e51-a0a0-8a7025fb6c97.jpg",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/d18b2160-2e0b-4e51-a0a0-8a7025fb6c97.jpg",
      alt: isEn ? "Louise - Yoga & TRE" : "Louise - Yoga og TRE"
    },
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/1fd99919-d159-4125-a1a7-06d67e871e2c.jpg",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/1fd99919-d159-4125-a1a7-06d67e871e2c.jpg",
      alt: isEn ? "Louise - Sound Healing & Teacher" : "Louise - Lydhealing og underviser"
    }
  ];

  const metteImages = [
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/image_9ba4ad3e.png",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/image_9ba4ad3e.png",
      alt: isEn ? "Mette Brudvig - Spiritual Teacher & Clairvoyant" : "Mette Brudvig - Spirituel Lærer & Clairvoyant"
    }
  ];

  const workshopGallery = [
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/gentle-yoga-workshop.jpg",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/gentle-yoga-workshop.jpg",
      alt: isEn ? "Gentle yoga and calm movement" : "Blid yoga og rolig bevægelse"
    },
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/sound-healing-workshop.jpg",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/sound-healing-workshop.jpg",
      alt: isEn ? "Sound healing with singing bowls" : "Lydhealing med syngeskåle"
    }
  ];

  const handleCopyPhone = () => {
    navigator.clipboard.writeText("27507083");
    setCopiedPhone(true);
    setTimeout(() => setCopiedPhone(false), 2500);
  };

  const weWorkWith = isEn ? [
    "Gentle yoga that helps you arrive in your body",
    "Sound healing with Louise",
    "Guided healing meditation",
    "Your energy and the connection to yourself",
    "Heart and soul connectedness"
  ] : [
    "Blid yoga, der hjælper dig ned i kroppen",
    "Lydhealing med Louise",
    "Guidet healende meditation",
    "Din energi og forbindelsen til dig selv",
    "Hjerte og sjælelig forbundethed"
  ];

  return (
    <div className="bg-[#faf9f6] min-h-screen">
      {/* Subpage Header / Hero */}
      <section className="relative pt-[185px] md:pt-[215px] lg:pt-[225px] pb-16 bg-[#f5efe9] border-b border-[#817262]/20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 z-10 relative">
          <FadeIn direction="up">
            <div className="mb-6 flex flex-wrap items-center gap-3">
              <Link 
                to={retreatPath}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#817262] hover:text-stone-900 transition-colors"
              >
                <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" />
                <span>{isEn ? "Costa del Sol Retreat" : "Costa del Sol retreat"}</span>
              </Link>
              <span className="text-stone-300">•</span>
              <span className="inline-flex items-center gap-1.5 bg-[#817262] text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full shadow-xs">
                <i className="fa-light fa-calendar-day text-xs" aria-hidden="true" />
                {isEn ? "2. September 2026 • Copenhagen" : "2. september • København"}
              </span>
            </div>
            
            <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#817262] mb-3 block">
              {isEn ? "Workshop • Sample Event" : "Smagsprøve • Workshop i København"}
            </span>

            <h1 className="text-3xl md:text-5xl lg:text-6xl font-serif text-stone-900 font-normal leading-tight mb-6">
              {isEn 
                ? "3 Hours with Sound Healing, Gentle Yoga & Meditation on September 2nd in Copenhagen" 
                : "3 timer med lydhealing, blid yoga og meditation den 2. september i København"}
            </h1>

            <p className="text-lg md:text-xl text-stone-700 font-light leading-relaxed max-w-3xl">
              {isEn 
                ? "Get out of your head and into your body, so you can let go of some of the noise, fear, and control that takes up space, and instead give more room to calm, intuition, and heart." 
                : "Kom ud af hovedet og ned i kroppen, så du kan slippe noget af den støj, frygt og kontrol, der fylder, og i stedet give mere plads til ro, intuition og hjerte."}
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Main Content Body */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            
            {/* Left Column: Text & Content */}
            <div className="lg:col-span-7 space-y-12">
              
              {/* Introduction Text */}
              <FadeIn className="space-y-6 text-stone-700 font-light text-base md:text-lg leading-relaxed">
                <p>
                  {isEn 
                    ? "On September 2nd, Louise and I invite you to 3 lovely hours of gentle yoga, sound healing, and guided healing meditation, where we work to create peace in the body and nervous system and strengthen the connection to yourself." 
                    : "Den 2. september inviterer Louise og jeg til 3 skønne timer med blid yoga, lydhealing og guidet healende meditation, hvor vi arbejder med at skabe ro i kroppen og nervesystemet og styrke forbindelsen til dig selv."}
                </p>

                {/* Retreat Connection Highlight Card */}
                <div className="bg-[#f5efe9] border-l-4 border-[#004f3b] p-6 md:p-8 rounded-r-sm space-y-4">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#004f3b]">
                    <i className="fa-light fa-sun" aria-hidden="true" />
                    <span>{isEn ? "Sample for Costa del Sol Retreat" : "Smagsprøve på Costa del Sol retreatet"}</span>
                  </div>
                  <p className="text-stone-800 font-normal">
                    {isEn 
                      ? "It is also a taste of my retreat on Costa del Sol on October 6th–11th, where we go even deeper with gentle yoga, meditation, healing, peace, and soul connection." 
                      : "Det er samtidig en smagsprøve på mit retreat på Costa del Sol den 6.–11. oktober, hvor vi går endnu dybere med blid yoga, meditation, healing, ro og sjælelig forbindelse."}
                  </p>
                  <p className="text-stone-900 font-medium bg-white/70 p-4 rounded-xs border border-stone-200/60">
                    <i className="fa-solid fa-gift text-[#817262] mr-2" aria-hidden="true" />
                    {isEn 
                      ? "If you subsequently choose to register for the retreat, I will deduct the 250 DKK you paid for this day." 
                      : "Vælger du efterfølgende at tilmelde dig retreatet, fratrækker jeg de 250 kr., du har betalt for denne dag."}
                  </p>
                  <div className="pt-2">
                    <Link 
                      to={retreatPath}
                      className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#004f3b] hover:text-stone-900 transition-colors"
                    >
                      <span>{isEn ? "Read more about Costa del Sol retreat" : "Læs mere om retreatet på Costa del Sol her"}</span>
                      <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              </FadeIn>

              {/* Vi skal arbejde med: */}
              <FadeIn className="space-y-6">
                <div>
                  <h2 className="text-2xl md:text-3xl font-serif text-stone-900 mb-2">
                    {isEn ? "We will work with:" : "Vi skal arbejde med:"}
                  </h2>
                  <div className="h-0.5 w-12 bg-[#004f3b] mt-3"></div>
                </div>

                <div className="bg-white p-6 md:p-8 border border-stone-200/80 rounded-sm shadow-xs space-y-4">
                  {weWorkWith.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-4">
                      <div className="w-7 h-7 rounded-full bg-[#f5efe9] flex items-center justify-center text-[#004f3b] shrink-0 mt-0.5">
                        <i className="fa-solid fa-sparkles text-xs" aria-hidden="true" />
                      </div>
                      <span className="text-stone-800 font-light text-base md:text-lg">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </FadeIn>

              {/* Visuals: Yoga & Sound Healing Photos */}
              <FadeIn className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {workshopGallery.map((img, idx) => (
                    <div 
                      key={idx}
                      className="group relative overflow-hidden rounded-sm border border-stone-200/80 aspect-4/3 cursor-pointer"
                      onClick={() => {
                        setLightboxImages(workshopGallery);
                        setActiveImageIndex(idx);
                      }}
                    >
                      <img 
                        src={img.src} 
                        alt={img.alt} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="bg-white/90 text-stone-900 text-xs px-3 py-1.5 rounded-full font-medium shadow-sm flex items-center gap-1.5">
                          <i className="fa-light fa-expand text-xs" aria-hidden="true" />
                          {isEn ? "View larger" : "Se større"}
                        </span>
                      </div>
                      <div className="absolute bottom-2 left-2 right-2 bg-stone-900/70 backdrop-blur-xs text-white text-[11px] px-2.5 py-1 rounded-xs font-light truncate">
                        {img.alt}
                      </div>
                    </div>
                  ))}
                </div>
              </FadeIn>

              {/* Dine undervisere */}
              <FadeIn className="space-y-8 border-t border-stone-200/80 pt-12">
                <div>
                  <h2 className="text-2xl md:text-3xl font-serif text-stone-900 mb-2">
                    {isEn ? "Your Teachers" : "Dine undervisere"}
                  </h2>
                  <div className="h-0.5 w-12 bg-[#004f3b] mt-3 mb-6"></div>
                  <p className="text-stone-700 font-light text-base md:text-lg leading-relaxed">
                    {isEn 
                      ? "Your teachers are Louise and me, Mette, and together we unite body, energy, consciousness, and spirituality. Louise works with yoga, the nervous system, somatic movement, and sound healing, while I work with meditation, healing, intuition, and soul connection. Together we create a safe and grounded space where you can land in your body, find peace, and feel yourself more clearly." 
                      : "Dine undervisere er Louise og mig, Mette, og sammen forener vi krop, energi, bevidsthed og spiritualitet. Louise arbejder med yoga, nervesystem, somatisk bevægelse og lydhealing, mens jeg arbejder med meditation, healing, intuition og sjælelig forbindelse. Sammen skaber vi et trygt og jordnært rum, hvor du kan komme ned i kroppen, finde ro og mærke dig selv tydeligere."}
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Louise Card */}
                  <div className="bg-white p-6 md:p-8 border border-stone-200/80 rounded-sm shadow-xs space-y-4 flex flex-col justify-between">
                    <div className="space-y-3">
                      <span className="text-xs uppercase tracking-widest text-[#004f3b] font-bold block">
                        {isEn ? "Yoga, TRE & Sound Healing" : "Yoga, TRE & Lydhealing"}
                      </span>
                      <h3 className="text-2xl font-serif text-stone-900">Louise</h3>
                      
                      <div className="grid grid-cols-2 gap-2 my-2 rounded-xs overflow-hidden">
                        {louiseImages.map((img, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => {
                              setLightboxImages(louiseImages);
                              setActiveImageIndex(idx);
                            }}
                            className="relative group aspect-square overflow-hidden bg-stone-100 block w-full focus:outline-hidden"
                          >
                            <img 
                              src={img.src} 
                              alt={img.alt} 
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <i className="fa-light fa-expand text-white text-xs" />
                            </div>
                          </button>
                        ))}
                      </div>

                      <p className="text-stone-600 text-sm font-light leading-relaxed">
                        {isEn 
                          ? "Louise is a trained educator. She has completed multiple yoga teacher trainings, is certified in TRE and sound healing, and creates safe spaces to ground and listen to your body." 
                          : "Louise er uddannet pædagog, har taget flere yogauddannelser, er uddannet i TRE og lydhealing, og skaber trygge rammer til at lande i kroppen og lytte til sig selv."}
                      </p>

                      {showLouiseBio && (
                        <div className="pt-3 border-t border-stone-100 text-stone-600 text-xs font-light space-y-2 leading-relaxed">
                          <p>
                            Louises rejse ind i arbejdet med krop og bevidsthed begyndte for 18 år siden, da hun fik konstateret kræft flere steder i kroppen. Denne periode blev et vendepunkt, som åbnede for en dybere forståelse af kroppens egen intelligens og evne til heling.
                          </p>
                          <p>
                            Louise har en bachelor i pædagogik og har gennemført flere 200-timers yogalæreruddannelser bl.a. i Indonesien, Portugal og USA. Hun bruger somatisk bevægelse, er uddannet TRE space holder og faciliterer klangbad.
                          </p>
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => setShowLouiseBio(!showLouiseBio)}
                      className="inline-flex items-center gap-1.5 text-xs text-[#004f3b] font-medium hover:underline pt-2 cursor-pointer"
                    >
                      <span>{showLouiseBio ? (isEn ? "Show less" : "Vis mindre") : (isEn ? "Read more about Louise" : "Læs mere om Louise")}</span>
                      <i className={`fa-light ${showLouiseBio ? 'fa-chevron-up' : 'fa-chevron-down'} text-xs`} />
                    </button>
                  </div>

                  {/* Mette Card */}
                  <div className="bg-white p-6 md:p-8 border border-stone-200/80 rounded-sm shadow-xs space-y-4 flex flex-col justify-between">
                    <div className="space-y-3">
                      <span className="text-xs uppercase tracking-widest text-[#004f3b] font-bold block">
                        {isEn ? "Spiritual Teacher & Clairvoyant" : "Spirituel lærer & clairvoyant"}
                      </span>
                      <h3 className="text-2xl font-serif text-stone-900">Mette Brudvig</h3>
                      
                      <div className="my-2 rounded-xs overflow-hidden">
                        <button
                          type="button"
                          onClick={() => {
                            setLightboxImages(metteImages);
                            setActiveImageIndex(0);
                          }}
                          className="relative group aspect-4/3 overflow-hidden bg-stone-100 block w-full focus:outline-hidden"
                        >
                          <img 
                            src={metteImages[0].src} 
                            alt={metteImages[0].alt} 
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <i className="fa-light fa-expand text-white text-xs" />
                          </div>
                        </button>
                      </div>

                      <p className="text-stone-600 text-sm font-light leading-relaxed">
                        {isEn 
                          ? "Mette works with meditation, healing, intuition, and soul connection. She guides you to reconnect with your heart and create a peaceful, authentic life." 
                          : "Mette arbejder med meditation, healing, intuition og sjælelig forbindelse. Hun guider dig kærligt til at forbinde dig med dit hjerte og leve i balance."}
                      </p>
                    </div>

                    <div className="pt-2">
                      <Link 
                        to={isEn ? "/en/about-mette" : "/om-mette"}
                        className="inline-flex items-center gap-1.5 text-xs text-[#004f3b] font-medium hover:underline"
                      >
                        <span>{isEn ? "Read more about Mette" : "Læs mere om Mette"}</span>
                        <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                      </Link>
                    </div>
                  </div>
                </div>
              </FadeIn>

              {/* Closing statement */}
              <FadeIn className="bg-[#f5efe9] p-8 rounded-sm border border-[#817262]/30 text-stone-800 font-light text-base md:text-lg leading-relaxed italic text-center">
                <i className="fa-solid fa-quote-left text-[#817262]/40 text-2xl block mb-3" aria-hidden="true" />
                {isEn 
                  ? "“We look forward to creating three hours where you can arrive completely in your body, feel yourself more clearly, and give more room to your heart, your intuition, and your own inner peace.”" 
                  : "“Vi glæder os til at skabe tre timer, hvor du kan komme helt ned i kroppen, mærke dig selv tydeligere og give mere plads til dit hjerte, din intuition og din egen indre ro.”"}
                <div className="mt-4 font-serif not-italic text-sm font-medium text-stone-900">
                  — Louise & Mette
                </div>
              </FadeIn>

            </div>

            {/* Right Column: Sticky Practical Box & Booking */}
            <div className="lg:col-span-5 lg:sticky lg:top-28 space-y-6">
              <FadeIn direction="left">
                <div className="bg-white border-2 border-[#817262]/30 rounded-sm p-8 md:p-10 shadow-lg space-y-8">
                  
                  {/* Practical Header */}
                  <div>
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#817262]">
                        {isEn ? "Practical Information" : "Praktisk information"}
                      </span>
                      <span className="bg-[#004f3b]/10 text-[#004f3b] text-[11px] font-bold px-2.5 py-0.5 rounded-full">
                        {isEn ? "Limited seats" : "Begrænsede pladser"}
                      </span>
                    </div>
                    <h3 className="text-2xl font-serif text-stone-900 font-medium">
                      {isEn ? "Workshop Details" : "Praktisk om arrangementet"}
                    </h3>
                  </div>

                  {/* Details List */}
                  <div className="space-y-5 border-y border-stone-200/80 py-6">
                    
                    {/* Dato & Tid */}
                    <div className="flex items-start gap-3.5">
                      <div className="w-8 h-8 rounded-full bg-[#f5efe9] flex items-center justify-center text-[#817262] shrink-0 mt-0.5">
                        <i className="fa-regular fa-calendar-clock text-sm" aria-hidden="true" />
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wider text-stone-500 font-medium">
                          {isEn ? "Date & Time" : "Tidspunkt"}
                        </div>
                        <div className="text-stone-900 font-medium text-base">
                          {isEn ? "September 2nd, 17:00–20:00" : "2. september kl. 17.00–20.00"}
                        </div>
                      </div>
                    </div>

                    {/* Sted / Lokation */}
                    <div className="flex items-start gap-3.5">
                      <div className="w-8 h-8 rounded-full bg-[#f5efe9] flex items-center justify-center text-[#817262] shrink-0 mt-0.5">
                        <i className="fa-regular fa-location-dot text-sm" aria-hidden="true" />
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wider text-stone-500 font-medium">
                          {isEn ? "Location" : "Afholdes her"}
                        </div>
                        <div className="text-stone-900 font-medium text-base">
                          Manuvision
                        </div>
                        <div className="text-stone-600 text-sm font-light">
                          Bernhard Bangs Allé 25–27<br />
                          2000 Frederiksberg C<br />
                          <span className="text-stone-500 text-xs italic">
                            ({isEn ? "a few minutes from Lindevang Metro station" : "få minutter fra Lindevang Metro station"})
                          </span>
                        </div>
                        <a 
                          href="https://maps.google.com/?q=Manuvision+Bernhard+Bangs+Allé+25+2000+Frederiksberg"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-[#004f3b] hover:underline mt-1 font-medium"
                        >
                          <i className="fa-regular fa-map-pin text-[10px]" />
                          <span>{isEn ? "Find on Google Maps" : "Find på Google Maps"}</span>
                        </a>
                      </div>
                    </div>

                    {/* Pris */}
                    <div className="flex items-start gap-3.5">
                      <div className="w-8 h-8 rounded-full bg-[#f5efe9] flex items-center justify-center text-[#817262] shrink-0 mt-0.5">
                        <i className="fa-regular fa-tag text-sm" aria-hidden="true" />
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wider text-stone-500 font-medium">
                          {isEn ? "Price" : "Pris"}
                        </div>
                        <div className="text-stone-900 font-serif text-2xl font-semibold">
                          250 kr.
                        </div>
                      </div>
                    </div>

                    {/* Medbring */}
                    <div className="flex items-start gap-3.5">
                      <div className="w-8 h-8 rounded-full bg-[#f5efe9] flex items-center justify-center text-[#817262] shrink-0 mt-0.5">
                        <i className="fa-regular fa-bottle-water text-sm" aria-hidden="true" />
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wider text-stone-500 font-medium">
                          {isEn ? "Please bring" : "Husk at medbringe"}
                        </div>
                        <div className="text-stone-700 text-sm font-light">
                          {isEn 
                            ? "Bring a water bottle and arrive 15 minutes before start. We will take care of the rest." 
                            : "Tag en drikkedunk med, og kom 15 minutter før start. Resten sørger vi for."}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tilmelding via MobilePay */}
                  <div className="bg-[#f5efe9]/70 p-6 rounded-sm border border-[#817262]/20 space-y-4">
                    <div className="flex items-center gap-2 text-stone-900 font-medium text-sm">
                      <i className="fa-solid fa-mobile-screen-button text-[#004f3b]" />
                      <span>{isEn ? "Registration & Payment" : "Tilmelding"}</span>
                    </div>
                    
                    <p className="text-stone-700 text-sm font-light">
                      {isEn 
                        ? "You are registered once you have transferred 250 DKK via MobilePay to phone " 
                        : "Du er tilmeldt, når du har overført 250 kr. til tlf. "}
                      <a href="tel:+4527507083" className="font-medium text-stone-900 underline hover:text-[#004f3b]">27 50 70 83</a>.
                    </p>

                    <div className="flex items-center justify-between bg-white p-3.5 rounded-sm border border-stone-200">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">
                          {isEn ? "MobilePay number" : "MobilePay nummer"}
                        </div>
                        <a href="tel:+4527507083" className="text-base font-mono font-bold text-stone-900 hover:text-[#004f3b] transition-colors">
                          27 50 70 83
                        </a>
                      </div>
                      <button
                        type="button"
                        onClick={handleCopyPhone}
                        className="px-3 py-1.5 text-xs bg-[#817262] text-white hover:bg-[#6c5e50] rounded-xs transition-colors flex items-center gap-1.5 font-medium cursor-pointer shadow-xs"
                      >
                        <i className={`fa-solid ${copiedPhone ? 'fa-check' : 'fa-copy'} text-xs`} />
                        <span>{copiedPhone ? (isEn ? "Copied!" : "Kopieret!") : (isEn ? "Copy" : "Kopier")}</span>
                      </button>
                    </div>

                    <p className="text-[11px] text-stone-500 italic">
                      {isEn 
                        ? "Write your name in the payment message." 
                        : "Skriv gerne dit navn i overførslens beskedfelt."}
                    </p>
                  </div>

                  {/* Costa del Sol retreat rebate reminder */}
                  <div className="pt-2">
                    <div className="p-4 bg-[#004f3b]/5 border border-[#004f3b]/20 rounded-xs text-xs text-stone-700 space-y-1.5">
                      <div className="font-semibold text-[#004f3b] flex items-center gap-1.5">
                        <i className="fa-solid fa-sparkles text-xs" />
                        <span>{isEn ? "Full price deduction on retreat" : "Fuld modregning på retreat"}</span>
                      </div>
                      <p>
                        {isEn 
                          ? "The 250 DKK is deducted from the retreat price if you join us on Costa del Sol in October." 
                          : "De 250 kr. fratrækkes retreatprisen, hvis du efterfølgende tilmelder dig retreatet på Costa del Sol."}
                      </p>
                    </div>
                  </div>

                </div>
              </FadeIn>
            </div>

          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <ImageLightboxModal 
        isOpen={activeImageIndex !== null}
        onClose={() => setActiveImageIndex(null)}
        images={lightboxImages || louiseImages}
        currentIndex={activeImageIndex ?? 0}
        onNavigate={setActiveImageIndex}
      />
    </div>
  );
};

const ThailandRetreatPage = () => {
  const [status, setStatus] = useState('');
  const [showLouiseBio, setShowLouiseBio] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(null);
  const { lang } = useLanguage();
  const isEn = lang === 'en';
  const { retreatThailand } = useCms();

  const heroBadge = retreatThailand?.hero_badge?.[lang] || retreatThailand?.hero_badge?.da || (isEn ? "Spiritual Retreat" : "Spirituelt Retreat");
  const heroTitle = retreatThailand?.hero_titel?.[lang] || retreatThailand?.hero_titel?.da || (isEn ? "Home to Body and Soul – Spiritual Retreat in Thailand" : "Hjem til krop og sjæl – spirituelt retreat i Thailand");
  const heroSubtitle = retreatThailand?.hero_undertitel?.[lang] || retreatThailand?.hero_undertitel?.da || (isEn ? "10 days in late January / early February – exact dates coming soon" : "10 dage i slutningen af januar/starten af februar – præcise datoer kommer snart");
  const heroImage = retreatThailand?.hero_billede?.[0] || "https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/thailand-retreat-hero.jpg";
  const omTekstHtml = retreatThailand?.om_tekst?.[lang] || retreatThailand?.om_tekst?.da;
  const detFaarDuTitel = retreatThailand?.det_faar_du_titel?.[lang] || retreatThailand?.det_faar_du_titel?.da || (isEn ? "What you get on the retreat" : "Du får blandt andet");
  const detFaarDuPunkterHtml = retreatThailand?.det_faar_du_punkter?.[lang] || retreatThailand?.det_faar_du_punkter?.da;
  const prisHtml = retreatThailand?.pris_sektion?.[lang] || retreatThailand?.pris_sektion?.da;
  const erfaringerTitel = retreatThailand?.erfaringer_titel?.[lang] || retreatThailand?.erfaringer_titel?.da || (isEn ? "Created from our own life experiences" : "Skabt ud fra vores egne erfaringer");
  const erfaringerTekstHtml = retreatThailand?.erfaringer_tekst?.[lang] || retreatThailand?.erfaringer_tekst?.da;
  const dageDesignetTitel = retreatThailand?.dage_designet_titel?.[lang] || retreatThailand?.dage_designet_titel?.da || (isEn ? "Days designed for deep peace & reconnection" : "Dage designet til at lande i kroppen");
  const dageDesignetTekstHtml = retreatThailand?.dage_designet_tekst?.[lang] || retreatThailand?.dage_designet_tekst?.da;
  const dageDesignetBillede = retreatThailand?.dage_designet_billede?.[0] || "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/thailand-yoga-nature.jpg";
  const udflugtTekstHtml = retreatThailand?.udflugt_tekst?.[lang] || retreatThailand?.udflugt_tekst?.da;
  const blivLaengereTekstHtml = retreatThailand?.bliv_laengere_tekst?.[lang] || retreatThailand?.bliv_laengere_tekst?.da;
  const louiseTekstHtml = retreatThailand?.louise_tekst?.[lang] || retreatThailand?.louise_tekst?.da;
  const louiseFuldBioHtml = retreatThailand?.louise_fuld_bio?.[lang] || retreatThailand?.louise_fuld_bio?.da;
  const metteTekstHtml = retreatThailand?.mette_tekst?.[lang] || retreatThailand?.mette_tekst?.da;
  const programTekstHtml = retreatThailand?.program_tekst?.[lang] || retreatThailand?.program_tekst?.da;
  const formTekst = retreatThailand?.form_tekst?.[lang] || retreatThailand?.form_tekst?.da || (isEn ? "Write your email to receive first notice when dates, location, and price are ready for the Thailand retreat." : "Skriv din henvendelse eller tilmeld dig interesselisten for at få besked først, når dato, sted og pris er klar til Thailand retreatet.");

  const louiseImages = [
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/img-6996.jpg",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/img-6996.jpg",
      alt: isEn ? "Louise - Yoga & TRE" : "Louise - Yoga og TRE"
    },
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/img-6997.jpg",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/img-6997.jpg",
      alt: isEn ? "Louise - Sound Healing" : "Louise - Lydhealing"
    },
    {
      src: "https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/img-6998.jpg",
      largeSrc: "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/img-6998.jpg",
      alt: isEn ? "Louise - Teacher" : "Louise - Underviser"
    }
  ];

  useEffect(() => {
    document.title = isEn 
      ? "Mette Brudvig - Spiritual Retreat in Thailand" 
      : "Mette Brudvig - Spirituelt retreat i Thailand";
    window.scrollTo(0, 0);
  }, [isEn]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#faf9f6] text-stone-800">
      {/* Light & Airy Hero Section */}
      <section className="pt-[185px] md:pt-[215px] lg:pt-[225px] pb-16 md:pb-24 px-6 bg-[#f5efe9]">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <FadeIn>
            <span className="text-xs uppercase tracking-[0.4em] font-bold text-[#004f3b] mb-4 block">
              {heroBadge}
            </span>
            <h1 className="text-4xl md:text-6xl font-serif text-stone-900 leading-tight">
              {heroTitle}
            </h1>
            <p className="text-lg md:text-xl font-serif italic text-[#004f3b] max-w-2xl mx-auto mt-4 leading-relaxed">
              {heroSubtitle}
            </p>
            <div className="h-0.5 w-16 bg-[#004f3b] mx-auto my-6"></div>
          </FadeIn>

          {/* Hero Image */}
          <FadeIn>
            <div className="mt-8 rounded-2xl overflow-hidden shadow-xl border border-stone-200/60 bg-white">
              <img 
                src={heroImage} 
                alt={isEn ? "Spiritual retreat in Thailand" : "Spirituelt retreat i Thailand"} 
                className="w-full h-auto object-cover max-h-[500px] block"
              />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 px-6 bg-[#faf9f6]">
        <div className="max-w-4xl mx-auto space-y-20">
          
          {/* Section: Intro */}
          <div className="space-y-8">
            <div className="space-y-5 text-stone-700 font-light text-base md:text-lg leading-relaxed">
              <FadeIn className="space-y-5">
                {omTekstHtml ? (
                  <div 
                    className="space-y-5 [&>p:first-child]:text-xl [&>p:first-child]:md:text-2xl [&>p:first-child]:font-serif [&>p:first-child]:text-[#004f3b] [&>p:first-child]:leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: omTekstHtml }}
                  />
                ) : (
                  <>
                    <p className="text-xl md:text-2xl font-serif text-[#004f3b] leading-relaxed">
                      {isEn
                        ? "Step out of daily life into Thailand's warm, lush, and sensual world."
                        : "Træd ud af hverdagen og ind i Thailands varme, frodige og sanselige verden."}
                    </p>
                    <p>
                      {isEn
                        ? "The retreat takes place in a scenic location surrounded by tropical nature, where there is room for peace, immersion, and long days in the warmth. We are still finding the exact right location – perhaps on beautiful Koh Samui – but common to the location we choose is that nature, tranquility, and beautiful surroundings create an experience of pampering, peace, and inner joy."
                        : "Retreatet kommer til at foregå et naturskønt sted omgivet af tropisk natur, hvor der er plads til både ro, fordybelse og lange dage i varmen. Vi er stadig ved at finde den helt rigtige location – måske bliver det på smukke Koh Samui – men fælles for det sted, vi vælger, er, at naturen, roen og de smukke omgivelser skal være med til at skabe oplevelsen af forkælelse, fred og indre glæde."}
                    </p>
                    <p>
                      {isEn
                        ? "Here you can wake up to the sounds of nature, start the day with gentle yoga, and slowly let your pace drop. You get time to swim, rest, read, get massages, enjoy Thai food, and simply be."
                        : "Her kan du vågne til lydene fra naturen, begynde dagen med blid yoga og langsomt lade tempoet falde. Du får tid til at bade, hvile, læse, få massage, nyde den thailandske mad og bare være."}
                    </p>
                  </>
                )}
              </FadeIn>
            </div>
          </div>

          {/* Section: Du får blandt andet */}
          <div className="border-t border-stone-200 pt-16">
            <div className="bg-white p-8 md:p-10 border border-stone-200/80 rounded-sm shadow-sm space-y-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-serif text-stone-900">
                  {detFaarDuTitel}
                </h2>
                <div className="h-0.5 w-12 bg-[#004f3b] mt-3 mb-6"></div>
              </div>

              {detFaarDuPunkterHtml ? (
                <div 
                  className="space-y-4 text-stone-700 font-light text-base md:text-lg [&>ul]:grid [&>ul]:md:grid-cols-2 [&>ul]:gap-4 [&>ul>li]:flex [&>ul>li]:items-start [&>ul>li]:before:content-['•'] [&>ul>li]:before:text-[#004f3b] [&>ul>li]:before:mr-3 [&>ul>li]:before:font-serif [&>ul>li]:before:font-bold"
                  dangerouslySetInnerHTML={{ __html: detFaarDuPunkterHtml }}
                />
              ) : (
                <ul className="grid md:grid-cols-2 gap-4 text-stone-700 font-light text-base md:text-lg">
                  {(isEn ? [
                    "Gentle yoga with a focus on peace, presence, and grounding",
                    "Guided meditations and inner reflection",
                    "Gentle TRE focused on creating calm in the body",
                    "Greater awareness of bodily signals and your own needs",
                    "Sound healing and cacao ceremony",
                    "Spiritual meditations and soulful immersion",
                    "Exciting, healthy, and nourishing Thai food",
                    "Time for silence, nature, and yourself",
                    "Option to purchase massage and additional wellness",
                    "An included full-day excursion to a magnificent nature experience",
                    "Concrete tools and a meditation to bring home"
                  ] : [
                    "Blid yoga med fokus på ro, nærvær og jordforbindelse",
                    "Guidede meditationer og indre fordybelse",
                    "Nænsom TRE med fokus på at skabe mere ro i kroppen",
                    "Større bevidsthed om kroppens signaler og dine egne behov",
                    "Lydhealing og cacaoceremoni",
                    "Spirituelle meditationer og sjælelig fordybelse",
                    "Spændende, sund og nærende thailandsk mad",
                    "Tid til stilhed, natur og dig selv",
                    "Mulighed for at tilkøbe massage og anden velvære",
                    "En inkluderet heldagsudflugt til en storslået naturoplevelse",
                    "Konkrete værktøjer og en meditation, du kan tage med hjem"
                  ]).map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-[#004f3b] mr-3 font-serif font-bold">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Section: Pris */}
          <div className="border-t border-stone-200 pt-16 w-full">
            <FadeIn>
              <div className="bg-[#ffffff] border border-stone-200 p-8 md:p-12 rounded-sm shadow-sm text-center space-y-6">
                <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
                  {isEn ? "Price" : "Pris"}
                </h2>
                <div className="h-0.5 w-12 bg-[#004f3b] mx-auto my-4"></div>
                {prisHtml ? (
                  <div 
                    className="space-y-4 text-stone-700 font-light text-base md:text-lg leading-relaxed [&>p:first-child]:text-2xl [&>p:first-child]:font-serif [&>p:first-child]:italic [&>p:first-child]:text-[#004f3b] [&>p:first-child]:font-medium"
                    dangerouslySetInnerHTML={{ __html: prisHtml }}
                  />
                ) : (
                  <>
                    <p className="text-2xl font-serif italic text-[#004f3b] font-medium">
                      {isEn ? "Coming soon." : "Kommer snart."}
                    </p>
                    <p className="text-stone-700 font-light leading-relaxed text-base md:text-lg">
                      {isEn
                        ? "We are currently finding the exact right venue and assembling the retreat, and soon we can announce both the final location, dates, and price."
                        : "Vi er i øjbelikket ved at finde det helt rigtige sted og sammensætte retreatet, og snart kan vi offentliggøre både den endelige location, datoerne og prisen."}
                    </p>
                    <p className="text-stone-600 font-light text-sm md:text-base">
                      {isEn
                        ? "Flights and travel insurance are not included. There will be an option to extend the trip before or after the retreat and experience more of Thailand."
                        : "Fly og rejseforsikring er ikke inkluderet. Der vil være mulighed for at forlænge rejsen før eller efter retreatet og opleve mere af Thailand."}
                    </p>
                    <div className="pt-2">
                      <p className="text-base text-[#004f3b] font-medium">
                        {isEn
                          ? "Send an email to mette@mettebrudvig.dk or call 27 50 70 83 to be among the first to receive notice when dates, location, and price are ready:"
                          : "Send en mail til mette@mettebrudvig.dk eller ring på 27 50 70 83, så du er blandt de første, der får besked, når dato, sted og pris er på plads."}
                      </p>
                    </div>
                  </>
                )}
                <div className="pt-3">
                  <a 
                    href="tel:+4527507083" 
                    className="inline-flex items-center gap-2 bg-[#f5efe9] text-stone-800 border border-stone-300 px-6 py-3.5 rounded-sm text-xs uppercase tracking-[0.15em] font-bold hover:bg-stone-900 hover:text-white hover:border-stone-900 transition-colors shadow-2xs"
                  >
                    <i className="fa-solid fa-phone text-[#004f3b] text-xs" aria-hidden="true" />
                    <span>{isEn ? "Call to hear more: +45 27 50 70 83" : "Ring, og hør mere: 27 50 70 83"}</span>
                  </a>
                </div>
              </div>
            </FadeIn>
          </div>

          {/* Section: Trygt rum & Vores baggrund */}
          <div className="border-t border-stone-200 pt-16 space-y-8">
            <FadeIn>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
                {erfaringerTitel}
              </h2>
              <div className="h-0.5 w-12 bg-[#004f3b] mt-4 mb-6"></div>
            </FadeIn>

            <div className="space-y-5 text-stone-700 font-light text-base md:text-lg leading-relaxed">
              <FadeIn className="space-y-5">
                {erfaringerTekstHtml ? (
                  <div 
                    className="space-y-5"
                    dangerouslySetInnerHTML={{ __html: erfaringerTekstHtml }}
                  />
                ) : (
                  <>
                    <p>
                      {isEn
                        ? "The retreat is held by me, Mette, and my lovely, talented friend Louise participates and contributes with wonderful gentle yoga, sound healing, and her vast knowledge of the body."
                        : "Retreatet holdes af mig, Mette, og min dejlige dygtige veninde Louise deltager og bidrager med skøn blid yoga, lydhealing og sin store viden om kroppen."}
                    </p>
                    <p>
                      {isEn
                        ? "The retreat is created from our own experiences with life's challenges. Louise has been through cancer and fought anxiety, and Mette has suffered from stress more than once. We have both had to create a healthier, more peaceful life to thrive and feel whole."
                        : "Retreatet er skabt ud fra vores egne erfaringer med livets udfordringer. Louise har været igennem et kræftforløb og kæmpet med angst, og Mette har været stressramt mere end én gang. Vi har begge været nødt til at skabe et sundere og mere fredfyldt liv for at trives og føle os hele."}
                    </p>
                    <p>
                      {isEn
                        ? "We have both educated ourselves and undergone great personal transformation, so we can also help other people find more peace, joy of life, and connection to themselves."
                        : "Vi har begge uddannet os og gennemgået stor personlig transformation, så vi også kan hjælpe andre mennesker med at finde mere fred, livsglæde og forbindelse til sig selv."}
                    </p>
                    <p>
                      {isEn
                        ? "We have felt in our own bodies what it means to need to pause, slow down, and find our way back to ourselves. Therefore, we wish to create a safe space for reflection and peace, where there is room for you exactly as you are."
                        : "Vi har mærket på egen krop, hvad det vil sige at have brug for at stoppe op, komme ned i gear og finde tilbage til sig selv. Derfor ønsker vi at skabe et trygt rum til fordybelse og fred, hvor der er plads til dig, præcis som du er."}
                    </p>
                  </>
                )}
              </FadeIn>
            </div>
          </div>

          {/* Section: Designet af dagene */}
          <div className="border-t border-stone-200 pt-16 space-y-8">
            <FadeIn>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
                {dageDesignetTitel}
              </h2>
              <div className="h-0.5 w-12 bg-[#004f3b] mt-4 mb-6"></div>
            </FadeIn>

            <div className="grid md:grid-cols-12 gap-8 items-stretch">
              <div className="md:col-span-7 space-y-5 text-stone-700 font-light text-base md:text-lg leading-relaxed">
                <FadeIn className="space-y-5">
                  {dageDesignetTekstHtml ? (
                    <div 
                      className="space-y-5"
                      dangerouslySetInnerHTML={{ __html: dageDesignetTekstHtml }}
                    />
                  ) : (
                    <>
                      <p>
                        {isEn
                          ? "We have designed the days so your body really gets permission to settle, and you come closer to yourself."
                          : "Vi har designet dagene, så din krop virkelig får lov til at falde til ro, og du kommer tættere på dig selv."}
                      </p>
                      <p>
                        {isEn
                          ? "Through gentle yoga exercises, breathwork, TRE, healing meditations, and time to simply be, you strengthen the connection to your body and your soul."
                          : "Gennem blide yogaøvelser, åndedræt, TRE, healende meditationer og tid til bare at være styrker du forbindelsen til din krop og din sjæl."}
                      </p>
                      <p>
                        {isEn
                          ? "When the pace drops, new insights, feelings, and a deeper understanding of yourself and the life you wish to live can arise."
                          : "Når tempoet falder, kan der opstå nye indsigter, følelser og en dybere forståelse af dig selv og det liv, du ønsker at leve."}
                      </p>
                      <p>
                        {isEn
                          ? "At the same time, you get concrete tools to bring home that can help you regulate your nervous system and continue strengthening your connection to yourself and your spiritual development in everyday life."
                          : "Du får samtidig konkrete værktøjer med hjem, som kan hjælpe dig med at regulere dit nervesystem og fortsat styrke forbindelsen til dig selv og din sjælelige udvikling i hverdagen."}
                      </p>
                      <p>
                        {isEn
                          ? "We also invite you to cacao ceremonies, sound healing, and guided spiritual meditations, where you can meet your guides and have the opportunity to travel back to a past life."
                          : "Vi inviterer også til cacaoceremoni, lydhealing og guidede spirituelle meditationer, hvor du blandt andet kan møde dine guider og få mulighed for at rejse tilbage til et tidligere liv."}
                      </p>
                      <p>
                        {isEn
                          ? "The pace is low, and there is plenty of room in the schedule to simply be."
                          : "Tempoet er lavt, og der er god plads i programmet til bare at være."}
                      </p>
                      <p>
                        {isEn
                          ? "Thailand is known for its strong tradition of stress-relieving massage and body treatments, and there will be time and opportunity to purchase massage and other wellness at local prices."
                          : "Thailand er kendt for sin stærke tradition for stresslindrende massage og kropsbehandlinger, og der vil være tid og mulighed for at tilkøbe massage og anden velvære til lokale priser."}
                      </p>
                      <p>
                        {isEn
                          ? "You decide yourself whether your free time should be spent on a massage, a book in the shade, a swim, a walk in nature, or simply being."
                          : "Du bestemmer selv, om din fritid skal bruges på en massage, en bog i skyggen, en svømmetur, en gåtur i naturen eller bare at være."}
                      </p>
                      <p>
                        {isEn
                          ? "You get exciting, nourishing, and delicious Thai food with lots of fresh ingredients, vegetables, herbs, and the flavors Thailand is famous for."
                          : "Du får spændende, nærende og velsmagende thailandsk mad med masser af friske råvarer, grøntsager, krydderurter og de smage, Thailand er kendt for."}
                      </p>
                    </>
                  )}
                </FadeIn>
              </div>

              <div className="md:col-span-5 flex flex-col justify-start">
                <FadeIn className="sticky top-28">
                  <img 
                    src={dageDesignetBillede} 
                    alt={isEn ? "Yoga in Thailand" : "Yoga og ro i Thailand"} 
                    className="w-full h-auto rounded-sm shadow-md border border-stone-200 object-cover"
                  />
                </FadeIn>
              </div>
            </div>
          </div>

          {/* Section: Udflugt */}
          <div className="bg-white border border-stone-200/80 rounded-sm p-8 md:p-10 space-y-4 shadow-sm">
            <FadeIn>
              {udflugtTekstHtml ? (
                <div 
                  className="space-y-4 text-stone-700 font-light text-base md:text-lg leading-relaxed [&>p:first-child>strong]:text-2xl [&>p:first-child>strong]:md:text-3xl [&>p:first-child>strong]:font-serif [&>p:first-child>strong]:text-stone-900 [&>p:first-child>strong]:font-normal [&>p:first-child>strong]:block [&>p:first-child>strong]:mb-3"
                  dangerouslySetInnerHTML={{ __html: udflugtTekstHtml }}
                />
              ) : (
                <>
                  <h3 className="text-2xl md:text-3xl font-serif text-stone-900">
                    {isEn ? "A day in magnificent Thai nature" : "En dag ud i den storslåede thailandske natur"}
                  </h3>
                  <div className="h-0.5 w-12 bg-[#004f3b] my-3"></div>
                  <p className="text-stone-700 font-light text-base md:text-lg leading-relaxed">
                    {isEn
                      ? "As part of the retreat, we take a full-day excursion together to one of the area's very special nature experiences."
                      : "Som en del af retreatet tager vi sammen på en heldagsudflugt til en af områdets helt særlige naturoplevelser."}
                  </p>
                  <p className="text-stone-700 font-light text-base md:text-lg leading-relaxed">
                    {isEn
                      ? "The exact location depends on where the retreat will be situated, but we choose an experience where we get close to Thailand's magnificent nature."
                      : "Det præcise sted afhænger af, hvor retreatet kommer til at ligge, men vi vælger en oplevelse, hvor vi kommer helt tæt på den storslåede thailandske natur."}
                  </p>
                  <p className="text-stone-700 font-light text-base md:text-lg leading-relaxed">
                    {isEn
                      ? "The excursion is included in the retreat and is also a day filled with shared experiences, community, and time to enjoy Thailand outside the retreat venue."
                      : "Udflugten er inkluderet i retreatet og bliver samtidig en dag med oplevelser, fællesskab og plads til at nyde Thailand uden for retreatstedet."}
                  </p>
                </>
              )}
            </FadeIn>
          </div>

          {/* Section: Extend Stay / Bliv længere */}
          <div className="border-t border-stone-200 pt-16 space-y-6">
            <FadeIn>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
                {isEn ? "Extend your stay and explore Thailand" : "Bliv længere og oplev Thailand"}
              </h2>
              <div className="h-0.5 w-12 bg-[#004f3b] mt-4 mb-6"></div>
              {blivLaengereTekstHtml ? (
                <div 
                  className="space-y-4 text-stone-700 font-light text-base md:text-lg leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: blivLaengereTekstHtml }}
                />
              ) : (
                <div className="space-y-4 text-stone-700 font-light text-base md:text-lg leading-relaxed">
                  <p>
                    {isEn
                      ? "When you have already traveled to Thailand, you can choose to extend your stay before or after the retreat."
                      : "Når du alligevel er rejst til Thailand, kan du vælge at forlænge opholdet før eller efter retreatet."}
                  </p>
                  <p>
                    {isEn
                      ? "It gives you the opportunity to experience more of the country, go on excursions, visit islands, beaches, temples, and markets, or simply stay a few extra days and enjoy the warmth and nature."
                      : "Det giver mulighed for at opleve mere af landet, tage på udflugter, besøge øer, strande, templer og markeder eller bare blive nogle ekstra dage og nyde varmen og naturen."}
                  </p>
                  <p>
                    {isEn
                      ? "That way, the retreat can both be an immersive journey inward and the beginning or end of a larger journey in Thailand. It can also simply be a transformative gift to yourself, traveling down at the start and returning home as a more peaceful, clear, and soulfully connected version of yourself."
                      : "På den måde kan retreatet både være en fordybende rejse indad og begyndelsen eller afslutningen på en større rejse i Thailand. Det kan også bare være en transformerende gave til dig selv, hvor du rejser ned ved start og hjem som en mere fredfyldt, afklaret og sjæleligt forbundet udgave af dig selv ved opholdets afslutning."}
                  </p>
                </div>
              )}
            </FadeIn>
          </div>

          {/* Section: Dine værter og undervisere */}
          <div className="border-t border-stone-200 pt-16 space-y-10">
            <FadeIn>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
                {isEn ? "Your hosts and teachers" : "Dine værter og undervisere"}
              </h2>
              <div className="h-0.5 w-12 bg-[#004f3b] mt-4 mb-6"></div>
            </FadeIn>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Host: Louise */}
              <FadeIn className="bg-white p-8 border border-stone-200 rounded-sm shadow-sm space-y-4">
                <div className="flex items-center space-x-4 mb-2">
                  <div className="w-14 h-14 rounded-full bg-[#f5efe9] flex items-center justify-center text-[#004f3b] font-serif text-xl border border-stone-200 font-bold overflow-hidden shrink-0">
                    <img 
                      src="https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/img-6996.jpg" 
                      alt="Louise" 
                      className="w-full h-full object-cover" 
                    />
                  </div>
                  <div>
                    <h3 className="font-serif text-2xl text-stone-900 font-medium">Louise</h3>
                    <p className="text-xs uppercase tracking-widest text-[#004f3b] font-bold">
                      {isEn ? "Yoga, TRE & Sound Healing" : "Yoga, TRE & Lydhealing"}
                    </p>
                  </div>
                </div>

                {/* 3 Billeder af Louise */}
                <div className="grid grid-cols-3 gap-2 my-3 rounded-xs overflow-hidden">
                  {louiseImages.map((img, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setActiveImageIndex(idx)}
                      className="relative group overflow-hidden rounded-xs cursor-pointer block text-left focus:outline-none focus:ring-2 focus:ring-[#004f3b] aspect-[3/4] sm:aspect-auto sm:h-32 md:h-40 bg-stone-100"
                      title={isEn ? "Click to enlarge" : "Klik for at forstørre"}
                      aria-label={img.alt}
                    >
                      <img 
                        src={img.src} 
                        alt={img.alt} 
                        className="w-full h-full object-cover rounded-xs transition-transform duration-300 group-hover:scale-105" 
                      />
                      <div className="absolute inset-0 bg-stone-900/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                        <span className="w-8 h-8 rounded-full bg-white/95 text-[#004f3b] flex items-center justify-center text-xs shadow-md transform scale-90 group-hover:scale-100 transition-transform">
                          <i className="fa-solid fa-magnifying-glass-plus" />
                        </span>
                      </div>
                    </button>
                  ))}
                </div>

                {louiseTekstHtml ? (
                  <div 
                    className="text-stone-700 font-light text-base leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: louiseTekstHtml }}
                  />
                ) : (
                  <p className="text-stone-700 font-light text-base leading-relaxed">
                    {isEn
                      ? "Louise is a trained educator. She was diagnosed with chronic cancer and dived deep into nutrition, body, and energy to heal herself. She has completed several yoga teacher trainings (e.g. in the East) and is trained in TRE (nervous system regulation) and sound healing. Louise has been cancer-free for 16 years."
                      : "Louise er uddannet pædagog. Hun blev diagnosticeret med kronisk kræft og er dykket dybt ned i kost, krop og energi for at helbrede sig selv. Hun har taget flere yogauddannelser, bl.a. i Østen, og hun er uddannet i TRE (regulering af nervesystemet) og lydhealing. Louise har været kræftfri i 16 år."}
                  </p>
                )}

                <button 
                  type="button" 
                  onClick={() => setShowLouiseBio(!showLouiseBio)}
                  className="inline-flex items-center gap-2 text-[#004f3b] hover:text-emerald-950 font-medium text-sm pt-2 transition-colors cursor-pointer"
                >
                  <span>
                    {showLouiseBio 
                      ? (isEn ? "Hide full background" : "Skjul Louises fulde baggrund") 
                      : (isEn ? "Read more about Louise" : "Læs mere om Louise her")}
                  </span>
                  <i className={`fa-solid fa-chevron-${showLouiseBio ? 'up' : 'down'} text-xs`} />
                </button>

                {showLouiseBio && (
                  <div className="pt-4 border-t border-stone-100 space-y-4 text-stone-600 font-light text-sm leading-relaxed animate-fadeIn">
                    {louiseFuldBioHtml ? (
                      <div 
                        className="space-y-4"
                        dangerouslySetInnerHTML={{ __html: louiseFuldBioHtml }}
                      />
                    ) : (
                      <>
                        <p>
                          Louises rejse ind i arbejdet med krop og bevidsthed begyndte for 18 år siden, da hun fik konstateret kræft flere steder i kroppen. Denne periode blev et vendepunkt, som åbnede for en dybere forståelse af kroppens egen intelligens og evne til heling. Hun har været igennem lange sygdomsforløb præget af dødsangst, paranoia, dysregulering samt både fysiske og psykiske udfordringer. Disse erfaringer udgør i dag et vigtigt fundament for hendes arbejde. Det har givet hende en dyb forståelse for vores sårbarhed og nervesystemets kompleksitet og samtidig tydeliggjort vigtigheden af at skabe trygge rammer, hvor vi kan møde os selv nænsomt og kærligt. Særligt i de øjeblikke, hvor frygt og indre hårdhed tager over.
                        </p>
                        <p>
                          Louise har en bachelor i pædagogik og har gennemført flere 200-timers yogalæreruddannelser bl.a. i Indonesien, Portugal og USA. Hun bruger somatisk bevægelse i tilgangen til kroppen. Derudover er hun uddannet TRE space holder og faciliterer klangbad. Gennem årene har hun rejst verden rundt, lært tai chi i bjergene i Kina, dyrket muay thai i Thailand og undervist på retreats og workshops både i Danmark og i udlandet.
                        </p>
                        <p>
                          Hendes faglige baggrund og internationale uddannelser danner et solidt fundament for hendes arbejde, hvor hun forener viden, erfaring og nærvær i mødet med det enkelte menneske. Hendes tilgang er både jordnær, sjov og nænsom. Hun arbejder med kroppen som indgang til forandring og møder hver proces med nærvær, respekt og blidhed. Hun inddrager energiarbejde og lydfrekvenser fra krystalsangskåle og andre instrumenter, som støtter nervesystemet og inviterer til dyb afspænding og indre ro.
                        </p>
                        <p>
                          Det, Louise er god til, er at skabe trygge rammer, hvor der er plads til at lande i kroppen, mærke sig selv og give slip i eget tempo. For hende handler heling ikke om at presse eller fikse, men om at lytte og møde det, der er, med venlighed og nænsomhed. Vi behøver ikke være bange for vores skyggesider. Det, vi kalder mørke, er ofte blot det, vi endnu ikke har set eller forstået. Når vi bringer opmærksomhed og bevidsthed til det, kan det blive en kilde til indsigt, forandring og større selvforståelse.
                        </p>
                      </>
                    )}
                  </div>
                )}
              </FadeIn>

              {/* Host: Mette */}
              <FadeIn delay={100} className="bg-white p-8 border border-stone-200 rounded-sm shadow-sm space-y-4 flex flex-col justify-between">
                <div className="space-y-4">
                  <div className="flex items-center space-x-4 mb-2">
                    <img 
                      src="https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=90,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/image_25bbfbe2.png" 
                      alt="Mette Brudvig" 
                      className="w-14 h-14 rounded-full object-cover border border-stone-200"
                    />
                    <div>
                      <h3 className="font-serif text-2xl text-stone-900 font-medium">Mette Brudvig</h3>
                      <p className="text-xs uppercase tracking-widest text-[#004f3b] font-bold">
                        {isEn ? "Spiritual Teacher, Coach & Clairvoyant" : "Spirituel Lærer, Coach & Clairvoyant"}
                      </p>
                    </div>
                  </div>

                  {/* Billede af Mette */}
                  <div className="my-3 rounded-xs overflow-hidden">
                    <img 
                      src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/image_25bbfbe2.png" 
                      alt="Mette Brudvig" 
                      className="w-full h-48 md:h-56 object-cover rounded-xs" 
                    />
                  </div>

                  {metteTekstHtml ? (
                    <div 
                      className="space-y-3 text-stone-700 font-light text-base leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: metteTekstHtml }}
                    />
                  ) : (
                    <>
                      <p className="text-stone-700 font-light text-base leading-relaxed">
                        {isEn
                          ? "Mette is a spiritual teacher, coach, and clairvoyant. She is a trained journalist and former boarding school teacher who explains both spiritual and scientific topics clearly and practically."
                          : "Mette er spirituel lærer, coach og clairvoyant. Hun er uddannet journalist og har været efterskolelærer og gør meget ud af at forklare både det åndelige og videnskabelige, så det er forståeligt og brugbart."}
                      </p>
                      <p className="text-stone-700 font-light text-base leading-relaxed">
                        {isEn
                          ? "She facilitates cacao ceremonies and deep meditations focusing on healing, guide contact, and soul journeys."
                          : "Hun faciliterer cacaoceremonier og dybe meditationer inden for blandt andet healing, kontakt til guider og sjælerejser."}
                      </p>
                    </>
                  )}
                </div>

                <div className="pt-2">
                  <Link 
                    to={isEn ? "/en/about-mette" : "/om-mette"}
                    className="inline-flex items-center gap-2 text-[#004f3b] hover:text-emerald-950 font-medium text-sm transition-colors"
                  >
                    <span>{isEn ? "Read more about Mette here" : "Læs mere om Mette her"}</span>
                    <i className="fa-solid fa-arrow-right text-xs" />
                  </Link>
                </div>
              </FadeIn>
            </div>
          </div>

          {/* Section: Program */}
          <div className="border-t border-stone-200 pt-16 space-y-8">
            <FadeIn>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
                {isEn ? "Program" : "Program"}
              </h2>
              <div className="h-0.5 w-12 bg-[#004f3b] mt-4 mb-6"></div>
              {programTekstHtml ? (
                <div 
                  className="bg-white p-8 md:p-10 border border-stone-200/80 rounded-sm shadow-sm space-y-4 text-stone-700 font-light text-base [&>ul]:grid [&>ul]:sm:grid-cols-2 [&>ul]:gap-4 [&>ul>li]:flex [&>ul>li]:items-start [&>ul>li]:space-x-3 [&>ul>li]:bg-[#f5efe9]/50 [&>ul>li]:p-3.5 [&>ul>li]:rounded-sm [&>ul>li]:border [&>ul>li]:border-stone-200/50 [&>ul>li]:before:content-['✦'] [&>ul>li]:before:text-[#004f3b] [&>ul>li]:before:text-xs [&>ul>li]:before:mt-1 [&>p]:italic [&>p]:text-stone-600"
                  dangerouslySetInnerHTML={{ __html: programTekstHtml }}
                />
              ) : (
                <>
                  <p className="text-stone-700 font-light text-base md:text-lg leading-relaxed italic">
                    {isEn
                      ? "We follow the energy and you as a group, and channeled messages about what you and the group need may arise. Therefore, you get the program as headlines rather than a fixed schedule with all details."
                      : "Vi følger energien og jer som gruppe, og der kan komme kanaliserede beskeder om, hvad du og gruppen har brug for. Derfor får du programmet i overskrifter frem for som et fast skema med alle detaljer."}
                  </p>
                  <div className="bg-white p-8 md:p-10 border border-stone-200/80 rounded-sm shadow-sm space-y-4">
                    <ul className="grid sm:grid-cols-2 gap-4 text-stone-700 font-light text-base">
                      {(isEn ? [
                        "Morning yoga",
                        "Morning swim for those who wish",
                        "Healthy and delicious Thai breakfast",
                        "Shared reflection and meditation",
                        "Peace and personal time (option for massage and wellness)",
                        "Healthy and light Thai lunch",
                        "Reflection and connection on nature hike (optional)",
                        "Spiritual meditation traveling inward for answers and insights",
                        "Healthy and delicious Thai dinner",
                        "Cozy time and conversations or personal space",
                        "Cacao ceremony",
                        "Sound healing"
                      ] : [
                        "Morgenyoga",
                        "Morgendukkert for dem, der har lyst",
                        "Sund og velsmagende thailandsk morgenmad",
                        "Fælles fordybelse og meditation",
                        "Ro og egen tid (mulighed for massage og anden velvære)",
                        "Sund og let thailandsk frokost",
                        "Fordybelse og forbindelse på vandretur i naturen (frivilligt)",
                        "Spirituel meditation, hvor du rejser indad efter svar og indsigter",
                        "Sund og velsmagende thailandsk aftensmad",
                        "Hygge og samtaler eller egentid",
                        "Cacaoceremoni",
                        "Lydhealing"
                      ]).map((item, idx) => (
                        <li key={idx} className="flex items-start space-x-3 bg-[#f5efe9]/50 p-3.5 rounded-sm border border-stone-200/50">
                          <i className="fa-solid fa-sparkles text-[#004f3b] text-xs mt-1" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="pt-4 text-stone-600 font-light text-sm italic border-t border-stone-100">
                      {isEn
                        ? "Our teaching and inspiring knowledge weave naturally into our activities along the way."
                        : "Vores undervisning og den viden, der kan inspirere dig, kommer naturally ind i det, vi laver undervejs."}
                    </p>
                  </div>
                </>
              )}
            </FadeIn>
          </div>

          {/* 4 Udtalelser fra deltagere og klienter - flyttet op inden interesseliste */}
          <div className="border-t border-stone-200/80 pt-16 pb-6">
            <FadeIn>
              <div className="text-center max-w-3xl mx-auto mb-10 space-y-2">
                <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#004f3b] block">
                  {isEn ? "Participant Testimonials" : "Udtalelser fra deltagere"}
                </span>
                <h3 className="text-2xl sm:text-3xl font-serif text-stone-900">
                  {isEn ? "What clients and participants say about Mette" : "Det siger tidligere deltagere om Mette"}
                </h3>
                <div className="h-0.5 w-12 bg-[#004f3b] mt-3 mx-auto"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  TESTIMONIALS.find(t => t.name === "Nina Bonde"),
                  TESTIMONIALS.find(t => t.name === "Signe Aarestrup"),
                  TESTIMONIALS.find(t => t.name === "Marianne Helleskov"),
                  TESTIMONIALS.find(t => t.name === "Sara Delpin")
                ].filter(Boolean).map((t, idx) => (
                  <FadeIn key={idx} delay={idx * 80} className="h-full">
                    <TestimonialQuoteCard testimonial={t} className="h-full bg-stone-50/70" />
                  </FadeIn>
                ))}
              </div>
            </FadeIn>
          </div>

          {/* Contact Form / Interest registration */}
          <div className="border-t border-stone-200 pt-16">
            <FadeIn className="bg-white p-8 md:p-12 rounded-sm border border-stone-200 shadow-md w-full space-y-6">
              <div className="text-center space-y-2">
                <h3 className="text-2xl font-serif text-stone-900">
                  {isEn ? "Interest List & Inquiry" : "Interesseliste & Spørgsmål"}
                </h3>
                <p className="text-stone-600 font-light text-sm">
                  {formTekst}
                </p>
                <p className="text-xs text-stone-500 pt-1">
                  {isEn ? "Questions? " : "Har du spørgsmål? "}
                  <a href="tel:+4527507083" className="font-semibold text-[#004f3b] hover:underline">
                    {isEn ? "Call to hear more: 27 50 70 83" : "Ring, og hør mere: 27 50 70 83"}
                  </a>
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6 pt-4">
                <input type="text" name="website_url" className="sr-only absolute" tabIndex="-1" autoComplete="off" />
                <input type="hidden" name="subject" value={isEn ? "Inquiry: Spiritual retreat in Thailand" : "Forespørgsel: Hjem til krop og sjæl - spirituelt retreat i Thailand"} />

                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase tracking-widest text-stone-500 font-medium">
                      {isEn ? "Name" : "Navn"}
                    </label>
                    <input 
                      required 
                      type="text" 
                      name="name"
                      placeholder={isEn ? "Your full name" : "Dit fulde navn"} 
                      className="w-full bg-stone-50 border border-stone-200 rounded-sm px-4 py-3 text-sm focus:border-[#004f3b] outline-none"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase tracking-widest text-stone-500 font-medium">
                      {isEn ? "Email" : "E-mail"}
                    </label>
                    <input 
                      required 
                      type="email" 
                      name="email"
                      placeholder={isEn ? "Your email address" : "Din e-mailadresse"} 
                      className="w-full bg-stone-50 border border-stone-200 rounded-sm px-4 py-3 text-sm focus:border-[#004f3b] outline-none"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase tracking-widest text-stone-500 font-medium">
                    {isEn ? "Message" : "Besked"}
                  </label>
                  <textarea 
                    required 
                    name="message"
                    rows={4} 
                    placeholder={isEn ? "Write your questions or message to Mette..." : "Skriv dine spørgsmål eller ønske om at komme på interesselisten..."} 
                    className="w-full bg-stone-50 border border-stone-200 rounded-sm px-4 py-3 text-sm focus:border-[#004f3b] outline-none"
                  />
                </div>

                <button 
                  type="submit" 
                  className="w-full bg-[#004f3b] text-white py-4 uppercase text-xs tracking-[0.3em] font-bold hover:bg-emerald-900 transition-colors rounded-sm shadow-md cursor-pointer"
                >
                  {isEn ? "Send inquiry" : "Send henvendelse"}
                </button>

                {status === 'success' && (
                  <p className="text-emerald-700 text-center font-serif italic pt-2 text-sm">
                    {isEn 
                      ? "Thank you! Your inquiry has been sent. Mette will get back to you as soon as possible."
                      : "Tak! Din henvendelse er sendt. Mette vender tilbage hurtigst muligt."}
                  </p>
                )}
                {status === 'error' && (
                  <p className="text-red-700 text-center font-serif italic pt-2 text-sm">
                    {isEn ? "An error occurred. Please try again." : "Der skete en fejl. Prøv venligst igen."}
                  </p>
                )}
              </form>
            </FadeIn>
          </div>

        </div>
      </section>

      {/* Lightbox Modal */}
      <ImageLightboxModal 
        isOpen={activeImageIndex !== null}
        onClose={() => setActiveImageIndex(null)}
        images={louiseImages}
        currentIndex={activeImageIndex ?? 0}
        onNavigate={setActiveImageIndex}
      />
    </div>
  );
};

const KontaktPage = () => {
  const [searchParams] = useSearchParams();
  const subjectParam = searchParams.get('subject');
  const { lang, t } = useLanguage();
  const isEn = lang === 'en';

  useEffect(() => {
    document.title = isEn ? "Mette Brudvig - Contact" : "Mette Brudvig - Kontakt";
  }, [isEn]);

  const [status, setStatus] = useState('');
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterStatus('loading');

    const formData = new FormData(e.target);
    const honey = formData.get('website_url');
    if (honey) {
      setNewsletterStatus('success');
      setNewsletterEmail('');
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: 'Tilmelding til nyhedsbrev',
          email: newsletterEmail,
          message: newsletterEmail,
          website_url: ''
        }),
      });
      if (response.ok) {
        setNewsletterStatus('success');
        setNewsletterEmail('');
      } else {
        setNewsletterStatus('error');
      }
    } catch {
      setNewsletterStatus('error');
    }
  };

  return (
    <div className="pt-[185px] md:pt-[215px] lg:pt-[225px] pb-24 px-6 min-h-screen bg-[#f5efe9]/30 relative overflow-hidden">
      {/* Ambient decorative background elements */}
      <div className="absolute top-1/4 -left-20 w-96 h-96 bg-[#817262]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/3 -right-20 w-[500px] h-[500px] bg-[#f5efe9]/60 rounded-full blur-3xl pointer-events-none" />
      
      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header Section */}
        <div className="text-center max-w-2xl mx-auto mb-20">
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#817262]/5 mb-4">
            <i className="fa-light fa-sparkles text-[#817262] text-lg" />
          </div>
          <span className="text-xs uppercase tracking-[0.25em] text-[#817262] font-semibold block mb-3">
            {isEn ? "Reach out" : "Ræk ud"}
          </span>
          <h1 className="text-4xl md:text-5xl font-serif text-stone-900 mb-6 leading-tight">
            {isEn ? "Contact Mette Brudvig" : "Lad os starte samtalen"}
          </h1>
          <div className="w-16 h-[1px] bg-[#817262]/30 mx-auto mb-6"></div>
          <p className="text-stone-600 font-light text-base md:text-lg leading-relaxed">
            {isEn 
              ? "If you would like a personal session or want to hear more about courses, workshops, retreats, or other inquiries, you are very welcome to write to me."
              : "Ønsker du en personlig session eller at høre mere om forløb, workshops, retreats eller andet, så er du velkommen til at skrive til mig."}
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Info & Portrait */}
          <div className="lg:col-span-5 space-y-8">
            <FadeIn direction="right" className="space-y-8">
              {/* Contact Details Cards */}
              <div className="space-y-4">
                <div className="bg-white/80 backdrop-blur-sm p-6 rounded-sm border border-stone-200/50 shadow-sm hover:shadow-md hover:border-[#817262]/20 transition-all duration-300 flex items-start gap-4 group">
                  <div className="w-10 h-10 rounded-full bg-[#f5efe9] flex items-center justify-center text-[#817262] shrink-0 group-hover:bg-[#817262] group-hover:text-white transition-all duration-300">
                    <i className="fa-light fa-phone text-base" />
                  </div>
                  <div>
                    <h3 className="text-xs uppercase tracking-wider text-stone-400 mb-1 font-semibold">
                      {isEn ? "Phone" : "Telefon"}
                    </h3>
                    <a href="tel:+4527507083" className="text-stone-900 hover:text-[#817262] transition-colors font-medium text-base">
                      +45 27 50 70 83
                    </a>
                    <p className="text-xs text-stone-500 mt-1">
                      {isEn ? "Call or text me directly – tap to call on mobile." : "Ring eller send en sms – tryk for at ringe direkte op."}
                    </p>
                  </div>
                </div>

                <div className="bg-white/80 backdrop-blur-sm p-6 rounded-sm border border-stone-200/50 shadow-sm hover:shadow-md hover:border-[#817262]/20 transition-all duration-300 flex items-start gap-4 group">
                  <div className="w-10 h-10 rounded-full bg-[#f5efe9] flex items-center justify-center text-[#817262] shrink-0 group-hover:bg-[#817262] group-hover:text-white transition-all duration-300">
                    <i className="fa-light fa-envelope text-base" />
                  </div>
                  <div>
                    <h3 className="text-xs uppercase tracking-wider text-stone-400 mb-1 font-semibold">E-mail</h3>
                    <a href="mailto:mette@mettebrudvig.dk" className="text-stone-900 hover:text-[#817262] transition-colors font-medium text-base">
                      mette@mettebrudvig.dk
                    </a>
                    <p className="text-xs text-stone-500 mt-1">
                      {isEn ? "Write directly to me – I will answer as soon as possible." : "Skriv direkte til mig – jeg svarer hurtigst muligt."}
                    </p>
                  </div>
                </div>

                <div className="bg-white/80 backdrop-blur-sm p-6 rounded-sm border border-stone-200/50 shadow-sm hover:shadow-md hover:border-[#817262]/20 transition-all duration-300 flex items-start gap-4 group">
                  <div className="w-10 h-10 rounded-full bg-[#f5efe9] flex items-center justify-center text-[#817262] shrink-0 group-hover:bg-[#817262] group-hover:text-white transition-all duration-300">
                    <i className="fa-light fa-location-dot text-base" />
                  </div>
                  <div>
                    <h3 className="text-xs uppercase tracking-wider text-stone-400 mb-1 font-semibold">
                      {isEn ? "Location" : "Lokation"}
                    </h3>
                    <p className="text-stone-900 font-medium text-base">
                      {isEn ? "Sønderborg and online" : "Sønderborg og online"}
                    </p>
                    <p className="text-xs text-stone-500 mt-1">
                      {isEn 
                        ? "Sessions take place either physically in Høruphav near Sønderborg or online in a confidential, digital space."
                        : "Sessioner afholdes enten fysisk i Høruphav ved Sønderborg eller online via et fortroligt, digitalt rum."}
                    </p>
                  </div>
                </div>

                <div className="bg-white/80 backdrop-blur-sm p-6 rounded-sm border border-stone-200/50 shadow-sm hover:shadow-md hover:border-[#817262]/20 transition-all duration-300 flex items-start gap-4 group">
                  <div className="w-10 h-10 rounded-full bg-[#f5efe9] flex items-center justify-center text-[#817262] shrink-0 group-hover:bg-[#817262] group-hover:text-white transition-all duration-300">
                    <i className="fa-light fa-clock text-base" />
                  </div>
                  <div>
                    <h3 className="text-xs uppercase tracking-wider text-stone-400 mb-1 font-semibold">
                      {isEn ? "Response time" : "Svartid"}
                    </h3>
                    <p className="text-stone-900 font-medium text-base">
                      {isEn ? "Within 24-48 hours" : "Inden for 24-48 timer"}
                    </p>
                    <p className="text-xs text-stone-500 mt-1">
                      {isEn ? "I strive to get back to you within two business days." : "Jeg bestræber mig på at vende tilbage til dig inden for to hverdage."}
                    </p>
                  </div>
                </div>

                <div className="bg-white/80 backdrop-blur-sm p-6 rounded-sm border border-stone-200/50 shadow-sm hover:shadow-md hover:border-[#817262]/20 transition-all duration-300 flex items-start gap-4 group">
                  <div className="w-10 h-10 rounded-full bg-[#f5efe9] flex items-center justify-center text-[#817262] shrink-0 group-hover:bg-[#817262] group-hover:text-white transition-all duration-300">
                    <i className="fa-light fa-calendar-xmark text-base" />
                  </div>
                  <div>
                    <h3 className="text-xs uppercase tracking-wider text-stone-400 mb-1 font-semibold">
                      {isEn ? "Cancellation policy" : "Afbudsregler"}
                    </h3>
                    <p className="text-stone-900 font-medium text-base">
                      {isEn ? "At least 24 hours prior" : "Senest 24 timer før"}
                    </p>
                    <p className="text-xs text-stone-500 mt-1">
                      {isEn 
                        ? "Cancellations must be made at least 24 hours before your appointment, otherwise full charge applies."
                        : "Afbud skal ske senest 24 timer før din aftalte tid, ellers opkræves det fulde beløb."}
                    </p>
                  </div>
                </div>
              </div>

              {/* Warm Personal Quote from Mette */}
              <div className="bg-[#817262]/5 p-8 rounded-sm border border-[#817262]/10 relative overflow-hidden">
                <i className="fa-light fa-quote-left text-4xl text-[#817262]/10 absolute top-4 left-4" />
                <p className="text-stone-700 italic font-light text-sm md:text-base leading-relaxed relative z-10 pl-4">
                  {isEn
                    ? '"It requires courage to listen to the soul\'s calling. Wherever you are on your path, I meet you with presence, openness, and a safe, confidential space for your personal and spiritual expansion."'
                    : '"Det kræver mod at lytte til sjælens kalden. Uanset hvor du er på din rejse, møder jeg dig med nærvær, åbenhed og et trygt, fortroligt rum til din personlige og spirituelle udfoldelse."'}
                </p>
                <p className="text-xs uppercase tracking-widest text-[#817262] font-semibold mt-4 text-right">— Mette Brudvig</p>
              </div>

              {/* Social Links */}
              <div className="space-y-4 pt-2">
                <div className="text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold">
                  {isEn ? "Follow along in the universe" : "Følg med i universet"}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <a 
                    href="https://www.instagram.com/mettebrudvig/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 bg-white/80 backdrop-blur-sm p-3 rounded-sm border border-stone-200/50 hover:border-[#817262]/40 hover:bg-white transition-all duration-300"
                  >
                    <i className="fa-brands fa-instagram text-[#817262] text-lg" />
                    <span className="text-xs text-stone-700 font-medium">Instagram</span>
                  </a>
                  <a 
                    href="https://www.facebook.com/mettebrudvighjertetstidsalder" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 bg-white/80 backdrop-blur-sm p-3 rounded-sm border border-stone-200/50 hover:border-[#817262]/40 hover:bg-white transition-all duration-300"
                  >
                    <i className="fa-brands fa-facebook text-[#817262] text-lg" />
                    <span className="text-xs text-stone-700 font-medium">Facebook</span>
                  </a>
                  <a 
                    href="https://www.linkedin.com/in/mette-brudvig-b6200a12/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 bg-white/80 backdrop-blur-sm p-3 rounded-sm border border-stone-200/50 hover:border-[#817262]/40 hover:bg-white transition-all duration-300"
                  >
                    <i className="fa-brands fa-linkedin text-[#817262] text-lg" />
                    <span className="text-xs text-stone-700 font-medium">LinkedIn</span>
                  </a>
                  <a 
                    href="https://open.spotify.com/show/3w5L5y0dHLNa0FfIr6PVsL?si=e41e2a3acacf4535" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 bg-white/80 backdrop-blur-sm p-3 rounded-sm border border-stone-200/50 hover:border-[#817262]/40 hover:bg-white transition-all duration-300"
                  >
                    <i className="fa-brands fa-spotify text-[#817262] text-lg" />
                    <span className="text-xs text-stone-700 font-medium">Spotify</span>
                  </a>
                  <a 
                    href="https://www.buzzsprout.com/2530331/episodes" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 bg-white/80 backdrop-blur-sm p-3 rounded-sm border border-stone-200/50 hover:border-[#817262]/40 hover:bg-white transition-all duration-300"
                  >
                    <i className="fa-solid fa-podcast text-[#817262] text-lg" />
                    <span className="text-xs text-stone-700 font-medium">Buzzsprout</span>
                  </a>
                </div>
              </div>
            </FadeIn>
          </div>

          {/* Right Column: Premium Form Card */}
          <div className="lg:col-span-7">
            <FadeIn direction="left" className="bg-white p-8 md:p-12 rounded-sm border border-stone-200/50 shadow-xl shadow-stone-200/30">
              <h2 className="text-2xl md:text-3xl font-serif text-stone-900 mb-2">
                {lang === 'en' ? "Send a Message" : "Send en besked"}
              </h2>
              <p className="text-stone-500 font-light text-sm mb-8">
                {lang === 'en'
                  ? "Fill out the form below and I will get back to you as soon as possible."
                  : "Udfyld formularen nedenfor, så vender jeg tilbage til dig så hurtigt som muligt."}
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <input type="text" name="website_url" className="sr-only absolute" tabIndex="-1" autoComplete="off" />
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
                      {lang === 'en' ? "Name" : "Navn"}
                    </label>
                    <input 
                      required 
                      type="text" 
                      name="name" 
                      placeholder={lang === 'en' ? "Your full name" : "Dit fulde navn"}
                      className="w-full bg-stone-50/30 border border-stone-200 rounded-sm px-4 py-3 text-sm focus:border-[#817262] focus:bg-white focus:ring-1 focus:ring-[#817262]/20 outline-none transition-all text-stone-900 font-light"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
                      {lang === 'en' ? "Email" : "Email"}
                    </label>
                    <input 
                      required 
                      type="email" 
                      name="email" 
                      placeholder={lang === 'en' ? "your@email.com" : "din@email.com"}
                      className="w-full bg-stone-50/30 border border-stone-200 rounded-sm px-4 py-3 text-sm focus:border-[#817262] focus:bg-white focus:ring-1 focus:ring-[#817262]/20 outline-none transition-all text-stone-900 font-light"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
                      {lang === 'en' ? "Phone (optional)" : "Telefon (valgfri)"}
                    </label>
                    <input 
                      type="tel" 
                      name="phone" 
                      placeholder={lang === 'en' ? "Your phone number" : "Dit telefonnummer"}
                      className="w-full bg-stone-50/30 border border-stone-200 rounded-sm px-4 py-3 text-sm focus:border-[#817262] focus:bg-white focus:ring-1 focus:ring-[#817262]/20 outline-none transition-all text-stone-900 font-light"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
                      {lang === 'en' ? "What is this regarding?" : "Hvad drejer det sig om?"}
                    </label>
                    <select 
                      required
                      name="subject" 
                      defaultValue={subjectParam || ""}
                      className="w-full bg-stone-50/30 border border-stone-200 rounded-sm px-4 py-3 text-sm focus:border-[#817262] focus:bg-white focus:ring-1 focus:ring-[#817262]/20 outline-none transition-all text-stone-800 font-light cursor-pointer"
                    >
                      <option value="" disabled>{lang === 'en' ? "Select topic..." : "Vælg emne..."}</option>
                      <option value="1:1 Sessioner">{lang === 'en' ? "1:1 Sessions" : "1:1 Sessioner"}</option>
                      <option value="Retreats">{lang === 'en' ? "Retreats (Denmark / Spain)" : "Retreats (Danmark / Spanien)"}</option>
                      <option value="Foredrag & Workshops">{lang === 'en' ? "Keynotes & Workshops" : "Foredrag & Workshops"}</option>
                      <option value="Frisæt dig">{lang === 'en' ? "Unleash Yourself" : "Frisæt dig"}</option>
                      <option value="KVINDE - Kend din power!">{lang === 'en' ? "WOMAN - Know Your Power!" : "KVINDE - Kend din power!"}</option>
                      <option value="Andet">{lang === 'en' ? "Other" : "Andet"}</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
                    {lang === 'en' ? "Message" : "Besked"}
                  </label>
                  <textarea 
                    required 
                    name="message" 
                    rows="6" 
                    defaultValue={subjectParam ? (lang === 'en' ? `Hello Mette,\n\nI would like to learn more about ${subjectParam}.` : `Hej Mette,\n\nJeg vil gerne høre mere om ${subjectParam}.`) : ""}
                    placeholder={lang === 'en' ? "Write your message here..." : "Skriv din besked her..."}
                    className="w-full bg-stone-50/30 border border-stone-200 rounded-sm px-4 py-3 text-sm focus:border-[#817262] focus:bg-white focus:ring-1 focus:ring-[#817262]/20 outline-none transition-all text-stone-900 font-light resize-none"
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  className="w-full bg-[#817262] text-white py-4 uppercase text-xs tracking-[0.25em] font-bold hover:bg-[#6e6153] transition-all duration-300 rounded-sm shadow-md shadow-stone-100 cursor-pointer flex items-center justify-center gap-2"
                >
                  <span>{lang === 'en' ? "Send Message" : "Send besked"}</span>
                  <i className="fa-light fa-arrow-right-long text-xs" />
                </button>

                {status === 'success' && (
                  <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-sm text-center text-sm font-serif italic flex items-center justify-center gap-2">
                    <i className="fa-solid fa-circle-check text-emerald-600" />
                    {lang === 'en'
                      ? "Thank you! Your message has been sent. I will get back to you soon."
                      : "Tak! Din besked er sendt. Jeg vender tilbage hurtigst muligt."}
                  </div>
                )}
                {status === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 text-red-800 rounded-sm text-center text-sm font-serif italic flex items-center justify-center gap-2">
                    <i className="fa-solid fa-circle-exclamation text-red-600" />
                    {lang === 'en'
                      ? "An error occurred while sending. Please try again."
                      : "Der skete en fejl under afsendelsen. Prøv venligst igen."}
                  </div>
                )}
              </form>
            </FadeIn>
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="mt-32 border-t border-stone-200/60 pt-24">
          <FadeIn direction="up" className="bg-white p-8 md:p-16 rounded-sm border border-stone-200/50 shadow-xl shadow-stone-200/20 max-w-4xl mx-auto text-center space-y-8 relative overflow-hidden">
            {/* Soft background glow for newsletter */}
            <div className="absolute -top-24 -left-24 w-48 h-48 bg-[#f5efe9] rounded-full blur-2xl pointer-events-none" />
            <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-[#817262]/5 rounded-full blur-2xl pointer-events-none" />
            
            <div className="w-12 h-12 rounded-full bg-[#f5efe9] flex items-center justify-center text-[#817262] mx-auto relative z-10">
              <i className="fa-light fa-paper-plane text-lg" />
            </div>
            <div className="space-y-3 relative z-10">
              <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
                {lang === 'en' ? "Ready to unleash yourself? / Ready to create the life of your dreams?" : "Er du klar til at sætte dig selv fri? / Er du klar til at skabe dit drømmeliv?"}
              </h2>
              <p className="text-stone-600 font-light text-sm md:text-base max-w-xl mx-auto leading-relaxed">
                {lang === 'en'
                  ? "Subscribe to the newsletter and receive tools, inspiration, insights, and free meditations directly in your inbox."
                  : "Tilmeld dig nyhedsbrevet og få værktøjer, inspiration, viden og gratis meditationer direkte i din indbakke."}
              </p>
            </div>

            <form 
              id="signup" 
              action="https://e.lumant.dk/api/v1/optin/list_6v-RTZGckC/subscribe" 
              method="post" 
              className="max-w-xl mx-auto space-y-3 relative z-10"
            >
              <input type="hidden" name="ref" value="" />
              <div style={{ position: 'absolute', left: '-5000px' }} aria-hidden="true">
                <input type="text" name="hp_reference" tabIndex={-1} autoComplete="off" />
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <input 
                  type="text" 
                  name="name" 
                  placeholder="Dit navn" 
                  className="flex-1 bg-stone-50/50 border border-stone-200 rounded-sm px-4 py-3 text-sm focus:border-[#817262] focus:bg-white focus:ring-1 focus:ring-[#817262]/20 outline-none transition-all text-stone-900 font-light"
                />
                <input 
                  type="email" 
                  name="email" 
                  placeholder="Din e-mail" 
                  pattern="[^@\s]+@[^@\s]+\.[^@\s]+" 
                  required 
                  className="flex-1 bg-stone-50/50 border border-stone-200 rounded-sm px-4 py-3 text-sm focus:border-[#817262] focus:bg-white focus:ring-1 focus:ring-[#817262]/20 outline-none transition-all text-stone-900 font-light"
                />
                <input 
                  type="submit" 
                  name="submit" 
                  value="TILMELD MIG NU" 
                  className="bg-[#817262] hover:bg-[#6e6153] text-white px-8 py-3 text-xs uppercase tracking-widest font-bold rounded-sm transition-all duration-300 shrink-0 cursor-pointer"
                />
              </div>
            </form>
          </FadeIn>
        </div>
      </div>
    </div>
  );
};

const GratisMiniworkshopPage = () => {
  const { lang } = useLanguage();
  const isEn = lang === 'en';
  const frisaetPath = isEn ? '/en/unleash-yourself' : '/frisaet-dig';
  const { gratisWorkshop } = useCms();

  useEffect(() => {
    document.title = isEn 
      ? "Frisæt dig – Online Mini-Course | Mette Brudvig" 
      : "Frisæt dig – Online minikursus | Mette Brudvig";
    window.scrollTo(0, 0);
  }, [isEn]);

  const titel = isEn 
    ? (gratisWorkshop?.titel?.en || "Frisæt dig") 
    : (gratisWorkshop?.titel?.da || "Frisæt dig");

  const undertitel = isEn 
    ? (gratisWorkshop?.undertitel?.en || "Online minikursus") 
    : (gratisWorkshop?.undertitel?.da || "Online minikursus");

  const datoTid = isEn 
    ? (gratisWorkshop?.dato_tid?.en || "Online recording · Watch at your own pace") 
    : (gratisWorkshop?.dato_tid?.da || "Online optagelse · Se i dit eget tempo");

  const punkterTitel = isEn 
    ? (gratisWorkshop?.punkter_titel?.en || "What you get") 
    : (gratisWorkshop?.punkter_titel?.da || "Du får");

  const praktiskTitel = isEn 
    ? (gratisWorkshop?.praktisk_titel?.en || "Practical") 
    : (gratisWorkshop?.praktisk_titel?.da || "Praktisk");

  const hilsen = isEn 
    ? (gratisWorkshop?.hilsen?.en || "I look forward to having you join us.") 
    : (gratisWorkshop?.hilsen?.da || "Jeg glæder mig til at have dig med.");

  const checkoutUrl = (gratisWorkshop?.form_action && gratisWorkshop.form_action !== "https://pay.lumant.ai/kurv/DVOL-gratis-frisaet-dig-workshop")
    ? gratisWorkshop.form_action
    : "";
  const heroImage = gratisWorkshop?.billede?.[0] || "https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/mettebrudvigokt2021-112-1.jpg";

  const rawAfsnit = isEn
    ? (gratisWorkshop?.afsnit_1?.en || "")
    : (gratisWorkshop?.afsnit_1?.da || "");

  const firstPMatch = rawAfsnit.match(/<p>(.*?)<\/p>/i);
  const heroTekst = firstPMatch 
    ? firstPMatch[1].replace(/<[^>]+>/g, '') 
    : (isEn 
        ? "Do you have something in your life you are ready to let go of – and something new you would like to create?" 
        : "Er du er klar til at slippe det, tynger og begrænser dig? Og bevidst skabe den version af dig selv og det liv, du ønsker?");

  const bodyAfsnitHtml = rawAfsnit.replace(/<p>.*?<\/p>/i, '').trim();

  const punkterHtml = isEn
    ? (gratisWorkshop?.punkter?.en || "")
    : (gratisWorkshop?.punkter?.da || "");

  const praktiskPunkterHtml = isEn
    ? (gratisWorkshop?.praktisk_punkter?.en || "")
    : (gratisWorkshop?.praktisk_punkter?.da || "");

  const replayTekstHtml = isEn
    ? (gratisWorkshop?.replay_tekst?.en || "")
    : (gratisWorkshop?.replay_tekst?.da || "");

  return (
    <div className="bg-[#faf9f6] min-h-screen text-stone-800">
      {/* Subpage Header / Breadcrumb Hero */}
      <section className="relative min-h-[60vh] md:min-h-[70vh] pt-[185px] md:pt-[215px] lg:pt-[225px] pb-12 flex items-center bg-[#f5efe9] border-b border-[#817262]/20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center z-10 w-full">
          <FadeIn direction="right">
            <div className="mb-6">
              <Link 
                to={frisaetPath}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#817262] hover:text-stone-900 transition-colors"
              >
                <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" />
                <span>{isEn ? "Back to Unleash Yourself" : "Tilbage til Frisæt dig"}</span>
              </Link>
            </div>
            
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-serif text-stone-900 leading-[1.1] mb-6">
              {undertitel || (isEn ? "Mini-Course – Recording" : "Minikursus – optagelse")}
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-stone-700 font-light leading-relaxed max-w-lg mb-8">
              {heroTekst}
            </p>

            <div className="pt-2 flex items-center gap-3 flex-wrap">
              {checkoutUrl ? (
                <a
                  href={checkoutUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-6 py-3.5 bg-[#817262] text-white hover:bg-stone-800 transition-all duration-300 text-xs uppercase tracking-widest font-bold rounded-sm shadow-sm"
                >
                  <span>{isEn ? "Buy recording – 250 DKK" : "Køb optagelse – 250 kr."}</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </a>
              ) : (
                <a
                  href="#tilmelding"
                  className="inline-flex items-center gap-2.5 px-6 py-3.5 bg-[#817262] text-white hover:bg-stone-800 transition-all duration-300 text-xs uppercase tracking-widest font-bold rounded-sm shadow-sm"
                >
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                  <span>{isEn ? "Recording coming soon" : "Kommer snart – Optagelse"}</span>
                  <i className="fa-solid fa-arrow-down text-xs" aria-hidden="true" />
                </a>
              )}
              <span className="text-xs text-stone-500 font-light">
                {isEn ? "Access to the full 2-hour recording" : "Fuld adgang til det 2-timers onlinekursus"}
              </span>
            </div>
          </FadeIn>
        </div>
        <div className="absolute top-0 right-0 w-full md:w-1/2 h-full opacity-20 md:opacity-100 pt-20 md:pt-24 lg:pt-28 pointer-events-none">
          <img 
            src={heroImage} 
            alt={`Mette Brudvig ${undertitel || "Online minikursus"}`} 
            className="w-full h-full object-cover object-top pointer-events-none"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#f5efe9] via-transparent to-transparent hidden md:block pointer-events-none" />
        </div>
      </section>

      {/* Main Content & Sign-up Layout */}
      <section className="py-16 md:py-24 max-w-7xl mx-auto px-6 space-y-12 md:space-y-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {/* Left Column: Workshop copy & points */}
          <div className="lg:col-span-7 space-y-12">
            
            {/* Opening hook & description */}
            <FadeIn>
              {bodyAfsnitHtml ? (
                <div 
                  className="space-y-6 text-stone-700 leading-relaxed text-base sm:text-lg font-light [&>p]:leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: bodyAfsnitHtml }}
                />
              ) : (
                <div className="space-y-6 text-stone-700 leading-relaxed text-base sm:text-lg font-light">
                  <p>
                    {isEn 
                      ? "In this two-hour online course we work with both."
                      : "På dette to timers online kursus arbejder vi med begge dele."}
                  </p>

                  <p>
                    {isEn 
                      ? "First, we work with what I call your dead weight: what you carry with you that limits you, holds you back, or drains your energy."
                      : "Først arbejder vi med det, jeg kalder din dødvægt: det, du bærer med dig, som begrænser dig, holder dig tilbage eller tager din energi."}
                  </p>

                  <p>
                    {isEn 
                      ? "You will gain knowledge and concrete tools on how to begin letting it go – both mentally and energetically."
                      : "Du får viden og konkrete værktøjer til, hvordan du kan begynde at slippe det – både mentalt og energetisk."}
                  </p>

                  <p>
                    {isEn 
                      ? "We will do a guided meditation where you open up to releasing what you no longer wish to carry, allowing the energy to do its work."
                      : "Vi laver en guidet meditation, hvor du åbner for at slippe noget af det, du ikke længere ønsker at tage med dig, og hvor energien får lov til at arbejde."}
                  </p>

                  <p>
                    {isEn 
                      ? "Then, we turn our attention toward what you want to create more of in your life."
                      : "Derefter vender vi blikket mod det, du gerne vil skabe mere af i dit liv."}
                  </p>

                  <p>
                    {isEn 
                      ? "You will learn how to work with your energy, focus, and intentions when you want to create change and attract more of what you desire."
                      : "Du får viden om, hvordan du kan arbejde med din energi, dit fokus og dine intentioner, når du ønsker at skabe forandring og tiltrække mere af det, du gerne vil have ind i dit liv."}
                  </p>

                  <p>
                    {isEn 
                      ? "We conclude with a guided manifestation meditation connecting you with what you wish to create."
                      : "Vi afslutter med en guidet manifestationsmeditation, hvor du forbinder dig med det, du ønsker at skabe."}
                  </p>
                </div>
              )}
            </FadeIn>

          </div>

          {/* Right Column: Checkout Card */}
          <div className="lg:col-span-5">
            
            {/* The Lumant Checkout Box */}
            <div id="tilmelding" className="scroll-mt-36">
              <FadeIn delay={100}>
                <div className="bg-white border-2 border-[#817262] rounded-2xl p-7 sm:p-8 shadow-md space-y-6">
                  <div className="border-b border-[#817262]/15 pb-4">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#817262]">
                        {isEn ? "Online Product" : "Online produkt"}
                      </span>
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#817262]/15 text-[#817262] text-[10px] uppercase tracking-wider font-bold">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#817262] animate-pulse" />
                        {isEn ? "Coming soon" : "Kommer snart"}
                      </span>
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-serif text-stone-900 font-medium">
                      {isEn ? "Course Recording" : "Optagelse af onlinekurset"}
                    </h3>
                    <p className="text-stone-600 text-xs sm:text-sm font-light mt-1">
                      {datoTid}
                    </p>
                  </div>

                  <div className="space-y-3">
                    {checkoutUrl ? (
                      <a
                        href={checkoutUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2.5 w-full bg-[#817262] hover:bg-stone-900 text-white py-4 px-6 uppercase text-xs sm:text-sm tracking-[0.2em] font-bold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 text-center cursor-pointer"
                      >
                        <span>{isEn ? "Buy recording – 250 DKK" : "Køb optagelse – 250 kr."}</span>
                        <i className="fa-solid fa-arrow-up-right-from-square text-xs" />
                      </a>
                    ) : (
                      <div className="flex items-center justify-center gap-2.5 w-full bg-[#817262]/15 text-[#817262] py-4 px-6 uppercase text-xs sm:text-sm tracking-[0.2em] font-bold rounded-xl border border-[#817262]/30 text-center cursor-default">
                        <i className="fa-regular fa-clock text-xs" />
                        <span>{isEn ? "Coming soon" : "Kommer snart"}</span>
                      </div>
                    )}

                    <p className="text-[11px] sm:text-xs text-center text-stone-500 font-light leading-relaxed">
                      {isEn 
                        ? "Access to the recording is being prepared and will be open for purchase here shortly." 
                        : "Adgang til optagelsen klargøres netop nu og bliver snarest tilgængelig for køb her på siden."}
                    </p>
                  </div>

                  <div className="pt-4 border-t border-stone-100 space-y-2 text-xs text-stone-600 font-light">
                    <div className="flex items-center gap-2">
                      <i className="fa-solid fa-check text-[#004f3b] text-xs shrink-0" />
                      <span>{isEn ? "Full access to the 2-hour course recording with Mette Brudvig" : "Fuld adgang til optagelsen af det 2 timers onlinekursus med Mette Brudvig"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="fa-solid fa-check text-[#004f3b] text-xs shrink-0" />
                      <span>{isEn ? "Watch and rewatch at your own pace whenever it suits you" : "Se og gense i dit eget tempo, når det passer dig"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="fa-solid fa-check text-[#004f3b] text-xs shrink-0" />
                      <span>{isEn ? "Guided meditations & practical energetic tools included" : "Guidede meditationer & konkrete energetiske værktøjer"}</span>
                    </div>
                  </div>
                </div>
              </FadeIn>
            </div>

          </div>

        </div>

        {/* Row 2: Du får & Praktisk - flugter helt perfekt */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start pt-12 md:pt-16 border-t border-stone-200/80">
          
          {/* Left Column: Du får & Kan du ikke være med live? */}
          <div className="lg:col-span-7">
            <FadeIn delay={100}>
              <div className="space-y-6">
                <h2 className="font-serif text-2xl md:text-3xl text-stone-900 font-normal">
                  {punkterTitel}
                </h2>

                {punkterHtml ? (
                  <div 
                    className="[&>ul]:space-y-3.5 [&>ul]:text-stone-700 [&>ul>li]:flex [&>ul>li]:items-start [&>ul>li]:gap-3 [&>ul>li]:leading-relaxed [&>ul>li]:text-xs sm:[&>ul>li]:text-sm [&>ul>li]:before:content-['✓'] [&>ul>li]:before:w-5 [&>ul>li]:before:h-5 [&>ul>li]:before:rounded-full [&>ul>li]:before:bg-[#004f3b]/10 [&>ul>li]:before:text-[#004f3b] [&>ul>li]:before:flex [&>ul>li]:before:items-center [&>ul>li]:before:justify-center [&>ul>li]:before:shrink-0 [&>ul>li]:before:mt-0.5 [&>ul>li]:before:text-xs [&>ul>li]:before:font-bold"
                    dangerouslySetInnerHTML={{ __html: punkterHtml }}
                  />
                ) : (
                  <ul className="space-y-3.5 text-stone-700 text-xs sm:text-sm">
                    {(isEn ? [
                      "Knowledge on how to work with what limits you",
                      "Concrete tools you can continue working with afterwards",
                      "A guided meditation to release dead weight",
                      "A guided manifestation meditation",
                      "Inspiration on how to work more consciously with your energy and the life you wish to create"
                    ] : [
                      "Viden om, hvordan du kan arbejde med det, der begrænser dig",
                      "Konkrete værktøjer, du kan arbejde videre med bagefter",
                      "En guidet meditation til at slippe dødvægt",
                      "En guidet manifestationsmeditation",
                      "Inspiration til, hvordan du kan arbejde mere bevidst med din energi og det liv, du ønsker at skabe"
                    ]).map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3 leading-relaxed">
                        <span className="w-5 h-5 rounded-full bg-[#004f3b]/10 text-[#004f3b] flex items-center justify-center shrink-0 mt-0.5 text-xs font-bold">
                          ✓
                        </span>
                        <span className="text-stone-800">{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Adgang til optagelse */}
                <div className="pt-6 border-t border-stone-200/80 space-y-2">
                  <div className="flex items-center gap-2 text-stone-900 font-semibold text-xs sm:text-sm">
                    <i className="fa-solid fa-circle-play text-[#817262]" />
                    <span>{isEn ? "Access to the recording" : "Adgang til optagelsen"}</span>
                  </div>
                  {replayTekstHtml ? (
                    <div 
                      className="text-stone-600 text-xs sm:text-sm leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: replayTekstHtml }}
                    />
                  ) : (
                    <p className="text-stone-600 text-xs sm:text-sm leading-relaxed">
                      {isEn 
                        ? "You get full access to the course recording, allowing you to watch or rewatch at your own pace."
                        : "Du får fuld adgang til optagelsen af kurset, så du kan se eller gense det i dit eget tempo, præcis når det passer dig."}
                    </p>
                  )}
                  <p className="text-stone-900 font-serif text-sm sm:text-base italic pt-1">
                    {hilsen}
                  </p>
                </div>
              </div>
            </FadeIn>
          </div>

          {/* Right Column: Praktisk information */}
          <div className="lg:col-span-5">
            <FadeIn delay={150}>
              <div className="bg-[#faf7f2] border border-stone-200/80 rounded-2xl p-6 sm:p-7 space-y-6">
                <h3 className="font-serif text-xl sm:text-2xl text-stone-900 font-medium border-b border-stone-200/60 pb-3">
                  {praktiskTitel}
                </h3>

                {praktiskPunkterHtml ? (
                  <div 
                    className="[&>ul]:space-y-3.5 [&>ul]:text-xs sm:[&>ul]:text-sm [&>ul]:text-stone-700 [&>ul>li]:flex [&>ul>li]:items-start [&>ul>li]:gap-3 [&>ul>li]:font-light [&>ul>li]:before:content-['•'] [&>ul>li]:before:text-[#817262] [&>ul>li]:before:font-bold [&>ul>li]:before:text-base [&>ul>li]:before:leading-none [&>ul>li]:before:mt-0.5"
                    dangerouslySetInnerHTML={{ __html: praktiskPunkterHtml }}
                  />
                ) : (
                  <div className="space-y-4 text-xs sm:text-sm text-stone-700">
                    <div className="flex items-start gap-3">
                      <i className="fa-solid fa-laptop text-[#817262] mt-0.5 w-4 shrink-0" />
                      <div>
                        <strong className="block text-stone-900 font-medium">{isEn ? "Format" : "Format"}</strong>
                        <span className="font-light text-stone-600">{isEn ? "Online product (video recording & meditations)" : "Online produkt (video-optagelse & meditationer)"}</span>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <i className="fa-regular fa-clock text-[#817262] mt-0.5 w-4 shrink-0" />
                      <div>
                        <strong className="block text-stone-900 font-medium">{isEn ? "Availability" : "Tilgængelighed"}</strong>
                        <span className="font-light text-stone-600">{datoTid}</span>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <i className="fa-solid fa-tag text-[#817262] mt-0.5 w-4 shrink-0" />
                      <div>
                        <strong className="block text-stone-900 font-medium">{isEn ? "Price" : "Pris"}</strong>
                        <span className="font-bold text-stone-900">{isEn ? "250 DKK" : "250 kr."}</span>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <i className="fa-solid fa-envelope text-[#817262] mt-0.5 w-4 shrink-0" />
                      <div>
                        <strong className="block text-stone-900 font-medium">{isEn ? "Access" : "Adgang"}</strong>
                        <span className="font-light text-stone-600">
                          {isEn 
                            ? "Link to the recording sent directly to your email upon purchase."
                            : "Link til optagelsen sendes direkte til din mail ved køb."}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="pt-3 border-t border-stone-200/80">
                  {checkoutUrl ? (
                    <a
                      href={checkoutUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[#817262] hover:text-stone-900 text-[11px] uppercase tracking-widest font-bold transition-colors"
                    >
                      <span>{isEn ? "Buy recording – 250 DKK" : "Køb optagelse – 250 kr."}</span>
                      <i className="fa-solid fa-arrow-up-right-from-square text-xs" />
                    </a>
                  ) : (
                    <span className="inline-flex items-center gap-2 text-[#817262] text-[11px] uppercase tracking-widest font-bold">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#817262] animate-pulse" />
                      <span>{isEn ? "Coming soon" : "Kommer snart"}</span>
                    </span>
                  )}
                </div>
              </div>
            </FadeIn>

          </div>

        </div>
      </section>

    </div>
  );
};

const MeditationAudioCard = ({ title, subtitle, durationLabel, initialDuration = 0, audioSrc, description, number }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(initialDuration);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { lang } = useLanguage();
  const isEn = lang === 'en';

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      if (audio.duration && !isNaN(audio.duration)) {
        setDuration(audio.duration);
      }
    };
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    const handleWaiting = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('canplay', handleCanPlay);

    if (audio.readyState >= 1 && audio.duration) {
      setDuration(audio.duration);
    }

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('canplay', handleCanPlay);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const fraction = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = fraction * duration;
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isMuted) {
      audio.muted = false;
      setIsMuted(false);
    } else {
      audio.muted = true;
      setIsMuted(true);
    }
  };

  const formatTime = (secs) => {
    if (isNaN(secs) || secs < 0) return '00:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m < 10 ? '0' : ''}${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className={`bg-white border rounded-2xl p-6 sm:p-8 transition-all duration-300 shadow-xs hover:shadow-lg ${
      isPlaying ? 'border-[#817262] ring-2 ring-[#817262]/20' : 'border-[#e9e5df] hover:border-[#817262]/40'
    }`}>
      <audio ref={audioRef} src={audioSrc} preload="metadata" />
      
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-5">
        <div className="space-y-1.5 flex-1">
          <h3 className="text-2xl sm:text-3xl font-serif text-stone-900 leading-snug">
            {title}
          </h3>
          <p className="text-sm sm:text-base font-serif italic text-[#817262]">
            {subtitle}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0 self-start">
          <span className="inline-flex items-center gap-1.5 bg-[#f5efe9] text-[#817262] text-xs font-mono px-3 py-1 rounded-full border border-[#817262]/15">
            <i className="fa-regular fa-clock text-[10px]" />
            <span>{duration > 0 ? formatTime(duration) : durationLabel}</span>
          </span>
          <a
            href={audioSrc}
            download
            className="inline-flex items-center gap-1.5 bg-stone-100 hover:bg-[#817262] text-stone-700 hover:text-white text-xs font-medium px-3.5 py-1.5 rounded-full transition-colors cursor-pointer"
            title={isEn ? "Download meditation (MP3)" : "Download meditation (MP3)"}
          >
            <i className="fa-light fa-arrow-down-to-line text-xs" />
            <span className="hidden sm:inline">{isEn ? "Download MP3" : "Download MP3"}</span>
          </a>
        </div>
      </div>

      <p className="text-stone-600 font-light text-sm sm:text-base leading-relaxed mb-6">
        {description}
      </p>

      {/* Audio Player Controls Box */}
      <div className="bg-[#fbf9f6] border border-[#e9e5df] rounded-xl p-4 sm:p-5 space-y-3">
        <div className="flex items-center gap-4">
          {/* Big Play/Pause Button */}
          <button
            type="button"
            onClick={togglePlay}
            className={`w-12 h-12 rounded-full flex items-center justify-center text-white shrink-0 transition-all duration-300 shadow-md hover:scale-105 cursor-pointer ${
              isPlaying ? 'bg-[#817262] hover:bg-stone-900' : 'bg-[#817262] hover:bg-[#6e6153]'
            }`}
            aria-label={isPlaying ? (isEn ? "Pause" : "Pause") : (isEn ? "Play" : "Afspil")}
          >
            {isLoading ? (
              <i className="fa-solid fa-spinner animate-spin text-base" />
            ) : isPlaying ? (
              <i className="fa-solid fa-pause text-base" />
            ) : (
              <i className="fa-solid fa-play text-base ml-0.5" />
            )}
          </button>

          {/* Title & Animated Status in player */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-xs font-medium text-stone-800 truncate">
                {title}
              </span>
              <div className="text-[11px] font-mono text-stone-500 shrink-0">
                <span>{formatTime(currentTime)}</span>
                <span className="mx-1 text-stone-300">/</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Clickable Progress Bar */}
            <div
              onClick={handleSeek}
              className="relative w-full h-2 bg-stone-200/80 rounded-full cursor-pointer overflow-hidden group/bar"
            >
              <div
                className="absolute top-0 left-0 h-full bg-[#817262] transition-all duration-100 rounded-full"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* Mute toggle button */}
          <button
            type="button"
            onClick={toggleMute}
            className="text-stone-400 hover:text-stone-700 p-2 rounded-full transition-colors cursor-pointer shrink-0"
            title={isMuted ? (isEn ? "Unmute" : "Slå lyd til") : (isEn ? "Mute" : "Lydløs")}
            aria-label={isMuted ? (isEn ? "Unmute" : "Slå lyd til") : (isEn ? "Mute" : "Lydløs")}
          >
            {isMuted ? (
              <i className="fa-solid fa-volume-xmark text-sm text-[#817262]" />
            ) : (
              <i className="fa-solid fa-volume-high text-sm" />
            )}
          </button>
        </div>

        {/* Visual Waveform / Sub-bar */}
        <div className="flex items-center justify-between text-[11px] text-stone-400 font-light pt-1 border-t border-stone-200/50">
          <div className="flex items-center gap-2">
            {isPlaying ? (
              <span className="inline-flex items-center gap-1.5 text-[#817262] font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-[#817262] animate-ping" />
                {isEn ? "Playing meditation..." : "Afspiller meditation..."}
              </span>
            ) : (
              <span>{isEn ? "Click play to listen" : "Tryk på afspil for at lytte"}</span>
            )}
          </div>
          <span className="text-[10px] uppercase tracking-wider text-stone-400">
            MP3 • {isEn ? "Free stream & download" : "Gratis streaming & download"}
          </span>
        </div>
      </div>
    </div>
  );
};

const GratisPage = () => {
  const { lang } = useLanguage();
  const isEn = lang === 'en';
  const { gratisMeditationer, meditationer: cmsMeditationer } = useCms();

  useEffect(() => {
    document.title = isEn 
      ? "Free Meditations | Mette Brudvig" 
      : "Gratis meditationer | Mette Brudvig";
    window.scrollTo(0, 0);
  }, [isEn]);

  const defaultMeditations = [
    {
      number: "1",
      audioSrc: "/_assets/grundmeditation.mp3",
      durationLabel: "09:51",
      initialDuration: 591,
      title: isEn ? "Light Meditation: Grounding Meditation" : "Lysmeditation: Grundmeditation",
      subtitle: isEn ? "Grounding, light replenishment and centering" : "Grounding, lyspåfyldning og centrering",
      description: isEn 
        ? "A deep foundational meditation that helps you settle fully into your body, ground into the calm energy of the earth, and replenish your energetic system with clear, healing light. Perfect for morning, evening, or whenever you need to return to your center."
        : "En dyb grundmeditation, der hjælper dig med at lande helt i din krop, grounde dig mod jordens rolige energi og fylde dit energisystem med klart, helende lys. Perfekt til både morgen og aften, eller når du har brug for at finde tilbage til dit eget center."
    },
    {
      number: "2",
      audioSrc: "/_assets/rodchakrahealing-5.mp3",
      durationLabel: "15:40",
      initialDuration: 940,
      title: isEn ? "Root Chakra Healing" : "Rodchakrahealing",
      subtitle: isEn ? "Safety, stability and connection to the earth" : "Tryghed, stabilitet og jordforbindelse",
      description: isEn
        ? "A healing journey into your root chakra, the foundation of your inner sense of safety and trust in life. Helps you release restlessness, fear, and survival energy, restoring a deep sense of being held and supported by the earth."
        : "En healende rejse ind i dit rodchakra, som er fundamentet for din indre tryghed og tillid til livet. Hjælper dig med at give slip på uro, frygt og overlevelsesenergi og genskabe en dyb fornemmelse af at være holdt og støttet af jorden."
    },
    {
      number: "3",
      audioSrc: "/_assets/sjaelsmeditationred-1.mp3",
      durationLabel: "16:01",
      initialDuration: 961,
      title: isEn ? "Make More Space for Your Soul" : "Skab mere plads til din sjæl",
      subtitle: isEn ? "Soul meditation – release mental noise and feel your essence" : "Sjælsmeditation – slip sindets støj og mærk dit sande væsen",
      description: isEn
        ? "A guided soul meditation to help you step out of mental chatter, drop your shoulders, and open up to your soul's wisdom. Feel your heart space expand, making room for the profound peace and clarity already living within you."
        : "En guidet sjælsmeditation, der hjælper dig med at træde ud af tankemylderet, sænke skuldrene og åbne døren til din sjæls visdom. Mærk dit hjerterum udvide sig, og giv plads til den fred og klarhed, som allerede bor i dig."
    }
  ];

  // Resolve CMS page fields for hero & råd with bilingual fallback
  const getField = (field, fallback) => {
    if (!gratisMeditationer) return fallback;
    const val = gratisMeditationer[field];
    if (val && typeof val === 'object') {
      return val[lang] || val.da || val.en || fallback;
    }
    return val || fallback;
  };

  // Map CMS meditations - check both 'gratis_meditationer' page collection fields and 'meditationer' list items
  const pageMeditations = [1, 2, 3].map((num, idx) => {
    const cmsItem = cmsMeditationer?.[idx];
    const defaultItem = defaultMeditations[idx];

    // Check page-level collection (gratis_meditationer)
    const pageTitle = getField(`meditation_${num}_titel`, '');
    const pageSubtitle = getField(`meditation_${num}_undertitel`, '');
    const pageDesc = getField(`meditation_${num}_beskrivelse`, '');
    const pageDuration = gratisMeditationer?.[`meditation_${num}_varighed`];
    const pageSeconds = gratisMeditationer?.[`meditation_${num}_sekunder`];
    const pageAudioFile = gratisMeditationer?.[`meditation_${num}_lydfil`]?.[0]
      ? assetPath(gratisMeditationer[`meditation_${num}_lydfil`][0])
      : '';
    const pageAudioUrl = gratisMeditationer?.[`meditation_${num}_lydfil_url`];

    // Item-level collection fallback
    const itemFileUrl = cmsItem?.lydfil?.[0] ? assetPath(cmsItem.lydfil[0]) : '';
    const itemAudioSrc = itemFileUrl || cmsItem?.lydfil_url || '';
    const itemTitle = typeof cmsItem?.titel === 'object'
      ? (cmsItem.titel?.[lang] || cmsItem.titel?.da || cmsItem.titel?.en || '')
      : (cmsItem?.titel || '');
    const itemSubtitle = typeof cmsItem?.undertitel === 'object'
      ? (cmsItem.undertitel?.[lang] || cmsItem.undertitel?.da || cmsItem.undertitel?.en || '')
      : (cmsItem?.undertitel || '');
    const itemDesc = typeof cmsItem?.beskrivelse === 'object'
      ? (cmsItem.beskrivelse?.[lang] || cmsItem.beskrivelse?.da || cmsItem.beskrivelse?.en || '')
      : (cmsItem?.beskrivelse || '');

    const audioSrc = pageAudioFile || pageAudioUrl || itemAudioSrc || defaultItem?.audioSrc || '';
    const title = pageTitle || itemTitle || defaultItem?.title || '';
    const subtitle = pageSubtitle || itemSubtitle || defaultItem?.subtitle || '';
    const description = pageDesc || itemDesc || defaultItem?.description || '';
    const durationLabel = pageDuration || cmsItem?.varighed || defaultItem?.durationLabel || '00:00';
    const initialDuration = Number(pageSeconds) || Number(cmsItem?.sekunder) || defaultItem?.initialDuration || 0;

    return {
      number: String(num),
      audioSrc,
      durationLabel,
      initialDuration,
      title,
      subtitle,
      description
    };
  });

  const meditations = [...pageMeditations];
  if (cmsMeditationer && cmsMeditationer.length > 3) {
    for (let idx = 3; idx < cmsMeditationer.length; idx++) {
      const item = cmsMeditationer[idx];
      const fileUrl = item.lydfil?.[0] ? assetPath(item.lydfil[0]) : '';
      const audioSrc = fileUrl || item.lydfil_url || '';
      const titleVal = typeof item.titel === 'object' ? (item.titel?.[lang] || item.titel?.da || item.titel?.en || '') : (item.titel || '');
      const subtitleVal = typeof item.undertitel === 'object' ? (item.undertitel?.[lang] || item.undertitel?.da || item.undertitel?.en || '') : (item.undertitel || '');
      const descVal = typeof item.beskrivelse === 'object' ? (item.beskrivelse?.[lang] || item.beskrivelse?.da || item.beskrivelse?.en || '') : (item.beskrivelse || '');
      meditations.push({
        number: String(item.nummer || idx + 1),
        audioSrc,
        durationLabel: item.varighed || '00:00',
        initialDuration: Number(item.sekunder) || 0,
        title: titleVal,
        subtitle: subtitleVal,
        description: descVal
      });
    }
  }

  const heroBadge = getField('hero_badge', isEn ? "Free Meditations" : "Gratis meditationer");
  const heroTitel = getField('hero_titel', isEn ? "Free Meditations" : "Gratis meditationer");
  const heroUndertitel = getField('hero_undertitel', isEn 
    ? "3 guided meditations for calm, grounding and soul connection" 
    : "3 guidede meditationer til ro, grounding og sjælekontakt");
  const heroTekst = getField('hero_tekst', isEn 
    ? "Here you can freely listen to or download three of Mette's guided meditations. Find a quiet moment, settle in comfortably – preferably with headphones – and allow yourself to land deeply in your body and soul's peace."
    : "Her kan du frit lytte til eller downloade tre af Mettes guidede meditationer. Find et roligt øjeblik, sæt dig godt til rette – gerne med høretelefoner – og tillad dig selv at lande dybt i kroppen og sjælens fred.");

  const raadTitel = getField('raad_titel', isEn ? "Tips for your meditation practice" : "Gode råd til din meditation");
  const raad1Titel = getField('raad_1_titel', isEn ? "1. Find a calm space" : "1. Find et roligt rum");
  const raad1Tekst = getField('raad_1_tekst', isEn 
    ? "Sit or lie down where you can remain undisturbed. Settle your body with a few deep breaths."
    : "Sæt dig eller læg dig et sted, hvor du kan være uforstyrret. Giv kroppen tid til at lande.");

  const raad2Titel = getField('raad_2_titel', isEn ? "2. Use headphones" : "2. Brug høretelefoner");
  const raad2Tekst = getField('raad_2_tekst', isEn 
    ? "Headphones create an intimate sound space that makes it easier to let go of surroundings."
    : "Høretelefoner skaber et intimt lydrum, så du nemmere kan give slip på omgivelserne.");

  const raad3Titel = getField('raad_3_titel', isEn ? "3. Let go of expectations" : "3. Slip forventningerne");
  const raad3Tekst = getField('raad_3_tekst', isEn 
    ? "Whatever you feel is welcome. Meditation is about meeting yourself exactly where you are."
    : "Uanset hvad du mærker, er det velkomment. Meditation handler om at møde dig selv lige dér, hvor du er.");

  return (
    <div className="bg-[#fbf9f6] min-h-screen text-stone-800">
      {/* Hero Section */}
      <section className="relative pt-[175px] md:pt-[205px] lg:pt-[215px] pb-16 md:pb-24 bg-[#f5efe9] border-b border-stone-200/60 overflow-hidden">
        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-white/40 rounded-full blur-3xl pointer-events-none -z-0" />
        <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
          <FadeIn>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-serif text-stone-900 leading-[1.15] mb-4">
              {heroTitel}
            </h1>

            <p className="text-xl sm:text-2xl font-serif text-[#817262] italic mb-6">
              {heroUndertitel}
            </p>

            <p className="max-w-2xl mx-auto text-stone-600 font-light text-base sm:text-lg leading-relaxed">
              {heroTekst}
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Meditation Players List */}
      <section className="py-16 md:py-24 max-w-4xl mx-auto px-6">
        <div className="space-y-8">
          {meditations.map((med, idx) => (
            <FadeIn key={idx} delay={idx * 100}>
              <MeditationAudioCard {...med} />
            </FadeIn>
          ))}
        </div>

        {/* Quiet Advice Box */}
        <FadeIn delay={300} className="mt-16">
          <div className="bg-[#f5efe9]/70 border border-[#817262]/20 rounded-2xl p-8 sm:p-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full bg-white text-[#817262] flex items-center justify-center shadow-2xs">
                <i className="fa-light fa-sparkles text-sm" />
              </div>
              <h3 className="text-xl font-serif text-stone-900">
                {raadTitel}
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-stone-600 font-light leading-relaxed">
              <div className="space-y-1.5">
                <h4 className="font-medium text-stone-900 text-sm">
                  {raad1Titel}
                </h4>
                <p>
                  {raad1Tekst}
                </p>
              </div>
              <div className="space-y-1.5">
                <h4 className="font-medium text-stone-900 text-sm">
                  {raad2Titel}
                </h4>
                <p>
                  {raad2Tekst}
                </p>
              </div>
              <div className="space-y-1.5">
                <h4 className="font-medium text-stone-900 text-sm">
                  {raad3Titel}
                </h4>
                <p>
                  {raad3Tekst}
                </p>
              </div>
            </div>
          </div>
        </FadeIn>
      </section>
    </div>
  );
};

const TaenketankPage = () => {
  const { lang, t } = useLanguage();
  const isEn = lang === 'en';

  useEffect(() => {
    document.title = isEn 
      ? "Mette Brudvig - NELA Think Tank & Education" 
      : "Mette Brudvig - NELA Tænketank & Uddannelser";
    window.scrollTo(0, 0);
  }, [isEn]);

  const [status, setStatus] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="bg-[#fbfaf8] min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] pt-[150px] md:pt-[170px] pb-12 flex items-center bg-stone-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/sjaelsintelligens-navn.jpg" 
            alt="NELA Tænketank" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950 via-stone-900/85 to-transparent"></div>
        <div className="max-w-5xl mx-auto px-6 relative z-10 w-full">
          <FadeIn direction="right">
            <span className="text-xs uppercase tracking-[0.4em] font-bold text-[#817262] mb-4 block">
              {isEn ? "THINK TANK" : "TÆNKETANK"}
            </span>
            <h1 className="text-4xl md:text-6xl font-serif leading-[1.1] mb-6 max-w-3xl">
              {isEn ? "NELA Think Tank" : "Tænketanken NELA"} <br />
              <span className="text-2xl md:text-4xl font-light text-stone-300 block mt-2">
                {isEn ? "– gathering new perspectives" : "– vi samler de nye tanker"}
              </span>
            </h1>
            <p className="text-lg md:text-xl text-stone-200 font-light leading-relaxed max-w-2xl mb-4">
              {isEn 
                ? "NELA is an academy, network, and think tank for people who realize that many traditional systems no longer serve the world we live in."
                : "NELA er et academy, et netværk og en tænketank for mennesker, der kan se, at mange af vores systemer ikke længere passer til den verden, vi lever i."}
            </p>
            <p className="text-lg md:text-xl text-stone-200 font-light leading-relaxed max-w-2xl mb-4 italic">
              {isEn ? '"We do not want to wait for the next crisis before thinking anew."' : '"Vi vil ikke vente på den næste krise, før vi tænker nyt."'}
            </p>
            <div className="pt-8 flex flex-wrap gap-4">
              <Link 
                to={isEn ? "/en/contact" : "/kontakt"}
                className="bg-[#817262] text-white px-8 py-3.5 uppercase text-xs tracking-[0.2em] font-bold hover:bg-[#6e6153] transition-colors rounded-sm shadow-md inline-block text-center"
              >
                {isEn ? "Contact" : "Kontakt"}
              </Link>
              <Link 
                to={isEn ? "/en/about-mette" : "/om-mette"}
                className="border border-stone-400 text-white px-8 py-3.5 uppercase text-xs tracking-[0.2em] font-bold hover:bg-white/10 transition-colors rounded-sm inline-block text-center"
              >
                {isEn ? "About Mette" : "Om Mette"}
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Tænketanken NELA Section */}
      <section id="taenketank" className="py-24 md:py-32 bg-white px-6 scroll-mt-16">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-12 gap-16 items-start mb-20">
            <div className="md:col-span-7 space-y-8">
              <FadeIn direction="right">
                <span className="text-xs uppercase tracking-[0.3em] text-[#817262] font-semibold">
                  {isEn ? "The Vision" : "Visionen"}
                </span>
                <h2 className="text-3xl md:text-4xl font-serif text-stone-900 leading-tight">
                  {isEn ? "In NELA's think tank, we gather new perspectives" : "I NELAs tænketanke samler vi de nye tanker"}
                </h2>
                <div className="h-[2px] w-20 bg-[#817262]/40"></div>
                <p className="text-stone-700 font-light text-lg leading-relaxed">
                  {isEn
                    ? "In NELA's think tank, we gather minds with expertise, wisdom, intuition, and courage to develop tangible solutions to key modern challenges across education, health, workplace culture, environment, leadership, policy, and new communities."
                    : "I NELAs tænketanke samler vi mennesker med viden, erfaring, intuition og mod til at udvikle konkrete løsninger på nogle af tidens største udfordringer. Det kan være inden for skole, sundhed, ældrepleje, arbejdsliv, miljø, ledelse, politik og nye fællesskaber."}
                </p>
                <p className="text-stone-700 font-light text-lg leading-relaxed">
                  {isEn
                    ? "We do not stop at highlighting flaws in existing models. We actively explore what can function far better."
                    : "Vi nøjes ikke med at påpege, hvad der ikke virker. Vi undersøger, hvad der kan fungere bedre."}
                </p>
              </FadeIn>

              <FadeIn direction="right" delay={0.1}>
                <div className="pt-8 border-t border-stone-100 space-y-4">
                  <h3 className="text-xl font-serif text-stone-900">
                    {isEn ? "Communication & Action" : "Formidling og handling"}
                  </h3>
                  <p className="text-stone-700 font-light leading-relaxed text-lg">
                    {isEn
                      ? "NELA's foundation rests on humanism, responsibility, professional rigor, consciousness, and holistic thinking. Solutions developed here are amplified through media, podcasts, talks, and dialogue with decision-makers."
                      : "NELAs arbejde bygger på humanisme, ansvar, faglighed, bevidsthed og helhedsorienteret tænkning. De idéer og løsninger, der udvikles i tænketankene, skal bringes videre gennem medier, podcast, foredrag og dialog med politikere, organisationer og beslutningstagere."}
                  </p>
                </div>
              </FadeIn>
            </div>

            <div className="md:col-span-5 space-y-8">
              <FadeIn direction="left">
                <div className="bg-[#f5efe9]/40 p-8 md:p-10 border-l-4 border-[#817262] rounded-r-sm space-y-8 shadow-sm">
                  <h3 className="text-lg font-serif text-stone-900 uppercase tracking-wider">
                    {isEn ? "Three Questions We Explore:" : "De tre spørgsmål vi stiller:"}
                  </h3>
                  <ul className="space-y-6">
                    <li className="flex gap-4 items-start">
                      <span className="text-[#817262] mt-1"><i className="fa-solid fa-circle-question text-lg"></i></span>
                      <p className="text-stone-800 font-serif text-base leading-relaxed">
                        {isEn
                          ? "How do we build systems that nourish human well-being rather than exhausting people?"
                          : "Hvordan skaber vi systemer, der styrker menneskers trivsel frem for at slide dem ned?"}
                      </p>
                    </li>
                    <li className="flex gap-4 items-start">
                      <span className="text-[#817262] mt-1"><i className="fa-solid fa-circle-question text-lg"></i></span>
                      <p className="text-stone-800 font-serif text-base leading-relaxed">
                        {isEn ? "How do we transition from 'more' to 'better'?" : "Hvordan går vi fra mere til bedre?"}
                      </p>
                    </li>
                    <li className="flex gap-4 items-start">
                      <span className="text-[#817262] mt-1"><i className="fa-solid fa-circle-question text-lg"></i></span>
                      <p className="text-stone-800 font-serif text-base leading-relaxed">
                        {isEn
                          ? "How do we bridge the society we have with the society we truly need?"
                          : "Hvordan bygger vi bro mellem det samfund, vi har, og det samfund, vi har brug for?"}
                      </p>
                    </li>
                  </ul>
                </div>
              </FadeIn>

              <FadeIn direction="left" delay={0.1}>
                <div className="bg-[#817262]/5 p-8 rounded-sm border border-[#817262]/20 text-center space-y-4">
                  <p className="text-stone-800 font-serif italic text-lg leading-relaxed max-w-2xl mx-auto">
                    {isEn
                      ? '"NELA is for those who do not merely think someone should take action. It is for those ready to participate."'
                      : '"NELA er for dig, der ikke kun tænker, at nogen burde gøre noget. Det er for dig, der selv vil være med."'}
                  </p>
                  <button 
                    onClick={() => scrollToSection('om-nela')}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#817262] hover:text-[#6e6153] transition-colors cursor-pointer pt-2"
                  >
                    <span>{isEn ? "Read more here" : "Læs mere her"}</span>
                    <i className="fa-solid fa-arrow-down"></i>
                  </button>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Om NELA Section */}
      <section id="om-nela" className="py-24 bg-[#f5efe9] px-6 scroll-mt-16">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-stretch">
            <div className="md:col-span-5 order-2 md:order-1 flex flex-col">
              <FadeIn direction="right" className="h-full flex flex-col">
                <div className="relative flex-1 min-h-[400px] md:min-h-0 rounded-sm overflow-hidden shadow-2xl h-full">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/mettebrudvigokt2021-124_1.jpg" 
                    alt="Mette Brudvig - NELA" 
                    className="w-full h-full object-cover object-center absolute inset-0"
                  />
                  <div className="absolute inset-0 bg-stone-900/10"></div>
                </div>
              </FadeIn>
            </div>

            <div className="md:col-span-7 order-1 md:order-2 space-y-6">
              <FadeIn direction="left">
                <span className="text-xs uppercase tracking-[0.3em] text-[#817262] font-semibold">
                  {isEn ? "The Academy & Movement" : "Akademiet & Bevægelsen"}
                </span>
                <h2 className="text-3xl md:text-4xl font-serif text-stone-900 leading-tight">
                  {isEn ? "About NELA" : "Om NELA"}
                </h2>
                <div className="h-[2px] w-20 bg-[#817262]/40 mb-6"></div>
                <div className="space-y-4 text-stone-700 font-light leading-relaxed text-base md:text-lg">
                  <p className="font-serif text-stone-900 italic">
                    {isEn
                      ? "NELA – New Earth Leaders Academy – is a movement, network, and academy for individuals ready to take active ownership of the world we are shaping."
                      : "NELA – New Earth Leaders Academy – er en bevægelse, et netværk og et academy for mennesker, der vil tage aktivt ansvar for den verden, vi er ved at skabe."}
                  </p>
                  <p>
                    {isEn
                      ? "We bridge personal and spiritual growth with leadership, expertise, and concrete action. Tomorrow's solutions demand individuals who stand anchored in themselves, connected to intuition, and able to act with courage and responsibility."
                      : "Vi forbinder personlig og spirituel udvikling med lederskab, faglighed og konkret handling. For fremtidens løsninger kræver mennesker, der står stærkt i sig selv, er forbundet med deres intuition og kan handle med både bevidsthed, mod og ansvar."}
                  </p>
                  <p>
                    {isEn
                      ? "At NELA, you can complete our foundational spiritual program, cultivating 360-degree personal expansion, intuition, energy work, healing, and heart intelligence."
                      : "Hos NELA kan du uddanne dig gennem vores spirituelle grunduddannelse, hvor du arbejder med 360 graders personlig udvikling, intuition, energi, healing og hjertets intelligens."}
                  </p>
                  <p>
                    {isEn
                      ? "You can also join our leadership program, designed to elevate your personal leadership and capacity to lead with authenticity, clarity, intuition, and human empathy."
                      : "Du kan også tage vores lederuddannelse, der styrker dit personlige lederskab og din evne til at lede med autenticitet, klarhed, intuition og menneskelig indsigt."}
                  </p>
                  <p>
                    {isEn
                      ? "Within NELA you grow personally and professionally, joining an engaged network shaping pioneering concepts for peace, well-being, sustainability, and human evolution."
                      : "I NELA kan du udvikle dig personligt og professionelt, blive en del af et engageret fællesskab og bidrage til nye tanker og løsninger, der fremmer fred, trivsel, bæredygtighed og menneskelig udvikling."}
                  </p>
                  <p className="font-serif text-stone-900">
                    {isEn
                      ? "We believe the new era is forged not by those who merely possess knowledge, but by those who have done the inner work and align their values with decisive action."
                      : "Vi tror på, at den nye verden ikke skabes af mennesker, der blot ved mere, men af mennesker, der har gjort det indre arbejde og er i stand til at omsætte deres værdier til handling."}
                  </p>
                </div>
                <div className="pt-6">
                  <button 
                    onClick={() => scrollToSection('ansoeg')}
                    className="inline-flex items-center gap-3 bg-[#817262] text-white px-8 py-4 uppercase text-xs tracking-[0.2em] font-bold hover:bg-[#6e6153] transition-colors rounded-sm shadow-md cursor-pointer"
                  >
                    <span>{isEn ? "APPLY FOR NELA THINK TANK" : "GÅ TIL NELA til tænketank"}</span>
                    <i className="fa-solid fa-arrow-right"></i>
                  </button>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Thought Leadership & Podcast Section */}
      <section className="py-24 bg-white px-6">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <FadeIn direction="down">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-stone-100 text-stone-600 mb-4">
              <i className="fa-solid fa-microphone text-lg"></i>
            </div>
            <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
              Thought Leadership & Podcast
            </h2>
            <p className="text-stone-600 font-light text-lg leading-relaxed max-w-2xl mx-auto">
              {isEn
                ? "Interested in diving deeper into the vision behind New Era Leadership? Listen to the podcast exploring consciousness, personal growth, and breaking outdated paradigms."
                : "Vil du fordybe dig i tankerne og visionerne bag Ny Tids Lederskab? Lyt til podcasten, hvor vi dykker ned i bevidsthed, personlig udvikling og hvordan vi bryder de gamle rammer."}
            </p>
            <div className="pt-4">
              <Link 
                to={isEn ? "/en/podcasts" : "/podcasts"} 
                className="inline-flex items-center gap-3 bg-stone-600 text-white px-8 py-4 uppercase text-xs tracking-[0.2em] font-bold hover:bg-stone-700 transition-colors rounded-sm shadow-md"
              >
                <span>{isEn ? "Go to Podcast & Blog" : "Gå til Podcast & Blog"}</span>
                <i className="fa-solid fa-arrow-right"></i>
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Sign-up Form Section */}
      <section id="ansoeg" className="py-24 bg-stone-100 px-6 border-t border-stone-200 scroll-mt-16">
        <div className="max-w-4xl mx-auto grid md:grid-cols-12 gap-16 items-center">
          <div className="md:col-span-5 space-y-6">
            <FadeIn direction="right">
              <h3 className="text-2xl md:text-3xl font-serif text-stone-900">
                {isEn ? "Apply for NELA Think Tank" : "Søg optagelse i NELA Tænketank"}
              </h3>
              <p className="text-stone-600 font-light text-base leading-relaxed">
                {isEn
                  ? "NELA Think Tank and educational programs are reserved for leaders, visionaries, and thinkers ready to commit to personal expansion and new era consciousness."
                  : "NELA Tænketank og vores uddannelsesforløb er forbeholdt ledere, politikere og tænkere, der er klar til at forpligte sig til personlig vækst og ny tids bevidsthed."}
              </p>
              <p className="text-stone-600 font-light text-base leading-relaxed">
                {isEn
                  ? "Fill out the form below to apply or to schedule an exploratory call with Mette to ensure mutual resonance."
                  : "Udfyld formularen for at ansøge om optagelse eller for at aftale en uforpligtende samtale med Mette for at se, om vores energier harmoniserer."}
              </p>
            </FadeIn>
          </div>

          <div className="md:col-span-7 bg-white p-8 md:p-12 border border-stone-200 shadow-xl rounded-sm">
            <FadeIn direction="left">
              <form onSubmit={handleSubmit} className="space-y-6">
                <input type="text" name="website_url" className="sr-only absolute" tabIndex="-1" autoComplete="off" />
                
                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">
                    {isEn ? "Name" : "Navn"}
                  </label>
                  <input 
                    required 
                    type="text" 
                    name="name" 
                    className="w-full bg-transparent border-b border-stone-200 py-2.5 focus:border-stone-900 outline-none transition-colors text-stone-900 font-light"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">
                    {isEn ? "Email" : "Email"}
                  </label>
                  <input 
                    required 
                    type="email" 
                    name="email" 
                    className="w-full bg-transparent border-b border-stone-200 py-2.5 focus:border-stone-900 outline-none transition-colors text-stone-900 font-light"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">
                    {isEn ? "Organization / Role" : "Organisation / Rolle"}
                  </label>
                  <input 
                    required
                    type="text" 
                    name="organisation" 
                    placeholder={isEn ? "e.g. Executive, Politician, Entrepreneur..." : "F.eks. Direktør, Politiker, Selvstændig..."}
                    className="w-full bg-transparent border-b border-stone-200 py-2.5 focus:border-stone-900 outline-none transition-colors text-stone-900 font-light placeholder-stone-300 text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">
                    {isEn ? "What calls you to NELA Think Tank?" : "Hvad kalder dig til NELA Tænketank?"}
                  </label>
                  <textarea 
                    name="message" 
                    rows="4" 
                    required
                    placeholder={isEn ? "Tell us briefly about your motivation and what growth you wish to facilitate..." : "Fortæl kort om din motivation, og hvad du ønsker at facilitere af vækst hos dig selv og andre..."}
                    className="w-full bg-transparent border-b border-stone-200 py-2.5 focus:border-stone-900 outline-none transition-colors text-stone-900 font-light resize-none placeholder-stone-300 text-sm"
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  className="w-full bg-stone-600 text-white py-4 uppercase text-xs tracking-[0.3em] font-bold hover:bg-stone-700 transition-colors rounded-sm shadow-md cursor-pointer"
                >
                  {isEn ? "Submit Application / Inquiry" : "Send ansøgning / forespørgsel"}
                </button>

                {status === 'success' && (
                  <p className="text-stone-700 text-center font-serif italic pt-4">
                    {isEn
                      ? "Thank you! Your application has been received. Mette will reach out to you personally."
                      : "Tak! Din ansøgning er modtaget. Mette vil vende tilbage til dig personligt for at aftale det videre forløb."}
                  </p>
                )}
                {status === 'error' && (
                  <p className="text-red-700 text-center font-serif italic pt-4">
                    {isEn
                      ? "An error occurred. Please try again or contact Mette directly."
                      : "Der skete en fejl. Prøv venligst igen eller kontakt Mette direkte."}
                  </p>
                )}
              </form>
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
};

const PrivatlivspolitikPage = () => {
  const { lang, t } = useLanguage();
  const isEn = lang === 'en';

  useEffect(() => {
    document.title = isEn ? "Mette Brudvig - Privacy Policy" : "Mette Brudvig - Privatlivspolitik";
    window.scrollTo(0, 0);
  }, [isEn]);

  return (
    <div className="pt-[150px] md:pt-[170px] pb-20 bg-[#f5efe9]/30">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-xs uppercase tracking-[0.4em] font-bold text-[#817262] mb-3 block">
            {isEn ? "Security & Confidentiality" : "Sikkerhed & Fortrolighed"}
          </span>
          <h1 className="text-4xl md:text-5xl font-serif text-stone-900 mb-6">
            {isEn ? "Privacy Policy" : "Privatlivspolitik"}
          </h1>
          <p className="text-stone-600 font-light max-w-2xl mx-auto leading-relaxed">
            {isEn 
              ? "When you visit my website, subscribe to my newsletter, or contact me for a session or retreat, you entrust me with your personal data. Here is how I protect it."
              : "Når du besøger min hjemmeside, tilmelder dig mit nyhedsbrev eller kontakter mig for en session, et forløb eller et retreat, betror du mig med dine personoplysninger. Her kan du læse, hvordan jeg passer på dem."}
          </p>
          <div className="w-12 h-[1px] bg-[#817262] mx-auto mt-8"></div>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-sm border border-stone-200/60 shadow-xs p-8 md:p-12 space-y-10 text-stone-700 font-light leading-relaxed">
          
          {/* Intro */}
          <section className="space-y-4">
            <h2 className="text-2xl font-serif text-stone-900 font-medium flex items-center gap-3">
              <i className="fa-regular fa-shield-halved text-[#817262] text-xl" />
              1. Generelt om min behandling af dine data
            </h2>
            <p>
              Hos Mette Brudvig (Hjertets Intelligens) vægter jeg fortrolighed og diskretion utroligt højt. Dette gælder i særdeleshed de personlige og spirituelle emner, vi berører under sessioner, clairvoyance og mentorforløb. 
            </p>
            <p>
              Denne privatlivspolitik beskriver, hvordan jeg indsamler, beskytter og anvender de personoplysninger, du efterlader eller afgiver, når du interagerer med min virksomhed.
            </p>
          </section>

          {/* Dataansvarlig */}
          <section className="space-y-4">
            <h2 className="text-2xl font-serif text-stone-900 font-medium flex items-center gap-3">
              <i className="fa-regular fa-address-card text-[#817262] text-xl" />
              2. Dataansvarlig
            </h2>
            <p>
              Den juridiske enhed, som er ansvarlig for behandlingen af dine personoplysninger, er:
            </p>
            <div className="bg-[#f5efe9]/40 p-6 rounded-sm border border-stone-100 space-y-2 text-stone-800 font-normal text-sm">
              <p className="font-serif text-base text-stone-900">Mette Brudvig</p>
              <p>Lokation: Sønderborg</p>
              <p>E-mail: <a href="mailto:mette@mettebrudvig.dk" className="text-[#817262] hover:underline font-medium">mette@mettebrudvig.dk</a></p>
              <p>Tlf: <a href="tel:+4527507083" className="text-[#817262] hover:underline font-medium">+45 27 50 70 83</a></p>
            </div>
          </section>

          {/* Hvilke oplysninger */}
          <section className="space-y-4">
            <h2 className="text-2xl font-serif text-stone-900 font-medium flex items-center gap-3">
              <i className="fa-regular fa-folder-open text-[#817262] text-xl" />
              3. Hvilke oplysninger indsamles og til hvilket formål?
            </h2>
            <p>
              Jeg indsamler og behandler personoplysninger til specifikke og legitime formål. Herunder kan du se, hvilke oplysninger jeg behandler i forskellige situationer:
            </p>
            
            <div className="space-y-6 mt-4">
              <div className="border-l-2 border-[#817262] pl-4 space-y-2">
                <h3 className="font-serif text-stone-900 font-medium">Ved tilmelding til nyhedsbrev:</h3>
                <p className="text-sm">
                  Jeg indsamler din e-mailadresse med det formål at kunne sende dig nyhedsbreve, inspiration, podcasts og information om kommende retreats eller uddannelser. Behandlingen sker på baggrund af dit samtykke.
                </p>
              </div>

              <div className="border-l-2 border-[#817262] pl-4 space-y-2">
                <h3 className="font-serif text-stone-900 font-medium">Ved kontakt via hjemmesiden eller e-mail:</h3>
                <p className="text-sm">
                  Når du skriver til mig via kontaktformularen eller direkte på e-mail, behandler jeg dit navn, din e-mailadresse og det indhold, du skriver til mig. Formålet er at kunne besvare din henvendelse og yde den bedst mulige service.
                </p>
              </div>

              <div className="border-l-2 border-[#817262] pl-4 space-y-2">
                <h3 className="font-serif text-stone-900 font-medium">Ved sessioner og mentorforløb:</h3>
                <p className="text-sm">
                  For at kunne gennemføre og forberede sessioner (clairvoyance, ledersparring, mentorforløb) behandler jeg kontaktoplysninger samt eventuelle relevante personlige eller faglige oplysninger, du deler med mig. Alle oplysninger, der kommer frem under en session, er underlagt fuld fortrolighed og tavshedspligt.
                </p>
              </div>

              <div className="border-l-2 border-[#817262] pl-4 space-y-2">
                <h3 className="font-serif text-stone-900 font-medium">Ved tilmelding til retreats:</h3>
                <p className="text-sm">
                  Når du tilmelder dig et af mine retreats (f.eks. til Montserrat), behandler jeg dine kontaktoplysninger, betalingsoplysninger samt eventuelle praktiske oplysninger (f.eks. allergier eller særlige hensyn), som er nødvendige for at planlægge og afvikle dit ophold.
                </p>
              </div>
            </div>
          </section>

          {/* Opbevaring og sletning */}
          <section className="space-y-4">
            <h2 className="text-2xl font-serif text-stone-900 font-medium flex items-center gap-3">
              <i className="fa-regular fa-clock text-[#817262] text-xl" />
              4. Opbevaring og sletning af dine oplysninger
            </h2>
            <p>
              Jeg opbevarer kun dine personoplysninger, så længe det er nødvendigt for at opfylde de formål, de er indsamlet til, eller for at overholde gældende lovgivning (f.eks. bogføringsloven).
            </p>
            <ul className="list-disc pl-6 space-y-2 text-sm">
              <li><strong>Nyhedsbrev:</strong> Dine oplysninger opbevares, så længe du ønsker at modtage nyhedsbrevet. Du kan til enhver tid afmelde dig via linket i bunden af e-mailen, hvorefter dine data slettes fra modtagerlisten.</li>
              <li><strong>Henvendelser:</strong> Almindelige e-mails og forespørgsler slettes løbende, når dialogen er afsluttet, og der ikke længere er et aktivt behov for opbevaring.</li>
              <li><strong>Regnskabsdata:</strong> Fakturaer og betalingsoplysninger opbevares i 5 år i overensstemmelse med bogføringslovens regler.</li>
              <li><strong>Sessionsnotater:</strong> Eventuelle personlige noter eller forberedelsesmateriale slettes eller makuleres senest 6 måneder efter afslutningen af dit forløb, medmindre andet aftales specifikt.</li>
            </ul>
          </section>

          {/* Fortrolighed og videregivelse */}
          <section className="space-y-4">
            <h2 className="text-2xl font-serif text-stone-900 font-medium flex items-center gap-3">
              <i className="fa-regular fa-lock text-[#817262] text-xl" />
              5. Fortrolighed og videregivelse af oplysninger
            </h2>
            <p>
              Dine oplysninger er i trygge hænder. Jeg sælger, udlejer eller videregiver aldrig dine personoplysninger til tredjemand med henblik på markedsføring eller lignende.
            </p>
            <p>
              Jeg benytter betroede databehandlere (f.eks. e-mail-systemer, hosting-partnere og regnskabssystemer), som udelukkende behandler oplysninger på mine vegne og efter mine instruktioner i overensstemmelse med gældende databeskyttelseslovgivning.
            </p>
          </section>

          {/* Dine rettigheder */}
          <section className="space-y-4">
            <h2 className="text-2xl font-serif text-stone-900 font-medium flex items-center gap-3">
              <i className="fa-regular fa-user-gear text-[#817262] text-xl" />
              6. Dine rettigheder
            </h2>
            <p>
              Du har efter databeskyttelsesforordningen en række rettigheder i forhold til min behandling af oplysninger om dig:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-sm">
              <li><strong>Ret til indsigt:</strong> Du har ret til at få indsigt i de oplysninger, som jeg behandler om dig.</li>
              <li><strong>Ret til berigtigelse:</strong> Du har ret til at få urigtige oplysninger om dig selv rettet.</li>
              <li><strong>Ret til sletning:</strong> I særlige tilfælde har du ret til at få slettet oplysninger om dig, inden tidspunktet for min almindelige generelle sletning indtræffer.</li>
              <li><strong>Ret til begrænsning af behandling:</strong> Du har i visse tilfælde ret til at få behandlingen af dine personoplysninger begrænset.</li>
              <li><strong>Ret til indsigelse:</strong> Du har ret til at gøre indsigelse mod min ellers lovlige behandling af dine personoplysninger.</li>
              <li><strong>Ret til at trække samtykke tilbage:</strong> Hvis min behandling er baseret på dit samtykke (f.eks. nyhedsbrev), har du til enhver tid ret til at trække dit samtykke tilbage.</li>
            </ul>
            <p className="mt-4">
              Hvis du ønsker at gøre brug af dine rettigheder, kan du skrive til mig på <a href="mailto:mette@mettebrudvig.dk" className="text-[#817262] hover:underline font-medium">mette@mettebrudvig.dk</a>.
            </p>
          </section>

          {/* Klage til Datatilsynet */}
          <section className="space-y-4">
            <h2 className="text-2xl font-serif text-stone-900 font-medium flex items-center gap-3">
              <i className="fa-regular fa-circle-question text-[#817262] text-xl" />
              7. Klage til Datatilsynet
            </h2>
            <p>
              Du har ret til at indgive en klage til Datatilsynet, hvis du er utilfreds med den måde, jeg behandler dine personoplysninger på. Du finder Datatilsynets kontaktoplysninger på <a href="https://www.datatilsynet.dk" target="_blank" rel="noopener noreferrer" className="text-[#817262] hover:underline font-medium">www.datatilsynet.dk</a>.
            </p>
          </section>

          {/* Cookies */}
          <section className="space-y-4">
            <h2 className="text-2xl font-serif text-stone-900 font-medium flex items-center gap-3">
              <i className="fa-regular fa-cookie text-[#817262] text-xl" />
              8. Cookies
            </h2>
            <p>
              Min hjemmeside anvender cookies for at optimere brugeroplevelsen og analysere trafikken. Cookies er små tekstfiler, som gemmes på din computer, mobil eller tilsvarende med det formål at genkende den, huske indstillinger og udføre statistik. Du kan til enhver tid slette eller blokere for cookies i dine browserindstillinger.
            </p>
          </section>

          {/* Opdatering */}
          <div className="pt-8 border-t border-stone-100 text-xs text-stone-400 italic">
            Senest opdateret: 15. juli 2026
          </div>

        </div>

        {/* Back Button */}
        <div className="text-center mt-12">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors text-sm font-medium uppercase tracking-widest"
          >
            <i className="fa-solid fa-arrow-left text-xs" />
            Tilbage til forsiden
          </Link>
        </div>
      </div>
    </div>
  );
};

// --- App Root ---

function App() {
  return (
    <Router>
      <LanguageProvider>
        <CmsProvider>
        <TestimonialProvider>
          <ScrollToTop />
          <SEO />
          <div className="min-h-screen bg-white text-stone-900 selection:bg-stone-100 flex flex-col">
            <Header />
            <main className="flex-grow">
              <PageTransition>
                <Routes>
                {/* Danish Primary Routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/foredrag" element={<ForedragPage />} />
                <Route path="/foredrag/privat" element={<ForedragPrivatPage />} />
                <Route path="/foredrag/virksomheder" element={<ForedragVirksomhederPage />} />
                <Route path="/virksomheder/spring-ind-i-fremtiden" element={<VirksomhedSpringIndIFremtidenPage />} />
                <Route path="/foredrag/virksomheder/spring-ind-i-fremtiden" element={<Navigate to="/virksomheder/spring-ind-i-fremtiden" replace />} />
                <Route path="/virksomheder/ny-energi-i-teamet" element={<VirksomhedNyEnergiITeametPage />} />
                <Route path="/foredrag/virksomheder/ny-energi-i-teamet" element={<Navigate to="/virksomheder/ny-energi-i-teamet" replace />} />
                <Route path="/virksomheder/fremstaa-trovaerdig" element={<VirksomhedFremstaaTrovaerdigPage />} />
                <Route path="/virksomheder/fremsta-trovaerdig" element={<Navigate to="/virksomheder/fremstaa-trovaerdig" replace />} />
                <Route path="/foredrag/virksomheder/fremstaa-trovaerdig" element={<Navigate to="/virksomheder/fremstaa-trovaerdig" replace />} />
                <Route path="/virksomheder/aktiver-hjertets-intelligens" element={<VirksomhedAktiverHjertetsIntelligensPage />} />
                <Route path="/foredrag/virksomheder/aktiver-hjertets-intelligens" element={<Navigate to="/virksomheder/aktiver-hjertets-intelligens" replace />} />
                <Route path="/frisaet-dig" element={<FrisaetDigPage />} />
                <Route path="/frisaet-dig/fysisk-forloeb" element={<FrisaetDigFysiskPage />} />
                <Route path="/frisaet-dig/online-forloeb" element={<FrisaetDigOnlinePage />} />
                <Route path="/frisaet-dig/videoforloeb" element={<Navigate to="/frisaet-dig" replace />} />
                <Route path="/frisaet-dig/workshop" element={<FrisaetDigWorkshopPage />} />
                <Route path="/frisaet-dig/workshop/praktisk-information" element={<FrisaetDigWorkshopPracticalPage />} />
                <Route path="/workshop/praktisk-information" element={<Navigate to="/frisaet-dig/workshop/praktisk-information" replace />} />
                <Route path="/frisaet-dig/weekendworkshop/praktisk-information" element={<Navigate to="/frisaet-dig/workshop/praktisk-information" replace />} />
                <Route path="/frisaet-dig/foredrag" element={<FrisaetDigForedragPage />} />
                <Route path="/kvinde-kend-din-power" element={<KvindeKendDinPowerPage />} />
                <Route path="/privat" element={<Navigate to="/foredrag/privat" replace />} />
                <Route path="/virksomheder" element={<Navigate to="/foredrag/virksomheder" replace />} />
                <Route path="/retreats" element={<RetreatsPage />} />
                <Route path="/montserrat" element={<MontserratPage />} />
                <Route path="/costa-del-sol" element={<CostaDelSolPage />} />
                <Route path="/costa-del-sol/smagsproeve" element={<CostaDelSolSmagsproevePage />} />
                <Route path="/smagsproeve" element={<CostaDelSolSmagsproevePage />} />
                <Route path="/yoga-og-indre-fred" element={<Navigate to="/costa-del-sol" replace />} />
                <Route path="/thailand-retreat" element={<ThailandRetreatPage />} />
                <Route path="/styrk-din-sjaelsintelligens" element={<Navigate to="/thailand-retreat" replace />} />
                <Route path="/taenketank" element={<Navigate to="/" replace />} />
                <Route path="/om-mette" element={<OmMettePage />} />
                <Route path="/om-mette/udtalelser" element={<UdtalelserPage />} />
                <Route path="/udtalelser" element={<UdtalelserPage />} />
                <Route path="/om-mette/virksomhedspraktik" element={<VirksomhedspraktikPage />} />
                <Route path="/virksomhedspraktik" element={<VirksomhedspraktikPage />} />
                <Route path="/sessioner" element={<SessionerPage />} />
                <Route path="/sessioner/clairvoyant-raadgivning" element={<ClairvoyantRaadgivningPage />} />
                <Route path="/sessioner/clairvoyance" element={<Navigate to="/sessioner/clairvoyant-raadgivning" replace />} />
                <Route path="/sessioner/ledersparring" element={<LedersparringSessionPage />} />
                <Route path="/sessioner/mentorforloeb" element={<MentorforloebSessionPage />} />
                <Route path="/sessioner/mentor" element={<Navigate to="/sessioner/mentorforloeb" replace />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/podcasts" element={<PodcastsPage />} />
                <Route path="/podcast" element={<Navigate to="/podcasts" replace />} />
                <Route path="/gratis" element={<GratisPage />} />
                <Route path="/gratis-og-events" element={<Navigate to="/gratis" replace />} />
                <Route path="/frisaet-dig-miniworkshop" element={<GratisMiniworkshopPage />} />
                <Route path="/gratis/frisaet-dig-miniworkshop" element={<Navigate to="/frisaet-dig-miniworkshop" replace />} />
                <Route path="/gratis/frisaet-dig" element={<Navigate to="/frisaet-dig-miniworkshop" replace />} />
                <Route path="/gratis/online-miniworkshop" element={<Navigate to="/frisaet-dig-miniworkshop" replace />} />
                <Route path="/gratis/miniworkshop" element={<Navigate to="/frisaet-dig-miniworkshop" replace />} />
                <Route path="/events" element={<Navigate to="/gratis" replace />} />
                <Route path="/blog/:id" element={<BlogPostPage />} />
                <Route path="/kontakt" element={<KontaktPage />} />
                <Route path="/logo" element={<LogoPage />} />
                <Route path="/privatlivspolitik" element={<PrivatlivspolitikPage />} />

                {/* English Language Routes */}
                <Route path="/en" element={<HomePage />} />
                <Route path="/en/keynotes" element={<ForedragPage />} />
                <Route path="/en/keynotes/private" element={<ForedragPrivatPage />} />
                <Route path="/en/keynotes/businesses" element={<ForedragVirksomhederPage />} />
                <Route path="/en/keynotes/businesses/leap-into-the-future" element={<VirksomhedSpringIndIFremtidenPage />} />
                <Route path="/en/keynotes/businesses/new-energy-in-the-team" element={<VirksomhedNyEnergiITeametPage />} />
                <Route path="/en/keynotes/businesses/communicate-with-authenticity" element={<VirksomhedFremstaaTrovaerdigPage />} />
                <Route path="/en/keynotes/businesses/activate-heart-intelligence" element={<VirksomhedAktiverHjertetsIntelligensPage />} />
                <Route path="/en/unleash-yourself" element={<FrisaetDigPage />} />
                <Route path="/en/unleash-yourself/physical-program" element={<FrisaetDigFysiskPage />} />
                <Route path="/en/unleash-yourself/online-program" element={<FrisaetDigOnlinePage />} />
                <Route path="/en/unleash-yourself/video-program" element={<Navigate to="/en/unleash-yourself" replace />} />
                <Route path="/en/unleash-yourself/workshop" element={<FrisaetDigWorkshopPage />} />
                <Route path="/en/unleash-yourself/workshop/practical-information" element={<FrisaetDigWorkshopPracticalPage />} />
                <Route path="/en/unleash-yourself/workshop/praktisk-information" element={<Navigate to="/en/unleash-yourself/workshop/practical-information" replace />} />
                <Route path="/en/frisaet-dig/workshop/praktisk-information" element={<Navigate to="/en/unleash-yourself/workshop/practical-information" replace />} />
                <Route path="/en/unleash-yourself/keynote" element={<FrisaetDigForedragPage />} />
                <Route path="/en/frisaet-dig/foredrag" element={<Navigate to="/en/unleash-yourself/keynote" replace />} />
                <Route path="/en/frisaet-dig/fysisk-forloeb" element={<Navigate to="/en/unleash-yourself/physical-program" replace />} />
                <Route path="/en/frisaet-dig/online-forloeb" element={<Navigate to="/en/unleash-yourself/online-program" replace />} />
                <Route path="/en/frisaet-dig/videoforloeb" element={<Navigate to="/en/unleash-yourself" replace />} />
                <Route path="/en/frisaet-dig/workshop" element={<Navigate to="/en/unleash-yourself/workshop" replace />} />
                <Route path="/en/woman-know-your-power" element={<KvindeKendDinPowerPage />} />
                <Route path="/en/retreats" element={<RetreatsPage />} />
                <Route path="/en/montserrat" element={<MontserratPage />} />
                <Route path="/en/costa-del-sol" element={<CostaDelSolPage />} />
                <Route path="/en/costa-del-sol/workshop" element={<CostaDelSolSmagsproevePage />} />
                <Route path="/en/costa-del-sol/smagsproeve" element={<Navigate to="/en/costa-del-sol/workshop" replace />} />
                <Route path="/en/smagsproeve" element={<Navigate to="/en/costa-del-sol/workshop" replace />} />
                <Route path="/en/yoga-and-inner-peace" element={<Navigate to="/en/costa-del-sol" replace />} />
                <Route path="/en/thailand-retreat" element={<ThailandRetreatPage />} />
                <Route path="/en/strengthen-your-soul-intelligence" element={<Navigate to="/en/thailand-retreat" replace />} />
                <Route path="/en/think-tank" element={<Navigate to="/en" replace />} />
                <Route path="/en/about-mette" element={<OmMettePage />} />
                <Route path="/en/about-mette/testimonials" element={<UdtalelserPage />} />
                <Route path="/en/testimonials" element={<UdtalelserPage />} />
                <Route path="/en/about-mette/internship" element={<VirksomhedspraktikPage />} />
                <Route path="/en/internship" element={<VirksomhedspraktikPage />} />
                <Route path="/en/sessions" element={<SessionerPage />} />
                <Route path="/en/sessions/clairvoyant-mentoring" element={<ClairvoyantRaadgivningPage />} />
                <Route path="/en/sessions/clairvoyant-advisory" element={<Navigate to="/en/sessions/clairvoyant-mentoring" replace />} />
                <Route path="/en/sessions/leadership-mentoring" element={<LedersparringSessionPage />} />
                <Route path="/en/sessions/mentor-program" element={<MentorforloebSessionPage />} />
                <Route path="/en/blog" element={<BlogPage />} />
                <Route path="/en/podcasts" element={<PodcastsPage />} />
                <Route path="/en/podcast" element={<Navigate to="/en/podcasts" replace />} />
                <Route path="/en/free" element={<GratisPage />} />
                <Route path="/en/free-and-events" element={<Navigate to="/en/free" replace />} />
                <Route path="/en/free/unleash-yourself-miniworkshop" element={<GratisMiniworkshopPage />} />
                <Route path="/en/free/frisaet-dig-miniworkshop" element={<Navigate to="/en/free/unleash-yourself-miniworkshop" replace />} />
                <Route path="/en/free/online-miniworkshop" element={<Navigate to="/en/free/unleash-yourself-miniworkshop" replace />} />
                <Route path="/en/gratis/frisaet-dig-miniworkshop" element={<Navigate to="/en/free/unleash-yourself-miniworkshop" replace />} />
                <Route path="/en/gratis" element={<Navigate to="/en/free" replace />} />
                <Route path="/en/gratis-og-events" element={<Navigate to="/en/free" replace />} />
                <Route path="/en/events" element={<Navigate to="/en/free" replace />} />
                <Route path="/en/blog/:id" element={<BlogPostPage />} />
                <Route path="/en/contact" element={<KontaktPage />} />
                <Route path="/en/privacy-policy" element={<PrivatlivspolitikPage />} />
                <Route path="/en/logo" element={<LogoPage />} />

                {/* 301 / Client Redirects for English URLs using Danish Slugs */}
                <Route path="/en/foredrag" element={<Navigate to="/en/keynotes" replace />} />
                <Route path="/en/foredrag/privat" element={<Navigate to="/en/keynotes/private" replace />} />
                <Route path="/en/foredrag/virksomheder" element={<Navigate to="/en/keynotes/businesses" replace />} />
                <Route path="/en/virksomheder/spring-ind-i-fremtiden" element={<Navigate to="/en/keynotes/businesses/leap-into-the-future" replace />} />
                <Route path="/en/keynotes/businesses/spring-ind-i-fremtiden" element={<Navigate to="/en/keynotes/businesses/leap-into-the-future" replace />} />
                <Route path="/en/foredrag/virksomheder/spring-ind-i-fremtiden" element={<Navigate to="/en/keynotes/businesses/leap-into-the-future" replace />} />
                <Route path="/en/virksomheder/ny-energi-i-teamet" element={<Navigate to="/en/keynotes/businesses/new-energy-in-the-team" replace />} />
                <Route path="/en/keynotes/businesses/ny-energi-i-teamet" element={<Navigate to="/en/keynotes/businesses/new-energy-in-the-team" replace />} />
                <Route path="/en/foredrag/virksomheder/ny-energi-i-teamet" element={<Navigate to="/en/keynotes/businesses/new-energy-in-the-team" replace />} />
                <Route path="/en/virksomheder/fremstaa-trovaerdig" element={<Navigate to="/en/keynotes/businesses/communicate-with-authenticity" replace />} />
                <Route path="/en/virksomheder/fremsta-trovaerdig" element={<Navigate to="/en/keynotes/businesses/communicate-with-authenticity" replace />} />
                <Route path="/en/keynotes/businesses/fremstaa-trovaerdig" element={<Navigate to="/en/keynotes/businesses/communicate-with-authenticity" replace />} />
                <Route path="/en/foredrag/virksomheder/fremstaa-trovaerdig" element={<Navigate to="/en/keynotes/businesses/communicate-with-authenticity" replace />} />
                <Route path="/en/virksomheder/aktiver-hjertets-intelligens" element={<Navigate to="/en/keynotes/businesses/activate-heart-intelligence" replace />} />
                <Route path="/en/keynotes/businesses/aktiver-hjertets-intelligens" element={<Navigate to="/en/keynotes/businesses/activate-heart-intelligence" replace />} />
                <Route path="/en/foredrag/virksomheder/aktiver-hjertets-intelligens" element={<Navigate to="/en/keynotes/businesses/activate-heart-intelligence" replace />} />
                <Route path="/en/frisaet-dig" element={<Navigate to="/en/unleash-yourself" replace />} />
                <Route path="/en/kvinde-kend-din-power" element={<Navigate to="/en/woman-know-your-power" replace />} />
                <Route path="/en/privat" element={<Navigate to="/en/keynotes/private" replace />} />
                <Route path="/en/virksomheder" element={<Navigate to="/en/keynotes/businesses" replace />} />
                <Route path="/en/taenketank" element={<Navigate to="/en" replace />} />
                <Route path="/en/om-mette" element={<Navigate to="/en/about-mette" replace />} />
                <Route path="/en/om-mette/udtalelser" element={<Navigate to="/en/about-mette/testimonials" replace />} />
                <Route path="/en/udtalelser" element={<Navigate to="/en/about-mette/testimonials" replace />} />
                <Route path="/en/om-mette/virksomhedspraktik" element={<Navigate to="/en/about-mette/internship" replace />} />
                <Route path="/en/virksomhedspraktik" element={<Navigate to="/en/about-mette/internship" replace />} />
                <Route path="/en/sessioner" element={<Navigate to="/en/sessions" replace />} />
                <Route path="/en/sessioner/clairvoyant-raadgivning" element={<Navigate to="/en/sessions/clairvoyant-mentoring" replace />} />
                <Route path="/en/sessioner/ledersparring" element={<Navigate to="/en/sessions/leadership-mentoring" replace />} />
                <Route path="/en/sessioner/mentorforloeb" element={<Navigate to="/en/sessions/mentor-program" replace />} />
                <Route path="/en/kontakt" element={<Navigate to="/en/contact" replace />} />
                <Route path="/en/privatlivspolitik" element={<Navigate to="/en/privacy-policy" replace />} />
                <Route path="/en/yoga-og-indre-fred" element={<Navigate to="/en/costa-del-sol" replace />} />
                <Route path="/en/styrk-din-sjaelsintelligens" element={<Navigate to="/en/strengthen-your-soul-intelligence" replace />} />

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </PageTransition>
          </main>
          <Footer />
          <NewsletterPopup />
        </div>
        </TestimonialProvider>
        </CmsProvider>
      </LanguageProvider>
    </Router>
  );
}

export default App;
